import { useState, useEffect, useRef, Fragment, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import GridLayout, { WidthProvider } from "react-grid-layout/legacy";
import SignatureCanvas from "react-signature-canvas";
import {
  Users, PauseCircle, UserPlus, UserX, Euro, Calendar, Megaphone, Cake, CheckSquare, Zap, BarChart3,
  LayoutDashboard, Landmark, Receipt, UserCog, Mail, Building2, Tag, Settings, LogOut, Layers, HeartPulse,
  Pin, PinOff, CreditCard, RefreshCw, Tv, Wifi, MessageCircle,
} from "lucide-react";
import THEME, { designUebernehmenAusEinstellungen, designUeberschreibungenZuruecksetzen, schriftgroesseUebernehmenAusEinstellungen, schriftgroesseZuruecksetzen, FONT_SCALE_OPTIONS, kontrastTextfarbe } from "./theme";
import { pushWirdUnterstuetzt, holeAktivesAbo, pushAktivieren, pushDeaktivieren, pushEinstellungenLaden, pushEinstellungenSpeichern } from "./push";
import FinanzModul from "./modules/finanzen/FinanzModul";
import BelegeUploadApp from "./modules/belege/BelegeUploadApp";
import TV2Page from "./TV2Page";
import RfidPruefliste from "./RfidPruefliste";
import RfidPruefHinweis, { START_TAB_SCHLUESSEL } from "./RfidPruefHinweis";
import MilonSyncWarnung from "./MilonSyncWarnung";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./App.css";
const TrainingApp = lazy(() => import("./training/TrainingApp"));

const ResponsiveGridLayout = WidthProvider(GridLayout);

function WidgetRahmen({ titel, onSchliessen, bearbeiten, autoHoehe, onHoeheGemessen, children }) {
  const inhaltRef = useRef(null);
  useEffect(() => {
    if (!autoHoehe || !onHoeheGemessen) return;
    const el = inhaltRef.current;
    if (!el) return;
    const messen = () => onHoeheGemessen(el.scrollHeight);
    messen();
    const ro = new ResizeObserver(messen);
    ro.observe(el);
    return () => ro.disconnect();
  }, [autoHoehe, onHoeheGemessen]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: THEME.colors.background, borderRadius: 12, overflow: "hidden", border: bearbeiten ? "1px dashed #2b8cc4" : "1px solid transparent" }}>
      {bearbeiten && (
        <div className="dash-drag-handle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", cursor: "move", background: THEME.colors.surface, flexShrink: 0 }}>
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>⠿ {titel}</span>
          <button onClick={(e) => { e.stopPropagation(); onSchliessen(); }} title="Modul ausblenden" style={{ background: "none", border: "none", color: THEME.colors.textMuted, cursor: "pointer", fontSize: THEME.fontSize.lg, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      )}
      <div ref={inhaltRef} style={autoHoehe ? { height: "auto", overflow: "visible", padding: 16 } : { flex: 1, overflow: "auto", padding: 16 }}>{children}</div>
    </div>
  );
}

const API = ["localhost", "127.0.0.1"].includes(window.location.hostname)
  ? "http://localhost:8000"
  : `${window.location.protocol}//${window.location.hostname}`;
// Frontend (localhost:3000) und Backend (localhost:8000) nutzen im Dev-Betrieb bewusst denselben
// Hostnamen "localhost" (nicht 127.0.0.1) - fuer das Sitzungs-Cookie zaehlt nur der Hostname als
// "Site", nicht der Port. 127.0.0.1 und localhost wuerden sonst als unterschiedliche Sites gelten
// und das Cookie beim Cross-Port-Request nicht mitgeschickt werden.

// Einmaliger globaler fetch-Patch: schickt bei JEDER der zahlreichen Fetch-Aufrufstellen in dieser
// Datei automatisch das httpOnly-Sitzungs-Cookie mit (noetig im Dev-Betrieb, da Frontend/Backend
// auf unterschiedlichen Ports laufen) und leitet bei einer 401-Antwort zentral zum Login zurueck -
// ohne dass jede einzelne Aufrufstelle selbst angepasst werden muss.
let _sitzungAbgelaufenCallback = null;
export function setzeSitzungAbgelaufenCallback(fn) { _sitzungAbgelaufenCallback = fn; }
const _fetchOriginal = window.fetch.bind(window);
window.fetch = async (url, optionen = {}) => {
  const antwort = await _fetchOriginal(url, { ...optionen, credentials: optionen.credentials || "include" });
  // /kalender/* liefert 401, wenn die GOOGLE-Kalender-Verbindung ungueltig ist (z.B. Google-Token-
  // Refresh schlaegt kurzzeitig fehl, siehe get_credentials() in routers_kalender.py) - das hat
  // nichts mit der eigenen ComeFit-Login-Sitzung zu tun. Vorher wurde das faelschlich als
  // abgelaufene Sitzung gewertet und der Admin komplett rausgeworfen, obwohl sein Sitzungs-Cookie
  // noch 30 Tage gueltig war (Nutzer-Meldung 2026-09-13: "fliege die ganze Zeit raus als admin").
  const istFremdAuth = typeof url === "string" && url.includes("/kalender/");
  if (antwort.status === 401 && !istFremdAuth && _sitzungAbgelaufenCallback) {
    _sitzungAbgelaufenCallback();
  }
  return antwort;
};
// Generisches Modul-Rechte-System: Nav-Punkt -> Modul-Key (muss mit backend/routers_benutzer.py MODULE übereinstimmen).
// Dashboard ist bewusst nicht enthalten – immer erlaubt, keine eigene Landing-Page-Sperre.
const NAV_MODUL_KEY = {
  "Mitglieder": "mitglieder", "SEPA": "sepa", "Statistiken": "statistiken", "Rechnungen": "rechnungen",
  "Mitarbeiter": "mitarbeiter", "Kalender": "kalender",
  "Studio": "studio", "Einstellungen": "einstellungen",
  "Finanzen": "finanzen_anzeigen", "TV": "tv",
};
// "Online Portal" buendelt drei eigenstaendige Module in einem Nav-Punkt (mit Tabs) -
// die Modul-Rechte selbst bleiben getrennt (siehe Mitarbeiter-Rechte-Formular),
// nur die Navigation zeigt sie zusammengefasst.
const ONLINE_PORTAL_MODULE_KEYS = ["online_vertraege", "kuendigungen", "feedback"];
// "Studio" buendelt analog dazu Kurse, E-Mails und Aktionen als eigene Tabs mit ein -
// verkuerzt die Hauptnavigation, die Modul-Rechte bleiben pro Tab getrennt.
const STUDIO_MODULE_KEYS = ["studio", "kurse", "emails", "aktionen", "vorfaelle_ansehen"];
// "Mitglieder" buendelt analog Wellpass-Nutzer/Hansefit-Nutzer als eigene Tabs mit ein -
// bessere Auffindbarkeit statt eigener Top-Level-Nav-Punkte, Modul-Rechte bleiben pro Tab getrennt.
const MITGLIEDER_MODULE_KEYS = ["mitglieder", "wellpass", "hansefit", "sonstige_nutzer"];
// "Check In" buendelt Wellpass und Hansefit (die Check-in-Verwaltung, nicht die Nutzerverwaltung
// unter Mitglieder) als eigene Tabs mit ein - Modul-Rechte bleiben pro Tab getrennt.
const CHECKIN_MODULE_KEYS = ["wellpass", "hansefit"];
const NAV_ICON = {
  "Dashboard": LayoutDashboard, "Mitglieder": Users, "SEPA": Landmark, "Statistiken": BarChart3,
  "Rechnungen": Receipt, "Mitarbeiter": UserCog,
  "Kalender": Calendar, "Studio": Building2, "Online Portal": Layers,
  "Einstellungen": Settings, "Check In": HeartPulse,
  "Finanzen": Euro, "TV": Tv,
};
const TARIFE = ["Premium", "Basic", "Studenten", "Zollstudenten", "Basic Partner", "Premium Partner", "Studenten Partner"];
const TARIF_PREISE = { Premium: 49.90, Basic: 44.90, Studenten: 39.90, Zollstudenten: 34.90, "Basic Partner": 39.90, "Premium Partner": 44.90, "Studenten Partner": 34.90 };
// Fallback, falls der Server (noch) keinen TURNSTILE_SITE_KEY gesetzt hat – Cloudflares
// offizieller Test-Key, lässt immer alles durch. Der echte Key kommt normalerweise dynamisch
// von /vertraege/tarife-und-aktionen, sodass ein Wechsel keinen neuen Frontend-Build braucht.
const TURNSTILE_SITE_KEY_FALLBACK = "1x00000000000000000000AA";

function datumDE(d) {
  if (!d) return "–";
  const [y, m, t] = d.split("-");
  return `${t}.${m}.${y}`;
}

// Erstlaufzeit-Ende eines Mitglieds: "6"/"12" = Monate ab Vertragsbeginn, "bis:2027-12-31" = festes Datum
// (Aktionen mit festem Gratis-Zeitraum, z.B. "Trainiere bis 2027 gratis"). null = keine Erstlaufzeit.
function erstlaufzeitEnde(mitglied) {
  const e = mitglied && mitglied.erstlaufzeit;
  if (!e || e === "keine") return null;
  if (String(e).startsWith("bis:")) return new Date(String(e).slice(4));
  if (!mitglied.vertragsbeginn) return null;
  const ablauf = new Date(mitglied.vertragsbeginn);
  ablauf.setMonth(ablauf.getMonth() + parseInt(e));
  return ablauf;
}
function erstlaufzeitText(e) {
  if (!e || e === "keine") return "Keine (monatlich kündbar)";
  if (String(e).startsWith("bis:")) return `bis ${datumDE(String(e).slice(4))} (Aktion)`;
  return `${e} Monate`;
}

function letzterTagMonat(jahr, monat) { return new Date(jahr, monat, 0); }

// Formatiert ein Date-Objekt anhand seiner LOKALEN Jahr/Monat/Tag-Werte als "YYYY-MM-DD" - im
// Unterschied zu date.toISOString().split("T")[0], das ueber UTC geht und bei einem lokal um
// Mitternacht konstruierten Date (z.B. letzterTagMonat(), new Date(jahr,monat,tag)) in jeder
// Zeitzone oestlich von UTC (z.B. Europe/Berlin, UTC+1/+2) einen Tag zu FRUEH liefert - das war
// der Bug hinter der Kuendigen-Datumsvorauswahl (Nutzer-Meldung 2026-08-29: "29. September" statt
// "30. September" als Vorschlag).
function datumZuLokalerISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function validiereIBAN(iban) {
  const sauber = iban.replace(/\s/g, "").toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(sauber)) return false;
  const umgestellt = sauber.slice(4) + sauber.slice(0, 4);
  const zahlen = umgestellt.split("").map(c => isNaN(c) ? c.charCodeAt(0) - 55 : c).join("");
  let rest = 0;
  for (let c of zahlen) { rest = (rest * 10 + parseInt(c)) % 97; }
  return rest === 1;
}

function berechneAlter(datum) {
  if (!datum) return null;
  const heute = new Date();
  const geb = new Date(datum);
  let alter = heute.getFullYear() - geb.getFullYear();
  const m = heute.getMonth() - geb.getMonth();
  if (m < 0 || (m === 0 && heute.getDate() < geb.getDate())) alter--;
  return alter;
}

// Nutzer-Wunsch 2026-09-07: Geldfelder sollen beim Tippen ueberhaupt nicht mehr eingreifen
// (keine Live-Umwandlung/-Rundung mehr, die vorherige Version hat trotz Fix noch "dazwischengefunkt")
// - stattdessen wird nur noch GEPRUEFT, ob der aktuell eingegebene Text ueberhaupt eine gueltige
// Zahl ist (Komma ODER Punkt als Trennzeichen), mit rotem Rahmen/Fehlertext als Rueckmeldung.
// Erst beim Verlassen des Feldes (onBlur) wird - nur wenn gueltig - sauber auf 2 Nachkommastellen
// normalisiert; waehrend des Tippens bleibt der Text exakt so stehen, wie eingegeben.
function parseGeldEingabe(wert) {
  if (wert === undefined || wert === null || wert === "") return NaN;
  return parseFloat(String(wert).replace(",", "."));
}

function berechneAnteilig(beitrag, datum) {
  if (!datum || !beitrag) return 0;
  const d = new Date(datum);
  const tageImMonat = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const verbleibend = tageImMonat - d.getDate() + 1;
  return Math.round((beitrag / tageImMonat * verbleibend) * 100) / 100;
}

function berechnePauseNachbelastung(beitrag, abDatum) {
  // Liefert die Monate, die bei einer Reaktivierung nach Pause NICHT mehr vom naechsten
  // regulaeren SEPA-Lauf erfasst werden koennen, weil deren eigener Lauf-Termin (immer der 1.
  // des jeweiligen Monats) bereits vorbei ist - z.B. weil ein Mitglied laut Pause-Datensatz noch
  // "pausiert" war, tatsaechlich aber schon vorher wieder trainiert hat. Diese Monate muessen als
  // Einmalzahlung nachbelastet werden, der naechste noch bevorstehende Monat läuft stattdessen
  // ganz normal über den regulären Lauf mit. Der erste betroffene Monat wird anteilig ab
  // abDatum berechnet (gleiche Logik wie berechneAnteilig), alle weiteren voll.
  if (!abDatum || !beitrag) return [];
  const heute = new Date();
  const naechsterLaufMonat = new Date(heute.getFullYear(), heute.getMonth() + 1, 1);
  const start = new Date(abDatum);
  const positionen = [];
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor < naechsterLaufMonat) {
    const istErsterMonat = positionen.length === 0;
    const betrag = istErsterMonat ? berechneAnteilig(beitrag, abDatum) : beitrag;
    positionen.push({ monatLabel: cursor.toLocaleDateString("de-DE", { month: "long", year: "numeric" }), betrag });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return positionen;
}

function statusFarbe(st) {
  return st === "aktiv" ? THEME.colors.success : st === "pausiert" ? THEME.colors.warning : st === "gekündigt" ? THEME.colors.accentOrange : st === "bezahlt" ? THEME.colors.success : st === "ausstehend" ? THEME.colors.warning : st === "zurueckgebucht" ? THEME.colors.danger : THEME.colors.textDisabled;
}

async function logAktivitaet(aktion, details, mitglied_id, mitglied_name, benutzer) {
  // Reines Hintergrund-Protokoll - ein Fehlschlag hier darf die eigentliche, bereits
  // erfolgreiche Aktion nicht mit einer Fehlermeldung ueberdecken, deshalb bewusst still.
  await fetch(`${API}/aktivitaeten/`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ benutzer: benutzer || "Admin", aktion, details, mitglied_id, mitglied_name })
  }).catch(() => {});
}

// ============ LOGIN ============
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [passwort, setPasswort] = useState("");
  const [fehler, setFehler] = useState("");
  const [laedt, setLaedt] = useState(false);
  const [modus, setModus] = useState("login"); // "login" | "vergessen"
  const [vergessenEmail, setVergessenEmail] = useState("");
  const [vergessenNachricht, setVergessenNachricht] = useState("");
  const [vergessenLaedt, setVergessenLaedt] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLaedt(true);
    try {
      const res = await fetch(`${API}/benutzer/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, passwort })
      });
      if (res.ok) {
        const data = await res.json();
        onLogin(data);
      } else {
        setFehler("E-Mail oder Passwort falsch.");
      }
    } catch {
      setFehler("Verbindung zum Server fehlgeschlagen.");
    }
    setLaedt(false);
  };

  const handlePasswortVergessen = async (e) => {
    e.preventDefault();
    setVergessenLaedt(true);
    try {
      const res = await fetch(`${API}/benutzer/passwort-vergessen`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: vergessenEmail }),
      });
      const data = await res.json();
      setVergessenNachricht(data.message || "Falls diese E-Mail-Adresse bei uns registriert ist, wurde ein Link zum Zurücksetzen des Passworts gesendet.");
    } catch {
      setVergessenNachricht("Verbindung zum Server fehlgeschlagen.");
    }
    setVergessenLaedt(false);
  };

  if (modus === "vergessen") {
    return (
      <div style={s.page}>
        <div style={s.loginCard}>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <img src={`${API}/einstellungen/logo`} alt="ComeFit" style={{ width: 220 }} />
          </div>
          <p style={s.subtitle}>Passwort zurücksetzen</p>
          {vergessenNachricht ? (
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, lineHeight: 1.6 }}>{vergessenNachricht}</p>
          ) : (
            <form onSubmit={handlePasswortVergessen}>
              <div style={s.inputGroup}>
                <label style={s.label}>E-Mail</label>
                <input style={s.input} type="email" value={vergessenEmail} onChange={e => setVergessenEmail(e.target.value)} placeholder="E-Mail-Adresse" required />
              </div>
              <button style={s.btn} type="submit" disabled={vergessenLaedt}>{vergessenLaedt ? "Wird gesendet…" : "Link zum Zurücksetzen senden"}</button>
            </form>
          )}
          <p style={{ textAlign: "center", marginTop: 16 }}>
            <span onClick={() => { setModus("login"); setVergessenNachricht(""); }} style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.base, cursor: "pointer", textDecoration: "underline" }}>Zurück zur Anmeldung</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.loginCard}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <img src={`${API}/einstellungen/logo`} alt="ComeFit" style={{ width: 220 }} />
        </div>
        <p style={s.subtitle}>Verwaltungssystem</p>
        <form onSubmit={handleLogin}>
          <div style={s.inputGroup}><label style={s.label}>E-Mail</label><input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-Mail-Adresse" autoComplete="username" /></div>
          <div style={s.inputGroup}><label style={s.label}>Passwort</label><input style={s.input} type="password" value={passwort} onChange={e => setPasswort(e.target.value)} placeholder="••••••••" /></div>
          {fehler && <p style={{ color: "#ff6b6b", fontSize: THEME.fontSize.md }}>{fehler}</p>}
          <button style={s.btn} type="submit" disabled={laedt}>{laedt ? "Anmelden..." : "Anmelden"}</button>
        </form>
        <p style={{ textAlign: "center", marginTop: 16 }}>
          <span onClick={() => setModus("vergessen")} style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.base, cursor: "pointer", textDecoration: "underline" }}>Passwort vergessen?</span>
        </p>
      </div>
    </div>
  );
}

// ============ TÜR ÖFFNEN (eigenständige Minimal-Seite für Handy-Startbildschirm-Icon) ============
// Bewusst NICHT Teil von HauptApp - eine einzige Seite mit nur einem grossen Button, damit sie
// sich per "Zum Startbildschirm hinzufuegen" wie eine eigene kleine App anfuehlt. Nutzt dieselbe
// Anmeldung (Login-Komponente, Sitzungs-Cookie) und denselben bereits bestehenden, bereits live
// getesteten /ttlock/fernoeffnen-Endpunkt wie der "Tür öffnen"-Button in der Hauptapp - keine neue
// Tueroeffnungs-Logik, kein neues Backend, keine neue Domain/kein neues Zertifikat noetig.
function TuerOeffnenSeite() {
  const [sitzung, setSitzung] = useState(undefined); // undefined = laedt, null = nicht eingeloggt
  const [oeffnetGerade, setOeffnetGerade] = useState(false);
  const [meldung, setMeldung] = useState(null);

  // Eigenes Manifest (manifest-tuer.json, start_url "/tuer") statt des geteilten Haupt-Manifests
  // (start_url ".", loest immer zur Startseite auf) - sonst wuerde "Zum Startbildschirm hinzufuegen"
  // von hier aus ein Icon anlegen, das beim Antippen die normale Hauptapp statt diese Seite oeffnet.
  // Nutzer hat bereits die Hauptapp als PWA installiert und wollte sichergehen, dass sich beide
  // Icons nicht gegenseitig ueberschreiben/verwechseln.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const vorherigesHref = link?.getAttribute("href");
    if (link) link.setAttribute("href", `${process.env.PUBLIC_URL}/manifest-tuer.json`);
    return () => {
      if (link && vorherigesHref) link.setAttribute("href", vorherigesHref);
    };
  }, []);

  useEffect(() => {
    fetch(`${API}/benutzer/me`).then(r => r.ok ? r.json() : null).then(setSitzung).catch(() => setSitzung(null));
  }, []);

  const darfOeffnen = sitzung?.rechte?.rolle === "admin" || !!sitzung?.rechte?.module?.ttlock_fernoeffnen;

  // Bewusst OHNE Bestaetigungsdialog - gleiche, bereits etablierte Vorgabe wie beim "Tür öffnen"-
  // Button in TTLockStatusWidget (Nutzer-Wunsch 2026-07-24: "direkt oeffnen, wichtiger ist die
  // Fehlermeldung falls es nicht klappt").
  const handleOeffnen = async () => {
    setOeffnetGerade(true);
    setMeldung(null);
    try {
      const res = await fetch(`${API}/ttlock/fernoeffnen`, { method: "POST" });
      const daten = await res.json();
      if (!res.ok) {
        setMeldung(daten.detail || "Tür konnte nicht geöffnet werden.");
      } else if (daten.error) {
        setMeldung(`Tür konnte nicht geöffnet werden: ${daten.error}`);
      } else if (daten.executed === false) {
        setMeldung(`Nicht ausgeführt (Modus: ${daten.mode || "?"}).`);
      } else {
        setMeldung("Tür wurde geöffnet.");
      }
    } catch {
      setMeldung("Tür konnte nicht geöffnet werden (Verbindungsfehler).");
    }
    setOeffnetGerade(false);
  };

  // Nutzer-Entscheidung 2026-08-01 (explizit, nach Warnhinweis): Tür öffnet automatisch beim Laden
  // der Seite, kein zweiter Tap auf einen Button mehr noetig - hebt die sonst in diesem Projekt
  // geltende "keine automatische Türöffnung ohne Bestätigung"-Regel bewusst NUR für diese eine,
  // eigens dafür angelegte Seite auf. `ausgeloest`-Ref verhindert Mehrfachausloesung bei Re-Renders.
  const ausgeloest = useRef(false);
  useEffect(() => {
    if (darfOeffnen && !ausgeloest.current) {
      ausgeloest.current = true;
      handleOeffnen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darfOeffnen]);

  if (sitzung === undefined) {
    return <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center" }}>Lädt…</div>;
  }
  if (!sitzung) {
    return <Login onLogin={setSitzung} />;
  }

  return (
    <div style={{ ...s.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <img src={`${API}/einstellungen/logo`} alt="ComeFit" style={{ width: 180, marginBottom: 32 }} />
      {darfOeffnen ? (
        <button onClick={handleOeffnen} disabled={oeffnetGerade}
          style={{ ...s.btn, width: "100%", maxWidth: 320, fontSize: 22, padding: "24px 20px", borderRadius: 16 }}>
          {oeffnetGerade ? "Öffnet…" : "🔓 Tür öffnen"}
        </button>
      ) : (
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, textAlign: "center" }}>
          Dir fehlt das Recht "Tür fernöffnen". Bitte beim Studio-Inhaber freischalten lassen.
        </p>
      )}
      {meldung && <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, marginTop: 20, textAlign: "center" }}>{meldung}</p>}
    </div>
  );
}

// ============ DOKUMENTE ============
// Lokaler Scan-Helper (siehe backend/scan_helper.py) - laeuft NUR auf dem PC, an dem der
// Scanner haengt, spricht kein Backend an, sondern direkt den Browser des Nutzers.
const SCAN_HELPER_URL = "http://localhost:9191";

// Muss mit DOKUMENT_KATEGORIEN in routers_dokumente.py uebereinstimmen.
const DOKUMENT_KATEGORIE_LABEL = {
  mitgliedsvertrag: "Mitgliedsvertrag", vertragsaenderung: "Vertragsänderung", rechnung: "Rechnung",
  bescheinigung: "Bescheinigung", kuendigungsbestaetigung: "Kündigungsbestätigung", sonstige: "Sonstiges",
};

function DokumenteBereich({ mitgliedId }) {
  const [dokumente, setDokumente] = useState([]);
  const [titel, setTitel] = useState("");
  const [datei, setDatei] = useState(null);
  const [kategorie, setKategorie] = useState("");
  const [fuerMitgliedSichtbar, setFuerMitgliedSichtbar] = useState(false);
  const [hochladenOffen, setHochladenOffen] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [scanntGerade, setScanntGerade] = useState(false);
  const [scanFehler, setScanFehler] = useState("");

  const laden = () => fetch(`${API}/dokumente/mitglied/${mitgliedId}`).then(r => r.json()).then(setDokumente).catch(() => {});
  useEffect(() => { laden(); }, [mitgliedId]);

  const handleHochladen = async () => {
    if (!datei || !titel) { alert("Bitte Titel und Datei angeben!"); return; }
    setLaedt(true);
    const formData = new FormData();
    formData.append("titel", titel);
    formData.append("datei", datei);
    formData.append("kategorie", kategorie);
    formData.append("fuer_mitglied_sichtbar", fuerMitgliedSichtbar ? "true" : "false");
    const res = await fetch(`${API}/dokumente/upload/${mitgliedId}`, { method: "POST", body: formData });
    setLaedt(false);
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Hochladen fehlgeschlagen."); return; }
    setTitel(""); setDatei(null); setKategorie(""); setFuerMitgliedSichtbar(false); setHochladenOffen(false);
    laden();
  };

  const handleSichtbarkeitAendern = async (d, neueKategorie, neueSichtbarkeit) => {
    const formData = new FormData();
    formData.append("kategorie", neueKategorie || "");
    formData.append("fuer_mitglied_sichtbar", neueSichtbarkeit ? "true" : "false");
    const res = await fetch(`${API}/dokumente/${d.id}/sichtbarkeit`, { method: "PUT", body: formData });
    if (!res.ok) { alert("Aktualisieren fehlgeschlagen."); return; }
    laden();
  };

  const handleLoeschen = async (id) => {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    const res = await fetch(`${API}/dokumente/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    laden();
  };

  // Scannt eine Seite ueber das lokale Scan-Hilfsprogramm (backend/scan_helper.py, muss auf
  // diesem PC laufen) und uebergibt sie an denselben Upload-Dialog wie eine manuell
  // ausgewaehlte Datei - kein zweiter Upload-Codepfad.
  const handleScannen = async () => {
    setScanFehler(""); setScanntGerade(true);
    try {
      // credentials: "omit" ist hier wichtig - der globale fetch-Override oben in dieser Datei
      // haengt sonst automatisch "include" an (fuer die eigenen ComeFit-Sitzungscookies gedacht),
      // was der fremde, cross-origin Scan-Helper nicht unterstuetzt und die Anfrage stumm an der
      // CORS-Pruefung scheitern laesst.
      const res = await fetch(`${SCAN_HELPER_URL}/scannen`, { method: "POST", credentials: "omit" });
      if (!res.ok) {
        const daten = await res.json().catch(() => ({}));
        throw new Error(daten.fehler || "Scan fehlgeschlagen.");
      }
      const blob = await res.blob();
      const datum = new Date().toISOString().slice(0, 10);
      const gescannteDatei = new File([blob], `Scan_${datum}.pdf`, { type: "application/pdf" });
      setDatei(gescannteDatei);
      setHochladenOffen(true);
    } catch (e) {
      setScanFehler(
        e.message === "Failed to fetch"
          ? "Scan-Hilfsprogramm nicht erreichbar. Ist es gestartet (scan_helper_starten.bat)?"
          : e.message
      );
    }
    setScanntGerade(false);
  };

  return (
    <div style={{ ...s.karte, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>Dokumente</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={handleScannen} disabled={scanntGerade} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.base }}>{scanntGerade ? "Scannt…" : "🖨️ Scannen"}</button>
          <button onClick={() => setHochladenOffen(!hochladenOffen)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.base }}>+ Dokument hochladen</button>
        </div>
      </div>
      {scanFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginBottom: 12 }}>{scanFehler}</p>}
      {hochladenOffen && (
        <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={s.inputGroup}><label style={s.label}>Titel *</label><input style={s.input} value={titel} onChange={e => setTitel(e.target.value)} placeholder="z.B. Vertragsunterlagen, Ausweiskopie..." /></div>
          <div style={s.inputGroup}><label style={s.label}>Datei *</label><input type="file" style={{ ...s.input, padding: "8px 14px" }} onChange={e => setDatei(e.target.files[0])} /></div>
          {datei && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>Ausgewählt: {datei.name}</p>}
          <div style={s.inputGroup}>
            <label style={s.label}>Kategorie (optional)</label>
            <select style={s.input} value={kategorie} onChange={e => setKategorie(e.target.value)}>
              <option value="">Keine Angabe</option>
              {Object.entries(DOKUMENT_KATEGORIE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer", margin: "4px 0 12px" }}>
            <input type="checkbox" checked={fuerMitgliedSichtbar} onChange={e => setFuerMitgliedSichtbar(e.target.checked)} />
            In der Mitglieder-App sichtbar
          </label>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <button onClick={() => setHochladenOffen(false)} style={s.btnGray}>Abbrechen</button>
            <button onClick={handleHochladen} style={{ ...s.btn, width: "auto" }} disabled={laedt}>{laedt ? "Lädt hoch..." : "Hochladen"}</button>
          </div>
        </div>
      )}
      {dokumente.length === 0 && !hochladenOffen && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.md }}>Noch keine Dokumente vorhanden.</p>}
      {dokumente.map(d => (
        <div key={d.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 110px 90px", gap: 12, padding: "12px 0", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
          <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md, fontWeight: 600 }}>{d.titel}</span>
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{d.dateiname}<br />{d.erstellt_am}</span>
          <a href={`${API}/dokumente/download/${d.id}`} target="_blank" rel="noreferrer" style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 12px", textDecoration: "none", textAlign: "center", display: "block" }}>↓ Download</a>
          <button onClick={() => handleLoeschen(d.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 12px", background: THEME.colors.dangerBg }}>Löschen</button>
          <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: -4 }}>
            <select style={{ ...s.input, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.xs }} value={d.kategorie || ""}
              onChange={e => handleSichtbarkeitAendern(d, e.target.value, d.fuer_mitglied_sichtbar)}>
              <option value="">Keine Kategorie</option>
              {Object.entries(DOKUMENT_KATEGORIE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: d.fuer_mitglied_sichtbar ? THEME.colors.success : THEME.colors.textMuted, fontSize: THEME.fontSize.xs, cursor: "pointer" }}>
              <input type="checkbox" checked={!!d.fuer_mitglied_sichtbar} onChange={e => handleSichtbarkeitAendern(d, d.kategorie, e.target.checked)} />
              In der Mitglieder-App sichtbar
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}


// ============ UNTERSCHRIFT PAD ============
function UnterschriftPad({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const [zeichnen, setZeichnen] = useState(false);
  const [hatInhalt, setHatInhalt] = useState(false);

  const startZeichnen = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setZeichnen(true);
  };

  const zeichne = (e) => {
    if (!zeichnen) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();
    setHatInhalt(true);
  };

  const stopZeichnen = () => setZeichnen(false);

  const loeschen = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHatInhalt(false);
  };

  const speichern = () => {
    if (!hatInhalt) { alert("Bitte unterschreiben!"); return; }
    const canvas = canvasRef.current;
    onSave(canvas.toDataURL("image/png"));
  };

  return (
    <div>
      <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginBottom: 8 }}>Bitte im Feld unterschreiben:</p>
      <canvas ref={canvasRef} width={500} height={150}
        style={{ border: "2px solid #2b3d52", borderRadius: 8, background: THEME.colors.text, cursor: "crosshair", touchAction: "none", width: "100%", maxWidth: 500 }}
        onMouseDown={startZeichnen} onMouseMove={zeichne} onMouseUp={stopZeichnen} onMouseLeave={stopZeichnen}
        onTouchStart={startZeichnen} onTouchMove={zeichne} onTouchEnd={stopZeichnen}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button onClick={loeschen} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>Löschen</button>
        <button onClick={onCancel} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>Abbrechen</button>
        <button onClick={speichern} style={{ ...s.btn, width: "auto" }}>✓ Unterschrift speichern</button>
      </div>
    </div>
  );
}

// ============ MITARBEITER SEITE ============
function MitarbeiterSeite({ session, rechte }) {
  const [aktiveTab, setAktiveTab] = useState("stundenzettel");
  const darfSchichtplan = rechte?.rolle === "admin" || rechte?.module?.schichtplan;
  const darfUrlaub = rechte?.rolle === "admin" || rechte?.module?.urlaub;
  const tabs = [["stundenzettel", "📋 Stundenzettel"]];
  if (darfSchichtplan) tabs.push(["schichtplan", "🗓️ Schichtplan"]);
  if (darfUrlaub) tabs.push(["urlaub", "🏖️ Urlaub & Abwesenheiten"]);
  return (
    <div>
      <h1 style={s.heading}>Mitarbeiter</h1>
      <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setAktiveTab(key)}
            style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: aktiveTab === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: aktiveTab === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md }}>{label}</button>
        ))}
      </div>
      {aktiveTab === "stundenzettel" && <StundenzettelSeite session={session} />}
      {aktiveTab === "schichtplan" && darfSchichtplan && <SchichtplanSeite session={session} istAdmin={rechte?.rolle === "admin"} />}
      {aktiveTab === "urlaub" && darfUrlaub && <UrlaubSeite session={session} istAdmin={rechte?.rolle === "admin"} />}
    </div>
  );
}

function StundenzettelSeite({ session }) {
  const isAdmin = session.rolle === "admin";
  const [monat, setMonat] = useState(new Date().getMonth() + 1);
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [buchhaltungLaedt, setBuchhaltungLaedt] = useState(false);
  const [stundenzettel, setStundenzettel] = useState([]);
  const [gewaehlterSZ, setGewaehlterSZ] = useState(null);
  const [szDetail, setSzDetail] = useState(null);
  const [unterschriftOffen, setUnterschriftOffen] = useState(null); // "ma" oder "admin"
  const [bearbeitenBlock, setBearbeitenBlock] = useState(null);
  const [neuerBlock, setNeuerBlock] = useState({ datum: new Date().toISOString().split("T")[0], beginn: "09:00", ende: "17:00", pause_minuten: 0, taetigkeit: "", notiz: "" });
  const [blockFormOffen, setBlockFormOffen] = useState(false);
  const [verlaufOffen, setVerlaufOffen] = useState(false);

  const MONATE = ["","Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  const TAETIGKEITEN = ["Betreuung", "Reinigung", "Kurs", "Büro", "Sonstiges"];

  const laden = async () => {
    const params = new URLSearchParams({ monat, jahr });
    if (!isAdmin) params.append("benutzer_id", session.id || session.benutzer_id || 1);
    const res = await fetch(`${API}/stundenzettel/?${params}`);
    const data = await res.json();
    setStundenzettel(Array.isArray(data) ? data : []);
  };

  const ladenDetail = async (id) => {
    const res = await fetch(`${API}/stundenzettel/${id}`);
    setSzDetail(await res.json());
  };

  useEffect(() => { laden(); }, [monat, jahr]);
  useEffect(() => { if (gewaehlterSZ) ladenDetail(gewaehlterSZ); }, [gewaehlterSZ]);

  const handleErstellen = async () => {
    // benutzer_id/benutzer_name werden serverseitig jetzt aus der echten Sitzung genommen
    // (Sicherheitsfix 2026-08-19, siehe routers_stundenzettel.py) - hier nicht mehr noetig.
    const res = await fetch(`${API}/stundenzettel/?monat=${monat}&jahr=${jahr}`, { method: "POST" });
    if (!res.ok) { alert("Stundenzettel konnte nicht erstellt werden."); return; }
    const data = await res.json();
    setGewaehlterSZ(data.id);
    laden();
  };

  const handleBlockSpeichern = async () => {
    const url = bearbeitenBlock
      ? `${API}/stundenzettel/${gewaehlterSZ}/zeitblock/${bearbeitenBlock.id}`
      : `${API}/stundenzettel/${gewaehlterSZ}/zeitblock`;
    const method = bearbeitenBlock ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(neuerBlock) });
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Speichern fehlgeschlagen."); return; }
    setBlockFormOffen(false); setBearbeitenBlock(null);
    setNeuerBlock({ datum: new Date().toISOString().split("T")[0], beginn: "09:00", ende: "17:00", pause_minuten: 0, taetigkeit: "", notiz: "" });
    ladenDetail(gewaehlterSZ); laden();
  };

  const handleBlockLoeschen = async (blockId) => {
    if (!window.confirm("Zeitblock löschen?")) return;
    const res = await fetch(`${API}/stundenzettel/${gewaehlterSZ}/zeitblock/${blockId}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladenDetail(gewaehlterSZ); laden();
  };

  const handleBearbeiten = (block) => {
    setBearbeitenBlock(block);
    setNeuerBlock({ datum: block.datum, beginn: block.beginn, ende: block.ende, pause_minuten: block.pause_minuten, taetigkeit: block.taetigkeit || "", notiz: block.notiz || "" });
    setBlockFormOffen(true);
  };

  const handleUnterschrift = async (unterschrift) => {
    const endpoint = unterschriftOffen === "ma"
      ? `${API}/stundenzettel/${gewaehlterSZ}/unterschreiben-ma`
      : `${API}/stundenzettel/${gewaehlterSZ}/freigeben`;
    const res = await fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ unterschrift, name: session.name }) });
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Unterschreiben fehlgeschlagen."); return; }
    setUnterschriftOffen(null);
    ladenDetail(gewaehlterSZ); laden();
  };

  const handleFreigabeZurueck = async () => {
    if (!window.confirm("Freigabe zurücknehmen?")) return;
    const res = await fetch(`${API}/stundenzettel/${gewaehlterSZ}/freigabe-zuruecknehmen`, { method: "POST" });
    if (!res.ok) { alert("Freigabe zurücknehmen fehlgeschlagen."); return; }
    ladenDetail(gewaehlterSZ); laden();
  };

  const statusFarbe = (st) => st === "freigegeben" ? THEME.colors.success : st === "eingereicht" ? THEME.colors.warning : THEME.colors.danger;
  const statusLabel = (st) => st === "freigegeben" ? "🟢 Freigegeben" : st === "eingereicht" ? "🟡 Eingereicht" : "🔴 Offen";

  // Stunden berechnen
  const berechneStunden = (beginn, ende, pause) => {
    try {
      const [bh, bm] = beginn.split(":").map(Number);
      const [eh, em] = ende.split(":").map(Number);
      const min = (eh*60+em) - (bh*60+bm) - (parseInt(pause)||0);
      return Math.max(0, min/60).toFixed(2);
    } catch { return "0.00"; }
  };

  // Bloecke nach Datum gruppieren
  const gruppiert = szDetail ? szDetail.bloecke.reduce((acc, b) => {
    const d = b.datum;
    if (!acc[d]) acc[d] = [];
    acc[d].push(b);
    return acc;
  }, {}) : {};

  return (
    <div>
      {unterschriftOffen && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(560px, 95vw)" }}>
            <h2 style={s.modalTitel}>{unterschriftOffen === "ma" ? "Stundenzettel einreichen" : "Stundenzettel freigeben"}</h2>
            <UnterschriftPad onSave={handleUnterschrift} onCancel={() => setUnterschriftOffen(null)} />
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <select style={{ ...s.input, width: 140 }} value={monat} onChange={e => setMonat(parseInt(e.target.value))}>
          {MONATE.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        <select style={{ ...s.input, width: 100 }} value={jahr} onChange={e => setJahr(parseInt(e.target.value))}>
          {[2024,2025,2026,2027].map(j => <option key={j}>{j}</option>)}
        </select>
        {!stundenzettel.find(sz => sz.benutzer_id === (session.id || session.benutzer_id || 1)) && (
          <button onClick={handleErstellen} style={{ ...s.btn, width: "auto" }}>+ Stundenzettel erstellen</button>
        )}
      </div>

      {/* Admin Übersicht */}
      {isAdmin && !gewaehlterSZ && (
        <div style={s.karte}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px" }}>Übersicht {MONATE[monat]} {jahr}</h3>
          {stundenzettel.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine Stundenzettel für diesen Monat.</p>}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {stundenzettel.map(sz => (
              <div key={sz.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: "12px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                  <div>
                    <p style={{ color: THEME.colors.text, fontWeight: 600, margin: "0 0 2px", fontSize: THEME.fontSize.md }}>{sz.benutzer_name}</p>
                    <p style={{ color: THEME.colors.primary, fontWeight: 700, margin: 0, fontSize: THEME.fontSize.base }}>{sz.gesamt_stunden.toFixed(2)} h</p>
                  </div>
                  <span style={{ color: statusFarbe(sz.status), fontWeight: 600, fontSize: THEME.fontSize.base }}>{statusLabel(sz.status)}</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => setGewaehlterSZ(sz.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 12px" }}>Öffnen</button>
                  <a href={`${API}/stundenzettel/${sz.id}/pdf`} target="_blank" rel="noreferrer" style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 12px", textDecoration: "none" }}>📄 PDF</a>
                  {sz.status !== "freigegeben" && (
                    <button onClick={async () => {
                      if (!window.confirm(`Stundenzettel von ${sz.benutzer_name} löschen?`)) return;
                      const res = await fetch(`${API}/stundenzettel/${sz.id}`, { method: "DELETE" });
                      if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
                      laden();
                    }} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 12px", background: THEME.colors.dangerBg }}>✗</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {stundenzettel.length > 0 && stundenzettel.every(s => s.status === "freigegeben") && (
            <div style={{ marginTop: 16, padding: "12px 16px", background: "#22c55e22", border: "1px solid #22c55e", borderRadius: 8 }}>
              <p style={{ color: THEME.colors.success, margin: "0 0 8px", fontWeight: 600 }}>✓ Alle Stundenzettel freigegeben!</p>
              <button onClick={async () => {
                setBuchhaltungLaedt(true);
                try {
                  const res = await fetch(`${API}/stundenzettel/an-buchhaltung-senden`, {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ monat, jahr }),
                  });
                  const data = await res.json();
                  alert(res.ok ? `✓ ${data.message}` : (data.detail || "Versand fehlgeschlagen."));
                } catch (e) {
                  alert("Versand fehlgeschlagen. Bitte Internetverbindung prüfen.");
                }
                setBuchhaltungLaedt(false);
              }} disabled={buchhaltungLaedt} style={{ ...s.btn, width: "auto", opacity: buchhaltungLaedt ? 0.6 : 1 }}>
                {buchhaltungLaedt ? "Wird gesendet…" : "📧 An Buchhaltung senden"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mitarbeiter eigener SZ */}
      {!isAdmin && !gewaehlterSZ && stundenzettel.length === 0 && (
        <div style={{ ...s.karte, textAlign: "center", padding: 40 }}>
          <p style={{ color: THEME.colors.textMuted }}>Noch kein Stundenzettel für {MONATE[monat]} {jahr}.</p>
        </div>
      )}

      {!isAdmin && !gewaehlterSZ && stundenzettel.length > 0 && (
        <div style={s.karte}>
          {stundenzettel.map(sz => (
            <div key={sz.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderTop: "1px solid #2b3d52" }}>
              <div>
                <p style={{ color: THEME.colors.text, fontWeight: 600, margin: "0 0 4px" }}>{MONATE[sz.monat]} {sz.jahr}</p>
                <p style={{ color: THEME.colors.primary, margin: 0 }}>{sz.gesamt_stunden.toFixed(2)} Stunden</p>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ color: statusFarbe(sz.status), fontWeight: 600 }}>{statusLabel(sz.status)}</span>
                <button onClick={() => setGewaehlterSZ(sz.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm }}>Öffnen</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Ansicht */}
      {gewaehlterSZ && szDetail && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <button onClick={() => { setGewaehlterSZ(null); setSzDetail(null); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>← Zurück</button>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span style={{ color: statusFarbe(szDetail.status), fontWeight: 700, fontSize: THEME.fontSize.md, background: THEME.colors.surface, padding: "5px 14px", borderRadius: 20 }}>{statusLabel(szDetail.status)}</span>
              <a href={`${API}/stundenzettel/${gewaehlterSZ}/pdf`} target="_blank" rel="noreferrer" style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 14px", textDecoration: "none" }}>📄 PDF</a>
              {isAdmin && szDetail.status === "freigegeben" && (
                <button onClick={handleFreigabeZurueck} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>↩ Freigabe zurücknehmen</button>
              )}
              {isAdmin && szDetail.status !== "freigegeben" && (
                <button onClick={() => setUnterschriftOffen("admin")} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>✓ Freigeben & unterschreiben</button>
              )}
              {!isAdmin && szDetail.status !== "freigegeben" && (
                <button onClick={() => setUnterschriftOffen("ma")} style={{ ...s.btn, width: "auto", background: THEME.colors.warning }}>✍ Einreichen & unterschreiben</button>
              )}
            </div>
          </div>

          <div style={{ ...s.karte, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ color: THEME.colors.text, margin: 0 }}>{szDetail.benutzer_name} – {MONATE[szDetail.monat]} {szDetail.jahr}</h3>
              <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xl2, fontWeight: 700, margin: 0 }}>{szDetail.gesamt_stunden.toFixed(2)} h gesamt</p>
            </div>

            {/* Unterschriften Status */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 12, borderLeft: `3px solid ${szDetail.ma_unterschrift ? THEME.colors.success : THEME.colors.textMuted}` }}>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "0 0 4px" }}>Mitarbeiter</p>
                {szDetail.ma_unterschrift
                  ? <><p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, fontWeight: 600, margin: "0 0 2px" }}>✓ Unterschrieben</p><p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>{szDetail.ma_unterschrift_name} · {szDetail.ma_unterschrift_datum}</p></>
                  : <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>Noch nicht unterschrieben</p>}
              </div>
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 12, borderLeft: `3px solid ${szDetail.admin_unterschrift ? THEME.colors.success : THEME.colors.textMuted}` }}>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "0 0 4px" }}>Arbeitgeber</p>
                {szDetail.admin_unterschrift
                  ? <><p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, fontWeight: 600, margin: "0 0 2px" }}>✓ Freigegeben</p><p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>{szDetail.admin_unterschrift_name} · {szDetail.admin_unterschrift_datum}</p></>
                  : <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>Noch nicht freigegeben</p>}
              </div>
            </div>

            {/* Zeitblöcke */}
            {Object.keys(gruppiert).sort().map(datum => {
              const bloecke = gruppiert[datum];
              const tagStunden = bloecke.reduce((s, b) => s + b.stunden, 0);
              const d = new Date(datum);
              const tagLabel = d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
              return (
                <div key={datum} style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #2b3d52" }}>
                    <span style={{ color: THEME.colors.primary, fontWeight: 600, fontSize: THEME.fontSize.base }}>{tagLabel}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600 }}>{tagStunden.toFixed(2)} h</span>
                      {szDetail.status !== "freigegeben" && (isAdmin || szDetail.benutzer_id === session.id) && (
                        <button onClick={() => { setBearbeitenBlock(null); setNeuerBlock({ datum, beginn: "09:00", ende: "17:00", pause_minuten: 0, taetigkeit: "", notiz: "" }); setBlockFormOffen(true); }}
                          title="Weiteren Zeitblock für diesen Tag hinzufügen"
                          style={{ ...s.btnGray, width: "auto", padding: "3px 10px", fontSize: THEME.fontSize.xs }}>+ weiterer Block</button>
                      )}
                    </div>
                  </div>
                  {bloecke.map(b => (
                    <div key={b.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 80px", gap: 8, padding: "6px 12px", background: THEME.colors.background, borderRadius: 6, marginBottom: 4, alignItems: "center" }}>
                      <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm }}>{b.beginn} – {b.ende}</span>
                      <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{b.pause_minuten ? `${b.pause_minuten} min Pause` : "Keine Pause"}</span>
                      <span style={{ color: THEME.colors.success, fontWeight: 600, fontSize: THEME.fontSize.sm }}>{b.stunden.toFixed(2)} h</span>
                      <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{b.taetigkeit || "–"}</span>
                      <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{b.notiz || ""}</span>
                      {szDetail.status !== "freigegeben" && (isAdmin || szDetail.benutzer_id === session.id) && (
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => handleBearbeiten(b)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xxs }}>✏</button>
                          <button onClick={() => handleBlockLoeschen(b.id)} style={{ ...s.btn, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xxs, background: THEME.colors.dangerBg }}>✗</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}

            {szDetail.status !== "freigegeben" && (isAdmin || szDetail.benutzer_id === session.id) && (
              <button onClick={() => { setBearbeitenBlock(null); setNeuerBlock({ datum: new Date().toISOString().split("T")[0], beginn: "09:00", ende: "17:00", pause_minuten: 0, taetigkeit: "", notiz: "" }); setBlockFormOffen(true); }}
                style={{ ...s.btn, width: "auto", marginTop: 8 }}>+ Zeitblock hinzufügen</button>
            )}

            {blockFormOffen && (
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginTop: 12 }}>
                <h4 style={{ color: THEME.colors.text, margin: "0 0 12px" }}>{bearbeitenBlock ? "Zeitblock bearbeiten" : "Neuer Zeitblock"}</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
                  <div style={s.inputGroup}><label style={s.label}>Datum</label><input type="date" style={s.input} value={neuerBlock.datum} onChange={e => setNeuerBlock(n => ({...n, datum: e.target.value}))} /></div>
                  <div style={s.inputGroup}><label style={s.label}>Beginn</label><input type="time" style={s.input} value={neuerBlock.beginn} onChange={e => setNeuerBlock(n => ({...n, beginn: e.target.value}))} /></div>
                  <div style={s.inputGroup}><label style={s.label}>Ende</label><input type="time" style={s.input} value={neuerBlock.ende} onChange={e => setNeuerBlock(n => ({...n, ende: e.target.value}))} /></div>
                  <div style={s.inputGroup}><label style={s.label}>Pause (Min)</label><input type="number" style={s.input} value={neuerBlock.pause_minuten} onChange={e => setNeuerBlock(n => ({...n, pause_minuten: parseInt(e.target.value)||0}))} /></div>
                  <div style={s.inputGroup}><label style={s.label}>Tätigkeit</label>
                    <select style={s.input} value={neuerBlock.taetigkeit} onChange={e => setNeuerBlock(n => ({...n, taetigkeit: e.target.value}))}>
                      <option value="">– wählen –</option>
                      {TAETIGKEITEN.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div style={s.inputGroup}><label style={s.label}>Notiz</label><input style={s.input} value={neuerBlock.notiz} onChange={e => setNeuerBlock(n => ({...n, notiz: e.target.value}))} /></div>
                </div>
                <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.base, margin: "8px 0" }}>
                  Berechnet: {berechneStunden(neuerBlock.beginn, neuerBlock.ende, neuerBlock.pause_minuten)} Stunden
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setBlockFormOffen(false); setBearbeitenBlock(null); }} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                  <button onClick={handleBlockSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                </div>
              </div>
            )}
          </div>

          {/* Verlauf */}
          <div style={s.karte}>
            <div onClick={() => setVerlaufOffen(!verlaufOffen)} style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}>
              <h3 style={{ color: THEME.colors.text, margin: 0 }}>Änderungsverlauf</h3>
              <span style={{ color: THEME.colors.textMuted }}>{verlaufOffen ? "▲" : "▼"}</span>
            </div>
            {verlaufOffen && (
              <div style={{ marginTop: 12 }}>
                {szDetail.verlauf.map((v, i) => (
                  <div key={i} style={{ display: "flex", gap: 16, padding: "8px 0", borderTop: "1px solid #2b3d52" }}>
                    <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, minWidth: 120 }}>{v.datum}</span>
                    <span style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.sm, minWidth: 100 }}>{v.benutzer}</span>
                    <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm }}>{v.aktion}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ URLAUBSVERWALTUNG ============
const URLAUB_STATUS = { beantragt: ["Beantragt", THEME.colors.warning], genehmigt: ["Genehmigt", THEME.colors.success], abgelehnt: ["Abgelehnt", THEME.colors.danger], storniert: ["Storniert", THEME.colors.textMuted], geplant: ["Geplant", THEME.colors.textMuted], verbindlich: ["Verbindlich", THEME.colors.success], aufgehoben: ["Aufgehoben", THEME.colors.textMuted] };
const URLAUB_ART_FARBE = { urlaub: THEME.colors.primary, betriebsferien: THEME.colors.accentOrange, schliessung: THEME.colors.textMuted };
const URLAUB_ART_TEXT = { urlaub: "Urlaub", betriebsferien: "Betriebsferien", schliessung: "Geschlossen" };
const URLAUB_HERKUNFT = { antrag: "Antrag", admin: "Vom Admin eingetragen", betriebsferien: "Betriebsferien" };
// Anzeige im Schichtplan/Kalender: Urlaub und Betriebsferien als schwarzes "AK U" (Nutzerwunsch 29.10.2026); Schliessung bleibt wie bisher
const URLAUB_SCHWARZ = { background: "#000", color: "#fff", border: "1px solid #5b6b80" };
const istUrlaubAnzeige = (a) => a.art === "urlaub" || a.art === "betriebsferien";

function urlaubDatumKurz(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return `${WOCHENTAGE_KURZ[(d.getDay() + 6) % 7]} ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
}
function urlaubZeitraum(von, bis) {
  const jahr = bis.slice(0, 4);
  return von === bis ? `${urlaubDatumKurz(von)}${jahr}` : `${urlaubDatumKurz(von)}${von.slice(0, 4) !== jahr ? von.slice(0, 4) : ""} – ${urlaubDatumKurz(bis)}${jahr}`;
}
async function urlaubApi(pfad, methode = "GET", body) {
  const res = await fetch(`${API}/urlaub${pfad}`, { method: methode, headers: body ? { "Content-Type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}
const urlaubFehler = (d) => (typeof d?.detail === "string" ? d.detail : "Aktion fehlgeschlagen.");

function UrlaubChip({ text, farbe }) {
  return <span style={{ background: farbe, color: kontrastTextfarbe(farbe), borderRadius: 12, padding: "2px 10px", fontSize: THEME.fontSize.xs, fontWeight: 700, whiteSpace: "nowrap" }}>{text}</span>;
}

function UrlaubSeite({ session, istAdmin }) {
  const eigeneId = session.id || session.benutzer_id;
  const heute = new Date();
  const [tab, setTab] = useState("uebersicht");
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [benutzerId, setBenutzerId] = useState(eigeneId);
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [konto, setKonto] = useState(null);
  const [antraege, setAntraege] = useState([]);
  const [offene, setOffene] = useState([]);
  const [betriebsferien, setBetriebsferien] = useState([]);
  const [meldung, setMeldung] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [formOffen, setFormOffen] = useState(false);

  useEffect(() => {
    if (!istAdmin) return;
    fetch(`${API}/benutzer/`).then(r => r.json()).then(liste => {
      const aktive = (Array.isArray(liste) ? liste : []).filter(m => m.aktiv);
      const ohneAdmin = aktive.filter(m => m.rolle !== "admin");
      const auswahl = ohneAdmin.length ? ohneAdmin : aktive;
      setMitarbeiter(auswahl);
      if (auswahl.length && !auswahl.find(m => m.id === eigeneId)) setBenutzerId(auswahl[0].id);
    }).catch(() => {});
  }, [istAdmin, eigeneId]);

  const laden = async () => {
    const [k, a, bf] = await Promise.all([
      urlaubApi(`/konto?benutzer_id=${benutzerId}&jahr=${jahr}`),
      urlaubApi(`/antraege?benutzer_id=${benutzerId}&jahr=${jahr}`),
      urlaubApi("/betriebsferien"),
    ]);
    setKonto(k.ok ? k.data : null);
    setAntraege(a.ok && Array.isArray(a.data) ? a.data : []);
    setBetriebsferien(bf.ok && Array.isArray(bf.data) ? bf.data : []);
    if (istAdmin) {
      const o = await urlaubApi("/antraege?alle=true&status=beantragt");
      setOffene(o.ok && Array.isArray(o.data) ? o.data : []);
    }
  };
  useEffect(() => { laden(); }, [benutzerId, jahr]); // eslint-disable-line react-hooks/exhaustive-deps

  const aktion = async (pfad, body, erfolg) => {
    setFehler(null);
    const r = await urlaubApi(pfad, "POST", body || {});
    if (!r.ok) { setFehler(urlaubFehler(r.data)); return false; }
    setMeldung(erfolg);
    await laden();
    return true;
  };

  const tabs = istAdmin
    ? [["uebersicht", "Übersicht"], ["offen", `Offene Anträge${offene.length ? ` (${offene.length})` : ""}`], ["betriebsferien", "Betriebsferien"], ["einstellungen", "Einstellungen"], ["kalender", "Kalender"]]
    : [["uebersicht", "Mein Urlaub"], ["kalender", "Kalender"]];
  const name = konto?.name || session.name;
  const meineBetriebsferien = betriebsferien.filter(b => b.status === "verbindlich" && (!istAdmin || b.benutzer_ids.includes(benutzerId)));

  const antragKarte = (a, mitName) => {
    const [stText, stFarbe] = URLAUB_STATUS[a.status] || [a.status, THEME.colors.textMuted];
    return (
      <div key={a.id} style={{ ...s.karte, padding: 14, borderLeftColor: stFarbe }} data-testid="urlaub-antrag">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ color: THEME.colors.text, fontWeight: 700 }}>{mitName && <span>{a.name} · </span>}{urlaubZeitraum(a.von, a.bis)}</div>
          <UrlaubChip text={stText} farbe={stFarbe} />
        </div>
        <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 4 }}>
          {a.art === "schliessung" ? `${a.tage} Schließtag(e), kein Urlaubsabzug` : `${a.tage} Urlaubstag${a.tage === 1 ? "" : "e"}`} · {URLAUB_HERKUNFT[a.herkunft] || a.herkunft}
          {a.herkunft === "betriebsferien" && a.kommentar ? ` (${a.kommentar})` : ""}
        </div>
        {a.herkunft !== "betriebsferien" && a.kommentar && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 2 }}>„{a.kommentar}"</div>}
        {a.entscheidung_kommentar && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 2 }}>Entscheidung: {a.entscheidung_kommentar}</div>}
        {istAdmin && a.hinweise?.length > 0 && a.status === "beantragt" && (
          <ul style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xs, margin: "8px 0 0", paddingLeft: 18 }}>{a.hinweise.map((h, i) => <li key={i}>{h}</li>)}</ul>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {istAdmin && a.status === "beantragt" && <>
            <button style={{ ...s.btn, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm }} onClick={() => aktion(`/antraege/${a.id}/genehmigen`, {}, "Antrag genehmigt.")}>Genehmigen</button>
            <button style={{ ...s.btnGray, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm }} onClick={() => { const k = window.prompt("Grund der Ablehnung (optional):", ""); if (k !== null) aktion(`/antraege/${a.id}/ablehnen`, { kommentar: k }, "Antrag abgelehnt."); }}>Ablehnen</button>
          </>}
          {!istAdmin && a.status === "beantragt" && <button style={{ ...s.btnGray, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm }} onClick={() => { if (window.confirm("Antrag zurückziehen?")) aktion(`/antraege/${a.id}/stornieren`, {}, "Antrag zurückgezogen."); }}>Zurückziehen</button>}
          {istAdmin && a.status === "genehmigt" && <button style={{ ...s.btnGray, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm }} onClick={() => { if (window.confirm("Genehmigten Urlaub wirklich stornieren? Die Tage werden dem Konto gutgeschrieben.")) aktion(`/antraege/${a.id}/stornieren`, {}, "Urlaub storniert."); }}>Stornieren</button>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        {istAdmin && (
          <select style={{ ...s.input, width: 240 }} value={benutzerId} onChange={e => setBenutzerId(Number(e.target.value))} data-testid="urlaub-person">
            {mitarbeiter.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <select style={{ ...s.input, width: 110 }} value={jahr} onChange={e => setJahr(Number(e.target.value))} data-testid="urlaub-jahr">
          {[heute.getFullYear() - 1, heute.getFullYear(), heute.getFullYear() + 1, heute.getFullYear() + 2, heute.getFullYear() + 3].map(j => <option key={j} value={j}>{j}</option>)}
        </select>
        <button style={{ ...s.btn, width: "auto", marginLeft: "auto" }} onClick={() => setFormOffen(true)} data-testid="urlaub-neu">{istAdmin ? "+ Urlaub eintragen" : "+ Urlaub beantragen"}</button>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ ...s.btnGray, width: "auto", padding: "8px 16px", fontSize: THEME.fontSize.sm, background: tab === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: tab === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted) }}>{label}</button>
        ))}
      </div>

      {meldung && <p style={{ color: THEME.colors.success, margin: "0 0 12px" }} data-testid="urlaub-meldung">✓ {meldung}</p>}
      {fehler && <p style={{ color: THEME.colors.danger, margin: "0 0 12px" }} data-testid="urlaub-fehler">{fehler}</p>}

      {tab === "uebersicht" && (
        <div>
          {konto && !konto.anspruch_hinterlegt && <p style={{ color: THEME.colors.warning }}>Für {jahr} ist noch kein Jahresanspruch hinterlegt{istAdmin ? " – bitte unter „Einstellungen“ eintragen." : "."}</p>}
          {konto && konto.anspruch_hinterlegt && !konto.geprueft && istAdmin && <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm }}>Der Anspruch {jahr} ist als „geplant" eingetragen und noch nicht rechtlich bestätigt.</p>}
          {konto && !konto.modell_hinterlegt && istAdmin && <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm }}>Für {name} ist noch kein Arbeitszeitmodell hinterlegt (Einstellungen).</p>}
          {konto && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 20 }} data-testid="urlaub-konto">
              {[["Jahresanspruch", konto.anspruch_tage], ["Resturlaub Vorjahr", konto.uebertrag_tage], ["Genommen", konto.genommen], ["Geplant (genehmigt)", konto.geplant], ["Beantragt", konto.beantragt]].map(([l, w]) => (
                <div key={l} style={{ ...s.karte, padding: 12 }}><p style={{ ...s.karteLabel, fontSize: THEME.fontSize.xs }}>{l}</p><div style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxl, fontWeight: 700 }}>{w}</div></div>
              ))}
              <div style={{ ...s.karte, padding: 12, borderLeftColor: konto.rest > 0 ? THEME.colors.success : THEME.colors.warning }}>
                <p style={{ ...s.karteLabel, fontSize: THEME.fontSize.xs }}>Resturlaub {jahr}</p>
                <div style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxl, fontWeight: 700 }} data-testid="urlaub-rest">{konto.rest}</div>
                {konto.davon_betriebsferien > 0 && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>davon {konto.davon_betriebsferien} Betriebsferien eingerechnet</div>}
                {konto.schliesstage_ohne_abzug > 0 && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{konto.schliesstage_ohne_abzug} Schließtag(e) ohne Abzug</div>}
              </div>
            </div>
          )}
          {meineBetriebsferien.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 8 }}>Betriebsferien / Schließtage</h3>
              {meineBetriebsferien.map(b => <div key={b.id} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginBottom: 4 }}>🏢 {b.titel}: {urlaubZeitraum(b.von, b.bis)} {b.art === "schliessung" ? "(kein Urlaubsabzug)" : "(wird als Urlaub angerechnet)"}</div>)}
            </div>
          )}
          <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 8 }}>{istAdmin ? `Anträge und Urlaub von ${name}` : "Meine Anträge"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
            {antraege.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine Einträge für {jahr}.</p>}
            {antraege.map(a => antragKarte(a, false))}
          </div>
        </div>
      )}

      {tab === "offen" && istAdmin && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
          {offene.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine offenen Anträge.</p>}
          {offene.map(a => antragKarte(a, true))}
        </div>
      )}

      {tab === "betriebsferien" && istAdmin && <UrlaubBetriebsferien mitarbeiter={mitarbeiter} liste={betriebsferien} onGeaendert={laden} setMeldung={setMeldung} setFehler={setFehler} />}
      {tab === "einstellungen" && istAdmin && <UrlaubEinstellungen mitarbeiter={mitarbeiter} benutzerId={benutzerId} jahr={jahr} konto={konto} onGeaendert={laden} setMeldung={setMeldung} setFehler={setFehler} />}
      {tab === "kalender" && <UrlaubKalender />}

      {formOffen && (
        <UrlaubFormDialog istAdmin={istAdmin} benutzerId={benutzerId} name={name} onAbbrechen={() => setFormOffen(false)}
          onFertig={(txt) => { setFormOffen(false); setFehler(null); setMeldung(txt); laden(); }} />
      )}
    </div>
  );
}

function UrlaubFormDialog({ istAdmin, benutzerId, name, onAbbrechen, onFertig }) {
  const [von, setVon] = useState("");
  const [bis, setBis] = useState("");
  const [kommentar, setKommentar] = useState("");
  const [vorschau, setVorschau] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [detailsOffen, setDetailsOffen] = useState(false);

  useEffect(() => {
    setVorschau(null);
    if (!von || !bis || bis < von) return;
    let abgebrochen = false;
    urlaubApi("/berechnen", "POST", { benutzer_id: benutzerId, von, bis }).then(r => { if (!abgebrochen) setVorschau(r.ok ? r.data : { buchbar: false, fehler: urlaubFehler(r.data), tage: [], hinweise: [] }); });
    return () => { abgebrochen = true; };
  }, [von, bis, benutzerId]);

  const speichern = async () => {
    setFehler(null); setLaedt(true);
    const r = await urlaubApi("/antraege", "POST", { benutzer_id: benutzerId, von, bis, kommentar });
    setLaedt(false);
    if (!r.ok) { setFehler(urlaubFehler(r.data)); return; }
    onFertig(istAdmin ? `Urlaub für ${name} eingetragen (${r.data.tage} Tage).` : `Antrag gestellt (${r.data.tage} Tage).`);
  };

  return (
    <div style={s.modalBg}>
      <div style={{ ...s.modal, width: "min(520px, 95vw)", padding: "1.75rem" }} data-testid="urlaub-dialog">
        <h2 style={s.heading}>{istAdmin ? `Urlaub eintragen – ${name}` : "Urlaub beantragen"}</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={s.label}>Von</label><input type="date" style={s.input} value={von} onChange={e => { setVon(e.target.value); if (!bis || bis < e.target.value) setBis(e.target.value); }} data-testid="urlaub-von" /></div>
          <div><label style={s.label}>Bis</label><input type="date" style={s.input} value={bis} min={von} onChange={e => setBis(e.target.value)} data-testid="urlaub-bis" /></div>
        </div>
        <label style={{ ...s.label, marginTop: 12 }}>Kommentar (optional)</label>
        <input style={s.input} value={kommentar} onChange={e => setKommentar(e.target.value)} />
        {vorschau && (
          <div style={{ marginTop: 16 }} data-testid="urlaub-vorschau">
            {vorschau.buchbar && <div style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 700 }}>{vorschau.urlaubstage} Urlaubstag{vorschau.urlaubstage === 1 ? "" : "e"}</div>}
            {vorschau.fehler && <p style={{ color: THEME.colors.danger, margin: "4px 0" }}>{vorschau.fehler}</p>}
            {vorschau.guthaben?.map(g => <div key={g.jahr} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>Resturlaub {g.jahr}: {g.rest_vorher} → {g.rest_nachher}</div>)}
            {vorschau.hinweise?.length > 0 && <ul style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, paddingLeft: 18 }}>{vorschau.hinweise.map((h, i) => <li key={i}>{h}</li>)}</ul>}
            {vorschau.tage?.length > 0 && (
              <>
                <span onClick={() => setDetailsOffen(!detailsOffen)} style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, cursor: "pointer", textDecoration: "underline" }}>{detailsOffen ? "Tage ausblenden" : "Welche Tage zählen?"}</span>
                {detailsOffen && (
                  <div style={{ marginTop: 6, maxHeight: 180, overflowY: "auto" }}>
                    {vorschau.tage.map(t => <div key={t.datum} style={{ fontSize: THEME.fontSize.xs, color: t.ergebnis === "zaehlt" ? THEME.colors.text : THEME.colors.textMuted }}>{urlaubDatumKurz(t.datum)} – {t.ergebnis === "zaehlt" ? "zählt 1 Tag" : `zählt nicht (${t.grund})`}</div>)}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}
        <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
          <button onClick={onAbbrechen} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
          <button onClick={speichern} disabled={laedt || !vorschau?.buchbar} style={{ ...s.btn, width: "auto", opacity: !vorschau?.buchbar ? 0.5 : 1 }} data-testid="urlaub-speichern">{laedt ? "Speichert…" : istAdmin ? "Eintragen" : "Beantragen"}</button>
        </div>
      </div>
    </div>
  );
}

function UrlaubKalender() {
  const heute = new Date();
  const [monat, setMonat] = useState(heute.getMonth() + 1);
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [eintraege, setEintraege] = useState([]);
  const start = montagDerWoche(new Date(jahr, monat - 1, 1));
  const tage = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  useEffect(() => {
    urlaubApi(`/abwesenheiten?von=${isoDatum(tage[0])}&bis=${isoDatum(tage[41])}`).then(r => setEintraege(r.ok && Array.isArray(r.data) ? r.data : []));
  }, [monat, jahr]); // eslint-disable-line react-hooks/exhaustive-deps
  const wechsel = (delta) => { const d = new Date(jahr, monat - 1 + delta, 1); setMonat(d.getMonth() + 1); setJahr(d.getFullYear()); };
  return (
    <div data-testid="urlaub-kalender">
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
        <button style={{ ...s.btnGray, width: "auto", padding: "8px 14px" }} onClick={() => wechsel(-1)}>◀</button>
        <b style={{ color: THEME.colors.text, minWidth: 140, textAlign: "center" }}>{SCHICHTPLAN_MONATE[monat]} {jahr}</b>
        <button style={{ ...s.btnGray, width: "auto", padding: "8px 14px" }} onClick={() => wechsel(1)}>▶</button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, fontSize: THEME.fontSize.xs, color: THEME.colors.textMuted, flexWrap: "wrap" }}>
          {Object.keys(URLAUB_ART_TEXT).map(k => <span key={k}><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: URLAUB_ART_FARBE[k], marginRight: 4 }} />{URLAUB_ART_TEXT[k]}</span>)}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {WOCHENTAGE_KURZ.map(w => <div key={w} style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, textAlign: "center" }}>{w}</div>)}
        {tage.map(tag => {
          const iso = isoDatum(tag);
          const imMonat = tag.getMonth() === monat - 1;
          const heutigerTag = iso === isoDatum(heute);
          const heutige = eintraege.filter(e => e.datum === iso);
          return (
            <div key={iso} style={{ background: imMonat ? THEME.colors.surface : "transparent", borderRadius: 6, padding: 4, minHeight: 56, opacity: imMonat ? 1 : 0.35, outline: heutigerTag ? `1px solid ${THEME.colors.primary}` : "none" }}>
              <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.tiny }}>{tag.getDate()}</div>
              {heutige.map((e, i) => <div key={i} title={`${e.name} – ${URLAUB_ART_TEXT[e.art]}`} data-testid="urlaub-kal-chip" style={{ background: URLAUB_ART_FARBE[e.art], color: kontrastTextfarbe(URLAUB_ART_FARBE[e.art]), borderRadius: 4, fontSize: THEME.fontSize.tiny, fontWeight: 700, padding: "1px 4px", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.kuerzel || e.name.split(" ")[0]}</div>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UrlaubBetriebsferien({ mitarbeiter, liste, onGeaendert, setMeldung, setFehler }) {
  const leer = { titel: "", von: "", bis: "", art: "urlaub", benutzer_ids: [], begruendung: "" };
  const [form, setForm] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [bestaetigen, setBestaetigen] = useState(null);
  const [geprueft, setGeprueft] = useState(false);
  const [begruendung, setBegruendung] = useState("");

  const anlegen = async () => {
    setFehler(null);
    const r = await urlaubApi("/betriebsferien", "POST", form);
    if (!r.ok) { setFehler(urlaubFehler(r.data)); return; }
    setForm(null); setMeldung("Betriebsferien als „geplant“ angelegt – es wurde noch nichts gebucht."); onGeaendert();
  };
  const zeigeVorschau = async (b) => { const r = await urlaubApi(`/betriebsferien/${b.id}/vorschau`); if (r.ok) setVorschau(r.data); else setFehler(urlaubFehler(r.data)); };
  const verbindlich = async () => {
    setFehler(null);
    const r = await urlaubApi(`/betriebsferien/${bestaetigen.id}/verbindlich`, "POST", { rechtlich_geprueft: geprueft, begruendung });
    if (!r.ok) { setFehler(urlaubFehler(r.data)); setBestaetigen(null); return; }
    setBestaetigen(null); setGeprueft(false); setBegruendung(""); setMeldung("Betriebsferien sind jetzt verbindlich und im Urlaubskonto gebucht."); onGeaendert();
  };
  const einfach = async (pfad, methode, text, body) => { setFehler(null); const r = await urlaubApi(pfad, methode, body); if (!r.ok) { setFehler(urlaubFehler(r.data)); return; } setMeldung(text); onGeaendert(); };

  return (
    <div data-testid="urlaub-bf">
      <button style={{ ...s.btn, width: "auto", marginBottom: 14 }} onClick={() => setForm({ ...leer })} data-testid="urlaub-bf-neu">+ Betriebsferien / Pflichturlaub</button>
      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 0 }}>Neue Einträge sind zuerst nur „geplant" (nichts wird gebucht). Verbindlich werden sie erst mit deiner ausdrücklichen Bestätigung; reicht bei jemandem das Guthaben nicht, wird für niemanden gebucht.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
        {liste.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Betriebsferien angelegt.</p>}
        {liste.map(b => {
          const [stText, stFarbe] = URLAUB_STATUS[b.status] || [b.status, THEME.colors.textMuted];
          return (
            <div key={b.id} style={{ ...s.karte, padding: 14, borderLeftColor: stFarbe }} data-testid="urlaub-bf-karte">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><b style={{ color: THEME.colors.text }}>{b.titel}</b><UrlaubChip text={stText} farbe={stFarbe} /></div>
              <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{urlaubZeitraum(b.von, b.bis)} · {b.art === "schliessung" ? "Schließung ohne Urlaubsabzug" : "Pflichturlaub"}</div>
              <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{b.benutzer_namen.join(", ")}</div>
              {b.status === "verbindlich" && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>Bestätigt von {b.bestaetigt_von}{b.begruendung ? ` – ${b.begruendung}` : ""}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {b.status === "geplant" && <>
                  <button style={{ ...s.btnGray, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm }} onClick={() => zeigeVorschau(b)}>Vorschau</button>
                  <button style={{ ...s.btn, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm }} onClick={() => { setBestaetigen(b); setGeprueft(false); setBegruendung(b.begruendung || ""); }} data-testid="urlaub-bf-verbindlich">Verbindlich setzen…</button>
                  <button style={{ ...s.btnGray, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm }} onClick={() => { if (window.confirm("Geplante Betriebsferien löschen?")) einfach(`/betriebsferien/${b.id}`, "DELETE", "Gelöscht."); }}>Löschen</button>
                </>}
                {b.status === "verbindlich" && <button style={{ ...s.btnGray, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm }} onClick={() => { if (window.confirm("Verbindliche Betriebsferien aufheben? Die Urlaubstage werden gutgeschrieben.")) einfach(`/betriebsferien/${b.id}/aufheben`, "POST", "Aufgehoben, Tage gutgeschrieben.", {}); }}>Aufheben</button>}
              </div>
            </div>
          );
        })}
      </div>

      {form && (
        <div style={s.modalBg}><div style={{ ...s.modal, width: "min(520px, 95vw)", padding: "1.75rem" }}>
          <h2 style={s.heading}>Betriebsferien / Pflichturlaub</h2>
          <label style={s.label}>Titel</label><input style={s.input} value={form.titel} onChange={e => setForm({ ...form, titel: e.target.value })} placeholder="z. B. Heiligabend" data-testid="urlaub-bf-titel" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            <div><label style={s.label}>Von</label><input type="date" style={s.input} value={form.von} onChange={e => setForm({ ...form, von: e.target.value, bis: form.bis && form.bis >= e.target.value ? form.bis : e.target.value })} data-testid="urlaub-bf-von" /></div>
            <div><label style={s.label}>Bis</label><input type="date" style={s.input} value={form.bis} min={form.von} onChange={e => setForm({ ...form, bis: e.target.value })} data-testid="urlaub-bf-bis" /></div>
          </div>
          <label style={{ ...s.label, marginTop: 12 }}>Art</label>
          <select style={s.input} value={form.art} onChange={e => setForm({ ...form, art: e.target.value })}>
            <option value="urlaub">Pflichturlaub (wird vom Urlaub abgezogen)</option>
            <option value="schliessung">Schließung ohne Urlaubsabzug</option>
          </select>
          <label style={{ ...s.label, marginTop: 12 }}>Betrifft</label>
          <div style={{ maxHeight: 130, overflowY: "auto" }}>
            {mitarbeiter.map(m => (
              <label key={m.id} style={{ display: "flex", gap: 8, color: THEME.colors.text, padding: "3px 0" }}>
                <input type="checkbox" checked={form.benutzer_ids.includes(m.id)} onChange={e => setForm({ ...form, benutzer_ids: e.target.checked ? [...form.benutzer_ids, m.id] : form.benutzer_ids.filter(x => x !== m.id) })} data-testid={`urlaub-bf-person-${m.id}`} /> {m.name}
              </label>
            ))}
          </div>
          <label style={{ ...s.label, marginTop: 12 }}>Grund (z. B. „ComeFit geschlossen")</label>
          <input style={s.input} value={form.begruendung} onChange={e => setForm({ ...form, begruendung: e.target.value })} />
          <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
            <button style={{ ...s.btnGray, width: "auto" }} onClick={() => setForm(null)}>Abbrechen</button>
            <button style={{ ...s.btn, width: "auto" }} onClick={anlegen} data-testid="urlaub-bf-speichern">Als „geplant" speichern</button>
          </div>
        </div></div>
      )}

      {vorschau && (
        <div style={s.modalBg}><div style={{ ...s.modal, width: "min(560px, 95vw)", padding: "1.75rem" }} data-testid="urlaub-bf-vorschau">
          <h2 style={s.heading}>Vorschau: {vorschau.eintrag.titel}</h2>
          {vorschau.personen.map(p => (
            <div key={p.benutzer_id} style={{ ...s.karte, padding: 12, marginBottom: 8, borderLeftColor: p.buchbar ? THEME.colors.success : THEME.colors.danger }}>
              <b style={{ color: THEME.colors.text }}>{p.name}</b>: {p.tage} Tag{p.tage === 1 ? "" : "e"}
              {p.rest_vorher != null && <span style={{ color: THEME.colors.textSecondary }}> · Resturlaub {p.rest_vorher} → {p.rest_nachher}</span>}
              {p.fehler && <div style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{p.fehler}</div>}
              {p.hinweise.map((h, i) => <div key={i} style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xs }}>{h}</div>)}
            </div>
          ))}
          <button style={{ ...s.btnGray, width: "auto", marginTop: 8 }} onClick={() => setVorschau(null)}>Schließen</button>
        </div></div>
      )}

      {bestaetigen && (
        <div style={s.modalBg}><div style={{ ...s.modal, width: "min(520px, 95vw)", padding: "1.75rem" }} data-testid="urlaub-bf-bestaetigung">
          <h2 style={s.heading}>„{bestaetigen.titel}" verbindlich setzen</h2>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{urlaubZeitraum(bestaetigen.von, bestaetigen.bis)} · {bestaetigen.benutzer_namen.join(", ")}. Die Tage werden sofort im Urlaubskonto gebucht. Reicht bei einer Person das Guthaben nicht, wird für niemanden gebucht.</p>
          <label style={{ display: "flex", gap: 8, color: THEME.colors.text, fontSize: THEME.fontSize.sm, margin: "12px 0" }}>
            <input type="checkbox" checked={geprueft} onChange={e => setGeprueft(e.target.checked)} data-testid="urlaub-bf-geprueft" />
            <span>Ich habe die rechtlichen Voraussetzungen geprüft (angemessene und nachweisbare Ankündigung, Urlaubswünsche berücksichtigt, dringende betriebliche Gründe, ausreichend frei planbarer Urlaub).</span>
          </label>
          <label style={s.label}>Begründung (wird gespeichert)</label>
          <input style={s.input} value={begruendung} onChange={e => setBegruendung(e.target.value)} data-testid="urlaub-bf-begruendung" />
          <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
            <button style={{ ...s.btnGray, width: "auto" }} onClick={() => setBestaetigen(null)}>Abbrechen</button>
            <button style={{ ...s.btn, width: "auto", opacity: geprueft && begruendung.trim() ? 1 : 0.5 }} disabled={!geprueft || !begruendung.trim()} onClick={verbindlich} data-testid="urlaub-bf-bestaetigen">Verbindlich setzen und buchen</button>
          </div>
        </div></div>
      )}
    </div>
  );
}

function UrlaubEinstellungen({ mitarbeiter, benutzerId, jahr, konto, onGeaendert, setMeldung, setFehler }) {
  const [anspruch, setAnspruch] = useState("");
  const [uebertrag, setUebertrag] = useState("");
  const [geprueft, setGeprueft] = useState(false);
  const [notiz, setNotiz] = useState("");
  const [eintritt, setEintritt] = useState("");
  const [austritt, setAustritt] = useState("");
  const [vorschlag, setVorschlag] = useState(null);
  const [modelle, setModelle] = useState([]);
  const [neuAb, setNeuAb] = useState("");
  const [neuTage, setNeuTage] = useState([0, 2, 3, 4]);
  const [schuleIds, setSchuleIds] = useState([]);
  const [verlauf, setVerlauf] = useState([]);

  useEffect(() => {
    if (!konto) return;
    setAnspruch(konto.anspruch_hinterlegt ? String(konto.anspruch_tage) : "");
    setUebertrag(String(konto.uebertrag_tage || 0)); setGeprueft(!!konto.geprueft); setNotiz(konto.notiz || ""); setVorschlag(null);
  }, [konto?.benutzer_id, konto?.jahr, konto?.anspruch_tage, konto?.uebertrag_tage, konto?.geprueft]); // eslint-disable-line react-hooks/exhaustive-deps

  const ladeNebenbei = async () => {
    const [m, v, sz] = await Promise.all([urlaubApi(`/modelle?benutzer_id=${benutzerId}`), urlaubApi(`/verlauf?benutzer_id=${benutzerId}&limit=30`), urlaubApi("/schule-zuordnung")]);
    setModelle(m.ok ? m.data : []); setVerlauf(v.ok ? v.data : []); setSchuleIds(sz.ok ? sz.data.benutzer_ids : []);
  };
  useEffect(() => { ladeNebenbei(); }, [benutzerId, konto?.rest, konto?.anspruch_tage]); // eslint-disable-line react-hooks/exhaustive-deps

  const speichereAnspruch = async () => {
    setFehler(null);
    const r = await urlaubApi("/anspruch", "PUT", { benutzer_id: benutzerId, jahr, anspruch_tage: Number(anspruch), uebertrag_tage: Number(uebertrag || 0), geprueft, notiz });
    if (!r.ok) { setFehler(urlaubFehler(r.data)); return; }
    setMeldung(`Anspruch ${jahr} gespeichert.`); onGeaendert();
  };
  const frageVorschlag = async () => {
    setFehler(null);
    const q = new URLSearchParams({ benutzer_id: benutzerId, jahr });
    if (eintritt) q.append("eintritt", eintritt);
    if (austritt) q.append("austritt", austritt);
    const r = await urlaubApi(`/mindesturlaub?${q}`);
    if (!r.ok) { setFehler(urlaubFehler(r.data)); return; }
    setVorschlag(r.data);
  };
  const speichereModell = async () => {
    setFehler(null);
    const r = await urlaubApi("/modelle", "POST", { benutzer_id: benutzerId, gueltig_ab: neuAb, arbeitstage: neuTage });
    if (!r.ok) { setFehler(urlaubFehler(r.data)); return; }
    setMeldung("Arbeitszeitmodell gespeichert."); setNeuAb(""); onGeaendert(); ladeNebenbei();
  };
  const speichereSchule = async (ids) => { setSchuleIds(ids); const r = await urlaubApi("/schule-zuordnung", "PUT", { benutzer_ids: ids }); if (!r.ok) setFehler(urlaubFehler(r.data)); else setMeldung("Berufsschul-Zuordnung gespeichert."); };
  const box = { ...s.karte, padding: 16, marginBottom: 14 };
  const tageText = (liste) => liste.map(t => WOCHENTAGE_KURZ[t]).join(", ");

  return (
    <div data-testid="urlaub-einstellungen">
      <div style={box}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Jahresanspruch {jahr}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <div><label style={s.label}>Jahresanspruch (Tage)</label><input type="number" min="0" style={s.input} value={anspruch} onChange={e => setAnspruch(e.target.value)} data-testid="urlaub-anspruch" /></div>
          <div><label style={s.label}>Resturlaub aus Vorjahr</label><input type="number" min="0" style={s.input} value={uebertrag} onChange={e => setUebertrag(e.target.value)} data-testid="urlaub-uebertrag" /></div>
        </div>
        <label style={{ display: "flex", gap: 8, color: THEME.colors.text, margin: "10px 0", fontSize: THEME.fontSize.sm }}><input type="checkbox" checked={geprueft} onChange={e => setGeprueft(e.target.checked)} /> Anspruch rechtlich geprüft / endgültig (sonst „geplant")</label>
        <input style={s.input} value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="Notiz (z. B. Vertrag, Rechenweg)" />
        <button style={{ ...s.btn, width: "auto", marginTop: 12 }} onClick={speichereAnspruch} disabled={anspruch === ""} data-testid="urlaub-anspruch-speichern">Anspruch speichern</button>
        <details style={{ marginTop: 14 }}>
          <summary style={{ color: THEME.colors.textSecondary, cursor: "pointer", fontSize: THEME.fontSize.sm }}>Vorschlag für den gesetzlichen Mindesturlaub</summary>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
            <div><label style={s.label}>Eintritt</label><input type="date" style={s.input} value={eintritt} onChange={e => setEintritt(e.target.value)} /></div>
            <div><label style={s.label}>Austritt / Ausbildungsende</label><input type="date" style={s.input} value={austritt} onChange={e => setAustritt(e.target.value)} /></div>
          </div>
          <button style={{ ...s.btnGray, width: "auto", marginTop: 8 }} onClick={frageVorschlag}>Vorschlag berechnen</button>
          {vorschlag && (
            <div style={{ marginTop: 8, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }} data-testid="urlaub-vorschlag">
              <b style={{ color: THEME.colors.text }}>{vorschlag.vorschlag_tage} Tage</b>{vorschlag.pruefpflichtig && <span style={{ color: THEME.colors.warning }}> · rechtlich prüfen</span>}
              <div>{vorschlag.begruendung}</div>
              {vorschlag.hinweise.map((h, i) => <div key={i} style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xs }}>{h}</div>)}
              <div style={{ fontSize: THEME.fontSize.xs, color: THEME.colors.textMuted }}>{vorschlag.hinweis_vertrag} Es wird nichts automatisch gespeichert.</div>
              <button style={{ ...s.btnGray, width: "auto", marginTop: 6, padding: "6px 12px" }} onClick={() => setAnspruch(String(vorschlag.vorschlag_tage))}>Wert ins Feld übernehmen (noch nicht gespeichert)</button>
            </div>
          )}
        </details>
      </div>

      <div style={box}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Regelmäßige Arbeitstage</h3>
        {modelle.length === 0 && <p style={{ color: THEME.colors.warning }}>Noch kein Modell – ohne Modell kann kein Urlaub berechnet werden.</p>}
        {modelle.map(m => <div key={m.id} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }} data-testid="urlaub-modell">ab {new Date(`${m.gueltig_ab}T00:00:00`).toLocaleDateString("de-DE")}: {tageText(m.arbeitstage)}</div>)}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
          <input type="date" style={{ ...s.input, width: 170 }} value={neuAb} onChange={e => setNeuAb(e.target.value)} data-testid="urlaub-modell-ab" />
          {WOCHENTAGE_KURZ.map((w, i) => (
            <label key={w} style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm }}><input type="checkbox" checked={neuTage.includes(i)} onChange={e => setNeuTage(e.target.checked ? [...neuTage, i] : neuTage.filter(x => x !== i))} /> {w}</label>
          ))}
          <button style={{ ...s.btnGray, width: "auto" }} onClick={speichereModell} disabled={!neuAb || neuTage.length === 0} data-testid="urlaub-modell-speichern">Neues Modell ab Datum</button>
        </div>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginBottom: 0 }}>Frühere Modelle bleiben erhalten; bereits gebuchter Urlaub wird nie neu berechnet.</p>
      </div>

      <div style={box}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Berufsschule betrifft</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 0 }}>Die Kalendereinträge heißen nur „Schule". Wer hier angehakt ist, bekommt bei Urlaubsanträgen an diesen Tagen einen eindeutigen Hinweis (keine Sperre).</p>
        {mitarbeiter.map(m => <label key={m.id} style={{ display: "inline-flex", gap: 6, color: THEME.colors.text, marginRight: 14, fontSize: THEME.fontSize.sm }}><input type="checkbox" checked={schuleIds.includes(m.id)} onChange={e => speichereSchule(e.target.checked ? [...schuleIds, m.id] : schuleIds.filter(x => x !== m.id))} /> {m.name}</label>)}
      </div>

      <div style={box}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Änderungsverlauf</h3>
        {verlauf.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Einträge.</p>}
        {verlauf.map((v, i) => <div key={i} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.xs, marginBottom: 3 }}>{new Date(`${v.zeit}Z`).toLocaleString("de-DE")} · {v.akteur} · {v.aktion}</div>)}
      </div>
    </div>
  );
}

// ============ SCHICHTPLAN ============

const SCHICHT_FARBEN = [THEME.colors.primary, THEME.colors.accentPurple, THEME.colors.accentCyan, THEME.colors.accentOrange, THEME.colors.accentPink, THEME.colors.accentGreenDark, THEME.colors.accentViolet];
function mitarbeiterFarbe(name) {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return SCHICHT_FARBEN[hash % SCHICHT_FARBEN.length];
}
function montagDerWoche(datum) {
  const d = new Date(datum);
  const tag = d.getDay(); // 0=So
  const versatz = tag === 0 ? -6 : 1 - tag;
  d.setDate(d.getDate() + versatz);
  d.setHours(0, 0, 0, 0);
  return d;
}
function isoDatum(d) {
  const jahr = d.getFullYear();
  const monat = String(d.getMonth() + 1).padStart(2, "0");
  const tag = String(d.getDate()).padStart(2, "0");
  return `${jahr}-${monat}-${tag}`;
}
const WOCHENTAGE_KURZ = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const LIMIT_STATUS_LABEL = { ok: "Im Rahmen", warnung: "Verdienstgrenze fast erreicht", ueberschritten: "Verdienstgrenze überschritten", not_applicable: "–" };
const LIMIT_STATUS_FARBE = { ok: THEME.colors.success, warnung: THEME.colors.warning, ueberschritten: THEME.colors.danger, not_applicable: THEME.colors.textMuted };
function geplanterLohnText(e) {
  if (e.planned_wage != null) return `${e.planned_wage.toFixed(2)} €`;
  if (e.employment_type === "eigentuemer") return "Eigentümer";
  return "–";
}

const SCHICHTPLAN_MONATE = ["", "Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

function SchichtplanSeite({ session, istAdmin }) {
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const pruefen = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", pruefen);
    return () => window.removeEventListener("resize", pruefen);
  }, []);

  const [ansicht, setAnsicht] = useState("woche");
  const [wochenStart, setWochenStart] = useState(montagDerWoche(new Date()));
  const [monat, setMonat] = useState(new Date().getMonth() + 1);
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [mitarbeiterListe, setMitarbeiterListe] = useState([]);
  const [mitarbeiterFilter, setMitarbeiterFilter] = useState("");
  const [nurAktive, setNurAktive] = useState(true);
  const [schichten, setSchichten] = useState([]);
  const [formOffen, setFormOffen] = useState(false);
  const [bearbeiteSchicht, setBearbeiteSchicht] = useState(null);
  const [neuesDatum, setNeuesDatum] = useState(null);
  const [kopierenOffen, setKopierenOffen] = useState(false);
  const [vorlagenOffen, setVorlagenOffen] = useState(false);
  const [vorlagen, setVorlagen] = useState([]);
  const [monatsauswertung, setMonatsauswertung] = useState(null);
  const [meldung, setMeldung] = useState(null);
  const [quelle, setQuelle] = useState("google");
  const [abgleichLaeuft, setAbgleichLaeuft] = useState(false);
  const [abwesenheiten, setAbwesenheiten] = useState([]);
  const ausGoogle = quelle === "google";

  const ladeVorlagen = () => {
    fetch(`${API}/schichtplan/vorlagen`).then(r => r.json()).then(d => setVorlagen(Array.isArray(d) ? d : [])).catch(() => {});
  };

  useEffect(() => {
    fetch(`${API}/benutzer/`).then(r => r.json()).then(setMitarbeiterListe).catch(() => {});
    fetch(`${API}/schichtplan/quelle`).then(r => r.json()).then(d => d?.quelle && setQuelle(d.quelle)).catch(() => {});
    ladeVorlagen();
  }, []);

  const tage = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(wochenStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const ladeSchichten = () => {
    let von, bis;
    if (ansicht === "woche") {
      von = isoDatum(tage[0]); bis = isoDatum(tage[6]);
    } else {
      von = `${jahr}-${String(monat).padStart(2, "0")}-01`;
      const letzterTag = new Date(jahr, monat, 0).getDate();
      bis = `${jahr}-${String(monat).padStart(2, "0")}-${String(letzterTag).padStart(2, "0")}`;
    }
    const params = new URLSearchParams({ date_from: von, date_to: bis, nur_aktive: nurAktive });
    if (mitarbeiterFilter) params.append("employee_id", mitarbeiterFilter);
    fetch(`${API}/schichtplan/schichten?${params}`).then(r => r.json()).then(d => setSchichten(Array.isArray(d) ? d : [])).catch(() => setSchichten([]));
    // Genehmigter Urlaub/Betriebsferien: nur Markierung (nichts davon wird als Schicht gespeichert)
    fetch(`${API}/urlaub/abwesenheiten?von=${von}&bis=${bis}`).then(r => r.json()).then(d => setAbwesenheiten(Array.isArray(d) ? d : [])).catch(() => setAbwesenheiten([]));
  };
  useEffect(ladeSchichten, [ansicht, wochenStart, monat, jahr, mitarbeiterFilter, nurAktive, quelle]); // eslint-disable-line react-hooks/exhaustive-deps

  const googleJetztAbgleichen = async () => {
    const von = ansicht === "woche" ? isoDatum(tage[0]) : `${jahr}-${String(monat).padStart(2, "0")}-01`;
    const bis = ansicht === "woche" ? isoDatum(tage[6]) : `${jahr}-${String(monat).padStart(2, "0")}-${String(new Date(jahr, monat, 0).getDate()).padStart(2, "0")}`;
    setAbgleichLaeuft(true);
    try {
      const res = await fetch(`${API}/schichtplan/google-abgleich?date_from=${von}&date_to=${bis}`, { method: "POST" });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setMeldung(`Abgleich fertig: ${d.neu || 0} neu, ${d.geaendert || 0} geändert, ${d.entfernt || 0} entfernt.`);
        ladeSchichten();
      } else {
        setMeldung(null);
        alert(d.detail || "Abgleich fehlgeschlagen.");
      }
    } finally { setAbgleichLaeuft(false); }
  };

  const quelleWechseln = async (neu) => {
    const text = neu === "intern"
      ? "Zur internen Planung wechseln? Dann werden die früher in der App angelegten Schichten statt der Google-Kalender-Schichten angezeigt."
      : "Wieder die Schichten aus dem Google Kalender verwenden?";
    if (!window.confirm(text)) return;
    const res = await fetch(`${API}/schichtplan/quelle`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ quelle: neu }) });
    if (res.ok) setQuelle(neu); else alert("Umschalten fehlgeschlagen.");
  };

  useEffect(() => {
    if (ansicht === "monat" && istAdmin) {
      fetch(`${API}/schichtplan/monatsauswertung?jahr=${jahr}&monat=${monat}`).then(r => r.json()).then(setMonatsauswertung).catch(() => setMonatsauswertung(null));
    }
  }, [ansicht, jahr, monat, istAdmin, schichten]);

  const oeffneNeu = (datum) => { setBearbeiteSchicht(null); setNeuesDatum(datum); setFormOffen(true); };
  const oeffneBearbeiten = (schicht) => { setBearbeiteSchicht(schicht); setNeuesDatum(null); setFormOffen(true); };

  const handleLoeschen = async (schicht) => {
    if (!window.confirm(`Schicht von ${schicht.employee_name} am ${new Date(schicht.schicht_datum).toLocaleDateString("de-DE")} wirklich löschen?`)) return;
    const res = await fetch(`${API}/schichtplan/schichten/${schicht.id}`, { method: "DELETE" });
    if (res.ok) { setFormOffen(false); ladeSchichten(); } else alert("Löschen fehlgeschlagen.");
  };

  const aktiveMitarbeiter = mitarbeiterListe.filter(m => !nurAktive || m.aktiv);
  const eigeneId = session.id || session.benutzer_id;
  const sichtbareAbwesenheiten = abwesenheiten.filter(a => (istAdmin || a.benutzer_id === eigeneId) && (!mitarbeiterFilter || String(a.benutzer_id) === String(mitarbeiterFilter)));

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 20 }}>
        <button style={{ ...s.btnGray, width: "auto" }} onClick={() => setWochenStart(montagDerWoche(new Date()))}>Heute</button>
        {ansicht === "woche" && <>
          <button style={{ ...s.btnGray, width: "auto" }} onClick={() => { const d = new Date(wochenStart); d.setDate(d.getDate() - 7); setWochenStart(d); }}>◀ Vorherige Woche</button>
          <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base }}>{tage[0].toLocaleDateString("de-DE")} – {tage[6].toLocaleDateString("de-DE")}</span>
          <button style={{ ...s.btnGray, width: "auto" }} onClick={() => { const d = new Date(wochenStart); d.setDate(d.getDate() + 7); setWochenStart(d); }}>Nächste Woche ▶</button>
        </>}
        {ansicht === "monat" && <>
          <select style={{ ...s.input, width: 140 }} value={monat} onChange={e => setMonat(Number(e.target.value))}>
            {SCHICHTPLAN_MONATE.slice(1).map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
          </select>
          <select style={{ ...s.input, width: 100 }} value={jahr} onChange={e => setJahr(Number(e.target.value))}>
            {[jahr - 1, jahr, jahr + 1].map(j => <option key={j} value={j}>{j}</option>)}
          </select>
        </>}
        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          {[["woche", "Woche"], ["monat", "Monat"]].map(([key, label]) => (
            <button key={key} onClick={() => setAnsicht(key)} style={{ ...s.btnGray, width: "auto", padding: "8px 16px", background: ansicht === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: ansicht === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted) }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 20 }}>
        <select style={{ ...s.input, width: 220 }} value={mitarbeiterFilter} onChange={e => setMitarbeiterFilter(e.target.value)}>
          <option value="">Alle Mitarbeiter</option>
          {aktiveMitarbeiter.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
          <input type="checkbox" checked={nurAktive} onChange={e => setNurAktive(e.target.checked)} /> Nur aktive Mitarbeiter
        </label>
        {istAdmin && ausGoogle && <button style={{ ...s.btn, width: "auto", marginLeft: "auto" }} disabled={abgleichLaeuft} onClick={googleJetztAbgleichen}>{abgleichLaeuft ? "Gleiche ab…" : "🔄 Jetzt abgleichen"}</button>}
        {istAdmin && !ausGoogle && <button style={{ ...s.btn, width: "auto", marginLeft: "auto" }} onClick={() => oeffneNeu(isoDatum(tage[0]))}>+ Neue Schicht</button>}
        {istAdmin && !ausGoogle && ansicht === "woche" && <button style={{ ...s.btnGray, width: "auto" }} onClick={() => setKopierenOffen(true)}>📋 Woche kopieren</button>}
        {istAdmin && !ausGoogle && <button style={{ ...s.btnGray, width: "auto" }} onClick={() => setVorlagenOffen(true)}>⚙ Arbeitszeiten</button>}
        <button style={{ ...s.btnGray, width: "auto" }} onClick={() => window.print()}>🖨 Drucken</button>
      </div>

      {ausGoogle && (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "0 0 16px" }}>
          Die Schichten kommen aus dem Google Kalender (Termin-Titel = Kürzel). Änderungen bitte dort vornehmen – die Ansicht gleicht sich beim Öffnen automatisch ab.
          {istAdmin && <> <span onClick={() => quelleWechseln("intern")} style={{ textDecoration: "underline", cursor: "pointer" }}>Zur internen Planung wechseln</span></>}
        </p>
      )}
      {!ausGoogle && istAdmin && (
        <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xs, margin: "0 0 16px" }}>
          Interne Planung aktiv (nicht der Google Kalender). <span onClick={() => quelleWechseln("google")} style={{ textDecoration: "underline", cursor: "pointer" }}>Wieder Google Kalender verwenden</span>
        </p>
      )}
      {meldung && <p style={{ color: THEME.colors.success, marginBottom: 16 }}>{meldung}</p>}

      {ansicht === "woche" && (
        <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "repeat(7, 1fr)", gap: 10 }}>
          {tage.map((tag, i) => {
            const tagIso = isoDatum(tag);
            const heute = isoDatum(new Date()) === tagIso;
            const tagesSchichten = schichten.filter(s2 => s2.schicht_datum === tagIso).sort((a, b) => a.beginn.localeCompare(b.beginn));
            return (
              <div key={tagIso} style={{ ...s.karte, padding: 12, borderLeftColor: heute ? THEME.colors.primary : THEME.colors.border }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ color: heute ? THEME.colors.primary : THEME.colors.text, fontWeight: 700, fontSize: THEME.fontSize.sm }}>{WOCHENTAGE_KURZ[i]} {tag.getDate()}.{tag.getMonth() + 1}.</span>
                  {istAdmin && !ausGoogle && <button onClick={() => oeffneNeu(tagIso)} style={{ background: "none", border: "none", color: THEME.colors.textMuted, cursor: "pointer", fontSize: THEME.fontSize.lg }}>+</button>}
                </div>
                {sichtbareAbwesenheiten.filter(a => a.datum === tagIso).map((a, i) => (
                  <div key={`u${i}`} data-testid="schichtplan-urlaub" style={{ ...(istUrlaubAnzeige(a) ? URLAUB_SCHWARZ : { background: URLAUB_ART_FARBE[a.art], color: kontrastTextfarbe(URLAUB_ART_FARBE[a.art]) }), borderRadius: 6, padding: "4px 8px", marginBottom: 6, fontSize: THEME.fontSize.xs, fontWeight: 700 }}>
                    {istUrlaubAnzeige(a) ? `${a.kuerzel || a.name} U` : `🏖 ${a.kuerzel || a.name} – ${URLAUB_ART_TEXT[a.art]}`}
                  </div>
                ))}
                {tagesSchichten.length === 0 && sichtbareAbwesenheiten.every(a => a.datum !== tagIso) && <p style={{ color: THEME.colors.textDisabled, fontSize: THEME.fontSize.xs, margin: 0 }}>–</p>}
                {tagesSchichten.map(sc => (
                  <div key={sc.id} onClick={ausGoogle ? undefined : () => oeffneBearbeiten(sc)}
                    style={{ background: THEME.colors.background, borderLeft: `3px solid ${mitarbeiterFarbe(sc.employee_name)}`, borderRadius: 6, padding: "6px 8px", marginBottom: 6, cursor: istAdmin && !ausGoogle ? "pointer" : "default" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {sc.kuerzel && <span style={{ background: mitarbeiterFarbe(sc.employee_name), color: kontrastTextfarbe(mitarbeiterFarbe(sc.employee_name)), borderRadius: 4, padding: "1px 6px", fontSize: THEME.fontSize.xs, fontWeight: 800 }}>{sc.kuerzel}</span>}
                      <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm, fontWeight: 600 }}>{sc.employee_name}</span>
                    </div>
                    <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.xs }}>{sc.beginn}–{sc.ende}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {ansicht === "monat" && (() => {
        const monatsstart = new Date(jahr, monat - 1, 1);
        const start = montagDerWoche(monatsstart);
        const tageGesamt = [];
        for (let i = 0; i < 42; i++) { const d = new Date(start); d.setDate(d.getDate() + i); tageGesamt.push(d); }
        return (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 24 }}>
              {WOCHENTAGE_KURZ.map(w => <div key={w} style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, textAlign: "center", padding: 4 }}>{w}</div>)}
              {tageGesamt.map((tag) => {
                const tagIso = isoDatum(tag);
                const imMonat = tag.getMonth() === monat - 1;
                const tagesSchichten = schichten.filter(s2 => s2.schicht_datum === tagIso);
                return (
                  <div key={tagIso} style={{ background: imMonat ? THEME.colors.surface : "transparent", borderRadius: 8, padding: 8, minHeight: 70, opacity: imMonat ? 1 : 0.35 }}>
                    <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{tag.getDate()}</div>
                    {sichtbareAbwesenheiten.filter(a => a.datum === tagIso).map((a, i) => istUrlaubAnzeige(a)
                      ? <div key={`u${i}`} data-testid="schichtplan-urlaub" style={{ ...URLAUB_SCHWARZ, borderRadius: 4, padding: "0 4px", marginBottom: 2, fontSize: THEME.fontSize.tiny, fontWeight: 800, display: "inline-block" }}>{a.kuerzel || a.name.split(" ")[0]} U</div>
                      : <div key={`u${i}`} data-testid="schichtplan-urlaub" style={{ color: URLAUB_ART_FARBE[a.art], fontSize: THEME.fontSize.tiny, fontWeight: 800 }}>🏖 {a.kuerzel || a.name.split(" ")[0]}</div>)}
                    {tagesSchichten.length > 0 && <div style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xs, fontWeight: 700 }}>{tagesSchichten.length} Schicht{tagesSchichten.length === 1 ? "" : "en"}</div>}
                    {tagesSchichten.slice(0, 4).map(sc => <div key={sc.id} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.tiny }}><b style={{ color: mitarbeiterFarbe(sc.employee_name) }}>{sc.kuerzel || ""}</b> {sc.kuerzel ? `${sc.beginn}–${sc.ende}` : sc.employee_name}</div>)}
                  </div>
                );
              })}
            </div>
            {istAdmin && monatsauswertung && (
              <div>
                <h3 style={{ color: THEME.colors.text, marginBottom: 12 }}>Monatsauswertung {SCHICHTPLAN_MONATE[monat]} {jahr}</h3>
                {mobil ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {monatsauswertung.map(e => (
                      <div key={e.employee_id} style={{ ...s.karte, padding: 14, borderLeftColor: LIMIT_STATUS_FARBE[e.limit_status] }}>
                        <div style={{ color: THEME.colors.text, fontWeight: 700 }}>{e.employee_name}</div>
                        <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{e.planned_hours} h geplant</div>
                        <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{geplanterLohnText(e)}</div>
                        {e.remaining_amount != null && <div style={{ color: LIMIT_STATUS_FARBE[e.limit_status], fontSize: THEME.fontSize.xs, marginTop: 4 }}>{LIMIT_STATUS_LABEL[e.limit_status]} ({e.remaining_amount.toFixed(2)} € übrig)</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={s.tabelle}>
                      <thead><tr><th>Mitarbeiter</th><th>Geplante Stunden</th><th>Stundenlohn</th><th>Geplanter Lohn</th><th>Rest bis Grenze</th></tr></thead>
                      <tbody>
                        {monatsauswertung.map(e => (
                          <tr key={e.employee_id}>
                            <td>{e.employee_name}</td>
                            <td>{e.planned_hours} h</td>
                            <td>{e.hourly_wage != null ? `${e.hourly_wage.toFixed(2)} €` : "–"}</td>
                            <td>{geplanterLohnText(e)}</td>
                            <td style={{ color: LIMIT_STATUS_FARBE[e.limit_status] }}>{e.remaining_amount != null ? `${e.remaining_amount.toFixed(2)} € – ${LIMIT_STATUS_LABEL[e.limit_status]}` : "–"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}

      {formOffen && (
        <SchichtFormDialog
          schicht={bearbeiteSchicht} initialDatum={neuesDatum} mitarbeiterListe={aktiveMitarbeiter} vorlagen={vorlagen}
          onAbbrechen={() => setFormOffen(false)}
          onLoeschen={bearbeiteSchicht ? () => handleLoeschen(bearbeiteSchicht) : null}
          onGespeichert={(txt) => { setFormOffen(false); if (txt) setMeldung(txt); ladeSchichten(); }}
        />
      )}
      {kopierenOffen && (
        <CopyWeekDialog
          quelleStart={wochenStart}
          onAbbrechen={() => setKopierenOffen(false)}
          onFertig={(txt) => { setKopierenOffen(false); setMeldung(txt); ladeSchichten(); }}
        />
      )}
      {vorlagenOffen && (
        <VorlagenVerwaltungDialog vorlagen={vorlagen} onAbbrechen={() => setVorlagenOffen(false)} onGeaendert={ladeVorlagen} />
      )}
    </div>
  );
}

function SchichtFormDialog({ schicht, initialDatum, mitarbeiterListe, vorlagen, onAbbrechen, onLoeschen, onGespeichert }) {
  const [employeeId, setEmployeeId] = useState(schicht?.employee_id || (mitarbeiterListe[0]?.id ?? ""));
  const [datum, setDatum] = useState(schicht?.schicht_datum || initialDatum || isoDatum(new Date()));
  const [beginn, setBeginn] = useState(schicht?.beginn || "09:00");
  const [ende, setEnde] = useState(schicht?.ende || "17:00");
  const [notiz, setNotiz] = useState(schicht?.notiz || "");
  const [wiederholung, setWiederholung] = useState("einmalig");
  const [wiederholungBis, setWiederholungBis] = useState("");
  const [fehler, setFehler] = useState(null);
  const [laedt, setLaedt] = useState(false);

  const wochentagVonDatum = (d) => (new Date(d).getDay() + 6) % 7; // 0=Montag ... 6=Sonntag
  const vorlagenFuerTag = (vorlagen || []).filter(v => v.wochentag === wochentagVonDatum(datum));

  const vorlageAnwenden = (vorlageId) => {
    const v = vorlagenFuerTag.find(x => String(x.id) === vorlageId);
    if (v) { setBeginn(v.beginn); setEnde(v.ende); }
  };

  const speichern = async () => {
    setFehler(null);
    if (ende <= beginn) { setFehler("Endzeit muss nach der Startzeit liegen."); return; }
    if (!schicht && wiederholung === "zeitraum" && !wiederholungBis) { setFehler("Bitte ein Enddatum für den Zeitraum angeben."); return; }
    setLaedt(true);
    const body = { employee_id: Number(employeeId), schicht_datum: datum, beginn, ende, notiz };
    if (!schicht) { body.wiederholung = wiederholung; body.wiederholung_bis = wiederholung === "zeitraum" ? wiederholungBis : null; }
    const url = schicht ? `${API}/schichtplan/schichten/${schicht.id}` : `${API}/schichtplan/schichten`;
    const res = await fetch(url, { method: schicht ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    setLaedt(false);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setFehler(typeof data.detail === "string" ? data.detail : "Speichern fehlgeschlagen.");
      return;
    }
    const data = await res.json();
    let meldungText = null;
    if (data.anzahl_erstellt > 1) {
      meldungText = `${data.anzahl_erstellt} Schichten angelegt (jeden ${new Date(datum).toLocaleDateString("de-DE", { weekday: "long" })}).`;
      if (data.uebersprungen?.length) meldungText += ` ${data.uebersprungen.length} Termine übersprungen (bereits belegt).`;
    }
    onGespeichert(meldungText);
  };

  return (
    <div style={s.modalBg}>
      <div style={s.modal}>
        <h2 style={s.heading}>{schicht ? "Schicht bearbeiten" : "Neue Schicht"}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={s.label}>Mitarbeiter</label>
            <select style={s.input} value={employeeId} onChange={e => setEmployeeId(e.target.value)}>
              {mitarbeiterListe.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>Datum {datum && `(${new Date(datum).toLocaleDateString("de-DE", { weekday: "long" })})`}</label>
            <input type="date" style={s.input} value={datum} onChange={e => setDatum(e.target.value)} />
          </div>
          {vorlagenFuerTag.length > 0 ? (
            <div>
              <label style={s.label}>Arbeitszeit übernehmen (danach frei änderbar)</label>
              <select style={s.input} value="" onChange={e => vorlageAnwenden(e.target.value)}>
                <option value="">– auswählen –</option>
                {vorlagenFuerTag.map(v => <option key={v.id} value={v.id}>{v.name} ({v.beginn}–{v.ende})</option>)}
              </select>
            </div>
          ) : vorlagen?.length > 0 && (
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>
              Für {WOCHENTAGE_KURZ[wochentagVonDatum(datum)]} sind keine Arbeitszeiten hinterlegt.
            </p>
          )}
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Beginn</label>
              <input type="time" style={s.input} value={beginn} onChange={e => setBeginn(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Ende</label>
              <input type="time" style={s.input} value={ende} onChange={e => setEnde(e.target.value)} />
            </div>
          </div>
          <div>
            <label style={s.label}>Notiz (optional)</label>
            <input style={s.input} value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z. B. Einweisung, Kursvertretung" />
          </div>
          {!schicht && (
            <div>
              <label style={s.label}>Wiederholung</label>
              <select style={s.input} value={wiederholung} onChange={e => setWiederholung(e.target.value)}>
                <option value="einmalig">Einmalig</option>
                <option value="zeitraum">Wöchentlich, in einem Zeitraum</option>
                <option value="dauerhaft">Wöchentlich, dauerhaft</option>
              </select>
              {wiederholung !== "einmalig" && (
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "6px 0 0" }}>
                  Wird jeden {new Date(datum).toLocaleDateString("de-DE", { weekday: "long" })} zur gleichen Uhrzeit angelegt.
                </p>
              )}
            </div>
          )}
          {wiederholung === "zeitraum" && !schicht && (
            <div>
              <label style={s.label}>Wiederholen bis</label>
              <input type="date" style={s.input} min={datum} value={wiederholungBis} onChange={e => setWiederholungBis(e.target.value)} />
            </div>
          )}
          {fehler && <p style={{ color: THEME.colors.danger, margin: 0 }}>{fehler}</p>}
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          {onLoeschen && <button onClick={onLoeschen} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Löschen</button>}
          <div style={{ marginLeft: "auto", display: "flex", gap: 12 }}>
            <button onClick={onAbbrechen} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
            <button onClick={speichern} disabled={laedt} style={{ ...s.btn, width: "auto" }}>{laedt ? "Speichert…" : "Speichern"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyWeekDialog({ quelleStart, onAbbrechen, onFertig }) {
  const [zielStart, setZielStart] = useState(() => { const d = new Date(quelleStart); d.setDate(d.getDate() + 7); return isoDatum(d); });
  const [konflikt, setKonflikt] = useState(null);
  const [laedt, setLaedt] = useState(false);

  const pruefen = async () => {
    setLaedt(true);
    const res = await fetch(`${API}/schichtplan/schichten/woche-kopieren`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quelle_start_datum: isoDatum(quelleStart), ziel_start_datum: zielStart }),
    });
    const data = await res.json();
    setLaedt(false);
    if (data.konflikt) { setKonflikt(data); return; }
    onFertig(`✓ ${data.kopiert} Schicht(en) kopiert.`);
  };

  const trotzdemKopieren = async () => {
    setLaedt(true);
    const res = await fetch(`${API}/schichtplan/schichten/woche-kopieren`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quelle_start_datum: isoDatum(quelleStart), ziel_start_datum: zielStart, modus: "nur_fehlende" }),
    });
    const data = await res.json();
    setLaedt(false);
    onFertig(`✓ ${data.kopiert} Schicht(en) kopiert, ${data.uebersprungen} wegen bestehender Schichten übersprungen.`);
  };

  return (
    <div style={s.modalBg}>
      <div style={{ ...s.modal, width: "min(500px, 95vw)" }}>
        <h2 style={s.heading}>Woche kopieren</h2>
        <p style={{ color: THEME.colors.textSecondary }}>Quellwoche: {new Date(quelleStart).toLocaleDateString("de-DE")}</p>
        <label style={s.label}>Zielwoche beginnt am</label>
        <input type="date" style={s.input} value={zielStart} onChange={e => { setZielStart(e.target.value); setKonflikt(null); }} />
        {konflikt && (
          <div style={{ marginTop: 16 }}>
            <p style={{ color: THEME.colors.warning }}>In der Zielwoche bestehen bereits Schichten.</p>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={trotzdemKopieren} disabled={laedt} style={{ ...s.btn, width: "auto" }}>Nur fehlende Schichten ergänzen</button>
              <button onClick={onAbbrechen} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
            </div>
          </div>
        )}
        {!konflikt && (
          <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
            <button onClick={onAbbrechen} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
            <button onClick={pruefen} disabled={laedt} style={{ ...s.btn, width: "auto" }}>{laedt ? "Prüft…" : "Kopieren bestätigen"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function VorlagenVerwaltungDialog({ vorlagen, onAbbrechen, onGeaendert }) {
  const WOCHENTAGE_LANG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
  const [bearbeiteId, setBearbeiteId] = useState(null);
  const [bearbeiteForm, setBearbeiteForm] = useState(null);
  const [neuFuerTag, setNeuFuerTag] = useState(null);
  const [neuForm, setNeuForm] = useState({ name: "", beginn: "09:00", ende: "12:00" });
  const [fehler, setFehler] = useState(null);
  const [laedt, setLaedt] = useState(false);

  const anlegen = async (wochentag) => {
    setFehler(null);
    if (!neuForm.name.trim()) { setFehler("Bitte einen Namen eingeben."); return; }
    if (neuForm.ende <= neuForm.beginn) { setFehler("Endzeit muss nach der Startzeit liegen."); return; }
    setLaedt(true);
    const res = await fetch(`${API}/schichtplan/vorlagen`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ wochentag, name: neuForm.name.trim(), beginn: neuForm.beginn, ende: neuForm.ende }),
    });
    setLaedt(false);
    if (!res.ok) { const d = await res.json().catch(() => ({})); setFehler(d.detail || "Anlegen fehlgeschlagen."); return; }
    setNeuFuerTag(null); setNeuForm({ name: "", beginn: "09:00", ende: "12:00" });
    onGeaendert();
  };

  const speichern = async (id) => {
    setFehler(null);
    if (bearbeiteForm.ende <= bearbeiteForm.beginn) { setFehler("Endzeit muss nach der Startzeit liegen."); return; }
    const res = await fetch(`${API}/schichtplan/vorlagen/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(bearbeiteForm),
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); setFehler(d.detail || "Speichern fehlgeschlagen."); return; }
    setBearbeiteId(null); setBearbeiteForm(null);
    onGeaendert();
  };

  const loeschen = async (v) => {
    if (!window.confirm(`"${v.name}" (${v.beginn}–${v.ende}) wirklich löschen?`)) return;
    const res = await fetch(`${API}/schichtplan/vorlagen/${v.id}`, { method: "DELETE" });
    if (res.ok) onGeaendert(); else alert("Löschen fehlgeschlagen.");
  };

  return (
    <div style={s.modalBg}>
      <div style={{ ...s.modal, width: "min(600px, 95vw)", maxHeight: "85vh", overflowY: "auto" }}>
        <h2 style={s.heading}>Arbeitszeiten</h2>
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: -8 }}>
          Lege hier fest, wann pro Wochentag normalerweise gearbeitet wird. Beim Anlegen einer Schicht kannst du diese Zeiten dann nur noch auswählen statt einzutippen – bleibt aber jederzeit frei änderbar.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 12 }}>
          {WOCHENTAGE_LANG.map((tagName, wochentag) => {
            const tagesVorlagen = vorlagen.filter(v => v.wochentag === wochentag);
            return (
              <div key={wochentag}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>{tagName}</h3>
                  {neuFuerTag !== wochentag && (
                    <button onClick={() => { setNeuFuerTag(wochentag); setNeuForm({ name: "", beginn: "09:00", ende: "12:00" }); setFehler(null); }}
                      style={{ background: "none", border: "none", color: THEME.colors.primary, cursor: "pointer", fontSize: THEME.fontSize.sm }}>+ Zeit hinzufügen</button>
                  )}
                </div>
                {tagesVorlagen.length === 0 && neuFuerTag !== wochentag && (
                  <p style={{ color: THEME.colors.textDisabled, fontSize: THEME.fontSize.sm, margin: 0 }}>Geschlossen / keine Arbeitszeit hinterlegt</p>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {tagesVorlagen.map(v => (
                    <div key={v.id} style={{ ...s.karte, padding: 8 }}>
                      {bearbeiteId === v.id ? (
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <input style={{ ...s.input, flex: 2 }} value={bearbeiteForm.name} onChange={e => setBearbeiteForm({ ...bearbeiteForm, name: e.target.value })} />
                          <input type="time" style={{ ...s.input, flex: 1 }} value={bearbeiteForm.beginn} onChange={e => setBearbeiteForm({ ...bearbeiteForm, beginn: e.target.value })} />
                          <input type="time" style={{ ...s.input, flex: 1 }} value={bearbeiteForm.ende} onChange={e => setBearbeiteForm({ ...bearbeiteForm, ende: e.target.value })} />
                          <button onClick={() => speichern(v.id)} style={{ ...s.btn, width: "auto" }}>✓</button>
                          <button onClick={() => { setBearbeiteId(null); setBearbeiteForm(null); }} style={{ ...s.btnGray, width: "auto" }}>✕</button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: THEME.colors.text }}>{v.name} <span style={{ color: THEME.colors.textSecondary }}>({v.beginn}–{v.ende})</span></span>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={() => { setBearbeiteId(v.id); setBearbeiteForm({ wochentag: v.wochentag, name: v.name, beginn: v.beginn, ende: v.ende }); }} style={{ background: "none", border: "none", color: THEME.colors.textMuted, cursor: "pointer" }}>✎</button>
                            <button onClick={() => loeschen(v)} style={{ background: "none", border: "none", color: THEME.colors.danger, cursor: "pointer" }}>🗑</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {neuFuerTag === wochentag && (
                    <div style={{ ...s.karte, padding: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <input style={{ ...s.input, flex: 2, minWidth: 120 }} placeholder="Name, z. B. Frühschicht" value={neuForm.name} onChange={e => setNeuForm({ ...neuForm, name: e.target.value })} autoFocus />
                      <input type="time" style={{ ...s.input, flex: 1, minWidth: 90 }} value={neuForm.beginn} onChange={e => setNeuForm({ ...neuForm, beginn: e.target.value })} />
                      <input type="time" style={{ ...s.input, flex: 1, minWidth: 90 }} value={neuForm.ende} onChange={e => setNeuForm({ ...neuForm, ende: e.target.value })} />
                      <button onClick={() => anlegen(wochentag)} disabled={laedt} style={{ ...s.btn, width: "auto" }}>✓</button>
                      <button onClick={() => setNeuFuerTag(null)} style={{ ...s.btnGray, width: "auto" }}>✕</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {fehler && <p style={{ color: THEME.colors.danger, marginTop: 4 }}>{fehler}</p>}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <button onClick={onAbbrechen} style={{ ...s.btnGray, width: "auto" }}>Schließen</button>
        </div>
      </div>
    </div>
  );
}

// ============ MITGLIED DETAIL ============


function ZahlungsHistorie({ mitgliedId }) {
  const [zahlungen, setZahlungen] = useState([]);

  useEffect(() => {
    fetch(`${API}/mitglieder/${mitgliedId}/zahlungen-historie`).then(r => r.json()).then(setZahlungen).catch(() => {});
  }, [mitgliedId]);

  const gesamt = zahlungen.reduce((s, z) => s + parseFloat(z.betrag || 0), 0);

  return (
    <div style={{ ...s.karte, marginBottom: 16 }}>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 16px 0", fontSize: THEME.fontSize.xl }}>Zahlungsverlauf (historisch)</h3>
      {zahlungen.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine historischen Zahlungen gefunden.</p>}
      {zahlungen.length > 0 && (
        <>
          <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 500 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: 8, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, marginBottom: 8 }}>
            <span>Datum</span><span>Betrag</span><span>Art</span><span>Vorgang</span>
          </div>
          {zahlungen.map((z, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: 8, padding: "8px 0", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base }}>{z.datum}</span>
              <span style={{ color: z.art === 'R' ? THEME.colors.danger : z.art === 'E_OFFEN' ? THEME.colors.warning : THEME.colors.success, fontSize: THEME.fontSize.base, fontWeight: 600 }}>
                {z.art === 'R' ? '-' : ''}{parseFloat(z.betrag).toFixed(2)} €
                {z.art === 'E_OFFEN' && <span style={{ fontSize: THEME.fontSize.xxs, marginLeft: 4 }}>(ausstehend)</span>}
              </span>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{z.art === 'L' ? 'Lastschrift' : z.art === 'R' ? 'Rücklastschrift' : z.art === 'E' ? 'Einmalzahlung ✓' : z.art === 'E_OFFEN' ? 'Einmalzahlung (offen)' : z.art}</span>
              <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{z.vorgang}</span>
            </div>
          ))}
          </div>
          </div>
          <div style={{ borderTop: "2px solid #2b8cc4", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: THEME.colors.textMuted, fontWeight: 600 }}>Gesamt ({zahlungen.length} Buchungen)</span>
            <span style={{ color: THEME.colors.primary, fontWeight: 700, fontSize: THEME.fontSize.lg }}>{gesamt.toFixed(2)} €</span>
          </div>
        </>
      )}
    </div>
  );
}

function WellpassMitgliedVerlauf({ mitgliedId }) {
  const [daten, setDaten] = useState(null);

  useEffect(() => {
    fetch(`${API}/wellpass/mitglied/${mitgliedId}`).then(r => r.json()).then(setDaten).catch(() => {});
  }, [mitgliedId]);

  return (
    <div style={{ ...s.karte, marginBottom: 16 }}>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 16px 0", fontSize: THEME.fontSize.xl }}>Wellpass</h3>
      {!daten && <p style={{ color: THEME.colors.textMuted }}>Lädt…</p>}
      {daten && daten.anzahl_gesamt === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine Wellpass-Check-ins für dieses Mitglied gefunden.</p>}
      {daten && daten.anzahl_gesamt > 0 && (
        <>
          <div style={{ display: "flex", gap: 30, marginBottom: 16, flexWrap: "wrap" }}>
            <div><p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>Diesen Monat</p><p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{daten.anzahl_monat}</p></div>
            <div><p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>Dieses Jahr</p><p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{daten.anzahl_jahr}</p></div>
            <div><p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>Check-ins gesamt</p><p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{daten.anzahl_gesamt}</p></div>
            <div><p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>Letzter Check-in</p><p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{daten.letzter_checkin}</p></div>
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {daten.verlauf.map((v, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid #2b3d52", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                <span>{v.datum}</span><span>{v.zeit}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function EinmalzahlungBereich({ mitglied }) {
  const [einmal, setEinmal] = useState([]);
  const [formOffen, setFormOffen] = useState(false);
  const [beschreibung, setBeschreibung] = useState("");
  const [betrag, setBetrag] = useState("");

  const laden = () => fetch(`${API}/einmalzahlungen/mitglied/${mitglied.id}`).then(r => r.json()).then(setEinmal).catch(() => {});
  useEffect(() => { laden(); }, [mitglied.id]);

  const handleHinzufuegen = async () => {
    if (!beschreibung || !betrag) { alert("Bitte Beschreibung und Betrag eingeben!"); return; }
    const res = await fetch(`${API}/einmalzahlungen/`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mitglied_id: mitglied.id, beschreibung, betrag: parseFloat(betrag) })
    });
    if (!res.ok) { alert("Einmalzahlung konnte nicht gespeichert werden."); return; }
    setBeschreibung(""); setBetrag(""); setFormOffen(false); laden();
  };

  const handleLoeschen = async (id) => {
    if (!window.confirm("Einmalzahlung löschen?")) return;
    const res = await fetch(`${API}/einmalzahlungen/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    laden();
  };

  const offen = einmal.filter(e => !e.eingezogen);
  const eingezogen = einmal.filter(e => e.eingezogen);

  return (
    <div style={{ ...s.karte, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>
          Einmalzahlungen
          {offen.length > 0 && <span style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.base, marginLeft: 8 }}>({offen.length} offen)</span>}
        </h3>
        <button onClick={() => setFormOffen(!formOffen)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.base }}>+ Hinzufügen</button>
      </div>

      {formOffen && (
        <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div style={s.inputGroup}>
              <label style={s.label}>Beschreibung *</label>
              <input style={s.input} value={beschreibung} onChange={e => setBeschreibung(e.target.value)} placeholder="z.B. Shaker, Trainingsprogramm..." />
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Betrag (€) *</label>
              <input style={s.input} type="number" step="0.01" value={betrag} onChange={e => setBetrag(e.target.value)} placeholder="50.00" />
            </div>
          </div>
          <div style={{ background: THEME.colors.surface, borderRadius: 6, padding: 10, marginBottom: 12, borderLeft: "3px solid #f59e0b" }}>
            <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, margin: 0 }}>⚠ Wird beim nächsten SEPA-Lauf automatisch mit eingezogen.</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setFormOffen(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
            <button onClick={handleHinzufuegen} style={{ ...s.btn, width: "auto" }}>Hinzufügen</button>
          </div>
        </div>
      )}

      {einmal.length === 0 && !formOffen && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.md }}>Keine Einmalzahlungen.</p>}

      {offen.length > 0 && (
        <>
          <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, margin: "0 0 8px" }}>Ausstehend (wird beim nächsten SEPA eingezogen):</p>
          {offen.map(e => (
            <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "1px solid #2b3d52" }}>
              <div>
                <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md, fontWeight: 600 }}>{e.beschreibung}</span>
                <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginLeft: 12 }}>{e.erstellt_am}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ color: THEME.colors.warning, fontWeight: 700, fontSize: THEME.fontSize.lg }}>{parseFloat(e.betrag).toFixed(2)} €</span>
                <button onClick={() => handleLoeschen(e.id)} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg, fontSize: THEME.fontSize.sm, padding: "5px 10px" }}>✗</button>
              </div>
            </div>
          ))}
        </>
      )}

      {eingezogen.length > 0 && (
        <>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "12px 0 8px" }}>Bereits eingezogen:</p>
          {eingezogen.slice(0, 5).map(e => (
            <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #2b3d52", opacity: 0.6 }}>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base }}>{e.beschreibung} <span style={{ fontSize: THEME.fontSize.xs }}>({e.eingezogen_am ? datumDE(e.eingezogen_am) : ""})</span></span>
              <span style={{ color: THEME.colors.success, fontSize: THEME.fontSize.base }}>{parseFloat(e.betrag).toFixed(2)} € ✓</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

const RFID_STATUS_LABEL = { keine: "Keine RFID", aktiv: "Aktiv", gesperrt: "Gesperrt", verloren: "Verloren" };
const RFID_STATUS_FARBE = { keine: THEME.colors.textMuted, aktiv: THEME.colors.success, gesperrt: THEME.colors.danger, verloren: THEME.colors.warning };

// NFC-Scan-Formel (Web NFC API, nur Chrome auf Android) - liest die rohe Chip-Seriennummer (Hex)
// und rechnet sie in die SafeSmart-Kartennummer um. An zwei echten Karten bestaetigt (siehe
// SafeSmartImportSeite): die ersten 3 Bytes der 4-Byte-UID, Reihenfolge umgekehrt, als Hexzahl
// gelesen - das 4. Byte (vermutlich ein Pruef-/Herstellerbyte) wird verworfen. Nur fuer
// 4-Byte-UIDs bestaetigt. Zentral hier definiert, damit RfidBereich und SafeSmartImportSeite
// dieselbe Formel verwenden statt sie unabhaengig zu duplizieren.
function berechneSafesmartKartennummer(bytes) {
  if (bytes.length !== 4) return null;
  const ersteDreiUmgekehrt = [...bytes.slice(0, 3)].reverse();
  return ersteDreiUmgekehrt.reduce((acc, b) => acc * 256 + b, 0);
}

// PC-USB-RFID-Kartenleser geben eine andere Rohzahl aus als die gespeicherte SafeSmart-
// Kartennummer (anderes Byte-Format als beim Handy-NFC-Scan) - bestaetigt an einer echten Karte:
// Leser-Zahl 426084359 (=0x19658807) -> oberstes Byte verwerfen -> 0x658807 = 6653959 (echte
// Kartennummer). Entspricht Leser-Zahl MOD 16.777.216 (2^24). Nur anwenden, wenn die Eingabe
// rein numerisch UND groesser als eine normale Kartennummer ist - sonst normale Nummern nicht anfassen.
function pcLeserZuKartennummer(wert) {
  if (!/^\d+$/.test(wert)) return null;
  const zahl = parseInt(wert, 10);
  if (zahl <= 0xFFFFFF) return null;
  return zahl % 16777216;
}

function RfidBereich({ mitglied, onAktualisiert }) {
  const [bearbeitenOffen, setBearbeitenOffen] = useState(false);
  const [kartennummer, setKartennummer] = useState(mitglied.rfid_kartennummer || "");
  const [status, setStatus] = useState(mitglied.rfid_status === "keine" ? "aktiv" : (mitglied.rfid_status || "aktiv"));
  const [fehler, setFehler] = useState("");
  const [speichert, setSpeichert] = useState(false);
  const nfcUnterstuetzt = typeof window !== "undefined" && "NDEFReader" in window;
  const [nfcScanLaeuft, setNfcScanLaeuft] = useState(false);
  // Merkt sich, ob die aktuell im Feld stehende Nummer per Handy-NFC-Scan berechnet wurde - TTLock
  // braucht je nach Erfassungsmethode eine andere Rueckrechnungsformel (siehe _ttlock_zielnummer
  // in routers_ttlock.py). Jede manuelle Tastatureingabe verwirft diesen Status wieder, da dann
  // vermutlich eine PC-Leser-Nummer eingegeben/eingefuegt wurde.
  const [erfassungsart, setErfassungsart] = useState(null);
  // Explizite Karte/Band-Angabe - ueberschreibt die aus dem Tarif geratene Primaervermutung
  // (siehe _ttlock_primaervermutung in routers_ttlock.py). "" bedeutet "automatisch/Tarif".
  const [medientyp, setMedientyp] = useState(mitglied.rfid_medientyp || "");
  // Direkter Hook-Zugriff statt <TTLockZutrittZeile> (Nutzer-Wunsch 2026-07-24: Statistik neben
  // der Ueberschrift, Aktions-Buttons in derselben Zeile wie "RFID bearbeiten" - "in die Breite
  // statt in die Tiefe") - siehe useTtlockZutritt() oben.
  const ttlock = useTtlockZutritt("mitglied", mitglied.id);
  const ttlockStat = ttlock.daten?.statistik || {};

  const handleNfcScan = async () => {
    setFehler("");
    setNfcScanLaeuft(true);
    try {
      const ndef = new window.NDEFReader();
      await ndef.scan();
      ndef.onreading = (event) => {
        const bytes = (event.serialNumber || "").split(":").filter(Boolean).map(h => parseInt(h, 16));
        const wert = berechneSafesmartKartennummer(bytes);
        setNfcScanLaeuft(false);
        if (wert != null) { setKartennummer(String(wert)); setErfassungsart("handy_nfc"); }
        else setFehler("Chip-Format nicht erkannt (nur 4-Byte-UIDs unterstützt) - bitte Nummer manuell eingeben.");
      };
      ndef.onreadingerror = () => {
        setFehler("Chip konnte nicht gelesen werden – bitte Karte näher ans Handy halten und erneut versuchen.");
        setNfcScanLaeuft(false);
      };
    } catch (e) {
      setFehler(e?.message || "NFC-Scan konnte nicht gestartet werden (Berechtigung erteilt?).");
      setNfcScanLaeuft(false);
    }
  };

  const handleSpeichern = async () => {
    setSpeichert(true);
    setFehler("");
    let karte = kartennummer.trim();
    if (erfassungsart !== "handy_nfc") {
      const umgerechnet = pcLeserZuKartennummer(karte);
      if (umgerechnet != null) { karte = String(umgerechnet); setKartennummer(karte); }
    }
    const res = await fetch(`${API}/mitglieder/${mitglied.id}/rfid`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rfid_kartennummer: karte || null, rfid_status: status, erfassungsart, medientyp: medientyp || null }),
    });
    if (res.ok) { setBearbeitenOffen(false); onAktualisiert(); }
    else { const d = await res.json().catch(() => ({})); setFehler(d.detail || "Fehler beim Speichern."); }
    setSpeichert(false);
  };

  const handleEntfernen = async () => {
    if (!window.confirm("RFID-Zuordnung wirklich entfernen?")) return;
    const res = await fetch(`${API}/mitglieder/${mitglied.id}/rfid`, { method: "DELETE" });
    if (!res.ok) { alert("RFID konnte nicht entfernt werden."); return; }
    onAktualisiert();
  };

  return (
    <div style={{ ...s.karte, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>RFID &amp; Zutritt</h3>
        {ttlock.daten && (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {[["Heute", ttlockStat.checkins_heute], ["Woche", ttlockStat.checkins_woche], ["Monat", ttlockStat.checkins_monat], ["Jahr", ttlockStat.checkins_jahr], ["Gesamt", ttlockStat.checkins_gesamt]].map(([label, wert]) => (
              <div key={label} style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{label}</span>
                <span style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxl, fontWeight: 700 }}>{wert ?? 0}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {!bearbeitenOffen ? (
        <>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.md, margin: "4px 0" }}>
            RFID-Nummer: <span style={{ color: THEME.colors.text }}>{mitglied.rfid_kartennummer || "–"}</span>
          </p>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.md, margin: "4px 0" }}>
            Status: <span style={{ color: RFID_STATUS_FARBE[mitglied.rfid_status] || THEME.colors.text, fontWeight: 600 }}>{RFID_STATUS_LABEL[mitglied.rfid_status] || "Keine RFID"}</span>
          </p>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.md, margin: "4px 0" }}>
            Quelle: <span style={{ color: THEME.colors.text }}>{mitglied.rfid_quelle || "–"}</span>
          </p>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.md, margin: "4px 0" }}>
            Letzte Änderung: <span style={{ color: THEME.colors.text }}>{mitglied.rfid_geaendert_am ? new Date(mitglied.rfid_geaendert_am).toLocaleString("de-DE") : "–"}</span>
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
            <button onClick={() => setBearbeitenOffen(true)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.base }}>RFID bearbeiten</button>
            {mitglied.rfid_kartennummer && <button onClick={handleEntfernen} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg, fontSize: THEME.fontSize.base }}>RFID entfernen</button>}
            {ttlock.daten && (
              <>
                <button onClick={ttlock.handleSync} disabled={ttlock.aktion === "sync"} style={{ ...s.btnGray, width: "auto" }}>{ttlock.aktion === "sync" ? "Synchronisiert…" : "🔄 Jetzt synchronisieren"}</button>
                {ttlock.daten.sync_status !== "entzogen" && (
                  <button onClick={ttlock.handleEntziehen} disabled={ttlock.aktion === "entzug"} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>{ttlock.aktion === "entzug" ? "Entzieht…" : "🔒 Zutritt entziehen"}</button>
                )}
                <button onClick={ttlock.oeffneUebernahme} style={{ ...s.btnGray, width: "auto" }}>📥 Von TTLock übernehmen</button>
              </>
            )}
          </div>
        </>
      ) : (
        <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16 }}>
          <div style={s.inputGroup}>
            <label style={s.label}>RFID-Kartennummer</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input style={{ ...s.input, flex: 1 }} value={kartennummer} onChange={e => { setKartennummer(e.target.value); setErfassungsart(null); }} onKeyDown={e => { if (e.key === "Enter") handleSpeichern(); }} placeholder="Kartennummer eingeben..." autoFocus />
              {nfcUnterstuetzt && (
                <button onClick={handleNfcScan} disabled={nfcScanLaeuft} style={{ ...s.btnGray, width: "auto", flexShrink: 0 }}>{nfcScanLaeuft ? "Scannt…" : "📱 Scannen"}</button>
              )}
            </div>
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>RFID-Status</label>
            <select style={s.input} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="aktiv">Aktiv</option>
              <option value="gesperrt">Gesperrt</option>
              <option value="verloren">Verloren</option>
            </select>
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>Medientyp (für TTLock-Zutritt)</label>
            <select style={s.input} value={medientyp} onChange={e => setMedientyp(e.target.value)}>
              <option value="">Automatisch (nach Tarif)</option>
              <option value="karte">Karte</option>
              <option value="band">Band</option>
            </select>
          </div>
          {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{fehler}</p>}
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={handleSpeichern} disabled={speichert} style={{ ...s.btn, width: "auto" }}>{speichert ? "Speichert…" : "Speichern"}</button>
            <button onClick={() => setBearbeitenOffen(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
          </div>
        </div>
      )}
      {ttlock.daten && (
        <div style={{ marginTop: 16 }}>
          <p style={{ color: ttlock.daten.sync_status === "konflikt" ? THEME.colors.danger : THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 8px" }}>Sync-Status: {ttlock.daten.sync_status}</p>
          {ttlock.daten.sync_status === "konflikt" && ttlock.daten.konflikte?.length > 0 && (
            <div style={{ background: THEME.colors.danger + "22", border: `1px solid ${THEME.colors.danger}`, borderRadius: 8, padding: 10, marginBottom: 12 }}>
              <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, fontWeight: 600, margin: "0 0 4px" }}>⚠ RFID-Konflikt - kein Zutritt synchronisiert:</p>
              {ttlock.daten.konflikte.map((k, i) => (
                <p key={i} style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: 0 }}>{k}</p>
              ))}
            </div>
          )}
          {ttlockStat.letzter_checkin && (
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "0 0 8px" }}>
              Letzter Check-in: {new Date(ttlockStat.letzter_checkin).toLocaleString("de-DE")}
            </p>
          )}
          {ttlockStat.verlauf?.length > 0 && (
            <>
              <p onClick={() => ttlock.setHistorieOffen(o => !o)} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, cursor: "pointer", userSelect: "none", margin: "0 0 8px" }}>
                {ttlock.historieOffen ? "▲ Check-in-Historie einklappen" : "▼ Check-in-Historie anzeigen"}
              </p>
              {ttlock.historieOffen && (
                <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 12 }}>
                  {ttlockStat.verlauf.map((v, i) => (
                    <div key={i} style={{ padding: "4px 0", borderTop: "1px solid #2b3d52", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.xs }}>
                      {v.zeitpunkt ? new Date(v.zeitpunkt).toLocaleString("de-DE") : "–"}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {ttlock.meldung && <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 8 }}>{ttlock.meldung}</p>}
          {ttlock.uebernahmeOffen && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => ttlock.setUebernahmeOffen(false)}>
              <div style={{ background: THEME.colors.surface, borderRadius: 12, padding: 20, width: "min(600px, 92vw)", maxHeight: "80vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 600, margin: "0 0 4px" }}>Von TTLock übernehmen</p>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>
                  Registriere die Karte/das Band zuerst direkt in der TTLock-App (unter irgendeinem Namen), dann hier auswählen. Die ComeFit-Nummer wird garantiert korrekt zurückgerechnet.
                </p>
                {ttlock.kandidatenLaedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Lädt…</p>}
                {ttlock.kandidaten && ttlock.kandidaten.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Keine Karten in TTLock gefunden.</p>}
                <div style={{ overflowY: "auto", flex: 1 }}>
                  {ttlock.kandidaten && ttlock.kandidaten.map(k => (
                    <div key={k.cardId} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 8, marginBottom: 6,
                      background: k.wahrscheinlicher_treffer ? THEME.colors.success + "22" : THEME.colors.background,
                      border: k.wahrscheinlicher_treffer ? `1px solid ${THEME.colors.success}` : "1px solid transparent",
                    }}>
                      <div>
                        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600, margin: 0 }}>
                          {k.cardName || "(ohne Namen)"} {k.wahrscheinlicher_treffer && <span style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xs }}>✓ wahrscheinlicher Treffer</span>}
                        </p>
                        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "2px 0 0" }}>
                          Nr. {k.cardNumber} · {k.createDate ? new Date(k.createDate).toLocaleString("de-DE") : "–"}
                        </p>
                      </div>
                      <button onClick={() => ttlock.handleUebernehmen(k)} disabled={ttlock.uebernimmtCardId === k.cardId} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 14px", flexShrink: 0 }}>
                        {ttlock.uebernimmtCardId === k.cardId ? "…" : "Übernehmen"}
                      </button>
                    </div>
                  ))}
                </div>
                <button onClick={() => ttlock.setUebernahmeOffen(false)} style={{ ...s.btnGray, width: "auto", marginTop: 12, alignSelf: "flex-end" }}>Schließen</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Gemeinsame Datenladung/Aktionen fuer den TTLock-Zutrittsbereich einer Person - als eigener Hook
// extrahiert (2026-07-24), damit RfidBereich (Mitglied) ein eigenes, breiteres Layout bauen kann
// (Statistik neben der Ueberschrift, Aktions-Buttons in einer Zeile mit "RFID bearbeiten"), waehrend
// TTLockZutrittZeile selbst fuer die anderen 4 Personentypen (Mitarbeiter/Wellpass/Hansefit/
// Sonstige) unveraendert das bisherige, gestapelte Layout weiterverwendet - identische Logik, nur
// die Darstellung ist getrennt.
function useTtlockZutritt(personTyp, personId) {
  const [daten, setDaten] = useState(null);
  const [aktion, setAktion] = useState("");
  const [historieOffen, setHistorieOffen] = useState(false);
  const [meldung, setMeldung] = useState("");
  const [uebernahmeOffen, setUebernahmeOffen] = useState(false);
  const [kandidaten, setKandidaten] = useState(null);
  const [kandidatenLaedt, setKandidatenLaedt] = useState(false);
  const [uebernimmtCardId, setUebernimmtCardId] = useState(null);

  const laden = () => {
    fetch(`${API}/ttlock/personen/${personTyp}/${personId}`).then(r => r.ok ? r.json() : null).then(setDaten).catch(() => {});
  };
  useEffect(laden, [personTyp, personId]);

  const oeffneUebernahme = async () => {
    setUebernahmeOffen(true);
    setKandidatenLaedt(true);
    setKandidaten(null);
    const res = await fetch(`${API}/ttlock/personen/${personTyp}/${personId}/ttlock-kandidaten`).catch(() => null);
    if (res && res.ok) setKandidaten(await res.json());
    setKandidatenLaedt(false);
  };

  const handleUebernehmen = async (karte) => {
    if (!window.confirm(`"${karte.cardName || "(ohne Namen)"}" (${karte.cardNumber}) wirklich dieser Person zuordnen?${daten?.sync_status === "synchronisiert" ? " Die bisherige RFID-Zuordnung wird ersetzt." : ""}`)) return;
    setUebernimmtCardId(karte.cardId);
    setMeldung("");
    const res = await fetch(`${API}/ttlock/personen/${personTyp}/${personId}/von-ttlock-uebernehmen`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ card_id: karte.cardId, card_nummer: String(karte.cardNumber) }),
    }).catch(() => null);
    if (res && res.ok) {
      // Die eigentliche Umbenennung am Schloss laeuft seit 2026-09-11 im Hintergrund (bis zu
      // 2x5 Wiederholungsversuche bei Schloss-Problemen) statt die Antwort zu blockieren - daher
      // hier kein "fertig", sondern ein Hinweis, dass es kurz dauern kann.
      setMeldung("Übernommen – Karte wird im Hintergrund umbenannt, das kann ein paar Sekunden dauern.");
      setUebernahmeOffen(false);
    } else {
      const d = await res?.json().catch(() => null);
      setMeldung(typeof d?.detail === "string" ? d.detail : "Übernahme fehlgeschlagen.");
    }
    setUebernimmtCardId(null);
    laden();
  };

  const handleSync = async () => {
    setAktion("sync");
    setMeldung("");
    const res = await fetch(`${API}/ttlock/personen/${personTyp}/${personId}/synchronisieren`, { method: "POST" }).catch(() => null);
    if (res && res.ok) {
      setMeldung("Synchronisierung ausgelöst.");
    } else {
      const d = await res?.json().catch(() => null);
      setMeldung(d?.detail || "Synchronisierung fehlgeschlagen.");
    }
    laden();
    setAktion("");
  };

  const handleEntziehen = async () => {
    if (!window.confirm("Zutritt für diese Person wirklich entziehen? Die Karte wird am Schloss gesperrt.")) return;
    setAktion("entzug");
    setMeldung("");
    const res = await fetch(`${API}/ttlock/personen/${personTyp}/${personId}/entziehen`, { method: "POST" }).catch(() => null);
    setMeldung(res && res.ok ? "Zutritt entzogen." : "Entziehen fehlgeschlagen.");
    laden();
    setAktion("");
  };

  return {
    daten, aktion, meldung, historieOffen, setHistorieOffen, uebernahmeOffen, setUebernahmeOffen,
    kandidaten, kandidatenLaedt, uebernimmtCardId, oeffneUebernahme, handleUebernehmen, handleSync, handleEntziehen,
  };
}

function TTLockZutrittZeile({ personTyp, personId }) {
  const {
    daten, aktion, meldung, historieOffen, setHistorieOffen, uebernahmeOffen, setUebernahmeOffen,
    kandidaten, kandidatenLaedt, uebernimmtCardId, oeffneUebernahme, handleUebernehmen, handleSync, handleEntziehen,
  } = useTtlockZutritt(personTyp, personId);

  if (!daten) return null;
  const stat = daten.statistik || {};
  return (
    <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginTop: 16 }}>
      <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600, margin: "0 0 8px" }}>🔐 TTLock-Zutritt</p>
      <p style={{ color: daten.sync_status === "konflikt" ? THEME.colors.danger : THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 8px" }}>Sync-Status: {daten.sync_status}</p>
      {daten.sync_status === "konflikt" && daten.konflikte?.length > 0 && (
        <div style={{ background: THEME.colors.danger + "22", border: `1px solid ${THEME.colors.danger}`, borderRadius: 8, padding: 10, marginBottom: 12 }}>
          <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, fontWeight: 600, margin: "0 0 4px" }}>⚠ RFID-Konflikt - kein Zutritt synchronisiert:</p>
          {daten.konflikte.map((k, i) => (
            <p key={i} style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: 0 }}>{k}</p>
          ))}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
        {[["Heute", stat.checkins_heute], ["Woche", stat.checkins_woche], ["Monat", stat.checkins_monat], ["Jahr", stat.checkins_jahr], ["Gesamt", stat.checkins_gesamt]].map(([label, wert]) => (
          <div key={label} style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>{label}</p>
            <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{wert ?? 0}</p>
          </div>
        ))}
      </div>
      {stat.letzter_checkin && (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "0 0 8px" }}>
          Letzter Check-in: {new Date(stat.letzter_checkin).toLocaleString("de-DE")}
        </p>
      )}
      {stat.verlauf?.length > 0 && (
        <>
          <p onClick={() => setHistorieOffen(o => !o)} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, cursor: "pointer", userSelect: "none", margin: "0 0 8px" }}>
            {historieOffen ? "▲ Check-in-Historie einklappen" : "▼ Check-in-Historie anzeigen"}
          </p>
          {historieOffen && (
            <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 12 }}>
              {stat.verlauf.map((v, i) => (
                <div key={i} style={{ padding: "4px 0", borderTop: "1px solid #2b3d52", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.xs }}>
                  {v.zeitpunkt ? new Date(v.zeitpunkt).toLocaleString("de-DE") : "–"}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={handleSync} disabled={aktion === "sync"} style={{ ...s.btnGray, width: "auto" }}>{aktion === "sync" ? "Synchronisiert…" : "🔄 Jetzt synchronisieren"}</button>
        {daten.sync_status !== "entzogen" && (
          <button onClick={handleEntziehen} disabled={aktion === "entzug"} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>{aktion === "entzug" ? "Entzieht…" : "🔒 Zutritt entziehen"}</button>
        )}
        <button onClick={oeffneUebernahme} style={{ ...s.btnGray, width: "auto" }}>📥 Von TTLock übernehmen</button>
      </div>
      {meldung && <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 8 }}>{meldung}</p>}

      {uebernahmeOffen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => setUebernahmeOffen(false)}>
          <div style={{ background: THEME.colors.surface, borderRadius: 12, padding: 20, width: "min(600px, 92vw)", maxHeight: "80vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
            <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 600, margin: "0 0 4px" }}>Von TTLock übernehmen</p>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>
              Registriere die Karte/das Band zuerst direkt in der TTLock-App (unter irgendeinem Namen), dann hier auswählen. Die ComeFit-Nummer wird garantiert korrekt zurückgerechnet.
            </p>
            {kandidatenLaedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Lädt…</p>}
            {kandidaten && kandidaten.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Keine Karten in TTLock gefunden.</p>}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {kandidaten && kandidaten.map(k => (
                <div key={k.cardId} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 8, marginBottom: 6,
                  background: k.wahrscheinlicher_treffer ? THEME.colors.success + "22" : THEME.colors.background,
                  border: k.wahrscheinlicher_treffer ? `1px solid ${THEME.colors.success}` : "1px solid transparent",
                }}>
                  <div>
                    <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600, margin: 0 }}>
                      {k.cardName || "(ohne Namen)"} {k.wahrscheinlicher_treffer && <span style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xs }}>✓ wahrscheinlicher Treffer</span>}
                    </p>
                    <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "2px 0 0" }}>
                      Nr. {k.cardNumber} · {k.createDate ? new Date(k.createDate).toLocaleString("de-DE") : "–"}
                    </p>
                  </div>
                  <button onClick={() => handleUebernehmen(k)} disabled={uebernimmtCardId === k.cardId} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 14px", flexShrink: 0 }}>
                    {uebernimmtCardId === k.cardId ? "…" : "Übernehmen"}
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => setUebernahmeOffen(false)} style={{ ...s.btnGray, width: "auto", marginTop: 12, alignSelf: "flex-end" }}>Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
}

const SAFESMART_KATEGORIE_LABEL = {
  exact_active_match: "Sicher zugeordnet",
  normalized_active_match: "Sicher zugeordnet",
  suggested_match: "Zu prüfen",
  ambiguous_match: "Mehrdeutig",
  paused_match: "Pausiert",
  inactive_match: "Inaktiv/Ehemalig",
  staff_admin: "Mitarbeiter/Admin",
  unmatched: "Nicht zugeordnet",
  invalid_row: "Ungültig",
  wellpass_pending: "Wellpass vorgemerkt",
  sonstige_pending: "Sonstige vorgemerkt",
};
const SAFESMART_KATEGORIE_FARBE = {
  exact_active_match: THEME.colors.success,
  normalized_active_match: THEME.colors.success,
  suggested_match: THEME.colors.accentCyan,
  ambiguous_match: THEME.colors.warning || "#d9a441",
  paused_match: THEME.colors.textMuted,
  inactive_match: THEME.colors.textMuted,
  staff_admin: THEME.colors.accentPurple || THEME.colors.primary,
  unmatched: THEME.colors.danger,
  invalid_row: THEME.colors.danger,
  wellpass_pending: THEME.colors.accentPurple || THEME.colors.primary,
  sonstige_pending: THEME.colors.accentPurple || THEME.colors.primary,
};
const SAFESMART_FILTER_OPTIONEN = [
  { value: "", label: "Alle Kategorien" },
  { value: "sicher", label: "Sicher zugeordnet" },
  { value: "suggested_match", label: "Zu prüfen" },
  { value: "ambiguous_match", label: "Mehrdeutig" },
  { value: "paused_match", label: "Pausiert" },
  { value: "inactive_match", label: "Inaktiv/Ehemalig" },
  { value: "staff_admin", label: "Mitarbeiter/Admin" },
  { value: "unmatched", label: "Nicht zugeordnet" },
  { value: "wellpass_pending", label: "Wellpass vorgemerkt" },
  { value: "sonstige_pending", label: "Sonstige vorgemerkt" },
  { value: "invalid_row", label: "Ungültig" },
];

const SAFESMART_DECISION_LABEL = {
  undecided: "Noch offen",
  confirmed_proposal: "Vorschlag bestätigt",
  manually_assigned: "Manuell zugeordnet",
  mark_for_wellpass: "Für Wellpass vorgemerkt",
  mark_for_sonstige: "Für Sonstige vorgemerkt",
  staff_admin: "Mitarbeiter/Admin",
  ignored: "Ignoriert",
  unresolved: "Ungelöst (offen gelassen)",
};
const SAFESMART_ENTSCHEIDUNG_FILTER_OPTIONEN = [
  { value: "", label: "Alle Entscheidungen" },
  ...Object.entries(SAFESMART_DECISION_LABEL).map(([value, label]) => ({ value, label })),
];
const SAFESMART_KONFLIKT_FILTER_OPTIONEN = [
  { value: "", label: "Alle" },
  { value: "mit", label: "Mit Konflikt" },
  { value: "ohne", label: "Ohne Konflikt" },
];
const SAFESMART_IGNORIEREN_GRUENDE = [
  "ehemaliger Eintrag", "Karte nicht mehr im Einsatz", "unbekannte Person", "doppelter Datensatz", "sonstiger Grund",
];
// Nur diese Kategorien gelten als "sicher zugeordnet" - einzig sie sind per Sammelaktion bestaetigbar
// (muss mit backend/routers_safesmart.py:SICHER_KATEGORIEN uebereinstimmen).
const SAFESMART_SICHER_KATEGORIEN = ["exact_active_match", "normalized_active_match"];

// Phase 4: Wellpass-Abgleich
const WELLPASS_MATCH_KATEGORIE_LABEL = {
  wellpass_exact_match: "Exakter Treffer",
  wellpass_normalized_match: "Normalisierter Treffer",
  wellpass_suggested_match: "Ähnlicher Vorschlag",
  wellpass_ambiguous: "Mehrdeutig",
  wellpass_existing_user: "Bestehender Nutzer",
  wellpass_unmatched: "Kein Treffer",
};
const WELLPASS_MATCH_KATEGORIE_FARBE = {
  wellpass_exact_match: THEME.colors.success,
  wellpass_normalized_match: THEME.colors.success,
  wellpass_suggested_match: THEME.colors.accentCyan,
  wellpass_ambiguous: THEME.colors.warning || "#d9a441",
  wellpass_existing_user: THEME.colors.primary,
  wellpass_unmatched: THEME.colors.danger,
};
const WELLPASS_RESOLUTION_LABEL = {
  existing_confirmed: "Bestehendem Nutzer zugeordnet",
  new_created: "Neuer Nutzer angelegt",
  rejected: "Abgelehnt",
};
const WELLPASS_FILTER_OPTIONEN = [
  { value: "", label: "Alle" },
  { value: "noch_nicht_geprueft", label: "Noch nicht geprüft" },
  ...Object.entries(WELLPASS_MATCH_KATEGORIE_LABEL).map(([value, label]) => ({ value, label })),
  { value: "bestaetigt", label: "Bestätigt (bestehender Nutzer)" },
  { value: "neu_angelegt", label: "Neuer Nutzer angelegt" },
  { value: "abgelehnt", label: "Abgelehnt" },
  { value: "rfid_konflikt", label: "RFID-Konflikt" },
];

function WellpassMatchBadge({ kategorie }) {
  if (!kategorie) return <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>–</span>;
  const farbe = WELLPASS_MATCH_KATEGORIE_FARBE[kategorie] || THEME.colors.textMuted;
  return (
    <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: THEME.fontSize.sm, fontWeight: 600, color: farbe, background: `${farbe}22`, whiteSpace: "nowrap" }}>
      {WELLPASS_MATCH_KATEGORIE_LABEL[kategorie] || kategorie}
    </span>
  );
}

// "Sonstige"-Abgleich (mirrort Phase 4/Wellpass, aber ohne Check-in-Historie/Aggregation)
const SONSTIGE_MATCH_KATEGORIE_LABEL = {
  sonstige_exact_match: "Exakter Treffer",
  sonstige_normalized_match: "Normalisierter Treffer",
  sonstige_suggested_match: "Ähnlicher Vorschlag",
  sonstige_ambiguous: "Mehrdeutig",
  sonstige_unmatched: "Kein Treffer",
};
const SONSTIGE_MATCH_KATEGORIE_FARBE = {
  sonstige_exact_match: THEME.colors.success,
  sonstige_normalized_match: THEME.colors.success,
  sonstige_suggested_match: THEME.colors.accentCyan,
  sonstige_ambiguous: THEME.colors.warning || "#d9a441",
  sonstige_unmatched: THEME.colors.danger,
};
const SONSTIGE_RESOLUTION_LABEL = {
  existing_confirmed: "Bestehendem Nutzer zugeordnet",
  new_created: "Neuer Nutzer angelegt",
  rejected: "Abgelehnt",
};
const SONSTIGE_FILTER_OPTIONEN = [
  { value: "", label: "Alle" },
  { value: "noch_nicht_geprueft", label: "Noch nicht geprüft" },
  ...Object.entries(SONSTIGE_MATCH_KATEGORIE_LABEL).map(([value, label]) => ({ value, label })),
  { value: "bestaetigt", label: "Bestätigt (bestehender Nutzer)" },
  { value: "neu_angelegt", label: "Neuer Nutzer angelegt" },
  { value: "abgelehnt", label: "Abgelehnt" },
  { value: "rfid_konflikt", label: "RFID-Konflikt" },
];

function SonstigeMatchBadge({ kategorie }) {
  if (!kategorie) return <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>–</span>;
  const farbe = SONSTIGE_MATCH_KATEGORIE_FARBE[kategorie] || THEME.colors.textMuted;
  return (
    <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: THEME.fontSize.sm, fontWeight: 600, color: farbe, background: `${farbe}22`, whiteSpace: "nowrap" }}>
      {SONSTIGE_MATCH_KATEGORIE_LABEL[kategorie] || kategorie}
    </span>
  );
}

function SonstigeNutzerAuswahlDialog({ zeile, onSchliessen, onAusgewaehlt }) {
  const [suche, setSuche] = useState("");
  const [ergebnisse, setErgebnisse] = useState([]);
  const [laedt, setLaedt] = useState(false);

  const handleSuche = async (q) => {
    setSuche(q);
    if (q.trim().length < 2) { setErgebnisse([]); return; }
    setLaedt(true);
    try {
      const res = await fetch(`${API}/sonstige/nutzer?suche=${encodeURIComponent(q.trim())}`);
      const data = await res.json();
      setErgebnisse((Array.isArray(data) ? data : []).slice(0, 20));
    } catch (e) { setErgebnisse([]); }
    setLaedt(false);
  };

  return (
    <div style={s.modalBg} onClick={onSchliessen}>
      <div style={{ ...s.modal, width: "min(560px, 95vw)" }} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitel}>Anderen Sonstige-Nutzer auswählen</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 16 }}>
          SafeSmart-Name: <strong style={{ color: THEME.colors.text }}>{zeile.source_name || "(leer)"}</strong>
        </p>
        <div style={s.inputGroup}>
          <label style={s.label}>Suche (Vorname, Nachname, RFID)</label>
          <input style={s.input} autoFocus value={suche} onChange={e => handleSuche(e.target.value)} placeholder="Suchen…" />
        </div>
        {laedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Suche läuft…</p>}
        <div style={{ maxHeight: 320, overflowY: "auto", background: THEME.colors.background, borderRadius: 8 }}>
          {ergebnisse.map(n => (
            <div key={n.id} onClick={() => onAusgewaehlt(n)}
              style={{ padding: "12px 16px", borderBottom: `1px solid ${THEME.colors.border}`, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ color: THEME.colors.text }}>
                {n.vorname} {n.nachname} <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>#{n.id}</span>
              </div>
              <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>
                {n.zutrittsstatus}{n.rfid_kartennummer ? ` · besitzt bereits RFID ${n.rfid_kartennummer}` : ""}
              </div>
            </div>
          ))}
          {!laedt && suche.trim().length >= 2 && ergebnisse.length === 0 && (
            <p style={{ color: THEME.colors.textMuted, padding: 16 }}>Keine Treffer.</p>
          )}
        </div>
        <button onClick={onSchliessen} style={{ ...s.btnGray, width: "auto", marginTop: 16 }}>Abbrechen</button>
      </div>
    </div>
  );
}

function SonstigeNutzerAnlegenDialog({ zeile, onSchliessen, onAnlegen }) {
  const vorschlagsTeile = (zeile.proposed_sonstige_name || zeile.source_name || "").trim().split(/\s+/);
  const [vorname, setVorname] = useState(vorschlagsTeile[0] || "");
  const [nachname, setNachname] = useState(vorschlagsTeile.slice(1).join(" ") || "");
  const [zutrittsstatus, setZutrittsstatus] = useState("aktiv");
  const [gueltigBis, setGueltigBis] = useState("");
  const [notiz, setNotiz] = useState("");

  return (
    <div style={s.modalBg} onClick={onSchliessen}>
      <div style={{ ...s.modal, width: "min(520px, 95vw)" }} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitel}>Als Sonstige-Nutzer anlegen</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 16 }}>
          SafeSmart-Name: <strong style={{ color: THEME.colors.text }}>{zeile.source_name || "(leer)"}</strong>
          {zeile.card_number && <> · Kartennummer: <strong style={{ color: THEME.colors.text }}>{zeile.card_number}</strong></>}
        </p>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ ...s.inputGroup, flex: 1 }}>
            <label style={s.label}>Vorname</label>
            <input style={s.input} value={vorname} onChange={e => setVorname(e.target.value)} />
          </div>
          <div style={{ ...s.inputGroup, flex: 1 }}>
            <label style={s.label}>Nachname</label>
            <input style={s.input} value={nachname} onChange={e => setNachname(e.target.value)} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ ...s.inputGroup, flex: 1 }}>
            <label style={s.label}>Zutrittsstatus</label>
            <select style={s.input} value={zutrittsstatus} onChange={e => setZutrittsstatus(e.target.value)}>
              <option value="aktiv">Aktiv</option>
              <option value="gesperrt">Gesperrt</option>
            </select>
          </div>
          <div style={{ ...s.inputGroup, flex: 1 }}>
            <label style={s.label}>Gültig bis</label>
            <input style={s.input} type="date" value={gueltigBis} onChange={e => setGueltigBis(e.target.value)} />
          </div>
        </div>
        <div style={s.inputGroup}>
          <label style={s.label}>Notiz (optional)</label>
          <textarea style={{ ...s.input, minHeight: 60 }} value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z.B. Grund/Kategorie: Kurzzeitmitgliedschaft, Präventionskurs…" />
        </div>
        {zeile.card_number && (
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginBottom: 12 }}>
            Die Kartennummer <strong style={{ color: THEME.colors.text }}>{zeile.card_number}</strong> wird beim Anlegen direkt als RFID hinterlegt.
          </p>
        )}
        <div style={{ display: "flex", gap: 12 }}>
          <button
            style={{ ...s.btn, width: "auto", opacity: vorname.trim() && nachname.trim() ? 1 : 0.5 }}
            disabled={!vorname.trim() || !nachname.trim()}
            onClick={() => onAnlegen({ vorname: vorname.trim(), nachname: nachname.trim(), zutrittsstatus, gueltig_bis: gueltigBis || null, notiz: notiz || null })}
          >
            Anlegen
          </button>
          <button style={{ ...s.btnGray, width: "auto" }} onClick={onSchliessen}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

function WellpassNutzerAuswahlDialog({ zeile, onSchliessen, onAusgewaehlt }) {
  const [suche, setSuche] = useState("");
  const [ergebnisse, setErgebnisse] = useState([]);
  const [laedt, setLaedt] = useState(false);

  const handleSuche = async (q) => {
    setSuche(q);
    if (q.trim().length < 2) { setErgebnisse([]); return; }
    setLaedt(true);
    try {
      const res = await fetch(`${API}/wellpass/nutzer?suche=${encodeURIComponent(q.trim())}`);
      const data = await res.json();
      setErgebnisse((Array.isArray(data) ? data : []).slice(0, 20));
    } catch (e) { setErgebnisse([]); }
    setLaedt(false);
  };

  return (
    <div style={s.modalBg} onClick={onSchliessen}>
      <div style={{ ...s.modal, width: "min(560px, 95vw)" }} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitel}>Anderen Wellpass-Nutzer auswählen</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 16 }}>
          SafeSmart-Name: <strong style={{ color: THEME.colors.text }}>{zeile.source_name || "(leer)"}</strong>
        </p>
        <div style={s.inputGroup}>
          <label style={s.label}>Suche (Vorname, Nachname, RFID, externe ID)</label>
          <input style={s.input} autoFocus value={suche} onChange={e => handleSuche(e.target.value)} placeholder="Suchen…" />
        </div>
        {laedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Suche läuft…</p>}
        <div style={{ maxHeight: 320, overflowY: "auto", background: THEME.colors.background, borderRadius: 8 }}>
          {ergebnisse.map(n => (
            <div key={n.id} onClick={() => onAusgewaehlt(n)}
              style={{ padding: "12px 16px", borderBottom: `1px solid ${THEME.colors.border}`, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ color: THEME.colors.text }}>
                {n.vorname} {n.nachname} <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>#{n.id}</span>
              </div>
              <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>
                {n.zutrittsstatus}{n.rfid_kartennummer ? ` · besitzt bereits RFID ${n.rfid_kartennummer}` : ""}
              </div>
            </div>
          ))}
          {!laedt && suche.trim().length >= 2 && ergebnisse.length === 0 && (
            <p style={{ color: THEME.colors.textMuted, padding: 16 }}>Keine Treffer.</p>
          )}
        </div>
        <button onClick={onSchliessen} style={{ ...s.btnGray, width: "auto", marginTop: 16 }}>Abbrechen</button>
      </div>
    </div>
  );
}

function WellpassNutzerAnlegenDialog({ zeile, onSchliessen, onAnlegen }) {
  const vorschlagsTeile = (zeile.proposed_wellpass_name || zeile.source_name || "").trim().split(/\s+/);
  const [vorname, setVorname] = useState(vorschlagsTeile[0] || "");
  const [nachname, setNachname] = useState(vorschlagsTeile.slice(1).join(" ") || "");
  const [zutrittsstatus, setZutrittsstatus] = useState("aktiv");
  const [notiz, setNotiz] = useState("");
  const [rueckwirkendZuordnen, setRueckwirkendZuordnen] = useState(true);

  return (
    <div style={s.modalBg} onClick={onSchliessen}>
      <div style={{ ...s.modal, width: "min(520px, 95vw)" }} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitel}>Als Wellpass-Nutzer anlegen</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 16 }}>
          SafeSmart-Name: <strong style={{ color: THEME.colors.text }}>{zeile.source_name || "(leer)"}</strong>
          {zeile.card_number && <> · Kartennummer: <strong style={{ color: THEME.colors.text }}>{zeile.card_number}</strong></>}
        </p>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ ...s.inputGroup, flex: 1 }}>
            <label style={s.label}>Vorname</label>
            <input style={s.input} value={vorname} onChange={e => setVorname(e.target.value)} />
          </div>
          <div style={{ ...s.inputGroup, flex: 1 }}>
            <label style={s.label}>Nachname</label>
            <input style={s.input} value={nachname} onChange={e => setNachname(e.target.value)} />
          </div>
        </div>
        <div style={s.inputGroup}>
          <label style={s.label}>Zutrittsstatus</label>
          <select style={s.input} value={zutrittsstatus} onChange={e => setZutrittsstatus(e.target.value)}>
            <option value="aktiv">Aktiv</option>
            <option value="gesperrt">Gesperrt</option>
          </select>
        </div>
        <div style={s.inputGroup}>
          <label style={s.label}>Notiz (optional)</label>
          <textarea style={{ ...s.input, minHeight: 60 }} value={notiz} onChange={e => setNotiz(e.target.value)} />
        </div>
        {(zeile.wellpass_checkin_count > 0) && (
          <div style={{ ...s.karte, background: THEME.colors.background, padding: 12, marginBottom: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
              <input type="checkbox" checked={rueckwirkendZuordnen} onChange={e => setRueckwirkendZuordnen(e.target.checked)} />
              {zeile.wellpass_checkin_count} bestehende Check-ins ({zeile.wellpass_first_checkin} – {zeile.wellpass_last_checkin}) rückwirkend zuordnen
            </label>
          </div>
        )}
        {zeile.card_number && (
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginBottom: 12 }}>
            Die Kartennummer <strong style={{ color: THEME.colors.text }}>{zeile.card_number}</strong> wird beim Anlegen direkt als RFID hinterlegt.
          </p>
        )}
        <div style={{ display: "flex", gap: 12 }}>
          <button
            style={{ ...s.btn, width: "auto", opacity: vorname.trim() && nachname.trim() ? 1 : 0.5 }}
            disabled={!vorname.trim() || !nachname.trim()}
            onClick={() => onAnlegen({ vorname: vorname.trim(), nachname: nachname.trim(), zutrittsstatus, notiz: notiz || null, rueckwirkend_zuordnen: rueckwirkendZuordnen })}
          >
            Anlegen
          </button>
          <button style={{ ...s.btnGray, width: "auto" }} onClick={onSchliessen}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

function SafeSmartBadge({ kategorie }) {
  const farbe = SAFESMART_KATEGORIE_FARBE[kategorie] || THEME.colors.textMuted;
  return (
    <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: THEME.fontSize.sm, fontWeight: 600, color: farbe, background: `${farbe}22`, whiteSpace: "nowrap" }}>
      {SAFESMART_KATEGORIE_LABEL[kategorie] || kategorie}
    </span>
  );
}

function SafeSmartMitgliedAuswahlDialog({ zeile, onSchliessen, onAusgewaehlt }) {
  const [suche, setSuche] = useState("");
  const [ergebnisse, setErgebnisse] = useState([]);
  const [laedt, setLaedt] = useState(false);

  const handleSuche = async (q) => {
    setSuche(q);
    if (q.trim().length < 2) { setErgebnisse([]); return; }
    setLaedt(true);
    try {
      const res = await fetch(`${API}/mitglieder/?suche=${encodeURIComponent(q.trim())}`);
      const data = await res.json();
      setErgebnisse(data.slice(0, 20));
    } catch (e) { setErgebnisse([]); }
    setLaedt(false);
  };

  return (
    <div style={s.modalBg} onClick={onSchliessen}>
      <div style={{ ...s.modal, width: "min(560px, 95vw)" }} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitel}>Anderes Mitglied auswählen</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 16 }}>
          SafeSmart-Name: <strong style={{ color: THEME.colors.text }}>{zeile.source_name || "(leer)"}</strong>
        </p>
        <div style={s.inputGroup}>
          <label style={s.label}>Suche (Vorname, Nachname, Mitgliedsnummer, E-Mail)</label>
          <input style={s.input} autoFocus value={suche} onChange={e => handleSuche(e.target.value)} placeholder="Suchen…" />
        </div>
        {laedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Suche läuft…</p>}
        <div style={{ maxHeight: 320, overflowY: "auto", background: THEME.colors.background, borderRadius: 8 }}>
          {ergebnisse.map(m => (
            <div key={m.id} onClick={() => onAusgewaehlt(m)}
              style={{ padding: "12px 16px", borderBottom: `1px solid ${THEME.colors.border}`, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ color: THEME.colors.text }}>
                {m.vorname} {m.nachname} <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>#{m.id}</span>
              </div>
              <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>
                {m.status}{m.geburtsdatum ? ` · ${m.geburtsdatum}` : ""}{m.rfid_kartennummer ? ` · besitzt bereits RFID ${m.rfid_kartennummer}` : ""}
              </div>
            </div>
          ))}
          {!laedt && suche.trim().length >= 2 && ergebnisse.length === 0 && (
            <p style={{ color: THEME.colors.textMuted, padding: 16 }}>Keine Treffer.</p>
          )}
        </div>
        <button onClick={onSchliessen} style={{ ...s.btnGray, width: "auto", marginTop: 16 }}>Abbrechen</button>
      </div>
    </div>
  );
}

function SafeSmartIgnorierenDialog({ onSchliessen, onBestaetigt }) {
  const [grund, setGrund] = useState(SAFESMART_IGNORIEREN_GRUENDE[0]);
  const [freitext, setFreitext] = useState("");
  return (
    <div style={s.modalBg} onClick={onSchliessen}>
      <div style={{ ...s.modal, width: "min(480px, 95vw)" }} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitel}>Zeile ignorieren</h3>
        <div style={s.inputGroup}>
          <label style={s.label}>Grund</label>
          <select style={s.input} value={grund} onChange={e => setGrund(e.target.value)}>
            {SAFESMART_IGNORIEREN_GRUENDE.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        {grund === "sonstiger Grund" && (
          <div style={s.inputGroup}>
            <label style={s.label}>Freitext</label>
            <textarea style={{ ...s.input, minHeight: 80 }} value={freitext} onChange={e => setFreitext(e.target.value)} />
          </div>
        )}
        <div style={{ display: "flex", gap: 12 }}>
          <button style={{ ...s.btn, width: "auto" }} onClick={() => onBestaetigt(grund === "sonstiger Grund" ? freitext : grund)}>Ignorieren</button>
          <button style={{ ...s.btnGray, width: "auto" }} onClick={onSchliessen}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

function SafeSmartZeilenDetailDialog({ zeile, onSchliessen }) {
  const feld = (label, wert) => (
    <p style={{ margin: "4px 0", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>
      <span style={{ color: THEME.colors.textMuted }}>{label}: </span>
      <span style={{ color: THEME.colors.text }}>{wert || "–"}</span>
    </p>
  );
  return (
    <div style={s.modalBg} onClick={onSchliessen}>
      <div style={{ ...s.modal, width: "min(640px, 95vw)" }} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitel}>Zeile {zeile.row_number}: {zeile.source_name || "(leer)"}</h3>

        <h4 style={{ color: THEME.colors.text, marginTop: 16, marginBottom: 4 }}>SafeSmart-Daten</h4>
        {feld("Originalname", zeile.source_name)}
        {feld("Normalisiert", zeile.normalized_name)}
        {feld("Kartennummer", zeile.card_number)}
        {feld("Staff Type", zeile.staff_type)}

        <h4 style={{ color: THEME.colors.text, marginTop: 16, marginBottom: 4 }}>Automatischer Vorschlag</h4>
        <p style={{ margin: "4px 0" }}><SafeSmartBadge kategorie={zeile.original_category} /></p>
        {feld("Vorgeschlagenes Mitglied", zeile.matched_member_name)}
        {feld("Mitgliedsstatus", zeile.matched_member_status)}
        {feld("Übereinstimmung", zeile.confidence_score != null ? `${zeile.confidence_score}%` : null)}
        {feld("Matching-Grund", zeile.match_reason)}
        {zeile.alternative_candidates && zeile.alternative_candidates.length > 0 && (
          <p style={{ margin: "4px 0", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
            Alternative Kandidaten: {zeile.alternative_candidates.map(a => `${a.matched_member_name} (${a.confidence_score}%)`).join(", ")}
          </p>
        )}

        <h4 style={{ color: THEME.colors.text, marginTop: 16, marginBottom: 4 }}>Konflikte</h4>
        <p style={{ margin: "4px 0", color: zeile.rfid_conflict?.length ? THEME.colors.danger : THEME.colors.textMuted }}>
          {zeile.rfid_conflict && zeile.rfid_conflict.length > 0 ? zeile.rfid_conflict.join("; ") : "Keine"}
        </p>
        {zeile.existing_rfid_owner && feld("Karte bereits vergeben an", zeile.existing_rfid_owner)}
        {zeile.existing_member_rfid && feld("Vorgeschlagenes Mitglied besitzt bereits RFID", zeile.existing_member_rfid)}
        {zeile.mehrfachzuordnung_konflikt && (
          <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>
            Dieses Mitglied ist in diesem Importlauf mehreren Zeilen zugeordnet.
          </p>
        )}

        <h4 style={{ color: THEME.colors.text, marginTop: 16, marginBottom: 4 }}>Entscheidung</h4>
        {feld("Aktuell", SAFESMART_DECISION_LABEL[zeile.decision])}
        {feld("Ausgewähltes Mitglied", zeile.selected_member_name)}
        {feld("Notiz", zeile.note)}
        {feld("Entschieden von", zeile.decided_by)}
        {feld("Entschieden am", zeile.decided_at ? new Date(zeile.decided_at).toLocaleString("de-DE") : null)}

        <button onClick={onSchliessen} style={{ ...s.btnGray, width: "auto", marginTop: 16 }}>Schließen</button>
      </div>
    </div>
  );
}

function RfidZielAuswahlDialog({ onSchliessen, onAusgewaehlt, typLabel, benutzerId, titel = "Anderer Person zuordnen" }) {
  const [suche, setSuche] = useState("");
  const [ergebnisse, setErgebnisse] = useState([]);
  const [laedt, setLaedt] = useState(false);

  const handleSuche = async (q) => {
    setSuche(q);
    if (q.trim().length < 2) { setErgebnisse([]); return; }
    setLaedt(true);
    try {
      const res = await fetch(`${API}/safesmart/rfid-ziel-suche?q=${encodeURIComponent(q.trim())}&benutzer_id=${benutzerId}`);
      const data = await res.json();
      setErgebnisse(Array.isArray(data) ? data : []);
    } catch (e) { setErgebnisse([]); }
    setLaedt(false);
  };

  return (
    <div style={s.modalBg} onClick={onSchliessen}>
      <div style={{ ...s.modal, width: "min(560px, 95vw)" }} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitel}>{titel}</h3>
        <div style={s.inputGroup}>
          <label style={s.label}>Suche (Vorname, Nachname, E-Mail) über alle Personentypen</label>
          <input style={s.input} autoFocus value={suche} onChange={e => handleSuche(e.target.value)} placeholder="Suchen…" />
        </div>
        {laedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Suche läuft…</p>}
        <div style={{ maxHeight: 320, overflowY: "auto", background: THEME.colors.background, borderRadius: 8 }}>
          {ergebnisse.map(p => (
            <div key={`${p.typ}-${p.id}`} onClick={() => onAusgewaehlt(p)}
              style={{ padding: "12px 16px", borderBottom: `1px solid ${THEME.colors.border}`, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ color: THEME.colors.text }}>
                {p.name} <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{typLabel[p.typ]} #{p.id}</span>
              </div>
              <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>
                {p.status}{p.hat_rfid ? ` · besitzt bereits RFID ${p.rfid_kartennummer}` : ""}
              </div>
            </div>
          ))}
          {!laedt && suche.trim().length >= 2 && ergebnisse.length === 0 && (
            <p style={{ color: THEME.colors.textMuted, padding: 16 }}>Keine Treffer.</p>
          )}
        </div>
        <button onClick={onSchliessen} style={{ ...s.btnGray, width: "auto", marginTop: 16 }}>Abbrechen</button>
      </div>
    </div>
  );
}

const RFID_SUCHE_TYP_LABEL = { mitglied: "Mitglied", wellpass: "Wellpass-Nutzer", hansefit: "Hansefit-Nutzer", mitarbeiter: "Mitarbeiter", sonstige: "Sonstige", probetraining: "Probetraining" };

// Umgekehrte Suchrichtung zu "Karte/Band gefunden": statt einer Kartennummer wird ein Name
// eingegeben (z.B. "Müller") - findet die Person(en) und zeigt direkt, ob bereits eine RFID-
// Karte hinterlegt ist. Ohne RFID -> Eingabefeld fuer eine neue Kartennummer (Zuweisen). Mit
// RFID -> gleiches Eingabefeld, Button "Ersetzen" - /rfid-neu-zuordnen uebernimmt die eigentliche
// Zuweisung ohnehin schon fuer beide Faelle (leere wie belegte Zielperson), inkl. der 409-
// Rueckfrage "Zielperson hat bereits eine andere Karte, trotzdem ersetzen?" - hier nur ueber die
// entgegengesetzte Einstiegsrichtung (Name zuerst statt Karte zuerst) erreichbar gemacht.
function RfidNamensSucheBox({ benutzerId, suche, onSucheChange }) {
  const [ergebnisse, setErgebnisse] = useState([]);
  const [laedt, setLaedt] = useState(false);
  const [ausgewaehlt, setAusgewaehlt] = useState(null);
  const [neueKarte, setNeueKarte] = useState("");
  const [meldung, setMeldung] = useState(null);

  // "suche" kommt kontrolliert vom gemeinsamen Eingabefeld der Elternkomponente (siehe
  // RfidSucheWidget) - ein einzelnes Feld erkennt selbst, ob Kartennummer oder Name eingegeben
  // wurde, damit man nicht zwei getrennte Suchfelder braucht (Nutzer-Feedback 2026-07-20).
  useEffect(() => {
    setAusgewaehlt(null);
    setMeldung(null);
    if (suche.trim().length < 2) { setErgebnisse([]); return; }
    let abgebrochen = false;
    setLaedt(true);
    fetch(`${API}/safesmart/rfid-ziel-suche?q=${encodeURIComponent(suche.trim())}&benutzer_id=${benutzerId}`)
      .then(r => r.json())
      .then(d => { if (!abgebrochen) setErgebnisse(Array.isArray(d) ? d : []); })
      .catch(() => { if (!abgebrochen) setErgebnisse([]); })
      .finally(() => { if (!abgebrochen) setLaedt(false); });
    return () => { abgebrochen = true; };
  }, [suche, benutzerId]);

  const handleAuswaehlen = (p) => {
    setAusgewaehlt(p);
    setErgebnisse([]);
    if (onSucheChange) onSucheChange(p.name);
    setNeueKarte("");
    setMeldung(null);
  };

  const handleZuweisen = async (confirmReplace = false) => {
    let karte = neueKarte.trim();
    const umgerechnet = pcLeserZuKartennummer(karte);
    if (umgerechnet != null) { karte = String(umgerechnet); setNeueKarte(karte); }
    if (!karte) { alert("Bitte Kartennummer eingeben."); return; }
    const res = await fetch(`${API}/safesmart/rfid-neu-zuordnen?benutzer_id=${benutzerId}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kartennummer: karte, ziel_typ: ausgewaehlt.typ, ziel_id: ausgewaehlt.id, confirm_replace_target_rfid: confirmReplace }),
    });
    if (res.status === 409) {
      const data = await res.json().catch(() => ({}));
      const detail = data.detail || {};
      if (detail.bestehende_ziel_rfid) {
        if (window.confirm(`${detail.message}\n\nBestehende RFID der Zielperson: ${detail.bestehende_ziel_rfid}\n\nTrotzdem ersetzen?`)) {
          await handleZuweisen(true);
        }
        return;
      }
      alert(`${detail.message || "Datenkonflikt."}\n\nBetroffene Datensätze: ${(detail.alle_inhaber || []).map(i => `${i.name} (${RFID_SUCHE_TYP_LABEL[i.typ]})`).join(", ")}`);
      return;
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(typeof data.detail === "string" ? data.detail : "Zuordnung fehlgeschlagen.");
      return;
    }
    const data = await res.json();
    setMeldung(
      `✓ Karte ${karte} jetzt bei ${data.neuer_inhaber.name} (${RFID_SUCHE_TYP_LABEL[data.neuer_inhaber.typ]})` +
      (data.alter_inhaber ? ` – vorher bei ${data.alter_inhaber.name} (${RFID_SUCHE_TYP_LABEL[data.alter_inhaber.typ]})` : "")
    );
    setAusgewaehlt(a => ({ ...a, hat_rfid: true, rfid_kartennummer: karte }));
    setNeueKarte("");
  };

  return (
    <div style={{ marginTop: 12 }}>
      {laedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 6px" }}>Sucht…</p>}
      {ergebnisse.length > 0 && (
        <div style={{ maxHeight: 260, overflowY: "auto", background: THEME.colors.background, borderRadius: 8, marginTop: 6, maxWidth: 420 }}>
          {ergebnisse.map(p => (
            <div key={`${p.typ}-${p.id}`} onClick={() => handleAuswaehlen(p)}
              style={{ padding: "10px 14px", borderBottom: `1px solid ${THEME.colors.border}`, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ color: THEME.colors.text }}>{p.name} <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{RFID_SUCHE_TYP_LABEL[p.typ]} #{p.id}</span></div>
              <div style={{ color: p.hat_rfid ? THEME.colors.textMuted : THEME.colors.warning, fontSize: THEME.fontSize.xs }}>
                {p.hat_rfid ? `hat bereits RFID ${p.rfid_kartennummer}` : "keine RFID-Karte"}
              </div>
            </div>
          ))}
        </div>
      )}
      {!laedt && suche.trim().length >= 2 && ergebnisse.length === 0 && !ausgewaehlt && (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "6px 0 0" }}>Keine Treffer.</p>
      )}
      {ausgewaehlt && (
        <div style={{ marginTop: 10 }}>
          <p style={{ color: ausgewaehlt.hat_rfid ? THEME.colors.text : THEME.colors.warning, margin: "0 0 8px" }}>
            <strong>{ausgewaehlt.name}</strong> ({RFID_SUCHE_TYP_LABEL[ausgewaehlt.typ]}){" "}
            {ausgewaehlt.hat_rfid ? <>hat bereits die RFID-Karte <strong>{ausgewaehlt.rfid_kartennummer}</strong>.</> : "hat noch keine RFID-Karte."}
          </p>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input style={{ ...s.input, maxWidth: 240 }} value={neueKarte} onChange={e => setNeueKarte(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleZuweisen(false); }}
              placeholder="Neue Kartennummer eingeben oder scannen…" autoFocus />
            <button onClick={() => handleZuweisen(false)} style={{ ...s.btn, width: "auto" }}>{ausgewaehlt.hat_rfid ? "Ersetzen" : "Zuweisen"}</button>
          </div>
        </div>
      )}
      {meldung && <p style={{ color: THEME.colors.success, marginTop: 10, marginBottom: 0 }}>{meldung}</p>}
    </div>
  );
}

// "Tür öffnen" (Fernöffnung) sitzt seit 2026-10-28 oben im Dashboard-Kopf neben "Cache leeren" (Nutzer-Wunsch),
// nicht mehr im TTLock-Widget unter "Heute eingecheckt". Bewusst OHNE Bestaetigungsdialog (Nutzer-Wunsch
// 2026-07-24: "direkt oeffnen, wichtiger ist die Fehlermeldung falls es nicht klappt") - der Endpunkt selbst
// liefert bei einem TTLock-Fehler trotzdem HTTP 200 mit einem "error"-Feld im Body statt eines 4xx/5xx (siehe
// fernoeffnen() im Backend), deshalb muss hier explizit auf daten.error geprueft werden, nicht nur auf res.ok.
// Erfolgsmeldung verschwindet nach ein paar Sekunden, Fehlermeldungen bleiben bis zum naechsten Versuch stehen.
function TuerOeffnenKnopf({ rechte }) {
  const [syncModus, setSyncModus] = useState(null);
  const [oeffnetGerade, setOeffnetGerade] = useState(false);
  const [meldung, setMeldung] = useState(null); // { text, fehler }
  const darf = rechte?.rolle === "admin" || !!rechte?.module?.ttlock_fernoeffnen;

  useEffect(() => {
    if (!darf) return;
    fetch(`${API}/ttlock/konfigurationsstatus`).then(r => r.ok ? r.json() : null).then(d => setSyncModus(d ? d.sync_modus : null)).catch(() => {});
  }, [darf]);

  useEffect(() => {
    if (!meldung || meldung.fehler) return undefined;
    const timer = setTimeout(() => setMeldung(null), 6000);
    return () => clearTimeout(timer);
  }, [meldung]);

  if (!darf) return null;

  const oeffnen = async () => {
    setOeffnetGerade(true);
    setMeldung(null);
    try {
      const res = await fetch(`${API}/ttlock/fernoeffnen`, { method: "POST" });
      const daten = await res.json();
      if (!res.ok) {
        setMeldung({ text: daten.detail || "Tür konnte nicht geöffnet werden.", fehler: true });
      } else if (daten.error) {
        setMeldung({ text: `Tür konnte nicht geöffnet werden: ${daten.error}`, fehler: true });
      } else if (daten.executed === false) {
        setMeldung({ text: `Nicht ausgeführt (Modus: ${daten.mode || syncModus}) – TTLock ist noch nicht live.`, fehler: true });
      } else {
        setMeldung({ text: "Tür wurde geöffnet.", fehler: false });
      }
    } catch {
      setMeldung({ text: "Tür konnte nicht geöffnet werden (Verbindungsfehler).", fehler: true });
    }
    setOeffnetGerade(false);
  };

  return (
    <>
      <button onClick={oeffnen} disabled={oeffnetGerade || syncModus !== "live"}
        style={{ ...s.btn, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm, opacity: syncModus === "live" ? 1 : 0.5 }}
        title={syncModus !== "live" ? "Erst verfügbar, sobald TTLock live geschaltet ist" : ""}>
        {oeffnetGerade ? "Öffnet…" : "🔓 Tür öffnen"}
      </button>
      {meldung && <span role="status" style={{ color: meldung.fehler ? THEME.colors.danger : THEME.colors.success, fontSize: THEME.fontSize.sm }}>{meldung.text}</span>}
    </>
  );
}

// Dashboard-Widget-Version der "Karte/Band gefunden"-Box aus SafeSmartImportSeite - bewusst
// eigenstaendig dupliziert statt die dortige, bereits laufende Logik umzubauen/zu teilen (gleiche
// Konvention wie an anderen Stellen dieses Projekts, siehe z.B. Wellpass-/Sonstige-Matching).
function TTLockStatusWidget({ navigiere, rechte }) {
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [listeOffen, setListeOffen] = useState(false);
  const [synchronisiertGerade, setSynchronisiertGerade] = useState(false);
  const [syncMeldung, setSyncMeldung] = useState(null);
  const [fehlerSyncErgebnisse, setFehlerSyncErgebnisse] = useState(null);

  const ladeDashboard = () => fetch(`${API}/ttlock/dashboard-status`).then(r => r.ok ? r.json() : null).then(setDashboard).catch(() => {});
  const [wifiStatus, setWifiStatus] = useState(null);

  useEffect(() => {
    fetch(`${API}/ttlock/konfigurationsstatus`).then(r => r.ok ? r.json() : null).then(setStatus).catch(() => {});
    ladeDashboard();
    // Kleine technische WLAN-Check-in-Statusanzeige (Agent online/offline) - bewusst nicht
    // prominent, nur eine unauffaellige Zeile ganz unten im Widget, siehe Render weiter unten.
    fetch(`${API}/api/wifi/admin/status`).then(r => r.ok ? r.json() : null).then(setWifiStatus).catch(() => {});
  }, []);

  // Manueller Anstoss des Protokoll-Abrufs (sonst nur alle 15 Min per Cron) - Nutzer-Wunsch
  // 2026-07-20, nachdem "Heute eingecheckt" veraltet war, weil der Cron zu diesem Zeitpunkt noch
  // nicht im Crontab eingetragen war. Seit 2026-07-24 stoesst derselbe Knopf zusaetzlich einen
  // erneuten Versuch fuer alle aktuell FEHLGESCHLAGENEN Karten-Synchronisierungen an (bewusst nicht
  // alle "ausstehenden" - die meisten davon sind Absicht, Teil des schrittweisen manuellen
  // Rollouts, siehe synchronisiere_fehlerhafte() im Backend).
  const handleProtokollSynchronisieren = async () => {
    setSynchronisiertGerade(true);
    setSyncMeldung(null);
    setFehlerSyncErgebnisse(null);
    try {
      const res = await fetch(`${API}/ttlock/protokoll/abrufen`, { method: "POST" });
      const daten = await res.json();
      if (!res.ok) { setSyncMeldung(daten.detail || "Fehler beim Synchronisieren."); }
      else {
        let text = `${daten.neue_eintraege} neue Ereignisse geladen.`;
        try {
          const resFehler = await fetch(`${API}/ttlock/synchronisiere-fehlerhafte`, { method: "POST" });
          if (resFehler.ok) {
            const datenFehler = await resFehler.json();
            if (datenFehler.anzahl > 0) {
              text += ` ${datenFehler.behoben} von ${datenFehler.anzahl} Karten-Fehlern behoben.`;
              setFehlerSyncErgebnisse(datenFehler.ergebnisse);
            }
          }
        } catch { /* Karten-Retry ist optional - Protokoll-Ergebnis bleibt trotzdem gueltig */ }
        setSyncMeldung(text);
        await ladeDashboard();
      }
    } catch {
      setSyncMeldung("Fehler beim Synchronisieren.");
    }
    setSynchronisiertGerade(false);
  };

  if (!status) return <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Lädt…</p>;

  // Eindeutige Personen heute: TTLock/WLAN (primaer) + zusaetzlich Personen, die NUR ueber Milon-Training erkannt wurden (Bronze). Aeltere API-Staende ohne die neuen Felder zeigen weiter nur TTLock.
  const anzahlErkannt = dashboard ? (dashboard.heute_erkannt ?? dashboard.heute_eingecheckt) : 0;
  const erkanntListe = dashboard ? (dashboard.heute_erkannt_liste ?? dashboard.heute_eingecheckt_liste) : [];
  const BRONZE = "#cd7f32";

  return (
    <div>
      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl, margin: "0 0 8px", display: "flex", alignItems: "center", gap: 8 }}>
        <span>Heute eingecheckt: {dashboard ? anzahlErkannt : "…"}</span>
        {dashboard && anzahlErkannt > 0 && (
          <span onClick={() => setListeOffen(o => !o)} style={{ color: THEME.colors.primary, cursor: "pointer", fontSize: THEME.fontSize.base }}>
            {listeOffen ? "▲" : "▼"}
          </span>
        )}
        <span onClick={synchronisiertGerade ? undefined : handleProtokollSynchronisieren}
          title="Zutrittsprotokoll jetzt manuell abrufen (läuft sonst automatisch alle 15 Min) + fehlgeschlagene Karten-Synchronisierungen erneut versuchen"
          style={{ color: THEME.colors.textMuted, cursor: synchronisiertGerade ? "default" : "pointer", fontSize: THEME.fontSize.base, opacity: synchronisiertGerade ? 0.5 : 1 }}>
          {synchronisiertGerade ? "⏳" : "🔄"}
        </span>
      </p>
      {syncMeldung && <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 8px" }}>{syncMeldung}</p>}
      {fehlerSyncErgebnisse && fehlerSyncErgebnisse.length > 0 && (
        <div style={{ background: THEME.colors.background, borderRadius: 8, padding: "8px 12px", margin: "0 0 8px" }}>
          {fehlerSyncErgebnisse.map(e => {
            const behoben = e.sync_status === "synchronisiert" || e.sync_status === "synchronisiert_unbestaetigt";
            return (
              <p key={`${e.person_typ}-${e.person_id}`} style={{ color: behoben ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.xs, margin: "2px 0" }}>
                {behoben ? "✓" : "✗"} {e.name}{!behoben && e.fehlertext ? `: ${e.fehlertext}` : ""}
              </p>
            );
          })}
        </div>
      )}
      {listeOffen && dashboard && anzahlErkannt > 0 && (
        <div style={{ background: THEME.colors.background, borderRadius: 8, padding: "8px 12px", margin: "0 0 8px" }}>
          {erkanntListe.map((e, idx) => e.quelle === "milon" ? (
            <p key={idx} data-testid="eingecheckt-milon" title="Hat heute am Milon-Zirkel trainiert – kein Nachweis für aktuelle Anwesenheit"
              style={{ position: "relative", margin: "4px 0", fontSize: THEME.fontSize.sm, color: BRONZE, display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <rect x="1" y="8" width="3" height="8" rx="1" /><rect x="4" y="5" width="3" height="14" rx="1" /><rect x="7" y="11" width="10" height="2" /><rect x="17" y="5" width="3" height="14" rx="1" /><rect x="20" y="8" width="3" height="8" rx="1" />
                </svg>
                <span>{e.person_name}<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}> (Milon-Training, kein Anwesenheitsnachweis)</span></span>
              </span>
              <span style={{ flexShrink: 0 }}>{new Date(e.zeitpunkt + "Z").toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span>
            </p>
          ) : (
            <p key={idx} style={{ margin: "4px 0", fontSize: THEME.fontSize.sm, color: THEME.colors.text, display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>{e.person_name}</span>
              <span style={{ color: THEME.colors.textMuted }}>{new Date(e.zeitpunkt + "Z").toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span>
            </p>
          ))}
          {dashboard.heute_nur_milon > 0 && (
            <p style={{ margin: "8px 0 0", fontSize: THEME.fontSize.xs, color: THEME.colors.textMuted }}>
              Bronze = nur über Milon-Training erkannt (Uhrzeit = Trainingsbeginn, kein Zutrittsnachweis).
            </p>
          )}
        </div>
      )}
      {dashboard && dashboard.fehlgeschlagene_versuche_heute > 0 && (
        <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: "0 0 8px" }}>
          ⚠ {dashboard.fehlgeschlagene_versuche_heute} fehlgeschlagene{dashboard.fehlgeschlagene_versuche_heute === 1 ? "r" : ""} Zutrittsversuch{dashboard.fehlgeschlagene_versuche_heute === 1 ? "" : "e"} heute
        </p>
      )}
      {/* WLAN-Check-in-Statuszeile auf Nutzerwunsch 2026-09-28 vorerst ausgeblendet (Feature laeuft
          noch nicht) - wifiStatus wird weiterhin geladen, damit die Zeile bei Bedarf einfach wieder
          einkommentiert werden kann, sobald WLAN-Check-in tatsaechlich in Betrieb geht. */}
      {false && rechte?.rolle === "admin" && wifiStatus && (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 10 }}>
          WLAN-Check-in: {wifiStatus.online ? "🟢 Online" : "⚪ Kein aktueller Agent"}
          {wifiStatus.letzte_abfrage_am && ` · zuletzt vor ${Math.max(0, Math.round((Date.now() - new Date(wifiStatus.letzte_abfrage_am + "Z").getTime()) / 1000))}s`}
          {` · ${wifiStatus.registrierte_geraete} Gerät${wifiStatus.registrierte_geraete === 1 ? "" : "e"}`}
        </p>
      )}
    </div>
  );
}

// Zeigt Karten/Baender, die schon direkt in TTLock registriert wurden (z.B. Ground-Truth-Tests),
// aber noch keiner ComeFit-Person zugeordnet sind - Gegenstueck zu TTLockZutrittZeiles "Von TTLock
// uebernehmen"-Button, hier aber von der Uebersicht aus statt aus einem einzelnen Profil heraus
// (Nutzer-Wunsch 2026-07-20). Nutzt denselben Uebernahme-Endpunkt, sobald eine Zielperson gewaehlt
// wurde, und den bereits bestehenden RfidZielAuswahlDialog fuer die typuebergreifende Personensuche.
function TTLockUnzugeordneteKartenWidget({ benutzerId, benutzer }) {
  const [karten, setKarten] = useState(null);
  const [ladeFehler, setLadeFehler] = useState("");
  const [zuordnenKarte, setZuordnenKarte] = useState(null);
  const [neuAnlegenKarte, setNeuAnlegenKarte] = useState(null);
  const [neuAnlegenTyp, setNeuAnlegenTyp] = useState("mitglied");
  const [zuordnetGerade, setZuordnetGerade] = useState(false);
  const [meldung, setMeldung] = useState("");
  // Ersetzt window.confirm(...) fuer die "Karte gehoert schon jemand anderem - trotzdem
  // uebernehmen?"-Rueckfrage sowie fuer "Karte wirklich loeschen?" - native confirm()/alert()-
  // Dialoge werden in manchen Android-Homescreen-/PWA-Kontexten stillschweigend unterdrueckt,
  // der Klick wirkt dann einfach wirkungslos (gleicher Bug wie im RFID-Suchfeld, siehe
  // RfidSucheWidget/rfidErsetzenAnfrage - hier dieselbe Korrektur fuer dieses zweite Widget).
  const [ersetzenAnfrage, setErsetzenAnfrage] = useState(null);
  const [loeschenAnfrage, setLoeschenAnfrage] = useState(null);

  const laden = () => {
    fetch(`${API}/ttlock/nicht-zugeordnete-karten`).then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { setKarten(Array.isArray(d) ? d : []); setLadeFehler(""); })
      .catch(() => setLadeFehler("Konnte nicht geladen werden."));
  };
  useEffect(laden, []);

  // Uebernahme-Endpunkt fuer eine bestehende ODER gerade erst neu angelegte Person - beide Wege
  // (bestehende Person auswaehlen, oder "+ Neu anlegen") landen am Ende hier, damit die Karte in
  // TTLock korrekt umbenannt (Loeschen+Neuanlegen) und als manuell_bestaetigt markiert wird, statt
  // nur das rfid_kartennummer-Feld beim Anlegen mitzugeben (das wuerde keine TTLock-Umbenennung
  // ausloesen und im TTLock-System eine verwaiste zweite Karte hinterlassen).
  const uebernehmen = async (typ, personId, karte, confirmReplaceOtherHolder = false) => {
    const res = await fetch(`${API}/ttlock/personen/${typ}/${personId}/von-ttlock-uebernehmen`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ card_id: karte.cardId, card_nummer: String(karte.cardNumber), confirm_replace_other_holder: confirmReplaceOtherHolder }),
    }).catch(() => null);
    return res;
  };

  // Gemeinsame Auswertung fuer beide Zuordnen-Wege: bei 409 mit bisheriger_inhaber fragt ein
  // window.confirm nach, ob die Karte dem bisherigen Inhaber (z.B. ein ehemaliges Mitglied, dessen
  // rfid_kartennummer nie geleert wurde) entzogen und der neuen Person zugewiesen werden soll -
  // bei "Ja" wird derselbe Aufruf mit confirm_replace_other_holder=true wiederholt.
  const zuordnungAuswerten = async (typ, personId, karte, personName, erfolgstext) => {
    let res = await uebernehmen(typ, personId, karte);
    if (res && res.status === 409) {
      const d = await res.json().catch(() => null);
      const inhaber = d?.detail?.bisheriger_inhaber;
      if (inhaber) {
        // Eigenes Bestaetigungsfenster statt window.confirm(...) - siehe Kommentar bei
        // ersetzenAnfrage weiter oben. Rueckfrage wird ueber handleErsetzenBestaetigt fortgesetzt.
        setErsetzenAnfrage({ typ, personId, karte, personName, erfolgstext, message: d.detail.message, inhaberName: inhaber.name });
        return null;
      }
    }
    if (res && res.ok) {
      // Die Umbenennung am Schloss laeuft seit 2026-09-11 im Hintergrund statt die Antwort zu
      // blockieren (Nutzer-Meldung "hängt sich auf ladet ewig") - Zuordnung selbst ist aber schon
      // gespeichert.
      setMeldung(`${erfolgstext} (Kartenname wird im Hintergrund aktualisiert.)`);
      return true;
    }
    const d = await res?.json().catch(() => null);
    const text = typeof d?.detail === "string" ? d.detail : (d?.detail?.konflikte ? d.detail.konflikte.join(", ") : d?.detail?.message || "Zuordnung fehlgeschlagen.");
    if (text) alert(text);
    return false;
  };

  const handleErsetzenBestaetigt = async () => {
    const a = ersetzenAnfrage;
    setErsetzenAnfrage(null);
    setZuordnetGerade(true);
    const res = await uebernehmen(a.typ, a.personId, a.karte, true);
    if (res && res.ok) {
      setMeldung(`${a.erfolgstext} (Kartenname wird im Hintergrund aktualisiert.)`);
      setZuordnenKarte(null);
      laden();
    } else {
      const d = await res?.json().catch(() => null);
      alert(typeof d?.detail === "string" ? d.detail : (d?.detail?.message || "Zuordnung fehlgeschlagen."));
    }
    setZuordnetGerade(false);
  };

  const handleAusgewaehlt = async (person) => {
    setZuordnetGerade(true);
    setMeldung("");
    const erfolg = await zuordnungAuswerten(person.typ, person.id, zuordnenKarte, person.name,
      `✓ "${zuordnenKarte.cardName || "(ohne Namen)"}" jetzt bei ${person.name} (${RFID_SUCHE_TYP_LABEL[person.typ]}).`);
    if (erfolg) { setZuordnenKarte(null); laden(); }
    setZuordnetGerade(false);
  };

  // Gegenstueck zu "Zuordnen"/"Neu anlegen": entfernt eine Karte komplett aus TTLock, wenn sie
  // gar keine dauerhafte Zuordnung braucht (z.B. ein einmaliges Probetag-Band).
  const handleKarteLoeschen = async (karte) => {
    setLoeschenAnfrage(karte);
  };

  const handleKarteLoeschenBestaetigt = async () => {
    const karte = loeschenAnfrage;
    setLoeschenAnfrage(null);
    setZuordnetGerade(true);
    setMeldung("");
    const res = await fetch(`${API}/ttlock/nicht-zugeordnete-karten/${karte.cardId}`, { method: "DELETE" }).catch(() => null);
    if (res && res.ok) {
      setMeldung(`✓ "${karte.cardName || "(ohne Namen)"}" gelöscht.`);
      laden();
    } else {
      const d = await res?.json().catch(() => null);
      alert(typeof d?.detail === "string" ? d.detail : "Löschen fehlgeschlagen.");
    }
    setZuordnetGerade(false);
  };

  // Backend liefert bei einer Karte optional "wahrscheinlichePerson" mit, wenn deren berechnete
  // Nummer exakt mit einer bereits gespeicherten (aber noch nicht am Schloss verknuepften)
  // rfid_kartennummer uebereinstimmt - erspart die Suche im "Zuordnen"-Dialog fuer den
  // Normalfall, dass eine frisch in TTLock registrierte Karte genau zu der Person gehoert, die
  // ohnehin schon im Profil steht.
  const handleSchnellBestaetigen = async (karte) => {
    const p = karte.wahrscheinlichePerson;
    setZuordnetGerade(true);
    setMeldung("");
    const erfolg = await zuordnungAuswerten(p.typ, p.id, karte, p.name,
      `✓ "${karte.cardName || "(ohne Namen)"}" jetzt bei ${p.name} (${RFID_SUCHE_TYP_LABEL[p.typ]}).`);
    if (erfolg) laden();
    setZuordnetGerade(false);
  };

  // Kartenname aus TTLock (falls vorhanden) als Vorname/Nachname-Vorschlag ins "Neue Person
  // anlegen"-Formular vorausfuellen - erstes Wort = Vorname, Rest = Nachname. Nutzer entscheidet im
  // Formular selbst den Typ (Mitglied/Wellpass/Hansefit/Sonstige), siehe PersonFormular's eigener
  // Typ-Umschalter.
  const kartennameAufteilen = (name) => {
    const teile = (name || "").trim().split(/\s+/).filter(Boolean);
    if (teile.length === 0) return { vorname: "", nachname: "" };
    if (teile.length === 1) return { vorname: teile[0], nachname: "" };
    return { vorname: teile[0], nachname: teile.slice(1).join(" ") };
  };

  const handleNeuAngelegt = async (typ, neuePerson) => {
    const karte = neuAnlegenKarte;
    setNeuAnlegenKarte(null);
    setMeldung("");
    const res = await uebernehmen(typ, neuePerson.id, karte);
    if (res && res.ok) {
      setMeldung(`✓ ${neuePerson.vorname} ${neuePerson.nachname} (${RFID_SUCHE_TYP_LABEL[typ]}) neu angelegt und Karte "${karte.cardName || "(ohne Namen)"}" zugeordnet.`);
    } else {
      alert(`${PERSON_TYP_CONFIG[typ]?.label || typ} wurde angelegt, aber die Karte konnte nicht automatisch zugeordnet werden - bitte über "Zuordnen" nachholen.`);
    }
    laden();
  };

  if (ladeFehler) return <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{ladeFehler}</p>;
  if (!karten) return <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Lädt…</p>;

  return (
    <div>
      {karten.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Keine unzugeordneten Karten.</p>}
      <div style={{ maxHeight: 280, overflowY: "auto" }}>
        {karten.map(k => (
          <div key={k.cardId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "8px 10px", borderRadius: 8, marginBottom: 6, background: THEME.colors.background }}>
            <div>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600, margin: 0 }}>{k.cardName || "(ohne Namen)"}</p>
              {k.wahrscheinlichePerson && (
                <p style={{ color: k.warnungBereitsAndereKarte ? THEME.colors.warning : THEME.colors.primary, fontSize: THEME.fontSize.xs, margin: "2px 0 0" }}>
                  Vermutlich: {k.wahrscheinlichePerson.name} ({RFID_SUCHE_TYP_LABEL[k.wahrscheinlichePerson.typ]})
                  {k.warnungBereitsAndereKarte && " – hat bereits eine andere Karte registriert!"}
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              {k.wahrscheinlichePerson && (
                <button onClick={() => handleSchnellBestaetigen(k)} disabled={zuordnetGerade} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", background: k.warnungBereitsAndereKarte ? THEME.colors.warning : THEME.colors.success }}>
                  {k.warnungBereitsAndereKarte ? "⚠ Ersetzen: " : "✓ "}{k.wahrscheinlichePerson.name.split(" ")[0]} bestätigen
                </button>
              )}
              <button onClick={() => setZuordnenKarte(k)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>Zuordnen</button>
              <button onClick={() => { setNeuAnlegenKarte(k); setNeuAnlegenTyp("mitglied"); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>+ Neu anlegen</button>
              <button onClick={() => handleKarteLoeschen(k)} disabled={zuordnetGerade} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", color: THEME.colors.danger }}>🗑</button>
            </div>
          </div>
        ))}
      </div>
      {meldung && <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, marginTop: 8 }}>{meldung}</p>}

      {zuordnenKarte && createPortal(
        <RfidZielAuswahlDialog
          onSchliessen={() => !zuordnetGerade && setZuordnenKarte(null)}
          onAusgewaehlt={handleAusgewaehlt}
          typLabel={RFID_SUCHE_TYP_LABEL}
          benutzerId={benutzerId}
        />,
        document.body
      )}

      {neuAnlegenKarte && createPortal(
        <PersonFormular
          typ={neuAnlegenTyp}
          onTypWechsel={setNeuAnlegenTyp}
          initial={kartennameAufteilen(neuAnlegenKarte.cardName)}
          onSpeichern={handleNeuAngelegt}
          onAbbrechen={() => setNeuAnlegenKarte(null)}
          benutzer={benutzer}
        />,
        document.body
      )}

      {ersetzenAnfrage && createPortal(
        <div style={s.modalBg} onClick={() => !zuordnetGerade && setErsetzenAnfrage(null)}>
          <div style={{ ...s.modal, width: "min(480px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitel}>Karte bereits vergeben</h3>
            <p style={{ color: THEME.colors.text }}>{ersetzenAnfrage.message}</p>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
              Trotzdem "{ersetzenAnfrage.karte.cardName || "(ohne Namen)"}" jetzt {ersetzenAnfrage.personName} zuweisen und {ersetzenAnfrage.inhaberName} entziehen?
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setErsetzenAnfrage(null)} style={s.btnGray} disabled={zuordnetGerade}>Abbrechen</button>
              <button onClick={handleErsetzenBestaetigt} style={{ ...s.btn, width: "auto", background: THEME.colors.danger }} disabled={zuordnetGerade}>
                {zuordnetGerade ? "Übernimmt…" : "Trotzdem zuweisen"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {loeschenAnfrage && createPortal(
        <div style={s.modalBg} onClick={() => !zuordnetGerade && setLoeschenAnfrage(null)}>
          <div style={{ ...s.modal, width: "min(480px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitel}>Karte löschen?</h3>
            <p style={{ color: THEME.colors.text }}>"{loeschenAnfrage.cardName || "(ohne Namen)"}" endgültig aus TTLock löschen?</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setLoeschenAnfrage(null)} style={s.btnGray} disabled={zuordnetGerade}>Abbrechen</button>
              <button onClick={handleKarteLoeschenBestaetigt} style={{ ...s.btn, width: "auto", background: THEME.colors.danger }} disabled={zuordnetGerade}>
                {zuordnetGerade ? "Löscht…" : "Löschen"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function RfidSucheWidget({ benutzerId, onPersonOeffnen, benutzer }) {
  const [rfidSucheWert, setRfidSucheWert] = useState("");
  const [rfidSucheErgebnis, setRfidSucheErgebnis] = useState(null);
  const [rfidSucheLaedt, setRfidSucheLaedt] = useState(false);
  const [rfidZielDialogOffen, setRfidZielDialogOffen] = useState(false);
  const [rfidZuordnenMeldung, setRfidZuordnenMeldung] = useState(null);
  const [rfidZuordnenLaedt, setRfidZuordnenLaedt] = useState(false);
  // Ersetzt das fruehere window.confirm(...) fuer die "Zielperson hat schon eine andere Karte -
  // trotzdem ersetzen?"-Rueckfrage (Bug 2026-08-31: native confirm()/alert()-Dialoge werden in
  // manchen Android-Homescreen-/PWA-Kontexten stillschweigend unterdrueckt - der Dialog erscheint
  // nie, confirm() liefert einfach false zurueck, die Aktion bricht wortlos ab). Eigenes,
  // In-App-Bestaetigungsfenster funktioniert ueberall zuverlaessig gleich.
  const [rfidErsetzenAnfrage, setRfidErsetzenAnfrage] = useState(null);
  const [rfidEntfernenAnfrage, setRfidEntfernenAnfrage] = useState(false);
  // "+ Neu anlegen" (Nutzer-Wunsch 2026-08-06): Person hatte schon vorher ein Band/eine Karte
  // (z.B. Probetraining), Vertrag wurde inzwischen auf Papier ausgefuellt - komplettes Formular auf
  // einmal ausfuellen und die vorhin gesuchte Kartennummer wird direkt beim Speichern zugeordnet,
  // damit die Person mit bereits funktionierendem Zutritt gehen kann. Gleiches Muster wie das
  // bestehende "+ Neu anlegen" in TTLockUnzugeordneteKartenWidget.
  const [neuAnlegenOffen, setNeuAnlegenOffen] = useState(false);
  const [neuAnlegenTyp, setNeuAnlegenTyp] = useState("mitglied");
  // Wie in RfidBereich: merkt sich, ob rfidSucheWert per Handy-NFC-Scan berechnet wurde - TTLock
  // braucht je nach Erfassungsmethode eine andere Rueckrechnungsformel (siehe _ttlock_zielnummer
  // in routers_ttlock.py).
  const [rfidSucheErfassungsart, setRfidSucheErfassungsart] = useState(null);
  const [nfcUnterstuetzt] = useState(typeof window !== "undefined" && "NDEFReader" in window);
  const [nfcScanLaeuft, setNfcScanLaeuft] = useState(false);
  const [nfcErgebnis, setNfcErgebnis] = useState(null);
  const [nfcFehler, setNfcFehler] = useState("");
  const RFID_SUCHE_TYP_LABEL = { mitglied: "Mitglied", wellpass: "Wellpass-Nutzer", hansefit: "Hansefit-Nutzer", mitarbeiter: "Mitarbeiter", sonstige: "Sonstige", probetraining: "Probetraining" };

  const handleRfidSuche = async (wertOverride) => {
    let wert = (wertOverride !== undefined ? wertOverride : rfidSucheWert).trim();
    const umgerechnet = pcLeserZuKartennummer(wert);
    if (umgerechnet != null) { wert = String(umgerechnet); setRfidSucheWert(wert); }
    if (!wert) { setRfidSucheErgebnis(null); return; }
    setRfidSucheLaedt(true);
    try {
      const res = await fetch(`${API}/safesmart/rfid-suche?kartennummer=${encodeURIComponent(wert)}&benutzer_id=${benutzerId}`);
      setRfidSucheErgebnis(await res.json());
    } catch { setRfidSucheErgebnis({ kartennummer_gueltig: true, gefunden: false, inhaber: null }); }
    setRfidSucheLaedt(false);
  };

  const handleNfcScan = async () => {
    setNfcFehler("");
    setNfcErgebnis(null);
    setNfcScanLaeuft(true);
    try {
      const ndef = new window.NDEFReader();
      await ndef.scan();
      ndef.onreading = (event) => {
        const seriennummer = event.serialNumber || "";
        const bytes = seriennummer.split(":").filter(Boolean).map(h => parseInt(h, 16));
        const kartennummer = berechneSafesmartKartennummer(bytes);
        setNfcErgebnis({ seriennummer, kartennummer });
        setNfcScanLaeuft(false);
        if (kartennummer != null) {
          const wert = String(kartennummer);
          setRfidSucheWert(wert);
          setRfidSucheErfassungsart("handy_nfc");
          setRfidZuordnenMeldung(null);
          handleRfidSuche(wert);
        }
      };
      ndef.onreadingerror = () => {
        setNfcFehler("Chip konnte nicht gelesen werden – bitte Karte näher ans Handy halten und erneut versuchen.");
        setNfcScanLaeuft(false);
      };
    } catch (e) {
      setNfcFehler(e?.message || "NFC-Scan konnte nicht gestartet werden (Berechtigung erteilt?).");
      setNfcScanLaeuft(false);
    }
  };

  const handleRfidZuordnen = async (ziel, confirmReplace = false) => {
    setRfidZuordnenMeldung(null);
    setRfidZuordnenLaedt(true);
    try {
      const res = await fetch(`${API}/safesmart/rfid-neu-zuordnen?benutzer_id=${benutzerId}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kartennummer: rfidSucheWert.trim(), ziel_typ: ziel.typ, ziel_id: ziel.id, confirm_replace_target_rfid: confirmReplace, erfassungsart: rfidSucheErfassungsart }),
      });
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail || {};
        if (detail.bestehende_ziel_rfid) {
          // Eigenes Bestaetigungsfenster statt window.confirm(...) - siehe Kommentar bei
          // rfidErsetzenAnfrage weiter oben.
          setRfidErsetzenAnfrage({ ziel, message: detail.message, bestehendeKarte: detail.bestehende_ziel_rfid });
          return;
        }
        alert(`${detail.message || "Datenkonflikt."}\n\nBetroffene Datensätze: ${(detail.alle_inhaber || []).map(i => `${i.name} (${RFID_SUCHE_TYP_LABEL[i.typ]})`).join(", ")}`);
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(typeof data.detail === "string" ? data.detail : "Zuordnung fehlgeschlagen.");
        return;
      }
      const data = await res.json();
      setRfidZielDialogOffen(false);
      setRfidErsetzenAnfrage(null);
      setRfidZuordnenMeldung(
        `✓ Karte ${rfidSucheWert.trim()} jetzt bei ${data.neuer_inhaber.name} (${RFID_SUCHE_TYP_LABEL[data.neuer_inhaber.typ]})` +
        (data.alter_inhaber ? ` – vorher bei ${data.alter_inhaber.name} (${RFID_SUCHE_TYP_LABEL[data.alter_inhaber.typ]})` : "")
      );
      await handleRfidSuche();
    } catch (e) {
      alert(`Zuordnung fehlgeschlagen: ${e?.message || "unbekannter Fehler"}`);
    } finally {
      setRfidZuordnenLaedt(false);
    }
  };

  const handleRfidEntfernen = async () => {
    const wert = rfidSucheWert.trim();
    setRfidEntfernenAnfrage(false);
    setRfidZuordnenMeldung(null);
    try {
      const res = await fetch(`${API}/safesmart/rfid-entfernen?benutzer_id=${benutzerId}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kartennummer: wert }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(typeof data.detail === "string" ? data.detail : (data.detail?.message || "Entfernen fehlgeschlagen."));
        return;
      }
      const data = await res.json();
      setRfidZuordnenMeldung(`✓ Karte ${wert} wurde von ${data.alter_inhaber.name} (${RFID_SUCHE_TYP_LABEL[data.alter_inhaber.typ]}) entfernt.`);
      await handleRfidSuche();
    } catch (e) {
      alert(`Entfernen fehlgeschlagen: ${e?.message || "unbekannter Fehler"}`);
    }
  };

  const handleNeuAngelegt = async (typ, neuePerson) => {
    setNeuAnlegenOffen(false);
    await handleRfidZuordnen({ typ, id: neuePerson.id });
  };

  // Ein Feld statt zwei getrennter Suchboxen (Nutzer-Feedback 2026-07-20): enthaelt der
  // eingegebene Wert einen Buchstaben, wird er als Namenssuche behandelt, sonst als
  // Kartennummer - eine Kartennummer ist immer rein numerisch (auch nach PC-Leser-/Handy-NFC-
  // Umrechnung), waehrend ein Name mindestens einen Buchstaben enthaelt.
  const nameModus = /[a-zA-ZÀ-ÿ]/.test(rfidSucheWert.trim());

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input style={{ ...s.input, flex: 1, maxWidth: 320 }} value={rfidSucheWert}
          onChange={e => { setRfidSucheWert(e.target.value); setRfidSucheErfassungsart(null); setRfidZuordnenMeldung(null); setRfidSucheErgebnis(null); }}
          onKeyDown={e => { if (e.key === "Enter" && !nameModus) handleRfidSuche(); }}
          placeholder="Kartennummer oder Name eingeben…" />
        {!nameModus && (
          <button onClick={() => handleRfidSuche()} style={{ ...s.btn, width: "auto" }}>{rfidSucheLaedt ? "Sucht…" : "Suchen"}</button>
        )}
        {!nameModus && rfidSucheErgebnis && rfidSucheErgebnis.kartennummer_gueltig && !rfidSucheErgebnis.konflikt && (
          <button onClick={() => setRfidZielDialogOffen(true)} style={{ ...s.btnGray, width: "auto" }}>{rfidSucheErgebnis.gefunden ? "Anderer Person zuordnen" : "Person zuweisen"}</button>
        )}
        {!nameModus && rfidSucheErgebnis && rfidSucheErgebnis.kartennummer_gueltig && !rfidSucheErgebnis.konflikt && (
          <button onClick={() => { setNeuAnlegenTyp("mitglied"); setNeuAnlegenOffen(true); }} style={{ ...s.btnGray, width: "auto" }}>+ Neu anlegen</button>
        )}
        {!nameModus && rfidSucheErgebnis && rfidSucheErgebnis.gefunden && !rfidSucheErgebnis.konflikt && (
          <button onClick={() => setRfidEntfernenAnfrage(true)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Entfernen</button>
        )}
      </div>
      {!nameModus && rfidZuordnenMeldung && <p style={{ color: THEME.colors.success, marginTop: 12, marginBottom: 0 }}>{rfidZuordnenMeldung}</p>}
      {!nameModus && !rfidZuordnenMeldung && rfidSucheErgebnis && (
        rfidSucheErgebnis.konflikt ? (
          <div style={{ marginTop: 12 }}>
            <p style={{ color: THEME.colors.danger, marginBottom: 4 }}>⚠ Datenkonflikt: diese Kartennummer ist mehreren Datensätzen zugeordnet:</p>
            {(rfidSucheErgebnis.alle_inhaber || []).map((i, idx) => (
              <p key={idx} style={{ color: THEME.colors.textMuted, margin: "2px 0" }}>{i.name} ({RFID_SUCHE_TYP_LABEL[i.typ]} #{i.id}, {i.status})</p>
            ))}
          </div>
        ) : rfidSucheErgebnis.gefunden ? (
          <p style={{ color: THEME.colors.success, marginTop: 12, marginBottom: 0 }}>
            ✓ Diese Karte gehört zu{" "}
            {onPersonOeffnen && ["mitglied", "wellpass", "hansefit", "sonstige"].includes(rfidSucheErgebnis.inhaber.typ) ? (
              <strong onClick={() => onPersonOeffnen(rfidSucheErgebnis.inhaber.typ, rfidSucheErgebnis.inhaber.id)} style={{ color: THEME.colors.primary, cursor: "pointer", textDecoration: "underline" }}>{rfidSucheErgebnis.inhaber.name}</strong>
            ) : (
              <strong>{rfidSucheErgebnis.inhaber.name}</strong>
            )}{" "}
            ({RFID_SUCHE_TYP_LABEL[rfidSucheErgebnis.inhaber.typ]} #{rfidSucheErgebnis.inhaber.id}, {rfidSucheErgebnis.inhaber.status})
          </p>
        ) : (
          <p style={{ color: THEME.colors.textMuted, marginTop: 12, marginBottom: 0 }}>Keine Zuordnung gefunden – kann direkt einer Person zugewiesen werden.</p>
        )
      )}
      {!nameModus && rfidZielDialogOffen && (
        <RfidZielAuswahlDialog onSchliessen={() => setRfidZielDialogOffen(false)} onAusgewaehlt={(ziel) => handleRfidZuordnen(ziel, false)} typLabel={RFID_SUCHE_TYP_LABEL} benutzerId={benutzerId} />
      )}
      {!nameModus && rfidErsetzenAnfrage && (
        <div style={s.modalBg} onClick={() => setRfidErsetzenAnfrage(null)}>
          <div style={{ ...s.modal, width: "min(480px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitel}>Bestehende Karte ersetzen?</h3>
            <p style={{ color: THEME.colors.text }}>{rfidErsetzenAnfrage.message}</p>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Bestehende RFID der Zielperson: {rfidErsetzenAnfrage.bestehendeKarte}</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setRfidErsetzenAnfrage(null)} style={s.btnGray}>Abbrechen</button>
              <button disabled={rfidZuordnenLaedt} onClick={() => handleRfidZuordnen(rfidErsetzenAnfrage.ziel, true)} style={{ ...s.btn, width: "auto", background: THEME.colors.danger }}>
                {rfidZuordnenLaedt ? "Ersetzt…" : "Trotzdem ersetzen"}
              </button>
            </div>
          </div>
        </div>
      )}
      {!nameModus && rfidEntfernenAnfrage && rfidSucheErgebnis?.inhaber && (
        <div style={s.modalBg} onClick={() => setRfidEntfernenAnfrage(false)}>
          <div style={{ ...s.modal, width: "min(480px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitel}>Karte entfernen?</h3>
            <p style={{ color: THEME.colors.text }}>Karte {rfidSucheWert.trim()} wirklich von {rfidSucheErgebnis.inhaber.name} entfernen, ohne sie neu zuzuweisen?</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setRfidEntfernenAnfrage(false)} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleRfidEntfernen} style={{ ...s.btn, width: "auto", background: THEME.colors.danger }}>Entfernen</button>
            </div>
          </div>
        </div>
      )}
      {!nameModus && neuAnlegenOffen && createPortal(
        <PersonFormular
          typ={neuAnlegenTyp}
          onTypWechsel={setNeuAnlegenTyp}
          onSpeichern={handleNeuAngelegt}
          onAbbrechen={() => setNeuAnlegenOffen(false)}
          benutzer={benutzer}
        />,
        document.body
      )}
      {nameModus && <RfidNamensSucheBox benutzerId={benutzerId} suche={rfidSucheWert.trim()} onSucheChange={setRfidSucheWert} />}
      {nfcUnterstuetzt && (
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px dashed ${THEME.colors.border}` }}>
        {(
          <>
            <button onClick={handleNfcScan} disabled={nfcScanLaeuft} style={{ ...s.btnGray, width: "auto" }}>{nfcScanLaeuft ? "Warte auf Karte…" : "Scan starten"}</button>
            {nfcFehler && <p style={{ color: THEME.colors.danger, marginTop: 8, marginBottom: 0, fontSize: THEME.fontSize.sm }}>{nfcFehler}</p>}
            {nfcErgebnis && (
              <div style={{ marginTop: 8 }}>
                <p style={{ margin: "2px 0", fontSize: THEME.fontSize.sm }}>Rohe Chip-Kennung (Hex): <strong>{nfcErgebnis.seriennummer}</strong></p>
                {nfcErgebnis.kartennummer != null ? (
                  <p style={{ margin: "8px 0 6px", fontSize: THEME.fontSize.sm }}>Berechnete SafeSmart-Kartennummer: <strong style={{ color: THEME.colors.success }}>{nfcErgebnis.kartennummer}</strong> – wird oben automatisch gesucht.</p>
                ) : (
                  <p style={{ margin: "8px 0 6px", fontSize: THEME.fontSize.sm, color: THEME.colors.danger }}>Umrechnung nur bei 4-Byte-Chip-Kennungen bestätigt.</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
      )}
    </div>
  );
}

function SafeSmartImportSeite({ benutzerId, onPersonOeffnen }) {
  const [datei, setDatei] = useState(null);
  const [uploadLaedt, setUploadLaedt] = useState(false);
  const [uploadFehler, setUploadFehler] = useState("");

  // TTLock-Fortschritt: solange TTLock noch nicht live an der Tuer haengt (Nutzer testet aktuell
  // an der Theke), zeigt das dem Nutzer, wer noch eine manuelle Ground-Truth-Registrierung braucht
  // (Nutzer-Wunsch 2026-07-23).
  const [ttlockOffen, setTtlockOffen] = useState(null);
  const [ttlockAufgeklappt, setTtlockAufgeklappt] = useState(false);
  const [ttlockSuche, setTtlockSuche] = useState("");
  const ladeTtlockFortschritt = () => {
    fetch(`${API}/ttlock/noch-nicht-manuell-bestaetigt`).then(r => r.ok ? r.json() : Promise.reject())
      .then(setTtlockOffen).catch(() => setTtlockOffen(null));
  };
  useEffect(ladeTtlockFortschritt, []);
  const TTLOCK_TYP_LABEL = { mitglied: "Mitglied", wellpass: "Wellpass", hansefit: "Hansefit", sonstige: "Sonstige", probetraining: "Probetraining" };
  // Entfernen aus der Fortschritts-Liste (Nutzer-Wunsch 2026-07-23: z.B. Sonstige-Nutzer, die
  // nicht mehr kommen) - aendert bewusst NICHT den echten Status der Person, blendet sie nur hier aus.
  const handleTtlockEntfernen = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`"${p.name}" aus dieser Liste entfernen? Der Status der Person selbst wird dadurch nicht geändert.`)) return;
    const res = await fetch(`${API}/ttlock/personen/${p.typ}/${p.id}/aus-fortschrittsliste-entfernen`, { method: "POST" }).catch(() => null);
    if (res && res.ok) {
      setTtlockOffen(o => ({ ...o, personen: o.personen.filter(x => !(x.typ === p.typ && x.id === p.id)), anzahl_offen: o.anzahl_offen - 1 }));
    } else {
      alert("Konnte nicht entfernt werden.");
    }
  };

  const [batches, setBatches] = useState([]);
  const [batchesLaedt, setBatchesLaedt] = useState(false);
  const [zusammenfassungModal, setZusammenfassungModal] = useState(null);

  const [aktiverBatchId, setAktiverBatchId] = useState(null);
  const [batchInfo, setBatchInfo] = useState(null);
  const [zeilen, setZeilen] = useState([]);
  const [zeilenLaedt, setZeilenLaedt] = useState(false);
  const [aktionFehler, setAktionFehler] = useState("");

  const [kategorieFilter, setKategorieFilter] = useState("");
  const [entscheidungFilter, setEntscheidungFilter] = useState("");
  const [konfliktFilter, setKonfliktFilter] = useState("");
  const [suche, setSuche] = useState("");
  const [ausgewaehlt, setAusgewaehlt] = useState(new Set());
  const [sortSpalte, setSortSpalte] = useState(null);
  const [sortRichtung, setSortRichtung] = useState("asc");
  const [autoVormerkenLaedt, setAutoVormerkenLaedt] = useState(false);

  const [detailZeile, setDetailZeile] = useState(null);
  const [mitgliedDialogZeile, setMitgliedDialogZeile] = useState(null);
  const [ignorierenZeile, setIgnorierenZeile] = useState(null);

  // Mobile-Kartenansicht statt der breiten Tabellen (Konvention wie ueberall sonst in der App:
  // 1024px-Breakpoint, siehe App.css .sidebar/.mobileheader).
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Karte gefunden? -> Suche nach Inhaber ueber alle fuenf RFID-Tabellen hinweg
  const [rfidSucheWert, setRfidSucheWert] = useState("");
  const [rfidSucheErgebnis, setRfidSucheErgebnis] = useState(null);
  const [rfidSucheLaedt, setRfidSucheLaedt] = useState(false);
  const [rfidZielDialogOffen, setRfidZielDialogOffen] = useState(false);
  const [rfidZuordnenMeldung, setRfidZuordnenMeldung] = useState(null);
  // Wie in RfidBereich: merkt sich, ob rfidSucheWert per Handy-NFC-Scan berechnet wurde - TTLock
  // braucht je nach Erfassungsmethode eine andere Rueckrechnungsformel (siehe _ttlock_zielnummer
  // in routers_ttlock.py). Manuelle Eingabe/Aenderung verwirft diesen Status wieder.
  const [rfidSucheErfassungsart, setRfidSucheErfassungsart] = useState(null);

  // NFC-Scan-Werkzeug (Web NFC API, nur Chrome auf Android) - liest die rohe Chip-Seriennummer
  // (Hex) und rechnet sie per berechneSafesmartKartennummer() (siehe RfidBereich weiter oben)
  // in die SafeSmart-Kartennummer um. Formel an zwei echten Karten bestaetigt
  // (Marcel Schmidt-Roeh #2879: 5e:68:e8:ef -> 15231070; Cifci Nefise: ce:6e:0a:f0 -> 683726).
  const [nfcUnterstuetzt] = useState(typeof window !== "undefined" && "NDEFReader" in window);
  const [nfcScanLaeuft, setNfcScanLaeuft] = useState(false);
  const [nfcErgebnis, setNfcErgebnis] = useState(null);
  const [nfcFehler, setNfcFehler] = useState("");
  const handleNfcScan = async () => {
    setNfcFehler("");
    setNfcErgebnis(null);
    setNfcScanLaeuft(true);
    try {
      const ndef = new window.NDEFReader();
      await ndef.scan();
      ndef.onreading = (event) => {
        const seriennummer = event.serialNumber || "";
        const bytes = seriennummer.split(":").filter(Boolean).map(h => parseInt(h, 16));
        const zuDezimal = (arr) => arr.reduce((acc, b) => acc * 256 + b, 0);
        const varianten = [];
        if (bytes.length > 0) {
          varianten.push({ label: "Alle Bytes, Reihenfolge wie gelesen", wert: zuDezimal(bytes) });
          varianten.push({ label: "Alle Bytes, umgekehrte Reihenfolge", wert: zuDezimal([...bytes].reverse()) });
          if (bytes.length > 4) {
            const letzte4 = bytes.slice(-4);
            varianten.push({ label: "Letzte 4 Bytes, wie gelesen", wert: zuDezimal(letzte4) });
            varianten.push({ label: "Letzte 4 Bytes, umgekehrt", wert: zuDezimal([...letzte4].reverse()) });
            const erste4 = bytes.slice(0, 4);
            varianten.push({ label: "Erste 4 Bytes, wie gelesen", wert: zuDezimal(erste4) });
            varianten.push({ label: "Erste 4 Bytes, umgekehrt", wert: zuDezimal([...erste4].reverse()) });
          }
        }
        const kartennummer = berechneSafesmartKartennummer(bytes);
        setNfcErgebnis({ seriennummer, varianten, kartennummer });
        setNfcScanLaeuft(false);
        // Direkt uebernehmen & suchen, kein manueller Zwischenschritt mehr noetig.
        if (kartennummer != null) {
          const wert = String(kartennummer);
          setRfidSucheWert(wert);
          setRfidSucheErfassungsart("handy_nfc");
          setRfidZuordnenMeldung(null);
          handleRfidSuche(wert);
        }
      };
      ndef.onreadingerror = () => {
        setNfcFehler("Chip konnte nicht gelesen werden – bitte Karte näher ans Handy halten und erneut versuchen.");
        setNfcScanLaeuft(false);
      };
    } catch (e) {
      setNfcFehler(e?.message || "NFC-Scan konnte nicht gestartet werden (Berechtigung erteilt?).");
      setNfcScanLaeuft(false);
    }
  };

  const handleRfidSuche = async (wertOverride) => {
    let wert = (wertOverride !== undefined ? wertOverride : rfidSucheWert).trim();
    const umgerechnet = pcLeserZuKartennummer(wert);
    if (umgerechnet != null) { wert = String(umgerechnet); setRfidSucheWert(wert); }
    if (!wert) { setRfidSucheErgebnis(null); return; }
    setRfidSucheLaedt(true);
    try {
      const res = await fetch(`${API}/safesmart/rfid-suche?kartennummer=${encodeURIComponent(wert)}&benutzer_id=${benutzerId}`);
      setRfidSucheErgebnis(await res.json());
    } catch { setRfidSucheErgebnis({ kartennummer_gueltig: true, gefunden: false, inhaber: null }); }
    setRfidSucheLaedt(false);
  };
  const RFID_SUCHE_TYP_LABEL = { mitglied: "Mitglied", wellpass: "Wellpass-Nutzer", hansefit: "Hansefit-Nutzer", mitarbeiter: "Mitarbeiter", sonstige: "Sonstige", probetraining: "Probetraining" };

  const handleRfidZuordnen = async (ziel, confirmReplace = false) => {
    setRfidZuordnenMeldung(null);
    const res = await fetch(`${API}/safesmart/rfid-neu-zuordnen?benutzer_id=${benutzerId}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kartennummer: rfidSucheWert.trim(), ziel_typ: ziel.typ, ziel_id: ziel.id, confirm_replace_target_rfid: confirmReplace, erfassungsart: rfidSucheErfassungsart }),
    });
    if (res.status === 409) {
      const data = await res.json().catch(() => ({}));
      const detail = data.detail || {};
      if (detail.bestehende_ziel_rfid) {
        const weiter = window.confirm(
          `${detail.message}\n\nBestehende RFID der Zielperson: ${detail.bestehende_ziel_rfid}\n\nTrotzdem ersetzen?`
        );
        if (weiter) await handleRfidZuordnen(ziel, true);
        return;
      }
      alert(`${detail.message || "Datenkonflikt."}\n\nBetroffene Datensätze: ${(detail.alle_inhaber || []).map(i => `${i.name} (${RFID_SUCHE_TYP_LABEL[i.typ]})`).join(", ")}`);
      return;
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(typeof data.detail === "string" ? data.detail : "Zuordnung fehlgeschlagen.");
      return;
    }
    const data = await res.json();
    setRfidZielDialogOffen(false);
    setRfidZuordnenMeldung(
      `✓ Karte ${rfidSucheWert.trim()} jetzt bei ${data.neuer_inhaber.name} (${RFID_SUCHE_TYP_LABEL[data.neuer_inhaber.typ]})` +
      (data.alter_inhaber ? ` – vorher bei ${data.alter_inhaber.name} (${RFID_SUCHE_TYP_LABEL[data.alter_inhaber.typ]})` : "")
    );
    await handleRfidSuche();
  };

  const handleRfidEntfernen = async () => {
    const wert = rfidSucheWert.trim();
    if (!window.confirm(`Karte ${wert} wirklich von ${rfidSucheErgebnis.inhaber.name} entfernen, ohne sie neu zuzuweisen?`)) return;
    setRfidZuordnenMeldung(null);
    const res = await fetch(`${API}/safesmart/rfid-entfernen?benutzer_id=${benutzerId}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kartennummer: wert }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(typeof data.detail === "string" ? data.detail : (data.detail?.message || "Entfernen fehlgeschlagen."));
      return;
    }
    const data = await res.json();
    setRfidZuordnenMeldung(`✓ Karte ${wert} wurde von ${data.alter_inhaber.name} (${RFID_SUCHE_TYP_LABEL[data.alter_inhaber.typ]}) entfernt.`);
    await handleRfidSuche();
  };

  // Ein Feld statt zwei getrennter Suchboxen (Nutzer-Feedback 2026-07-20): enthaelt der
  // eingegebene Wert einen Buchstaben, wird er als Namenssuche behandelt, sonst als
  // Kartennummer - eine Kartennummer ist immer rein numerisch (auch nach PC-Leser-/Handy-NFC-
  // Umrechnung), waehrend ein Name mindestens einen Buchstaben enthaelt.
  const rfidNameModus = /[a-zA-ZÀ-ÿ]/.test(rfidSucheWert.trim());

  const karteSucheBox = (
    <div style={{ ...s.karte, marginBottom: 20 }}>
      <h3 style={{ color: THEME.colors.text, marginTop: 0, marginBottom: 12 }}>🔍 Karte/Band gefunden – wem gehört sie?</h3>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input style={{ ...s.input, flex: 1, maxWidth: 320 }} value={rfidSucheWert}
          onChange={e => { setRfidSucheWert(e.target.value); setRfidSucheErfassungsart(null); setRfidZuordnenMeldung(null); setRfidSucheErgebnis(null); }}
          onKeyDown={e => { if (e.key === "Enter" && !rfidNameModus) handleRfidSuche(); }}
          placeholder="Kartennummer oder Name eingeben…" />
        {!rfidNameModus && (
          <button onClick={() => handleRfidSuche()} style={{ ...s.btn, width: "auto" }}>{rfidSucheLaedt ? "Sucht…" : "Suchen"}</button>
        )}
        {!rfidNameModus && rfidSucheErgebnis && rfidSucheErgebnis.kartennummer_gueltig && !rfidSucheErgebnis.konflikt && (
          <button onClick={() => setRfidZielDialogOffen(true)} style={{ ...s.btnGray, width: "auto" }}>{rfidSucheErgebnis.gefunden ? "Anderer Person zuordnen" : "Person zuweisen"}</button>
        )}
        {!rfidNameModus && rfidSucheErgebnis && rfidSucheErgebnis.gefunden && !rfidSucheErgebnis.konflikt && (
          <button onClick={handleRfidEntfernen} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Entfernen</button>
        )}
      </div>
      {!rfidNameModus && rfidZuordnenMeldung && <p style={{ color: THEME.colors.success, marginTop: 12, marginBottom: 0 }}>{rfidZuordnenMeldung}</p>}
      {!rfidNameModus && !rfidZuordnenMeldung && rfidSucheErgebnis && (
        rfidSucheErgebnis.konflikt ? (
          <div style={{ marginTop: 12 }}>
            <p style={{ color: THEME.colors.danger, marginBottom: 4 }}>⚠ Datenkonflikt: diese Kartennummer ist mehreren Datensätzen zugeordnet:</p>
            {(rfidSucheErgebnis.alle_inhaber || []).map((i, idx) => (
              <p key={idx} style={{ color: THEME.colors.textMuted, margin: "2px 0" }}>{i.name} ({RFID_SUCHE_TYP_LABEL[i.typ]} #{i.id}, {i.status})</p>
            ))}
          </div>
        ) : rfidSucheErgebnis.gefunden ? (
          <p style={{ color: THEME.colors.success, marginTop: 12, marginBottom: 0 }}>
            ✓ Diese Karte gehört zu{" "}
            {onPersonOeffnen && ["mitglied", "wellpass", "hansefit", "sonstige"].includes(rfidSucheErgebnis.inhaber.typ) ? (
              <strong onClick={() => onPersonOeffnen(rfidSucheErgebnis.inhaber.typ, rfidSucheErgebnis.inhaber.id)} style={{ color: THEME.colors.primary, cursor: "pointer", textDecoration: "underline" }}>{rfidSucheErgebnis.inhaber.name}</strong>
            ) : (
              <strong>{rfidSucheErgebnis.inhaber.name}</strong>
            )}{" "}
            ({RFID_SUCHE_TYP_LABEL[rfidSucheErgebnis.inhaber.typ]} #{rfidSucheErgebnis.inhaber.id}, {rfidSucheErgebnis.inhaber.status})
          </p>
        ) : (
          <p style={{ color: THEME.colors.textMuted, marginTop: 12, marginBottom: 0 }}>Keine Zuordnung gefunden – kann direkt einer Person zugewiesen werden.</p>
        )
      )}
      {!rfidNameModus && rfidZielDialogOffen && (
        <RfidZielAuswahlDialog onSchliessen={() => setRfidZielDialogOffen(false)} onAusgewaehlt={(ziel) => handleRfidZuordnen(ziel, false)} typLabel={RFID_SUCHE_TYP_LABEL} benutzerId={benutzerId} />
      )}
      {rfidNameModus && <RfidNamensSucheBox benutzerId={benutzerId} suche={rfidSucheWert.trim()} onSucheChange={setRfidSucheWert} />}
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px dashed ${THEME.colors.border}` }}>
        {!nfcUnterstuetzt ? (
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>Dieses Gerät/dieser Browser unterstützt Web NFC nicht (nur Chrome auf Android).</p>
        ) : (
          <>
            <button onClick={handleNfcScan} disabled={nfcScanLaeuft} style={{ ...s.btnGray, width: "auto" }}>{nfcScanLaeuft ? "Warte auf Karte…" : "Scan starten"}</button>
            {nfcFehler && <p style={{ color: THEME.colors.danger, marginTop: 8, marginBottom: 0, fontSize: THEME.fontSize.sm }}>{nfcFehler}</p>}
            {nfcErgebnis && (
              <div style={{ marginTop: 8 }}>
                <p style={{ margin: "2px 0", fontSize: THEME.fontSize.sm }}>Rohe Chip-Kennung (Hex): <strong>{nfcErgebnis.seriennummer}</strong></p>
                {nfcErgebnis.kartennummer != null ? (
                  <p style={{ margin: "8px 0 6px", fontSize: THEME.fontSize.sm }}>Berechnete SafeSmart-Kartennummer: <strong style={{ color: THEME.colors.success }}>{nfcErgebnis.kartennummer}</strong> – wird oben automatisch gesucht.</p>
                ) : (
                  <p style={{ margin: "8px 0 6px", fontSize: THEME.fontSize.sm, color: THEME.colors.danger }}>Umrechnung nur bei 4-Byte-Chip-Kennungen bestätigt (hier {nfcErgebnis.seriennummer.split(":").filter(Boolean).length} Bytes gelesen).</p>
                )}
                <details style={{ marginTop: 10 }}>
                  <summary style={{ cursor: "pointer", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Alle Rohvarianten (Diagnose)</summary>
                  {nfcErgebnis.varianten.map((v, idx) => (
                    <p key={idx} style={{ margin: "2px 0", fontSize: THEME.fontSize.sm, color: THEME.colors.textMuted }}>{v.label}: <strong style={{ color: THEME.colors.text }}>{v.wert}</strong></p>
                  ))}
                </details>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Phase 4: Wellpass-Abgleich
  const [subTab, setSubTab] = useState("uebersicht");
  const [wellpassZusammenfassung, setWellpassZusammenfassung] = useState(null);
  const [wellpassAbgleichLaedt, setWellpassAbgleichLaedt] = useState(false);
  const [wellpassKategorieFilter, setWellpassKategorieFilter] = useState("");
  const [wellpassAuswahlZeile, setWellpassAuswahlZeile] = useState(null);
  const [wellpassAnlegenZeile, setWellpassAnlegenZeile] = useState(null);

  // "Sonstige"-Abgleich (mirrort Wellpass-Abgleich, ohne Check-in-Historie/Aggregation)
  const [sonstigeZusammenfassung, setSonstigeZusammenfassung] = useState(null);
  const [sonstigeAbgleichLaedt, setSonstigeAbgleichLaedt] = useState(false);
  const [sonstigeKategorieFilter, setSonstigeKategorieFilter] = useState("");
  const [sonstigeAuswahlZeile, setSonstigeAuswahlZeile] = useState(null);
  const [sonstigeAnlegenZeile, setSonstigeAnlegenZeile] = useState(null);

  const ladeBatches = async () => {
    setBatchesLaedt(true);
    try {
      const res = await fetch(`${API}/safesmart/batches?benutzer_id=${benutzerId}`);
      setBatches(await res.json());
    } catch (e) { /* still shows last known list */ }
    setBatchesLaedt(false);
  };
  useEffect(() => { ladeBatches(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ladeZeilen = async (batchId) => {
    setZeilenLaedt(true);
    setAktionFehler("");
    try {
      const [batchRes, zeilenRes] = await Promise.all([
        fetch(`${API}/safesmart/batches/${batchId}?benutzer_id=${benutzerId}`),
        fetch(`${API}/safesmart/batches/${batchId}/rows?benutzer_id=${benutzerId}`),
      ]);
      setBatchInfo(await batchRes.json());
      setZeilen(await zeilenRes.json());
    } catch (e) {
      setAktionFehler("Importlauf konnte nicht geladen werden.");
    }
    setZeilenLaedt(false);
  };

  const ladeWellpassZusammenfassung = async (batchId) => {
    try {
      const res = await fetch(`${API}/safesmart/batches/${batchId}/wellpass-summary?benutzer_id=${benutzerId}`);
      setWellpassZusammenfassung(await res.json());
    } catch (e) { /* still shows last known summary */ }
  };

  const ladeSonstigeZusammenfassung = async (batchId) => {
    try {
      const res = await fetch(`${API}/safesmart/batches/${batchId}/sonstige-summary?benutzer_id=${benutzerId}`);
      setSonstigeZusammenfassung(await res.json());
    } catch (e) { /* still shows last known summary */ }
  };

  const oeffneBatch = async (batchId) => {
    setAktiverBatchId(batchId);
    setAusgewaehlt(new Set());
    setKategorieFilter(""); setEntscheidungFilter(""); setKonfliktFilter(""); setSuche("");
    setSubTab("uebersicht"); setWellpassKategorieFilter(""); setSonstigeKategorieFilter("");
    await ladeZeilen(batchId);
    await ladeWellpassZusammenfassung(batchId);
    await ladeSonstigeZusammenfassung(batchId);
  };

  const zurueckZurListe = () => {
    setAktiverBatchId(null);
    setBatchInfo(null);
    setZeilen([]);
    setWellpassZusammenfassung(null);
    setSonstigeZusammenfassung(null);
    ladeBatches();
  };

  const handleWellpassAbgleich = async () => {
    setWellpassAbgleichLaedt(true);
    setAktionFehler("");
    try {
      const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/match-wellpass?benutzer_id=${benutzerId}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setAktionFehler(typeof data.detail === "string" ? data.detail : "Wellpass-Abgleich fehlgeschlagen.");
      } else {
        await ladeZeilen(aktiverBatchId);
        await ladeWellpassZusammenfassung(aktiverBatchId);
      }
    } catch (e) {
      setAktionFehler("Wellpass-Abgleich fehlgeschlagen.");
    }
    setWellpassAbgleichLaedt(false);
  };

  const wellpassEntscheiden = async (zeile, payload) => {
    setAktionFehler("");
    const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/rows/${zeile.row_id}/wellpass-decision?benutzer_id=${benutzerId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setAktionFehler(typeof data.detail === "string" ? data.detail : "Aktion fehlgeschlagen.");
      return;
    }
    await ladeZeilen(aktiverBatchId);
    await ladeWellpassZusammenfassung(aktiverBatchId);
  };

  const handleWellpassVorschlagBestaetigen = async (zeile) => {
    await wellpassEntscheiden(zeile, { aktion: "confirm_existing" });
  };

  const handleWellpassAnderenNutzer = async (nutzer) => {
    const zeile = wellpassAuswahlZeile;
    setWellpassAuswahlZeile(null);
    await wellpassEntscheiden(zeile, { aktion: "confirm_existing", selected_wellpass_user_id: nutzer.id });
  };

  const handleWellpassAblehnen = async (zeile) => {
    await wellpassEntscheiden(zeile, { aktion: "reject" });
  };

  const handleWellpassNeuAnlegen = async (formData, ignoriereWarnung = false) => {
    const zeile = wellpassAnlegenZeile;
    setAktionFehler("");
    const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/rows/${zeile.row_id}/create-wellpass-user?benutzer_id=${benutzerId}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...formData, ignoriere_mitglied_warnung: ignoriereWarnung }),
    });
    if (res.status === 409) {
      const data = await res.json().catch(() => ({}));
      const detail = data.detail || {};
      const kandidatenText = (detail.kandidaten || []).map(k => `${k.matched_member_name} (${k.confidence_score}%)`).join(", ");
      const weiter = window.confirm(
        `${detail.message || "Möglicherweise existiert bereits ein reguläres Mitglied mit diesem Namen."}\n\nKategorie: ${detail.kategorie}\nKandidaten: ${kandidatenText || "keine"}\n\nTrotzdem als neuen Wellpass-Nutzer anlegen?`
      );
      if (!weiter) return;
      await handleWellpassNeuAnlegen(formData, true);
      return;
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setAktionFehler(typeof data.detail === "string" ? data.detail : "Anlegen fehlgeschlagen.");
      return;
    }
    setWellpassAnlegenZeile(null);
    await ladeZeilen(aktiverBatchId);
    await ladeWellpassZusammenfassung(aktiverBatchId);
  };

  const handleSonstigeAbgleich = async () => {
    setSonstigeAbgleichLaedt(true);
    setAktionFehler("");
    try {
      const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/match-sonstige?benutzer_id=${benutzerId}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setAktionFehler(typeof data.detail === "string" ? data.detail : "Sonstige-Abgleich fehlgeschlagen.");
      } else {
        await ladeZeilen(aktiverBatchId);
        await ladeSonstigeZusammenfassung(aktiverBatchId);
      }
    } catch (e) {
      setAktionFehler("Sonstige-Abgleich fehlgeschlagen.");
    }
    setSonstigeAbgleichLaedt(false);
  };

  const sonstigeEntscheiden = async (zeile, payload) => {
    setAktionFehler("");
    const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/rows/${zeile.row_id}/sonstige-decision?benutzer_id=${benutzerId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setAktionFehler(typeof data.detail === "string" ? data.detail : "Aktion fehlgeschlagen.");
      return;
    }
    await ladeZeilen(aktiverBatchId);
    await ladeSonstigeZusammenfassung(aktiverBatchId);
  };

  const handleSonstigeVorschlagBestaetigen = async (zeile) => {
    await sonstigeEntscheiden(zeile, { aktion: "confirm_existing" });
  };

  const handleSonstigeAnderenNutzer = async (nutzer) => {
    const zeile = sonstigeAuswahlZeile;
    setSonstigeAuswahlZeile(null);
    await sonstigeEntscheiden(zeile, { aktion: "confirm_existing", selected_sonstige_user_id: nutzer.id });
  };

  const handleSonstigeAblehnen = async (zeile) => {
    await sonstigeEntscheiden(zeile, { aktion: "reject" });
  };

  const handleSonstigeNeuAnlegen = async (formData, ignoriereWarnung = false) => {
    const zeile = sonstigeAnlegenZeile;
    setAktionFehler("");
    const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/rows/${zeile.row_id}/create-sonstige-user?benutzer_id=${benutzerId}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...formData, ignoriere_mitglied_warnung: ignoriereWarnung }),
    });
    if (res.status === 409) {
      const data = await res.json().catch(() => ({}));
      const detail = data.detail || {};
      const kandidatenText = (detail.kandidaten || []).map(k => `${k.matched_member_name} (${k.confidence_score}%)`).join(", ");
      const weiter = window.confirm(
        `${detail.message || "Möglicherweise existiert bereits ein reguläres Mitglied mit diesem Namen."}\n\nKategorie: ${detail.kategorie}\nKandidaten: ${kandidatenText || "keine"}\n\nTrotzdem als neuen Sonstige-Nutzer anlegen?`
      );
      if (!weiter) return;
      await handleSonstigeNeuAnlegen(formData, true);
      return;
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setAktionFehler(typeof data.detail === "string" ? data.detail : "Anlegen fehlgeschlagen.");
      return;
    }
    setSonstigeAnlegenZeile(null);
    await ladeZeilen(aktiverBatchId);
    await ladeSonstigeZusammenfassung(aktiverBatchId);
  };

  const handleAnalysieren = async () => {
    if (!datei) return;
    setUploadLaedt(true);
    setUploadFehler("");
    try {
      const formData = new FormData();
      formData.append("file", datei);
      const res = await fetch(`${API}/safesmart/analyze?benutzer_id=${benutzerId}`, { method: "POST", body: formData });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Analyse fehlgeschlagen.");
      }
      const data = await res.json();
      setDatei(null);
      await oeffneBatch(data.batch_id);
    } catch (e) {
      setUploadFehler(e.message || "Analyse fehlgeschlagen.");
    }
    setUploadLaedt(false);
  };

  const entscheiden = async (zeile, payload) => {
    setAktionFehler("");
    const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/rows/${zeile.row_id}/decision?benutzer_id=${benutzerId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
    });
    if (res.status === 409) {
      const data = await res.json().catch(() => ({}));
      const detail = data.detail || {};
      const weiter = window.confirm(
        `${detail.message || "Dieses Mitglied ist bereits einer anderen Zeile zugeordnet."}\n\nBetroffene Zeilen-IDs: ${(detail.betroffene_zeilen || []).join(", ")}\n\nTrotzdem zuordnen?`
      );
      if (!weiter) return;
      await entscheiden(zeile, { ...payload, confirm_duplicate_assignment: true });
      return;
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setAktionFehler(typeof data.detail === "string" ? data.detail : "Entscheidung konnte nicht gespeichert werden.");
      return;
    }
    await ladeZeilen(aktiverBatchId);
  };

  const handleAndersesMitglied = async (mitglied) => {
    const zeile = mitgliedDialogZeile;
    setMitgliedDialogZeile(null);
    await entscheiden(zeile, { decision: "manually_assigned", selected_member_id: mitglied.id });
  };

  const handleIgnorieren = async (grund) => {
    const zeile = ignorierenZeile;
    setIgnorierenZeile(null);
    await entscheiden(zeile, { decision: "ignored", note: grund });
  };

  const bulkEntscheiden = async (decision, note) => {
    if (ausgewaehlt.size === 0) return;
    setAktionFehler("");
    const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/rows/bulk-decision?benutzer_id=${benutzerId}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ row_ids: Array.from(ausgewaehlt), decision, note }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setAktionFehler(typeof data.detail === "string" ? data.detail : "Sammelaktion fehlgeschlagen.");
      return;
    }
    const uebersprungen = (data.ergebnisse || []).filter(r => !r.applied);
    setAusgewaehlt(new Set());
    await ladeZeilen(aktiverBatchId);
    if (uebersprungen.length > 0) {
      alert(`${data.angewendet} von ${data.gesamt} Zeilen aktualisiert. Übersprungen:\n` + uebersprungen.map(r => `Zeile-ID ${r.row_id}: ${r.grund}`).join("\n"));
    }
  };

  const handleWellpassAutoVormerken = async () => {
    setAutoVormerkenLaedt(true);
    setAktionFehler("");
    try {
      const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/auto-vormerken-wellpass?benutzer_id=${benutzerId}`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAktionFehler(typeof data.detail === "string" ? data.detail : "Automatische Vormerkung fehlgeschlagen.");
      } else {
        await ladeZeilen(aktiverBatchId);
        alert(`${data.geprueft} Zeilen geprüft: ${data.vorgemerkt} automatisch für Wellpass vorgemerkt, ${data.unklar} weiterhin unklar (unverändert).`);
      }
    } catch (e) {
      setAktionFehler("Automatische Vormerkung fehlgeschlagen.");
    }
    setAutoVormerkenLaedt(false);
  };

  const pruefungAbschliessen = async () => {
    if (!window.confirm("Prüfung abschließen? Dies importiert noch nichts, markiert den Lauf nur als bereit für den nächsten Schritt.")) return;
    setAktionFehler("");
    const res = await fetch(`${API}/safesmart/batches/${aktiverBatchId}/complete-review?benutzer_id=${benutzerId}`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = data.detail || {};
      alert(`${detail.message || "Prüfung kann noch nicht abgeschlossen werden."}\n\nOffene Zeilen-IDs: ${(detail.offene_zeilen || []).join(", ") || "keine"}\nUngelöste Konflikte (Zeilen-IDs): ${(detail.ungeloeste_konflikte || []).join(", ") || "keine"}`);
      return;
    }
    await ladeZeilen(aktiverBatchId);
  };

  const batchAbbrechen = async (batchId) => {
    if (!window.confirm("Diesen Importlauf abbrechen?")) return;
    const res = await fetch(`${API}/safesmart/batches/${batchId}/cancel?benutzer_id=${benutzerId}`, { method: "POST" });
    if (!res.ok) { alert("Abbrechen fehlgeschlagen."); return; }
    if (aktiverBatchId === batchId) await ladeZeilen(batchId); else ladeBatches();
  };

  const batchLoeschen = async (batchId) => {
    if (!window.confirm("Diesen Importlauf endgültig löschen? Die Entscheidungen gehen dabei verloren.")) return;
    const res = await fetch(`${API}/safesmart/batches/${batchId}?benutzer_id=${benutzerId}`, { method: "DELETE" });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.detail || "Löschen fehlgeschlagen.");
      return;
    }
    if (aktiverBatchId === batchId) zurueckZurListe(); else ladeBatches();
  };

  const zusammenfassungAnsehen = async (batchId) => {
    const res = await fetch(`${API}/safesmart/batches/${batchId}?benutzer_id=${benutzerId}`);
    setZusammenfassungModal(await res.json());
  };

  const gefiltert = zeilen.filter(z => {
    const konflikt = (z.rfid_conflict && z.rfid_conflict.length > 0) || z.mehrfachzuordnung_konflikt;
    if (konfliktFilter === "mit" && !konflikt) return false;
    if (konfliktFilter === "ohne" && konflikt) return false;
    if (kategorieFilter) {
      if (kategorieFilter === "sicher") {
        if (!SAFESMART_SICHER_KATEGORIEN.includes(z.category)) return false;
      } else if (z.category !== kategorieFilter) return false;
    }
    if (entscheidungFilter && z.decision !== entscheidungFilter) return false;
    if (suche.trim()) {
      const q = suche.trim().toLowerCase();
      const treffer = [z.source_name, z.matched_member_name, z.selected_member_name, z.card_number].some(f => (f || "").toLowerCase().includes(q));
      if (!treffer) return false;
    }
    return true;
  }).sort((a, b) => {
    if (!sortSpalte) return 0;
    const va = typeof a[sortSpalte] === "string" ? a[sortSpalte].toLowerCase() : (a[sortSpalte] ?? 0);
    const vb = typeof b[sortSpalte] === "string" ? b[sortSpalte].toLowerCase() : (b[sortSpalte] ?? 0);
    if (va === vb) return 0;
    return sortRichtung === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const sortierbareSpalte = (key, label) => (
    <th style={{ padding: "14px 16px", textAlign: "left", cursor: "pointer", userSelect: "none" }}
      onClick={() => { if (sortSpalte === key) setSortRichtung(r => r === "asc" ? "desc" : "asc"); else { setSortSpalte(key); setSortRichtung("asc"); } }}>
      {label} {sortSpalte === key ? (sortRichtung === "asc" ? "↑" : "↓") : "↕"}
    </th>
  );

  const wellpassZeilen = zeilen.filter(z => z.decision === "mark_for_wellpass" || z.category === "wellpass_pending");
  const wellpassGefiltert = wellpassZeilen.filter(z => {
    if (!wellpassKategorieFilter) return true;
    if (wellpassKategorieFilter === "noch_nicht_geprueft") return !z.wellpass_matched_at;
    if (wellpassKategorieFilter === "rfid_konflikt") return (z.wellpass_rfid_conflicts || []).length > 0;
    if (wellpassKategorieFilter === "neu_angelegt") return z.wellpass_resolution === "new_created";
    if (wellpassKategorieFilter === "bestaetigt") return z.wellpass_resolution === "existing_confirmed";
    if (wellpassKategorieFilter === "abgelehnt") return z.wellpass_resolution === "rejected";
    return z.wellpass_match_category === wellpassKategorieFilter;
  });

  const sonstigeZeilen = zeilen.filter(z => z.decision === "mark_for_sonstige" || z.category === "sonstige_pending");
  const sonstigeGefiltert = sonstigeZeilen.filter(z => {
    if (!sonstigeKategorieFilter) return true;
    if (sonstigeKategorieFilter === "noch_nicht_geprueft") return !z.sonstige_matched_at;
    if (sonstigeKategorieFilter === "rfid_konflikt") return (z.sonstige_rfid_conflicts || []).length > 0;
    if (sonstigeKategorieFilter === "neu_angelegt") return z.sonstige_resolution === "new_created";
    if (sonstigeKategorieFilter === "bestaetigt") return z.sonstige_resolution === "existing_confirmed";
    if (sonstigeKategorieFilter === "abgelehnt") return z.sonstige_resolution === "rejected";
    return z.sonstige_match_category === sonstigeKategorieFilter;
  });

  const alleAufSeiteAusgewaehlt = gefiltert.length > 0 && gefiltert.every(z => ausgewaehlt.has(z.row_id));
  const toggleAlle = () => setAusgewaehlt(prev => alleAufSeiteAusgewaehlt ? new Set() : new Set(gefiltert.map(z => z.row_id)));
  const toggleEins = (rowId) => setAusgewaehlt(prev => {
    const neu = new Set(prev);
    if (neu.has(rowId)) neu.delete(rowId); else neu.add(rowId);
    return neu;
  });

  const gesamt = zeilen.length;
  const entschieden = zeilen.filter(z => z.decision !== "undecided").length;
  const fortschritt = [
    { label: "Gesamt", wert: gesamt },
    { label: "Entschieden", wert: entschieden },
    { label: "Offen", wert: zeilen.filter(z => z.category !== "invalid_row" && z.decision === "undecided").length, farbe: THEME.colors.danger },
    { label: "Bestätigt", wert: zeilen.filter(z => z.decision === "confirmed_proposal").length, farbe: THEME.colors.success },
    { label: "Manuell zugeordnet", wert: zeilen.filter(z => z.decision === "manually_assigned").length, farbe: THEME.colors.success },
    { label: "Wellpass vorgemerkt", wert: zeilen.filter(z => z.decision === "mark_for_wellpass").length },
    { label: "Sonstige vorgemerkt", wert: zeilen.filter(z => z.decision === "mark_for_sonstige").length },
    { label: "Mitarbeiter/Admin", wert: zeilen.filter(z => z.decision === "staff_admin").length },
    { label: "Ignoriert", wert: zeilen.filter(z => z.decision === "ignored").length },
    { label: "Konflikte", wert: zeilen.filter(z => (z.rfid_conflict?.length || z.mehrfachzuordnung_konflikt)).length, farbe: THEME.colors.danger },
  ];

  if (aktiverBatchId) {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ ...s.heading, marginBottom: 4 }}>Prüfung: {batchInfo?.filename}</h1>
            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Status: {batchInfo?.status}</span>
          </div>
          <button onClick={zurueckZurListe} style={{ ...s.btnGray, width: "auto" }}>← Zurück zur Übersicht</button>
        </div>

        {karteSucheBox}

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button onClick={() => setSubTab("uebersicht")} style={{ ...(subTab === "uebersicht" ? s.btn : s.btnGray), width: "auto", padding: "8px 16px" }}>Übersicht</button>
          <button onClick={() => setSubTab("wellpass")} style={{ ...(subTab === "wellpass" ? s.btn : s.btnGray), width: "auto", padding: "8px 16px" }}>
            Wellpass-Abgleich{wellpassZeilen.length > 0 ? ` (${wellpassZeilen.length})` : ""}
          </button>
          <button onClick={() => setSubTab("sonstige")} style={{ ...(subTab === "sonstige" ? s.btn : s.btnGray), width: "auto", padding: "8px 16px" }}>
            Sonstige-Abgleich{sonstigeZeilen.length > 0 ? ` (${sonstigeZeilen.length})` : ""}
          </button>
        </div>

        {zeilenLaedt ? <p style={{ color: THEME.colors.textMuted }}>Lädt…</p> : subTab === "wellpass" ? (
          <>
            {aktionFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginBottom: 12 }}>{aktionFehler}</p>}

            <div style={{ ...s.karte, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
                <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                  {wellpassZeilen.length} für Wellpass vorgemerkte Zeile{wellpassZeilen.length === 1 ? "" : "n"}
                </span>
                <button onClick={handleWellpassAbgleich} disabled={wellpassAbgleichLaedt || wellpassZeilen.length === 0}
                  style={{ ...s.btn, width: "auto", padding: "8px 16px", opacity: wellpassZeilen.length === 0 ? 0.5 : 1 }}>
                  {wellpassAbgleichLaedt ? "Gleicht ab…" : "Wellpass-Abgleich starten"}
                </button>
              </div>
              {wellpassZusammenfassung && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                  {[
                    { label: "Vorgemerkt", wert: wellpassZusammenfassung.vorgemerkt },
                    { label: "Geprüft", wert: wellpassZusammenfassung.geprueft },
                    { label: "Exakt", wert: wellpassZusammenfassung.wellpass_exact_match },
                    { label: "Normalisiert", wert: wellpassZusammenfassung.wellpass_normalized_match },
                    { label: "Ähnlich", wert: wellpassZusammenfassung.wellpass_suggested_match },
                    { label: "Mehrdeutig", wert: wellpassZusammenfassung.wellpass_ambiguous },
                    { label: "Bestehender Nutzer", wert: wellpassZusammenfassung.wellpass_existing_user },
                    { label: "Kein Treffer", wert: wellpassZusammenfassung.wellpass_unmatched, farbe: THEME.colors.danger },
                    { label: "Bestätigt", wert: wellpassZusammenfassung.existing_confirmed, farbe: THEME.colors.success },
                    { label: "Neu angelegt", wert: wellpassZusammenfassung.new_created, farbe: THEME.colors.success },
                    { label: "Abgelehnt", wert: wellpassZusammenfassung.rejected },
                    { label: "RFID-Konflikte", wert: wellpassZusammenfassung.rfid_konflikte, farbe: THEME.colors.danger },
                  ].map(k => (
                    <div key={k.label}>
                      <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{k.label}</div>
                      <div style={{ color: k.farbe || THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 700 }}>{k.wert ?? 0}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ ...s.karte, marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ minWidth: 220 }}>
                <label style={s.label}>Filter</label>
                <select style={s.input} value={wellpassKategorieFilter} onChange={e => setWellpassKategorieFilter(e.target.value)}>
                  {WELLPASS_FILTER_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, paddingBottom: 14 }}>{wellpassGefiltert.length} von {wellpassZeilen.length} Zeilen</span>
            </div>

            {(() => {
              const wellpassAktionen = (z) => (
                !z.wellpass_resolution || z.wellpass_resolution === "rejected" ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {z.wellpass_match_category === "wellpass_ambiguous" ? (
                      <>
                        {(z.wellpass_candidates || []).map((k, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{k.matched_wellpass_name} ({k.confidence_score}%)</span>
                            {k.matched_wellpass_user_id ? (
                              <button onClick={() => wellpassEntscheiden(z, { aktion: "confirm_existing", selected_wellpass_user_id: k.matched_wellpass_user_id })}
                                style={{ ...s.btn, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Diesen bestätigen</button>
                            ) : (
                              <button onClick={() => setWellpassAnlegenZeile({ ...z, proposed_wellpass_name: k.matched_wellpass_name, wellpass_checkin_count: k.checkins_gesamt, wellpass_first_checkin: k.erster_checkin, wellpass_last_checkin: k.letzter_checkin })}
                                style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Als neu anlegen</button>
                            )}
                          </div>
                        ))}
                        <div>
                          <button onClick={() => setWellpassAuswahlZeile(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs, marginRight: 6 }}>Anderen auswählen</button>
                          <button onClick={() => handleWellpassAblehnen(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Ablehnen</button>
                        </div>
                      </>
                    ) : z.wellpass_match_category === "wellpass_existing_user" ? (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button onClick={() => handleWellpassVorschlagBestaetigen(z)} style={{ ...s.btn, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Bestehenden Nutzer bestätigen</button>
                        <button onClick={() => setWellpassAuswahlZeile(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Anderen Wellpass-Nutzer auswählen</button>
                        <button onClick={() => handleWellpassAblehnen(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Ablehnen</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button onClick={() => setWellpassAnlegenZeile(z)} style={{ ...s.btn, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>
                          {z.wellpass_match_category === "wellpass_suggested_match" ? "Vorschlag verwenden" : z.wellpass_match_category === "wellpass_unmatched" ? "Neu anlegen" : "Als Wellpass-Nutzer anlegen"}
                        </button>
                        <button onClick={() => setWellpassAuswahlZeile(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Anderen auswählen</button>
                        <button onClick={() => handleWellpassAblehnen(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Ablehnen</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>Erledigt</span>
                )
              );

              return mobil ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {wellpassGefiltert.map(z => (
                    <div key={z.row_id} style={s.karte}>
                      <p style={{ color: THEME.colors.text, fontWeight: 600, margin: "0 0 8px" }}>{z.source_name || <em style={{ color: THEME.colors.textMuted }}>(leer)</em>}</p>
                      {[
                        ["Karte", <span style={{ fontFamily: "monospace" }}>{z.card_number || "–"}</span>],
                        ["Vorschlag", z.proposed_wellpass_name || "–"],
                        ["Kategorie", <WellpassMatchBadge kategorie={z.wellpass_match_category} />],
                        ["Score", z.wellpass_confidence_score != null ? `${z.wellpass_confidence_score}%` : "–"],
                        ["Grund", z.wellpass_match_reason || "–"],
                        ["Check-ins", z.wellpass_checkin_count ?? "–"],
                        ["Zeitraum", z.wellpass_first_checkin ? `${z.wellpass_first_checkin} – ${z.wellpass_last_checkin}` : "–"],
                        ["RFID-Konflikt", (z.wellpass_rfid_conflicts || []).length > 0 ? <span style={{ color: THEME.colors.danger }}>{z.wellpass_rfid_conflicts.join("; ")}</span> : "–"],
                        ["Entscheidung", <>{z.wellpass_resolution ? WELLPASS_RESOLUTION_LABEL[z.wellpass_resolution] : (z.wellpass_matched_at ? "Noch offen" : "Noch nicht geprüft")}{z.selected_wellpass_user_name && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{z.selected_wellpass_user_name}</div>}</>],
                      ].map(([label, wert]) => (
                        <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 0", fontSize: THEME.fontSize.sm, borderTop: `1px solid ${THEME.colors.border}` }}>
                          <span style={{ color: THEME.colors.textMuted, flexShrink: 0 }}>{label}</span>
                          <span style={{ color: THEME.colors.text, textAlign: "right" }}>{wert}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${THEME.colors.border}` }}>{wellpassAktionen(z)}</div>
                    </div>
                  ))}
                  {wellpassGefiltert.length === 0 && (
                    <p style={{ padding: 24, textAlign: "center", color: THEME.colors.textMuted }}>Keine Zeilen für diesen Filter.</p>
                  )}
                </div>
              ) : (
                <div style={{ ...s.tabelle, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1300 }}>
                    <thead>
                      <tr style={{ background: THEME.colors.background, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, textTransform: "uppercase" }}>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>SafeSmart-Name</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Karte</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Vorschlag</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Kategorie</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Score</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Grund</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Check-ins</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Zeitraum</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>RFID-Konflikt</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Entscheidung</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wellpassGefiltert.map(z => (
                        <tr key={z.row_id} style={{ borderTop: `1px solid ${THEME.colors.border}` }}>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{z.source_name || <em style={{ color: THEME.colors.textMuted }}>(leer)</em>}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text, fontFamily: "monospace" }}>{z.card_number || "–"}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{z.proposed_wellpass_name || "–"}</td>
                          <td style={{ padding: "12px 16px" }}><WellpassMatchBadge kategorie={z.wellpass_match_category} /></td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textMuted }}>{z.wellpass_confidence_score != null ? `${z.wellpass_confidence_score}%` : "–"}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{z.wellpass_match_reason || "–"}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{z.wellpass_checkin_count ?? "–"}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
                            {z.wellpass_first_checkin ? `${z.wellpass_first_checkin} – ${z.wellpass_last_checkin}` : "–"}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            {(z.wellpass_rfid_conflicts || []).length > 0 ? (
                              <span style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{z.wellpass_rfid_conflicts.join("; ")}</span>
                            ) : "–"}
                          </td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                            {z.wellpass_resolution ? WELLPASS_RESOLUTION_LABEL[z.wellpass_resolution] : (z.wellpass_matched_at ? "Noch offen" : "Noch nicht geprüft")}
                            {z.selected_wellpass_user_name && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{z.selected_wellpass_user_name}</div>}
                          </td>
                          <td style={{ padding: "12px 16px" }}>{wellpassAktionen(z)}</td>
                        </tr>
                      ))}
                      {wellpassGefiltert.length === 0 && (
                        <tr><td colSpan={11} style={{ padding: 24, textAlign: "center", color: THEME.colors.textMuted }}>Keine Zeilen für diesen Filter.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </>
        ) : subTab === "sonstige" ? (
          <>
            {aktionFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginBottom: 12 }}>{aktionFehler}</p>}

            <div style={{ ...s.karte, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
                <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                  {sonstigeZeilen.length} für Sonstige vorgemerkte Zeile{sonstigeZeilen.length === 1 ? "" : "n"}
                </span>
                <button onClick={handleSonstigeAbgleich} disabled={sonstigeAbgleichLaedt || sonstigeZeilen.length === 0}
                  style={{ ...s.btn, width: "auto", padding: "8px 16px", opacity: sonstigeZeilen.length === 0 ? 0.5 : 1 }}>
                  {sonstigeAbgleichLaedt ? "Gleicht ab…" : "Sonstige-Abgleich starten"}
                </button>
              </div>
              {sonstigeZusammenfassung && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                  {[
                    { label: "Vorgemerkt", wert: sonstigeZusammenfassung.vorgemerkt },
                    { label: "Geprüft", wert: sonstigeZusammenfassung.geprueft },
                    { label: "Exakt", wert: sonstigeZusammenfassung.sonstige_exact_match },
                    { label: "Normalisiert", wert: sonstigeZusammenfassung.sonstige_normalized_match },
                    { label: "Ähnlich", wert: sonstigeZusammenfassung.sonstige_suggested_match },
                    { label: "Mehrdeutig", wert: sonstigeZusammenfassung.sonstige_ambiguous },
                    { label: "Kein Treffer", wert: sonstigeZusammenfassung.sonstige_unmatched, farbe: THEME.colors.danger },
                    { label: "Bestätigt", wert: sonstigeZusammenfassung.existing_confirmed, farbe: THEME.colors.success },
                    { label: "Neu angelegt", wert: sonstigeZusammenfassung.new_created, farbe: THEME.colors.success },
                    { label: "Abgelehnt", wert: sonstigeZusammenfassung.rejected },
                    { label: "RFID-Konflikte", wert: sonstigeZusammenfassung.rfid_konflikte, farbe: THEME.colors.danger },
                  ].map(k => (
                    <div key={k.label}>
                      <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{k.label}</div>
                      <div style={{ color: k.farbe || THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 700 }}>{k.wert ?? 0}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ ...s.karte, marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ minWidth: 220 }}>
                <label style={s.label}>Filter</label>
                <select style={s.input} value={sonstigeKategorieFilter} onChange={e => setSonstigeKategorieFilter(e.target.value)}>
                  {SONSTIGE_FILTER_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, paddingBottom: 14 }}>{sonstigeGefiltert.length} von {sonstigeZeilen.length} Zeilen</span>
            </div>

            {(() => {
              const sonstigeAktionen = (z) => (
                !z.sonstige_resolution || z.sonstige_resolution === "rejected" ? (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {z.proposed_sonstige_user_id && (
                      <button onClick={() => handleSonstigeVorschlagBestaetigen(z)} style={{ ...s.btn, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Bestätigen</button>
                    )}
                    <button onClick={() => setSonstigeAuswahlZeile(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Anderen auswählen</button>
                    <button onClick={() => setSonstigeAnlegenZeile(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Neu anlegen</button>
                    <button onClick={() => handleSonstigeAblehnen(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Ablehnen</button>
                  </div>
                ) : (
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>Erledigt</span>
                )
              );

              return mobil ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {sonstigeGefiltert.map(z => (
                    <div key={z.row_id} style={s.karte}>
                      <p style={{ color: THEME.colors.text, fontWeight: 600, margin: "0 0 8px" }}>{z.source_name || <em style={{ color: THEME.colors.textMuted }}>(leer)</em>}</p>
                      {[
                        ["Karte", <span style={{ fontFamily: "monospace" }}>{z.card_number || "–"}</span>],
                        ["Vorschlag", z.proposed_sonstige_name || "–"],
                        ["Kategorie", <SonstigeMatchBadge kategorie={z.sonstige_match_category} />],
                        ["Score", z.sonstige_confidence_score != null ? `${z.sonstige_confidence_score}%` : "–"],
                        ["RFID-Konflikt", (z.sonstige_rfid_conflicts || []).length > 0 ? <span style={{ color: THEME.colors.danger }}>{z.sonstige_rfid_conflicts.join("; ")}</span> : "–"],
                        ["Entscheidung", <>{z.sonstige_resolution ? SONSTIGE_RESOLUTION_LABEL[z.sonstige_resolution] : (z.sonstige_matched_at ? "Noch offen" : "Noch nicht geprüft")}{z.selected_sonstige_user_name && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{z.selected_sonstige_user_name}</div>}</>],
                      ].map(([label, wert]) => (
                        <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 0", fontSize: THEME.fontSize.sm, borderTop: `1px solid ${THEME.colors.border}` }}>
                          <span style={{ color: THEME.colors.textMuted, flexShrink: 0 }}>{label}</span>
                          <span style={{ color: THEME.colors.text, textAlign: "right" }}>{wert}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${THEME.colors.border}` }}>{sonstigeAktionen(z)}</div>
                    </div>
                  ))}
                  {sonstigeGefiltert.length === 0 && (
                    <p style={{ padding: 24, textAlign: "center", color: THEME.colors.textMuted }}>Keine Zeilen für diesen Filter.</p>
                  )}
                </div>
              ) : (
                <div style={{ ...s.tabelle, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
                    <thead>
                      <tr style={{ background: THEME.colors.background, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, textTransform: "uppercase" }}>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>SafeSmart-Name</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Karte</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Vorschlag</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Kategorie</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Score</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>RFID-Konflikt</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Entscheidung</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sonstigeGefiltert.map(z => (
                        <tr key={z.row_id} style={{ borderTop: `1px solid ${THEME.colors.border}` }}>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{z.source_name || <em style={{ color: THEME.colors.textMuted }}>(leer)</em>}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text, fontFamily: "monospace" }}>{z.card_number || "–"}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{z.proposed_sonstige_name || "–"}</td>
                          <td style={{ padding: "12px 16px" }}><SonstigeMatchBadge kategorie={z.sonstige_match_category} /></td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textMuted }}>{z.sonstige_confidence_score != null ? `${z.sonstige_confidence_score}%` : "–"}</td>
                          <td style={{ padding: "12px 16px" }}>
                            {(z.sonstige_rfid_conflicts || []).length > 0 ? (
                              <span style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{z.sonstige_rfid_conflicts.join("; ")}</span>
                            ) : "–"}
                          </td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                            {z.sonstige_resolution ? SONSTIGE_RESOLUTION_LABEL[z.sonstige_resolution] : (z.sonstige_matched_at ? "Noch offen" : "Noch nicht geprüft")}
                            {z.selected_sonstige_user_name && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{z.selected_sonstige_user_name}</div>}
                          </td>
                          <td style={{ padding: "12px 16px" }}>{sonstigeAktionen(z)}</td>
                        </tr>
                      ))}
                      {sonstigeGefiltert.length === 0 && (
                        <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: THEME.colors.textMuted }}>Keine Zeilen für diesen Filter.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </>
        ) : (
          <>
            <div style={{ ...s.karte, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                <span>{entschieden} von {gesamt} Datensätzen geprüft</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={handleWellpassAutoVormerken} disabled={autoVormerkenLaedt} style={{ ...s.btnGray, width: "auto", padding: "8px 16px" }}>
                    {autoVormerkenLaedt ? "Prüft…" : "Alle offenen automatisch gegen Wellpass prüfen"}
                  </button>
                  <button onClick={pruefungAbschliessen} style={{ ...s.btn, width: "auto", padding: "8px 16px" }}>Prüfung abschließen</button>
                </div>
              </div>
              <div style={{ height: 8, background: THEME.colors.background, borderRadius: 4, overflow: "hidden", marginBottom: 12 }}>
                <div style={{ height: "100%", width: `${gesamt ? (entschieden / gesamt) * 100 : 0}%`, background: THEME.colors.success }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
                {fortschritt.map(k => (
                  <div key={k.label}>
                    <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{k.label}</div>
                    <div style={{ color: k.farbe || THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 700 }}>{k.wert}</div>
                  </div>
                ))}
              </div>
            </div>

            {aktionFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginBottom: 12 }}>{aktionFehler}</p>}

            <div style={{ ...s.karte, marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ minWidth: 200 }}>
                <label style={s.label}>Kategorie</label>
                <select style={s.input} value={kategorieFilter} onChange={e => setKategorieFilter(e.target.value)}>
                  {SAFESMART_FILTER_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ minWidth: 200 }}>
                <label style={s.label}>Entscheidung</label>
                <select style={s.input} value={entscheidungFilter} onChange={e => setEntscheidungFilter(e.target.value)}>
                  {SAFESMART_ENTSCHEIDUNG_FILTER_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ minWidth: 160 }}>
                <label style={s.label}>Konflikt</label>
                <select style={s.input} value={konfliktFilter} onChange={e => setKonfliktFilter(e.target.value)}>
                  {SAFESMART_KONFLIKT_FILTER_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ minWidth: 240, flex: 1 }}>
                <label style={s.label}>Suche (Name, Mitglied, Kartennummer)</label>
                <input style={s.input} value={suche} onChange={e => setSuche(e.target.value)} placeholder="Suchen…" />
              </div>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, paddingBottom: 14 }}>{gefiltert.length} von {zeilen.length} Zeilen</span>
            </div>

            {ausgewaehlt.size > 0 && (
              <div style={{ ...s.karte, marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{ausgewaehlt.size} ausgewählt:</span>
                <button onClick={() => bulkEntscheiden("confirmed_proposal")} style={{ ...s.btn, width: "auto", padding: "8px 14px" }}>Vorschläge bestätigen</button>
                <button onClick={() => bulkEntscheiden("mark_for_wellpass")} style={{ ...s.btnGray, width: "auto", padding: "8px 14px" }}>Als Wellpass vormerken</button>
                <button onClick={() => bulkEntscheiden("ignored", "Sammelaktion")} style={{ ...s.btnGray, width: "auto", padding: "8px 14px" }}>Ignorieren</button>
                <button onClick={() => bulkEntscheiden("undecided")} style={{ ...s.btnGray, width: "auto", padding: "8px 14px" }}>Zurücksetzen</button>
              </div>
            )}

            {(() => {
              const zeilenAktionen = (z) => (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {z.category !== "invalid_row" && z.category !== "staff_admin" && z.decision !== "confirmed_proposal" && z.matched_member_id && (
                    <button onClick={() => entscheiden(z, { decision: "confirmed_proposal" })} style={{ ...s.btn, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Vorschlag bestätigen</button>
                  )}
                  {z.category !== "invalid_row" && (
                    <button onClick={() => setMitgliedDialogZeile(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Anderes Mitglied</button>
                  )}
                  {z.category !== "invalid_row" && z.decision !== "mark_for_wellpass" && (
                    <button onClick={() => entscheiden(z, { decision: "mark_for_wellpass" })} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Wellpass</button>
                  )}
                  {z.category !== "invalid_row" && z.decision !== "mark_for_sonstige" && (
                    <button onClick={() => entscheiden(z, { decision: "mark_for_sonstige" })} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Sonstige</button>
                  )}
                  {z.category !== "invalid_row" && z.decision !== "staff_admin" && (
                    <button onClick={() => entscheiden(z, { decision: "staff_admin" })} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Mitarbeiter/Admin</button>
                  )}
                  {z.category !== "invalid_row" && z.decision !== "ignored" && (
                    <button onClick={() => setIgnorierenZeile(z)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Ignorieren</button>
                  )}
                  {z.decision !== "undecided" && (
                    <button onClick={() => entscheiden(z, { decision: "undecided" })} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>Zurücksetzen</button>
                  )}
                </div>
              );

              return mobil ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {gefiltert.map(z => (
                    <div key={z.row_id} style={s.karte}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                        <input type="checkbox" checked={ausgewaehlt.has(z.row_id)} onChange={() => toggleEins(z.row_id)} style={{ marginTop: 4 }} />
                        <p style={{ color: THEME.colors.text, fontWeight: 600, margin: 0, cursor: "pointer" }} onClick={() => setDetailZeile(z)}>
                          #{z.row_number} · {z.source_name || <em style={{ color: THEME.colors.textMuted }}>(leer)</em>}
                        </p>
                      </div>
                      {[
                        ["Karte", <span style={{ fontFamily: "monospace" }}>{z.card_number || "–"}</span>],
                        ["Kategorie", <SafeSmartBadge kategorie={z.category} />],
                        ["Vorschlag", z.matched_member_name || "–"],
                        ["Score", z.confidence_score != null ? `${z.confidence_score}%` : "–"],
                        ["RFID-Konflikt", (z.rfid_conflict?.length > 0 || z.mehrfachzuordnung_konflikt) ? (
                          <span style={{ color: THEME.colors.danger }}>{[...(z.rfid_conflict || []), z.mehrfachzuordnung_konflikt ? "Mehrfach im Lauf zugeordnet" : null].filter(Boolean).join("; ")}</span>
                        ) : "–"],
                        ["Entscheidung", <>{SAFESMART_DECISION_LABEL[z.decision]}{z.decided_by && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{z.decided_by}</div>}</>],
                        ["Ausgewählte Person", z.selected_member_name || "–"],
                        ["Notiz", z.note || "–"],
                      ].map(([label, wert]) => (
                        <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 0", fontSize: THEME.fontSize.sm, borderTop: `1px solid ${THEME.colors.border}` }}>
                          <span style={{ color: THEME.colors.textMuted, flexShrink: 0 }}>{label}</span>
                          <span style={{ color: THEME.colors.text, textAlign: "right" }}>{wert}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${THEME.colors.border}` }}>{zeilenAktionen(z)}</div>
                    </div>
                  ))}
                  {gefiltert.length === 0 && (
                    <p style={{ padding: 24, textAlign: "center", color: THEME.colors.textMuted }}>Keine Zeilen für diese Filter.</p>
                  )}
                </div>
              ) : (
                <div style={{ ...s.tabelle, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
                    <thead>
                      <tr style={{ background: THEME.colors.background, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, textTransform: "uppercase" }}>
                        <th style={{ padding: "14px 8px" }}><input type="checkbox" checked={alleAufSeiteAusgewaehlt} onChange={toggleAlle} /></th>
                        {sortierbareSpalte("row_number", "Zeile")}
                        {sortierbareSpalte("source_name", "SafeSmart-Name")}
                        {sortierbareSpalte("card_number", "Karte")}
                        {sortierbareSpalte("category", "Kategorie")}
                        {sortierbareSpalte("matched_member_name", "Vorschlag")}
                        {sortierbareSpalte("confidence_score", "Score")}
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>RFID-Konflikt</th>
                        {sortierbareSpalte("decision", "Entscheidung")}
                        {sortierbareSpalte("selected_member_name", "Ausgewählte Person")}
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Notiz</th>
                        <th style={{ padding: "14px 16px", textAlign: "left" }}>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gefiltert.map(z => (
                        <tr key={z.row_id} style={{ borderTop: `1px solid ${THEME.colors.border}` }}>
                          <td style={{ padding: "12px 8px" }}><input type="checkbox" checked={ausgewaehlt.has(z.row_id)} onChange={() => toggleEins(z.row_id)} /></td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textMuted, cursor: "pointer" }} onClick={() => setDetailZeile(z)}>{z.row_number}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text, cursor: "pointer" }} onClick={() => setDetailZeile(z)}>
                            {z.source_name || <em style={{ color: THEME.colors.textMuted }}>(leer)</em>}
                          </td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text, fontFamily: "monospace" }}>{z.card_number || "–"}</td>
                          <td style={{ padding: "12px 16px" }}><SafeSmartBadge kategorie={z.category} /></td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{z.matched_member_name || "–"}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textMuted }}>{z.confidence_score != null ? `${z.confidence_score}%` : "–"}</td>
                          <td style={{ padding: "12px 16px" }}>
                            {(z.rfid_conflict?.length > 0 || z.mehrfachzuordnung_konflikt) ? (
                              <span style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>
                                {[...(z.rfid_conflict || []), z.mehrfachzuordnung_konflikt ? "Mehrfach im Lauf zugeordnet" : null].filter(Boolean).join("; ")}
                              </span>
                            ) : "–"}
                          </td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                            {SAFESMART_DECISION_LABEL[z.decision]}
                            {z.decided_by && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{z.decided_by}</div>}
                          </td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{z.selected_member_name || "–"}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, maxWidth: 160 }}>{z.note || "–"}</td>
                          <td style={{ padding: "12px 16px" }}>{zeilenAktionen(z)}</td>
                        </tr>
                      ))}
                      {gefiltert.length === 0 && (
                        <tr><td colSpan={12} style={{ padding: 24, textAlign: "center", color: THEME.colors.textMuted }}>Keine Zeilen für diese Filter.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </>
        )}

        {detailZeile && <SafeSmartZeilenDetailDialog zeile={detailZeile} onSchliessen={() => setDetailZeile(null)} />}
        {mitgliedDialogZeile && (
          <SafeSmartMitgliedAuswahlDialog zeile={mitgliedDialogZeile} onSchliessen={() => setMitgliedDialogZeile(null)} onAusgewaehlt={handleAndersesMitglied} />
        )}
        {ignorierenZeile && <SafeSmartIgnorierenDialog onSchliessen={() => setIgnorierenZeile(null)} onBestaetigt={handleIgnorieren} />}
        {wellpassAuswahlZeile && (
          <WellpassNutzerAuswahlDialog zeile={wellpassAuswahlZeile} onSchliessen={() => setWellpassAuswahlZeile(null)} onAusgewaehlt={handleWellpassAnderenNutzer} />
        )}
        {wellpassAnlegenZeile && (
          <WellpassNutzerAnlegenDialog zeile={wellpassAnlegenZeile} onSchliessen={() => setWellpassAnlegenZeile(null)} onAnlegen={(formData) => handleWellpassNeuAnlegen(formData)} />
        )}
        {sonstigeAuswahlZeile && (
          <SonstigeNutzerAuswahlDialog zeile={sonstigeAuswahlZeile} onSchliessen={() => setSonstigeAuswahlZeile(null)} onAusgewaehlt={handleSonstigeAnderenNutzer} />
        )}
        {sonstigeAnlegenZeile && (
          <SonstigeNutzerAnlegenDialog zeile={sonstigeAnlegenZeile} onSchliessen={() => setSonstigeAnlegenZeile(null)} onAnlegen={(formData) => handleSonstigeNeuAnlegen(formData)} />
        )}
      </div>
    );
  }

  return (
    <div>
      <h1 style={s.heading}>SafeSmart-Import</h1>

      {karteSucheBox}

      <div style={{ ...s.karte, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <h3 onClick={() => setTtlockAufgeklappt(a => !a)} style={{ color: THEME.colors.text, margin: 0, cursor: "pointer", flex: 1 }}>🔐 TTLock: manuelle Registrierung – Fortschritt</h3>
          <input
            value={ttlockSuche}
            onChange={e => { setTtlockSuche(e.target.value); if (e.target.value.trim() && !ttlockAufgeklappt) setTtlockAufgeklappt(true); }}
            placeholder="Nach Name suchen…"
            style={{ ...s.input, width: 200 }}
          />
          <span onClick={() => setTtlockAufgeklappt(a => !a)} style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl, cursor: "pointer" }}>{ttlockAufgeklappt ? "▲" : "▼"}</span>
        </div>
        {!ttlockOffen ? (
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 12 }}>Lädt…</p>
        ) : (
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "12px 0 0" }}>
            <strong style={{ color: THEME.colors.success }}>{ttlockOffen.anzahl_bestaetigt}</strong> manuell bestätigt · <strong style={{ color: ttlockOffen.anzahl_offen > 0 ? THEME.colors.warning : THEME.colors.success }}>{ttlockOffen.anzahl_offen}</strong> noch offen
          </p>
        )}
        {ttlockAufgeklappt && ttlockOffen && (() => {
          const gefiltert = ttlockSuche.trim()
            ? ttlockOffen.personen.filter(p => p.name.toLowerCase().includes(ttlockSuche.trim().toLowerCase()))
            : ttlockOffen.personen;
          return (
            <div style={{ maxHeight: 400, overflowY: "auto", marginTop: 14 }}>
              {ttlockOffen.personen.length === 0 && <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm }}>🎉 Alle aktiven Personen sind manuell bestätigt.</p>}
              {ttlockOffen.personen.length > 0 && gefiltert.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Keine Treffer für "{ttlockSuche}".</p>}
              {gefiltert.map(p => (
                <div key={`${p.typ}-${p.id}`} onClick={() => onPersonOeffnen(p.typ, p.id)}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, marginBottom: 4, background: THEME.colors.background, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted}
                  onMouseLeave={e => e.currentTarget.style.background = THEME.colors.background}>
                  <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base }}>{p.name}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                    <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{TTLOCK_TYP_LABEL[p.typ]}</span>
                    <button onClick={e => handleTtlockEntfernen(p, e)} title="Aus dieser Liste entfernen" style={{ background: "none", border: "none", color: THEME.colors.textMuted, cursor: "pointer", fontSize: THEME.fontSize.base, padding: "0 4px", lineHeight: 1 }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      <div style={{ ...s.karte, marginBottom: 20 }}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0, marginBottom: 12 }}>Bisherige Importläufe</h3>
        {batchesLaedt ? <p style={{ color: THEME.colors.textMuted }}>Lädt…</p> : batches.length === 0 ? (
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Noch keine Importläufe vorhanden.</p>
        ) : mobil ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {batches.map(b => (
              <div key={b.batch_id} style={s.karte}>
                <p style={{ color: THEME.colors.text, fontWeight: 600, margin: "0 0 8px" }}>{b.filename}</p>
                {[
                  ["Hochgeladen", b.uploaded_at ? new Date(b.uploaded_at).toLocaleString("de-DE") : "–"],
                  ["Status", b.status],
                  ["Zeilen", b.total_rows],
                  ["Sicher", <span style={{ color: THEME.colors.success }}>{b.sichere_treffer}</span>],
                  ["Offen", <span style={{ color: b.offene_faelle > 0 ? THEME.colors.danger : THEME.colors.text }}>{b.offene_faelle}</span>],
                  ["Konflikte", <span style={{ color: b.konflikte > 0 ? THEME.colors.danger : THEME.colors.text }}>{b.konflikte}</span>],
                  ["Bearbeitet von", b.bearbeitet_von],
                ].map(([label, wert]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 0", fontSize: THEME.fontSize.sm, borderTop: `1px solid ${THEME.colors.border}` }}>
                    <span style={{ color: THEME.colors.textMuted, flexShrink: 0 }}>{label}</span>
                    <span style={{ color: THEME.colors.text, textAlign: "right" }}>{wert}</span>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${THEME.colors.border}` }}>
                  {b.status !== "cancelled" && (
                    <button onClick={() => oeffneBatch(b.batch_id)} style={{ ...s.btn, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.xs }}>Prüfung fortsetzen</button>
                  )}
                  <button onClick={() => zusammenfassungAnsehen(b.batch_id)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.xs }}>Zusammenfassung</button>
                  {b.status !== "cancelled" && (
                    <button onClick={() => batchAbbrechen(b.batch_id)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.xs }}>Abbrechen</button>
                  )}
                  <button onClick={() => batchLoeschen(b.batch_id)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.xs, color: THEME.colors.danger }}>Löschen</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
              <thead>
                <tr style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, textTransform: "uppercase" }}>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Dateiname</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Hochgeladen</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Status</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Zeilen</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Sicher</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Offen</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Konflikte</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Bearbeitet von</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.batch_id} style={{ borderTop: `1px solid ${THEME.colors.border}` }}>
                    <td style={{ padding: "10px 12px", color: THEME.colors.text }}>{b.filename}</td>
                    <td style={{ padding: "10px 12px", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{b.uploaded_at ? new Date(b.uploaded_at).toLocaleString("de-DE") : "–"}</td>
                    <td style={{ padding: "10px 12px", color: THEME.colors.textMuted }}>{b.status}</td>
                    <td style={{ padding: "10px 12px", color: THEME.colors.text }}>{b.total_rows}</td>
                    <td style={{ padding: "10px 12px", color: THEME.colors.success }}>{b.sichere_treffer}</td>
                    <td style={{ padding: "10px 12px", color: b.offene_faelle > 0 ? THEME.colors.danger : THEME.colors.text }}>{b.offene_faelle}</td>
                    <td style={{ padding: "10px 12px", color: b.konflikte > 0 ? THEME.colors.danger : THEME.colors.text }}>{b.konflikte}</td>
                    <td style={{ padding: "10px 12px", color: THEME.colors.textMuted }}>{b.bearbeitet_von}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {b.status !== "cancelled" && (
                          <button onClick={() => oeffneBatch(b.batch_id)} style={{ ...s.btn, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.xs }}>Prüfung fortsetzen</button>
                        )}
                        <button onClick={() => zusammenfassungAnsehen(b.batch_id)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.xs }}>Zusammenfassung</button>
                        {b.status !== "cancelled" && (
                          <button onClick={() => batchAbbrechen(b.batch_id)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.xs }}>Abbrechen</button>
                        )}
                        <button onClick={() => batchLoeschen(b.batch_id)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.xs, color: THEME.colors.danger }}>Löschen</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={s.karte}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0, marginBottom: 12 }}>Neue Analyse</h3>
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, marginBottom: 20 }}>
          SafeSmart-Export (.xlsx) hochladen, um eine Vorschau-Analyse gegen die bestehenden ComeFit-Mitglieder zu erhalten.
          Es wird noch keine RFID-Nummer gespeichert oder importiert.
        </p>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ ...s.btnGray, width: "auto", cursor: "pointer", display: "inline-block" }}>
            Datei auswählen
            <input type="file" accept=".xlsx" style={{ display: "none" }} onChange={e => setDatei(e.target.files[0] || null)} />
          </label>
          {datei && (
            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
              {datei.name} ({(datei.size / 1024).toFixed(0)} KB) – bereit zur Analyse
            </span>
          )}
          <button
            onClick={handleAnalysieren}
            style={{ ...s.btn, width: "auto", opacity: datei && !uploadLaedt ? 1 : 0.5 }}
            disabled={!datei || uploadLaedt}
          >
            {uploadLaedt ? "Analysiert…" : "Datei analysieren"}
          </button>
        </div>
        {uploadFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 12 }}>{uploadFehler}</p>}
      </div>

      {zusammenfassungModal && (
        <div style={s.modalBg} onClick={() => setZusammenfassungModal(null)}>
          <div style={{ ...s.modal, width: "min(480px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitel}>Zusammenfassung: {zusammenfassungModal.filename}</h3>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Status: {zusammenfassungModal.status}</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
              {Object.entries(zusammenfassungModal.zusammenfassung || {}).map(([k, v]) => (
                <div key={k} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{k}: <strong style={{ color: THEME.colors.text }}>{String(v)}</strong></div>
              ))}
            </div>
            <button onClick={() => setZusammenfassungModal(null)} style={{ ...s.btnGray, width: "auto", marginTop: 16 }}>Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PartnerBereich({ mitglied, onAktualisiert }) {
  const [partner, setPartner] = useState([]);
  const [suche, setSuche] = useState("");
  const [sucheErgebnisse, setSucheErgebnisse] = useState([]);
  const [suchOffen, setSuchOffen] = useState(false);

  const laden = () => fetch(`${API}/partner/mitglied/${mitglied.id}`).then(r => r.json()).then(setPartner).catch(() => {});
  useEffect(() => { laden(); }, [mitglied.id]);

  const handleSuche = async (q) => {
    setSuche(q);
    if (q.length < 2) { setSucheErgebnisse([]); return; }
    const res = await fetch(`${API}/partner/suche?q=${encodeURIComponent(q)}`);
    const data = await res.json();
    setSucheErgebnisse(data.filter(m => m.id !== mitglied.id));
  };

  const handleVerknuepfen = async (partnerId) => {
    const res = await fetch(`${API}/partner/verknuepfen`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mitglied_id_1: mitglied.id, mitglied_id_2: partnerId })
    });
    if (!res.ok) { alert("Verknüpfen fehlgeschlagen."); return; }
    setSuche(""); setSucheErgebnisse([]); setSuchOffen(false);
    laden();
  };

  const handleTrennen = async (partnerId) => {
    if (!window.confirm("Partner wirklich trennen? Der Partnerrabatt wird zurückgesetzt.")) return;
    const res = await fetch(`${API}/partner/trennen/${partnerId}`, { method: "DELETE" });
    if (!res.ok) { alert("Trennen fehlgeschlagen."); return; }
    laden();
  };

  return (
    <div style={{ ...s.karte, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>Partner ({partner.length})</h3>
        <button onClick={() => setSuchOffen(!suchOffen)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.base }}>+ Partner verknüpfen</button>
      </div>

      {suchOffen && (
        <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={s.inputGroup}>
            <label style={s.label}>Mitglied suchen</label>
            <input style={s.input} value={suche} onChange={e => handleSuche(e.target.value)} placeholder="Name eingeben..." autoFocus />
          </div>
          {sucheErgebnisse.length > 0 && (
            <div style={{ background: THEME.colors.surface, borderRadius: 8, overflow: "hidden" }}>
              {sucheErgebnisse.map(m => (
                <div key={m.id} onClick={() => handleVerknuepfen(m.id)}
                  style={{ padding: "12px 16px", borderBottom: "1px solid #2b3d52", cursor: "pointer", display: "flex", justifyContent: "space-between" }}
                  onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <span style={{ color: THEME.colors.text }}>{m.name}</span>
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{m.tarif} – {parseFloat(m.preis).toFixed(2)} €</span>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => { setSuchOffen(false); setSuche(""); setSucheErgebnisse([]); }} style={{ ...s.btnGray, marginTop: 8, width: "auto" }}>Abbrechen</button>
        </div>
      )}

      {partner.length === 0 && !suchOffen && (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.md }}>Keine Partner verknüpft.</p>
      )}

      {partner.map(p => (
        <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "1px solid #2b3d52" }}>
          <div>
            <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md, fontWeight: 600 }}>{p.name}</span>
            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginLeft: 12 }}>{p.tarif} – {parseFloat(p.preis).toFixed(2)} €</span>
            {p.status !== "aktiv" && <span style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.xs, marginLeft: 8 }}>⚠ {p.status}</span>}
          </div>
          <button onClick={() => handleTrennen(p.id)} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg, fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>Trennen</button>
        </div>
      ))}
    </div>
  );
}

// Mitglieder-App (my.comefit.de) - Zugangsstatus + Sperren/Entsperren (Nutzer-Wunsch 2026-08-22:
// die Aktivierung selbst laeuft automatisch per E-Mail-Bestaetigung, ein Admin soll aber
// jederzeit im Nachhinein einen einzelnen Zugang sperren koennen). Nur fuer Admins sichtbar,
// siehe Aufrufstelle in MitgliedDetail.
function MitgliederAppZugangBereich({ mitgliedId, herkunft, quelleId }) {
  // herkunft/quelleId: Wellpass-/Hansefit-/Sonstige-Person statt Mitglied (App-Zugang ueber Schatten-Konto, siehe backend/app_konto.py)
  const [status, setStatus] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const basis = herkunft ? `${API}/api/member/admin/extern/${herkunft}/${quelleId}/zugang` : `${API}/api/member/admin/mitglied/${mitgliedId}/zugang`;

  const laden = () => fetch(basis).then(r => r.ok ? r.json() : null).then(setStatus).catch(() => setStatus(null));
  useEffect(() => { laden(); }, [mitgliedId, herkunft, quelleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sperren = async () => {
    setLaedt(true);
    const res = await fetch(`${basis}/sperren`, { method: "PUT" });
    setLaedt(false);
    if (!res.ok) { alert("Sperren fehlgeschlagen."); return; }
    laden();
  };
  const entsperren = async () => {
    setLaedt(true);
    const res = await fetch(`${basis}/entsperren`, { method: "PUT" });
    setLaedt(false);
    if (!res.ok) { alert("Entsperren fehlgeschlagen."); return; }
    laden();
  };

  return (
    <div style={{ ...s.karte, marginBottom: 16 }}>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Mitglieder-App</h3>
      {!status ? (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, margin: 0 }}>Lädt…</p>
      ) : !status.hat_zugang ? (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, margin: 0 }}>Noch kein Zugang eingerichtet – {herkunft ? "die Person hat" : "das Mitglied hat"} die App-Aktivierung noch nicht angefordert.{herkunft && status.email_vorhanden === false ? " Es ist keine E-Mail-Adresse hinterlegt – ohne E-Mail kann sich die Person nicht selbst freischalten." : ""}</p>
      ) : !status.aktiviert ? (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, margin: 0 }}>Aktivierung angefordert, aber noch kein Passwort vergeben.</p>
      ) : status.aktiv ? (
        <>
          <p style={{ color: THEME.colors.success, fontWeight: 700, fontSize: THEME.fontSize.base, margin: "0 0 12px" }}>✓ Zugang aktiv</p>
          <button onClick={sperren} disabled={laedt} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg }}>{laedt ? "…" : "Zugang sperren"}</button>
        </>
      ) : (
        <>
          <p style={{ color: THEME.colors.danger, fontWeight: 700, fontSize: THEME.fontSize.base, margin: "0 0 12px" }}>⊘ Zugang gesperrt</p>
          <button onClick={entsperren} disabled={laedt} style={{ ...s.btn, width: "auto" }}>{laedt ? "…" : "Zugang entsperren"}</button>
        </>
      )}
    </div>
  );
}

// WLAN-Check-in-Geraeteverwaltung (Phase 6 der WLAN-Erweiterung, siehe routers_wifi.py) - bewusst
// klein/unauffaellig gehalten (Nutzer-Vorgabe: "nicht unnoetig prominent darstellen"), direkt
// unter der Mitglieder-App-Zugangsbox, da beides "Selbstbedienungs-Zugaenge" desselben Mitglieds sind.
function WlanGeraeteBereich({ mitgliedId }) {
  const [geraete, setGeraete] = useState(null);
  const [laedt, setLaedt] = useState(false);

  const laden = () => fetch(`${API}/api/wifi/admin/mitglied/${mitgliedId}/geraete`).then(r => r.ok ? r.json() : []).then(setGeraete).catch(() => setGeraete([]));
  useEffect(() => { laden(); }, [mitgliedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const entfernen = async (geraetId) => {
    if (!window.confirm("Dieses Gerät wirklich entfernen? Es kann danach keinen automatischen Check-in mehr auslösen.")) return;
    setLaedt(true);
    const res = await fetch(`${API}/api/wifi/admin/mitglied/${mitgliedId}/geraete/${geraetId}`, { method: "DELETE" });
    setLaedt(false);
    if (!res.ok) { alert("Entfernen fehlgeschlagen."); return; }
    laden();
  };

  const widerrufen = async () => {
    if (!window.confirm("Einwilligung widerrufen und alle Geräte deaktivieren?")) return;
    setLaedt(true);
    const res = await fetch(`${API}/api/wifi/admin/mitglied/${mitgliedId}/widerrufen`, { method: "POST" });
    setLaedt(false);
    if (!res.ok) { alert("Widerrufen fehlgeschlagen."); return; }
    laden();
  };

  if (!geraete || geraete.length === 0) return null; // kein Geraet registriert - keine leere Box anzeigen

  return (
    <div style={{ ...s.karte, marginBottom: 16 }}>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.md }}>WLAN-Geräte</h3>
      {geraete.map(g => (
        <div key={g.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: `1px solid ${THEME.colors.border}` }}>
          <div>
            <p style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.sm }}>
              {g.device_name}{!g.active && <span style={{ color: THEME.colors.textMuted }}> (deaktiviert)</span>}{g.pending && <span style={{ color: THEME.colors.warning }}> (wartet auf Erkennung)</span>}
            </p>
            <p style={{ color: THEME.colors.textMuted, margin: "2px 0 0", fontSize: THEME.fontSize.xs }}>
              {g.last_seen_at ? `zuletzt erkannt: ${new Date(g.last_seen_at).toLocaleDateString("de-DE")}` : "noch nie erkannt"}
            </p>
          </div>
          <button onClick={() => entfernen(g.id)} disabled={laedt} style={{ ...s.btnGray, width: "auto", marginBottom: 0, fontSize: THEME.fontSize.xs, padding: "6px 10px" }}>Gerät entfernen</button>
        </div>
      ))}
      <button onClick={widerrufen} disabled={laedt} style={{ ...s.btnGray, marginTop: 12, fontSize: THEME.fontSize.xs, color: THEME.colors.danger }}>Einwilligung komplett widerrufen</button>
    </div>
  );
}

function MitgliedDetail({ mitglied, onZurueck, onAktualisiert, benutzer, rechte, onVorfallOeffnen }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [form, setForm] = useState({ ...mitglied });
  const [ibanFehler, setIbanFehler] = useState("");
  const [ibanGueltig, setIbanGueltig] = useState(true);
  const [pauseDialog, setPauseDialog] = useState(false);
  const [pauseMonate, setPauseMonate] = useState(1);
  const [pauseStartOffset, setPauseStartOffset] = useState(0);
  // Gratismonat (Werbepraemie, z.B. 6-Monats-Vertrag -> 1 Monat gratis) - bewusst eigener,
  // schlankerer Dialog ohne Status-/Reaktivierungs-Logik (siehe routers_gratismonate.py): anders
  // als bei Pause bleibt das Mitglied die ganze Zeit "aktiv" mit normalem Tuerzutritt.
  const [gratismonatDialog, setGratismonatDialog] = useState(false);
  const [gratismonatMonate, setGratismonatMonate] = useState(1);
  const [gratismonatStartOffset, setGratismonatStartOffset] = useState(0);
  const [gratismonate, setGratismonate] = useState([]);
  // Eigener, schlankerer Dialog fuer bereits pausierte Mitglieder (nur neues Enddatum, kein
  // "Pause ab"-Monat) - der bestehende Pausieren-Dialog ist fuer Mitglieder mit Status "aktiv"
  // gebaut (Start = naechster Monat) und passt nicht fuer "laufende Pause verlaengern".
  const [pauseVerlaengernDialog, setPauseVerlaengernDialog] = useState(false);
  const [pauseVerlaengernBis, setPauseVerlaengernBis] = useState("");
  const [pauseVerlaengernFehler, setPauseVerlaengernFehler] = useState("");
  const [kuendDialog, setKuendDialog] = useState(false);
  const [loeschenDialog, setLoeschenDialog] = useState(false);
  const [loeschenBlockiert, setLoeschenBlockiert] = useState("");
  const [umwandelnOffen, setUmwandelnOffen] = useState(false);
  const [umwandelnTyp, setUmwandelnTyp] = useState("wellpass");
  const [notizen, setNotizen] = useState([]);
  const [neueNotiz, setNeueNotiz] = useState("");
  const [notizenOffen, setNotizenOffen] = useState(false);
  const [aktionenOffen, setAktionenOffen] = useState(false);
  const [einmalzahlungenOffen, setEinmalzahlungenOffen] = useState(false);
  const [partnerOffen, setPartnerOffen] = useState(false);
  const [dokumenteOffen, setDokumenteOffen] = useState(false);
  const [zahlungsverlaufOffen, setZahlungsverlaufOffen] = useState(false);
  const [wellpassOffen, setWellpassOffen] = useState(false);
  const [wellpassAnzahl, setWellpassAnzahl] = useState(0);
  const [rfidOffen, setRfidOffen] = useState(false);
  const [pausen, setPausen] = useState([]);
  const [zahlungen, setZahlungen] = useState([]);
  const heute = new Date();

  const pauseStartDate = new Date(heute.getFullYear(), heute.getMonth() + 1 + pauseStartOffset, 1);
  const pauseVon = `01.${String(pauseStartDate.getMonth()+1).padStart(2,"0")}.${pauseStartDate.getFullYear()}`;
  const pauseEndeDate = new Date(pauseStartDate.getFullYear(), pauseStartDate.getMonth() + pauseMonate, 0);
  const pauseEnde = `${String(pauseEndeDate.getDate()).padStart(2,"0")}.${String(pauseEndeDate.getMonth()+1).padStart(2,"0")}.${pauseEndeDate.getFullYear()}`;
  const gratismonatStartDate = new Date(heute.getFullYear(), heute.getMonth() + 1 + gratismonatStartOffset, 1);
  const gratismonatVon = `01.${String(gratismonatStartDate.getMonth()+1).padStart(2,"0")}.${gratismonatStartDate.getFullYear()}`;
  const gratismonatEndeDate = new Date(gratismonatStartDate.getFullYear(), gratismonatStartDate.getMonth() + gratismonatMonate, 0);
  const gratismonatEnde = `${String(gratismonatEndeDate.getDate()).padStart(2,"0")}.${String(gratismonatEndeDate.getMonth()+1).padStart(2,"0")}.${gratismonatEndeDate.getFullYear()}`;
  const kuendHeuteDE = `${String(heute.getDate()).padStart(2,"0")}.${String(heute.getMonth()+1).padStart(2,"0")}.${heute.getFullYear()}`;
  const kuendStandardBis = letzterTagMonat(heute.getFullYear(), heute.getMonth() + 2);
  const [kuendBisDate, setKuendBisDate] = useState(datumZuLokalerISO(kuendStandardBis));
  const [kuendFehler, setKuendFehler] = useState("");
  const heuteISO = datumZuLokalerISO(heute);
  const [reaktivierDialog, setReaktivierDialog] = useState(false);
  const [reaktivierAbDate, setReaktivierAbDate] = useState(heuteISO);
  const [reaktivierFehler, setReaktivierFehler] = useState("");
  const [reaktivierPauseDialog, setReaktivierPauseDialog] = useState(false);
  const [reaktivierPauseAbDate, setReaktivierPauseAbDate] = useState(heuteISO);
  const [reaktivierPauseFehler, setReaktivierPauseFehler] = useState("");
  // Schuetzt Kuendigen/Pausieren/Status-aendern vor Doppelklick UND davor, dass ein
  // fehlgeschlagener PUT (z.B. 422) unbemerkt durchrutscht - siehe handleKuendigungSpeichern.
  const [statusAktionLaeuft, setStatusAktionLaeuft] = useState(false);

  useEffect(() => {
    fetch(`${API}/pausen/mitglied/${mitglied.id}`).then(r => r.json()).then(setPausen);
    fetch(`${API}/gratismonate/mitglied/${mitglied.id}`).then(r => r.json()).then(setGratismonate);
    fetch(`${API}/sepa/mitglied/${mitglied.id}/zahlungen`).then(r => r.json()).then(setZahlungen);
    fetch(`${API}/wellpass/mitglied/${mitglied.id}`).then(r => r.json()).then(d => setWellpassAnzahl(d.anzahl_gesamt || 0)).catch(() => {});
  }, [mitglied.id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Liest eine Fehlermeldung aus einer nicht-ok Response - z.B. um bei einem fehlgeschlagenen
  // PUT (422 durch ein fremdes Pflichtfeld) sichtbar zu machen, WAS schiefging, statt es wie
  // frueher stillschweigend zu verschlucken (siehe handleKuendigungSpeichern).
  const fehlertextAusAntwortM = async (res) => {
    try {
      const err = await res.json();
      return err?.detail ? JSON.stringify(err.detail) : `${res.status} ${res.statusText}`;
    } catch {
      return `${res.status} ${res.statusText}`;
    }
  };

  const handleIBAN = (v) => {
    set("iban", v);
    if (v.length > 4) { const ok = validiereIBAN(v); setIbanGueltig(ok); setIbanFehler(ok ? "" : "IBAN ist ungültig"); }
    else { setIbanFehler(""); setIbanGueltig(false); }
  };

  // Baut den Detailtext fuers Aktivitaetsprotokoll ("Nachname: Röschle → Rösch, E-Mail geändert").
  // Werte nur bei unkritischen Feldern (Name, Tarif, Preis, Vertragsbeginn) - bei Kontakt-/
  // Bankdaten nur der Feldname, damit keine personenbezogenen Daten im Protokoll stehen bleiben.
  const aenderungenAlsText = (alt, neu) => {
    const norm = v => String(v ?? "").trim();
    const mitWert = [["vorname", "Vorname"], ["nachname", "Nachname"], ["tarif", "Tarif"], ["preis", "Preis"], ["vertragsbeginn", "Vertragsbeginn", datumDE], ["erstlaufzeit", "Erstlaufzeit"]];
    const nurName = [["geburtsdatum", "Geburtsdatum"], ["email", "E-Mail"], ["telefon", "Telefon"], ["strasse", "Straße"], ["plz", "PLZ"], ["ort", "Ort"], ["iban", "IBAN"], ["kontoinhaber", "Kontoinhaber"], ["mandats_id", "Mandats-ID"]];
    const teile = [];
    for (const [k, label, fmt] of mitWert) {
      if (norm(alt[k]) !== norm(neu[k])) teile.push(`${label}: ${(fmt ? fmt(norm(alt[k])) : norm(alt[k])) || "–"} → ${(fmt ? fmt(norm(neu[k])) : norm(neu[k])) || "–"}`);
    }
    for (const [k, label] of nurName) {
      if (norm(alt[k]) !== norm(neu[k])) teile.push(`${label} geändert`);
    }
    return teile.length ? teile.join(", ") : null;
  };

  const handleSpeichern = async () => {
    const res = await fetch(`${API}/mitglieder/${mitglied.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    if (res.ok) { await logAktivitaet("Mitglied bearbeitet", aenderungenAlsText(mitglied, form), mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer); setBearbeiten(false); onAktualisiert(); }
    else alert("Fehler beim Speichern!");
  };

  const ladeNotizen = () => fetch(`${API}/mitglieder/${mitglied.id}/notizen`).then(r => r.json()).then(setNotizen).catch(() => {});
  useEffect(() => { ladeNotizen(); }, [mitglied.id]);

  const handleNotizHinzufuegen = async () => {
    if (!neueNotiz.trim()) return;
    const res = await fetch(`${API}/mitglieder/${mitglied.id}/notizen`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: neueNotiz }) });
    if (res.ok) { setNeueNotiz(""); ladeNotizen(); }
    else alert("Fehler beim Speichern!");
  };

  const handleNotizLoeschen = async (id) => {
    if (!window.confirm("Diese Notiz löschen?")) return;
    const res = await fetch(`${API}/mitglieder/notizen/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladeNotizen();
  };

  const handleStatusAendern = async (neuerStatus) => {
    if (statusAktionLaeuft) return;
    setStatusAktionLaeuft(true);
    try {
      const vorherigerStatus = mitglied.status;
      const patchRes = await fetch(`${API}/mitglieder/${mitglied.id}/status?status=${neuerStatus}`, { method: "PATCH" });
      if (!patchRes.ok) { alert("Fehler beim Ändern des Status: " + await fehlertextAusAntwortM(patchRes)); return; }
      if (neuerStatus === "aktiv" && (vorherigerStatus === "gekündigt" || vorherigerStatus === "ehemalig")) {
        // Kündigung zurückgenommen: Kündigungsdatum zurücksetzen und die automatisch
        // angelegte "Gekündigt: ..."-Notiz wieder entfernen, damit keine veraltete
        // Kündigungs-Notiz an einem wieder aktiven Mitglied hängen bleibt.
        const putRes = await fetch(`${API}/mitglieder/${mitglied.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...mitglied, status: "aktiv", kuendigung_zum: null }) });
        if (!putRes.ok) { alert("Status wurde geändert, aber das Kündigungsdatum konnte nicht zurückgesetzt werden: " + await fehlertextAusAntwortM(putRes)); }
        for (const n of notizen) {
          if (n.text && n.text.startsWith("Gekündigt:")) {
            await fetch(`${API}/mitglieder/notizen/${n.id}`, { method: "DELETE" });
          }
        }
        ladeNotizen();
      }
      await logAktivitaet(`Status geändert zu: ${neuerStatus}`, null, mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
      onAktualisiert();
    } finally {
      setStatusAktionLaeuft(false);
    }
  };

  const handleReaktivierenSpeichern = async () => {
    if (statusAktionLaeuft) return;
    if (!reaktivierAbDate) { setReaktivierFehler("Bitte ein Datum auswählen, ab wann die Mitgliedschaft wieder aktiv ist."); return; }
    setReaktivierFehler("");
    setStatusAktionLaeuft(true);
    try {
      const abZukunft = reaktivierAbDate > heuteISO;
      const patchRes = await fetch(`${API}/mitglieder/${mitglied.id}/status?status=aktiv`, { method: "PATCH" });
      if (!patchRes.ok) { setReaktivierFehler("Fehler beim Ändern des Status: " + await fehlertextAusAntwortM(patchRes)); return; }
      // Kuendigungsdatum wird immer zurueckgesetzt. vertragsbeginn wird NUR angepasst, wenn die
      // Reaktivierung erst in der Zukunft beginnen soll - sonst wuerde ein "heute"-Wert das
      // urspruengliche (ggf. Jahre alte) Beitrittsdatum ueberschreiben, obwohl fuer die
      // Abrechnung ab heute ohnehin nichts Besonderes zu beachten ist. Ein zukuenftiges
      // vertragsbeginn sorgt in aktive_mitglieder_fuer_datum()/_ist_beitrittsmonat()
      // (routers_sepa.py) automatisch dafuer, dass vorher kein Beitrag abgebucht wird und der
      // erste Einzug danach korrekt als Beitrittsmonat behandelt wird.
      const body = { ...mitglied, status: "aktiv", kuendigung_zum: null };
      if (abZukunft) {
        body.vertragsbeginn = reaktivierAbDate;
        // Bei einem Reaktivierungsdatum mitten im Monat (z.B. 15.09.) faellt der eigentliche
        // Beitrittsmonat-Lauf (immer zum 1.) schon vorbei, bevor das Mitglied ueberhaupt
        // faellig wird - ohne diesen anteiligen Betrag wuerde _erstbeitrag_nachzahlung()
        // (routers_sepa.py) nichts nachbelasten und der Rest-Monat ginge schlicht verloren.
        // Gleiche Berechnung wie beim Neuanlegen eines Mitglieds (siehe berechneAnteilig()).
        body.anteiliger_erstbeitrag = berechneAnteilig(mitglied.preis, reaktivierAbDate);
      }
      const putRes = await fetch(`${API}/mitglieder/${mitglied.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!putRes.ok) { setReaktivierFehler("Status wurde geändert, aber nicht alle Felder konnten gespeichert werden: " + await fehlertextAusAntwortM(putRes)); return; }
      for (const n of notizen) {
        if (n.text && n.text.startsWith("Gekündigt:")) {
          await fetch(`${API}/mitglieder/notizen/${n.id}`, { method: "DELETE" });
        }
      }
      ladeNotizen();
      await logAktivitaet(abZukunft ? `Wieder aktiviert ab ${datumDE(reaktivierAbDate)}` : "Wieder aktiviert", null, mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
      setReaktivierDialog(false);
      onAktualisiert();
    } finally {
      setStatusAktionLaeuft(false);
    }
  };

  const handleReaktivierenPauseSpeichern = async () => {
    if (statusAktionLaeuft) return;
    if (!reaktivierPauseAbDate) { setReaktivierPauseFehler("Bitte ein Datum auswählen, ab wann die Mitgliedschaft wieder aktiv ist."); return; }
    setReaktivierPauseFehler("");
    setStatusAktionLaeuft(true);
    try {
      const nachbelastung = berechnePauseNachbelastung(mitglied.preis, reaktivierPauseAbDate);
      const patchRes = await fetch(`${API}/mitglieder/${mitglied.id}/status?status=aktiv`, { method: "PATCH" });
      if (!patchRes.ok) { setReaktivierPauseFehler("Fehler beim Ändern des Status: " + await fehlertextAusAntwortM(patchRes)); return; }
      // pause_bis wird auf den Tag VOR dem Reaktivierungsdatum gesetzt (nicht geloescht) - sonst
      // wuerde aktive_mitglieder_fuer_datum() (routers_sepa.py) das Mitglied bei einem noch in
      // der Zukunft liegenden alten pause_bis faelschlich weiter von JEDEM SEPA-Lauf ausschliessen,
      // obwohl der Status hier schon wieder auf "aktiv" steht (das war der eigentliche Bug: der
      // alte "Reaktivieren"-Button hat pause_bis nie angefasst).
      const tagDavor = new Date(reaktivierPauseAbDate);
      tagDavor.setDate(tagDavor.getDate() - 1);
      const putRes = await fetch(`${API}/mitglieder/${mitglied.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...mitglied, status: "aktiv", pause_bis: tagDavor.toISOString().split("T")[0] }) });
      if (!putRes.ok) { setReaktivierPauseFehler("Status wurde geändert, aber das Pausenende konnte nicht angepasst werden: " + await fehlertextAusAntwortM(putRes)); return; }
      // Bereits verstrichene Monate (deren regulaerer 1.-des-Monats-Lauf schon vorbei ist)
      // koennen nicht mehr regulaer erfasst werden - werden stattdessen als Einmalzahlung
      // nachbelastet, damit sie beim naechsten Lauf automatisch mit abgerechnet werden.
      for (const pos of nachbelastung) {
        if (pos.betrag > 0) {
          await fetch(`${API}/einmalzahlungen/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({
            mitglied_id: mitglied.id, beschreibung: `Nachbelastung ${pos.monatLabel} (Reaktivierung nach Pause)`, betrag: pos.betrag,
          }) });
        }
      }
      await logAktivitaet(`Wieder aktiviert nach Pause ab ${datumDE(reaktivierPauseAbDate)}`, nachbelastung.length ? `+ Nachbelastung: ${nachbelastung.map(p => `${p.monatLabel} ${p.betrag.toFixed(2)} €`).join(", ")}` : null, mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
      setReaktivierPauseDialog(false);
      onAktualisiert();
    } finally {
      setStatusAktionLaeuft(false);
    }
  };

  const handlePauseSpeichern = async () => {
    if (statusAktionLaeuft) return;
    setStatusAktionLaeuft(true);
    try {
      const startISO = `${pauseStartDate.getFullYear()}-${String(pauseStartDate.getMonth()+1).padStart(2,"0")}-01`;
      const endeISO = `${pauseEndeDate.getFullYear()}-${String(pauseEndeDate.getMonth()+1).padStart(2,"0")}-${String(pauseEndeDate.getDate()).padStart(2,"0")}`;
      const pauseRes = await fetch(`${API}/pausen/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mitglied_id: mitglied.id, start_datum: startISO, end_datum: endeISO, monate: pauseMonate }) });
      if (!pauseRes.ok) {
        // Kritisch: ohne diesen Datensatz findet der Cron zur automatischen Reaktivierung
        // nach Pausenende nichts - deshalb hart abbrechen statt (wie frueher) trotzdem mit dem
        // Statuswechsel weiterzumachen (siehe Audit-Fund #1).
        alert("Die Pause konnte nicht angelegt werden: " + await fehlertextAusAntwortM(pauseRes));
        return;
      }
      // status nur SOFORT auf "pausiert" setzen, wenn die Pause schon begonnen hat - liegt
      // start_datum in der Zukunft, bleibt das Mitglied bis dahin aktiv (genau wie im Backend
      // bei pause_anlegen()), der Cron aktiviere_faellige_pausen() holt den Statuswechsel am
      // Start-Tag automatisch nach. Vorher wurde hier IMMER sofort "pausiert" gesetzt, auch bei
      // einer erst kommenden Pause (gefunden 2026-09-03, Nelly Vollmer: Pause ab 01.10., aber
      // Status schon am 03.09. auf "pausiert" - haette sogar die naechste, eigentlich noch
      // faellige Abbuchung faelschlich ausgeschlossen).
      const putBody = startISO <= isoDatum(new Date()) ? { ...mitglied, status: "pausiert", pause_bis: endeISO } : { ...mitglied, pause_bis: endeISO };
      const putRes = await fetch(`${API}/mitglieder/${mitglied.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(putBody) });
      if (!putRes.ok) { alert("Die Pause wurde angelegt, aber das Pause-Enddatum konnte NICHT am Mitglied gespeichert werden: " + await fehlertextAusAntwortM(putRes) + "\n\nBitte Fehler beheben und erneut versuchen, sonst taucht das Mitglied in keiner Statistik als pausiert auf."); return; }
      await logAktivitaet("Pause angelegt", `${pauseVon} – ${pauseEnde}`, mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
      setPauseDialog(false); onAktualisiert();
    } finally {
      setStatusAktionLaeuft(false);
    }
  };

  const handlePauseVerlaengern = async () => {
    if (statusAktionLaeuft) return;
    if (!pauseVerlaengernBis) { setPauseVerlaengernFehler("Bitte ein Datum auswählen."); return; }
    const bisherigesEnde = mitglied.pause_bis || new Date().toISOString().split("T")[0];
    if (pauseVerlaengernBis <= bisherigesEnde) { setPauseVerlaengernFehler("Das neue Enddatum muss nach dem bisherigen Pausenende liegen."); return; }
    setPauseVerlaengernFehler("");
    setStatusAktionLaeuft(true);
    try {
      // Nahtlos an die laufende Pause anschliessen (Starttag = Tag nach bisherigem Ende), damit
      // keine Luecke in der Pausenhistorie entsteht und weiterhin kein Beitragseinzug stattfindet.
      const startDatum = new Date(bisherigesEnde);
      startDatum.setDate(startDatum.getDate() + 1);
      const startISO = startDatum.toISOString().split("T")[0];
      // Grobe Monatsangabe nur fuers Pausen-Protokoll/Statistik (siehe routers_pausen.py) -
      // keine andere Logik haengt daran.
      const monate = Math.max(1, Math.round((new Date(pauseVerlaengernBis) - startDatum) / (1000 * 60 * 60 * 24 * 30)));
      const pauseRes = await fetch(`${API}/pausen/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mitglied_id: mitglied.id, start_datum: startISO, end_datum: pauseVerlaengernBis, monate }) });
      if (!pauseRes.ok) { setPauseVerlaengernFehler("Die Verlängerung konnte nicht gespeichert werden: " + await fehlertextAusAntwortM(pauseRes)); return; }
      // Gleiche Absicherung wie in handlePauseSpeichern - status nur setzen, wenn der neue
      // Pausenabschnitt schon begonnen hat (in der Praxis fast immer der Fall bei einer
      // Verlaengerung, aber sicherheitshalber genauso geprueft).
      const verlaengernBody = startISO <= isoDatum(new Date()) ? { ...mitglied, status: "pausiert", pause_bis: pauseVerlaengernBis } : { ...mitglied, pause_bis: pauseVerlaengernBis };
      const putRes = await fetch(`${API}/mitglieder/${mitglied.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(verlaengernBody) });
      if (!putRes.ok) { setPauseVerlaengernFehler("Verlängerung angelegt, aber das neue Enddatum konnte NICHT am Mitglied gespeichert werden: " + await fehlertextAusAntwortM(putRes)); return; }
      await logAktivitaet("Pause verlängert", `bis ${datumDE(pauseVerlaengernBis)}`, mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
      setPauseVerlaengernDialog(false); onAktualisiert();
    } finally {
      setStatusAktionLaeuft(false);
    }
  };

  const handlePauseLoeschen = async (pause) => {
    if (!window.confirm(`Pause ${datumDE(pause.start_datum)} – ${datumDE(pause.end_datum)} wirklich löschen?`)) return;
    if (statusAktionLaeuft) return;
    setStatusAktionLaeuft(true);
    try {
      const res = await fetch(`${API}/pausen/${pause.id}`, { method: "DELETE" });
      if (!res.ok) { alert("Die Pause konnte nicht gelöscht werden: " + await fehlertextAusAntwortM(res)); return; }
      await logAktivitaet("Pause gelöscht", `${datumDE(pause.start_datum)} – ${datumDE(pause.end_datum)}`, mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
      onAktualisiert();
    } finally {
      setStatusAktionLaeuft(false);
    }
  };

  const handleGratismonatSpeichern = async () => {
    if (statusAktionLaeuft) return;
    setStatusAktionLaeuft(true);
    try {
      const startISO = `${gratismonatStartDate.getFullYear()}-${String(gratismonatStartDate.getMonth()+1).padStart(2,"0")}-01`;
      const endeISO = `${gratismonatEndeDate.getFullYear()}-${String(gratismonatEndeDate.getMonth()+1).padStart(2,"0")}-${String(gratismonatEndeDate.getDate()).padStart(2,"0")}`;
      const res = await fetch(`${API}/gratismonate/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mitglied_id: mitglied.id, start_datum: startISO, end_datum: endeISO, monate: gratismonatMonate }) });
      if (!res.ok) { alert("Der Gratismonat konnte nicht angelegt werden: " + await fehlertextAusAntwortM(res)); return; }
      await logAktivitaet("Gratismonat gewährt", `${gratismonatVon} – ${gratismonatEnde}`, mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
      setGratismonatDialog(false); onAktualisiert();
    } finally {
      setStatusAktionLaeuft(false);
    }
  };

  const handleGratismonatLoeschen = async (eintrag) => {
    if (!window.confirm(`Gratismonat ${datumDE(eintrag.start_datum)} – ${datumDE(eintrag.end_datum)} wirklich löschen?`)) return;
    if (statusAktionLaeuft) return;
    setStatusAktionLaeuft(true);
    try {
      const res = await fetch(`${API}/gratismonate/${eintrag.id}`, { method: "DELETE" });
      if (!res.ok) { alert("Der Gratismonat konnte nicht gelöscht werden: " + await fehlertextAusAntwortM(res)); return; }
      await logAktivitaet("Gratismonat gelöscht", `${datumDE(eintrag.start_datum)} – ${datumDE(eintrag.end_datum)}`, mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
      onAktualisiert();
    } finally {
      setStatusAktionLaeuft(false);
    }
  };

  const handleKuendigungSpeichern = async () => {
    if (statusAktionLaeuft) return;
    if (!kuendBisDate) { setKuendFehler("Bitte ein Datum auswählen, bis wann die Mitgliedschaft läuft."); return; }
    setKuendFehler("");
    setStatusAktionLaeuft(true);
    try {
      const kuendBisDE = datumDE(kuendBisDate);
      const patchRes = await fetch(`${API}/mitglieder/${mitglied.id}/status?status=gekündigt`, { method: "PATCH" });
      if (!patchRes.ok) { setKuendFehler("Fehler beim Ändern des Status: " + await fehlertextAusAntwortM(patchRes)); return; }
      const putRes = await fetch(`${API}/mitglieder/${mitglied.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...mitglied, status: "gekündigt", kuendigung_zum: kuendBisDate }) });
      if (!putRes.ok) {
        // Kritisch: Status steht bereits auf "gekündigt" (siehe PATCH oben), aber ohne dieses
        // PUT fehlt das Kündigungsdatum in der DB - das Mitglied waere dann in KEINER
        // Kündigungs-Statistik mehr sichtbar, obwohl es korrekt als gekündigt gilt. Deshalb hier
        // hart abbrechen statt (wie frueher) stillschweigend weiterzumachen.
        setKuendFehler("Status wurde auf „gekündigt“ gesetzt, aber das Kündigungsdatum konnte NICHT gespeichert werden: " + await fehlertextAusAntwortM(putRes) + " – bitte Daten prüfen und erneut versuchen.");
        return;
      }
      await fetch(`${API}/mitglieder/${mitglied.id}/notizen`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: `Gekündigt: ${kuendHeuteDE}, läuft bis: ${kuendBisDE}` }) });
      await logAktivitaet("Kündigung eingetragen", `Läuft bis: ${kuendBisDE}`, mitglied.id, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
      const emailAdresse = mitglied.email?.includes("@comefit.intern") ? "" : mitglied.email || "";
      const betreff = encodeURIComponent(`Bestätigung deiner Kündigung bei ComeFit`);
      const body = encodeURIComponent(`Liebe/r ${mitglied.vorname} ${mitglied.nachname},\n\nhiermit bestätigen wir den Eingang deiner Kündigung.\n\nDeine Mitgliedschaft endet zum: ${kuendBisDE}\n\nEs tut uns leid, dass du uns verlässt. Wir wünschen dir alles Gute für die Zukunft und hoffen, dich irgendwann wieder bei uns begrüßen zu dürfen.\n\nMit freundlichen Grüßen\nDein ComeFit Team\n\nComeFit Gesundheit und Fitnessstudio\nHauptstraße 50, 72488 Sigmaringen-Laiz\nTel: 07571-68 71 959\ninfo@comefit.de`);
      // Dialog SOFORT schliessen, bevor der Mail-Client aufgerufen wird - siehe Kommentar unten.
      setKuendDialog(false); onAktualisiert();
      if (emailAdresse) {
        // Kein window.confirm() mehr davor (war eine zusaetzliche, mit dem Hinweistext im Dialog
        // ("Nach Bestaetigung wird eine Kuendigungsbestaetigung per E-Mail geoeffnet.") ohnehin
        // doppelte Rueckfrage) und window.location.href statt window.open(..., "_blank") - Nutzer-
        // Meldung 2026-08-29 (mobil): nach "Speichern" blieb der Dialog auf "Speichert..." haengen,
        // die Rueckfrage kam erst nach Schliessen+Neuoeffnen der App. Grund: window.confirm() UND
        // window.open() folgen hier auf mehrere await fetch()-Aufrufe - auf Mobilgeraeten gilt der
        // urspruengliche Button-Tap dann nicht mehr als "frisch genug", um zuverlaessig einen Dialog/
        // Popup ausloesen zu duerfen, das haengt dann bis zum naechsten Vordergrundwechsel der App.
        // location.href fuer ein mailto: ist eine normale Navigation (kein Popup) und wird davon
        // nicht ausgebremst.
        window.location.href = `mailto:${emailAdresse}?subject=${betreff}&body=${body}`;
      }
    } finally {
      setStatusAktionLaeuft(false);
    }
  };

  const handleLoeschen = async () => {
    const res = await fetch(`${API}/mitglieder/${mitglied.id}`, { method: "DELETE" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setLoeschenBlockiert(data.detail || "Löschen fehlgeschlagen.");
      return;
    }
    await logAktivitaet("Mitglied anonymisiert", null, null, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
    setLoeschenDialog(false); onZurueck();
  };

  const handleLoeschenErzwingen = async () => {
    if (!window.confirm(`Wirklich ALLES zu ${mitglied.vorname} ${mitglied.nachname} unwiderruflich löschen – inklusive Dokumenten und Zahlungshistorie? Dies kann die gesetzliche Aufbewahrungspflicht verletzen und lässt sich nicht rückgängig machen.`)) return;
    const res = await fetch(`${API}/mitglieder/${mitglied.id}/erzwingen`, { method: "DELETE" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      alert(data.detail || "Löschen fehlgeschlagen.");
      return;
    }
    await logAktivitaet("Mitglied inkl. aller Daten unwiderruflich gelöscht", null, null, `${mitglied.nachname}, ${mitglied.vorname}`, benutzer);
    setLoeschenDialog(false); setLoeschenBlockiert(""); onZurueck();
  };

  const handleRuecklastschrift = async (zahlungId) => {
    if (!window.confirm("Als Rücklastschrift markieren?")) return;
    const res = await fetch(`${API}/sepa/zahlung/${zahlungId}/ruecklastschrift`, { method: "PATCH" });
    if (!res.ok) { alert("Konnte nicht als Rücklastschrift markiert werden."); return; }
    fetch(`${API}/sepa/mitglied/${mitglied.id}/zahlungen`).then(r => r.json()).then(setZahlungen);
  };

  const handleBezahlt = async (zahlungId) => {
    const res = await fetch(`${API}/sepa/zahlung/${zahlungId}/bezahlt`, { method: "PATCH" });
    if (!res.ok) { alert("Konnte nicht als bezahlt markiert werden."); return; }
    fetch(`${API}/sepa/mitglied/${mitglied.id}/zahlungen`).then(r => r.json()).then(setZahlungen);
  };

  const emailAnzeige = mitglied.email?.includes("@comefit.intern") ? "–" : mitglied.email || "–";

  const aktionsButtonStyle = (farbe, offen) => ({
    width: "100%", textAlign: "left", padding: "12px 16px", borderRadius: 8, border: offen ? "2px solid #fff" : "2px solid transparent",
    background: farbe, color: THEME.colors.text, fontSize: THEME.fontSize.md, fontWeight: offen ? 700 : 600, cursor: "pointer",
    display: "flex", justifyContent: "space-between", alignItems: "center",
  });

  return (
    <div style={{ maxWidth: 1680, margin: "0 auto" }}>
      {pauseDialog && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(460px, 95vw)" }}>
            <h2 style={s.modalTitel}>Mitgliedschaft pausieren</h2>
            <p style={{ color: THEME.colors.textMuted, marginBottom: 20, fontSize: THEME.fontSize.md }}>{mitglied.vorname} {mitglied.nachname}</p>
            <div style={s.inputGroup}><label style={s.label}>Pause ab</label>
              <select style={s.input} value={pauseStartOffset} onChange={e => setPauseStartOffset(parseInt(e.target.value))}>
                {Array.from({length: 6}, (_, i) => { const d = new Date(heute.getFullYear(), heute.getMonth() + 1 + i, 1); return <option key={i} value={i}>01.{String(d.getMonth()+1).padStart(2,"0")}.{d.getFullYear()}</option>; })}
              </select>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Anzahl Monate</label>
              <select style={s.input} value={pauseMonate} onChange={e => setPauseMonate(parseInt(e.target.value))}>
                {[1,2,3,4,5,6].map(m => <option key={m} value={m}>{m} Monat{m > 1 ? "e" : ""}</option>)}
              </select>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Pause endet am</label><input style={{ ...s.input, background: THEME.colors.surfaceMuted, color: THEME.colors.success, fontWeight: 600 }} value={pauseEnde} readOnly /></div>
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 20, borderLeft: "4px solid #f59e0b" }}>
              <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.base, margin: 0 }}>⚠ Kein Beitragseinzug während der Pause.<br />Vertrag verlängert sich um {pauseMonate} Monat{pauseMonate > 1 ? "e" : ""}.</p>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setPauseDialog(false)} style={s.btnGray} disabled={statusAktionLaeuft}>Abbrechen</button>
              <button onClick={handlePauseSpeichern} style={{ ...s.btn, width: "auto", background: THEME.colors.warning, opacity: statusAktionLaeuft ? 0.6 : 1 }} disabled={statusAktionLaeuft}>{statusAktionLaeuft ? "Speichert…" : "Pause bestätigen"}</button>
            </div>
          </div>
        </div>
      )}

      {pauseVerlaengernDialog && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(460px, 95vw)" }}>
            <h2 style={s.modalTitel}>Pause verlängern</h2>
            <p style={{ color: THEME.colors.textMuted, marginBottom: 20, fontSize: THEME.fontSize.md }}>{mitglied.vorname} {mitglied.nachname}</p>
            <div style={s.inputGroup}><label style={s.label}>Bisheriges Pausenende</label><input style={{ ...s.input, background: THEME.colors.surfaceMuted, color: THEME.colors.textSecondary }} value={datumDE(mitglied.pause_bis)} readOnly /></div>
            <div style={s.inputGroup}>
              <label style={s.label}>Neues Pausenende</label>
              <input type="date" required style={{ ...s.input, borderColor: pauseVerlaengernFehler ? THEME.colors.danger : THEME.colors.primary }}
                value={pauseVerlaengernBis} onChange={e => { setPauseVerlaengernBis(e.target.value); if (pauseVerlaengernFehler) setPauseVerlaengernFehler(""); }} />
            </div>
            {pauseVerlaengernFehler && (
              <div style={{ background: "#ef444422", border: `1px solid ${THEME.colors.danger}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <p style={{ color: THEME.colors.danger, fontWeight: 600, margin: 0, fontSize: THEME.fontSize.sm }}>⚠ {pauseVerlaengernFehler}</p>
              </div>
            )}
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 20, borderLeft: "4px solid #f59e0b" }}>
              <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.base, margin: 0 }}>⚠ Weiterhin kein Beitragseinzug bis zum neuen Enddatum.</p>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setPauseVerlaengernDialog(false)} style={s.btnGray} disabled={statusAktionLaeuft}>Abbrechen</button>
              <button onClick={handlePauseVerlaengern} style={{ ...s.btn, width: "auto", background: THEME.colors.warning, opacity: statusAktionLaeuft ? 0.6 : 1 }} disabled={statusAktionLaeuft}>{statusAktionLaeuft ? "Speichert…" : "Verlängerung bestätigen"}</button>
            </div>
          </div>
        </div>
      )}

      {gratismonatDialog && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(460px, 95vw)" }}>
            <h2 style={s.modalTitel}>Gratismonat gewähren</h2>
            <p style={{ color: THEME.colors.textMuted, marginBottom: 20, fontSize: THEME.fontSize.md }}>{mitglied.vorname} {mitglied.nachname}</p>
            <div style={s.inputGroup}><label style={s.label}>Gratis ab</label>
              <select style={s.input} value={gratismonatStartOffset} onChange={e => setGratismonatStartOffset(parseInt(e.target.value))}>
                {Array.from({length: 6}, (_, i) => { const d = new Date(heute.getFullYear(), heute.getMonth() + 1 + i, 1); return <option key={i} value={i}>01.{String(d.getMonth()+1).padStart(2,"0")}.{d.getFullYear()}</option>; })}
              </select>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Anzahl Monate</label>
              <select style={s.input} value={gratismonatMonate} onChange={e => setGratismonatMonate(parseInt(e.target.value))}>
                {[1,2,3].map(m => <option key={m} value={m}>{m} Monat{m > 1 ? "e" : ""}</option>)}
              </select>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Gratismonat endet am</label><input style={{ ...s.input, background: THEME.colors.surfaceMuted, color: THEME.colors.success, fontWeight: 600 }} value={gratismonatEnde} readOnly /></div>
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 20, borderLeft: "4px solid #22c55e" }}>
              <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.base, margin: 0 }}>✓ Kein Beitragseinzug in diesem Zeitraum. Anders als bei Pause bleibt das Mitglied "aktiv" und behält ganz normal Türzutritt.</p>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setGratismonatDialog(false)} style={s.btnGray} disabled={statusAktionLaeuft}>Abbrechen</button>
              <button onClick={handleGratismonatSpeichern} style={{ ...s.btn, width: "auto", background: THEME.colors.success, opacity: statusAktionLaeuft ? 0.6 : 1 }} disabled={statusAktionLaeuft}>{statusAktionLaeuft ? "Speichert…" : "Gratismonat bestätigen"}</button>
            </div>
          </div>
        </div>
      )}

      {kuendDialog && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(460px, 95vw)" }}>
            <h2 style={s.modalTitel}>Mitgliedschaft kündigen</h2>
            <p style={{ color: THEME.colors.textMuted, marginBottom: 12, fontSize: THEME.fontSize.md }}>{mitglied.vorname} {mitglied.nachname}</p>
            {erstlaufzeitEnde(mitglied) && (() => {
              const ablauf = erstlaufzeitEnde(mitglied);
              if (ablauf > new Date()) return (
                <div style={{ background: "#f59e0b22", border: "1px solid #f59e0b", borderRadius: 8, padding: 14, marginBottom: 16 }}>
                  <p style={{ color: THEME.colors.warning, fontWeight: 700, margin: "0 0 4px" }}>⚠ Erstlaufzeit läuft noch!</p>
                  <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, margin: 0 }}>{erstlaufzeitText(mitglied.erstlaufzeit)} Laufzeit – frühester Kündigungstermin: <strong>{ablauf.toLocaleDateString("de-DE")}</strong></p>
                </div>
              );
              return null;
            })()}
            <div style={s.inputGroup}><label style={s.label}>Kündigungsdatum</label><input style={{ ...s.input, background: THEME.colors.surfaceMuted, color: THEME.colors.textSecondary }} value={kuendHeuteDE} readOnly /></div>
            <div style={s.inputGroup}>
              <label style={s.label}>Mitgliedschaft läuft bis</label>
              <input type="date" required style={{ ...s.input, borderColor: kuendFehler ? THEME.colors.danger : THEME.colors.primary }} value={kuendBisDate} onChange={e => { setKuendBisDate(e.target.value); if (kuendFehler) setKuendFehler(""); }} />
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Standard: letzter Tag des übernächsten Monats</p>
            </div>
            {kuendFehler && (
              <div style={{ background: "#ef444422", border: `1px solid ${THEME.colors.danger}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <p style={{ color: THEME.colors.danger, fontWeight: 600, margin: 0, fontSize: THEME.fontSize.sm }}>⚠ {kuendFehler}</p>
              </div>
            )}
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 20, borderLeft: "4px solid #ef4444" }}>
              <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: 0 }}>Nach Bestätigung wird eine Kündigungsbestätigung per E-Mail geöffnet.</p>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setKuendDialog(false); setKuendFehler(""); }} style={s.btnGray} disabled={statusAktionLaeuft}>Abbrechen</button>
              <button onClick={handleKuendigungSpeichern} style={{ ...s.btn, width: "auto", background: THEME.colors.danger, opacity: statusAktionLaeuft ? 0.6 : 1 }} disabled={statusAktionLaeuft}>{statusAktionLaeuft ? "Speichert…" : "Kündigung bestätigen"}</button>
            </div>
          </div>
        </div>
      )}

      {reaktivierDialog && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(460px, 95vw)" }}>
            <h2 style={s.modalTitel}>Mitgliedschaft wieder aktivieren</h2>
            <p style={{ color: THEME.colors.textMuted, marginBottom: 20, fontSize: THEME.fontSize.md }}>{mitglied.vorname} {mitglied.nachname}</p>
            <div style={s.inputGroup}>
              <label style={s.label}>Wieder aktiv ab</label>
              <input type="date" required style={{ ...s.input, borderColor: reaktivierFehler ? THEME.colors.danger : THEME.colors.primary }}
                value={reaktivierAbDate} onChange={e => { setReaktivierAbDate(e.target.value); if (reaktivierFehler) setReaktivierFehler(""); }} />
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Standard: heute</p>
            </div>
            {reaktivierFehler && (
              <div style={{ background: "#ef444422", border: `1px solid ${THEME.colors.danger}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <p style={{ color: THEME.colors.danger, fontWeight: 600, margin: 0, fontSize: THEME.fontSize.sm }}>⚠ {reaktivierFehler}</p>
              </div>
            )}
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 20, borderLeft: "4px solid #22c55e" }}>
              <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: 0 }}>
                {reaktivierAbDate > heuteISO
                  ? `Liegt in der Zukunft: Bis dahin erfolgt kein Beitragseinzug. Bei einem Start mitten im Monat wird automatisch nur der anteilige Rest-Monat berechnet (${berechneAnteilig(mitglied.preis, reaktivierAbDate).toFixed(2)} € für den verbleibenden ${new Date(reaktivierAbDate).toLocaleDateString("de-DE", { month: "long" })}) – zusammen mit dem vollen Folgemonat fällig, sobald der reguläre SEPA-Lauf danach läuft.`
                  : "Die Mitgliedschaft ist sofort wieder aktiv, der reguläre Beitragseinzug läuft wie gewohnt weiter."}
              </p>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setReaktivierDialog(false); setReaktivierFehler(""); }} style={s.btnGray} disabled={statusAktionLaeuft}>Abbrechen</button>
              <button onClick={handleReaktivierenSpeichern} style={{ ...s.btn, width: "auto", background: THEME.colors.success, opacity: statusAktionLaeuft ? 0.6 : 1 }} disabled={statusAktionLaeuft}>{statusAktionLaeuft ? "Speichert…" : "Reaktivierung bestätigen"}</button>
            </div>
          </div>
        </div>
      )}

      {reaktivierPauseDialog && (() => {
        const nachbelastung = berechnePauseNachbelastung(mitglied.preis, reaktivierPauseAbDate);
        const nachbelastungSumme = nachbelastung.reduce((s, p) => s + p.betrag, 0);
        return (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(460px, 95vw)" }}>
            <h2 style={s.modalTitel}>Pause beenden & reaktivieren</h2>
            <p style={{ color: THEME.colors.textMuted, marginBottom: 20, fontSize: THEME.fontSize.md }}>{mitglied.vorname} {mitglied.nachname}</p>
            <div style={s.inputGroup}>
              <label style={s.label}>Tatsächlich wieder aktiv ab</label>
              <input type="date" required style={{ ...s.input, borderColor: reaktivierPauseFehler ? THEME.colors.danger : THEME.colors.primary }}
                value={reaktivierPauseAbDate} onChange={e => { setReaktivierPauseAbDate(e.target.value); if (reaktivierPauseFehler) setReaktivierPauseFehler(""); }} />
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Auch ein vergangenes Datum möglich, z.B. wenn das Mitglied schon vor der offiziellen Statusänderung wieder trainiert hat.</p>
            </div>
            {reaktivierPauseFehler && (
              <div style={{ background: "#ef444422", border: `1px solid ${THEME.colors.danger}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <p style={{ color: THEME.colors.danger, fontWeight: 600, margin: 0, fontSize: THEME.fontSize.sm }}>⚠ {reaktivierPauseFehler}</p>
              </div>
            )}
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 20, borderLeft: `4px solid ${nachbelastung.length ? "#f59e0b" : "#22c55e"}` }}>
              {nachbelastung.length === 0 ? (
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: 0 }}>Kein regulärer Lauf verpasst – der nächste reguläre SEPA-Lauf rechnet ganz normal ab.</p>
              ) : (
                <>
                  <p style={{ color: THEME.colors.warning, fontWeight: 700, margin: "0 0 6px" }}>⚠ {nachbelastung.length} Monat{nachbelastung.length > 1 ? "e" : ""} bereits verpasst – wird als Einmalzahlung nachbelastet:</p>
                  {nachbelastung.map((p, i) => (
                    <p key={i} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 2px" }}>{p.monatLabel}: {p.betrag.toFixed(2)} €</p>
                  ))}
                  <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, margin: "6px 0 0", fontWeight: 600 }}>Gesamt {nachbelastungSumme.toFixed(2)} € zusätzlich zum nächsten regulären Beitrag.</p>
                </>
              )}
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setReaktivierPauseDialog(false); setReaktivierPauseFehler(""); }} style={s.btnGray} disabled={statusAktionLaeuft}>Abbrechen</button>
              <button onClick={handleReaktivierenPauseSpeichern} style={{ ...s.btn, width: "auto", background: THEME.colors.success, opacity: statusAktionLaeuft ? 0.6 : 1 }} disabled={statusAktionLaeuft}>{statusAktionLaeuft ? "Speichert…" : "Reaktivierung bestätigen"}</button>
            </div>
          </div>
        </div>
        );
      })()}

      {loeschenDialog && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(460px, 95vw)" }}>
            <h2 style={{ ...s.modalTitel, color: THEME.colors.danger }}>Mitglied löschen?</h2>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.lg, marginBottom: 20 }}>
              <strong style={{ color: THEME.colors.text }}>{mitglied.vorname} {mitglied.nachname}</strong> wird anonymisiert: Name, Adresse, IBAN und Kontaktdaten werden durch Platzhalter ersetzt und Notizen/Dokumente gelöscht.
              Rechnungen und Zahlungen bleiben für die gesetzliche 10-jährige Aufbewahrungsfrist unverändert erhalten. Dieser Schritt lässt sich nicht rückgängig machen.
            </p>
            {loeschenBlockiert && (
              <div style={{ background: "#2a1414", border: "1px solid #7f1d1d", borderRadius: 8, padding: 12, marginBottom: 20 }}>
                <p style={{ color: "#fca5a5", fontSize: THEME.fontSize.base, margin: "0 0 10px" }}>{loeschenBlockiert}</p>
                <p style={{ color: "#fca5a5", fontSize: THEME.fontSize.sm, margin: "0 0 10px" }}>⚠ Nur verwenden, wenn dieses Mitglied wirklich fehlerhaft angelegt wurde und es keine Rechnungen/Zahlungen gibt – sonst bitte stattdessen "Mitglied löschen" (Anonymisierung) nutzen.</p>
                <button onClick={handleLoeschenErzwingen} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg, fontSize: THEME.fontSize.sm }}>Trotzdem alles löschen (unwiderruflich)</button>
              </div>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setLoeschenDialog(false); setLoeschenBlockiert(""); }} style={s.btnGray}>Abbrechen</button>
              {!loeschenBlockiert && <button onClick={handleLoeschen} style={{ ...s.btn, width: "auto", background: THEME.colors.danger }}>Ja, anonymisieren</button>}
            </div>
          </div>
        </div>
      )}

      {umwandelnOffen && (
        <PersonFormular
          typ={umwandelnTyp} onTypWechsel={setUmwandelnTyp} benutzer={benutzer}
          initial={{
            anrede: mitglied.anrede, vorname: mitglied.vorname, nachname: mitglied.nachname,
            geburtsdatum: mitglied.geburtsdatum, telefon: mitglied.telefon,
            email: mitglied.email?.includes("@comefit.intern") ? "" : mitglied.email,
            strasse: mitglied.strasse, plz: mitglied.plz, ort: mitglied.ort,
          }}
          konvertierung={{ richtung: "von_mitglied", mitglied }}
          onSpeichern={() => { setUmwandelnOffen(false); onAktualisiert(); }}
          onAbbrechen={() => setUmwandelnOffen(false)}
        />
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <button onClick={onZurueck} style={{ ...s.btnGray, width: "auto" }}>← Zurück</button>
        <h1 style={{ ...s.heading, margin: 0, fontSize: THEME.fontSize.xxl }}>{mitglied.anrede} {mitglied.vorname} {mitglied.nachname}</h1>
        <span style={{ color: statusFarbe(mitglied.status), fontWeight: 700, fontSize: THEME.fontSize.md, background: THEME.colors.surface, padding: "5px 14px", borderRadius: 20 }}>{mitglied.status}</span>
        {mitglied.anonymisiert_am && (
          <span style={{ color: THEME.colors.textMuted, fontWeight: 700, fontSize: THEME.fontSize.md, background: THEME.colors.surface, padding: "5px 14px", borderRadius: 20 }}>
            🔒 Anonymisiert am {new Date(mitglied.anonymisiert_am).toLocaleDateString("de-DE")}
          </span>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        {[
          { label: "Monatlicher Beitrag", wert: `${parseFloat(mitglied.preis || 0).toFixed(2)} €`, farbe: THEME.colors.primary },
          { label: "Tarif", wert: mitglied.tarif, farbe: THEME.colors.text },
          { label: "Mitglied seit", wert: datumDE(mitglied.vertragsbeginn), farbe: THEME.colors.text },
          { label: "Mandats-ID", wert: mitglied.mandats_id || "–", farbe: THEME.colors.text },
          ...(mitglied.kuendigung_zum ? [{ label: "Gekündigt zum", wert: datumDE(mitglied.kuendigung_zum), farbe: THEME.colors.accentOrange }] : []),
        ].map(k => (
          <div key={k.label} style={s.karte}>
            <p style={{ ...s.karteLabel, fontSize: THEME.fontSize.sm }}>{k.label}</p>
            <p style={{ color: k.farbe, fontSize: THEME.fontSize.xl2, fontWeight: 700, margin: "6px 0 0" }}>{k.wert}</p>
          </div>
        ))}
      </div>

      <div className="mitglied-layout" style={{ marginBottom: 16 }}>
      <div className="mitglied-hauptspalte">
      <div style={{ ...s.karte, marginBottom: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>Mitgliedsdaten</h3>
          <div style={{ display: "flex", gap: 8 }}>
            {!bearbeiten && (rechte?.darf_mitglieder_bearbeiten || rechte?.rolle === "admin") && <button onClick={() => setBearbeiten(true)} style={{ ...s.btn, width: "auto" }}>Bearbeiten</button>}
            {bearbeiten && <>
              <button onClick={() => setBearbeiten(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
              <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
            </>}
          </div>
        </div>
        {!bearbeiten ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 36 }}>
            {[["Vorname", mitglied.vorname],["Nachname", mitglied.nachname],["Geburtsdatum", `${datumDE(mitglied.geburtsdatum)} (${berechneAlter(mitglied.geburtsdatum)} Jahre)`],["Telefon / Mobil", mitglied.telefon || "–"],["E-Mail", emailAnzeige],["Straße", mitglied.strasse || "–"],["PLZ", mitglied.plz || "–"],["Ort", mitglied.ort || "–"],["IBAN", mitglied.iban || "–"],["Kontoinhaber", mitglied.kontoinhaber || "–"],["Startpaket", `${parseFloat(mitglied.startpaket || 0).toFixed(2)} €`],["Erstlaufzeit", erstlaufzeitText(mitglied.erstlaufzeit)]].map(([label, wert]) => (
              <div key={label} style={{ minWidth: 0, background: THEME.colors.background, borderRadius: THEME.radius.md, padding: "10px 14px" }}>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>{label}</p>
                <p title={wert} style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, margin: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}>{wert}</p>
              </div>
            ))}
            {erstlaufzeitEnde(mitglied) && (
              <div style={{ gridColumn: "1 / -1" }}>
                {(() => {
                  const ablauf = erstlaufzeitEnde(mitglied);
                  const nochAktiv = ablauf > new Date();
                  return <p style={{ color: nochAktiv ? THEME.colors.warning : THEME.colors.success, fontSize: THEME.fontSize.base, margin: 0 }}>
                    {nochAktiv ? "⏱ Erstlaufzeit läuft noch bis: " : "✓ Erstlaufzeit abgelaufen am: "}{ablauf.toLocaleDateString("de-DE")}
                  </p>;
                })()}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            {[["anrede","Anrede","select"],["vorname","Vorname","text"],["nachname","Nachname","text"],["geburtsdatum","Geburtsdatum","date"],["telefon","Telefon / Mobil","text"],["email","E-Mail","email"],["strasse","Straße & Nr.","text"],["plz","PLZ","text"],["ort","Ort","text"],["kontoinhaber","Kontoinhaber","text"],["startpaket","Startpaket (€)","number"]].map(([key, label, type]) => (
              <div key={key} style={s.inputGroup}><label style={s.label}>{label}</label>
                {type === "select" ? <select style={s.input} value={form[key]} onChange={e => set(key, e.target.value)}><option>Herr</option><option>Frau</option></select>
                : <input style={s.input} type={type} value={form[key] || ""} onChange={e => set(key, e.target.value)} />}
              </div>
            ))}
            <div style={s.inputGroup}><label style={s.label}>IBAN</label><input style={{ ...s.input, borderColor: ibanFehler ? THEME.colors.danger : ibanGueltig ? THEME.colors.success : THEME.colors.surfaceMuted }} value={form.iban || ""} onChange={e => handleIBAN(e.target.value)} />{ibanFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 4 }}>{ibanFehler}</p>}</div>
            <div style={s.inputGroup}><label style={s.label}>Tarif</label><select style={s.input} value={form.tarif} onChange={e => set("tarif", e.target.value)}>{TARIFE.map(t => <option key={t}>{t}</option>)}</select></div>
            {/* type="text"+inputMode="decimal" statt type="number": Zahlenfelder mit erzwungenem
                value={parseFloat(...).toFixed(2)} auf JEDEM Tastendruck kaempften auf dem Handy
                gegen die eigene Eingabe (Meldung 2026-09-07). Jetzt greift onChange ueberhaupt
                nicht mehr ein - der Text bleibt exakt so stehen wie eingetippt (Komma oder Punkt,
                auch mitten in einer noch unvollstaendigen Eingabe). Ungueltige Werte werden nur per
                rotem Rahmen/Fehlertext angezeigt, nichts wird automatisch ueberschrieben. Erst beim
                Verlassen des Feldes (onBlur) wird - nur falls gueltig - auf 2 Nachkommastellen
                normalisiert. */}
            <div style={s.inputGroup}><label style={s.label}>Monatlicher Beitrag (€)</label>
              <input style={{ ...s.input, borderColor: isNaN(parseGeldEingabe(form.preis)) ? THEME.colors.danger : s.input.borderColor }}
                type="text" inputMode="decimal" value={form.preis ?? ""} onChange={e => set("preis", e.target.value)}
                onBlur={e => { const p = parseGeldEingabe(e.target.value); if (!isNaN(p)) set("preis", p.toFixed(2)); }} />
              {isNaN(parseGeldEingabe(form.preis)) && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Ungültiger Betrag</p>}
            </div>
            <div style={s.inputGroup}><label style={s.label}>Anteiliger Erstbeitrag (€)</label>
              <input style={{ ...s.input, borderColor: isNaN(parseGeldEingabe(form.anteiliger_erstbeitrag)) ? THEME.colors.danger : s.input.borderColor }}
                type="text" inputMode="decimal" value={form.anteiliger_erstbeitrag ?? ""} onChange={e => set("anteiliger_erstbeitrag", e.target.value)}
                onBlur={e => { const p = parseGeldEingabe(e.target.value); if (!isNaN(p)) set("anteiliger_erstbeitrag", p.toFixed(2)); }} />
              {isNaN(parseGeldEingabe(form.anteiliger_erstbeitrag)) && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Ungültiger Betrag</p>}
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Erstlaufzeit</label>
              <select style={s.input} value={form.erstlaufzeit || "keine"} onChange={e => set("erstlaufzeit", e.target.value)}>
                <option value="keine">Keine (monatlich kündbar)</option>
                <option value="6">6 Monate</option>
                <option value="12">12 Monate</option>
                {String(form.erstlaufzeit || "").startsWith("bis:") && <option value={form.erstlaufzeit}>{erstlaufzeitText(form.erstlaufzeit)}</option>}
              </select>
            </div>
          </div>
        )}
      </div>
      </div>
      <div className="mitglied-aktionsspalte">
        <button onClick={() => setNotizenOffen(v => !v)} style={aktionsButtonStyle(THEME.colors.primary, notizenOffen)}>
          <span>Notizen</span>
          {notizen.length > 0 && <span style={{ background: THEME.colors.background, color: THEME.colors.text, borderRadius: 10, padding: "1px 7px", fontSize: THEME.fontSize.xs, fontWeight: 700 }}>{notizen.length}</span>}
        </button>
        <button onClick={() => setAktionenOffen(v => !v)} style={aktionsButtonStyle(THEME.colors.warning, aktionenOffen)}>Aktionen</button>
        <button onClick={() => setEinmalzahlungenOffen(v => !v)} style={aktionsButtonStyle(THEME.colors.accentViolet, einmalzahlungenOffen)}>Einmalzahlungen</button>
        <button onClick={() => setPartnerOffen(v => !v)} style={aktionsButtonStyle(THEME.colors.accentCyan, partnerOffen)}>Partner</button>
        <button onClick={() => setDokumenteOffen(v => !v)} style={aktionsButtonStyle(THEME.colors.accentPink, dokumenteOffen)}>Dokumente</button>
        <button onClick={() => setZahlungsverlaufOffen(v => !v)} style={aktionsButtonStyle(THEME.colors.accentGreenDark, zahlungsverlaufOffen)}>Zahlungsverlauf</button>
        {wellpassAnzahl > 0 && <button onClick={() => setWellpassOffen(v => !v)} style={aktionsButtonStyle(THEME.colors.accentOrange, wellpassOffen)}>Wellpass</button>}
        <button onClick={() => setRfidOffen(v => !v)} style={aktionsButtonStyle(THEME.colors.accentCyan, rfidOffen)}>Zutritt und Mitgliederapp</button>
      </div>
      </div>

      {rfidOffen && (
        <>
          <RfidBereich mitglied={mitglied} onAktualisiert={onAktualisiert} />
          {rechte?.rolle === "admin" && <MitgliederAppZugangBereich mitgliedId={mitglied.id} />}
          {rechte?.rolle === "admin" && <WlanGeraeteBereich mitgliedId={mitglied.id} />}
        </>
      )}

      {notizenOffen && (
      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Notizen</h3>
        {notizen.length === 0 ? (
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, margin: "0 0 12px" }}>Noch keine Notizen – z. B. Sonderkonditionen, Aktionen, Absprachen.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            {notizen.map(n => (
              <div key={n.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div>
                  <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md, margin: 0, whiteSpace: "pre-wrap" }}>{n.text}</p>
                  <p style={{ color: "#5a6b7d", fontSize: THEME.fontSize.xs, margin: "4px 0 0" }}>{n.erstellt_am}</p>
                </div>
                <button onClick={() => handleNotizLoeschen(n.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "4px 10px", background: THEME.colors.dangerBg, flexShrink: 0 }}>✗</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={{ ...s.input, flex: 1 }}
            value={neueNotiz}
            onChange={e => setNeueNotiz(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") handleNotizHinzufuegen(); }}
            placeholder="Neue Notiz eingeben…"
          />
          <button onClick={handleNotizHinzufuegen} style={{ ...s.btn, width: "auto" }}>Hinzufügen</button>
        </div>
      </div>
      )}

      {aktionenOffen && (
      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Aktionen</h3>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {mitglied.status === "aktiv" && rechte?.darf_pausieren && <button onClick={() => setPauseDialog(true)} style={{ ...s.btn, width: "auto", background: THEME.colors.warning }}>Pausieren</button>}
          {(mitglied.status === "aktiv" || mitglied.status === "pausiert") && rechte?.darf_pausieren && <button onClick={() => setGratismonatDialog(true)} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>Gratismonat gewähren</button>}
          {mitglied.status === "aktiv" && rechte?.darf_kuendigen && <button onClick={() => setKuendDialog(true)} style={{ ...s.btn, width: "auto", background: THEME.colors.danger }}>Kündigen</button>}
          {mitglied.status === "pausiert" && rechte?.darf_pausieren && <button onClick={() => { setPauseVerlaengernBis(mitglied.pause_bis || ""); setPauseVerlaengernFehler(""); setPauseVerlaengernDialog(true); }} style={{ ...s.btn, width: "auto", background: THEME.colors.warning }}>Pause verlängern</button>}
          {mitglied.status === "pausiert" && rechte?.darf_pausieren && <button onClick={() => { setReaktivierPauseAbDate(heuteISO); setReaktivierPauseFehler(""); setReaktivierPauseDialog(true); }} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>Reaktivieren</button>}
          {mitglied.status === "pausiert" && rechte?.darf_kuendigen && <button onClick={() => setKuendDialog(true)} style={{ ...s.btn, width: "auto", background: THEME.colors.danger }}>Kündigen</button>}
          {(mitglied.status === "gekündigt" || mitglied.status === "ehemalig") && <button onClick={() => { setReaktivierAbDate(heuteISO); setReaktivierFehler(""); setReaktivierDialog(true); }} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>Wieder aktivieren</button>}
          {mitglied.email && !mitglied.email.includes("@comefit.intern") && <button onClick={() => window.open(`mailto:${mitglied.email}`, "_blank")} style={{ ...s.btn, width: "auto", background: THEME.colors.primary }}>✉ E-Mail senden</button>}
          {(mitglied.status === "aktiv" || mitglied.status === "pausiert") && (rechte?.rolle === "admin" || rechte?.darf_mitglieder_bearbeiten) && <button onClick={() => { setUmwandelnTyp("wellpass"); setUmwandelnOffen(true); }} style={{ ...s.btn, width: "auto", background: THEME.colors.warning }}>🔄 Zu Wellpass/Hansefit umwandeln</button>}
          {(rechte?.rolle === "admin" || rechte?.darf_mitglieder_bearbeiten) && <button onClick={() => window.open(`${API}/mitglieder/${mitglied.id}/datenauskunft/pdf`, "_blank")} style={{ ...s.btn, width: "auto", background: THEME.colors.primary }}>📄 Datenauskunft exportieren</button>}
          {(rechte?.rolle === "admin" || rechte?.darf_mitglieder_loeschen) && <button onClick={() => setLoeschenDialog(true)} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg, marginLeft: "auto" }}>Mitglied löschen</button>}

        </div>
      </div>
      )}

      {pausen.length > 0 && (
        <div style={{ ...s.karte, marginBottom: 16 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Pausenverlauf</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, marginBottom: 8 }}><span>Von</span><span>Bis</span><span>Dauer</span><span></span></div>
          {pausen.map(p => (
            <div key={p.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, padding: "10px 0", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{datumDE(p.start_datum)}</span>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{datumDE(p.end_datum)}</span>
              <span style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.md }}>{p.monate} Monat{p.monate > 1 ? "e" : ""}</span>
              {rechte?.darf_pausieren && <button onClick={() => handlePauseLoeschen(p)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px", background: THEME.colors.danger }}>Löschen</button>}
            </div>
          ))}
        </div>
      )}

      {gratismonate.length > 0 && (
        <div style={{ ...s.karte, marginBottom: 16 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Gratismonatsverlauf</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, marginBottom: 8 }}><span>Von</span><span>Bis</span><span>Dauer</span><span></span></div>
          {gratismonate.map(g => (
            <div key={g.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, padding: "10px 0", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{datumDE(g.start_datum)}</span>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{datumDE(g.end_datum)}</span>
              <span style={{ color: THEME.colors.success, fontSize: THEME.fontSize.md }}>{g.monate} Monat{g.monate > 1 ? "e" : ""}</span>
              {rechte?.darf_pausieren && <button onClick={() => handleGratismonatLoeschen(g)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px", background: THEME.colors.danger }}>Löschen</button>}
            </div>
          ))}
        </div>
      )}

      {zahlungen.length > 0 && (
        <div style={{ ...s.karte, marginBottom: 16 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Zahlungsverlauf</h3>
          <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 480 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, marginBottom: 8 }}><span>Abbuchung</span><span>Betrag</span><span>Status</span><span>Aktion</span></div>
          {zahlungen.map(z => (
            <div key={z.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, padding: "10px 0", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{datumDE(z.abbuchungsdatum)}</span>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{parseFloat(z.betrag).toFixed(2)} €</span>
              <span style={{ color: statusFarbe(z.status), fontWeight: 600, fontSize: THEME.fontSize.base }}>{z.status === "ausstehend" ? "Ausstehend" : z.status === "bezahlt" ? "Bezahlt ✓" : "Rücklastschrift ✗"}</span>
              <div style={{ display: "flex", gap: 6 }}>
                {z.status === "ausstehend" && <><button onClick={() => handleBezahlt(z.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px", background: THEME.colors.success }}>✓</button><button onClick={() => handleRuecklastschrift(z.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px", background: THEME.colors.danger }}>RL</button></>}
                {z.status === "zurueckgebucht" && <button onClick={() => handleBezahlt(z.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px", background: THEME.colors.success }}>✓ Bezahlt</button>}
              </div>
            </div>
          ))}
          </div>
          </div>
        </div>
      )}

      {einmalzahlungenOffen && <EinmalzahlungBereich mitglied={mitglied} />}
      {partnerOffen && <PartnerBereich mitglied={mitglied} onAktualisiert={onAktualisiert} />}

      {zahlungsverlaufOffen && <ZahlungsHistorie mitgliedId={mitglied.id} />}
      {dokumenteOffen && (
        <>
          <DokumenteBereich mitgliedId={mitglied.id} />
          {(rechte?.rolle === "admin" || rechte?.module?.vorfaelle_ansehen) && (
            <VorfallberichteEmbed typ="mitglied" personId={mitglied.id} rechte={rechte} onOeffnen={onVorfallOeffnen} />
          )}
        </>
      )}
      {wellpassOffen && <WellpassMitgliedVerlauf mitgliedId={mitglied.id} />}
    </div>
  );
}

// ============ PERSON FORMULAR (Mitglied / Wellpass-Nutzer / Hansefit-Nutzer) ============
const PERSON_TYP_CONFIG = {
  mitglied: { label: "Mitglied", endpoint: "/mitglieder/" },
  wellpass: { label: "Wellpass-Nutzer", endpoint: "/wellpass/nutzer" },
  hansefit: { label: "Hansefit-Nutzer", endpoint: "/hansefit/nutzer" },
  sonstige: { label: "Sonstige", endpoint: "/sonstige/nutzer" },
};

function PersonFormular({ typ, onTypWechsel, typLocked, initial, onSpeichern, onAbbrechen, benutzer, konvertierung, alsSeite }) {
  const heute = new Date().toISOString().split("T")[0];
  const leer = {
    anrede: "Herr", vorname: "", nachname: "", geburtsdatum: "", email: "", telefon: "", strasse: "", plz: "", ort: "",
    // anteiliger_erstbeitrag muss hier schon berechnet werden, nicht nur als Platzhalter 0 stehen -
    // sonst blieb er bei 0, solange man Tarif/Vertragsbeginn nie aktiv anfasst (z.B. einfach
    // durchtabbt und beim Default "Premium" bleibt), obwohl der korrekte Wert laengst feststeht
    // (gefunden 2026-07-21).
    tarif: "Premium", preis: 49.90, vertragsbeginn: heute, iban: "", kontoinhaber: "",
    startpaket: 0, anteiliger_erstbeitrag: berechneAnteilig(49.90, heute), mandats_id: "", erstlaufzeit: "keine",
    externe_id: "", rfid_kartennummer: "", gueltig_bis: "",
    notizen: "", wechselDatum: heute,
    ...initial,
  };
  const [form, setForm] = useState(leer);
  // Aktion (z.B. "Trainiere bis 2027 gratis") fuer handschriftlich ausgefuellte Vertraege: setzt Erstlaufzeit, Erstbeitrag 0
  // und legt nach dem Speichern den Gratis-Zeitraum an - gleiche Logik wie bei der Online-Freigabe (routers_vertraege.py).
  const [aktionen, setAktionen] = useState([]);
  const [aktionId, setAktionId] = useState("");
  useEffect(() => {
    if (typ !== "mitglied" || initial?.id) return;
    fetch(`${API}/vertraege/tarife-und-aktionen`).then(r => r.json())
      .then(d => setAktionen((d.aktionen || []).filter(a => a.gratis_bis_datum))).catch(() => {});
  }, [typ]); // eslint-disable-line react-hooks/exhaustive-deps
  const aktionErstlaufzeitEnde = (a) => {
    const [j, m, t] = a.gratis_bis_datum.split("-").map(Number);
    const start = new Date(Date.UTC(j, m - 1, t + 1));
    const ende = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + a.laufzeit_monate, start.getUTCDate() - 1));
    return ende.toISOString().slice(0, 10);
  };
  // Hinweis auf bereits vorhandene Eintraege mit demselben Namen, waehrend getippt wird (nicht erst beim Speichern):
  // Wer nach einer Kuendigung wiederkommt, soll am ehemaligen Eintrag reaktiviert statt doppelt angelegt werden.
  const [gleichNamige, setGleichNamige] = useState([]);
  useEffect(() => {
    const vn = (form.vorname || "").trim(), nn = (form.nachname || "").trim();
    if (typ !== "mitglied" || initial?.id || vn.length < 2 || nn.length < 2) { setGleichNamige([]); return; }
    let verworfen = false;
    const t = setTimeout(() => {
      fetch(`${API}/mitglieder/?suche=${encodeURIComponent(`${vn} ${nn}`)}`).then(r => r.ok ? r.json() : [])
        .then(liste => {
          if (verworfen || !Array.isArray(liste)) return;
          const norm = (x) => (x || "").trim().toLowerCase();
          setGleichNamige(liste.filter(m => norm(m.vorname) === norm(vn) && norm(m.nachname) === norm(nn)).slice(0, 5));
        }).catch(() => {});
    }, 400);
    return () => { verworfen = true; clearTimeout(t); };
  }, [typ, form.vorname, form.nachname]); // eslint-disable-line react-hooks/exhaustive-deps
  const [ibanFehler, setIbanFehler] = useState("");
  const [ibanGueltig, setIbanGueltig] = useState(false);
  // Kontoinhaber standardmaessig Vorname+Nachname des Mitglieds, solange er nicht manuell
  // geaendert wurde (z.B. abweichender Kontoinhaber) - kein Auto-Ueberschreiben mehr, sobald
  // einmal von Hand editiert oder wenn initial bereits einen Wert mitbringt.
  const [kontoinhaberManuell, setKontoinhaberManuell] = useState(!!leer.kontoinhaber);
  useEffect(() => {
    if (typ === "mitglied" && !kontoinhaberManuell) {
      const auto = `${form.vorname} ${form.nachname}`.trim();
      setForm(f => (f.kontoinhaber === auto ? f : { ...f, kontoinhaber: auto }));
    }
  }, [typ, kontoinhaberManuell, form.vorname, form.nachname]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // PLZ -> Ort automatisch ausfuellen (Nutzer-Wunsch 2026-07-21): kein externer PLZ-Datensatz
  // noetig, es reicht der Blick in bereits vorhandene Mitglieder mit derselben PLZ, da sich bei
  // einem lokalen Studio dieselben Postleitzahlen ohnehin staendig wiederholen. Ueberschreibt nur
  // ein noch LEERES Ort-Feld, nie eine bereits von Hand eingegebene Angabe.
  const [strassenVorschlaege, setStrassenVorschlaege] = useState([]);
  const handlePlzChange = (v) => {
    set("plz", v);
    if (v.trim().length >= 4) {
      fetch(`${API}/mitglieder/ort-fuer-plz?plz=${encodeURIComponent(v.trim())}`).then(r => r.json())
        .then(d => { if (d.ort) setForm(f => (f.ort ? f : { ...f, ort: d.ort })); }).catch(() => {});
      fetch(`${API}/mitglieder/strassen-vorschlaege?plz=${encodeURIComponent(v.trim())}`).then(r => r.json())
        .then(d => setStrassenVorschlaege(Array.isArray(d) ? d : [])).catch(() => {});
    } else {
      setStrassenVorschlaege([]);
    }
  };
  const handleTarif = (tarif) => { const preis = TARIF_PREISE[tarif] || form.preis; setForm(f => ({ ...f, tarif, preis, anteiliger_erstbeitrag: aktionId ? "0.00" : berechneAnteilig(preis, f.vertragsbeginn) })); };
  const handleVertragsbeginn = (datum) => setForm(f => ({ ...f, vertragsbeginn: datum, anteiliger_erstbeitrag: aktionId ? "0.00" : berechneAnteilig(f.preis, datum) }));
  const handlePreis = (preis) => setForm(f => {
    const p = parseGeldEingabe(preis);
    return { ...f, preis, anteiliger_erstbeitrag: aktionId ? "0.00" : (isNaN(p) ? f.anteiliger_erstbeitrag : berechneAnteilig(p, f.vertragsbeginn)) };
  });
  const handleAktion = (idStr) => {
    setAktionId(idStr);
    const a = aktionen.find(x => String(x.id) === idStr);
    if (!a) {
      setForm(f => ({ ...f, erstlaufzeit: String(f.erstlaufzeit || "").startsWith("bis:") ? "keine" : f.erstlaufzeit, anteiliger_erstbeitrag: berechneAnteilig(parseGeldEingabe(f.preis) || 0, f.vertragsbeginn) }));
      return;
    }
    setForm(f => ({ ...f, erstlaufzeit: `bis:${aktionErstlaufzeitEnde(a)}`, anteiliger_erstbeitrag: "0.00" }));
  };
  const handleIBAN = (v) => { set("iban", v); if (v.length > 4) { const ok = validiereIBAN(v); setIbanGueltig(ok); setIbanFehler(ok ? "" : "IBAN ist ungültig"); } else { setIbanFehler(""); setIbanGueltig(false); } };
  const alter = berechneAlter(form.geburtsdatum);
  const pflicht = typ === "mitglied";
  const vonMitglied = konvertierung?.richtung === "von_mitglied";
  const zuMitglied = konvertierung?.richtung === "zu_mitglied";

  const [speichertGerade, setSpeichertGerade] = useState(false);

  // Liest den Fehlertext einer fehlgeschlagenen Antwort robust aus - eine Session-Ablauf-Antwort
  // (401, HTML-/Text-Body statt JSON) liess res.json() bisher mit einer SyntaxError abbrechen,
  // ohne dass der try/catch drumherum existierte - dadurch verschwand der Klick auf "Speichern"
  // komplett lautlos, ohne jede Rueckmeldung (gefunden 2026-08-13).
  const fehlertextAusAntwort = async (res) => {
    try {
      const err = await res.json();
      return err?.detail ? JSON.stringify(err.detail) : `${res.status} ${res.statusText}`;
    } catch {
      return `${res.status} ${res.statusText}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (speichertGerade) return;
    setSpeichertGerade(true);
    try {
      if (typ === "mitglied") {
        // IBAN ist bewusst kein Pflichtfeld mehr - kommt manchmal erst nachtraeglich, wenn das
        // Mitglied seine Karte vergessen hat. Wird aber, wenn eingegeben, weiterhin geprueft.
        if (form.iban && !ibanGueltig) { setIbanFehler("Bitte gültige IBAN eingeben"); return; }
        const rfidUmgerechnet = form.rfid_kartennummer ? (pcLeserZuKartennummer(form.rfid_kartennummer) ?? form.rfid_kartennummer) : null;
        // parseGeldEingabe statt parseFloat: Sicherheitsnetz falls beim Absenden (z.B. Enter-Taste)
        // noch ein Komma im Feld steht, ohne dass vorher ein onBlur ausgeloest wurde.
        const payload = { ...form, sepa_mandat: !!form.iban, rfid_kartennummer: rfidUmgerechnet != null ? String(rfidUmgerechnet) : null, preis: parseGeldEingabe(form.preis) || 0, startpaket: parseGeldEingabe(form.startpaket) || 0, anteiliger_erstbeitrag: parseGeldEingabe(form.anteiliger_erstbeitrag) || 0, mandats_id: form.mandats_id === "" || form.mandats_id === null || form.mandats_id === undefined ? null : parseInt(form.mandats_id, 10) };
        const res = await fetch(`${API}/mitglieder/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        if (!res.ok) { alert("Fehler beim Speichern: " + await fehlertextAusAntwort(res)); return; }
        const neuesMitglied = await res.json();
        try { await logAktivitaet("Neues Mitglied angelegt", null, neuesMitglied?.id ?? null, `${form.nachname}, ${form.vorname}`, benutzer); } catch {}
        if (aktionId && neuesMitglied?.id) {
          const a = aktionen.find(x => String(x.id) === aktionId);
          if (a) {
            const [vj, vm] = form.vertragsbeginn.split("-").map(Number);
            const [gj, gm] = a.gratis_bis_datum.split("-").map(Number);
            const monateGratis = Math.max(1, (gj - vj) * 12 + gm - vm + 1);
            try {
              const gr = await fetch(`${API}/gratismonate/`, { method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ mitglied_id: neuesMitglied.id, start_datum: form.vertragsbeginn, end_datum: a.gratis_bis_datum, monate: monateGratis, grund: `Aktion: ${a.name}` }) });
              if (!gr.ok) alert(`${form.nachname}, ${form.vorname} wurde angelegt, aber der Gratis-Zeitraum konnte nicht eingetragen werden (${await fehlertextAusAntwort(gr)}). Bitte im Mitgliedsprofil über „Gratismonat“ nachtragen.`);
            } catch { alert("Mitglied angelegt, aber der Gratis-Zeitraum konnte nicht eingetragen werden. Bitte im Mitgliedsprofil nachtragen."); }
            try {
              await fetch(`${API}/mitglieder/${neuesMitglied.id}/notizen`, { method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: `Aktion "${a.name}" (Papiervertrag): beitragsfrei vom ${datumDE(form.vertragsbeginn)} bis ${datumDE(a.gratis_bis_datum)}, danach ${a.laufzeit_monate} Monate Laufzeit bis ${datumDE(aktionErstlaufzeitEnde(a))}.` }) });
            } catch {}
          }
        }
        if (zuMitglied) {
          const istProbetraining = konvertierung.quelleTyp === "probetraining";
          const verknuepfenPfad = konvertierung.quelleTyp === "wellpass" ? "/wellpass/nutzer"
            : konvertierung.quelleTyp === "hansefit" ? "/hansefit/nutzer" : "/sonstige/nutzer";
          // Probetraining: eigener Endpunkt - loest die RFID transaktionssicher vom Probetraining und weist sie dem neuen Mitglied zu
          const verknuepfenUrl = istProbetraining
            ? `${API}/probetraining/nutzer/${konvertierung.quelleId}/als-mitglied-uebernehmen`
            : `${API}${verknuepfenPfad}/${konvertierung.quelleId}/mitglied-verknuepfen`;
          const verknuepfenRes = await fetch(verknuepfenUrl, {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mitglied_id: neuesMitglied.id }),
          });
          if (!verknuepfenRes.ok) {
            alert(istProbetraining
              ? `${form.nachname}, ${form.vorname} wurde als Mitglied angelegt, aber die Übernahme des Probetrainings (inkl. RFID) war nicht möglich: ${await fehlertextAusAntwort(verknuepfenRes)}

Das Probetraining ist unverändert aktiv, die RFID wurde nichts verändert.`
              : `${form.nachname}, ${form.vorname} wurde als Mitglied angelegt, aber die Verknüpfung zum ursprünglichen Datensatz konnte nicht gespeichert werden.`);
          }
        }
        onSpeichern(typ, neuesMitglied);
        return;
      }
      const rfidUmgerechnet = form.rfid_kartennummer ? (pcLeserZuKartennummer(form.rfid_kartennummer) ?? form.rfid_kartennummer) : null;
      const payload = typ === "sonstige"
        ? { vorname: form.vorname || null, nachname: form.nachname || null,
            email: form.email || null, telefon: form.telefon || null,
            rfid_kartennummer: rfidUmgerechnet != null ? String(rfidUmgerechnet) : null,
            gueltig_bis: form.gueltig_bis || null, notiz: form.notizen || null }
        : {
            vorname: form.vorname || null, nachname: form.nachname || null,
            anrede: form.anrede || null, geburtsdatum: form.geburtsdatum || null,
            email: form.email || null, telefon: form.telefon || null,
            strasse: form.strasse || null, plz: form.plz || null, ort: form.ort || null,
            rfid_kartennummer: rfidUmgerechnet != null ? String(rfidUmgerechnet) : null,
            notiz: form.notizen || null,
          };
      if (typ === "hansefit") payload.externe_hansefit_id = form.externe_id || null;
      if (vonMitglied) payload.mitglied_id = konvertierung.mitglied.id;
      const res = await fetch(`${API}${PERSON_TYP_CONFIG[typ].endpoint}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) { alert("Fehler beim Speichern: " + await fehlertextAusAntwort(res)); return; }
      const neuerNutzer = await res.json();
      if (vonMitglied) {
        const m = konvertierung.mitglied;
        const kStatusRes = await fetch(`${API}/mitglieder/${m.id}/status?status=gekündigt`, { method: "PATCH" });
        if (!kStatusRes.ok) { alert(`${PERSON_TYP_CONFIG[typ].label}-Konto wurde angelegt, aber der Status von ${m.vorname} ${m.nachname} konnte nicht auf „gekündigt" gesetzt werden: ` + await fehlertextAusAntwort(kStatusRes)); return; }
        const kPutRes = await fetch(`${API}/mitglieder/${m.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...m, status: "gekündigt", kuendigung_zum: form.wechselDatum }) });
        if (!kPutRes.ok) {
          // Gleicher Fehlerfall wie in handleKuendigungSpeichern: Status ist bereits gesetzt,
          // aber ohne dieses PUT fehlt kuendigung_zum in der DB - sichtbar machen statt verschlucken.
          alert(`${m.vorname} ${m.nachname} wurde als gekündigt markiert, aber das Kündigungsdatum konnte NICHT gespeichert werden: ` + await fehlertextAusAntwort(kPutRes) + " – bitte am Mitglied manuell nachtragen.");
        }
        await fetch(`${API}/mitglieder/${m.id}/notizen`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: `Umgewandelt zu ${PERSON_TYP_CONFIG[typ].label}, Wechsel zum ${datumDE(form.wechselDatum)}` }) });
        try { await logAktivitaet(`Zu ${PERSON_TYP_CONFIG[typ].label} umgewandelt`, `Wechsel zum ${datumDE(form.wechselDatum)}`, m.id, `${m.nachname}, ${m.vorname}`, benutzer); } catch {}
      }
      onSpeichern(typ, neuerNutzer);
    } catch (fehler) {
      console.error("PersonFormular handleSubmit fehlgeschlagen:", fehler);
      alert("Fehler beim Speichern: " + (fehler?.message || "Unbekannter Fehler - bitte Internetverbindung/Sitzung prüfen und erneut versuchen."));
    } finally {
      setSpeichertGerade(false);
    }
  };

  const titel = vonMitglied ? `${konvertierung.mitglied.vorname} ${konvertierung.mitglied.nachname} umwandeln` : zuMitglied ? `${konvertierung.quelleName} zu Mitglied umwandeln` : "Neue Person anlegen";

  const inhalt = (
    <>
        {typLocked ? (
          <p style={{ color: THEME.colors.textMuted, marginBottom: 16 }}>Typ: <strong style={{ color: THEME.colors.text }}>{PERSON_TYP_CONFIG[typ].label}</strong></p>
        ) : (
          <div style={{ ...s.inputGroup, marginBottom: 16 }}>
            <label style={s.label}>Typ</label>
            <select style={s.input} value={typ} onChange={e => onTypWechsel(e.target.value)}>
              {!vonMitglied && <option value="mitglied">Mitglied</option>}
              <option value="wellpass">Wellpass-Nutzer</option>
              <option value="hansefit">Hansefit-Nutzer</option>
              <option value="sonstige">Sonstige</option>
            </select>
          </div>
        )}
        {vonMitglied && (
          <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 16, borderLeft: `4px solid ${THEME.colors.warning}` }}>
            <div style={s.inputGroup}>
              <label style={s.label}>Wechsel zum</label>
              <input style={{ ...s.input, borderColor: THEME.colors.primary }} type="date" required value={form.wechselDatum} onChange={e => set("wechselDatum", e.target.value)} />
            </div>
            <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, margin: "8px 0 0" }}>⚠ Ab diesem Datum wird {konvertierung.mitglied.vorname} {konvertierung.mitglied.nachname} als gekündigt geführt (Kündigung zum {datumDE(form.wechselDatum)}).</p>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {typ === "sonstige" ? (
            // SonstigeNutzer hat bewusst nur Vorname/Nachname als Stammdaten (siehe
            // backend/routers_sonstige.py) - die Kontakt-/Adressfelder wuerden vom Backend
            // ohnehin still verworfen, deshalb hier gar nicht erst anzeigen.
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
              <div style={s.inputGroup}><label style={s.label}>Vorname</label><input style={s.input} value={form.vorname} onChange={e => set("vorname", e.target.value)} /></div>
              <div style={s.inputGroup}><label style={s.label}>Nachname</label><input style={s.input} value={form.nachname} onChange={e => set("nachname", e.target.value)} /></div>
              <div style={s.inputGroup}><label style={s.label}>E-Mail</label><input style={s.input} type="email" value={form.email} onChange={e => set("email", e.target.value)} /></div>
              <div style={s.inputGroup}><label style={s.label}>Telefon / Mobil</label><input style={s.input} value={form.telefon} onChange={e => set("telefon", e.target.value)} /></div>
            </div>
          ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            <div style={s.inputGroup}><label style={s.label}>Anrede</label><select style={s.input} value={form.anrede} onChange={e => set("anrede", e.target.value)}><option>Herr</option><option>Frau</option></select></div>
            <div style={s.inputGroup}><label style={s.label}>Vorname{pflicht ? " *" : ""}</label><input style={s.input} required={pflicht} value={form.vorname} onChange={e => set("vorname", e.target.value)} /></div>
            <div style={s.inputGroup}><label style={s.label}>Nachname{pflicht ? " *" : ""}</label><input style={s.input} required={pflicht} value={form.nachname} onChange={e => set("nachname", e.target.value)} /></div>
            {gleichNamige.length > 0 && (
              <div style={{ gridColumn: "1 / -1", background: THEME.colors.warning + "22", border: `1px solid ${THEME.colors.warning}`, borderRadius: 8, padding: "10px 14px", color: THEME.colors.text, fontSize: THEME.fontSize.base }}>
                <strong>⚠ Diesen Namen gibt es schon:</strong>
                {gleichNamige.map(m => (
                  <div key={m.id} style={{ marginTop: 4 }}>
                    #{m.id} {m.vorname} {m.nachname} – {m.status === "aktiv" ? "aktives Mitglied" : m.status}
                    {m.kuendigung_zum ? ` (gekündigt zum ${datumDE(String(m.kuendigung_zum).slice(0, 10))})` : ""}
                  </div>
                ))}
                <div style={{ marginTop: 6, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                  {gleichNamige.some(m => m.status !== "aktiv")
                    ? "Kommt die Person zurück? Dann bitte den ehemaligen Eintrag öffnen und „Reaktivieren“ nutzen, statt neu anzulegen – so bleiben Karte und Verlauf erhalten."
                    : "Bitte prüfen, ob die Person schon Mitglied ist (z. B. Familienmitglied mit gleichem Namen)."}
                </div>
              </div>
            )}
            <div style={s.inputGroup}>
              <label style={s.label}>Geburtsdatum{pflicht ? " *" : ""}</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input style={{ ...s.input, flex: 1 }} type="date" required={pflicht} value={form.geburtsdatum} onChange={e => set("geburtsdatum", e.target.value)} max={new Date().toISOString().split("T")[0]} />
                {alter !== null && <span style={{ color: alter < 18 ? THEME.colors.danger : THEME.colors.success, fontWeight: 600, fontSize: THEME.fontSize.base }}>{alter} J.</span>}
              </div>
              {pflicht && alter !== null && alter < 18 && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 4 }}>⚠ Erziehungsberechtigter muss unterschreiben!</p>}
            </div>
            <div style={s.inputGroup}><label style={s.label}>E-Mail</label><input style={s.input} type="email" value={form.email} onChange={e => set("email", e.target.value)} /></div>
            <div style={s.inputGroup}><label style={s.label}>Telefon / Mobil</label><input style={s.input} value={form.telefon} onChange={e => set("telefon", e.target.value)} /></div>
            <div style={s.inputGroup}>
              <label style={s.label}>Straße & Nr.{pflicht ? " *" : ""}</label>
              <input style={s.input} required={pflicht} value={form.strasse} onChange={e => set("strasse", e.target.value)} list="strassen-vorschlaege-liste" />
              <datalist id="strassen-vorschlaege-liste">
                {strassenVorschlaege.map(str => <option key={str} value={str} />)}
              </datalist>
            </div>
            <div style={s.inputGroup}><label style={s.label}>PLZ{pflicht ? " *" : ""}</label><input style={s.input} required={pflicht} value={form.plz} onChange={e => handlePlzChange(e.target.value)} maxLength={5} /></div>
            <div style={s.inputGroup}><label style={s.label}>Ort{pflicht ? " *" : ""}</label><input style={s.input} required={pflicht} value={form.ort} onChange={e => set("ort", e.target.value)} /></div>
          </div>
          )}

          {typ === "mitglied" && (
            <fieldset style={{ border: `1px solid ${THEME.colors.border}`, borderRadius: 8, padding: 16, marginTop: 16 }}>
              <legend style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, padding: "0 8px" }}>Vertrag &amp; SEPA</legend>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
                <div style={s.inputGroup}><label style={s.label}>Tarif *</label><select style={s.input} value={form.tarif} onChange={e => handleTarif(e.target.value)}>{TARIFE.map(t => <option key={t} value={t}>{t} – {TARIF_PREISE[t]?.toFixed(2)} €</option>)}</select></div>
                <div style={s.inputGroup}><label style={s.label}>Monatlicher Beitrag (€) *</label>
                  <input style={{ ...s.input, borderColor: isNaN(parseGeldEingabe(form.preis)) ? THEME.colors.danger : s.input.borderColor }}
                    type="text" inputMode="decimal" required value={form.preis ?? ""} onChange={e => handlePreis(e.target.value)}
                    onBlur={e => { const p = parseGeldEingabe(e.target.value); if (!isNaN(p)) handlePreis(p.toFixed(2)); }} />
                  {isNaN(parseGeldEingabe(form.preis)) && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Ungültiger Betrag</p>}
                </div>
                <div style={s.inputGroup}><label style={s.label}>Vertragsbeginn *</label><input style={s.input} type="date" required value={form.vertragsbeginn} onChange={e => handleVertragsbeginn(e.target.value)} /></div>
                <div style={s.inputGroup}>
                  <label style={s.label}>IBAN</label>
                  <input style={{ ...s.input, borderColor: ibanFehler ? THEME.colors.danger : ibanGueltig ? THEME.colors.success : THEME.colors.surfaceMuted }} value={form.iban} onChange={e => handleIBAN(e.target.value)} placeholder="DE89370400440532013000 (kann auch nachgereicht werden)" />
                  {ibanFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 4 }}>{ibanFehler}</p>}
                  {ibanGueltig && <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, marginTop: 4 }}>✓ IBAN gültig</p>}
                </div>
                <div style={s.inputGroup}><label style={s.label}>Kontoinhaber{form.iban ? " *" : ""}</label><input style={s.input} required={!!form.iban} value={form.kontoinhaber} onChange={e => { setKontoinhaberManuell(true); set("kontoinhaber", e.target.value); }} /></div>
                <div style={s.inputGroup}><label style={s.label}>Mandats-ID</label><input style={s.input} type="number" value={form.mandats_id} onChange={e => set("mandats_id", e.target.value)} placeholder="automatisch" /></div>
                <div style={s.inputGroup}>
                  <label style={s.label}>Anteiliger Erstbeitrag (€)</label>
                  <input style={{ ...s.input, borderColor: isNaN(parseGeldEingabe(form.anteiliger_erstbeitrag)) ? THEME.colors.danger : THEME.colors.primary }}
                    type="text" inputMode="decimal" value={form.anteiliger_erstbeitrag ?? ""} onChange={e => set("anteiliger_erstbeitrag", e.target.value)}
                    onBlur={e => { const p = parseGeldEingabe(e.target.value); if (!isNaN(p)) set("anteiliger_erstbeitrag", p.toFixed(2)); }} />
                  {isNaN(parseGeldEingabe(form.anteiliger_erstbeitrag)) && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Ungültiger Betrag</p>}
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Automatisch berechnet – kann angepasst werden</p>
                </div>
                <div style={s.inputGroup}><label style={s.label}>Startpaket (€)</label>
                  <input style={{ ...s.input, borderColor: isNaN(parseGeldEingabe(form.startpaket)) ? THEME.colors.danger : s.input.borderColor }}
                    type="text" inputMode="decimal" value={form.startpaket ?? ""} onChange={e => set("startpaket", e.target.value)}
                    onBlur={e => { const p = parseGeldEingabe(e.target.value); if (!isNaN(p)) set("startpaket", p.toFixed(2)); }} />
                  {isNaN(parseGeldEingabe(form.startpaket)) && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Ungültiger Betrag</p>}
                </div>
                {!initial?.id && aktionen.length > 0 && (
                  <div style={s.inputGroup}>
                    <label style={s.label}>Aktion (optional)</label>
                    <select style={s.input} value={aktionId} onChange={e => handleAktion(e.target.value)}>
                      <option value="">Keine Aktion</option>
                      {aktionen.map(a => <option key={a.id} value={String(a.id)}>{a.name} – 0 € bis {datumDE(a.gratis_bis_datum)}, danach {a.laufzeit_monate} Monate</option>)}
                    </select>
                    {aktionId && (
                      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 4 }}>
                        Setzt Erstlaufzeit und Erstbeitrag 0 € und trägt nach dem Speichern den Gratis-Zeitraum ab Vertragsbeginn ein. Als Vertragsbeginn den ersten Trainingstag (heute) wählen – ein späterer Beginn sperrt den Zutritt bis dahin.
                      </p>
                    )}
                  </div>
                )}
                <div style={s.inputGroup}>
                  <label style={s.label}>Erstlaufzeit</label>
                  <select style={s.input} value={form.erstlaufzeit || "keine"} onChange={e => set("erstlaufzeit", e.target.value)}>
                    <option value="keine">Keine (monatlich kündbar)</option>
                    <option value="6">6 Monate</option>
                    <option value="12">12 Monate</option>
                    {String(form.erstlaufzeit || "").startsWith("bis:") && <option value={form.erstlaufzeit}>{erstlaufzeitText(form.erstlaufzeit)}</option>}
                  </select>
                </div>
                <div style={s.inputGroup}><label style={s.label}>RFID-Kartennummer</label><input style={s.input} value={form.rfid_kartennummer} onChange={e => set("rfid_kartennummer", e.target.value)} placeholder="optional" /></div>
              </div>
            </fieldset>
          )}

          {typ !== "mitglied" && (
            <fieldset style={{ border: `1px solid ${THEME.colors.border}`, borderRadius: 8, padding: 16, marginTop: 16 }}>
              <legend style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, padding: "0 8px" }}>
                {typ === "wellpass" ? "Wellpass" : typ === "hansefit" ? "Hansefit" : "Sonstige"}-Zugang
              </legend>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
                {typ === "hansefit" && <div style={s.inputGroup}><label style={s.label}>Externe Hansefit-ID</label><input style={s.input} value={form.externe_id} onChange={e => set("externe_id", e.target.value)} /></div>}
                {typ === "sonstige" && <div style={s.inputGroup}><label style={s.label}>Gültig bis</label><input style={s.input} type="date" value={form.gueltig_bis} onChange={e => set("gueltig_bis", e.target.value)} /></div>}
                <div style={s.inputGroup}><label style={s.label}>RFID-Kartennummer</label><input style={s.input} value={form.rfid_kartennummer} onChange={e => set("rfid_kartennummer", e.target.value)} /></div>
              </div>
              {typ === "hansefit" && !form.externe_id && <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, marginTop: 8 }}>⚠ Ohne externe ID können künftige Check-ins nicht automatisch zugeordnet werden.</p>}
            </fieldset>
          )}

          <div style={{ ...s.inputGroup, marginTop: 16 }}><label style={s.label}>Notizen</label><textarea style={{ ...s.input, height: 80 }} value={form.notizen} onChange={e => set("notizen", e.target.value)} /></div>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
            <button type="button" onClick={onAbbrechen} style={s.btnGray}>Abbrechen</button>
            <button type="submit" disabled={speichertGerade} style={{ ...s.btn, width: "auto", opacity: speichertGerade ? 0.6 : 1 }}>{speichertGerade ? "Speichert..." : "Speichern"}</button>
          </div>
        </form>
    </>
  );

  if (alsSeite) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          <button onClick={onAbbrechen} style={{ ...s.btnGray, width: "auto" }}>← Zurück</button>
          <h1 style={{ ...s.heading, margin: 0, fontSize: THEME.fontSize.xxl }}>{titel}</h1>
        </div>
        <div style={s.karte}>{inhalt}</div>
      </div>
    );
  }

  return (
    <div style={s.modalBg}>
      <div style={s.modal}>
        <h2 style={s.modalTitel}>{titel}</h2>
        {inhalt}
      </div>
    </div>
  );
}

// ============ MITGLIEDER SEITE ============
const TAB_ZU_PERSON_TYP = { mitglieder: "mitglied", "wellpass-nutzer": "wellpass", "hansefit-nutzer": "hansefit", "sonstige-nutzer": "sonstige" };

function MitgliederSeite({ mitgliedOeffnen, mitgliedOeffnenNonce, personSubTabOeffnen, onPersonOeffnen, onVorfallOeffnen, benutzer, rechte }) {
  const [aktiveTab, setAktiveTab] = useState("mitglieder");
  const [mitglieder, setMitglieder] = useState([]);
  const [suche, setSuche] = useState("");
  const [uebergreifendSuche, setUebergreifendSuche] = useState("");
  const [uebergreifendErgebnisse, setUebergreifendErgebnisse] = useState([]);
  const [uebergreifendLaedt, setUebergreifendLaedt] = useState(false);
  const [uebergreifendHighlight, setUebergreifendHighlight] = useState(-1);
  const [tarifFilter, setTarifFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rfidFilter, setRfidFilter] = useState("");
  const [telefonFilter, setTelefonFilter] = useState("");
  const [ibanFilter, setIbanFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [notizenFilter, setNotizenFilter] = useState("");
  const [dokumenteFilter, setDokumenteFilter] = useState("");
  const [einmalzahlungenFilter, setEinmalzahlungenFilter] = useState("");
  const [filterPanelOffen, setFilterPanelOffen] = useState(false);
  const [formOffen, setFormOffen] = useState(false);
  const [formTyp, setFormTyp] = useState("mitglied");
  const [refreshKey, setRefreshKey] = useState(0);
  const [ausgewaehlt, setAusgewaehlt] = useState(null);
  const [sortSpalte, setSortSpalte] = useState("nachname");
  const [sortRichtung, setSortRichtung] = useState("asc");
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const pruefen = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", pruefen);
    return () => window.removeEventListener("resize", pruefen);
  }, []);
  // Verhindert eine Race Condition beim schnellen Tippen: jeder Tastendruck feuert einen eigenen
  // Fetch, und wenn eine AELTERE (z.B. die grosse ungefilterte "alle Mitglieder"-Anfrage vor dem
  // ersten Buchstaben) langsamer ist und NACH einer neueren, gefilterten Antwort zurueckkommt,
  // ueberschrieb sie bisher das Ergebnis wieder mit der falschen/veralteten Liste - genau das
  // Symptom "Suche nach 'mai' zeigt trotzdem alle Mitglieder" (gefunden 2026-07-20).
  const ladeAnfrageZaehler = useRef(0);
  const laden = async () => {
    const eigeneAnfrageId = ++ladeAnfrageZaehler.current;
    const params = new URLSearchParams();
    if (suche) params.append("suche", suche);
    if (tarifFilter) params.append("tarif", tarifFilter);
    if (statusFilter) params.append("status", statusFilter);
    if (rfidFilter) params.append("rfid", rfidFilter);
    if (telefonFilter) params.append("telefon", telefonFilter);
    if (ibanFilter) params.append("iban", ibanFilter);
    if (emailFilter) params.append("email", emailFilter);
    if (partnerFilter) params.append("partner", partnerFilter);
    if (notizenFilter) params.append("notizen", notizenFilter);
    if (dokumenteFilter) params.append("dokumente", dokumenteFilter);
    if (einmalzahlungenFilter) params.append("einmalzahlungen", einmalzahlungenFilter);
    const res = await fetch(`${API}/mitglieder/?${params}`);
    const daten = await res.json();
    if (eigeneAnfrageId === ladeAnfrageZaehler.current) setMitglieder(daten);
  };

  useEffect(() => {
    if (mitgliedOeffnen) {
      // Muss den Sub-Tab selbst auf "mitglieder" umschalten (analog zum SUB_TAB_NACH_TYP-Effekt
      // unten fuer die anderen drei Personentypen) - sonst bleibt die Detailansicht unsichtbar,
      // falls z.B. die uebergreifende Suche ein Mitglied oeffnet, waehrend gerade ein anderer
      // Sub-Tab (Wellpass/Hansefit/Sonstige) aktiv ist (Zeile 5076 rendert nur bei aktiveTab
      // === "mitglieder", gefunden 2026-07-24).
      setAktiveTab("mitglieder");
      fetch(`${API}/mitglieder/${mitgliedOeffnen}`).then(r => r.json()).then(m => setAusgewaehlt(m));
    }
  }, [mitgliedOeffnen, mitgliedOeffnenNonce]);
  // Springt beim Oeffnen aus der RFID-Kartensuche (Dashboard/Zutritt) auf den passenden
  // Sub-Tab, falls die gefundene Person kein Mitglied ist (siehe onPersonOeffnen in HauptApp).
  const SUB_TAB_NACH_TYP = { wellpass: "wellpass-nutzer", hansefit: "hansefit-nutzer", sonstige: "sonstige-nutzer" };
  useEffect(() => {
    if (personSubTabOeffnen && SUB_TAB_NACH_TYP[personSubTabOeffnen.typ]) {
      setAktiveTab(SUB_TAB_NACH_TYP[personSubTabOeffnen.typ]);
    }
  }, [personSubTabOeffnen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { laden(); }, [suche, tarifFilter, statusFilter, rfidFilter, telefonFilter, ibanFilter, emailFilter, partnerFilter, notizenFilter, dokumenteFilter, einmalzahlungenFilter]);

  // Uebergreifende Suche ueber alle vier Personentypen hinweg (Nutzer-Wunsch 2026-07-24: manchmal
  // ist am Namen allein nicht klar, ob jemand Mitglied/Wellpass/Hansefit/Sonstige ist) - nutzt den
  // bereits bestehenden, typuebergreifenden Such-Endpunkt aus dem SafeSmart-RFID-Zuordnen-Dialog
  // mit (gleiche Datenquelle wie dort, keine neue Backend-Logik noetig), blendet aber Mitarbeiter
  // aus (hier geht es nur um die vier Personentypen unter "Mitglieder").
  useEffect(() => {
    setUebergreifendErgebnisse([]);
    setUebergreifendHighlight(-1);
    if (uebergreifendSuche.trim().length < 2) return;
    let abgebrochen = false;
    setUebergreifendLaedt(true);
    fetch(`${API}/safesmart/rfid-ziel-suche?q=${encodeURIComponent(uebergreifendSuche.trim())}`)
      .then(r => r.json())
      .then(d => { if (!abgebrochen) { const liste = Array.isArray(d) ? d.filter(p => p.typ !== "mitarbeiter") : []; setUebergreifendErgebnisse(liste); setUebergreifendHighlight(liste.length > 0 ? 0 : -1); } })
      .catch(() => { if (!abgebrochen) setUebergreifendErgebnisse([]); })
      .finally(() => { if (!abgebrochen) setUebergreifendLaedt(false); });
    return () => { abgebrochen = true; };
  }, [uebergreifendSuche]);

  const UEBERGREIFEND_TYP_LABEL = { mitglied: "Mitglied", wellpass: "Wellpass-Nutzer", hansefit: "Hansefit-Nutzer", sonstige: "Sonstige" };
  const handleUebergreifendAuswaehlen = (p) => {
    setUebergreifendSuche("");
    setUebergreifendErgebnisse([]);
    setUebergreifendHighlight(-1);
    if (onPersonOeffnen) onPersonOeffnen(p.typ, p.id);
  };
  const handleUebergreifendTastatur = (e) => {
    if (uebergreifendErgebnisse.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setUebergreifendHighlight(i => (i + 1) % uebergreifendErgebnisse.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setUebergreifendHighlight(i => (i - 1 + uebergreifendErgebnisse.length) % uebergreifendErgebnisse.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const ausgewaehlt = uebergreifendErgebnisse[uebergreifendHighlight] || uebergreifendErgebnisse[0];
      if (ausgewaehlt) handleUebergreifendAuswaehlen(ausgewaehlt);
    } else if (e.key === "Escape") {
      setUebergreifendSuche("");
      setUebergreifendErgebnisse([]);
      setUebergreifendHighlight(-1);
    }
  };

  const handleSort = (spalte) => {
    if (sortSpalte === spalte) { if (sortRichtung === "asc") setSortRichtung("desc"); else if (sortRichtung === "desc") { setSortSpalte(null); setSortRichtung(null); } else setSortRichtung("asc"); }
    else { setSortSpalte(spalte); setSortRichtung("asc"); }
  };

  const sortiert = [...mitglieder].sort((a, b) => {
    // Mitglieder ohne IBAN werden immer oben angeheftet, egal nach welcher Spalte gerade sortiert
    // wird - damit fallen fehlende Bankdaten sofort auf, statt in der Liste unterzugehen.
    const aOhneIban = !a.iban;
    const bOhneIban = !b.iban;
    if (aOhneIban !== bOhneIban) return aOhneIban ? -1 : 1;
    if (!sortSpalte || !sortRichtung) return 0;
    const va = (a[sortSpalte] || "").toString().toLowerCase();
    const vb = (b[sortSpalte] || "").toString().toLowerCase();
    const vergleich = va.localeCompare(vb);
    if (vergleich !== 0) return sortRichtung === "asc" ? vergleich : -vergleich;
    // Bei gleichem Nachnamen zusaetzlich nach Vorname sortieren (gleiche Konvention wie
    // Wellpass-/Hansefit-/Sonstige-Nutzer-Listen, die serverseitig nach nachname, vorname sortieren).
    if (sortSpalte === "nachname") {
      const vornameVergleich = (a.vorname || "").toString().toLowerCase().localeCompare((b.vorname || "").toString().toLowerCase());
      return sortRichtung === "asc" ? vornameVergleich : -vornameVergleich;
    }
    return 0;
  });

  const sortPfeil = (spalte) => sortSpalte !== spalte ? " ↕" : sortRichtung === "asc" ? " ↑" : " ↓";

  if (aktiveTab === "mitglieder" && ausgewaehlt) return <MitgliedDetail mitglied={ausgewaehlt} benutzer={benutzer} rechte={rechte} onVorfallOeffnen={onVorfallOeffnen} onZurueck={() => setAusgewaehlt(null)} onAktualisiert={() => { laden(); fetch(`${API}/mitglieder/${ausgewaehlt.id}`).then(r => r.json()).then(setAusgewaehlt); }} />;

  if (formOffen) return (
    <PersonFormular
      alsSeite
      typ={formTyp} onTypWechsel={setFormTyp} benutzer={benutzer}
      onSpeichern={() => { setFormOffen(false); laden(); setRefreshKey(k => k + 1); }}
      onAbbrechen={() => setFormOffen(false)}
    />
  );

  return (
    <div>
      <h1 style={s.heading}>Mitglieder</h1>
      <div style={{ position: "relative", marginBottom: 20 }}>
        <input
          value={uebergreifendSuche}
          onChange={e => setUebergreifendSuche(e.target.value)}
          onKeyDown={handleUebergreifendTastatur}
          placeholder="🔍 Übergreifend suchen – egal ob Mitglied, Wellpass-, Hansefit- oder Sonstige-Nutzer…"
          style={{ ...s.input, width: "100%" }}
        />
        {uebergreifendSuche.trim().length >= 2 && (
          <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 20, background: THEME.colors.surface, border: `1px solid ${THEME.colors.border}`, borderRadius: 10, padding: 8, maxHeight: 320, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
            {uebergreifendLaedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, padding: 8, margin: 0 }}>Sucht…</p>}
            {!uebergreifendLaedt && uebergreifendErgebnisse.length === 0 && (
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, padding: 8, margin: 0 }}>Keine Treffer.</p>
            )}
            {!uebergreifendLaedt && uebergreifendErgebnisse.map((p, i) => (
              <div key={`${p.typ}-${p.id}`} onClick={() => handleUebergreifendAuswaehlen(p)}
                onMouseEnter={() => setUebergreifendHighlight(i)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: i === uebergreifendHighlight ? THEME.colors.surfaceMuted : "transparent" }}>
                <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base }}>{p.name}</span>
                <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, flexShrink: 0 }}>{UEBERGREIFEND_TYP_LABEL[p.typ]} · {p.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {[
            ["mitglieder", "Mitglieder", null], ["wellpass-nutzer", "Wellpass-Nutzer", "wellpass"], ["hansefit-nutzer", "Hansefit-Nutzer", "hansefit"], ["sonstige-nutzer", "Sonstige", "sonstige_nutzer"],
          ].filter(([, , modul]) => !modul || rechte?.rolle === "admin" || rechte?.module?.[modul]).map(([key, label]) => (
            <button key={key} onClick={() => { setAktiveTab(key); setAusgewaehlt(null); }}
              style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: aktiveTab === key ? THEME.colors.tabAktiv : THEME.colors.surfaceMuted, color: kontrastTextfarbe(aktiveTab === key ? THEME.colors.tabAktiv : THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md, flexShrink: 0 }}>{label}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {rechte?.darf_mitglieder_anlegen && (
            <button style={{ ...s.btn, width: "auto" }} onClick={() => { setFormTyp(TAB_ZU_PERSON_TYP[aktiveTab] || "mitglied"); setFormOffen(true); }}>+ Neu</button>
          )}
        </div>
      </div>

      {aktiveTab === "mitglieder" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <input style={{ ...s.input, flex: 1, minWidth: 180 }} placeholder="Suche nach Name, Straße, Telefon..." value={suche} onChange={e => setSuche(e.target.value)} />
            <select style={{ ...s.input, width: 150 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="aktiv">Aktiv</option><option value="pausiert">Pausiert</option><option value="gekündigt">Gekündigt</option><option value="ehemalig">Ehemalig</option><option value="">Alle</option>
            </select>
            <div style={{ position: "relative" }}>
              <button onClick={() => setFilterPanelOffen(o => !o)} style={{ ...s.btnGray, width: "auto", padding: "10px 20px", display: "flex", alignItems: "center", gap: 8 }}>
                ⚙ Filter{[tarifFilter, rfidFilter, telefonFilter, ibanFilter, emailFilter, partnerFilter, notizenFilter, dokumenteFilter, einmalzahlungenFilter].filter(Boolean).length > 0
                  ? ` (${[tarifFilter, rfidFilter, telefonFilter, ibanFilter, emailFilter, partnerFilter, notizenFilter, dokumenteFilter, einmalzahlungenFilter].filter(Boolean).length})` : ""}
              </button>
              {filterPanelOffen && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 20, background: THEME.colors.surface, border: `1px solid ${THEME.colors.border}`, borderRadius: 10, padding: 16, width: 220, maxWidth: "calc(100vw - 32px)", boxShadow: "0 8px 24px rgba(0,0,0,0.4)", display: "flex", flexDirection: "column", gap: 10 }}>
                  <select style={{ ...s.input, width: "100%" }} value={tarifFilter} onChange={e => setTarifFilter(e.target.value)}>
                    <option value="">Alle Tarife</option>{TARIFE.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <select style={{ ...s.input, width: "100%" }} value={rfidFilter} onChange={e => setRfidFilter(e.target.value)}>
                    <option value="">RFID: alle</option>
                    <option value="mit">Mit RFID</option>
                    <option value="ohne">Ohne RFID</option>
                  </select>
                  <select style={{ ...s.input, width: "100%" }} value={telefonFilter} onChange={e => setTelefonFilter(e.target.value)}>
                    <option value="">Telefon: alle</option>
                    <option value="mit">Mit Telefon</option>
                    <option value="ohne">Ohne Telefon</option>
                  </select>
                  <select style={{ ...s.input, width: "100%" }} value={ibanFilter} onChange={e => setIbanFilter(e.target.value)}>
                    <option value="">IBAN: alle</option>
                    <option value="mit">Mit IBAN</option>
                    <option value="ohne">Ohne IBAN</option>
                  </select>
                  <select style={{ ...s.input, width: "100%" }} value={emailFilter} onChange={e => setEmailFilter(e.target.value)}>
                    <option value="">E-Mail: alle</option>
                    <option value="mit">Mit E-Mail</option>
                    <option value="ohne">Ohne E-Mail</option>
                  </select>
                  <select style={{ ...s.input, width: "100%" }} value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)}>
                    <option value="">Partner: alle</option>
                    <option value="mit">Mit Partner</option>
                    <option value="ohne">Ohne Partner</option>
                  </select>
                  <select style={{ ...s.input, width: "100%" }} value={notizenFilter} onChange={e => setNotizenFilter(e.target.value)}>
                    <option value="">Notizen: alle</option>
                    <option value="mit">Mit Notizen</option>
                    <option value="ohne">Ohne Notizen</option>
                  </select>
                  <select style={{ ...s.input, width: "100%" }} value={dokumenteFilter} onChange={e => setDokumenteFilter(e.target.value)}>
                    <option value="">Dokumente: alle</option>
                    <option value="mit">Mit Dokumenten</option>
                    <option value="ohne">Ohne Dokumente</option>
                  </select>
                  <select style={{ ...s.input, width: "100%" }} value={einmalzahlungenFilter} onChange={e => setEinmalzahlungenFilter(e.target.value)}>
                    <option value="">Einmalzahlungen: alle</option>
                    <option value="mit">Mit Einmalzahlungen</option>
                    <option value="ohne">Ohne Einmalzahlungen</option>
                  </select>
                  <button onClick={() => setFilterPanelOffen(false)} style={{ ...s.btn, padding: "8px 0", marginTop: 4 }}>Anwenden</button>
                </div>
              )}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <div style={{ ...s.tabelle, minWidth: mobil ? "auto" : 600 }}>
              <div style={mobil ? { ...s.tabelleHeader, gridTemplateColumns: "1.5fr 1.5fr 1.2fr" } : s.tabelleHeader}>
                {(mobil
                  ? [["nachname","Nachname"],["vorname","Vorname"],["status","Status"]]
                  : [["nachname","Nachname"],["vorname","Vorname"],["tarif","Tarif"],["preis","Beitrag"],["telefon","Telefon"],["status","Status"],["vertragsbeginn","Seit"]]
                ).map(([key, label]) => (
                  <span key={key} onClick={() => handleSort(key)} style={{ cursor: "pointer", userSelect: "none" }}>{label}{sortPfeil(key)}</span>
                ))}
              </div>
              {sortiert.length === 0 && <p style={{ color: THEME.colors.textMuted, padding: 20, fontSize: THEME.fontSize.lg }}>Keine Mitglieder gefunden.</p>}
              {sortiert.map(m => {
                const ohneIban = !m.iban;
                const zeilenHintergrund = ohneIban ? "rgba(245, 158, 11, 0.14)" : "transparent";
                return (
                <div key={m.id} onClick={() => setAusgewaehlt(m)} title={ohneIban ? "Keine IBAN hinterlegt" : undefined}
                  style={mobil ? { ...s.tabelleZeile, gridTemplateColumns: "1.5fr 1.5fr 1.2fr", cursor: "pointer", background: zeilenHintergrund } : { ...s.tabelleZeile, cursor: "pointer", background: zeilenHintergrund }}
                  onMouseEnter={e => e.currentTarget.style.background = "#1f2f42"}
                  onMouseLeave={e => e.currentTarget.style.background = zeilenHintergrund}>
                  <span style={{ color: THEME.colors.text, fontWeight: 600, fontSize: THEME.fontSize.md }}>{ohneIban ? "⚠ " : ""}{m.nachname}</span>
                  <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{m.vorname}</span>
                  {mobil
                    ? <span style={{ color: statusFarbe(m.status), fontWeight: 700, fontSize: THEME.fontSize.md }}>{m.status}</span>
                    : <span style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.md }}>{m.tarif}</span>}
                  {!mobil && <>
                    <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{parseFloat(m.preis || 0).toFixed(2)} €</span>
                    <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>{m.telefon || "–"}</span>
                    <span style={{ color: statusFarbe(m.status), fontWeight: 700, fontSize: THEME.fontSize.md }}>{m.status}</span>
                    <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>{datumDE(m.vertragsbeginn)}</span>
                  </>}
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {aktiveTab === "wellpass-nutzer" && <WellpassNutzerSeite key={`wp-${refreshKey}`} nutzerOeffnen={personSubTabOeffnen?.typ === "wellpass" ? personSubTabOeffnen.id : null} />}
      {aktiveTab === "hansefit-nutzer" && <HansefitNutzerSeite key={`hf-${refreshKey}`} nutzerOeffnen={personSubTabOeffnen?.typ === "hansefit" ? personSubTabOeffnen.id : null} />}
      {aktiveTab === "sonstige-nutzer" && <SonstigeNutzerSeite key={`so-${refreshKey}`} nutzerOeffnen={personSubTabOeffnen?.typ === "sonstige" ? personSubTabOeffnen.id : null} />}
    </div>
  );
}

// ============ SEPA SEITE ============
function SepaSeite({ onMitgliedOeffnen }) {
  const heute = new Date();
  // datumZuLokalerISO statt toISOString(): Letzteres geht ueber UTC und haette hier (lokal
  // Mitternacht des 1. des Folgemonats) in Europe/Berlin fast immer den 31./30./28./29. des
  // AKTUELLEN Monats als Default-Abbuchungsdatum vorgeschlagen statt den 1. des Folgemonats -
  // derselbe Bug wie bei der Kuendigen-Datumsvorauswahl (Nutzer-Meldung 2026-08-29).
  const defaultDatum = datumZuLokalerISO(new Date(heute.getFullYear(), heute.getMonth() + 1, 1));
  const [abbuchungsdatum, setAbbuchungsdatum] = useState(defaultDatum);
  const [vorschau, setVorschau] = useState(null);
  const [laeufe, setLaeufe] = useState([]);
  const [ladend, setLadend] = useState(false);
  const [einreichenBestaetigung, setEinreichenBestaetigung] = useState(false);

  const [sepaFilter, setSepaFilter] = useState("");
  const [sepaSortSpalte, setSepaSortSpalte] = useState(null);
  const [sepaSortRichtung, setSepaSortRichtung] = useState("asc");

  const [kontoauszugLaedt, setKontoauszugLaedt] = useState(false);
  const [kontoauszugErgebnis, setKontoauszugErgebnis] = useState(null);
  const [kontoauszugAusgewaehlt, setKontoauszugAusgewaehlt] = useState({});
  const kontoauszugInputRef = useRef(null);
  const [ruecklastschriftenOffen, setRuecklastschriftenOffen] = useState([]);
  const [ruecklastschriftenPanelOffen, setRuecklastschriftenPanelOffen] = useState(false);
  const [aktionenPanelOffen, setAktionenPanelOffen] = useState(false);
  const ladeRuecklastschriftenOffen = () => fetch(`${API}/sepa/ruecklastschriften-offen`).then(r => r.json()).then(d => setRuecklastschriftenOffen(Array.isArray(d) ? d : [])).catch(() => {});
  const [aktionMenuOffenFuer, setAktionMenuOffenFuer] = useState(null);
  const [emailVorlagen, setEmailVorlagen] = useState([]);
  const [emailDialogFall, setEmailDialogFall] = useState(null);
  const [emailDialogVorlageId, setEmailDialogVorlageId] = useState(null);
  const [emailDialogBetreff, setEmailDialogBetreff] = useState("");
  const [emailDialogText, setEmailDialogText] = useState("");
  const [emailDialogEmpfaenger, setEmailDialogEmpfaenger] = useState(null);
  const [emailDialogLaedt, setEmailDialogLaedt] = useState(false);
  const [anrufDialogFall, setAnrufDialogFall] = useState(null);
  const [anrufText, setAnrufText] = useState("");
  const [notizDialogFall, setNotizDialogFall] = useState(null);
  const [notizText, setNotizText] = useState("");
  const [verlaufDialogFall, setVerlaufDialogFall] = useState(null);
  const [verlaufEintraege, setVerlaufEintraege] = useState([]);
  useEffect(() => {
    fetch(`${API}/email-vorlagen/?kategorie=ruecklastschrift`).then(r => r.json()).then(d => setEmailVorlagen(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const pruefen = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", pruefen);
    return () => window.removeEventListener("resize", pruefen);
  }, []);

  const navigiereMonat = async (richtung) => {
    const d = new Date(abbuchungsdatum);
    d.setMonth(d.getMonth() + richtung);
    const letzterTag = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    if (d.getDate() > letzterTag) d.setDate(letzterTag);
    const neuesDatum = d.toISOString().split("T")[0];
    setAbbuchungsdatum(neuesDatum);
    // Direkt mit neuem Datum laden
    setLadend(true);
    const res = await fetch(`${API}/sepa/vorschau?abbuchungsdatum=${neuesDatum}`);
    setVorschau(await res.json());
    setLadend(false);
  };

  const ladeVorschau = async () => { setLadend(true); const res = await fetch(`${API}/sepa/vorschau?abbuchungsdatum=${abbuchungsdatum}`); setVorschau(await res.json()); setLadend(false); };
  const ladeLaeufe = async () => { const res = await fetch(`${API}/sepa/laeufe`); setLaeufe(await res.json()); };
  useEffect(() => { ladeVorschau(); ladeLaeufe(); ladeRuecklastschriftenOffen(); }, [abbuchungsdatum]);

  const [laufDetailOffenFuer, setLaufDetailOffenFuer] = useState(null);
  const [laufMitglieder, setLaufMitglieder] = useState([]);
  const [laufMitgliederLaedt, setLaufMitgliederLaedt] = useState(false);

  const handleLaufDetailToggle = async (laufId) => {
    if (laufDetailOffenFuer === laufId) { setLaufDetailOffenFuer(null); return; }
    setLaufDetailOffenFuer(laufId);
    setLaufMitgliederLaedt(true);
    const res = await fetch(`${API}/sepa/lauf/${laufId}/mitglieder`);
    setLaufMitglieder(await res.json());
    setLaufMitgliederLaedt(false);
  };

  const handleAusLaufEntfernen = async (laufId, mitgliedId, name) => {
    if (!window.confirm(`${name} aus diesem Lauf entfernen? Das Mitglied gilt danach wieder als offen und wird beim nächsten SEPA-Einzug berücksichtigt.`)) return;
    const entfernenRes = await fetch(`${API}/sepa/lauf/${laufId}/mitglied/${mitgliedId}`, { method: "DELETE" });
    if (!entfernenRes.ok) { alert("Entfernen fehlgeschlagen."); return; }
    const res = await fetch(`${API}/sepa/lauf/${laufId}/mitglieder`);
    setLaufMitglieder(await res.json());
    ladeLaeufe();
    ladeVorschau();
  };

  const generiereXML = () => window.open(`${API}/sepa/generieren?abbuchungsdatum=${abbuchungsdatum}`, "_blank");
  const generierePDF = () => window.open(`${API}/statistiken/sepa-abschluss-pdf?abbuchungsdatum=${abbuchungsdatum}`, "_blank");

  const handleEinreichen = async () => {
    const res = await fetch(`${API}/sepa/einreichen?abbuchungsdatum=${abbuchungsdatum}`, { method: "POST" });
    setEinreichenBestaetigung(false);
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Einreichen fehlgeschlagen."); return; }
    const data = await res.json();
    alert(`✓ Lauf #${data.lauf_nr} eingereicht!\n${data.anzahl} Lastschriften wurden als "ausstehend" markiert.`);
    ladeLaeufe();
    ladeRuecklastschriftenOffen();
  };

  const handleAlleBezahlt = async (laufId) => {
    if (!window.confirm("Alle ausstehenden Zahlungen als bezahlt markieren?")) return;
    const res = await fetch(`${API}/sepa/lauf/${laufId}/alle-bezahlt`, { method: "POST" });
    if (!res.ok) { alert("Konnte nicht als bezahlt markiert werden."); return; }
    ladeLaeufe();
  };

  const handleKontoauszugDatei = async (e) => {
    const datei = e.target.files[0];
    if (!datei) return;
    setKontoauszugLaedt(true);
    const formData = new FormData();
    formData.append("datei", datei);
    try {
      const res = await fetch(`${API}/sepa/kontoauszug-vorschau`, { method: "POST", body: formData });
      const data = await res.json();
      setKontoauszugErgebnis(data);
      // Nur Posten aus dem aktuellen oder vorherigen Monat vorauswaehlen - aeltere, weiterhin
      // offene Ruecklastschriften (z.B. weil der Fall separat ueber "Offene Faelle" bearbeitet
      // wird) tauchen zwar zur Info weiter auf, muessen aber nicht bei jedem neuen Kontoauszug-
      // Import erneut per Hand abgewaehlt werden.
      const heute = new Date();
      let aktMonat = heute.getMonth() + 1, aktJahr = heute.getFullYear();
      let vorMonat = aktMonat - 1, vorJahr = aktJahr;
      if (vorMonat === 0) { vorMonat = 12; vorJahr -= 1; }
      const alleAn = {};
      (data.zugeordnet || []).forEach((z, i) => {
        const istAktuell = z.wiederholter_bounce || (z.ziel_monat === aktMonat && z.ziel_jahr === aktJahr) || (z.ziel_monat === vorMonat && z.ziel_jahr === vorJahr);
        alleAn[i] = istAktuell;
      });
      setKontoauszugAusgewaehlt(alleAn);
    } catch {
      alert("Datei konnte nicht gelesen werden.");
    }
    setKontoauszugLaedt(false);
    e.target.value = "";
  };

  const handleKontoauszugUebernehmen = async () => {
    const eintraege = (kontoauszugErgebnis?.zugeordnet || [])
      .filter((z, i) => kontoauszugAusgewaehlt[i])
      .map(z => ({
        zahlung_id: z.zahlung_id, mitglied_id: z.mitglied_id, grund: z.grund,
        betrag: z.zahlung_betrag, ziel_monat: z.ziel_monat, ziel_jahr: z.ziel_jahr,
        fall_id: z.fall_id, wiederholter_bounce: z.wiederholter_bounce,
      }));
    if (eintraege.length === 0) { setKontoauszugErgebnis(null); return; }
    const res = await fetch(`${API}/sepa/kontoauszug-anwenden`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eintraege }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(data.detail || "Übernehmen fehlgeschlagen."); return; }
    alert(`✓ ${data.message}`);
    setKontoauszugErgebnis(null);
    ladeLaeufe();
    ladeRuecklastschriftenOffen();
    ladeVorschau();
  };

  const STATUS_LABEL = { offen: "Offen", email_versendet: "E-Mail versendet", telefoniert: "Telefoniert", erledigt: "Erledigt" };
  const STATUS_FARBE = { offen: THEME.colors.danger, email_versendet: THEME.colors.primary, telefoniert: THEME.colors.warning, erledigt: THEME.colors.success };

  const handleGebuehrAendern = async (fallId, wert) => {
    setRuecklastschriftenOffen(liste => liste.map(r => r.fall_id === fallId ? { ...r, gebuehr: parseFloat(wert) || 0 } : r));
    const res = await fetch(`${API}/sepa/ruecklastschrift-fall/${fallId}/gebuehr`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ gebuehr: parseFloat(wert) || 0 }),
    });
    if (!res.ok) { alert("Gebühr konnte nicht gespeichert werden."); ladeRuecklastschriftenOffen(); }
  };

  const ladeEmailVorschau = async (fall, vorlageId) => {
    setEmailDialogLaedt(true);
    setEmailDialogVorlageId(vorlageId);
    const res = await fetch(`${API}/sepa/ruecklastschrift-fall/${fall.fall_id}/email-vorschau?vorlage_id=${vorlageId}`);
    const data = await res.json();
    setEmailDialogBetreff(data.betreff || "");
    setEmailDialogText(data.text || "");
    setEmailDialogEmpfaenger(data.empfaenger_email || null);
    setEmailDialogLaedt(false);
  };

  const oeffneEmailDialog = (fall) => {
    setAktionMenuOffenFuer(null);
    setEmailDialogFall(fall);
    if (emailVorlagen[0]) ladeEmailVorschau(fall, emailVorlagen[0].id);
  };

  const handleEmailSenden = async () => {
    const vorlage = emailVorlagen.find(v => v.id === emailDialogVorlageId);
    const mailto = `mailto:${encodeURIComponent(emailDialogEmpfaenger || "")}?subject=${encodeURIComponent(emailDialogBetreff)}&body=${encodeURIComponent(emailDialogText)}`;
    window.open(mailto, "_blank");
    const res = await fetch(`${API}/sepa/ruecklastschrift-fall/${emailDialogFall.fall_id}/email-versendet`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vorlage_name: vorlage?.name || "Individuelle Nachricht", betreff: emailDialogBetreff, text: emailDialogText }),
    });
    if (!res.ok) { alert("Versand konnte nicht dokumentiert werden."); return; }
    setEmailDialogFall(null);
    ladeRuecklastschriftenOffen();
  };

  const handleAnrufSpeichern = async () => {
    const res = await fetch(`${API}/sepa/ruecklastschrift-fall/${anrufDialogFall.fall_id}/anruf`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: anrufText || null }),
    });
    if (!res.ok) { alert("Telefonat konnte nicht gespeichert werden."); return; }
    setAnrufDialogFall(null);
    setAnrufText("");
    ladeRuecklastschriftenOffen();
  };

  const handleNotizSpeichern = async () => {
    if (!notizText.trim()) return;
    const res = await fetch(`${API}/sepa/ruecklastschrift-fall/${notizDialogFall.fall_id}/notiz`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: notizText }),
    });
    if (!res.ok) { alert("Notiz konnte nicht gespeichert werden."); return; }
    setNotizDialogFall(null);
    setNotizText("");
    ladeRuecklastschriftenOffen();
  };

  const handleFallErledigt = async (fall) => {
    setAktionMenuOffenFuer(null);
    if (!window.confirm("Als erledigt markieren? Wird dann bei der nächsten Abbuchung nicht mehr nachbelastet.")) return;
    const res = await fetch(`${API}/sepa/ruecklastschrift-fall/${fall.fall_id}/erledigt`, { method: "PATCH" });
    if (!res.ok) { alert("Konnte nicht als erledigt markiert werden."); return; }
    ladeRuecklastschriftenOffen();
    ladeVorschau();
  };

  const oeffneVerlauf = async (fall) => {
    setAktionMenuOffenFuer(null);
    setVerlaufDialogFall(fall);
    const res = await fetch(`${API}/sepa/ruecklastschrift-fall/${fall.fall_id}/verlauf`);
    setVerlaufEintraege(await res.json());
  };

  return (
    <div>
      {einreichenBestaetigung && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(440px, 95vw)" }}>
            <h2 style={s.modalTitel}>Lastschrift einreichen</h2>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.lg, marginBottom: 20 }}>Lauf vom <strong style={{ color: THEME.colors.text }}>{datumDE(abbuchungsdatum)}</strong> mit <strong style={{ color: THEME.colors.primary }}>{vorschau?.anzahl} Lastschriften</strong> und <strong style={{ color: THEME.colors.success }}>{vorschau?.gesamt?.toFixed(2)} €</strong> einreichen?</p>
            <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.base, marginBottom: 20 }}>⚠ Alle Mitglieder werden als "ausstehend" markiert.</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setEinreichenBestaetigung(false)} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleEinreichen} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>Ja, einreichen</button>
            </div>
          </div>
        </div>
      )}
      <h1 style={s.heading}>SEPA Lastschrift</h1>
      <div style={{ ...s.karte, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label style={s.label}>Abbuchungsdatum</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={() => navigiereMonat(-1)} style={{ ...s.btnGray, width: "auto", padding: "12px 14px" }}>◀</button>
              <input style={{ ...s.input, width: "auto", minWidth: "13ch" }} type="date" value={abbuchungsdatum} onChange={e => {
                const d = new Date(e.target.value);
                if (!isNaN(d)) setAbbuchungsdatum(e.target.value);
              }} onBlur={e => {
                const d = new Date(e.target.value);
                if (!isNaN(d)) {
                  const letzterTag = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
                  if (d.getDate() > letzterTag) {
                    d.setDate(letzterTag);
                    setAbbuchungsdatum(d.toISOString().split("T")[0]);
                  }
                }
              }} />
              <button onClick={() => navigiereMonat(1)} style={{ ...s.btnGray, width: "auto", padding: "12px 14px" }}>▶</button>
            </div>
          </div>
          <input ref={kontoauszugInputRef} type="file" accept=".csv,.CSV" style={{ display: "none" }} onChange={handleKontoauszugDatei} />
          {/* Alle Aktionen (ausser Datumsauswahl) hinter einem Button gebuendelt - genau wie das
              Filter-Menue bei Mitglieder, damit die Zeile auf dem Handy nicht mit selten
              gebrauchten Buttons vollgestopft ist. */}
          <div style={{ position: "relative" }}>
            <button onClick={() => setAktionenPanelOffen(o => !o)} style={{ ...s.btnGray, width: "auto", padding: "10px 20px", display: "flex", alignItems: "center", gap: 8 }}>
              ⚙ Aktionen{ruecklastschriftenOffen.length > 0 && ` (${ruecklastschriftenOffen.length})`}
            </button>
            {aktionenPanelOffen && (
              <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20, background: THEME.colors.surface, border: `1px solid ${THEME.colors.border}`, borderRadius: 10, padding: 16, width: 260, maxWidth: "calc(100vw - 32px)", boxShadow: "0 8px 24px rgba(0,0,0,0.4)", display: "flex", flexDirection: "column", gap: 10 }}>
                <button onClick={() => { ladeVorschau(); setAktionenPanelOffen(false); }} style={{ ...s.btnGray, width: "100%" }}>Aktualisieren</button>
                <button onClick={() => { generiereXML(); setAktionenPanelOffen(false); }} style={{ ...s.btn, width: "100%" }}>XML herunterladen</button>
                <button onClick={() => { generierePDF(); setAktionenPanelOffen(false); }} style={{ ...s.btn, width: "100%", background: THEME.colors.accentPurple }}>📄 Abschluss-PDF</button>
                <button onClick={() => { setEinreichenBestaetigung(true); setAktionenPanelOffen(false); }} style={{ ...s.btn, width: "100%", background: THEME.colors.success }}>Als eingereicht markieren</button>
                <button onClick={() => { kontoauszugInputRef.current.click(); setAktionenPanelOffen(false); }} disabled={kontoauszugLaedt} style={{ ...s.btnGray, width: "100%", background: THEME.colors.accentPurple }}>
                  {kontoauszugLaedt ? "Lese Datei..." : "📥 Kontoauszug importieren"}
                </button>
                <button onClick={() => { setRuecklastschriftenPanelOffen(o => !o); setAktionenPanelOffen(false); }} style={{ ...s.btn, width: "100%", background: ruecklastschriftenOffen.length > 0 ? THEME.colors.danger : THEME.colors.surfaceMuted }}>
                  🔴 Rücklastschrift {ruecklastschriftenOffen.length > 0 && `(${ruecklastschriftenOffen.length})`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {ruecklastschriftenPanelOffen && (
        <div style={{ ...s.karte, marginBottom: 20, borderLeftColor: THEME.colors.danger }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ color: THEME.colors.text, margin: 0 }}>🔴 Offene Rücklastschriften {ruecklastschriftenOffen.length > 0 && `(${ruecklastschriftenOffen.length})`}</h3>
            <button onClick={() => setRuecklastschriftenPanelOffen(false)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>✕ Schließen</button>
          </div>
          {ruecklastschriftenOffen.length === 0 ? (
            <p style={{ color: THEME.colors.success, margin: 0 }}>✓ Keine offenen Rücklastschriften – nichts wird nachbelastet.</p>
          ) : (
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: -8, marginBottom: 16 }}>
              Werden automatisch in die SEPA-Vorschau oben für das dort gewählte Abbuchungsdatum mit aufgenommen – kein separater Lauf nötig.
            </p>
          )}
          {ruecklastschriftenOffen.length === 0 ? null : mobil ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {ruecklastschriftenOffen.map(r => (
                <div key={r.fall_id} style={{ background: THEME.colors.background, borderRadius: 8, padding: "12px 16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <span onClick={() => onMitgliedOeffnen(r.mitglied_id)} style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.md, cursor: "pointer", textDecoration: "underline" }}>{r.mitglied_name}</span>
                    <span style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.md, fontWeight: 600, whiteSpace: "nowrap" }}>{r.offener_betrag.toFixed(2)} €</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 8 }}>
                    <button onClick={() => oeffneVerlauf(r)} style={{ background: "none", border: `1px solid ${STATUS_FARBE[r.status]}`, color: STATUS_FARBE[r.status], borderRadius: 6, padding: "3px 10px", fontSize: THEME.fontSize.xs, cursor: "pointer" }}>{STATUS_LABEL[r.status]}</button>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>Gebühr</span>
                      <input type="number" step="0.01" defaultValue={r.gebuehr} onBlur={e => handleGebuehrAendern(r.fall_id, e.target.value)}
                        style={{ ...s.input, width: 70, padding: "4px 8px", fontSize: THEME.fontSize.sm }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                    <button onClick={() => oeffneEmailDialog(r)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px" }}>📧 E-Mail</button>
                    <button onClick={() => { setAktionMenuOffenFuer(null); setAnrufDialogFall(r); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px" }}>📞 Anruf</button>
                    <button onClick={() => { setAktionMenuOffenFuer(null); setNotizDialogFall(r); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px" }}>📝 Notiz</button>
                    <button onClick={() => handleFallErledigt(r)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px", background: THEME.colors.success }}>✓ Erledigt</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: THEME.colors.surface, borderRadius: 12, minWidth: 700 }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 60px", padding: "14px 20px", background: THEME.colors.background, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase", borderRadius: "12px 12px 0 0" }}>
                  <span>Mitglied</span><span>Offener Betrag</span><span>Rücklastschriftgebühren</span><span>Status</span><span></span>
                </div>
                {ruecklastschriftenOffen.map(r => (
                  <div key={r.fall_id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 60px", padding: "14px 20px", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
                    <span onClick={() => onMitgliedOeffnen(r.mitglied_id)} style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.md, cursor: "pointer", textDecoration: "underline" }}>{r.mitglied_name}</span>
                    <span style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.md, fontWeight: 600 }}>{r.offener_betrag.toFixed(2)} €</span>
                    <input type="number" step="0.01" defaultValue={r.gebuehr} onBlur={e => handleGebuehrAendern(r.fall_id, e.target.value)}
                      style={{ ...s.input, width: 90, padding: "6px 10px", fontSize: THEME.fontSize.base }} />
                    <button onClick={() => oeffneVerlauf(r)} style={{ background: "none", border: `1px solid ${STATUS_FARBE[r.status]}`, color: STATUS_FARBE[r.status], borderRadius: 6, padding: "4px 10px", fontSize: THEME.fontSize.sm, cursor: "pointer", width: "fit-content" }}>{STATUS_LABEL[r.status]}</button>
                    <div style={{ position: "relative" }}>
                      <button onClick={() => setAktionMenuOffenFuer(o => o === r.fall_id ? null : r.fall_id)} style={{ background: "none", border: "none", color: THEME.colors.text, fontSize: THEME.fontSize.xl2, cursor: "pointer", padding: "4px 8px" }}>⋮</button>
                      {aktionMenuOffenFuer === r.fall_id && (
                        <div style={{ position: "absolute", top: "110%", right: 0, background: THEME.colors.surface, border: "1px solid #2b3d52", borderRadius: 8, padding: 8, zIndex: 20, minWidth: 200 }}>
                          {[
                            ["📧 E-Mail senden", () => oeffneEmailDialog(r)],
                            ["📞 Anruf dokumentieren", () => { setAktionMenuOffenFuer(null); setAnrufDialogFall(r); }],
                            ["📝 Notiz hinzufügen", () => { setAktionMenuOffenFuer(null); setNotizDialogFall(r); }],
                            ["✅ Als erledigt markieren", () => handleFallErledigt(r)],
                          ].map(([label, fn]) => (
                            <div key={label} onClick={fn} style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, padding: "8px 10px", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap" }}
                              onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              {label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {emailDialogFall && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(640px, 95vw)" }}>
            <h2 style={s.modalTitel}>E-Mail senden – {emailDialogFall.mitglied_name}</h2>
            <div style={s.inputGroup}>
              <label style={s.label}>Vorlage</label>
              <select style={s.input} value={emailDialogVorlageId || ""} onChange={e => ladeEmailVorschau(emailDialogFall, parseInt(e.target.value))}>
                {emailVorlagen.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Betreff</label>
              <input style={s.input} value={emailDialogBetreff} onChange={e => setEmailDialogBetreff(e.target.value)} disabled={emailDialogLaedt} />
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Text</label>
              <textarea style={{ ...s.input, height: 280, fontFamily: "inherit", whiteSpace: "pre-wrap" }} value={emailDialogText} onChange={e => setEmailDialogText(e.target.value)} disabled={emailDialogLaedt} />
            </div>
            {!emailDialogEmpfaenger && (
              <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, marginBottom: 12 }}>⚠ Für dieses Mitglied ist keine E-Mail-Adresse hinterlegt – bitte Empfänger manuell im Mail-Programm ergänzen.</p>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setEmailDialogFall(null)} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleEmailSenden} disabled={emailDialogLaedt} style={{ ...s.btn, width: "auto" }}>📧 Senden (Outlook öffnen)</button>
            </div>
          </div>
        </div>
      )}

      {anrufDialogFall && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(440px, 95vw)" }}>
            <h2 style={s.modalTitel}>Anruf dokumentieren – {anrufDialogFall.mitglied_name}</h2>
            <div style={s.inputGroup}>
              <label style={s.label}>Notiz (optional)</label>
              <textarea style={{ ...s.input, height: 100 }} value={anrufText} onChange={e => setAnrufText(e.target.value)} placeholder="z.B. Überweist morgen." />
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setAnrufDialogFall(null); setAnrufText(""); }} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleAnrufSpeichern} style={{ ...s.btn, width: "auto" }}>📞 Speichern</button>
            </div>
          </div>
        </div>
      )}

      {notizDialogFall && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(440px, 95vw)" }}>
            <h2 style={s.modalTitel}>Notiz hinzufügen – {notizDialogFall.mitglied_name}</h2>
            <div style={s.inputGroup}>
              <label style={s.label}>Notiz</label>
              <textarea style={{ ...s.input, height: 100 }} value={notizText} onChange={e => setNotizText(e.target.value)} placeholder="Interne Notiz..." />
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setNotizDialogFall(null); setNotizText(""); }} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleNotizSpeichern} style={{ ...s.btn, width: "auto" }}>📝 Speichern</button>
            </div>
          </div>
        </div>
      )}

      {verlaufDialogFall && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(560px, 95vw)" }}>
            <h2 style={s.modalTitel}>Verlauf – {verlaufDialogFall.mitglied_name}</h2>
            {verlaufEintraege.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Einträge.</p>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {verlaufEintraege.map(e => (
                <div key={e.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>
                    {e.typ === "email" ? "📧" : e.typ === "anruf" ? "📞" : "📝"} {e.erstellt_am}
                    {e.vorlage_name && ` · ${e.vorlage_name}`}
                  </div>
                  {e.text && <div style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, marginTop: 4, whiteSpace: "pre-wrap" }}>{e.text}</div>}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
              <button onClick={() => setVerlaufDialogFall(null)} style={s.btnGray}>Schließen</button>
            </div>
          </div>
        </div>
      )}

      {kontoauszugErgebnis && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(980px, 95vw)", maxHeight: "90vh", overflowY: "auto" }}>
            <h2 style={s.modalTitel}>Rücklastschriften aus Kontoauszug</h2>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: 16 }}>
              Nur "LS RUECKBELASTUNG"-Buchungen wurden ausgewertet. Nichts wird übernommen, bevor du unten bestätigst.
            </p>

            {kontoauszugErgebnis.zugeordnet.length > 0 ? (
              <>
                <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 8 }}>Zugeordnet ({kontoauszugErgebnis.zugeordnet.length})</h3>
                <div style={{ maxHeight: 420, overflowY: "auto", marginBottom: 20 }}>
                  {kontoauszugErgebnis.zugeordnet.map((z, i) => (
                    <label key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid #2b3d52", cursor: "pointer" }}>
                      <input type="checkbox" checked={!!kontoauszugAusgewaehlt[i]}
                        onChange={e => setKontoauszugAusgewaehlt(a => ({ ...a, [i]: e.target.checked }))} />
                      <div style={{ flex: 1 }}>
                        <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{z.mitglied_name}</span>
                        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginLeft: 8 }}>{z.buchungstag} · {z.grund}</span>
                        {z.neu && <span style={{ color: THEME.colors.accentPurple, fontSize: THEME.fontSize.xxs, marginLeft: 8, border: "1px solid #a78bfa", borderRadius: 4, padding: "1px 5px" }}>neuer Posten</span>}
                        {z.wiederholter_bounce && <span style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xxs, marginLeft: 8, border: `1px solid ${THEME.colors.warning}`, borderRadius: 4, padding: "1px 5px" }}>wiederholter Bounce · bestehender Fall</span>}
                      </div>
                      <span style={{ color: THEME.colors.danger, fontWeight: 600 }}>{parseFloat(z.zahlung_betrag).toFixed(2)} €</span>
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ color: THEME.colors.textMuted, marginBottom: 20 }}>Keine Rücklastschriften in der Datei gefunden.</p>
            )}

            {(() => {
              const alle = kontoauszugErgebnis.nicht_zugeordnet;
              const zuPruefen = alle.filter(z => !z.bereits_erfasst);
              const bereitsErfasst = alle.filter(z => z.bereits_erfasst);
              return (
                <>
                  {zuPruefen.length > 0 && (
                    <>
                      <h3 style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.lg, marginBottom: 8 }}>Nicht zugeordnet ({zuPruefen.length}) – bitte manuell prüfen</h3>
                      <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 20 }}>
                        {zuPruefen.map((z, i) => (
                          <div key={i} style={{ padding: "8px 0", borderTop: "1px solid #2b3d52" }}>
                            {z.mitglied_id ? (
                              <span onClick={() => onMitgliedOeffnen(z.mitglied_id)} style={{ color: THEME.colors.primary, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>{z.name || "(unbekannt)"}</span>
                            ) : (
                              <span style={{ color: THEME.colors.text }}>{z.name || "(unbekannt)"}</span>
                            )}
                            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginLeft: 8 }}>{z.buchungstag} · {z.betrag} €</span>
                            <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, margin: "2px 0 0" }}>{z.fehler}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {bereitsErfasst.length > 0 && (
                    <details style={{ marginBottom: 20 }}>
                      <summary style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, cursor: "pointer" }}>
                        Bereits erfasst ({bereitsErfasst.length}) – schon als Rücklastschrift verbucht, keine Aktion nötig
                      </summary>
                      <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8 }}>
                        {bereitsErfasst.map((z, i) => (
                          <div key={i} style={{ padding: "6px 0", borderTop: "1px solid #2b3d52" }}>
                            <span style={{ color: THEME.colors.textMuted }}>{z.name || "(unbekannt)"}</span>
                            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginLeft: 8 }}>{z.buchungstag} · {z.betrag} €</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              );
            })()}

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setKontoauszugErgebnis(null)} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleKontoauszugUebernehmen} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>
                Ausgewählte als Rücklastschrift übernehmen
              </button>
            </div>
          </div>
        </div>
      )}
      {vorschau && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
            <div style={s.karte}><p style={s.karteLabel}>Anzahl</p><p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{vorschau.anzahl}</p></div>
            <div style={s.karte}><p style={s.karteLabel}>Gesamtbetrag</p><p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{vorschau.gesamt?.toFixed(2)} €</p></div>
            <div style={s.karte}><p style={s.karteLabel}>Abbuchungsdatum</p><p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl2, fontWeight: 700, margin: "8px 0 0" }}>{datumDE(abbuchungsdatum)}</p></div>
          </div>
          {/* Suche + Sortierung */}
          <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "center" }}>
            <input style={{ ...s.input, flex: 1, maxWidth: 300 }} placeholder="Suche nach Name oder IBAN..." value={sepaFilter} onChange={e => setSepaFilter(e.target.value)} />
            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
              {(() => {
                const gefiltert = (vorschau.positionen || []).filter(p => !sepaFilter || p.name?.toLowerCase().includes(sepaFilter.toLowerCase()) || p.iban?.includes(sepaFilter));
                return `${gefiltert.length} von ${vorschau.positionen?.length || 0} Einträgen`;
              })()}
            </span>
          </div>

          {(() => {
            const alleSpalten = [
              { key: "erstbeitrag", label: "Erstbeitrag", zeigen: p => p.erstbeitrag > 0 },
              { key: "einmalzahlung", label: "Einmalig", zeigen: p => p.einmalzahlung > 0 },
              { key: "ruecklastschrift", label: "Rückl.", zeigen: p => p.ruecklastschrift > 0 },
            ];
            const optionaleSpalten = mobil ? [] : alleSpalten.filter(s => (vorschau.positionen || []).some(s.zeigen));
            const spalten = mobil
              ? [["name","Name"], ["gesamt","Gesamt"]]
              : [["name","Name"],["iban","IBAN"],["beitrag","Beitrag"], ...optionaleSpalten.map(s => [s.key, s.label]), ["gesamt","Gesamt"]];
            const gridCols = (mobil ? "2fr 1fr" : `2fr 2fr ${"1fr ".repeat(spalten.length - 3)}1fr`) + " 36px";

            const zellenFarbe = { erstbeitrag: THEME.colors.warning, einmalzahlung: THEME.colors.accentPurple, ruecklastschrift: THEME.colors.danger };

            let pos = (vorschau.positionen || []).filter(p => !sepaFilter || p.name?.toLowerCase().includes(sepaFilter.toLowerCase()) || p.iban?.includes(sepaFilter));
            if (sepaSortSpalte) {
              pos = [...pos].sort((a, b) => {
                const va = typeof a[sepaSortSpalte] === "string" ? a[sepaSortSpalte].toLowerCase() : (a[sepaSortSpalte] || 0);
                const vb = typeof b[sepaSortSpalte] === "string" ? b[sepaSortSpalte].toLowerCase() : (b[sepaSortSpalte] || 0);
                return sepaSortRichtung === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
              });
            }

            return (
              <div style={{ overflowX: "auto", marginBottom: 32 }}>
                <div style={{ ...s.tabelle, minWidth: mobil ? "auto" : 500 + optionaleSpalten.length * 110 }}>
                  <div style={{ display: "grid", gridTemplateColumns: gridCols, padding: "14px 20px", background: THEME.colors.background, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>
                    {spalten.map(([key, label]) => (
                      <span key={key} onClick={() => { if (sepaSortSpalte === key) setSepaSortRichtung(r => r === "asc" ? "desc" : "asc"); else { setSepaSortSpalte(key); setSepaSortRichtung("asc"); } }}
                        style={{ cursor: "pointer", userSelect: "none" }}>
                        {label} {sepaSortSpalte === key ? (sepaSortRichtung === "asc" ? "↑" : "↓") : "↕"}
                      </span>
                    ))}
                    <span></span>
                  </div>
                  {pos.map((p, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: gridCols, padding: "14px 20px", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
                      <span onClick={() => onMitgliedOeffnen(p.id)} style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.md, cursor: "pointer", textDecoration: "underline" }}>{p.name}</span>
                      {!mobil && <>
                        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{p.iban}</span>
                        <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{p.beitrag.toFixed(2)} €</span>
                        {optionaleSpalten.map(s => (
                          <span key={s.key} title={s.key === "einmalzahlung" ? p.einmalzahlung_bez : undefined}
                            style={{ color: p[s.key] > 0 ? zellenFarbe[s.key] : THEME.colors.textMuted, fontSize: THEME.fontSize.md }}>
                            {p[s.key] > 0 ? `${p[s.key].toFixed(2)} €` : "–"}
                          </span>
                        ))}
                      </>}
                      <span style={{ color: THEME.colors.success, fontSize: THEME.fontSize.md, fontWeight: 600 }}>{p.gesamt.toFixed(2)} €</span>
                      {p.ruecklastschrift_fall_id ? (
                        <span onClick={() => handleFallErledigt({ fall_id: p.ruecklastschrift_fall_id })} title="Rücklastschrift als erledigt markieren (z.B. per Überweisung ausgeglichen) – wird bei der nächsten Abbuchung nicht mehr nachbelastet"
                          style={{ color: THEME.colors.textMuted, cursor: "pointer", textAlign: "center", fontWeight: 700 }}>✗</span>
                      ) : <span></span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {laeufe.length > 0 && (
        <>
          <h2 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl2, marginBottom: 16 }}>SEPA-Verlauf</h2>
          <div style={{ overflowX: "auto" }}>
            <div style={{ ...s.tabelle, minWidth: 600 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 90px 120px 70px", padding: "14px 20px", background: THEME.colors.background, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>
                <span>Lauf</span><span>Eingereicht</span><span>Abbuchung</span><span>Anzahl</span><span>Betrag</span><span>Rückl.</span><span></span><span></span><span></span>
              </div>
              {laeufe.map(l => (
                <Fragment key={l.id}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 90px 120px 70px", padding: "14px 20px", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
                    <span style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.md, fontWeight: 600 }}>#{l.lauf_nr}</span>
                    <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{datumDE(l.datum)}</span>
                    <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{datumDE(l.abbuchungsdatum)}</span>
                    <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{l.anzahl}</span>
                    <span style={{ color: THEME.colors.success, fontSize: THEME.fontSize.md }}>{parseFloat(l.betrag).toFixed(2)} €</span>
                    <span style={{ color: l.offene_posten > 0 ? THEME.colors.danger : THEME.colors.success, fontSize: THEME.fontSize.md, fontWeight: 600 }}>{l.offene_posten > 0 ? `${l.offene_posten} RL` : "–"}</span>
                    <button onClick={() => handleLaufDetailToggle(l.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px" }}>{laufDetailOffenFuer === l.id ? "▲ Details" : "▼ Details"}</button>
                    <button onClick={() => handleAlleBezahlt(l.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px", background: THEME.colors.success }}>Alle bezahlt</button>
                    <button onClick={() => window.open(`${API}/sepa/lauf/${l.id}/abschluss-pdf`, "_blank")} title="Abschluss-PDF für die Buchhaltung" style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 10px" }}>📄 PDF</button>
                  </div>
                  {laufDetailOffenFuer === l.id && (
                    <div style={{ borderTop: "1px solid #2b3d52", background: THEME.colors.background, padding: "12px 20px 16px" }}>
                      {laufMitgliederLaedt ? (
                        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Lädt...</span>
                      ) : laufMitglieder.length === 0 ? (
                        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Keine Mitglieder in diesem Lauf.</span>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {laufMitglieder.map(m => (
                            <div key={m.zahlung_id} style={{ display: "grid", gridTemplateColumns: "1fr 140px 100px 120px 90px", alignItems: "center", gap: 8, padding: "6px 10px", background: THEME.colors.card, borderRadius: 6 }}>
                              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm }}>{m.name}</span>
                              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>Mandat: {m.mandats_id ?? "–"}</span>
                              <span style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm }}>{parseFloat(m.betrag).toFixed(2)} €</span>
                              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{m.status}</span>
                              <button onClick={() => handleAusLaufEntfernen(l.id, m.mitglied_id, m.name)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 8px", background: THEME.colors.danger }}>✗ Entfernen</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </>
      )}
      {ladend && <p style={{ color: THEME.colors.textMuted, padding: 20 }}>Lade...</p>}
    </div>
  );
}

// ============ STATISTIKEN ============

function ExportDialog({ buchJahr }) {
  const [offen, setOffen] = useState(false);
  const [format, setFormat] = useState("pdf");
  const [nurJahr, setNurJahr] = useState(false);
  const [optionen, setOptionen] = useState({
    jahresuebersicht: true,
    monatsuebersicht: true,
    tarife: true,
  });

  const handleExport = () => {
    const params = new URLSearchParams({
      format,
      jahresuebersicht: optionen.jahresuebersicht,
      monatsuebersicht: optionen.monatsuebersicht,
      tarife: optionen.tarife,
    });
    if (nurJahr && buchJahr) params.append("jahr", buchJahr);
    window.open(`${API}/statistiken/buchhaltung/export?${params}`, "_blank");
    setOffen(false);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <button onClick={() => setOffen(!offen)} style={{ ...s.btn, width: "auto", background: THEME.colors.accentPurple }}>
        📥 Exportieren
      </button>

      {offen && (
        <div style={{ ...s.karte, marginTop: 12, borderLeftColor: THEME.colors.accentPurple }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Export-Optionen</h3>

          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: 8 }}>Was möchtest du exportieren?</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {[["jahresuebersicht", "Jahresübersicht"], ["monatsuebersicht", "Monatsübersicht"], ["tarife", "Tarif-Aufschlüsselung"]].map(([key, label]) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.text, cursor: "pointer", fontSize: THEME.fontSize.md }}>
                <input type="checkbox" checked={optionen[key]} onChange={e => setOptionen(o => ({ ...o, [key]: e.target.checked }))} />
                {label}
              </label>
            ))}
          </div>

          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: 8 }}>Einmalzahlungen:</p>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <button onClick={() => { const p = new URLSearchParams({ format: "csv_einmal" }); if (nurJahr && buchJahr) p.append("jahr", buchJahr); window.open(`${API}/statistiken/buchhaltung/export?${p}`, "_blank"); }}
              style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.base }}>📊 Einmalzahlungen CSV</button>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.warning, cursor: "pointer", fontSize: THEME.fontSize.base, marginBottom: 16 }}>
            <input type="checkbox" checked={nurJahr} onChange={e => setNurJahr(e.target.checked)} />
            Nur ausgewähltes Jahr ({buchJahr})
          </label>

          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: 8 }}>Format:</p>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[["pdf", "📄 PDF"], ["csv", "📊 CSV (Excel)"]].map(([val, label]) => (
              <button key={val} onClick={() => setFormat(val)}
                style={{ ...s.btnGray, width: "auto", padding: "8px 16px", background: format === val ? THEME.colors.primary : THEME.colors.surfaceMuted, fontSize: THEME.fontSize.base }}>
                {label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={() => setOffen(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
            <button onClick={handleExport} style={{ ...s.btn, width: "auto", background: THEME.colors.accentPurple }}>Exportieren</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatistikenSeite() {
  const [stats, setStats] = useState(null);
  const [monateAnzahl, setMonateAnzahl] = useState(12);
  const [aktiveTab, setAktiveTab] = useState("uebersicht");
  const [buch, setBuch] = useState(null);
  const [buchJahr, setBuchJahr] = useState(null);
  const heute = new Date();
  const [neuzugangJahr, setNeuzugangJahr] = useState(heute.getFullYear());
  const [neuzugangMonat, setNeuzugangMonat] = useState(heute.getMonth() + 1);
  const [neuzugaenge, setNeuzugaenge] = useState(null);
  const [abgangJahr, setAbgangJahr] = useState(heute.getFullYear());
  const [abgangMonat, setAbgangMonat] = useState(heute.getMonth() + 1);
  const [abgaenge, setAbgaenge] = useState(null);
  const [pausierteJahr, setPausierteJahr] = useState(heute.getFullYear());
  const [pausierteMonat, setPausierteMonat] = useState(heute.getMonth() + 1);
  const [pausierteDaten, setPauisierteDaten] = useState(null);
  const [analyse, setAnalyse] = useState(null);

  useEffect(() => {
    if (aktiveTab === "analyse" && !analyse) {
      fetch(`${API}/statistiken/mitgliederanalyse`).then(r => r.ok ? r.json() : null).then(d => d && setAnalyse(d)).catch(() => {});
    }
  }, [aktiveTab]);

  useEffect(() => {
    if (aktiveTab === "neuzugaenge") {
      setNeuzugaenge(null);
      // r.ok bewusst geprueft: eine abgelaufene Sitzung liefert 401 mit einem {detail:"..."}-Body,
      // der ohne diese Pruefung als "gueltige" Antwort durchging (r.json() wirft bei einem
      // Fehler-Body ja nicht) und beim Rendern (neuzugaenge.umsatz_zugewinn.toFixed(2)) mit einer
      // TypeError abstuerzte, ohne dass die Seite je aus "Lade..." herauskam - der globale
      // 401-Handler (siehe fetch-Patch oben) haette stattdessen sauber zum Login geleitet.
      fetch(`${API}/statistiken/neuzugaenge?jahr=${neuzugangJahr}&monat=${neuzugangMonat}`)
        .then(r => r.ok ? r.json() : null).then(d => d && setNeuzugaenge(d)).catch(() => {});
    }
  }, [aktiveTab, neuzugangJahr, neuzugangMonat]);

  useEffect(() => {
    if (aktiveTab === "abgaenge") {
      setAbgaenge(null);
      fetch(`${API}/statistiken/abgaenge?jahr=${abgangJahr}&monat=${abgangMonat}`)
        .then(r => r.ok ? r.json() : null).then(d => d && setAbgaenge(d)).catch(() => {});
    }
  }, [aktiveTab, abgangJahr, abgangMonat]);

  useEffect(() => {
    if (aktiveTab === "pausierte") {
      setPauisierteDaten(null);
      fetch(`${API}/statistiken/pausierte?jahr=${pausierteJahr}&monat=${pausierteMonat}`)
        .then(r => r.ok ? r.json() : null).then(d => d && setPauisierteDaten(d)).catch(() => {});
    }
  }, [aktiveTab, pausierteJahr, pausierteMonat]);

  useEffect(() => {
    fetch(`${API}/statistiken/uebersicht?monate_anzahl=${monateAnzahl}`).then(r => r.json()).then(setStats).catch(() => {});
  }, [monateAnzahl]);

  useEffect(() => {
    if (aktiveTab === "buchhaltung" && !buch) {
      fetch(`${API}/statistiken/buchhaltung`).then(r => r.json()).then(data => {
        setBuch(data);
        if (data.jahre.length > 0) setBuchJahr(data.jahre[0].jahr);
      }).catch(() => {});
    }
  }, [aktiveTab]);
  if (!stats) return <div style={{ color: THEME.colors.textMuted, padding: 40, textAlign: "center" }}>Lade Statistiken...</div>;
  const FARBEN = [THEME.colors.primary, THEME.colors.success, THEME.colors.warning, THEME.colors.danger, THEME.colors.accentPurple, THEME.colors.accentOrange, "#06b6d4"];
  const tarifDaten = Object.entries(stats.tarife).sort((a,b) => b[1]-a[1]);
  const maxTarif = Math.max(...tarifDaten.map(([,v]) => v));

  // Buchhaltung Tab
  const buchMonate = buch ? buch.monate.filter(m => m.jahr === buchJahr) : [];
  const buchTarife = buch ? buch.tarife.filter(t => t.jahr === buchJahr) : [];

  const MONATSNAMEN = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  const monatLabel = (monatStr) => { const [m, j] = monatStr.split("/"); return `${MONATSNAMEN[parseInt(m, 10) - 1]} ${j}`; };
  const monateAbsteigend = [...stats.monate].reverse();

  const MonatsVerlaufKarte = ({ feld, ueberschrift, farbe }) => (
    <div style={{ ...s.karte, marginTop: 24 }}>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.lg }}>{ueberschrift}</h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: THEME.colors.background }}>
              {["Monat", "Anzahl"].map(h => (
                <th key={h} style={{ padding: "10px 16px", textAlign: h === "Monat" ? "left" : "right", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monateAbsteigend.map(m => (
              <tr key={m.monat} style={{ borderTop: "1px solid #2b3d52" }}>
                <td style={{ padding: "10px 16px", color: THEME.colors.textSecondary }}>{monatLabel(m.monat)}</td>
                <td style={{ padding: "10px 16px", textAlign: "right", color: farbe, fontWeight: 700 }}>{m[feld]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <h1 style={s.heading}>Statistiken</h1>
      <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
        {[["uebersicht", "Übersicht"], ["buchhaltung", "Buchhaltung"], ["neuzugaenge", "Neuzugänge"], ["abgaenge", "Abgänge"], ["pausierte", "Pausierte"], ["analyse", "Mitgliederanalyse"]].map(([key, label]) => (
          <button key={key} onClick={() => setAktiveTab(key)} style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: aktiveTab === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: aktiveTab === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md, flexShrink: 0 }}>{label}</button>
        ))}
      </div>

      {aktiveTab === "buchhaltung" && buch && (
        <div>
          {/* Gesamtübersicht */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
            <div style={{ ...s.karte, borderLeftColor: THEME.colors.success }}>
              <p style={s.karteLabel}>Gesamtumsatz (alle Jahre)</p>
              <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xxl2, fontWeight: 700, margin: "8px 0 0" }}>{buch.gesamt_umsatz.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
            </div>
            <div style={{ ...s.karte, borderLeftColor: THEME.colors.primary }}>
              <p style={s.karteLabel}>Gesamtbuchungen</p>
              <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxl2, fontWeight: 700, margin: "8px 0 0" }}>{buch.gesamt_buchungen}</p>
            </div>
            <div style={{ ...s.karte, borderLeftColor: THEME.colors.warning }}>
              <p style={s.karteLabel}>Einmalzahlungen gesamt</p>
              <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xxl2, fontWeight: 700, margin: "8px 0 0" }}>{(buch.gesamt_einmal || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
            </div>
          </div>

          {/* Export Dialog */}
          <ExportDialog buchJahr={buchJahr} />

          {/* Jahresübersicht */}
          <div style={{ ...s.karte, marginBottom: 16 }}>
            <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Jahresübersicht</h3>
            <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: THEME.colors.background }}>
                  {["Jahr", "Buchungen", "Umsatz", "Rücklastschriften", "RL-Betrag"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: h === "Jahr" ? "left" : "right", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {buch.jahre.map(j => (
                  <tr key={j.jahr} style={{ borderTop: "1px solid #2b3d52", cursor: "pointer", background: buchJahr === j.jahr ? "#1f2f42" : "transparent" }}
                    onClick={() => setBuchJahr(j.jahr)}>
                    <td style={{ padding: "12px 16px", color: THEME.colors.primary, fontWeight: 700 }}>{j.jahr}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.text }}>{j.buchungen}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.success, fontWeight: 700 }}>{j.umsatz.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: j.ruecklastschriften > 0 ? THEME.colors.danger : THEME.colors.textMuted }}>{j.ruecklastschriften > 0 ? j.ruecklastschriften : "–"}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: j.rl_betrag > 0 ? THEME.colors.danger : THEME.colors.textMuted }}>{j.rl_betrag > 0 ? `${j.rl_betrag.toFixed(2)} €` : "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* Einmalzahlungen Jahresübersicht */}
          {buch.einmal_jahre && buch.einmal_jahre.length > 0 && (
            <div style={{ ...s.karte, marginBottom: 16 }}>
              <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Einmalzahlungen nach Jahr</h3>
              <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: THEME.colors.background }}>
                    {["Jahr", "Anzahl", "Gesamt"].map(h => (
                      <th key={h} style={{ padding: "12px 16px", textAlign: h === "Jahr" ? "left" : "right", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {buch.einmal_jahre.map(j => (
                    <tr key={j.jahr} style={{ borderTop: "1px solid #2b3d52" }}>
                      <td style={{ padding: "12px 16px", color: THEME.colors.warning, fontWeight: 700 }}>{j.jahr}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.text }}>{j.anzahl}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.warning, fontWeight: 700 }}>{j.gesamt.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "2px solid #f59e0b", background: THEME.colors.background }}>
                    <td style={{ padding: "12px 16px", color: THEME.colors.text, fontWeight: 700 }}>Gesamt</td>
                    <td style={{ padding: "12px 16px" }}></td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.warning, fontWeight: 700 }}>{buch.gesamt_einmal.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Detail für gewähltes Jahr */}
          {buchJahr && (
            <>
              <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl2, margin: "0 0 16px" }}>Details {buchJahr}</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                {/* Tarif-Aufschlüsselung */}
                <div style={s.karte}>
                  <h4 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.lg }}>Umsatz nach Tarif</h4>
                  {buchTarife.map(t => (
                    <div key={t.tarif} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid #2b3d52" }}>
                      <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>{t.tarif || "Unbekannt"}</span>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ color: THEME.colors.success, fontWeight: 600 }}>{t.umsatz.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginLeft: 8 }}>({t.buchungen}x)</span>
                      </div>
                    </div>
                  ))}
                  <div style={{ borderTop: "2px solid #2b8cc4", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: THEME.colors.text, fontWeight: 700 }}>Gesamt</span>
                    <span style={{ color: THEME.colors.success, fontWeight: 700 }}>{buchTarife.reduce((s,t) => s + t.umsatz, 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                  </div>
                </div>

                {/* Monatsübersicht */}
                <div style={s.karte}>
                  <h4 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.lg }}>Monate {buchJahr}</h4>
                  <div style={{ overflowY: "auto", maxHeight: 300 }}>
                    {buchMonate.map(m => (
                      <div key={m.monat} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
                        <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>{m.monat_str}</span>
                        <div style={{ textAlign: "right" }}>
                          <span style={{ color: THEME.colors.success, fontWeight: 600 }}>{m.umsatz.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                          {m.ruecklastschriften > 0 && <span style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.xs, marginLeft: 8 }}>{m.ruecklastschriften} RL</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {aktiveTab === "buchhaltung" && !buch && <div style={{ color: THEME.colors.textMuted, padding: 40, textAlign: "center" }}>Lade Buchhaltungsdaten...</div>}

      {aktiveTab === "neuzugaenge" && (
        <div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
            <div style={s.inputGroup}>
              <label style={s.label}>Monat</label>
              <select style={{ ...s.input, width: 140 }} value={neuzugangMonat} onChange={e => setNeuzugangMonat(parseInt(e.target.value))}>
                {["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"].map((m, i) => (
                  <option key={i} value={i+1}>{m}</option>
                ))}
              </select>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Jahr</label>
              <select style={{ ...s.input, width: 100 }} value={neuzugangJahr} onChange={e => setNeuzugangJahr(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(j => <option key={j}>{j}</option>)}
              </select>
            </div>
          </div>

          {!neuzugaenge && <p style={{ color: THEME.colors.textMuted }}>Lade...</p>}

          {neuzugaenge && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.success }}>
                  <p style={s.karteLabel}>Neuzugänge diesen Monat</p>
                  <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{neuzugaenge.anzahl}</p>
                </div>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.primary }}>
                  <p style={s.karteLabel}>Umsatzzuwachs / Monat</p>
                  <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{neuzugaenge.umsatz_zugewinn.toFixed(2)} €</p>
                </div>
              </div>

              {neuzugaenge.neuzugaenge.length === 0 && (
                <div style={{ ...s.karte, textAlign: "center", padding: 40 }}>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>Keine Neuzugänge in diesem Monat.</p>
                </div>
              )}

              {neuzugaenge.neuzugaenge.length > 0 && (
                <div style={s.karte}>
                  <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: THEME.colors.background }}>
                        {["Name", "Tarif", "Beitrag", "Mitglied seit", "Status"].map(h => (
                          <th key={h} style={{ padding: "12px 16px", textAlign: "left", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {neuzugaenge.neuzugaenge.map((n, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #2b3d52" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#1f2f42"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text, fontWeight: 600 }}>{n.name}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.primary }}>{n.tarif}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.success }}>{n.preis.toFixed(2)} €</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary }}>{datumDE(n.vertragsbeginn)}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textMuted }}>{n.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              <MonatsVerlaufKarte feld="neuzugaenge" ueberschrift="Neuzugänge im Verlauf" farbe={THEME.colors.success} />
            </>
          )}
        </div>
      )}

      {aktiveTab === "abgaenge" && (
        <div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
            <div style={s.inputGroup}>
              <label style={s.label}>Monat</label>
              <select style={{ ...s.input, width: 140 }} value={abgangMonat} onChange={e => setAbgangMonat(parseInt(e.target.value))}>
                {["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"].map((m, i) => (
                  <option key={i} value={i+1}>{m}</option>
                ))}
              </select>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Jahr</label>
              <select style={{ ...s.input, width: 100 }} value={abgangJahr} onChange={e => setAbgangJahr(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(j => <option key={j}>{j}</option>)}
              </select>
            </div>
          </div>

          {!abgaenge && <p style={{ color: THEME.colors.textMuted }}>Lade...</p>}

          {abgaenge && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.danger }}>
                  <p style={s.karteLabel}>Abgänge diesen Monat</p>
                  <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{abgaenge.anzahl}</p>
                </div>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.accentOrange }}>
                  <p style={s.karteLabel}>Umsatzverlust / Monat</p>
                  <p style={{ color: THEME.colors.accentOrange, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{abgaenge.umsatz_verlust.toFixed(2)} €</p>
                </div>
              </div>

              {abgaenge.abgaenge.length === 0 && (
                <div style={{ ...s.karte, textAlign: "center", padding: 40 }}>
                  <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xl }}>🎉 Keine Abgänge in diesem Monat!</p>
                </div>
              )}

              {abgaenge.abgaenge.length > 0 && (
                <div style={s.karte}>
                  <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: THEME.colors.background }}>
                        {["Name", "Tarif", "Beitrag", "Mitglied seit", "Gekündigt zum", "Dabei"].map(h => (
                          <th key={h} style={{ padding: "12px 16px", textAlign: "left", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {abgaenge.abgaenge.map((a, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #2b3d52" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#1f2f42"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text, fontWeight: 600 }}>{a.name}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.primary }}>{a.tarif}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.accentOrange }}>{a.preis.toFixed(2)} €</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary }}>{datumDE(a.vertragsbeginn)}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.danger }}>{datumDE(a.kuendigung_zum)}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textMuted }}>
                            {a.monate_dabei >= 12 ? `${a.jahre_dabei} Jahre` : `${a.monate_dabei} Monate`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              <MonatsVerlaufKarte feld="kuendigungen" ueberschrift="Abgänge im Verlauf" farbe={THEME.colors.danger} />
            </>
          )}
        </div>
      )}

      {aktiveTab === "pausierte" && (
        <div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
            <div style={s.inputGroup}>
              <label style={s.label}>Monat</label>
              <select style={{ ...s.input, width: 140 }} value={pausierteMonat} onChange={e => setPausierteMonat(parseInt(e.target.value))}>
                {["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"].map((m, i) => (
                  <option key={i} value={i+1}>{m}</option>
                ))}
              </select>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Jahr</label>
              <select style={{ ...s.input, width: 100 }} value={pausierteJahr} onChange={e => setPausierteJahr(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(j => <option key={j}>{j}</option>)}
              </select>
            </div>
          </div>

          {!pausierteDaten && <p style={{ color: THEME.colors.textMuted }}>Lade...</p>}

          {pausierteDaten && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.warning }}>
                  <p style={s.karteLabel}>Pausierte diesen Monat</p>
                  <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{pausierteDaten.anzahl}</p>
                </div>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.warning }}>
                  <p style={s.karteLabel}>Noch aktuell pausiert</p>
                  <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{pausierteDaten.aktuell_noch_pausiert}</p>
                </div>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.accentOrange }}>
                  <p style={s.karteLabel}>Umsatzausfall / Monat</p>
                  <p style={{ color: THEME.colors.accentOrange, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{pausierteDaten.umsatz_verlust.toFixed(2)} €</p>
                </div>
              </div>

              {pausierteDaten.pausierte.length === 0 && (
                <div style={{ ...s.karte, textAlign: "center", padding: 40 }}>
                  <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xl }}>🎉 Keine Pausen in diesem Monat!</p>
                </div>
              )}

              {pausierteDaten.pausierte.length > 0 && (
                <div style={s.karte}>
                  <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: THEME.colors.background }}>
                        {["Name", "Tarif", "Beitrag", "Pausiert von", "Pausiert bis", "Dauer", "Noch pausiert"].map(h => (
                          <th key={h} style={{ padding: "12px 16px", textAlign: "left", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pausierteDaten.pausierte.map((p, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #2b3d52" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#1f2f42"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text, fontWeight: 600 }}>{p.name}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.primary }}>{p.tarif}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.accentOrange }}>{p.preis.toFixed(2)} €</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary }}>{datumDE(p.start_datum)}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary }}>{datumDE(p.end_datum)}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.warning }}>{p.monate_gesamt} Monat{p.monate_gesamt > 1 ? "e" : ""}</td>
                          <td style={{ padding: "12px 16px" }}>
                            {p.noch_aktiv
                              ? <span style={{ color: THEME.colors.warning, fontWeight: 600 }}>{p.monate_noch} Monat{p.monate_noch > 1 ? "e" : ""}</span>
                              : <span style={{ color: THEME.colors.success }}>Beendet ✓</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              <MonatsVerlaufKarte feld="pausiert" ueberschrift="Pausierte im Verlauf" farbe={THEME.colors.warning} />
            </>
          )}
        </div>
      )}

      {aktiveTab === "analyse" && (
        <div>
          {!analyse && <p style={{ color: THEME.colors.textMuted }}>Lade...</p>}
          {analyse && (
            <>
              {/* Karten oben */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.primary }}>
                  <p style={s.karteLabel}>Durchschnittsalter</p>
                  <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.display, fontWeight: 700, margin: "8px 0 0" }}>{analyse.durchschnittsalter} J.</p>
                </div>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.success }}>
                  <p style={s.karteLabel}>Jüngstes Mitglied</p>
                  <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.display, fontWeight: 700, margin: "8px 0 0" }}>{analyse.juengster} J.</p>
                </div>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.warning }}>
                  <p style={s.karteLabel}>Ältestes Mitglied</p>
                  <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.display, fontWeight: 700, margin: "8px 0 0" }}>{analyse.aeltester} J.</p>
                </div>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.textMuted }}>
                  <p style={s.karteLabel}>Mit Geburtsdatum</p>
                  <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.display, fontWeight: 700, margin: "8px 0 0" }}>{analyse.gesamt_mit_geburtsdatum}</p>
                </div>
              </div>

              {/* Altersstruktur */}
              <div style={{ ...s.karte, marginBottom: 16 }}>
                <h3 style={{ color: THEME.colors.text, margin: "0 0 20px", fontSize: THEME.fontSize.xl }}>Altersstruktur (aktive Mitglieder)</h3>
                {(() => {
                  const max = Math.max(...analyse.altersgruppen.map(a => a.anzahl), 1);
                  return analyse.altersgruppen.map((a, i) => (
                    <div key={a.label} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, minWidth: 70 }}>{a.label}</span>
                        <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600 }}>{a.anzahl}</span>
                      </div>
                      <div style={{ background: THEME.colors.background, borderRadius: 4, height: 12, overflow: "hidden" }}>
                        <div style={{ background: `hsl(${200 + i * 8}, 70%, 55%)`, height: "100%", width: `${(a.anzahl / max) * 100}%`, borderRadius: 4, transition: "width 0.5s" }} />
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {/* Wohnorte */}
              <div style={s.karte}>
                <h3 style={{ color: THEME.colors.text, margin: "0 0 20px", fontSize: THEME.fontSize.xl }}>Top Wohnorte (aktive Mitglieder)</h3>
                {(() => {
                  const max = Math.max(...analyse.orte.map(o => o.anzahl), 1);
                  return analyse.orte.map((o, i) => (
                    <div key={o.ort} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>{o.ort}</span>
                        <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600 }}>{o.anzahl}</span>
                      </div>
                      <div style={{ background: THEME.colors.background, borderRadius: 4, height: 12, overflow: "hidden" }}>
                        <div style={{ background: THEME.colors.success, height: "100%", width: `${(o.anzahl / max) * 100}%`, borderRadius: 4 }} />
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {aktiveTab === "uebersicht" && <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
        {[{label:"Aktive Mitglieder",wert:stats.gesamt_aktiv,farbe:THEME.colors.success},{label:"Pausierte",wert:stats.gesamt_pausiert,farbe:THEME.colors.warning},{label:"Gekündigt (laufend)",wert:stats.gesamt_gekuendigt,farbe:THEME.colors.accentOrange},{label:"Ehemalige",wert:stats.gesamt_ehemalig,farbe:THEME.colors.textDisabled}].map(k => (
          <div key={k.label} style={{ ...s.karte, borderLeftColor: k.farbe }}><p style={s.karteLabel}>{k.label}</p><p style={{ color: k.farbe, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{k.wert}</p></div>
        ))}
      </div>
      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 20px", fontSize: THEME.fontSize.xl }}>Mitglieder nach Tarif</h3>
        {tarifDaten.map(([tarif, anzahl], i) => (
          <div key={tarif} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>{tarif}</span>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600 }}>{anzahl}</span>
            </div>
            <div style={{ background: THEME.colors.background, borderRadius: 4, height: 10, overflow: "hidden" }}>
              <div style={{ background: FARBEN[i % FARBEN.length], height: "100%", width: `${(anzahl / maxTarif) * 100}%`, borderRadius: 4 }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ ...s.karte }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 20px", fontSize: THEME.fontSize.xl }}>Monatsübersicht (letzte 12 Monate)</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: THEME.fontSize.base }}>
            <thead>
              <tr style={{ background: THEME.colors.background }}>
                {["Monat", "Aktive", "+/- Netto", "Neuzugänge", "Kündigungen", "Pausierte", "Ø Beitrag", "Umsatz"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: h === "Monat" ? "left" : "right", color: THEME.colors.textMuted, fontWeight: 600, fontSize: THEME.fontSize.sm, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...stats.monate].reverse().map((m, i) => {
                const netto = m.netto;
                const schnitt = m.aktiv > 0 ? (m.umsatz / m.aktiv) : 0;
                return (
                  <tr key={i} style={{ borderTop: "1px solid #2b3d52" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#1f2f42"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "12px 16px", color: THEME.colors.text, fontWeight: 600 }}>{m.monat}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.success, fontWeight: 600 }}>{m.aktiv}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right" }}>
                      <span style={{ color: netto > 0 ? THEME.colors.success : netto < 0 ? THEME.colors.danger : THEME.colors.textMuted, fontWeight: 700 }}>
                        {netto > 0 ? `+${netto}` : netto < 0 ? `${netto}` : "±0"}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: m.neuzugaenge > 0 ? THEME.colors.primary : THEME.colors.textMuted, fontWeight: m.neuzugaenge > 0 ? 600 : 400 }}>
                      {m.neuzugaenge > 0 ? `+${m.neuzugaenge}` : "–"}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: m.kuendigungen > 0 ? THEME.colors.danger : THEME.colors.textMuted, fontWeight: m.kuendigungen > 0 ? 600 : 400 }}>
                      {m.kuendigungen > 0 ? `-${m.kuendigungen}` : "–"}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: m.pausiert > 0 ? THEME.colors.warning : THEME.colors.textMuted, fontWeight: m.pausiert > 0 ? 600 : 400 }}>
                      {m.pausiert > 0 ? m.pausiert : "–"}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.textSecondary }}>
                      {schnitt.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.primary, fontWeight: 600 }}>
                      {m.umsatz.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #2b8cc4", background: THEME.colors.background }}>
                <td style={{ padding: "12px 16px", color: THEME.colors.text, fontWeight: 700 }}>Gesamt / Ø</td>
                <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.textMuted }}>–</td>
                <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700 }}>
                  {(() => { const n = stats.monate.reduce((s,m) => s + m.neuzugaenge - m.kuendigungen, 0); return <span style={{ color: n >= 0 ? THEME.colors.success : THEME.colors.danger }}>{n > 0 ? `+${n}` : n}</span>; })()}
                </td>
                <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.primary, fontWeight: 700 }}>
                  +{stats.monate.reduce((s, m) => s + m.neuzugaenge, 0)}
                </td>
                <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.danger, fontWeight: 700 }}>
                  -{stats.monate.reduce((s, m) => s + m.kuendigungen, 0)}
                </td>
                <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.warning, fontWeight: 700 }}>
                  {Math.max(...stats.monate.map(m => m.pausiert || 0))}
                </td>
                <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.textSecondary, fontWeight: 700 }}>
                  {(stats.monate.reduce((s,m) => s + (m.aktiv > 0 ? m.umsatz/m.aktiv : 0), 0) / stats.monate.length).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                </td>
                <td style={{ padding: "12px 16px", textAlign: "right", color: THEME.colors.primary, fontWeight: 700 }}>
                  Ø {(stats.monate.reduce((s,m) => s + m.umsatz, 0) / stats.monate.length).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {stats?.hat_mehr && (
          <button
            onClick={() => setMonateAnzahl(m => m + 12)}
            style={{ ...s.btnGray, width: "auto", margin: "16px auto 0", display: "block", padding: "10px 24px" }}>
            ↓ 12 weitere Monate anzeigen
          </button>
        )}
        {!stats?.hat_mehr && monateAnzahl > 12 && (
          <p style={{ color: THEME.colors.textMuted, textAlign: "center", marginTop: 12, fontSize: THEME.fontSize.base }}>Alle verfügbaren Monate werden angezeigt.</p>
        )}
      </div>
      </>}
    </div>
  );
}

// ============ EINSTELLUNGEN: E-MAIL-VORLAGEN ============
const EMAIL_VORLAGEN_KATEGORIEN = [
  { key: "ruecklastschrift", label: "Rücklastschrift", platzhalter: ["ANREDE", "VORNAME", "NACHNAME", "OFFENE_MONATE", "EINZELBETRAEGE", "GESAMTBETRAG", "GEBUEHR", "VERTRAGSNUMMER", "KONTOINHABER", "IBAN"] },
  { key: "kuendigung_bestaetigung", label: "Kündigungsbestätigung", platzhalter: ["VORNAME", "NACHNAME", "DATUM"] },
];

function EmailVorlagenTab() {
  const [kategorie, setKategorie] = useState(EMAIL_VORLAGEN_KATEGORIEN[0].key);
  const [vorlagen, setVorlagen] = useState([]);
  const [bearbeitenId, setBearbeitenId] = useState(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [form, setForm] = useState({ name: "", betreff: "", text: "" });

  const laden = () => fetch(`${API}/email-vorlagen/?kategorie=${kategorie}`).then(r => r.json()).then(d => setVorlagen(Array.isArray(d) ? d : [])).catch(() => {});
  useEffect(() => { laden(); setBearbeitenId(null); setNeuOffen(false); }, [kategorie]);

  const handleBearbeiten = (v) => { setForm({ name: v.name, betreff: v.betreff || "", text: v.text || "" }); setBearbeitenId(v.id); setNeuOffen(false); };
  const handleNeu = () => { setForm({ name: "", betreff: "", text: "" }); setNeuOffen(true); setBearbeitenId(null); };
  const handleAbbrechen = () => { setNeuOffen(false); setBearbeitenId(null); };

  const handleSpeichern = async () => {
    if (!form.name.trim()) { alert("Bitte einen Namen eingeben."); return; }
    const payload = { name: form.name, kategorie, betreff: form.betreff, text: form.text };
    const url = bearbeitenId ? `${API}/email-vorlagen/${bearbeitenId}` : `${API}/email-vorlagen/`;
    await fetch(url, { method: bearbeitenId ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    handleAbbrechen();
    laden();
  };

  const handleLoeschen = async (v) => {
    if (!window.confirm(`Vorlage "${v.name}" löschen?`)) return;
    const res = await fetch(`${API}/email-vorlagen/${v.id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    laden();
  };

  const aktuelleKategorie = EMAIL_VORLAGEN_KATEGORIEN.find(k => k.key === kategorie);

  return (
    <div style={s.karte}>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>E-Mail-Vorlagen</h3>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {EMAIL_VORLAGEN_KATEGORIEN.map(k => (
          <button key={k.key} onClick={() => setKategorie(k.key)}
            style={{ ...s.btnGray, width: "auto", padding: "8px 16px", background: kategorie === k.key ? THEME.colors.primary : THEME.colors.surfaceMuted }}>
            {k.label}
          </button>
        ))}
      </div>
      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginBottom: 16 }}>
        Verfügbare Platzhalter: {aktuelleKategorie.platzhalter.map(p => `{${p}}`).join(", ")}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
        {vorlagen.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Noch keine Vorlagen in dieser Kategorie.</p>}
        {vorlagen.map(v => (
          <div key={v.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div>
                <b style={{ color: THEME.colors.text }}>{v.name}</b>
                {v.betreff && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "4px 0 0" }}>Betreff: {v.betreff}</p>}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => handleBearbeiten(v)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Bearbeiten</button>
                <button onClick={() => handleLoeschen(v)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px", color: THEME.colors.danger }}>Löschen</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!neuOffen && bearbeitenId === null && (
        <button onClick={handleNeu} style={{ ...s.btn, width: "auto" }}>+ Neue Vorlage</button>
      )}

      {(neuOffen || bearbeitenId !== null) && (
        <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginTop: 12 }}>
          <div style={s.inputGroup}><label style={s.label}>Name</label><input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div style={s.inputGroup}><label style={s.label}>Betreff</label><input style={s.input} value={form.betreff} onChange={e => setForm(f => ({ ...f, betreff: e.target.value }))} /></div>
          <div style={s.inputGroup}>
            <label style={s.label}>Text</label>
            <textarea style={{ ...s.input, height: 220, fontFamily: "inherit" }} value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
            <button onClick={handleAbbrechen} style={s.btnGray}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ ANLEITUNGEN (rechtebasiert gefiltert) ============
// Ersetzt die fruehere statische iframe-Seite (public/anleitungen.html) - als echte
// React-Komponente hat sie Zugriff auf "rechte" und zeigt jedem Mitarbeiter nur die
// Anleitungen zu Bereichen, auf die er auch tatsaechlich zugreifen darf (gleiche
// sichtbar()-Pruefungen wie die jeweilige echte Seite selbst verwendet).
const aStil = {
  purpose: { color: THEME.colors.textMuted, fontSize: THEME.fontSize.md, marginBottom: 16, maxWidth: 640, lineHeight: 1.6 },
  ol: { margin: "0 0 16px", paddingLeft: 22, color: THEME.colors.text, lineHeight: 1.75, fontSize: THEME.fontSize.base },
  olInner: { margin: "8px 0 0", paddingLeft: 20, lineHeight: 1.6 },
  callout: (typ) => ({
    marginTop: 16, padding: "14px 18px", borderRadius: 8,
    background: typ === "danger" ? THEME.colors.dangerBg : typ === "good" ? "#22c55e22" : THEME.colors.surfaceMuted,
    borderLeft: `4px solid ${typ === "danger" ? THEME.colors.danger : typ === "good" ? THEME.colors.success : THEME.colors.primary}`,
    color: THEME.colors.text, fontSize: THEME.fontSize.base, lineHeight: 1.6,
  }),
  tabrow: { display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0 16px" },
  tabchip: { fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 6, background: THEME.colors.surfaceMuted, color: THEME.colors.textSecondary, border: `1px solid ${THEME.colors.border}` },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 },
  miniKarte: { background: THEME.colors.background, border: `1px solid ${THEME.colors.border}`, borderRadius: 8, padding: 16 },
  miniLabel: { fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: THEME.colors.primary, marginBottom: 8 },
  bild: { display: "block", maxWidth: "100%", borderRadius: 8, border: `1px solid ${THEME.colors.border}`, marginBottom: 18, cursor: "zoom-in" },
};

const ANLEITUNGEN = [
  {
    id: "mitglieder-anlegen", kategorie: "Mitglieder", titel: "Mitglieder anlegen", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.darf_mitglieder_anlegen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Neues Mitglied mit Stammdaten, Vertrag und SEPA-Angaben anlegen — zum Beispiel, wenn jemand den Vertrag auf Papier ausgefüllt hat. Anträge über die Webseite landen dagegen unter „Online Portal" (siehe „Online-Verträge bearbeiten").</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Mitglieder</b> klicken.</li>
          <li>Oben rechts auf <b>+ Neu</b> klicken. Der Typ steht standardmäßig auf <i>Mitglied</i> — nur bei Wellpass-/Hansefit-/Sonstige-Nutzern oben umstellen.</li>
          <li>Stammdaten eintragen: Anrede, Vorname*, Nachname*, Geburtsdatum*, E-Mail, Telefon, Straße &amp; Nr.*, PLZ*, Ort*. PLZ eingeben und kurz warten — Ort füllt sich automatisch, wenn schon Mitglieder mit dieser PLZ existieren, bei der Straße erscheinen passende Vorschläge.</li>
          <li>Unter „Vertrag &amp; SEPA": Tarif*, Monatlicher Beitrag*, Vertragsbeginn*, IBAN (optional, kann nachgereicht werden), Kontoinhaber (automatisch aus Vor-/Nachname), Mandats-ID (automatisch), Anteiliger Erstbeitrag (automatisch berechnet), Startpaket, Erstlaufzeit, RFID-Kartennummer (optional).</li>
          <li><b>Läuft gerade eine Aktion</b> (z.&nbsp;B. „Trainiere bis 2027 gratis")? Im Feld <b>Aktion (optional)</b> die passende Aktion wählen. Das Formular setzt dann die Erstlaufzeit passend zur Aktion, den Erstbeitrag auf 0&nbsp;€ und trägt nach dem Speichern automatisch den Gratis-Zeitraum ein — dazu kommt eine Notiz im Profil.</li>
          <li>Optional eine Notiz eintragen, dann <b>Speichern</b>.</li>
        </ol>
        <div style={aStil.callout()}>Ist die Person laut Geburtsdatum unter 18, erscheint automatisch der Hinweis „Erziehungsberechtigter muss unterschreiben!" — der Vertrag braucht dann zusätzlich dessen Unterschrift.</div>
        <div style={aStil.callout("danger")}><b>Bei Aktionen den Vertragsbeginn auf den ersten Trainingstag setzen</b> (also meist heute). Liegt der Vertragsbeginn in der Zukunft, bleibt der Zutritt bis dahin gesperrt — wer sich am 1.&nbsp;12. einträgt, kommt vorher nicht ins Studio.</div>
      </>
    ),
  },
  {
    id: "pausieren", kategorie: "Mitglieder", titel: "Mitgliedschaft pausieren", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.darf_pausieren,
    inhalt: (
      <>
        <p style={aStil.purpose}>Beitragspause anlegen — z.&nbsp;B. bei Verletzung, Schwangerschaft oder längerer Abwesenheit. Während der Pause wird kein Beitrag eingezogen, der Vertrag verlängert sich automatisch um die pausierten Monate.</p>
        <ol style={aStil.ol}>
          <li>Das Mitglied über <b>Mitglieder</b> suchen und öffnen.</li>
          <li>Im Bereich <b>Aktionen</b> auf <b>Pausieren</b> klicken.</li>
          <li><b>Pause ab</b> (nächste 6 Monatsersten) und <b>Anzahl Monate</b> (1–6) wählen — „Pause endet am" wird automatisch berechnet.</li>
          <li><b>Pause bestätigen</b> klicken.</li>
        </ol>
        <div style={aStil.callout("good")}>Läuft die Pause ab, wird die Mitgliedschaft automatisch wieder aktiv. Soll sie früher enden: im Profil auf <b>Reaktivieren</b> klicken. Beginnt die Pause erst in der Zukunft, bleibt das Mitglied bis zum Startdatum aktiv — der Wechsel auf „pausiert" passiert dann automatisch am Starttag.</div>
        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, marginTop: 12 }}><b>Pause verlängern:</b> Ist ein Mitglied bereits pausiert, steht im Bereich <b>Aktionen</b> statt „Pausieren" der Button <b>Pause verlängern</b>. Im Fenster das <b>Neue Pausenende</b> wählen (das bisherige steht zur Kontrolle daneben) und bestätigen — bis zum neuen Datum wird weiterhin kein Beitrag eingezogen.</p>
      </>
    ),
  },
  {
    id: "gratismonat", kategorie: "Mitglieder", titel: "Gratismonat gewähren",
    sichtbar: r => r?.rolle === "admin" || r?.darf_pausieren,
    inhalt: (
      <>
        <p style={aStil.purpose}>Einen beitragsfreien Zeitraum eintragen — z.&nbsp;B. als Werbeprämie („6-Monats-Vertrag → 1 Monat gratis") oder als Kulanz. Anders als bei einer Pause bleibt das Mitglied ganz normal „aktiv" und behält den Türzutritt, nur der Beitragseinzug entfällt.</p>
        <ol style={aStil.ol}>
          <li>Das Mitglied über <b>Mitglieder</b> suchen und öffnen.</li>
          <li>Im Bereich <b>Aktionen</b> auf <b>Gratismonat gewähren</b> klicken.</li>
          <li><b>Gratis ab</b> und <b>Anzahl Monate</b> wählen — „Gratismonat endet am" wird automatisch berechnet.</li>
          <li>Bestätigen. Im Profil erscheint der Zeitraum anschließend in der Liste der Gratismonate.</li>
        </ol>
        <div style={aStil.callout("good")}>Bei der <b>SEPA-Vorschau</b> wird das Mitglied für diesen Zeitraum automatisch übersprungen. Wer über eine Aktion wie „Trainiere bis 2027 gratis" kommt, bekommt den Gratis-Zeitraum schon beim Anlegen/Freigeben automatisch — dann ist hier nichts mehr zu tun.</div>
      </>
    ),
  },
  {
    id: "kuendigen", kategorie: "Mitglieder", titel: "Mitgliedschaft kündigen", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.darf_kuendigen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Kündigung eintragen und direkt die Kündigungsbestätigung verschicken. Das Kündigungsdatum (heute) wird automatisch gesetzt — festgelegt wird nur, bis wann die Mitgliedschaft noch läuft.</p>
        <ol style={aStil.ol}>
          <li>Das Mitglied über <b>Mitglieder</b> suchen und öffnen.</li>
          <li>Im Bereich <b>Aktionen</b> auf <b>Kündigen</b> klicken.</li>
          <li>„Mitgliedschaft läuft bis" prüfen (Standard: letzter Tag des übernächsten Monats). Bei Erstlaufzeit zeigt das Fenster zusätzlich den frühestmöglichen Kündigungstermin.</li>
          <li><b>Kündigung bestätigen</b> klicken.</li>
          <li>Bei der Rückfrage nach der Bestätigungsmail mit „Ja" ein vorausgefülltes E-Mail-Fenster öffnen und abschicken.</li>
        </ol>
        <div style={aStil.callout("good")}>Solange die Mitgliedschaft noch als „gekündigt" geführt wird, lässt sie sich über <b>Wieder aktivieren</b> zurückholen.</div>
      </>
    ),
  },
  {
    id: "mitglied-loeschen", kategorie: "Mitglieder", titel: "Mitglied löschen", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.darf_mitglieder_loeschen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Mitglied gemäß DSGVO anonymisieren — der Standardweg, um eine Person auf Wunsch endgültig aus dem System zu entfernen.</p>
        <ol style={aStil.ol}>
          <li>Das Mitglied über <b>Mitglieder</b> suchen und öffnen.</li>
          <li>Im Bereich <b>Aktionen</b> ganz unten rechts auf <b>Mitglied löschen</b> klicken.</li>
          <li>Den Hinweistext lesen und mit <b>Ja, anonymisieren</b> bestätigen.</li>
        </ol>
        <div style={aStil.callout("danger")}>Name, Adresse, IBAN und Kontaktdaten werden dabei durch Platzhalter ersetzt, Notizen und Dokumente gelöscht — das lässt sich <b>nicht rückgängig machen</b>. Rechnungen und Zahlungen bleiben wegen der gesetzlichen 10-jährigen Aufbewahrungsfrist unverändert erhalten. Gibt es dafür noch offene Zahlungen, meldet das System das vor dem Löschen.</div>
      </>
    ),
  },
  {
    id: "probetraining", kategorie: "Mitglieder", titel: "Probetraining anlegen & übernehmen",
    sichtbar: () => true,
    inhalt: (
      <>
        <p style={aStil.purpose}>Interessenten, die zum Probetraining kommen, bekommen vorübergehend eine Karte oder ein Band mit Zutritt — ohne gleich als Mitglied angelegt zu werden. Wird die Person Mitglied, übernimmst du sie mit einem Klick samt Karte.</p>
        <ol style={aStil.ol}>
          <li>Im <b>Dashboard</b> die Kachel <b>🏃 Probetrainings</b> öffnen. Über das Auswahlfeld lässt sich zwischen „Aktive Probetrainings", „Beendet (Historie)", „Als Mitglied übernommen" und „Alle" umschalten.</li>
          <li>Neben dem Auswahlfeld auf <b>+</b> klicken („Neues Probetraining anlegen").</li>
          <li><b>Vorname*</b> und <b>Nachname*</b> eintragen, Telefon, E-Mail und Notiz sind optional.</li>
          <li>Unter „1. Karte oder Band wählen" den Medientyp festlegen und die Nummer erfassen (PC-Leser oder Handy-NFC). Die Karte kann auch später zugewiesen werden.</li>
          <li>Anlegen klicken. Wurde das Häkchen für den Zutritt gesetzt, ist die Karte sofort am Schloss freigeschaltet — ohne Häkchen ist sie nur zugeordnet, öffnet aber noch nicht.</li>
        </ol>
        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, marginTop: 12 }}><b>In der Liste</b> gibt es pro Person die Knöpfe <b>Öffnen</b> (Stammdaten, RFID sperren/aktivieren/entfernen, Besuchsverlauf), <b>Als Mitglied übernehmen</b> und <b>Beenden</b>.</p>
        <div style={aStil.callout("good")}><b>Als Mitglied übernehmen</b> öffnet das normale Mitgliedsformular mit den vorhandenen Daten. Beim Speichern wird die Karte automatisch vom Probetraining auf das neue Mitglied übertragen; das Probetraining steht danach unter „Als Mitglied übernommen".</div>
        <div style={aStil.callout()}><b>Beenden</b>, wenn jemand nicht Mitglied wird: im Fragefenster „Probetraining beenden?" wählen, ob die Karte/das Band wieder frei wird, damit du es erneut ausgeben kannst. Der Zutritt wird dabei entzogen.</div>
      </>
    ),
  },
  {
    id: "sepa-lauf", kategorie: "Zahlungen", titel: "SEPA-Lauf erstellen", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.module?.sepa,
    inhalt: (
      <>
        <p style={aStil.purpose}>Monatliche Lastschriftdatei für die Bank erzeugen und den Lauf als eingereicht zurückmelden. Pausen, Kündigungen und noch nicht begonnene Verträge werden automatisch berücksichtigt.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>SEPA</b> klicken.</li>
          <li><b>Abbuchungsdatum</b> prüfen (Standard: 1. des Folgemonats), bei Bedarf mit ◀ / ▶ wechseln und <b>Aktualisieren</b> klicken.</li>
          <li>Vorschau kontrollieren: Anzahl fälliger Mitglieder und Gesamtbetrag.</li>
          <li><b>XML herunterladen</b> und die Datei im Online-Banking hochladen.</li>
          <li>Danach <b>Als eingereicht markieren</b> klicken, Anzahl/Summe prüfen und mit <b>Ja, einreichen</b> bestätigen.</li>
        </ol>
        <div style={aStil.callout()}>Ab dem Einreichen gelten alle Mitglieder dieses Laufs als „ausstehend", bis der Zahlungseingang bestätigt ist.</div>
        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, marginTop: 12 }}>Sobald der Kontoauszug da ist: über <b>📥 Kontoauszug importieren</b> die CSV hochladen — Zahlungseingänge und Rücklastschriften werden automatisch zugeordnet.</p>
      </>
    ),
  },
  {
    id: "rechnung", kategorie: "Zahlungen", titel: "Rechnung erstellen", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.module?.rechnungen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Freie Rechnung schreiben — mit oder ohne Verknüpfung zu einem Mitglied. Rechnungsnummer wird automatisch vergeben.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Rechnungen</b>, dann <b>+ Neue Rechnung</b> klicken.</li>
          <li>Empfänger festlegen: entweder bei <b>Mitglied suchen</b> eine bestehende Person wählen (füllt Name/Adresse automatisch) oder <b>Empfänger*</b>/<b>Adresse</b> frei eintippen.</li>
          <li>Positionen eintragen: Beschreibung*, Menge, Einheit, Preis. Über „+ Position hinzufügen" weitere Zeilen ergänzen.</li>
          <li><b>MwSt %</b> (19/7/0) und <b>Zahlung</b> („Bitte überweisen" / „Bereits bar bezahlt") wählen.</li>
          <li><b>Rechnung erstellen</b> klicken.</li>
        </ol>
        <div style={aStil.callout("good")}>Für wiederkehrende Rechnungen vorher auf <b>⭐ Als Vorlage speichern</b> klicken — taucht danach im Feld „Vorlage" zur Wiederverwendung auf.</div>
      </>
    ),
  },
  {
    id: "ttlock", kategorie: "Zutritt (TTLock)", titel: "Karten & Bänder anlegen", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.module?.ttlock_mitgliederzutritt_verwalten,
    inhalt: (
      <>
        <p style={aStil.purpose}>Einer bestehenden Person ihre Zutrittskarte oder ihr Band zuordnen. Trägst du eine Kartennummer im Profil ein, versucht das System automatisch, sie am Schloss zu hinterlegen.</p>
        <div style={aStil.tabrow}>
          <span style={aStil.tabchip}>synchronisiert — Zutritt eingerichtet</span>
          <span style={aStil.tabchip}>konflikt — Nummer gehört schon jemand anderem</span>
          <span style={aStil.tabchip}>fehler — Registrierung hat nicht geklappt</span>
        </div>
        <ol style={aStil.ol}>
          <li>Die Person öffnen (Mitglied, Wellpass-/Hansefit-/Sonstige-Nutzer oder Mitarbeiter — überall gleich).</li>
          <li>Zur Karte <b>RFID &amp; Zutritt</b> scrollen, <b>RFID bearbeiten</b> klicken.</li>
          <li>Nummer eintippen oder mit NFC-fähigem Handy <b>📱 Scannen</b>. Bei einem Band unter „Medientyp" explizit <i>Band</i> wählen.</li>
          <li><b>Speichern</b> klicken — das System registriert die Karte automatisch am Schloss.</li>
          <li>Weiter unten den <b>Sync-Status</b> prüfen, oder die Person die Tür testen lassen.</li>
        </ol>
        <div style={aStil.callout("danger")}>
          <b>Falls es nicht klappt</b> (Status „konflikt"/„fehler" oder Tür öffnet trotzdem nicht):
          <ol style={aStil.olInner}>
            <li>Karte/Band direkt in der TTLock-App (oder am Schloss) unter beliebigem Namen neu registrieren.</li>
            <li>Im Profil auf <b>📥 Von TTLock übernehmen</b> klicken.</li>
            <li>In der Liste die gerade registrierte Karte suchen — mit „✓ wahrscheinlicher Treffer" markierte Einträge zuerst probieren.</li>
            <li><b>Übernehmen</b> klicken — berechnet die ComeFit-Nummer garantiert korrekt zurück.</li>
          </ol>
        </div>
        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, marginTop: 12 }}>Zwei weitere Hilfsmittel: das Dashboard-Widget <b>📥 TTLock: Unzugeordnete Karten</b> zeigt bereits registrierte, aber noch nicht zugeordnete Karten. Der Button <b>🔒 Zutritt entziehen</b> im Profil sperrt den Zutritt sofort — z.&nbsp;B. bei Kündigung oder Verlust.</p>
        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, marginTop: 12 }}>Meldet sich jemand, dessen Karte nicht funktioniert hat: im <b>Protokoll</b> nachsehen, was das Schloss gemeldet hat — siehe die Anleitung „TTLock-Protokoll &amp; Fehlversuche".</p>
      </>
    ),
  },
  {
    id: "rfid-konflikt", kategorie: "Zutritt (TTLock)", titel: "RFID-Konflikt lösen", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.module?.safesmart,
    inhalt: (
      <>
        <p style={aStil.purpose}>Herausfinden, wem eine Karte gerade gehört, und sie bei Bedarf jemand anderem zuordnen. Nützlich, wenn eine Karte am Terminal nicht funktioniert oder jemand eine gebrauchte Karte übernimmt.</p>
        <ol style={aStil.ol}>
          <li>Im Dashboard oben im Suchfeld die Kartennummer (oder einen Namen) eingeben, <b>Suchen</b> klicken.</li>
          <li>Ergebnis lesen: „✓ Diese Karte gehört zu …" (bereits vergeben), „Keine Zuordnung gefunden" (frei) oder „⚠ Datenkonflikt" (mehrere Datensätze, siehe unten).</li>
          <li>Bereits vergeben: <b>Anderer Person zuordnen</b>, Person suchen und auswählen. Noch frei: <b>Person zuweisen</b>. Soll niemandem mehr gehören: <b>Entfernen</b>.</li>
          <li>Hat die Zielperson schon eine andere Karte, erscheint eine Rückfrage mit der bisherigen Nummer — nur bestätigen, wenn sie wirklich ersetzt werden soll.</li>
        </ol>
        <div style={aStil.callout("danger")}>
          <b>Echter Datenkonflikt</b> (mehrere Datensätze, eine Nummer — kommt nur bei alten, importierten Daten vor):
          <ol style={aStil.olInner}>
            <li>Jeden aufgelisteten Datensatz einzeln öffnen und prüfen, wer die Karte tatsächlich besitzt.</li>
            <li>Im Profil der falschen Person(en) unter „RFID &amp; Zutritt" auf <b>RFID entfernen</b> klicken.</li>
            <li>Karte erneut im Dashboard suchen — jetzt eindeutig — und der richtigen Person zuweisen.</li>
          </ol>
        </div>
      </>
    ),
  },
  {
    id: "ttlock-protokoll", kategorie: "Zutritt (TTLock)", titel: "TTLock-Protokoll & Fehlversuche",
    sichtbar: r => r?.rolle === "admin" || r?.module?.ttlock_protokoll_ansehen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Nachsehen, wer wann die Tür geöffnet hat — und warum es bei jemandem nicht geklappt hat („Operation failed" am Schloss). Praktisch, wenn ein Mitglied sagt: „Meine Karte ging heute Morgen nicht."</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Einstellungen</b>, dann Reiter „Zutritt (TTLock)" klicken und zum Bereich <b>🚪 Protokoll</b> scrollen.</li>
          <li><b>Protokoll laden</b> klicken. Ohne Datum zeigt die Liste die letzten 100 Ereignisse; mit einem gewählten <b>Datum</b> nur diesen Tag.</li>
          <li>Im Suchfeld nach <b>Name oder Chipnummer</b> suchen.</li>
          <li><b>Nur fehlgeschlagene Versuche ❌</b> anhaken, um nur die gescheiterten Öffnungen zu sehen. Darunter steht, wie viele Fehlversuche in der Liste sind.</li>
          <li>Fehlversuche sind rot hinterlegt. In der Spalte <b>Chipnummer</b> steht die Nummer, die das Schloss gelesen hat — auch wenn die Karte niemandem zugeordnet ist („Unbekannt").</li>
        </ol>
        <div style={aStil.callout("good")}>Ist die Karte noch keinem zugeordnet: Person öffnen → <b>📥 Von TTLock übernehmen</b> (siehe „Karten &amp; Bänder anlegen") — oder den Zutritt im Profil prüfen (Status „konflikt"/„fehler").</div>
        <div style={aStil.callout()}><b>Automatische Meldung:</b> Schlagen zwei Öffnungen direkt hintereinander fehl, bekommst du eine Mail an info@comefit.de und eine Push-Nachricht (geprüft wird alle 15&nbsp;Minuten). Einzelne Fehlversuche, z.&nbsp;B. eine falsch gehaltene Karte, lösen bewusst nichts aus.</div>
        <div style={aStil.callout("danger")}>Das Protokoll zeigt nur, was das Schloss selbst an die TTLock-Cloud meldet. Eine <b>völlig unbekannte</b> Karte oder ein falsch codiertes Band taucht dort teils gar nicht auf — dann hilft nur Neuregistrierung am Schloss wie in „Karten &amp; Bänder anlegen" beschrieben.</div>
      </>
    ),
  },
  {
    id: "mitglieder-app-zugang", kategorie: "Zutritt (TTLock)", titel: "Mitglieder-App-Zugang & WLAN-Geräte",
    sichtbar: r => r?.rolle === "admin" || r?.darf_mitglieder_bearbeiten,
    inhalt: (
      <>
        <p style={aStil.purpose}>Im Mitgliedsprofil siehst du, ob ein Mitglied die „Meine ComeFit App" (my.comefit.de) nutzt, kannst den Zugang sperren und die WLAN-Geräte verwalten, mit denen sich ein Mitglied automatisch einchecken lässt.</p>
        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 700, marginBottom: 6 }}>Mitglieder-App</p>
        <ol style={aStil.ol}>
          <li>Das Mitglied öffnen und zum Bereich <b>Mitglieder-App</b> im Zutritt-Block scrollen.</li>
          <li>Der Status zeigt: „Noch kein Zugang eingerichtet", „Aktivierung angefordert, aber noch kein Passwort vergeben", „✓ Zugang aktiv" oder „⊘ Zugang gesperrt".</li>
          <li>Mit <b>Zugang sperren</b> verliert das Mitglied den Zugang zur App sofort; <b>Zugang entsperren</b> stellt ihn wieder her.</li>
        </ol>
        <div style={aStil.callout("good")}>So aktiviert ein Mitglied die App: my.comefit.de öffnen → <b>Zugang aktivieren</b> → die im Profil hinterlegte <b>E-Mail-Adresse</b> eingeben → Passwort festlegen. Das System legt nie von selbst ein Konto an: ohne passende E-Mail im Profil klappt die Aktivierung nicht — bei Problemen zuerst die E-Mail-Adresse prüfen.</div>
        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 700, margin: "18px 0 6px" }}>WLAN-Geräte</p>
        <ol style={aStil.ol}>
          <li>Im selben Profil erscheint der Block <b>WLAN-Geräte</b> mit allen registrierten Handys. „(wartet auf Erkennung)" heißt: Gerät ist angelegt, aber noch nicht im Studio-WLAN aufgetaucht.</li>
          <li><b>Gerät entfernen</b> löscht ein einzelnes Gerät — es löst danach keinen automatischen Check-in mehr aus.</li>
          <li><b>Einwilligung komplett widerrufen</b> deaktiviert alle Geräte des Mitglieds auf einmal (z.&nbsp;B. auf Wunsch aus Datenschutzgründen).</li>
        </ol>
      </>
    ),
  },
  {
    id: "check-in-abgleich", kategorie: "Check In", titel: "Check-in-Abgleich (Wellpass/Hansefit)", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.module?.checkins_zu_klaeren,
    inhalt: (
      <>
        <p style={aStil.purpose}>Check-ins von Wellpass und Hansefit werden automatisch importiert, lassen sich aber nicht immer eindeutig einer Person zuordnen — diese Fälle landen unter „Zu klären".</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Check In</b> klicken, dann Reiter „Wellpass" oder „Hansefit" wählen.</li>
          <li>Mit <b>🔄 Jetzt synchronisieren</b> die neuesten Check-ins abrufen.</li>
          <li>In der Liste „Zu klären" bei einem Eintrag <b>Mitglied zuordnen</b> bzw. <b>Nutzer zuordnen</b> klicken und die passende Person suchen.</li>
          <li>Gehört der Check-in zu niemandem im System: <b>Ignorieren</b> klicken.</li>
          <li>Ist die Person noch gar nicht als Wellpass-/Hansefit-Nutzer angelegt: direkt aus der Zeile heraus einen neuen Nutzer anlegen (bei Hansefit über „Neuer Hansefit-Nutzer") — bereits vorhandene passende Check-ins werden dabei rückwirkend zugeordnet.</li>
        </ol>
      </>
    ),
  },
  {
    id: "schichtplan", kategorie: "Verwaltung", titel: "Schichtplan",
    sichtbar: r => r?.rolle === "admin" || r?.module?.schichtplan,
    inhalt: (
      <>
        <p style={aStil.purpose}>Wer arbeitet wann? Der Schichtplan zeigt Wochen- und Monatsansicht aller Mitarbeiter. Er liest die Schichten standardmäßig aus dem Google-Kalender „Schichtplan" — dort tragen die Kollegen ihre Schichten mit ihrem Kürzel (z.&nbsp;B. LF, XD) ein.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Mitarbeiter</b>, dann Reiter „Schichtplan" klicken.</li>
          <li>Oben zwischen <b>Woche</b> und <b>Monat</b> umschalten. In der Woche helfen <b>Heute</b>, <b>◀ Vorherige Woche</b> und <b>Nächste Woche ▶</b>; im Monat wählst du Monat und Jahr.</li>
          <li>Über das Auswahlfeld <b>Alle Mitarbeiter</b> lässt sich der Plan auf eine Person eingrenzen. <b>🖨 Drucken</b> gibt die aktuelle Ansicht aus.</li>
          <li>Admins sehen in der Monatsansicht zusätzlich je Mitarbeiter die geplanten Stunden, den Stundenlohn, den geplanten Lohn und den Rest bis zur Verdienstgrenze.</li>
        </ol>
        <div style={aStil.callout("good")}><b>Aus Google Kalender:</b> Ändert jemand Schichten im Kalender, einfach <b>🔄 Jetzt abgleichen</b> klicken (nur Admin) — das System meldet, wie viele Schichten neu, geändert oder entfernt wurden. Welches Kürzel zu welchem Mitarbeiter gehört, ist hinterlegt; bei einem neuen Kürzel bitte den Admin fragen.</div>
        <div style={aStil.callout()}><b>Interne Planung:</b> Wer lieber direkt in der App plant, wechselt als Admin über „Zur internen Planung wechseln". Dann stehen <b>+ Neue Schicht</b> (mit Wiederholung: einmalig / wöchentlich in einem Zeitraum / wöchentlich dauerhaft), <b>📋 Woche kopieren</b> (mit „Nur fehlende Schichten ergänzen") und <b>⚙ Arbeitszeiten</b> (Vorlagen je Wochentag) zur Verfügung. „Wieder Google Kalender verwenden" schaltet zurück.</div>
      </>
    ),
  },
  {
    id: "dashboard", kategorie: "Verwaltung", titel: "Dashboard bearbeiten", bild: true,
    sichtbar: () => true,
    inhalt: (
      <>
        <p style={aStil.purpose}>Die Startseite mit den Kacheln einrichten, die im Alltag wichtig sind. Die Anordnung merkt sich dein Browser lokal — andere Mitarbeiter sehen ihre eigene.</p>
        <ol style={aStil.ol}>
          <li>Unten auf der Dashboard-Seite auf <b>🔧 Dashboard bearbeiten</b> klicken.</li>
          <li>Kachel verschieben: am Griff „⠿ Titel" oben in der Kachel per Drag &amp; Drop ziehen.</li>
          <li>Größe anpassen: an der unteren rechten Ecke der Kachel ziehen.</li>
          <li>Kachel ausblenden: oben rechts auf „×" klicken.</li>
          <li>Ausgeblendete Kachel zurückholen: oben auf <b>+ Modul</b> klicken und auswählen.</li>
          <li>Fertig: <b>💾 Dashboard speichern</b> klicken (jede Änderung ist zu diesem Zeitpunkt schon automatisch gespeichert).</li>
        </ol>
        <div style={aStil.callout("good")}>Im Bearbeiten-Modus unten auf <b>↺ Zurücksetzen</b> klicken, um wieder bei der Standard-Anordnung zu starten.</div>
      </>
    ),
  },
  {
    id: "stundenzettel", kategorie: "Verwaltung", titel: "Stundenzettel", bild: true,
    sichtbar: r => r?.rolle === "admin" || r?.module?.mitarbeiter,
    inhalt: (
      <>
        <p style={aStil.purpose}>Arbeitszeit eintragen, am Monatsende zur Freigabe einreichen und jederzeit das unterschriebene PDF wiederfinden.</p>
        <div style={aStil.tabrow}>
          <span style={aStil.tabchip}>Offen — wird noch bearbeitet</span>
          <span style={aStil.tabchip}>Eingereicht — wartet auf Freigabe</span>
          <span style={aStil.tabchip}>Freigegeben — abgeschlossen</span>
        </div>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Mitarbeiter</b> klicken — landet automatisch im Reiter „📋 Stundenzettel".</li>
          <li>Oben Monat und Jahr wählen (Standard: aktueller Monat).</li>
          <li>Existiert für den Monat noch keiner: <b>+ Stundenzettel erstellen</b> anklicken.</li>
          <li><b>+ Zeitblock hinzufügen</b>: Datum, Beginn, Ende, Pause (Minuten), Tätigkeit ausfüllen — die Stunden werden sofort automatisch berechnet.</li>
          <li>Für jeden weiteren Arbeitstag wiederholen; mehrere Blöcke pro Tag sind möglich.</li>
          <li>Am Monatsende <b>✍ Einreichen &amp; unterschreiben</b> klicken, im Feld unterschreiben und speichern.</li>
          <li>Ein Admin prüft und gibt mit eigener Unterschrift frei — danach lassen sich keine Einträge mehr ändern.</li>
          <li>Über <b>📄 PDF</b> ist der Stundenzettel jederzeit als Dokument abrufbar.</li>
        </ol>
      </>
    ),
  },
  {
    id: "statistiken", kategorie: "Auswertung", titel: "Statistiken – Überblick",
    sichtbar: r => r?.rolle === "admin" || r?.module?.statistiken,
    inhalt: (
      <>
        <p style={aStil.purpose}>Fünf unabhängige Reiter, fünf verschiedene Blickwinkel auf dieselben Mitgliederdaten — keine feste Reihenfolge.</p>
        <div style={aStil.grid2}>
          <div style={aStil.miniKarte}><div style={aStil.miniLabel}>Übersicht</div><p style={{ margin: 0, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>Aktueller Stand: aktive/pausierte/gekündigte/ehemalige Mitglieder, Verteilung nach Tarif, 12-Monats-Trend.</p></div>
          <div style={aStil.miniKarte}><div style={aStil.miniLabel}>Buchhaltung</div><p style={{ margin: 0, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>Umsatzzahlen nach Jahr, dann nach Monat/Tarif — ein Jahr anklicken für Details.</p></div>
          <div style={aStil.miniKarte}><div style={aStil.miniLabel}>Abgänge</div><p style={{ margin: 0, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>Wer in einem gewählten Monat gekündigt hat, Zugehörigkeitsdauer, Umsatzverlust.</p></div>
          <div style={aStil.miniKarte}><div style={aStil.miniLabel}>Pausierte</div><p style={{ margin: 0, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>Wer in einem gewählten Monat pausiert hat, ob noch aktiv, Umsatzausfall.</p></div>
        </div>
        <div style={aStil.miniKarte}><div style={aStil.miniLabel}>Mitgliederanalyse</div><p style={{ margin: 0, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>Durchschnittsalter, Altersstruktur und Top-Wohnorte der aktiven Mitglieder.</p></div>
        <div style={aStil.callout()}>Faustregel: Aktueller Stand → <b>Übersicht</b>. Geld/Steuerberater → <b>Buchhaltung</b>. Wer genau ist gegangen/pausiert → <b>Abgänge</b>/<b>Pausierte</b>. Wer sind unsere Mitglieder → <b>Mitgliederanalyse</b>.</div>
      </>
    ),
  },
  {
    id: "schwarzes-brett", kategorie: "Studio", titel: "Schwarzes Brett",
    sichtbar: r => r?.rolle === "admin" || r?.module?.studio,
    inhalt: (
      <>
        <p style={aStil.purpose}>Interne Ankündigung für alle Mitarbeiter veröffentlichen — z.&nbsp;B. Geräteinspektion, Urlaubsvertretung, wichtige Hinweise.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Studio</b> klicken — landet automatisch im Reiter „📢 Schwarzes Brett".</li>
          <li><b>+ Neue Nachricht</b> klicken.</li>
          <li><b>Titel*</b> und <b>Nachricht*</b> eintragen, optional ein <b>Ablaufdatum</b> setzen.</li>
          <li><b>Veröffentlichen</b> klicken.</li>
        </ol>
        <div style={aStil.callout("good")}>Nach Ablaufdatum wird die Nachricht automatisch ausgegraut und als „Abgelaufen" markiert, bleibt aber sichtbar. Über „✏ Bearbeiten" oder „Löschen" lässt sie sich jederzeit ändern oder entfernen.</div>
      </>
    ),
  },
  {
    id: "reinigungsprotokoll", kategorie: "Studio", titel: "Reinigungsprotokoll",
    sichtbar: r => r?.rolle === "admin" || r?.module?.studio,
    inhalt: (
      <>
        <p style={aStil.purpose}>Reinigung eines Bereichs bestätigen und den Überblick behalten, was gerade fällig ist.</p>
        <div style={aStil.tabrow}>
          <span style={aStil.tabchip}>🟢 grün — noch Zeit</span>
          <span style={aStil.tabchip}>🟡 gelb — bald fällig</span>
          <span style={aStil.tabchip}>🔴 rot — überfällig</span>
        </div>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Studio</b>, dann Reiter „🧹 Reinigungsprotokoll" klicken.</li>
          <li>Bei der passenden Karte auf <b>✓ Jetzt gereinigt</b> klicken, sobald ein Bereich erledigt ist.</li>
          <li>Für einen neuen Bereich: <b>+ Neuer Bereich</b> klicken — Name*, Intervall (Täglich/Wöchentlich/Monatlich/Alle X Stunden) und optional eine Frist-Uhrzeit festlegen.</li>
          <li>Über <b>📋 Verlauf anzeigen</b> lassen sich die letzten 50 Reinigungen nachschlagen (wer, wann, welcher Bereich).</li>
        </ol>
      </>
    ),
  },
  {
    id: "vorfallbericht", kategorie: "Studio", titel: "Vorfall-/Unfallbericht schreiben",
    sichtbar: r => r?.rolle === "admin" || r?.module?.vorfaelle_ansehen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Unfälle, Verletzungen, Beschwerden oder sonstige besondere Vorkommnisse im Studio sauber dokumentieren — mit Beteiligten, Zeugen, Maßnahmen und Unterschriften, und bei Bedarf als PDF.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Studio</b>, dann Reiter „🚑 Vorfallberichte" klicken. Die Liste lässt sich nach Name, Zeitraum (von/bis), Art und Status filtern.</li>
          <li><b>+ Neuer Vorfall</b> klicken (braucht das Recht „Vorfallberichte erstellen").</li>
          <li>Das Formular der Reihe nach ausfüllen: <b>1. Allgemeine Angaben</b> (Datum*, Uhrzeit*, Ort im Studio*), <b>2. Art des Vorfalls*</b> (mindestens eine), <b>3. Betroffene Person</b> (ein Mitglied suchen oder Name/Telefon/E-Mail von Hand), <b>4. Beschreibung</b> („Was ist passiert?*" und „Wie kam es zu dem Vorfall?"), <b>5. Maßnahmen</b>, <b>6. Zeugen</b>, <b>7. Anwesende Mitarbeiter</b>.</li>
          <li>Optional <b>8. Bilder und Dokumente</b> (werden nach dem Speichern hochgeladen) und <b>9. Unterschriften</b>. Zur Nachbereitung gibt es Wiedervorlage-Datum, Zuständiger Mitarbeiter und Notiz.</li>
          <li>Speichern. Der Bericht steht danach als <b>Offen</b> in der Liste.</li>
        </ol>
        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, marginTop: 12 }}><b>Später bearbeiten:</b> Einen Bericht in der Liste anklicken — dort gibt es <b>✏ Bearbeiten</b>, <b>📄 PDF erstellen</b>, <b>✓ Abschließen</b> bzw. <b>Wieder öffnen</b> und <b>Löschen</b>. Der <b>Änderungsverlauf</b> unten zeigt, wer wann was geändert hat. Status: „Offen", „Nachbearbeitung erforderlich" oder „Abgeschlossen".</p>
        <div style={aStil.callout("good")}>Zu einem Mitglied gehörende Berichte erscheinen auch direkt in dessen Profil. Ansehen, Erstellen, Bearbeiten, Abschließen, Löschen und PDF-Export sind getrennte Rechte (siehe „Mitarbeiter anlegen &amp; Rechte vergeben").</div>
        <div style={aStil.callout("danger")}>Berichte enthalten oft Gesundheitsdaten — nur an Berechtigte weitergeben und PDFs nicht unverschlüsselt per E-Mail verschicken.</div>
      </>
    ),
  },
  {
    id: "kurse", kategorie: "Studio", titel: "Kurse verwalten",
    sichtbar: r => r?.rolle === "admin" || r?.module?.kurse,
    inhalt: (
      <>
        <p style={aStil.purpose}>Kurs anlegen, Teilnehmer eintragen und bei Bedarf eine E-Mail an alle Teilnehmer schicken.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Studio</b>, dann Reiter „🎓 Kurse" klicken.</li>
          <li><b>+ Neuer Kurs</b> klicken: Kursname*, Trainer, Beschreibung, Wochentag, Uhrzeit, Raum, Max. Teilnehmer eintragen, dann speichern.</li>
          <li>Einen Kurs in der Liste anklicken, um die Teilnehmerliste zu öffnen.</li>
          <li>Teilnehmer hinzufügen: unter „Teilnehmer hinzufügen" den Namen eintippen und aus den Vorschlägen auswählen, oder <b>+ Teilnehmer manuell hinzufügen</b> mit Vorname*, Nachname*, E-Mail, Handynummer.</li>
          <li><b>QR-Code</b> auf der Kurskarte klicken, um den QR-Code für die öffentliche Selbstregistrierung anzuzeigen, herunterzuladen oder den Link zu kopieren — im Kursraum aushängen, damit sich Teilnehmer (ob Mitglied oder nicht) selbst eintragen können. Über das ✏ neben einem Teilnehmer lassen sich dessen Kontaktdaten jederzeit nachträglich korrigieren.</li>
          <li>Bei Kursausfall, Zeitänderung o.&nbsp;ä.: <b>📧 E-Mail an alle</b> klicken — dort stehen bereits fertige Vorlagen (Ausfall, Zeitänderung, Erinnerung, Ferienregelung, Neustart) zur Auswahl bereit.</li>
        </ol>
      </>
    ),
  },
  {
    id: "aktionen", kategorie: "Studio", titel: "Aktionen verwalten",
    sichtbar: r => r?.rolle === "admin" || r?.module?.aktionen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Zeitlich begrenzte Sonderangebote pflegen — erscheinen automatisch im Online-Mitgliedsantrag und auf der Sonderaktion-Landingpage.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Studio</b>, dann Reiter „🏷 Aktionen" klicken.</li>
          <li><b>+ Neue Aktion</b> klicken.</li>
          <li>Aktionsname, Gültig von/bis, Vertragslaufzeit (Monate) und Gratis-Monate eintragen.</li>
          <li>Bei „Betroffene Tarife" die passenden Tarife ankreuzen — bleibt das Feld leer, gilt die Aktion für alle Tarife.</li>
          <li>Optional einen abweichenden Startpaketpreis eintragen oder „Anmeldegebühr entfällt" aktivieren.</li>
          <li>Häkchen bei <b>Aktiv</b> setzen, damit sie im öffentlichen Formular sichtbar wird, dann speichern.</li>
        </ol>
      </>
    ),
  },
  {
    id: "massen-email", kategorie: "Studio", titel: "Massen-E-Mail versenden",
    sichtbar: r => r?.rolle === "admin" || r?.module?.emails,
    inhalt: (
      <>
        <p style={aStil.purpose}>Eine Nachricht an eine ganze Mitgliedergruppe schicken — als gestaltete HTML-Mail im Studio-Design, mit dem echten Vornamen jedes Empfängers. Abgemeldete Adressen werden automatisch übersprungen.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Studio</b>, dann Reiter „✉ E-Mails" klicken (Unterreiter „Massen-E-Mail").</li>
          <li><b>Empfänger</b>-Gruppe wählen (alle aktiven/pausierten/gekündigten/ehemaligen Mitglieder oder alle zusammen). Ehemalige, die inzwischen als Wellpass-/Hansefit-/Sonstige-Nutzer wieder im Studio sind, werden automatisch ausgelassen.</li>
          <li>Optional oben bei <b>Vorlage</b> eine fertige Vorlage wählen — sie ersetzt Betreff und Text (vorher kommt eine Rückfrage).</li>
          <li><b>Betreff*</b> und <b>Text*</b> eintragen. Beim <b>Vorschautext</b> (optional) steht die graue Zeile, die das Postfach hinter dem Betreff anzeigt.</li>
          <li>Den Text gestalten: über die Leiste <b>Fett</b>, <b>Kursiv</b>, <b>Überschrift</b>, <b>• Liste</b>, <b>🔗 Link</b>, <b>🔘 Button</b> und <b>― Linie</b> — oder direkt tippen (<code>**fett**</code>, <code># Überschrift</code>, <code>[[Button-Text|https://…]]</code>).</li>
          <li>Mit den Platzhaltern <code>{"{Vorname}"}</code>, <code>{"{Nachname}"}</code> und <code>{"{Anrede}"}</code> wird jede Mail persönlich.</li>
          <li>Rechts siehst du live die <b>E-Mail-Vorschau</b> und die <b>Nur-Text-Fassung</b> (für alte Mailprogramme).</li>
          <li><b>✔ Empfänger prüfen</b> klicken — zeigt, wie viele Personen die Mail bekommen und wer ausgeschlossen wurde.</li>
          <li>Immer zuerst <b>✉ Testmail an mich</b> mit der eigenen Adresse senden und in Outlook <i>und</i> auf dem Handy ansehen.</li>
          <li>Erst dann <b>🚀 Jetzt an … Empfänger einzeln versenden</b> klicken. Der Versand steht danach im Aktivitätsprotokoll.</li>
        </ol>
        <div style={aStil.callout("good")}>Jede Mail enthält unten einen Abmeldelink. Wer ihn anklickt, bekommt künftig keine Werbe-Mails mehr; Rechnungen und Vertragsmails sind davon nicht betroffen. Der Weg über <b>📧 Outlook öffnen</b> (eine Sammelmail mit allen im BCC) bleibt als Alternative, kann aber nicht personalisieren.</div>
        <div style={aStil.callout()}>Eingehende Antworten landen im Unterreiter „Posteingang" derselben Seite.</div>
      </>
    ),
  },
  {
    id: "online-vertraege", kategorie: "Online Portal", titel: "Online-Verträge bearbeiten",
    sichtbar: r => r?.rolle === "admin" || r?.module?.online_vertraege,
    inhalt: (
      <>
        <p style={aStil.purpose}>Über die öffentliche Anmeldeseite (anmeldung.comefit.de bzw. „Jetzt Mitglied werden" auf der Webseite) eingegangene Mitgliedsanträge prüfen und freigeben. Neue Anträge melden sich per Push-Benachrichtigung.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Online Portal</b> klicken — Reiter „Online-Verträge" ist der erste.</li>
          <li>Bei einem neuen Antrag auf <b>Ansehen</b> klicken, um alle Angaben zu prüfen (Adresse, Tarif, IBAN, Vertragslaufzeit). Vertrag, AGB und Datenschutz lassen sich dort auch direkt als PDF öffnen.</li>
          <li>Passt alles: <b>Freigeben</b> klicken — legt automatisch ein neues Mitglied an und öffnet direkt dessen Profil.</li>
          <li>Passt etwas nicht: <b>Ablehnen</b> klicken, optional einen Grund eintragen.</li>
        </ol>
        <div style={aStil.callout("good")}>Ist ein Partner-/Familienrabatt angegeben, zeigt der Antrag automatisch, ob ein passendes bestehendes Mitglied dazu gefunden wurde.</div>
        <div style={aStil.callout()}><b>Vertragsart prüfen:</b> Bei einer <b>Aktion</b> (z.&nbsp;B. „Trainiere bis 2027 gratis") legt die Freigabe automatisch den Gratis-Zeitraum an. Bei <b>Firmenfitness</b> steht im Antrag „Firmenfitness – Firma: …" und der Beitrag beträgt 39,90&nbsp;€ — die Person soll beim ersten Besuch einen Nachweis (z.&nbsp;B. Mitarbeiterausweis) mitbringen, den du dann einsiehst.</div>
      </>
    ),
  },
  {
    id: "online-kuendigungen", kategorie: "Online Portal", titel: "Online-Kündigungen bearbeiten",
    sichtbar: r => r?.rolle === "admin" || r?.module?.kuendigungen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Über die öffentliche Kündigungsseite (kuendigung.comefit.de bzw. „Vertrag kündigen" im Fußbereich der Webseite) eingereichte Kündigungen prüfen, dem richtigen Mitglied zuordnen und bestätigen.</p>
        <div style={aStil.tabrow}>
          <span style={aStil.tabchip}>✓ Mitglied gefunden</span>
          <span style={aStil.tabchip}>⚠ Mögliche Treffer</span>
          <span style={aStil.tabchip}>✗ Kein Mitglied gefunden</span>
        </div>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Online Portal</b>, dann Reiter „Kündigungen" klicken.</li>
          <li>Eine Kündigung öffnen — die Software zeigt automatisch an, ob sie eindeutig einem Mitglied zugeordnet werden konnte.</li>
          <li>Bei „⚠ Mögliche Treffer" oder „✗ Kein Mitglied gefunden": das richtige Mitglied manuell zuordnen.</li>
          <li>Unklarheiten? <b>Rückfrage stellen</b> klicken und eine Nachricht formulieren, oder zunächst <b>In Prüfung setzen</b>.</li>
          <li>Ist alles klar: <b>Bestätigen &amp; senden</b> klicken, das Vertragsende festlegen und eine Vorlage wählen — verschickt automatisch die Bestätigungsmail ans Mitglied.</li>
        </ol>
        <div style={aStil.callout("good")}>Neue Kündigungen melden sich ebenfalls per Push-Benachrichtigung (siehe „Push-Benachrichtigungen einrichten").</div>
      </>
    ),
  },
  {
    id: "feedback", kategorie: "Online Portal", titel: "Feedback ansehen",
    sichtbar: r => r?.rolle === "admin" || r?.module?.feedback,
    inhalt: (
      <>
        <p style={aStil.purpose}>Anonymes Feedback von Mitgliedern und Besuchern durchsehen und bearbeiten (über die öffentliche Umfrage-Seite eingereicht).</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Online Portal</b>, dann Reiter „Feedback" klicken.</li>
          <li>Bei Bedarf nach Kategorie filtern (Kritik/Verbesserungsvorschlag/Lob/Sonstiges) oder die Sortierung umschalten.</li>
          <li>Einen Eintrag mit <b>In Bearbeitung</b> markieren, sobald jemand sich darum kümmert, und mit <b>Erledigt</b> abschließen.</li>
          <li>Wurde eine Antwort gewünscht und ein Kontakt (E-Mail) hinterlegt, lässt sich direkt über <b>Antworten</b> eine E-Mail öffnen.</li>
        </ol>
        <div style={aStil.callout("good")}>Über <b>QR-Code anzeigen</b> lässt sich der Feedback-QR-Code als PNG oder fertiges Poster-PDF herunterladen, z.&nbsp;B. zum Aushängen im Studio.</div>
      </>
    ),
  },
  {
    id: "kampagne-besucher", kategorie: "Online Portal", titel: "Besucherzahlen der Aktionsseiten",
    sichtbar: r => r?.rolle === "admin" || r?.module?.online_vertraege || r?.module?.kuendigungen || r?.module?.feedback,
    inhalt: (
      <>
        <p style={aStil.purpose}>Sehen, wie viele Besucher die Aktionsseite (aktion.comefit.de) und die Sonderaktion-Seite haben und woher sie kommen — ohne Cookies und ohne Personenbezug.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Online Portal</b>, dann Reiter „Aktion" klicken.</li>
          <li>Oben steht die Kampagne <b>„Trainiere bis 2027 gratis"</b> mit den Seiten (Station) und ihren Aufrufen <b>Heute</b>, <b>7 Tage</b>, <b>30 Tage</b> und <b>Gesamt</b> plus dem Anteil der letzten 30 Tage.</li>
          <li>Darunter zeigt <b>Herkunft der Besucher</b>, über welche Quelle sie kamen (z.&nbsp;B. Anzeige, E-Mail, Direktaufruf).</li>
          <li>Ganz unten stehen die Zahlen der Seite <b>/sonderaktion</b>.</li>
        </ol>
        <div style={aStil.callout()}>Ein Neuladen der Seite zählt mit — die Zahlen sind eine Orientierung, keine exakte Personenzahl. Für Anzeigen (Meta) gibt es eine eigene Auswertung im Werbekonto.</div>
      </>
    ),
  },
  {
    id: "termin", kategorie: "Kalender", titel: "Termin anlegen",
    sichtbar: r => r?.rolle === "admin" || r?.module?.kalender,
    inhalt: (
      <>
        <p style={aStil.purpose}>Neuen Termin im geteilten Google-Kalender eintragen, wahlweise per freier Zeitwahl oder anhand der bei Calendly hinterlegten freien Zeiten.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Kalender</b> klicken.</li>
          <li><b>+ Termin</b> klicken.</li>
          <li><b>Kalender</b> wählen: „Termine" oder „Schichtplan".</li>
          <li><b>Titel*</b> eintragen. Für einen ganztägigen Termin die Checkbox <b>Ganztägig</b> aktivieren.</li>
          <li>Bei Bedarf eine der unter „📅 Freie Zeiten laut Calendly" angezeigten Zeiten anklicken — übernimmt Start und Ende automatisch. Sonst <b>Start*</b>/<b>Ende</b> direkt eintragen.</li>
          <li>Optional eine <b>Beschreibung</b> ergänzen, dann speichern.</li>
        </ol>
        <div style={aStil.callout("good")}>Bestehende Termine lassen sich durch Anklicken im Kalender öffnen, bearbeiten oder löschen — inklusive Link zum Original-Termin in Google Kalender.</div>
        <div style={aStil.callout()}>Sobald ein Termin <b>über die App</b> eingetragen ist, bekommen alle Kollegen mit aktivierten Push-Benachrichtigungen eine Meldung („Neuer Termin eingetragen – Name: Datum – Titel"). Termine, die du direkt in Google Kalender anlegst, lösen keine Meldung aus. Kunden-Buchungen über Calendly erscheinen automatisch im Kalender („… und Comefit Fitness"), brauchen aber keinen Eintrag von Hand.</div>
      </>
    ),
  },
  {
    id: "tv-infoboard", kategorie: "TV", titel: "TV-Infoboard pflegen",
    sichtbar: r => r?.rolle === "admin" || r?.module?.tv,
    inhalt: (
      <>
        <p style={aStil.purpose}>Der Fernseher im Studio zeigt automatisch Hauptmeldung, News, Tipps, Kurse, Aktionen und die heutigen Zeiten. Hier pflegst du, was angezeigt wird. Die Anzeige selbst läuft auf der Seite „/tv" (bzw. „/tv2" für Veranstaltungen).</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>TV</b> klicken. Oben siehst du eine Live-Vorschau, darunter die Reiter.</li>
          <li><b>📢 Hauptmeldung:</b> Titel, Text, optional Start/Ende und ein Bild (1600 × 900 px, quer) eintragen, Bildausrichtung wählen und <b>Speichern</b>. Mit <b>Als Vorlage speichern</b> und „Vorlage auswählen… → Übernehmen" lassen sich wiederkehrende Meldungen schnell wiederverwenden.</li>
          <li><b>📰 News</b> und <b>💡 Schon gewusst?</b>: kurzen Text eintippen und <b>+ Hinzufügen</b>. Bei News gibt es optional Start- und Enddatum.</li>
          <li><b>🏋 Kurse:</b> Kursname, Wochentag, Start- und Endzeit (und optional einen Zusatztext wie „Anfänger willkommen") eintragen. Es werden maximal vier Kurse angezeigt.</li>
          <li><b>🏷️ Aktionen:</b> Titel, Text, Start/Ende, optional Link (als QR-Code) und Bild. Eine Aktion lässt sich als <b>★ Hauptaktion</b> hervorheben.</li>
          <li><b>📅 Heute:</b> Betreuungs-, Sauna- und Trainingszeiten je Wochentag — daraus berechnet das Infoboard auch, ob das Studio gerade betreut ist.</li>
          <li><b>⚙️ Einstellungen:</b> WLAN (SSID/Passwort/Verschlüsselung, erzeugt den QR-Code), Kontaktnummern für den Unbetreut-Hinweis, Google-Bewertungs- und Feedback-Link, Aktualisierungsintervall und TV-Design.</li>
        </ol>
        <div style={aStil.callout("good")}>Über <b>→ TV-Seite in neuem Tab öffnen</b> prüfst du die echte Anzeige. Änderungen erscheinen am Fernseher nach spätestens dem eingestellten Aktualisierungsintervall.</div>
      </>
    ),
  },
  {
    id: "mitarbeiter-anlegen", kategorie: "Mitarbeiter", titel: "Mitarbeiter anlegen & Rechte vergeben",
    sichtbar: r => r?.rolle === "admin",
    inhalt: (
      <>
        <p style={aStil.purpose}>Neuen Zugang für einen Kollegen anlegen und genau festlegen, was er sehen und tun darf.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Einstellungen</b>, dann Reiter „Benutzer" klicken.</li>
          <li><b>+ Neuer Mitarbeiter</b> klicken: Name*, E-Mail*, Passwort* eintragen.</li>
          <li>Feinrechte innerhalb „Mitglieder" auswählen: Mitglieder anlegen/bearbeiten/löschen, Pausieren, Kündigen.</li>
          <li>Unter „Modul-Zugriff (Navigation)" ankreuzen, welche Bereiche in der Seitenleiste überhaupt erscheinen sollen (z.&nbsp;B. SEPA, Statistiken, Rechnungen, Finanzen, Schichtplan, TV).</li>
          <li>Ist „Finanzen" aktiviert, zusätzlich die passenden Finanzmodul-Feinrechte setzen (Bankumsätze, Import, Auswertungen usw.) — sonst bleiben sie wirkungslos.</li>
          <li>Für <b>Zutritt (TTLock)</b> gibt es eigene Feinrechte (Zutritte ansehen/verwalten, Fernöffnung, Protokoll, Karten verwalten …) und für <b>Vorfall-/Unfallberichte</b> ebenfalls (ansehen, erstellen, bearbeiten, abschließen, löschen, PDF-Export) — bewusst einzeln vergebbar.</li>
          <li><b>Anlegen</b> klicken.</li>
        </ol>
        <div style={aStil.callout("good")}>Eine bestehende RFID-Karte lässt sich beim Bearbeiten eines Mitarbeiters direkt zuordnen — siehe die Anleitung „Karten &amp; Bänder anlegen" unter Zutritt (TTLock), das Vorgehen ist identisch.</div>
        <div style={aStil.callout()}>Jeder Mitarbeiter sieht in „Anleitungen" nur die Anleitungen zu Bereichen, für die er Rechte hat — wer etwas Bestimmtes nicht findet, hat meist das passende Modul-Recht noch nicht.</div>
      </>
    ),
  },
  {
    id: "trainingsplan", kategorie: "Training", titel: "Trainingsplan erstellen",
    sichtbar: r => r?.rolle === "admin" || r?.module?.training,
    inhalt: (
      <>
        <p style={aStil.purpose}>Individuellen Trainingsplan für ein Mitglied (oder eine externe Person) zusammenstellen und als PDF ausgeben oder im Mitgliedsprofil ablegen.</p>
        <ol style={aStil.ol}>
          <li>Trainingsplan-Ersteller aufrufen und mit den eigenen Zugangsdaten anmelden.</li>
          <li>Unter „Kunde" ein bestehendes Mitglied (oder Wellpass-/Hansefit-/Sonstige-Nutzer) suchen, oder Name frei eintippen, falls die Person noch nicht im System ist.</li>
          <li>Unter „Übung hinzufügen" Muskelgruppe und Gerät wählen. Je nach Gerät erscheinen automatisch die passenden Einstellungsfelder (z.&nbsp;B. Sitzposition, Lehnenposition) — bei Geräten ohne hinterlegte Felder steht stattdessen ein einfaches „Sitzhöhe"-Feld bereit.</li>
          <li>Sätze, WH, kg und eine Notiz ergänzen, dann <b>In Plan übernehmen</b> klicken. Für jede weitere Übung wiederholen.</li>
          <li>Optional die Checkbox <b>Mit Gerätebildern drucken</b> aktivieren — dann zeigt das PDF zu jeder Übung ein Foto des Geräts statt der kompakten Tabelle.</li>
          <li><b>Speichern</b> klicken, danach <b>Als PDF / Drucken</b> für die Ausgabe.</li>
        </ol>
        <div style={aStil.callout("good")}>Ist der Plan mit einem echten Mitglied verknüpft, wird automatisch eine aktuelle PDF-Kopie im „Dokumente"-Tab des Mitgliedsprofils abgelegt.</div>
      </>
    ),
  },
  {
    id: "tarife", kategorie: "Einstellungen", titel: "Tarife & Preise ändern",
    sichtbar: r => r?.rolle === "admin" || r?.module?.einstellungen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Monatspreise der Standardtarife anpassen — wirkt sich auf neue Mitglieder und den Online-Mitgliedsantrag aus, nicht rückwirkend auf bestehende Verträge.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Einstellungen</b>, dann Reiter „Tarife" klicken.</li>
          <li>Bei jedem Tarif den neuen Preis eintragen.</li>
          <li><b>Speichern</b> klicken.</li>
        </ol>
      </>
    ),
  },
  {
    id: "email-vorlagen", kategorie: "Einstellungen", titel: "E-Mail-Vorlagen bearbeiten",
    sichtbar: r => r?.rolle === "admin" || r?.module?.einstellungen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Feste Textbausteine für automatisch vorausgefüllte E-Mails pflegen — aktuell für Rücklastschriften und Kündigungsbestätigungen.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Einstellungen</b>, dann Reiter „E-Mail-Vorlagen" klicken.</li>
          <li>Oben die passende Kategorie wählen (Rücklastschrift oder Kündigungsbestätigung) — darunter stehen die dafür verfügbaren Platzhalter, z.&nbsp;B. <code>{"{VORNAME}"}</code>.</li>
          <li><b>+ Neue Vorlage</b> klicken oder eine bestehende über <b>Bearbeiten</b> öffnen.</li>
          <li>Name, Betreff und Text eintragen (Platzhalter aus Schritt 2 verwenden), dann <b>Speichern</b>.</li>
        </ol>
      </>
    ),
  },
  {
    id: "push", kategorie: "Einstellungen", titel: "Push-Benachrichtigungen einrichten",
    sichtbar: r => r?.rolle === "admin" || r?.module?.einstellungen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Auf dem Handy oder Rechner eine Meldung bekommen, wenn etwas Neues eingeht — ohne die Software offen zu haben. Jedes Gerät richtest du einzeln ein.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Einstellungen</b>, dann Reiter „🔔 Benachrichtigungen" klicken.</li>
          <li>Auf <b>Aktivieren</b> klicken und die Frage des Browsers mit „Zulassen" beantworten. Danach steht dort „✓ Auf diesem Gerät aktiv."</li>
          <li>Anhaken, wofür du Meldungen willst: <b>Feedback</b>, <b>Kündigungen</b>, <b>Online-Verträgen</b> und <b>Shelly-Status (Studio, Sauna, …)</b>. Dann <b>Speichern</b>.</li>
          <li>Mit <b>Deaktivieren</b> schaltest du die Meldungen auf diesem Gerät wieder ab.</li>
        </ol>
        <div style={aStil.callout("good")}>Auf dem iPhone funktionieren Push-Meldungen nur, wenn die Software über „Zum Home-Bildschirm" als App installiert wurde. Auf Android reicht Chrome.</div>
        <div style={aStil.callout()}><b>Zusätzlich</b>, ohne Auswahl: Wird <b>in der App ein Termin eingetragen</b> („Kalender → + Termin"), bekommen alle Geräte mit aktiven Benachrichtigungen die Meldung „Neuer Termin eingetragen". Dasselbe gilt für Alarmmeldungen am Schloss (zwei Fehlversuche in Folge).</div>
      </>
    ),
  },
  {
    id: "agb", kategorie: "Einstellungen", titel: "AGB & Datenschutz bearbeiten",
    sichtbar: r => r?.rolle === "admin" || r?.module?.einstellungen,
    inhalt: (
      <>
        <p style={aStil.purpose}>Rechtstexte pflegen, die im Online-Mitgliedsantrag (/mitglied-werden) angezeigt und in die Vertrags-PDFs übernommen werden.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Einstellungen</b>, dann Reiter „AGB &amp; Datenschutz" klicken.</li>
          <li><b>AGB-Text</b> und/oder <b>Datenschutz-Text</b> anpassen.</li>
          <li><b>Speichern</b> klicken.</li>
        </ol>
        <div style={aStil.callout()}>Änderungen wirken sich nur auf neue Anträge aus, nicht auf bereits abgeschlossene Verträge.</div>
      </>
    ),
  },
  {
    id: "backup", kategorie: "Einstellungen", titel: "Backup erstellen",
    sichtbar: r => r?.rolle === "admin",
    inhalt: (
      <>
        <p style={aStil.purpose}>Vollständige Sicherung von Datenbank, Dokumenten und Konfiguration als ZIP herunterladen.</p>
        <ol style={aStil.ol}>
          <li>In der Navigation auf <b>Einstellungen</b>, dann Reiter „Backup" klicken.</li>
          <li><b>Zugriffsschlüssel</b> eingeben.</li>
          <li><b>💾 Backup jetzt erstellen &amp; herunterladen</b> klicken.</li>
        </ol>
        <div style={aStil.callout()}>Enthält datenbank.sql, alle hochgeladenen Mitgliederdateien, den Google-Kalender-Token sowie eine Wiederherstellungsanleitung. Empfehlung: wöchentlich erstellen und an einem sicheren Ort außerhalb des Studios aufbewahren.</div>
        <div style={aStil.callout("danger")}>Der separate Button „📦 Alles sichern (Code + Struktur)" ist nur für technische Zwecke gedacht — die Datei enthält echte Zugangsdaten (Datenbank-Passwort, API-Schlüssel) und sollte ausschließlich sicher verwahrt werden.</div>
      </>
    ),
  },
];

function AnleitungenSeite({ rechte }) {
  const sichtbareGuides = ANLEITUNGEN.filter(g => g.sichtbar(rechte));
  const kategorien = [];
  sichtbareGuides.forEach(g => { if (!kategorien.includes(g.kategorie)) kategorien.push(g.kategorie); });
  const [aktivesId, setAktivesId] = useState(sichtbareGuides[0]?.id || null);
  const aktiv = sichtbareGuides.find(g => g.id === aktivesId) || sichtbareGuides[0] || null;
  const [bildVergroessert, setBildVergroessert] = useState(false);

  if (sichtbareGuides.length === 0) {
    return <p style={{ color: THEME.colors.textMuted }}>Für deine aktuellen Rechte sind aktuell keine Anleitungen verfügbar.</p>;
  }

  return (
    <div>
      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 20 }}>
        Zeigt nur Anleitungen zu Bereichen, auf die du auch tatsächlich Zugriff hast.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 24, alignItems: "start" }}>
        <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {kategorien.map(kat => (
            <div key={kat} style={{ marginBottom: 10 }}>
              <p style={{ color: THEME.colors.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", margin: "0 0 4px 10px" }}>{kat}</p>
              {sichtbareGuides.filter(g => g.kategorie === kat).map(g => (
                <button key={g.id} onClick={() => { setAktivesId(g.id); setBildVergroessert(false); }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6, border: "none", cursor: "pointer",
                    background: aktiv?.id === g.id ? THEME.colors.primary : "transparent", color: aktiv?.id === g.id ? "#fff" : THEME.colors.textSecondary,
                    fontSize: THEME.fontSize.sm, fontWeight: aktiv?.id === g.id ? 700 : 500 }}>
                  {g.titel}
                </button>
              ))}
            </div>
          ))}
        </nav>
        {aktiv && (
          <div style={s.karte}>
            <h3 style={{ color: THEME.colors.text, margin: "0 0 4px", fontSize: THEME.fontSize.xl }}>{aktiv.titel}</h3>
            <p style={{ color: THEME.colors.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", margin: "0 0 16px" }}>{aktiv.kategorie}</p>
            {aktiv.bild && (
              <img src={`${process.env.PUBLIC_URL}/anleitungen-bilder/${aktiv.id}.png`} alt={`Screenshot: ${aktiv.titel}`}
                style={aStil.bild} onClick={() => setBildVergroessert(true)} />
            )}
            {aktiv.inhalt}
          </div>
        )}
      </div>
      {bildVergroessert && aktiv && (
        <div style={s.modalBg} onClick={() => setBildVergroessert(false)}>
          <img src={`${process.env.PUBLIC_URL}/anleitungen-bilder/${aktiv.id}.png`} alt={`Screenshot: ${aktiv.titel}`}
            style={{ maxWidth: "95vw", maxHeight: "95vh", borderRadius: 8, cursor: "zoom-out" }} onClick={() => setBildVergroessert(false)} />
        </div>
      )}
    </div>
  );
}

// ============ EINSTELLUNGEN ============
function EinstellungenSeite({ rechte, onPersonOeffnen, studioSeite = null }) {
  // "Studio" ist Unterpunkt der Einstellungen (frueher eigener Nav-Punkt); beide Modulrechte bleiben eigenstaendig
  const darfStudio = rechte?.rolle === "admin" || STUDIO_MODULE_KEYS.some(k => !!rechte?.module?.[k]);
  const darfEinstellungen = rechte?.rolle === "admin" || !!rechte?.module?.einstellungen;
  const [einstellungen, setEinstellungen] = useState({});
  const [form, setForm] = useState({});
  const [benutzer, setBenutzer] = useState([]);
  const LEER_MODUL_RECHTE = {
    mitglieder: true, sepa: false, statistiken: false, rechnungen: false, kurse: true, mitarbeiter: true,
    emails: true, kalender: true, studio: true, online_vertraege: true, kuendigungen: true, feedback: true,
    aktionen: true, einstellungen: false, wellpass: false, hansefit: false, sonstige_nutzer: false, checkins_extern: false, checkins_zu_klaeren: false,
    ttlock: false, ttlock_mitgliederzutritt_ansehen: false, ttlock_mitgliederzutritt_verwalten: false,
    ttlock_sync_vorschau: false, ttlock_sync_ausfuehren: false, ttlock_fernoeffnen: false,
    ttlock_protokoll_ansehen: false, ttlock_einstellungen: false, ttlock_mitarbeiter_verwalten: false,
    ttlock_karten_verwalten: false, tv: false,
  };
  const LEER_BENUTZER = { name: "", email: "", passwort: "", rolle: "mitarbeiter", darf_mitglieder_anlegen: true, darf_mitglieder_bearbeiten: true, darf_mitglieder_loeschen: false, darf_pausieren: true, darf_kuendigen: true, module: { ...LEER_MODUL_RECHTE } };
  const [neuerBenutzer, setNeuerBenutzer] = useState(LEER_BENUTZER);
  const [benutzerFormOffen, setBenutzerFormOffen] = useState(false);
  const [bearbeitenBenutzer, setBearbeitenBenutzer] = useState(null);
  const [passwortResetId, setPasswortResetId] = useState(null);
  const [neuesPasswort, setNeuesPasswort] = useState("");
  const [rfidEingabe, setRfidEingabe] = useState("");
  const [rfidMedientyp, setRfidMedientyp] = useState("");
  const [rfidFehler, setRfidFehler] = useState("");
  const [gespeichert, setGespeichert] = useState(false);
  const [aktiveTab, setAktiveTab] = useState(() => {
    try { const t = sessionStorage.getItem(START_TAB_SCHLUESSEL); if (t) { sessionStorage.removeItem(START_TAB_SCHLUESSEL); return t; } } catch {}
    return darfEinstellungen ? "studio" : "studiobereich";
  });
  const [backupSchluessel, setBackupSchluessel] = useState("");
  const [backupLaedt, setBackupLaedt] = useState(false);
  const [vollbackupLaedt, setVollbackupLaedt] = useState(false);
  const [vollbackupBestaetigt, setVollbackupBestaetigt] = useState(false);
  const [ttlockStatus, setTtlockStatus] = useState(null);
  const [ttlockVerbindungErgebnis, setTtlockVerbindungErgebnis] = useState(null);
  const [ttlockVerbindungLaedt, setTtlockVerbindungLaedt] = useState(false);
  const [ttlockVorschau, setTtlockVorschau] = useState(null);
  const [ttlockVorschauLaedt, setTtlockVorschauLaedt] = useState(false);
  const [ttlockProtokoll, setTtlockProtokoll] = useState(null);
  const [ttlockNurFehler, setTtlockNurFehler] = useState(false);
  const [ttlockProtokollLaedt, setTtlockProtokollLaedt] = useState(false);
  const [ttlockProtokollSuche, setTtlockProtokollSuche] = useState("");
  // Leer = wie bisher die letzten 100 Ereignisse ueber alle Tage; gesetzt = gezielt nur dieser
  // Tag (Nutzer-Wunsch 2026-07-20, z.B. "wer war gestern eingecheckt").
  const [ttlockProtokollDatum, setTtlockProtokollDatum] = useState("");
  // Selbstbedienungs-Werkzeug fuer neue Karten-/Band-Chargen mit abweichendem Praefix-Byte -
  // siehe TTLockChargenRegel im Backend fuer den Hintergrund.
  const [chargenMedientyp, setChargenMedientyp] = useState("band");
  const [chargenPaare, setChargenPaare] = useState([{ comefit_nummer: "", echte_nummer: "" }]);
  const [chargenBerechnenLaedt, setChargenBerechnenLaedt] = useState(false);
  const [chargenErgebnis, setChargenErgebnis] = useState(null);
  const [chargenBezeichnung, setChargenBezeichnung] = useState("");
  const [chargenVon, setChargenVon] = useState("");
  const [chargenBis, setChargenBis] = useState("");
  const [chargenSpeichernLaedt, setChargenSpeichernLaedt] = useState(false);
  const [chargenListe, setChargenListe] = useState([]);
  const [logoLaedt, setLogoLaedt] = useState(false);
  const [logoFehler, setLogoFehler] = useState("");
  const [logoVersion, setLogoVersion] = useState(() => Date.now());

  const handleBackupErstellen = async () => {
    setBackupLaedt(true);
    try {
      const res = await fetch(`${API}/backup/erstellen`, { headers: { "X-Backup-Schluessel": backupSchluessel } });
      if (!res.ok) { alert("Zugriff verweigert – Schlüssel prüfen."); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comefit_backup_${new Date().toISOString().split("T")[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Backup konnte nicht erstellt werden.");
    }
    setBackupLaedt(false);
  };

  const handleVollbackup = async () => {
    setVollbackupLaedt(true);
    try {
      const res = await fetch(`${API}/backup/vollbackup`, { headers: { "X-Backup-Schluessel": backupSchluessel } });
      if (!res.ok) { alert("Zugriff verweigert – Schlüssel prüfen."); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comefit_vollbackup_${new Date().toISOString().split("T")[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Vollbackup konnte nicht erstellt werden.");
    }
    setVollbackupLaedt(false);
  };

  useEffect(() => {
    fetch(`${API}/einstellungen/`).then(r => r.json()).then(data => { setEinstellungen(data); setForm(data); });
    fetch(`${API}/benutzer/`).then(r => r.json()).then(setBenutzer);
    fetch(`${API}/ttlock/konfigurationsstatus`).then(r => r.ok ? r.json() : null).then(setTtlockStatus).catch(() => {});
    ladeChargenListe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function ladeChargenListe() {
    fetch(`${API}/ttlock/chargen`).then(r => r.ok ? r.json() : []).then(setChargenListe).catch(() => {});
  }

  const handleChargenPaarAendern = (index, feld, wert) => {
    setChargenPaare(paare => paare.map((p, i) => i === index ? { ...p, [feld]: wert } : p));
  };
  const handleChargenPaarHinzufuegen = () => setChargenPaare(paare => [...paare, { comefit_nummer: "", echte_nummer: "" }]);
  const handleChargenPaarEntfernen = (index) => setChargenPaare(paare => paare.filter((_, i) => i !== index));

  const handleChargenBerechnen = async () => {
    setChargenBerechnenLaedt(true);
    setChargenErgebnis(null);
    try {
      const paare = chargenPaare.filter(p => p.comefit_nummer !== "" && p.echte_nummer !== "").map(p => ({ comefit_nummer: Number(p.comefit_nummer), echte_nummer: Number(p.echte_nummer) }));
      const res = await fetch(`${API}/ttlock/chargen/berechnen`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ medientyp: chargenMedientyp, paare }),
      });
      const daten = await res.json();
      if (!res.ok) { alert(daten.detail || "Berechnung fehlgeschlagen."); return; }
      setChargenErgebnis(daten);
      setChargenVon(String(daten.min_comefit_nummer));
      setChargenBis("");
    } catch {
      alert("Berechnung fehlgeschlagen.");
    }
    setChargenBerechnenLaedt(false);
  };

  const handleChargenSpeichern = async () => {
    setChargenSpeichernLaedt(true);
    try {
      const res = await fetch(`${API}/ttlock/chargen`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          medientyp: chargenMedientyp, praefix_byte: chargenErgebnis.einheitliches_praefix,
          nummer_von: chargenVon !== "" ? Number(chargenVon) : null, nummer_bis: chargenBis !== "" ? Number(chargenBis) : null,
          bezeichnung: chargenBezeichnung || null,
        }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Speichern fehlgeschlagen."); return; }
      setChargenErgebnis(null);
      setChargenPaare([{ comefit_nummer: "", echte_nummer: "" }]);
      setChargenBezeichnung(""); setChargenVon(""); setChargenBis("");
      ladeChargenListe();
    } catch {
      alert("Speichern fehlgeschlagen.");
    }
    setChargenSpeichernLaedt(false);
  };

  const handleChargenLoeschen = async (id) => {
    if (!window.confirm("Diese Chargen-Regel wirklich löschen?")) return;
    const res = await fetch(`${API}/ttlock/chargen/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladeChargenListe();
  };

  const handleTtlockVerbindungTesten = async () => {
    setTtlockVerbindungLaedt(true);
    setTtlockVerbindungErgebnis(null);
    try {
      const res = await fetch(`${API}/ttlock/verbindung-testen`, { method: "POST" });
      setTtlockVerbindungErgebnis(await res.json());
    } catch {
      setTtlockVerbindungErgebnis({ erfolg: false, fehler: "Anfrage fehlgeschlagen." });
    }
    setTtlockVerbindungLaedt(false);
  };

  const handleTtlockVorschauLaden = async () => {
    setTtlockVorschauLaedt(true);
    try {
      const res = await fetch(`${API}/ttlock/sync-vorschau`);
      if (res.ok) setTtlockVorschau(await res.json());
    } catch {}
    setTtlockVorschauLaedt(false);
  };

  const handleTtlockProtokollLaden = async (datumOverride) => {
    const datum = datumOverride !== undefined ? datumOverride : ttlockProtokollDatum;
    setTtlockProtokollLaedt(true);
    try {
      const url = datum ? `${API}/ttlock/protokoll?datum=${datum}` : `${API}/ttlock/protokoll`;
      const res = await fetch(url);
      if (res.ok) setTtlockProtokoll(await res.json());
    } catch {}
    setTtlockProtokollLaedt(false);
  };
  const handleTtlockProtokollDatumWaehlen = (datum) => {
    setTtlockProtokollDatum(datum);
    handleTtlockProtokollLaden(datum);
  };

  const handleSpeichern = async () => {
    const res = await fetch(`${API}/einstellungen/batch`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    if (!res.ok) { alert("Speichern fehlgeschlagen."); return; }
    setGespeichert(true);
    setTimeout(() => setGespeichert(false), 3000);
  };

  const handleLogoHochladen = async (datei) => {
    if (!datei) return;
    setLogoLaedt(true);
    setLogoFehler("");
    try {
      const formData = new FormData();
      formData.append("datei", datei);
      const res = await fetch(`${API}/einstellungen/logo`, { method: "POST", body: formData });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Logo konnte nicht hochgeladen werden.");
      }
      // Neu laden, damit das neue Logo ueberall (Sidebar, Login-Bildschirm etc.) sofort
      // sichtbar ist, nicht nur in dieser Vorschau - gleiches Muster wie beim Design-Speichern.
      setLogoVersion(Date.now());
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      setLogoFehler(e.message || "Logo konnte nicht hochgeladen werden.");
    }
    setLogoLaedt(false);
  };

  const handleBenutzerAnlegen = async () => {
    let benutzerId = bearbeitenBenutzer?.id;
    if (bearbeitenBenutzer) {
      const res = await fetch(`${API}/benutzer/${bearbeitenBenutzer.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(neuerBenutzer) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || "Speichern fehlgeschlagen.");
        return;
      }
    } else {
      if (!neuerBenutzer.name || !neuerBenutzer.email || !neuerBenutzer.passwort) { alert("Name, E-Mail und Passwort sind pflicht!"); return; }
      const res = await fetch(`${API}/benutzer/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(neuerBenutzer) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { alert(data.detail || "Anlegen fehlgeschlagen."); return; }
      benutzerId = data.id;
    }
    if (benutzerId) {
      const modulRes = await fetch(`${API}/benutzer/${benutzerId}/modul-rechte`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(neuerBenutzer.module || {}) });
      if (!modulRes.ok) { alert("Benutzer gespeichert, aber die Modul-Rechte konnten nicht aktualisiert werden."); }
    }
    fetch(`${API}/benutzer/`).then(r => r.json()).then(setBenutzer);
    setBenutzerFormOffen(false);
    setBearbeitenBenutzer(null);
    setNeuerBenutzer(LEER_BENUTZER);
  };

  const handleBenutzerBearbeiten = async (b) => {
    setBearbeitenBenutzer(b);
    const moduleRechte = await fetch(`${API}/benutzer/${b.id}/modul-rechte`).then(r => r.json()).catch(() => ({ ...LEER_MODUL_RECHTE }));
    setNeuerBenutzer({ ...b, passwort: "", module: moduleRechte });
    setRfidEingabe(b.rfid_kartennummer || "");
    setRfidMedientyp(b.rfid_medientyp || "");
    setRfidFehler("");
    setBenutzerFormOffen(true);
  };

  const handleRfidZuweisen = async () => {
    setRfidFehler("");
    let karte = rfidEingabe.trim();
    const umgerechnet = pcLeserZuKartennummer(karte);
    if (umgerechnet != null) { karte = String(umgerechnet); setRfidEingabe(karte); }
    const res = await fetch(`${API}/benutzer/${bearbeitenBenutzer.id}/rfid`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rfid_kartennummer: karte || null, medientyp: rfidMedientyp || null }) });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setRfidFehler(d.detail || "RFID konnte nicht zugewiesen werden.");
      return;
    }
    fetch(`${API}/benutzer/`).then(r => r.json()).then(setBenutzer);
  };

  const handleRfidEntfernen = async () => {
    const res = await fetch(`${API}/benutzer/${bearbeitenBenutzer.id}/rfid`, { method: "DELETE" });
    if (!res.ok) { setRfidFehler("RFID konnte nicht entfernt werden."); return; }
    setRfidEingabe("");
    setRfidFehler("");
    fetch(`${API}/benutzer/`).then(r => r.json()).then(setBenutzer);
  };

  const handlePasswortReset = async () => {
    if (!neuesPasswort || neuesPasswort.length < 4) { alert("Bitte mindestens 4 Zeichen eingeben!"); return; }
    const res = await fetch(`${API}/benutzer/${passwortResetId}/passwort-reset`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ neues_passwort: neuesPasswort }) });
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Passwort konnte nicht zurückgesetzt werden."); return; }
    setPasswortResetId(null); setNeuesPasswort("");
    alert("Passwort erfolgreich zurückgesetzt!");
  };

  const handleBenutzerLoeschen = async (id) => {
    if (!window.confirm("Benutzer deaktivieren?")) return;
    const res = await fetch(`${API}/benutzer/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Deaktivieren fehlgeschlagen."); return; }
    fetch(`${API}/benutzer/`).then(r => r.json()).then(setBenutzer);
  };

  const tabs = [...(darfEinstellungen ? ["studio", "tarife", "rechtstexte", "buchhaltung", "emailvorlagen", "design", "benutzer", "zutritt", ...(rechte?.rolle === "admin" ? ["rfidpruefliste"] : []), "benachrichtigungen", "backup", "anleitungen", "dokumentation"] : []), ...(darfStudio && studioSeite ? ["studiobereich"] : [])];
  const tabLabel = { studiobereich: "Studio", studio: "Studio & SEPA", tarife: "Tarife", rechtstexte: "AGB & Datenschutz", buchhaltung: "Buchhaltung", emailvorlagen: "E-Mail-Vorlagen", design: "Design", benutzer: "Benutzer", zutritt: "Zutritt (TTLock)", rfidpruefliste: "🪪 RFID-Prüfliste", benachrichtigungen: "🔔 Benachrichtigungen", backup: "Backup", anleitungen: "Anleitungen", dokumentation: "Dokumentation" };

  const STANDARD_FARBEN = {
    design_primary: "#2b8cc4", design_accent: "#e8a020", design_background: "#0f1923",
    design_surface: "#1a2535", design_text: "#ffffff", design_textMuted: "#8899aa",
    design_border: "#2b3d52", design_hover: "#2b3d52", design_success: "#22c55e",
    design_warning: "#f59e0b", design_danger: "#ef4444", design_tabAktiv: "#2b8cc4", design_tabInaktiv: "#2b3d52",
  };
  const DESIGN_PRESETS = [
    {
      name: "ComeFit Standard",
      beschreibung: "Das aktuelle, gewohnte dunkle Blau",
      farben: STANDARD_FARBEN,
    },
    {
      name: "Modern Dunkel",
      beschreibung: "Fast schwarz mit Indigo-Akzent, angelehnt an Linear/Vercel",
      farben: {
        design_primary: "#6366f1", design_accent: "#f59e0b", design_background: "#0a0a0f",
        design_surface: "#18181f", design_text: "#f4f4f5", design_textMuted: "#9ca3af",
        design_border: "#27272f", design_hover: "#27272f", design_success: "#22c55e",
        design_warning: "#f59e0b", design_danger: "#ef4444", design_tabAktiv: "#6366f1", design_tabInaktiv: "#27272f",
      },
    },
    {
      name: "Hell",
      beschreibung: "Helles Theme mit blauem Akzent",
      farben: {
        design_primary: "#2563eb", design_accent: "#d97706", design_background: "#f5f7fa",
        design_surface: "#ffffff", design_text: "#111827", design_textMuted: "#6b7280",
        design_border: "#e2e8f0", design_hover: "#eef2f7", design_success: "#16a34a",
        design_warning: "#d97706", design_danger: "#dc2626", design_tabAktiv: "#2563eb", design_tabInaktiv: "#eef2f7",
      },
    },
    {
      name: "Ozean",
      beschreibung: "Dunkles Petrol/Türkis",
      farben: {
        design_primary: "#06b6d4", design_accent: "#f59e0b", design_background: "#071a1f",
        design_surface: "#0f2830", design_text: "#e6fbff", design_textMuted: "#7fa8b0",
        design_border: "#1c3d45", design_hover: "#1c3d45", design_success: "#22c55e",
        design_warning: "#f59e0b", design_danger: "#f87171", design_tabAktiv: "#06b6d4", design_tabInaktiv: "#1c3d45",
      },
    },
    {
      name: "Sonnenuntergang",
      beschreibung: "Warmes Dunkel mit Orange-Akzent",
      farben: {
        design_primary: "#f97316", design_accent: "#fbbf24", design_background: "#121212",
        design_surface: "#1e1e1e", design_text: "#ffffff", design_textMuted: "#a3a3a3",
        design_border: "#333333", design_hover: "#333333", design_success: "#4ade80",
        design_warning: "#fbbf24", design_danger: "#f87171", design_tabAktiv: "#f97316", design_tabInaktiv: "#333333",
      },
    },
  ];

  const handlePresetAnwenden = (farben) => {
    setForm(f => ({ ...f, ...farben }));
  };

  const DESIGN_FELDER = [
    ["design_primary", "Primärfarbe", "Haupt-Akzentfarbe: Buttons, aktive Navigation, Kartenlinien"],
    ["design_accent", "Akzentfarbe", "Logo-Schriftzug-Akzent"],
    ["design_background", "Hintergrundfarbe", "Seitenhintergrund"],
    ["design_surface", "Kartenfarbe", "Karten, Sidebar, Dialoge"],
    ["design_text", "Textfarbe", "Haupttext"],
    ["design_textMuted", "Textfarbe (gedimmt)", "Labels, Nebentexte"],
    ["design_border", "Rahmenfarbe", "Trennlinien, Eingabefeld-Rahmen"],
    ["design_hover", "Hover-Farbe", "Hervorhebung beim Überfahren mit der Maus"],
    ["design_success", "Erfolgsfarbe", "Aktiv, positiv, bestätigt"],
    ["design_warning", "Warnfarbe", "Pausiert, Hinweise"],
    ["design_danger", "Fehlerfarbe", "Kündigung, Löschen, Fehler"],
    ["design_tabAktiv", "Tab-Buttons (aktiv)", "Hintergrund der aktiven Reiter oben bei Mitglieder/Wellpass-Nutzer/Hansefit-Nutzer/Sonstige – unabhängig von der Primärfarbe, Textfarbe passt sich automatisch an"],
    ["design_tabInaktiv", "Graue Buttons & inaktive Tabs", "Hintergrund aller grauen Buttons und nicht ausgewählten Reiter app-weit (z. B. Zurücksetzen-Button, Schriftgröße Klein/Groß) – Textfarbe passt sich automatisch an"],
  ];

  const handleDesignSpeichern = async () => {
    const res = await fetch(`${API}/einstellungen/batch`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    if (!res.ok) { alert("Design konnte nicht gespeichert werden."); return; }
    designUebernehmenAusEinstellungen(form);
    schriftgroesseUebernehmenAusEinstellungen(form);
    alert("Design gespeichert – die Seite wird jetzt neu geladen, damit die Änderungen angewendet werden.");
    window.location.reload();
  };

  const handleDesignZuruecksetzen = async () => {
    if (!window.confirm("Design wirklich auf den ComeFit-Standard zurücksetzen?")) return;
    const neuesForm = { ...form, ...STANDARD_FARBEN, design_schriftgroesse: "standard" };
    const res = await fetch(`${API}/einstellungen/batch`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(neuesForm) });
    if (!res.ok) { alert("Zurücksetzen fehlgeschlagen."); return; }
    designUeberschreibungenZuruecksetzen();
    schriftgroesseZuruecksetzen();
    window.location.reload();
  };

  return (
    <div style={{ maxWidth: 1100 }}>
      <h1 style={s.heading}>Einstellungen</h1>

      {gespeichert && <div style={{ background: "#22c55e22", border: "1px solid #22c55e", borderRadius: 8, padding: "10px 16px", marginBottom: 16, color: THEME.colors.success }}>✓ Gespeichert!</div>}

      <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setAktiveTab(tab)} style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: aktiveTab === tab ? THEME.colors.primary : THEME.colors.surfaceMuted, color: aktiveTab === tab ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md }}>{tabLabel[tab]}</button>
        ))}
      </div>

      {aktiveTab === "studiobereich" && darfStudio && studioSeite}

      {aktiveTab === "studio" && (
        <div style={s.karte}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 20px", fontSize: THEME.fontSize.xl }}>Logo</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24, flexWrap: "wrap" }}>
            <div style={{ background: "#fff", borderRadius: 8, padding: 12, display: "flex", alignItems: "center", justifyContent: "center", minWidth: 180, minHeight: 72 }}>
              <img key={logoVersion} src={`${API}/einstellungen/logo?v=${logoVersion}`} alt="Logo" style={{ maxWidth: 220, maxHeight: 70, objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
            </div>
            <div>
              <label style={{ ...s.btnGray, display: "inline-block", cursor: "pointer", padding: "10px 18px" }}>
                {logoLaedt ? "Wird hochgeladen…" : "Logo ändern"}
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: "none" }} disabled={logoLaedt}
                  onChange={e => { const datei = e.target.files?.[0]; e.target.value = ""; handleLogoHochladen(datei); }} />
              </label>
              <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 8 }}>PNG, JPG, SVG oder WebP. Wird sofort überall übernommen (Rechnungen, Verträge, Anmeldung).</div>
              {logoFehler && <div style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 6 }}>{logoFehler}</div>}
            </div>
          </div>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 20px", fontSize: THEME.fontSize.xl }}>Studio-Daten</h3>
          {[["studio_name","Studioname"],["studio_strasse","Straße"],["studio_plz","PLZ"],["studio_ort","Ort"],["studio_telefon","Telefon"],["studio_email","E-Mail"]].map(([key, label]) => (
            <div key={key} style={s.inputGroup}><label style={s.label}>{label}</label><input style={s.input} value={form[key] || ""} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} /></div>
          ))}
          <h3 style={{ color: THEME.colors.text, margin: "20px 0 16px", fontSize: THEME.fontSize.xl }}>SEPA-Daten</h3>
          {[["sepa_iban","Deine IBAN"],["sepa_glaeubiger_id","Gläubiger-ID"]].map(([key, label]) => (
            <div key={key} style={s.inputGroup}><label style={s.label}>{label}</label><input style={s.input} value={form[key] || ""} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} /></div>
          ))}
          <h3 style={{ color: THEME.colors.text, margin: "20px 0 16px", fontSize: THEME.fontSize.xl }}>Sonstige</h3>
          <div style={s.inputGroup}><label style={s.label}>Kündigungsfrist (Monate)</label><input style={s.input} type="number" value={form.kuendigungsfrist_monate || "1"} onChange={e => setForm(f => ({ ...f, kuendigungsfrist_monate: e.target.value }))} /></div>
          <div style={s.inputGroup}><label style={s.label}>Partnerrabatt (€)</label><input style={s.input} type="number" step="0.01" value={form.partnerrabatt_euro || "5"} onChange={e => setForm(f => ({ ...f, partnerrabatt_euro: e.target.value }))} /></div>
          <h3 style={{ color: THEME.colors.text, margin: "20px 0 16px", fontSize: THEME.fontSize.xl }}>Mitglieder-App (my.comefit.de)</h3>
          <div style={s.inputGroup}>
            <label style={s.label}>Erlaubte Studio-IP(s) für die Türöffnung</label>
            <input style={s.input} value={form.member_app_studio_ip_liste || ""} onChange={e => setForm(f => ({ ...f, member_app_studio_ip_liste: e.target.value }))} placeholder="z. B. 85.123.45.67 (mehrere mit Komma trennen)" />
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 4 }}>
              Die öffentliche IP-Adresse des ComeFit-Gast-WLANs bzw. Internetanschlusses. Nur wenn ein Mitglied darüber verbunden ist, funktioniert „Tür öffnen" in der App. Leer = Türöffnung über die App komplett gesperrt.
            </p>
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>WhatsApp-Nummer fürs Kontakt-Formular</label>
            <input style={s.input} value={form.member_app_whatsapp_nummer || ""} onChange={e => setForm(f => ({ ...f, member_app_whatsapp_nummer: e.target.value }))} placeholder="z. B. 0157 1234567" />
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 4 }}>
              Beliebiges Format möglich. Leer = WhatsApp-Button in der App wird ausgeblendet.
            </p>
          </div>
          <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto", marginTop: 20 }}>Speichern</button>
        </div>
      )}

      {aktiveTab === "tarife" && (
        <div style={s.karte}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 20px", fontSize: THEME.fontSize.xl }}>Tarife & Preise</h3>
          {TARIFE.map(tarif => (
            <div key={tarif} style={{ ...s.inputGroup, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "center" }}>
              <label style={{ ...s.label, margin: 0, fontSize: THEME.fontSize.lg, color: THEME.colors.text }}>{tarif}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input style={{ ...s.input, width: 120 }} type="number" step="0.01" value={form[`tarif_${tarif}`] || ""} onChange={e => setForm(f => ({ ...f, [`tarif_${tarif}`]: e.target.value }))} />
                <span style={{ color: THEME.colors.textMuted }}>€</span>
              </div>
            </div>
          ))}
          <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto", marginTop: 8 }}>Speichern</button>
        </div>
      )}

      {aktiveTab === "rechtstexte" && (
        <div style={s.karte}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>AGB &amp; Datenschutzerklärung</h3>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: 16 }}>
            Dieser Text wird für die PDFs im Online-Mitgliedsantrag (/mitglied-werden) verwendet und dort auch dem
            Interessenten zum Nachlesen angezeigt. Änderungen wirken sich erst auf neue Anträge aus.
          </p>
          <div style={s.inputGroup}>
            <label style={s.label}>AGB-Text</label>
            <textarea style={{ ...s.input, height: 280, fontFamily: "inherit" }} value={form.agb_text || ""} onChange={e => setForm(f => ({ ...f, agb_text: e.target.value }))} />
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>Datenschutz-Text</label>
            <textarea style={{ ...s.input, height: 280, fontFamily: "inherit" }} value={form.datenschutz_text || ""} onChange={e => setForm(f => ({ ...f, datenschutz_text: e.target.value }))} />
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>Widerrufsbelehrung <span style={{ color: THEME.colors.textMuted, fontWeight: 400 }}>(wird auf aktion.comefit.de/anmeldung und in den Kleinfenstern dort angezeigt)</span></label>
            <textarea style={{ ...s.input, height: 280, fontFamily: "inherit" }} value={form.widerruf_text || ""} onChange={e => setForm(f => ({ ...f, widerruf_text: e.target.value }))} />
          </div>
          <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
        </div>
      )}

      {aktiveTab === "buchhaltung" && (
        <div style={s.karte}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Buchhaltung</h3>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: 16 }}>
            Diese Angaben werden verwendet, wenn im Bereich "Stundenzettel" freigegebene Stundenzettel eines Monats
            gesammelt per E-Mail an die Buchhaltung verschickt werden.
          </p>
          <div style={s.inputGroup}><label style={s.label}>E-Mail-Adresse der Buchhaltung * <span style={{ color: THEME.colors.textMuted, fontWeight: 400 }}>(mehrere Adressen mit Komma trennen)</span></label><input style={s.input} type="email" multiple value={form.buchhaltung_email || ""} onChange={e => setForm(f => ({ ...f, buchhaltung_email: e.target.value }))} placeholder="buchhaltung@beispiel.de, steuerberater@beispiel.de" /></div>
          <div style={s.inputGroup}><label style={s.label}>Ansprechpartner (optional)</label><input style={s.input} value={form.buchhaltung_ansprechpartner || ""} onChange={e => setForm(f => ({ ...f, buchhaltung_ansprechpartner: e.target.value }))} /></div>
          <div style={s.inputGroup}>
            <label style={s.label}>Standard-Betreff</label>
            <input style={s.input} value={form.buchhaltung_betreff || ""} onChange={e => setForm(f => ({ ...f, buchhaltung_betreff: e.target.value }))} />
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 4 }}>Platzhalter: {"{monat}"}, {"{jahr}"}</p>
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>Standard-E-Mailtext</label>
            <textarea style={{ ...s.input, height: 160, fontFamily: "inherit" }} value={form.buchhaltung_text || ""} onChange={e => setForm(f => ({ ...f, buchhaltung_text: e.target.value }))} />
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 4 }}>Platzhalter: {"{monat}"}, {"{jahr}"}</p>
          </div>
          <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
        </div>
      )}

      {aktiveTab === "emailvorlagen" && <EmailVorlagenTab />}

      {aktiveTab === "dokumentation" && (
        <div style={s.karte}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>Dokumentation</h3>
            <a href="/dokumentation.html" target="_blank" rel="noreferrer" style={{ ...s.btnGray, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>↗ In neuem Tab öffnen</a>
          </div>
          <iframe title="ComeFit Dokumentation" src="/dokumentation.html" style={{ width: "100%", height: "75vh", border: `1px solid ${THEME.colors.border}`, borderRadius: 8, background: THEME.colors.background }} />
        </div>
      )}

      {aktiveTab === "anleitungen" && <AnleitungenSeite rechte={rechte} />}

      {aktiveTab === "design" && (
        <div style={s.karte}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Design</h3>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: 20 }}>
            Passe die Farben der Anwendung an. Änderungen wirken app-weit, sobald du auf "Speichern &amp; Anwenden" klickst
            (die Seite lädt danach einmal neu).
          </p>

          <label style={{ ...s.label, marginBottom: 10 }}>Vorlagen</label>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "-6px 0 12px" }}>
            Klick eine Vorlage an, um die Felder unten damit zu befüllen – gespeichert wird erst über "Speichern &amp; Anwenden".
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 28 }}>
            {DESIGN_PRESETS.map(preset => (
              <div
                key={preset.name}
                onClick={() => handlePresetAnwenden(preset.farben)}
                style={{
                  background: preset.farben.design_background, border: `1px solid ${preset.farben.design_border}`,
                  borderRadius: 10, padding: 14, cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                  {["design_primary", "design_accent", "design_surface", "design_success", "design_danger"].map(k => (
                    <div key={k} style={{ width: 20, height: 20, borderRadius: "50%", background: preset.farben[k], border: `1px solid ${preset.farben.design_border}` }} />
                  ))}
                </div>
                <p style={{ color: preset.farben.design_text, fontSize: THEME.fontSize.base, fontWeight: 700, margin: "0 0 2px" }}>{preset.name}</p>
                <p style={{ color: preset.farben.design_textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>{preset.beschreibung}</p>
              </div>
            ))}
          </div>

          <label style={{ ...s.label, marginBottom: 10 }}>Einzelne Farben</label>
          {DESIGN_FELDER.map(([key, label, hint]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <input
                type="color"
                value={form[key] || STANDARD_FARBEN[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                style={{ width: 44, height: 44, padding: 0, border: `1px solid ${THEME.colors.border}`, borderRadius: 8, background: "none", cursor: "pointer", flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <label style={{ ...s.label, marginBottom: 2 }}>{label}</label>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>{hint}</p>
              </div>
              <input
                style={{ ...s.input, width: 120, flexShrink: 0 }}
                value={form[key] || ""}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              />
            </div>
          ))}
          <label style={{ ...s.label, marginTop: 8, marginBottom: 10 }}>Schriftgröße</label>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "-6px 0 12px" }}>
            Gilt app-weit für Navigation, Überschriften, Karten, Formulare, Tabellen, Listen, Dialoge und Buttons.
          </p>
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
            {Object.keys(FONT_SCALE_OPTIONS).map(stufe => {
              const LABEL = { klein: "Klein", standard: "Standard", gross: "Groß" };
              const aktiv = (form.design_schriftgroesse || "standard") === stufe;
              return (
                <button
                  key={stufe}
                  onClick={() => setForm(f => ({ ...f, design_schriftgroesse: stufe }))}
                  style={{
                    ...s.btnGray, width: "auto", padding: "10px 20px", borderRadius: 8,
                    background: aktiv ? THEME.colors.primary : THEME.colors.surfaceMuted, color: aktiv ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted),
                  }}
                >
                  {LABEL[stufe]}{stufe === "standard" ? " (Standard)" : ""}
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button onClick={handleDesignSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern &amp; Anwenden</button>
            <button onClick={handleDesignZuruecksetzen} style={{ ...s.btnGray, width: "auto" }}>Zurücksetzen auf Standard</button>
          </div>
        </div>
      )}

      {aktiveTab === "zutritt" && (
        <div>
          <div style={{ ...s.karte, marginBottom: 16 }}>
            <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>🔐 TTLock-Verbindung</h3>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, marginBottom: 16 }}>
              Grundlage für die künftige Zutrittskontrolle. Solange die TTLock-Cloud-API noch nicht freigegeben ist, werden hier ausschließlich Vorschauen berechnet – es wird nichts wirklich auf das Schloss geschrieben.
            </p>
            {ttlockStatus && (
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Client-ID hinterlegt: {ttlockStatus.client_id_vorhanden ? "✅" : "❌"}</p>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Client-Secret hinterlegt: {ttlockStatus.client_secret_vorhanden ? "✅" : "❌"}</p>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Zugangsdaten hinterlegt: {ttlockStatus.zugangsdaten_vorhanden ? "✅" : "❌"}</p>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Vollständig konfiguriert: {ttlockStatus.vollstaendig_konfiguriert ? "✅" : "❌"}</p>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Synchronisationsmodus: <strong>{ttlockStatus.sync_modus}</strong></p>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>Verbindungstyp: {ttlockStatus.verbindungstyp}</p>
              </div>
            )}
            <button onClick={handleTtlockVerbindungTesten} disabled={ttlockVerbindungLaedt} style={{ ...s.btn, width: "auto" }}>
              {ttlockVerbindungLaedt ? "Teste…" : "Verbindung testen"}
            </button>
            {ttlockVerbindungErgebnis && (
              <p style={{ marginTop: 12, fontSize: THEME.fontSize.base, color: ttlockVerbindungErgebnis.erfolg ? THEME.colors.success : THEME.colors.danger }}>
                {ttlockVerbindungErgebnis.erfolg
                  ? `✅ Verbindung erfolgreich (${ttlockVerbindungErgebnis.anzahl_schloesser ?? "?"} Schloss/Schlösser gefunden)`
                  : `❌ ${ttlockVerbindungErgebnis.fehler}`}
              </p>
            )}
          </div>

          <div style={s.karte}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>📋 Sync-Vorschau</h3>
              <button onClick={handleTtlockVorschauLaden} disabled={ttlockVorschauLaedt} style={{ ...s.btnGray, width: "auto" }}>
                {ttlockVorschauLaedt ? "Berechne…" : "Vorschau berechnen"}
              </button>
            </div>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, marginBottom: 16 }}>
              Zeigt für jede Person mit hinterlegter RFID-Kartennummer, welche TTLock-Berechtigung geplant ist – Mitarbeiter erhalten dauerhaften Zutritt, alle anderen den Standard-Wochenplan (Mo/Di/Do/Fr 6–22 Uhr, Mi 12–22 Uhr, Sa/So 9–20 Uhr). Es wird dabei nichts wirklich synchronisiert.
            </p>
            {ttlockVorschau && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                      <th style={{ padding: "8px 12px" }}>Name</th>
                      <th style={{ padding: "8px 12px" }}>Typ</th>
                      <th style={{ padding: "8px 12px" }}>RFID</th>
                      <th style={{ padding: "8px 12px" }}>Geplanter Zutritt</th>
                      <th style={{ padding: "8px 12px" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ttlockVorschau.personen.map(p => (
                      <tr key={`${p.person_typ}-${p.person_id}`} style={{ borderTop: `1px solid ${THEME.colors.border}` }}>
                        <td style={{ padding: "8px 12px", color: THEME.colors.text }}>{p.name}{p.ist_testperson && " 🧪"}</td>
                        <td style={{ padding: "8px 12px", color: THEME.colors.textSecondary }}>{p.person_typ}</td>
                        <td style={{ padding: "8px 12px", color: THEME.colors.textSecondary }}>{p.rfid_kartennummer}</td>
                        <td style={{ padding: "8px 12px", color: THEME.colors.textSecondary }}>Dauerhaft</td>
                        <td style={{ padding: "8px 12px", color: THEME.colors.textSecondary }}>{p.sync_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 12 }}>{ttlockVorschau.anzahl} Personen · Modus: {ttlockVorschau.sync_modus}</p>
              </div>
            )}
          </div>

          <div style={{ ...s.karte, marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
              <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>🚪 Protokoll</h3>
              <button onClick={() => handleTtlockProtokollLaden()} disabled={ttlockProtokollLaedt} style={{ ...s.btnGray, width: "auto" }}>
                {ttlockProtokollLaedt ? "Lädt…" : "Protokoll laden"}
              </button>
            </div>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, marginBottom: 16 }}>
              Zeigt die zuletzt von der TTLock-Cloud abgerufenen Zutrittsereignisse (neueste zuerst) - oder gezielt einen einzelnen Tag.
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
              <input
                style={{ ...s.input, width: "auto" }}
                type="date"
                value={ttlockProtokollDatum}
                onChange={e => handleTtlockProtokollDatumWaehlen(e.target.value)}
              />
              <button onClick={() => handleTtlockProtokollDatumWaehlen(new Date().toISOString().split("T")[0])} style={{ ...s.btnGray, width: "auto" }}>Heute</button>
              <button onClick={() => handleTtlockProtokollDatumWaehlen(new Date(Date.now() - 86400000).toISOString().split("T")[0])} style={{ ...s.btnGray, width: "auto" }}>Gestern</button>
              {ttlockProtokollDatum && (
                <button onClick={() => handleTtlockProtokollDatumWaehlen("")} style={{ ...s.btnGray, width: "auto", color: THEME.colors.textMuted }}>Zurücksetzen (letzte 100)</button>
              )}
            </div>
            {ttlockProtokoll && (
              <div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
                  <input
                    style={{ ...s.input, maxWidth: 320 }}
                    placeholder="Nach Name oder Chipnummer suchen…"
                    value={ttlockProtokollSuche}
                    onChange={e => setTtlockProtokollSuche(e.target.value)}
                  />
                  <label style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                    <input type="checkbox" checked={ttlockNurFehler} onChange={e => setTtlockNurFehler(e.target.checked)} /> Nur fehlgeschlagene Versuche ❌
                  </label>
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
                    {ttlockProtokoll.filter(e => !e.erfolgreich).length} Fehlversuche in dieser Liste
                  </span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                        <th style={{ padding: "8px 12px" }}>Name</th>
                        <th style={{ padding: "8px 12px" }}>Chipnummer</th>
                        <th style={{ padding: "8px 12px" }}>Typ</th>
                        <th style={{ padding: "8px 12px" }}>Ereignis</th>
                        <th style={{ padding: "8px 12px" }}>Erfolgreich</th>
                        <th style={{ padding: "8px 12px" }}>Zeitpunkt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ttlockProtokoll
                        .filter(e => !ttlockNurFehler || !e.erfolgreich)
                        .filter(e => !ttlockProtokollSuche || (e.person_name || "").toLowerCase().includes(ttlockProtokollSuche.toLowerCase()) || String(e.rfid_kartennummer || "").includes(ttlockProtokollSuche.trim()))
                        .map(e => (
                          <tr key={e.id} style={{ borderTop: `1px solid ${THEME.colors.border}`, background: e.erfolgreich ? "transparent" : "rgba(239,68,68,0.10)" }}>
                            <td style={{ padding: "8px 12px", color: THEME.colors.text }}>{e.person_name || "Unbekannt"}</td>
                            <td style={{ padding: "8px 12px", color: THEME.colors.textSecondary, fontFamily: "ui-monospace, Consolas, monospace" }}>{e.rfid_kartennummer || "–"}</td>
                            <td style={{ padding: "8px 12px", color: THEME.colors.textSecondary }}>{e.person_typ || "–"}</td>
                            <td style={{ padding: "8px 12px", color: THEME.colors.textSecondary }}>{e.event_type}</td>
                            <td style={{ padding: "8px 12px", color: e.erfolgreich ? THEME.colors.success : THEME.colors.danger }}>{e.erfolgreich ? "✅" : "❌"}</td>
                            <td style={{ padding: "8px 12px", color: THEME.colors.textSecondary }}>{e.zeitpunkt ? new Date(e.zeitpunkt).toLocaleString("de-DE") : "–"}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {ttlockProtokoll.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Noch keine Ereignisse abgerufen.</p>}
                </div>
              </div>
            )}
          </div>

          <div style={{ ...s.karte, marginTop: 16 }}>
            <h3 style={{ color: THEME.colors.text, margin: "0 0 8px", fontSize: THEME.fontSize.xl }}>🏷️ Neue Karten-/Band-Charge einrichten</h3>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, marginBottom: 16 }}>
              Kommt eine neue Lieferung mit abweichendem Code an: ein paar Karten/Bänder zuerst direkt in der TTLock-App registrieren ("Von TTLock übernehmen" liefert dann die echte Nummer), die Zahlenpaare hier eintragen und berechnen lassen. Danach werden alle künftigen Karten/Bänder in diesem Nummernbereich automatisch richtig erfasst - ganz ohne Code-Änderung.
            </p>
            <div style={s.inputGroup}>
              <label style={s.label}>Medientyp</label>
              <select style={{ ...s.input, maxWidth: 200 }} value={chargenMedientyp} onChange={e => setChargenMedientyp(e.target.value)}>
                <option value="band">Band</option>
                <option value="karte">Karte</option>
              </select>
            </div>
            {chargenPaare.map((p, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                <input style={{ ...s.input, maxWidth: 220 }} placeholder="ComeFit-Nummer (PC-Leser)" value={p.comefit_nummer}
                  onChange={e => handleChargenPaarAendern(i, "comefit_nummer", e.target.value)} />
                <input style={{ ...s.input, maxWidth: 220 }} placeholder="Echte Nummer (Von TTLock übernehmen)" value={p.echte_nummer}
                  onChange={e => handleChargenPaarAendern(i, "echte_nummer", e.target.value)} />
                {chargenPaare.length > 1 && (
                  <button onClick={() => handleChargenPaarEntfernen(i)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>✕</button>
                )}
              </div>
            ))}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <button onClick={handleChargenPaarHinzufuegen} style={{ ...s.btnGray, width: "auto" }}>+ Weiteres Paar</button>
              <button onClick={handleChargenBerechnen} disabled={chargenBerechnenLaedt} style={{ ...s.btn, width: "auto" }}>
                {chargenBerechnenLaedt ? "Berechne…" : "Berechnen"}
              </button>
            </div>

            {chargenErgebnis && (
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ overflowX: "auto", marginBottom: 12 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                        <th style={{ padding: "6px 10px" }}>ComeFit-Nummer</th>
                        <th style={{ padding: "6px 10px" }}>Berechnet</th>
                        <th style={{ padding: "6px 10px" }}>Präfix</th>
                        <th style={{ padding: "6px 10px" }}>Stimmt?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chargenErgebnis.ergebnisse.map((e, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${THEME.colors.border}` }}>
                          <td style={{ padding: "6px 10px", color: THEME.colors.text }}>{e.comefit_nummer}</td>
                          <td style={{ padding: "6px 10px", color: THEME.colors.textSecondary }}>{e.berechnete_comefit_nummer}</td>
                          <td style={{ padding: "6px 10px", color: THEME.colors.textSecondary }}>{e.praefix_hex}</td>
                          <td style={{ padding: "6px 10px", color: e.stimmt_ueberein ? THEME.colors.success : THEME.colors.danger }}>{e.stimmt_ueberein ? "✅" : "❌ Zahlendreher?"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {chargenErgebnis.einheitlich ? (
                  <>
                    <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.base, marginBottom: 12 }}>
                      ✅ Einheitliches Präfix gefunden: <strong>{chargenErgebnis.einheitliches_praefix_hex}</strong>. Jetzt als Regel speichern:
                    </p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                      <input style={{ ...s.input, maxWidth: 220 }} placeholder="Bezeichnung (z.B. 'Charge Sept. 2026')" value={chargenBezeichnung} onChange={e => setChargenBezeichnung(e.target.value)} />
                      <input style={{ ...s.input, maxWidth: 160 }} placeholder="Von (Nummer)" value={chargenVon} onChange={e => setChargenVon(e.target.value)} />
                      <input style={{ ...s.input, maxWidth: 160 }} placeholder="Bis (leer = offen)" value={chargenBis} onChange={e => setChargenBis(e.target.value)} />
                    </div>
                    <button onClick={handleChargenSpeichern} disabled={chargenSpeichernLaedt} style={{ ...s.btn, width: "auto" }}>
                      {chargenSpeichernLaedt ? "Speichert…" : "✓ Regel speichern"}
                    </button>
                  </>
                ) : (
                  <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base, margin: 0 }}>
                    ⚠ Die Paare ergeben unterschiedliche Präfixe - vermutlich sind Karten aus verschiedenen Chargen gemischt oder ein Zahlendreher ist passiert. Bitte prüfen und erneut berechnen.
                  </p>
                )}
              </div>
            )}

            {chargenListe.length > 0 && (
              <div>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600, margin: "0 0 8px" }}>Bereits eingerichtete Chargen</p>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                        <th style={{ padding: "6px 10px" }}>Medientyp</th>
                        <th style={{ padding: "6px 10px" }}>Präfix</th>
                        <th style={{ padding: "6px 10px" }}>Bereich</th>
                        <th style={{ padding: "6px 10px" }}>Bezeichnung</th>
                        <th style={{ padding: "6px 10px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {chargenListe.map(r => (
                        <tr key={r.id} style={{ borderTop: `1px solid ${THEME.colors.border}` }}>
                          <td style={{ padding: "6px 10px", color: THEME.colors.text }}>{r.medientyp}</td>
                          <td style={{ padding: "6px 10px", color: THEME.colors.textSecondary }}>{r.praefix_hex}</td>
                          <td style={{ padding: "6px 10px", color: THEME.colors.textSecondary }}>{r.nummer_von ?? "–"} bis {r.nummer_bis ?? "offen"}</td>
                          <td style={{ padding: "6px 10px", color: THEME.colors.textSecondary }}>{r.bezeichnung || "–"}</td>
                          <td style={{ padding: "6px 10px" }}><button onClick={() => handleChargenLoeschen(r.id)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger, fontSize: THEME.fontSize.xs }}>Löschen</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {aktiveTab === "rfidpruefliste" && rechte?.rolle === "admin" && <RfidPruefliste onPersonOeffnen={onPersonOeffnen} />}

      {aktiveTab === "benachrichtigungen" && <PushEinstellungenKarte />}

      {aktiveTab === "backup" && (
        <div style={s.karte}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>💾 Backup erstellen</h3>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, marginBottom: 16 }}>
            Erstellt ein vollständiges Backup der Datenbank, aller Dokumente und der Konfiguration als ZIP-Datei.
          </p>
          <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: "0 0 8px", fontWeight: 600 }}>Inhalt des Backups:</p>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "0 0 4px" }}>🗄 datenbank.sql – Alle Mitglieder, Zahlungen, Rechnungen, Kurse, Einstellungen</p>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "0 0 4px" }}>📁 dokumente/ – Alle hochgeladenen Mitgliederdateien</p>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "0 0 4px" }}>⚙ config/ – Google Kalender Token</p>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: 0 }}>📄 ANLEITUNG.txt – Wiederherstellungsanleitung</p>
          </div>
          <div style={{ background: "#f59e0b22", border: "1px solid #f59e0b", borderRadius: 8, padding: 14, marginBottom: 20 }}>
            <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, margin: 0 }}>
              ⚠ Empfehlung: Backup wöchentlich erstellen und an einem sicheren Ort speichern (z.B. externe Festplatte, Cloud).
            </p>
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>Zugriffsschlüssel</label>
            <input type="password" style={{ ...s.input, maxWidth: 420 }} value={backupSchluessel}
              onChange={e => setBackupSchluessel(e.target.value)} placeholder="Schlüssel eingeben (nicht im Code hinterlegt)" autoComplete="off" />
          </div>
          <button onClick={handleBackupErstellen} disabled={!backupSchluessel || backupLaedt}
            style={{ ...s.btn, width: "auto", display: "inline-block", textAlign: "center", padding: "14px 28px", fontSize: THEME.fontSize.lg,
              opacity: (backupSchluessel && !backupLaedt) ? 1 : 0.5, cursor: (backupSchluessel && !backupLaedt) ? "pointer" : "not-allowed" }}>
            {backupLaedt ? "Erstelle Backup…" : "💾 Backup jetzt erstellen & herunterladen"}
          </button>
        </div>
      )}

      {aktiveTab === "backup" && (
        <div style={{ ...s.karte, marginTop: 16 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>📦 Alles sichern (Code + Struktur)</h3>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, marginBottom: 16 }}>
            Erstellt ein ZIP mit dem kompletten aktuellen Code (Backend + Frontend, so wie er gerade ist) und einer frischen Datenbankstruktur ohne Mitgliederdaten.
          </p>
          <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: "0 0 8px", fontWeight: 600 }}>Inhalt:</p>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "0 0 4px" }}>💻 Kompletter Code (Backend + Frontend), inklusive der echten .env-Dateien mit den aktuellen Zugangsdaten</p>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: 0 }}>🗄 schema_struktur.sql – Datenbankstruktur ohne Inhalte (keine Mitglieder, Zahlungen etc.)</p>
          </div>
          <div style={{ background: "#ef444422", border: "1px solid #ef4444", borderRadius: 8, padding: 14, marginBottom: 20 }}>
            <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: 0 }}>
              ⚠ Diese Datei enthält echte Zugangsdaten (Datenbank-Passwort, API-Schlüssel etc.) – sicher aufbewahren und nicht öffentlich teilen.
            </p>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, cursor: "pointer", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>
            <input type="checkbox" checked={vollbackupBestaetigt} onChange={e => setVollbackupBestaetigt(e.target.checked)} />
            Ich habe verstanden, dass diese Datei echte Zugangsdaten enthält
          </label>
          <button onClick={handleVollbackup} disabled={!backupSchluessel || !vollbackupBestaetigt || vollbackupLaedt}
            style={{ ...s.btn, width: "auto", display: "inline-block", textAlign: "center", padding: "14px 28px", fontSize: THEME.fontSize.lg,
              opacity: (backupSchluessel && vollbackupBestaetigt && !vollbackupLaedt) ? 1 : 0.5,
              cursor: (backupSchluessel && vollbackupBestaetigt && !vollbackupLaedt) ? "pointer" : "not-allowed" }}>
            {vollbackupLaedt ? "Erstelle Vollbackup…" : "📦 Alles sichern (Code + Struktur)"}
          </button>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 8 }}>Nutzt den gleichen Zugriffsschlüssel wie oben.</p>
        </div>
      )}

      {aktiveTab === "benutzer" && (
        <div>
          <div style={{ ...s.karte, marginBottom: 16 }}>
            {passwortResetId && (
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 16, borderLeft: "3px solid #f59e0b" }}>
                <h4 style={{ color: THEME.colors.text, margin: "0 0 12px" }}>🔑 Passwort zurücksetzen</h4>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <input style={{ ...s.input, flex: 1 }} type="password" placeholder="Neues Passwort (min. 4 Zeichen)" value={neuesPasswort} onChange={e => setNeuesPasswort(e.target.value)} autoFocus />
                  <button onClick={() => { setPasswortResetId(null); setNeuesPasswort(""); }} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                  <button onClick={handlePasswortReset} style={{ ...s.btn, width: "auto", background: THEME.colors.warning }}>Zurücksetzen</button>
                </div>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>Mitarbeiter</h3>
              <button onClick={() => { setBearbeitenBenutzer(null); setNeuerBenutzer(LEER_BENUTZER); setRfidEingabe(""); setRfidFehler(""); setBenutzerFormOffen(!benutzerFormOffen); }} style={{ ...s.btn, width: "auto" }}>+ Neuer Mitarbeiter</button>
            </div>

            {benutzerFormOffen && (
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <h4 style={{ color: THEME.colors.text, margin: "0 0 12px" }}>{bearbeitenBenutzer ? `${bearbeitenBenutzer.name} bearbeiten` : "Neuer Mitarbeiter"}</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
                  <div style={s.inputGroup}><label style={s.label}>Name *</label><input style={s.input} value={neuerBenutzer.name} onChange={e => setNeuerBenutzer(b => ({ ...b, name: e.target.value }))} /></div>
                  <div style={s.inputGroup}><label style={s.label}>E-Mail *</label><input style={s.input} type="email" value={neuerBenutzer.email} onChange={e => setNeuerBenutzer(b => ({ ...b, email: e.target.value }))} /></div>
                  {!bearbeitenBenutzer && <div style={s.inputGroup}><label style={s.label}>Passwort *</label><input style={s.input} type="password" value={neuerBenutzer.passwort} onChange={e => setNeuerBenutzer(b => ({ ...b, passwort: e.target.value }))} /></div>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16, marginTop: 12 }}>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Beschäftigungsart</label>
                    <select style={s.input} value={neuerBenutzer.beschaeftigungsart || "sonstige"}
                      onChange={e => setNeuerBenutzer(b => ({ ...b, beschaeftigungsart: e.target.value, schichtplan_lohnberechnung_aktiv: e.target.value === "eigentuemer" ? false : b.schichtplan_lohnberechnung_aktiv }))}>
                      <option value="eigentuemer">Eigentümer</option>
                      <option value="minijob">Minijob</option>
                      <option value="teilzeit">Teilzeit</option>
                      <option value="vollzeit">Vollzeit</option>
                      <option value="sonstige">Sonstige</option>
                    </select>
                  </div>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Stundenlohn (€)</label>
                    <input style={s.input} type="number" step="0.01" placeholder="z. B. 15,50" value={neuerBenutzer.stundenlohn ?? ""}
                      onChange={e => setNeuerBenutzer(b => ({ ...b, stundenlohn: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
                  </div>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Monatliche Verdienstgrenze (€)</label>
                    <input style={s.input} type="number" step="0.01" placeholder="z. B. 600,00" value={neuerBenutzer.monatliche_verdienstgrenze ?? ""}
                      onChange={e => setNeuerBenutzer(b => ({ ...b, monatliche_verdienstgrenze: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
                  </div>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer", margin: "8px 0 4px" }}>
                  <input type="checkbox" checked={neuerBenutzer.schichtplan_lohnberechnung_aktiv ?? true}
                    onChange={e => setNeuerBenutzer(b => ({ ...b, schichtplan_lohnberechnung_aktiv: e.target.checked }))} />
                  Lohnberechnung im Schichtplan aktiv
                </label>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "0 0 12px" }}>
                  Die Verdienstgrenze ist ein individuell hinterlegter Planungswert. Bitte bei gesetzlichen Änderungen prüfen und gegebenenfalls anpassen.
                </p>
                {bearbeitenBenutzer && (
                  <div style={{ background: THEME.colors.surfaceMuted, borderRadius: 8, padding: 12, marginBottom: 16 }}>
                    <label style={s.label}>RFID-Kartennummer (Zutritt)</label>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <input style={{ ...s.input, flex: "1 1 180px" }} value={rfidEingabe} onChange={e => setRfidEingabe(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleRfidZuweisen(); }} placeholder="Kartennummer eingeben…" />
                      <select style={{ ...s.input, width: "auto", flexShrink: 0 }} value={rfidMedientyp} onChange={e => setRfidMedientyp(e.target.value)}>
                        <option value="">Automatisch (Band)</option>
                        <option value="karte">Karte</option>
                        <option value="band">Band</option>
                      </select>
                      <button onClick={handleRfidZuweisen} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                      {bearbeitenBenutzer.rfid_kartennummer && <button onClick={handleRfidEntfernen} style={{ ...s.btnGray, width: "auto" }}>Entfernen</button>}
                    </div>
                    {rfidFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: "6px 0 0" }}>{rfidFehler}</p>}
                  </div>
                )}
                {bearbeitenBenutzer?.id && <TTLockZutrittZeile personTyp="mitarbeiter" personId={bearbeitenBenutzer.id} />}
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "8px 0" }}>Feinrechte innerhalb "Mitglieder":</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 8, marginBottom: 16 }}>
                  {[
                    ["darf_mitglieder_anlegen","Mitglieder anlegen"],
                    ["darf_mitglieder_bearbeiten","Mitglieder bearbeiten"],
                    ["darf_mitglieder_loeschen","Mitglieder löschen"],
                    ["darf_pausieren","Pausieren"],
                    ["darf_kuendigen","Kündigen"],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer" }}>
                      <input type="checkbox" checked={neuerBenutzer[key] || false} onChange={e => setNeuerBenutzer(b => ({ ...b, [key]: e.target.checked }))} />
                      {label}
                    </label>
                  ))}
                </div>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "8px 0" }}>Modul-Zugriff (Navigation):</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 8, marginBottom: 16 }}>
                  {[
                    ["mitglieder","Mitglieder"], ["sepa","SEPA"], ["statistiken","Statistiken"],
                    ["rechnungen","Rechnungen"], ["kurse","Kurse"], ["mitarbeiter","Mitarbeiter"],
                    ["emails","E-Mails"], ["kalender","Kalender"], ["studio","Studio"],
                    ["online_vertraege","Online-Verträge"], ["kuendigungen","Kündigungen"],
                    ["feedback","Feedback"], ["aktionen","Aktionen"], ["einstellungen","Einstellungen"],
                    ["wellpass","Wellpass"], ["hansefit","Hansefit"], ["sonstige_nutzer","Sonstige"],
                    ["finanzen_anzeigen","Finanzen"], ["schichtplan","Schichtplan"], ["urlaub","Urlaub"], ["tv","TV"],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer" }}>
                      <input type="checkbox" checked={neuerBenutzer.module?.[key] || false} onChange={e => setNeuerBenutzer(b => ({ ...b, module: { ...b.module, [key]: e.target.checked } }))} />
                      {label}
                    </label>
                  ))}
                </div>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "8px 0" }}>Feinrechte Vorfall-/Unfallberichte:</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 8, marginBottom: 16 }}>
                  {[
                    ["vorfaelle_ansehen","Vorfallberichte ansehen"], ["vorfaelle_erstellen","Vorfallberichte erstellen"],
                    ["vorfaelle_bearbeiten","Vorfallberichte bearbeiten"], ["vorfaelle_abschliessen","Vorfallberichte abschließen"],
                    ["vorfaelle_loeschen","Vorfallberichte löschen"], ["vorfaelle_pdf_export","Vorfallberichte als PDF exportieren"],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer" }}>
                      <input type="checkbox" checked={neuerBenutzer.module?.[key] || false} onChange={e => setNeuerBenutzer(b => ({ ...b, module: { ...b.module, [key]: e.target.checked } }))} />
                      {label}
                    </label>
                  ))}
                </div>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "8px 0" }}>Feinrechte Check-ins (Wellpass &amp; Hansefit):</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 8, marginBottom: 16 }}>
                  {[
                    ["checkins_extern","Externe Check-ins ansehen"],
                    ["checkins_zu_klaeren","Ungeklärte Check-ins bearbeiten"],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer" }}>
                      <input type="checkbox" checked={neuerBenutzer.module?.[key] || false} onChange={e => setNeuerBenutzer(b => ({ ...b, module: { ...b.module, [key]: e.target.checked } }))} />
                      {label}
                    </label>
                  ))}
                </div>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "8px 0" }}>Feinrechte TTLock-Zutritt:</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 8, marginBottom: 16 }}>
                  {[
                    ["ttlock","Dashboard-Kachel (Heute eingecheckt)"],
                    ["ttlock_mitgliederzutritt_ansehen","Zutritts-Status ansehen"],
                    ["ttlock_mitgliederzutritt_verwalten","Zutritt verwalten (sync/entziehen)"],
                    ["ttlock_sync_vorschau","Sync-Vorschau ansehen"],
                    ["ttlock_fernoeffnen","Tür fernöffnen"],
                    ["ttlock_protokoll_ansehen","Zutritts-Protokoll ansehen"],
                    ["ttlock_einstellungen","Verbindung testen/konfigurieren"],
                    ["ttlock_karten_verwalten","Kartenverwaltung (Massenumrechnung)"],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer" }}>
                      <input type="checkbox" checked={neuerBenutzer.module?.[key] || false} onChange={e => setNeuerBenutzer(b => ({ ...b, module: { ...b.module, [key]: e.target.checked } }))} />
                      {label}
                    </label>
                  ))}
                </div>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "8px 0" }}>Feinrechte Finanzmodul (nur wirksam, wenn "Finanzen" oben aktiviert ist):</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 8, marginBottom: 16 }}>
                  {[
                    ["finanzen_bankumsaetze","Bankumsätze"],
                    ["finanzen_import","CSV-Import"],
                    ["finanzen_buchungen_bearbeiten","Buchungen bearbeiten"],
                    ["finanzen_kategorien","Kategorien verwalten"],
                    ["finanzen_regeln","Kategorisierungsregeln"],
                    ["finanzen_wiederkehrend","Wiederkehrende Zahlungen"],
                    ["finanzen_ruecklastschriften","Rücklastschriften"],
                    ["finanzen_auswertungen","Auswertungen"],
                    ["finanzen_export","Export"],
                    ["finanzen_loeschen","Löschen"],
                    ["finanzen_einstellungen","Finanz-Einstellungen"],
                    ["finanzen_bwa","BWA"],
                    ["finanzen_kontenplan","Kontenplan (SKR03)"],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer" }}>
                      <input type="checkbox" checked={neuerBenutzer.module?.[key] || false} onChange={e => setNeuerBenutzer(b => ({ ...b, module: { ...b.module, [key]: e.target.checked } }))} />
                      {label}
                    </label>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => setBenutzerFormOffen(false)} style={s.btnGray}>Abbrechen</button>
                  <button onClick={handleBenutzerAnlegen} style={{ ...s.btn, width: "auto" }}>{bearbeitenBenutzer ? "Speichern" : "Anlegen"}</button>
                </div>
              </div>
            )}

            {benutzer.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Mitarbeiter angelegt.</p>}
            {benutzer.filter(b => b.aktiv).map(b => (
              <div key={b.id} style={{ padding: "12px 0", borderTop: "1px solid #2b3d52", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md, fontWeight: 600, margin: "0 0 4px" }}>{b.name}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>{b.email}</p>
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    {b.darf_mitglieder_anlegen && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.primary, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Anlegen</span>}
                    {b.darf_mitglieder_bearbeiten && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.primary, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Bearbeiten</span>}
                    {b.darf_mitglieder_loeschen && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.danger, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Löschen</span>}
                    {b.darf_pausieren && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.warning, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Pausieren</span>}
                    {b.darf_kuendigen && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.accentOrange, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Kündigen</span>}
                    {b.darf_sepa && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.success, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>SEPA</span>}
                    {b.darf_statistiken && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.accentPurple, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Statistiken</span>}
                    {b.darf_rechnungen && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.success, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Rechnungen</span>}
                    {b.darf_kurse && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.primary, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Kurse</span>}
                    {b.darf_einstellungen && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.danger, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Einstellungen</span>}
                    {b.module?.finanzen_anzeigen && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.success, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>Finanzen</span>}
                    {b.rfid_kartennummer && <span style={{ background: THEME.colors.surfaceMuted, color: THEME.colors.text, padding: "2px 8px", borderRadius: 4, fontSize: THEME.fontSize.xs }}>💳 {b.rfid_kartennummer}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => handleBenutzerBearbeiten(b)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>✏ Bearbeiten</button>
                  <button onClick={() => { setPasswortResetId(b.id); setNeuesPasswort(""); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>🔑 Passwort</button>
                  <button onClick={() => handleBenutzerLoeschen(b.id)} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg, fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>Deaktivieren</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ TODO + DASHBOARD ============


// ============ SHELLY WIDGET (Dashboard) ============
function ShellyWidget({ onOffenChange, initialOffen }) {
  const [geraete, setGeraete] = useState([]);
  const [warnungen, setWarnungen] = useState([]);
  const [offen, setOffen] = useState(!!initialOffen);
  const toggleOffen = () => { const neu = !offen; setOffen(neu); onOffenChange?.(neu); };
  const [laedt, setLaedt] = useState(false);
  // Siehe ShellySeite weiter unten fuer die ausfuehrliche Begruendung: Shelly Cloud meldet
  // direkt nach einem Schaltvorgang manchmal kurzzeitig faelschlich "offline" - erst nach zwei
  // aufeinanderfolgenden "offline"-Antworten in Folge wirklich als offline anzeigen.
  const offlineStreakRef = useRef({});
  const geglaettetOnline = (frischeGeraete) => frischeGeraete.map(g => {
    if (g.typ !== "schalter" && g.typ !== "sensor") return g;
    if (g.online) { offlineStreakRef.current[g.id] = 0; return g; }
    const streak = (offlineStreakRef.current[g.id] || 0) + 1;
    offlineStreakRef.current[g.id] = streak;
    return streak < 2 ? { ...g, online: true } : g;
  });

  const laden = async (leise = false) => {
    if (!leise) setLaedt(true);
    try {
      const [g, w] = await Promise.all([
        fetch(`${API}/shelly/geraete`).then(r => r.json()),
        fetch(`${API}/shelly/warnungen`).then(r => r.json())
      ]);
      setGeraete(geglaettetOnline(g));
      setWarnungen(w);
    } catch {}
    if (!leise) setLaedt(false);
  };

  useEffect(() => {
    laden();
    const intervall = setInterval(() => laden(true), 5000);
    return () => clearInterval(intervall);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSchalten = async (id, einAn) => {
    // Sofort optimistisch im UI umschalten statt auf die Cloud-Bestaetigung zu warten, danach
    // mehrmals in kurzem Abstand nachpollen (siehe ShellySeite fuer die ausfuehrliche Begruendung).
    setGeraete(prev => prev.map(g => g.id === id ? { ...g, ein: einAn } : g));
    await fetch(`${API}/shelly/schalten/${id}?ein=${einAn}`, { method: "POST" }).catch(() => {});
    [1000, 2500, 4500, 7000].forEach(ms => setTimeout(() => laden(true), ms));
  };

  const schalter = geraete.filter(g => g.typ === "schalter");
  const sensor = geraete.find(g => g.typ === "sensor");

  return (
    <div style={{ ...s.karte, marginBottom: 16, borderLeftColor: THEME.colors.accentPurple }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div onClick={toggleOffen} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }}>
          <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl, display: "flex", alignItems: "center", gap: 8 }}><Zap size={18} color={THEME.colors.accentPurple} /> Studio-Steuerung</h3>
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{offen ? "▲" : "▼"}</span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); laden(); }} style={{ ...s.btnGray, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.sm }}>🔄</button>
      </div>

      {/* Warnungen immer sichtbar */}
      {warnungen.map((w, i) => (
        <div key={i} style={{ background: "#7f1d1d33", border: "1px solid #ef4444", borderRadius: 6, padding: "8px 12px", marginTop: 8, color: THEME.colors.danger, fontSize: THEME.fontSize.base }}>
          {w.text}
        </div>
      ))}

      {offen && (
        <div style={{ marginTop: 16 }}>
          {laedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base }}>Lade...</p>}

          {/* Sensor Daten */}
          {sensor && sensor.online && (
            <div style={{ display: "flex", gap: 16, marginBottom: 16, padding: "12px 0", borderBottom: "1px solid #2b3d52" }}>
              {sensor.temperatur !== null && (
                <div style={{ textAlign: "center" }}>
                  <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{sensor.temperatur?.toFixed(1)}°C</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>Temperatur</p>
                </div>
              )}
              {sensor.luftfeuchtigkeit !== null && (
                <div style={{ textAlign: "center" }}>
                  <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{sensor.luftfeuchtigkeit?.toFixed(0)}%</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>Luftfeuchtigkeit</p>
                </div>
              )}
            </div>
          )}

          {/* Schalter */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            {schalter.map(g => (
              <div key={g.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: "12px", textAlign: "center", border: `1px solid ${g.online ? (g.ein ? THEME.colors.success : THEME.colors.surfaceMuted) : THEME.colors.danger}` }}>
                <p style={{ color: g.online ? THEME.colors.text : THEME.colors.textMuted, fontSize: THEME.fontSize.base, fontWeight: 600, margin: "0 0 8px" }}>{g.name}</p>
                {!g.online && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.xs, margin: "0 0 8px" }}>Offline</p>}
                {g.online && (
                  <button onClick={() => handleSchalten(g.id, !g.ein)} style={{
                    background: g.ein ? THEME.colors.success : THEME.colors.surfaceMuted,
                    border: "none", borderRadius: 20, padding: "6px 16px",
                    color: THEME.colors.text, fontSize: THEME.fontSize.sm, cursor: "pointer", fontWeight: 600
                  }}>
                    {g.ein ? "● EIN" : "○ AUS"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ DASHBOARD-WIDGET: ONLINE PORTAL (offene Vertraege/Kuendigungen/Feedback) ============
function OnlinePortalWidget({ onOffenChange, initialOffen, navigiere, rechte }) {
  const [offeneVertraege, setOffeneVertraege] = useState([]);
  const [offeneKuendigungen, setOffeneKuendigungen] = useState([]);
  const [offenesFeedback, setOffenesFeedback] = useState([]);
  const [offen, setOffen] = useState(!!initialOffen);
  const toggleOffen = () => { const neu = !offen; setOffen(neu); onOffenChange?.(neu); };

  const laden = () => {
    fetch(`${API}/vertraege/`).then(r => r.json()).then(d => setOffeneVertraege(d.filter(a => a.status === "neu"))).catch(() => {});
    fetch(`${API}/kuendigungen/`).then(r => r.json()).then(d => setOffeneKuendigungen(d.filter(k => k.bearbeitungsstatus === "neu"))).catch(() => {});
    fetch(`${API}/feedback/`).then(r => r.json()).then(d => setOffenesFeedback(d.filter(e => e.status === "neu"))).catch(() => {});
  };
  useEffect(() => {
    laden();
    const intervall = setInterval(laden, 30000);
    return () => clearInterval(intervall);
  }, []);

  const istAdmin = rechte?.rolle === "admin";
  const abschnitte = [
    { key: "vertraege", modul: "online_vertraege", label: "Online-Verträge", eintraege: offeneVertraege,
      zeile: a => `${a.vorname} ${a.nachname} · ${a.tarif}` },
    { key: "kuendigungen", modul: "kuendigungen", label: "Kündigungen", eintraege: offeneKuendigungen,
      zeile: k => `${k.vorname} ${k.nachname} · zum ${datumDE(k.gewuenschtes_kuendigungsdatum)}` },
    { key: "feedback", modul: "feedback", label: "Feedback", eintraege: offenesFeedback,
      zeile: e => `${e.kategorie}: ${e.nachricht.slice(0, 60)}${e.nachricht.length > 60 ? "…" : ""}` },
  ].filter(a => istAdmin || rechte?.module?.[a.modul]);

  const gesamtOffen = abschnitte.reduce((sum, a) => sum + a.eintraege.length, 0);

  return (
    <div style={{ ...s.karte, marginBottom: 16, borderLeftColor: THEME.colors.accentViolet }}>
      <div onClick={toggleOffen} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
        <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl, display: "flex", alignItems: "center", gap: 8 }}>
          <Layers size={18} color={THEME.colors.accentViolet} /> Online Portal
          {gesamtOffen > 0 && <span style={{ background: THEME.colors.success, color: THEME.colors.text, fontSize: THEME.fontSize.xs, padding: "2px 8px", borderRadius: 10 }}>{gesamtOffen} offen</span>}
        </h3>
        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{offen ? "▲" : "▼"}</span>
      </div>

      {offen && (
        <div style={{ marginTop: 12 }}>
          {abschnitte.every(a => a.eintraege.length === 0) ? (
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base }}>Keine offenen Vorgänge. 🎉</p>
          ) : (
            abschnitte.map(a => a.eintraege.length > 0 && (
              <div key={a.key} style={{ marginBottom: 14 }}>
                <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, fontWeight: 600, margin: "0 0 6px" }}>{a.label} ({a.eintraege.length})</p>
                {a.eintraege.slice(0, 3).map((e, i) => (
                  <p key={e.id ?? i} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "0 0 4px" }}>{a.zeile(e)}</p>
                ))}
              </div>
            ))
          )}
          {gesamtOffen > 0 && (
            <button onClick={() => navigiere?.("Online Portal")} style={{ ...s.btn, width: "auto", marginTop: 4 }}>Zum Online Portal</button>
          )}
        </div>
      )}
    </div>
  );
}

// ============ DASHBOARD-WIDGET: CHECK IN (heutige Check-ins Wellpass/Hansefit) ============
function CheckInWidget({ navigiere }) {
  const [wpHeute, setWpHeute] = useState(null);
  const [hfHeute, setHfHeute] = useState(null);
  const [syncLaeuft, setSyncLaeuft] = useState(false);

  const laden = () => {
    fetch(`${API}/wellpass/statistiken`).then(r => r.json()).then(d => setWpHeute(d.checkins_heute ?? 0)).catch(() => {});
    fetch(`${API}/hansefit/statistiken`).then(r => r.json()).then(d => setHfHeute(d.checkins_heute ?? 0)).catch(() => {});
  };
  useEffect(() => {
    laden();
    const intervall = setInterval(laden, 60000);
    return () => clearInterval(intervall);
  }, []);

  const handleSync = async (e) => {
    e.stopPropagation();
    if (syncLaeuft) return;
    setSyncLaeuft(true);
    try {
      await Promise.all([
        fetch(`${API}/wellpass/sync`, { method: "POST" }),
        fetch(`${API}/hansefit/sync`, { method: "POST" }),
      ]);
    } catch {}
    laden();
    setSyncLaeuft(false);
  };

  return (
    <div onClick={() => navigiere?.("Check In")} style={{ cursor: navigiere ? "pointer" : "default" }}>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}><HeartPulse size={18} color={THEME.colors.accentOrange} /> Check-ins heute</span>
        <button onClick={handleSync} disabled={syncLaeuft} title="Jetzt synchronisieren" style={{ background: "none", border: "none", cursor: syncLaeuft ? "default" : "pointer", padding: 4, display: "flex" }}>
          <RefreshCw size={16} color={THEME.colors.textMuted} style={syncLaeuft ? { animation: "spin 1s linear infinite" } : undefined} />
        </button>
      </h3>
      <div style={{ display: "flex", gap: 30 }}>
        <div>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>WP</p>
          <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxxl2, fontWeight: 700, margin: 0 }}>{wpHeute ?? "…"}</p>
        </div>
        <div>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>HF</p>
          <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxxl2, fontWeight: 700, margin: 0 }}>{hfHeute ?? "…"}</p>
        </div>
      </div>
    </div>
  );
}

// ============ SHELLY SEITE (Studio) ============
function ShellySeite() {
  const [geraete, setGeraete] = useState([]);
  const [warnungen, setWarnungen] = useState([]);
  const [tuyaGeraete, setTuyaGeraete] = useState([]);
  const [laedt, setLaedt] = useState(false);
  // Shelly Cloud meldet direkt nach einem Schaltvorgang manchmal kurzzeitig "offline" fuer ein
  // Geraet, das tatsaechlich online ist (Propagations-Verzoegerung in deren Cloud, kein echter
  // Verbindungsabbruch) - deshalb erst nach zwei aufeinanderfolgenden "offline"-Antworten in Folge
  // wirklich als offline anzeigen, statt bei jedem einzelnen Ausreisser zu flackern.
  const offlineStreakRef = useRef({});
  const geglaettetOnline = (frischeGeraete) => frischeGeraete.map(g => {
    if (g.typ !== "schalter" && g.typ !== "sensor") return g;
    if (g.online) { offlineStreakRef.current[g.id] = 0; return g; }
    const streak = (offlineStreakRef.current[g.id] || 0) + 1;
    offlineStreakRef.current[g.id] = streak;
    return streak < 2 ? { ...g, online: true } : g;
  });

  const laden = async (leise = false) => {
    if (!leise) setLaedt(true);
    try {
      const [g, w, t] = await Promise.all([
        fetch(`${API}/shelly/geraete`).then(r => r.json()),
        fetch(`${API}/shelly/warnungen`).then(r => r.json()),
        fetch(`${API}/tuya/geraete`).then(r => r.json())
      ]);
      setGeraete(geglaettetOnline(g));
      setWarnungen(w);
      setTuyaGeraete(t);
    } catch {}
    if (!leise) setLaedt(false);
  };

  useEffect(() => {
    laden();
    const intervall = setInterval(() => laden(true), 5000);
    return () => clearInterval(intervall);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSchalten = async (id, einAn) => {
    // Sofort optimistisch im UI umschalten, statt auf die Cloud-Bestaetigung zu warten - genau
    // das war der gemeldete Kern des Problems ("muss immer wieder aktualisieren"). Danach mehrmals
    // in kurzem Abstand nachpollen (statt nur einmal nach 3s), damit sich die Anzeige zuegig mit
    // dem echten Cloud-Stand abgleicht, falls der Schaltvorgang doch nicht griff.
    setGeraete(prev => prev.map(g => g.id === id ? { ...g, ein: einAn } : g));
    await fetch(`${API}/shelly/schalten/${id}?ein=${einAn}`, { method: "POST" }).catch(() => {});
    [1000, 2500, 4500, 7000].forEach(ms => setTimeout(() => laden(true), ms));
  };

  const handleTuyaSchalten = async (id, einAn) => {
    await fetch(`${API}/tuya/geraete/${id}/schalten?ein=${einAn}`, { method: "POST" }).catch(() => {});
    setTimeout(() => laden(true), 3000);
  };

  const schalter = geraete.filter(g => g.typ === "schalter");
  const sensoren = geraete.filter(g => g.typ === "sensor");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl2 }}>⚡ Studio-Steuerung</h2>
        <button onClick={laden} style={{ ...s.btnGray, width: "auto", padding: "6px 14px", fontSize: THEME.fontSize.sm }}>🔄 Aktualisieren</button>
      </div>

      {warnungen.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {warnungen.map((w, i) => (
            <div key={i} style={{ background: "#7f1d1d33", border: "1px solid #ef4444", borderRadius: 8, padding: "10px 16px", marginBottom: 8, color: THEME.colors.danger, fontSize: THEME.fontSize.md }}>
              {w.text}
            </div>
          ))}
        </div>
      )}

      {sensoren.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
          {sensoren.map(g => (
            <div key={g.id} style={{ ...s.karte, borderLeftColor: THEME.colors.primary }}>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 600, margin: "0 0 12px" }}>{g.name}</p>
              <div style={{ display: "flex", gap: 20 }}>
                {g.temperatur !== null && g.temperatur !== undefined && (
                  <div>
                    <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xxxl2, fontWeight: 700, margin: 0 }}>{g.temperatur?.toFixed(1)}°C</p>
                    <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>Temperatur</p>
                  </div>
                )}
                {g.luftfeuchtigkeit !== null && g.luftfeuchtigkeit !== undefined && (
                  <div>
                    <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxxl2, fontWeight: 700, margin: 0 }}>{g.luftfeuchtigkeit?.toFixed(0)}%</p>
                    <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>Luftfeuchtigkeit</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
        {schalter.map(g => (
          <div key={g.id} style={{ ...s.karte, borderLeftColor: g.ein ? THEME.colors.success : THEME.colors.surfaceMuted }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg2, fontWeight: 600, margin: 0 }}>{g.name}</p>
              <span style={{
                fontSize: THEME.fontSize.xs, background: THEME.colors.background, padding: "3px 10px", borderRadius: 20,
                color: g.online ? THEME.colors.success : THEME.colors.textMuted
              }}>{g.online ? "Online" : "Offline"}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <p style={{ color: g.ein ? THEME.colors.success : THEME.colors.textMuted, fontSize: THEME.fontSize.xl2, fontWeight: 700, margin: 0 }}>
                {g.ein ? "● EIN" : "○ AUS"}
              </p>
              <button onClick={() => handleSchalten(g.id, !g.ein)} disabled={!g.online} style={{
                background: g.ein ? THEME.colors.dangerBg : THEME.colors.success,
                border: "none", borderRadius: 8, padding: "8px 20px",
                opacity: g.online ? 1 : 0.5, cursor: g.online ? "pointer" : "not-allowed",
                color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600
              }}>
                {g.ein ? "Ausschalten" : "Einschalten"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {tuyaGeraete.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.lg2 }}>🏋 Trainingsgeräte (Tuya)</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
            {tuyaGeraete.map(g => (
              <div key={g.id} style={{ ...s.karte, borderLeftColor: g.ein ? THEME.colors.success : THEME.colors.surfaceMuted }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg2, fontWeight: 600, margin: 0 }}>{g.name}</p>
                  <span style={{
                    fontSize: THEME.fontSize.xs, background: THEME.colors.background, padding: "3px 10px", borderRadius: 20,
                    color: g.online ? THEME.colors.success : THEME.colors.textMuted
                  }}>{g.online ? "Online" : "Offline"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <p style={{ color: g.ein ? THEME.colors.success : THEME.colors.textMuted, fontSize: THEME.fontSize.xl2, fontWeight: 700, margin: 0 }}>
                    {g.ein ? "● EIN" : "○ AUS"}
                  </p>
                  {g.leistung_w !== null && g.leistung_w !== undefined && (
                    <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>{g.leistung_w.toFixed(1)} W</p>
                  )}
                  <button onClick={() => handleTuyaSchalten(g.id, !g.ein)} disabled={!g.online} style={{
                    background: g.ein ? THEME.colors.dangerBg : THEME.colors.success,
                    border: "none", borderRadius: 8, padding: "8px 20px",
                    color: THEME.colors.text, fontSize: THEME.fontSize.base, cursor: g.online ? "pointer" : "not-allowed",
                    fontWeight: 600, opacity: g.online ? 1 : 0.5
                  }}>
                    {g.ein ? "Ausschalten" : "Einschalten"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KalenderWidget({ onOffenChange, initialOffen }) {
  // tagesOffset 0 = heutiger Tag; +1 = morgen, +2 = uebermorgen, usw. (auch negativ moeglich).
  // Nutzer-Wunsch 2026-09-04: Pfeile links/rechts zum Durchblaettern + Wisch-Geste am Handy
  // (wie beim naechsten-Bild-Wischen) statt der zuvor fest verdrahteten "Heute"+"Morgen"-Ansicht.
  const [tagesOffset, setTagesOffset] = useState(0);
  // Cache je Tages-Offset (statt eines einzelnen "termine"-States): einmal geladene Tage bleiben
  // im Speicher, Vor-/Zurueckblaettern zu einem schon besuchten Tag ist dann sofort da statt bei
  // jedem Klick neu bei Google anzufragen (Nutzer-Meldung 2026-09-04: "es ist etwas langsam").
  // Nebenbei verhindert das Rendern direkt aus dem Cache (statt eines separaten "termine"-States,
  // der von einer verzoegerten Antwort ueberschrieben werden koennte) auch Race-Conditions beim
  // schnellen Weiterklicken/-wischen.
  const [cache, setCache] = useState({});
  const [verbunden, setVerbunden] = useState(false);
  const [offen, setOffen] = useState(!!initialOffen);
  const toggleOffen = () => { const neu = !offen; setOffen(neu); onOffenChange?.(neu); };
  const wischStartX = useRef(null);
  const ladenGerade = useRef(new Set());
  // Klick auf einen Termin oeffnet denselben grossen Bearbeiten-Dialog wie auf der Kalenderseite
  // (Nutzer-Wunsch 2026-10-06) - eigener schlanker Nachbau, da der Dialog der Kalenderseite eng
  // an deren State (Calendly, Ansichten) haengt.
  const [dialogTermin, setDialogTermin] = useState(null);
  const [dialogForm, setDialogForm] = useState(null);
  const [dialogSpeichert, setDialogSpeichert] = useState(false);

  const KALENDER_IDS = ["info@comefit.de", "e.lauria93.el@gmail.com"];

  useEffect(() => {
    fetch(`${API}/kalender/status`).then(r => r.json()).then(d => setVerbunden(d.verbunden)).catch(() => {});
  }, []);

  const ladeOffset = (offset, erzwingen = false) => {
    if (!erzwingen && (cache[offset] !== undefined || ladenGerade.current.has(offset))) return;
    ladenGerade.current.add(offset);
    const tag = new Date(); tag.setDate(tag.getDate() + offset);
    const von = new Date(tag); von.setHours(0, 0, 0, 0);
    const bis = new Date(tag); bis.setHours(23, 59, 59, 0);
    // Format ohne "Z"/Millisekunden, exakt wie die bestehende Kalenderseite (App.js ladenTermine)
    // es schon richtig macht - das Backend haengt selbst noch ein "Z" an (routers_kalender.py
    // termine_laden), ein zusaetzliches "Z" aus toISOString() fuehrte zu einem ungueltigen
    // doppelten Zeitzone-Suffix und die Google-Anfrage lieferte leer zurueck (Nutzer-Meldung
    // 2026-09-04: "bei heute steht jetzt 0 drin").
    Promise.all(KALENDER_IDS.map(id => fetch(`${API}/kalender/termine?von=${von.toISOString().slice(0, 19)}&bis=${bis.toISOString().slice(0, 19)}&kalender_id=${id}`).then(r => r.json()).catch(() => [])))
      .then(ergebnisse => {
        const liste = ergebnisse.flat().sort((a, b) => (a.uhrzeit || "").localeCompare(b.uhrzeit || ""));
        setCache(alt => ({ ...alt, [offset]: liste }));
      })
      .finally(() => ladenGerade.current.delete(offset));
  };

  useEffect(() => {
    if (!verbunden) return;
    ladeOffset(tagesOffset);
    // Nachbartage im Hintergrund vorladen - ein weiterer Klick/Wisch kommt dadurch meist schon
    // aus dem Cache statt erneut auf Google warten zu muessen.
    ladeOffset(tagesOffset - 1);
    ladeOffset(tagesOffset + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verbunden, tagesOffset]);

  if (!verbunden) return null;

  const termine = cache[tagesOffset] || [];
  const laedt = cache[tagesOffset] === undefined;

  const heuteVergleich = new Date(); heuteVergleich.setHours(0, 0, 0, 0);
  const gewaehlterTag = new Date(); gewaehlterTag.setDate(gewaehlterTag.getDate() + tagesOffset); gewaehlterTag.setHours(0, 0, 0, 0);
  const tagLabel = tagesOffset === 0 ? "Heute" : tagesOffset === 1 ? "Morgen" : tagesOffset === -1 ? "Gestern"
    : gewaehlterTag.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });

  const terminOeffnen = (t) => {
    setDialogTermin(t);
    setDialogForm({ titel: t.titel, start: t.start?.slice(0, 16) || "", ende: t.ende?.slice(0, 16) || "", beschreibung: t.beschreibung || "", ort: t.ort || "", ganztaegig: !!t.ganztaegig, kalender_id: t.kalender_id });
  };
  const dialogSchliessen = () => { setDialogTermin(null); setDialogForm(null); };
  const nachAenderungNeuLaden = () => {
    ladenGerade.current.clear();
    [tagesOffset - 1, tagesOffset, tagesOffset + 1].forEach(o => ladeOffset(o, true));
  };
  const terminSpeichern = async () => {
    if (!dialogForm.titel || !dialogForm.start) { alert("Bitte Titel und Startzeit eingeben!"); return; }
    setDialogSpeichert(true);
    try {
      const res = await fetch(`${API}/kalender/termine/${dialogTermin.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...dialogForm, kalender_id: dialogTermin.kalender_id }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Termin konnte nicht gespeichert werden."); return; }
      dialogSchliessen(); nachAenderungNeuLaden();
    } finally { setDialogSpeichert(false); }
  };
  const terminLoeschen = async () => {
    if (!window.confirm("Termin wirklich löschen?")) return;
    const res = await fetch(`${API}/kalender/termine/${dialogTermin.id}?kalender_id=${dialogTermin.kalender_id}`, { method: "DELETE" });
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Termin konnte nicht gelöscht werden."); return; }
    dialogSchliessen(); nachAenderungNeuLaden();
  };

  const wischStart = e => { wischStartX.current = e.touches[0].clientX; };
  const wischEnde = e => {
    if (wischStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - wischStartX.current;
    if (Math.abs(delta) > 40) setTagesOffset(o => o + (delta < 0 ? 1 : -1));
    wischStartX.current = null;
  };

  return (
    <div style={{ ...s.karte, marginBottom: 16, borderLeftColor: THEME.colors.primary }}>
      <div onClick={toggleOffen} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
        <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl, display: "flex", alignItems: "center", gap: 8 }}><Calendar size={18} color={THEME.colors.primary} /> Kalender ({termine.length} Termine)</h3>
        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{offen ? "▲" : "▼"}</span>
      </div>
      {offen && (
        <div style={{ marginTop: 12 }} onTouchStart={wischStart} onTouchEnd={wischEnde}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <button onClick={() => setTagesOffset(o => o - 1)} style={{ background: "none", border: "none", color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl, cursor: "pointer", padding: "0 10px" }}>‹</button>
            <span style={{ color: THEME.colors.text, fontWeight: 600, fontSize: THEME.fontSize.base }}>
              {tagLabel}{gewaehlterTag.getTime() !== heuteVergleich.getTime() && <> ({gewaehlterTag.toLocaleDateString("de-DE")})</>}
            </span>
            <button onClick={() => setTagesOffset(o => o + 1)} style={{ background: "none", border: "none", color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl, cursor: "pointer", padding: "0 10px" }}>›</button>
          </div>
          {laedt ? <p style={{ color: THEME.colors.textMuted }}>Lädt…</p> : termine.length > 0 ? termine.map(t => (
            <div key={t.id} onClick={() => terminOeffnen(t)} title="Termin öffnen" style={{ display: "flex", gap: 12, padding: "6px 0", borderTop: "1px solid #2b3d52", alignItems: "center", cursor: "pointer" }}>
              <span style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.base, fontWeight: 600, minWidth: 50 }}>{t.ganztaegig ? "📅" : t.uhrzeit}</span>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, flex: 1 }}>{t.titel}</span>
              <a href={t.link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>🔗</a>
            </div>
          )) : <p style={{ color: THEME.colors.textMuted }}>Keine Termine.</p>}
          {tagesOffset !== 0 && (
            <button onClick={() => setTagesOffset(0)} style={{ ...s.btnGray, width: "auto", marginTop: 10, padding: "6px 14px", fontSize: THEME.fontSize.sm }}>Zurück zu Heute</button>
          )}
        </div>
      )}
      {dialogTermin && dialogForm && createPortal(
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(1100px, 95vw)", maxHeight: "92vh", overflowY: "auto" }}>
            <h2 style={{ ...s.modalTitel, marginBottom: 12 }}>Termin bearbeiten</h2>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>Kalender: {dialogTermin.kalender_id}</p>
            <div style={s.inputGroup}><label style={s.label}>Titel *</label><input style={s.input} value={dialogForm.titel} onChange={e => setDialogForm(f => ({ ...f, titel: e.target.value }))} /></div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, marginBottom: 16, cursor: "pointer" }}>
              <input type="checkbox" checked={dialogForm.ganztaegig} onChange={e => setDialogForm(f => ({ ...f, ganztaegig: e.target.checked }))} /> Ganztägig
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginBottom: 8 }}>
              <div style={s.inputGroup}><label style={s.label}>Start *</label><input style={s.input} type={dialogForm.ganztaegig ? "date" : "datetime-local"} value={dialogForm.ganztaegig ? dialogForm.start.slice(0, 10) : dialogForm.start} onChange={e => setDialogForm(f => ({ ...f, start: e.target.value }))} /></div>
              <div style={s.inputGroup}><label style={s.label}>Ende</label><input style={s.input} type={dialogForm.ganztaegig ? "date" : "datetime-local"} value={dialogForm.ganztaegig ? dialogForm.ende.slice(0, 10) : dialogForm.ende} onChange={e => setDialogForm(f => ({ ...f, ende: e.target.value }))} /></div>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Beschreibung</label><textarea style={{ ...s.input, height: "42vh", minHeight: 220, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }} value={dialogForm.beschreibung} onChange={e => setDialogForm(f => ({ ...f, beschreibung: e.target.value }))} /></div>
            <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <a href={dialogTermin.link} target="_blank" rel="noreferrer" style={{ ...s.btnGray, width: "auto", padding: "10px 14px", textDecoration: "none" }}>🔗 Google</a>
                <button onClick={terminLoeschen} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg }}>Löschen</button>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button onClick={dialogSchliessen} style={s.btnGray} disabled={dialogSpeichert}>Abbrechen</button>
                <button onClick={terminSpeichern} style={{ ...s.btn, width: "auto", opacity: dialogSpeichert ? 0.6 : 1 }} disabled={dialogSpeichert}>{dialogSpeichert ? "Speichert…" : "Speichern"}</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function leseAufgeklappt(id, standard) {
  try {
    const alle = JSON.parse(localStorage.getItem("dashboardAufgeklappt")) || {};
    return alle[id] ?? standard;
  } catch { return standard; }
}

// ============ PROBETRAINING / INTERESSENTEN (Dashboard-Widget) ============
// Eigene Personenkategorie (person_typ "probetraining", Backend: routers_probetraining.py) - kein
// Mitglied, kein Vertrag. Besuche kommen aus studio_checkins, Zutritt ueber die bestehende TTLock-
// Infrastruktur. Bleibt so lange auf dem Dashboard, bis ein Mitarbeiter beendet oder als Mitglied uebernimmt.
const PROBETRAINING_STATUS_LABEL = { aktiv: "Probetraining aktiv", beendet: "Beendet", mitglied_geworden: "Mitglied geworden" };
const PROBETRAINING_STATUS_FARBE = { aktiv: THEME.colors.success, beendet: THEME.colors.textMuted, mitglied_geworden: THEME.colors.primary };
const PROBETRAINING_LEER = { vorname: "", nachname: "", telefon: "", email: "", notiz: "", rfid: "", medientyp: "", zutritt: true };

async function probetrainingFehlertext(res) {
  try {
    const d = await res.json();
    return typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail || d);
  } catch { return `${res.status} ${res.statusText}`; }
}

function probetrainingRfidText(n) {
  if (!n.hat_rfid) return null;
  const art = n.rfid_medientyp === "karte" ? "Karte" : n.rfid_medientyp === "band" ? "Band" : "Band/Karte";
  return n.rfid_status === "gesperrt" ? `${art} gesperrt` : `${art} vorhanden`;
}

// Gemeinsames RFID-Eingabefeld (PC-Leser tippt die Nummer + Enter, alternativ Handy-NFC) fuer "Neu" und "RFID zuweisen"
function ProbetrainingRfidFeld({ wert, setWert, medientyp, setMedientyp, setErfassungsart, onEnter, autoFocus, zutritt, setZutritt }) {
  const nfcUnterstuetzt = typeof window !== "undefined" && "NDEFReader" in window;
  const [nfcLaeuft, setNfcLaeuft] = useState(false);
  const [nfcFehler, setNfcFehler] = useState("");
  const handleNfc = async () => {
    setNfcFehler(""); setNfcLaeuft(true);
    try {
      const ndef = new window.NDEFReader();
      await ndef.scan();
      ndef.onreading = (event) => {
        const bytes = (event.serialNumber || "").split(":").filter(Boolean).map(h => parseInt(h, 16));
        const nummer = berechneSafesmartKartennummer(bytes);
        setNfcLaeuft(false);
        if (nummer != null) { setWert(String(nummer)); setErfassungsart("handy_nfc"); }
        else setNfcFehler("Chip-Format nicht erkannt (nur 4-Byte-UIDs) - bitte Nummer manuell eingeben.");
      };
      ndef.onreadingerror = () => { setNfcFehler("Chip konnte nicht gelesen werden - bitte näher ans Handy halten."); setNfcLaeuft(false); };
    } catch (e) { setNfcFehler(e?.message || "NFC-Scan konnte nicht gestartet werden."); setNfcLaeuft(false); }
  };
  const scanFeldRef = useRef(null);
  useEffect(() => { if (medientyp && scanFeldRef.current) scanFeldRef.current.focus(); }, [medientyp]);
  return (
    <>
      <div style={s.inputGroup}>
        <label style={s.label}>1. Karte oder Band wählen</label>
        <select style={s.input} value={medientyp} autoFocus={autoFocus} onChange={e => setMedientyp(e.target.value)}>
          <option value="">Bitte wählen…</option>
          <option value="band">Band</option>
          <option value="karte">Karte</option>
        </select>
      </div>
      {setZutritt && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.text, margin: "0 0 12px", cursor: "pointer" }}>
          <input type="checkbox" checked={zutritt !== false} onChange={e => { setZutritt(e.target.checked); setTimeout(() => scanFeldRef.current && medientyp && scanFeldRef.current.focus(), 0); }} />
          2. Darf durch die Tür (Zutritt mit {medientyp === "karte" ? "Karte" : medientyp === "band" ? "Band" : "Karte/Band"} freischalten)
        </label>
      )}
      <div style={s.inputGroup}>
        <label style={s.label}>{setZutritt ? "3" : "2"}. RFID scannen ({medientyp === "karte" ? "Karte" : medientyp === "band" ? "Band" : "Karte/Band"} an den Leser halten)</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input ref={scanFeldRef} style={{ ...s.input, flex: 1, opacity: medientyp ? 1 : 0.5 }} value={wert} disabled={!medientyp}
            placeholder={medientyp ? "Kartennummer / Scan" : "Erst oben Karte oder Band wählen"}
            onChange={e => { setWert(e.target.value); setErfassungsart(null); }}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); onEnter && onEnter(); } }} />
          {nfcUnterstuetzt && <button type="button" onClick={handleNfc} disabled={nfcLaeuft || !medientyp} style={{ ...s.btnGray, width: "auto" }}>{nfcLaeuft ? "Scannt…" : "📱 NFC"}</button>}
        </div>
        {nfcFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: "6px 0 0" }}>{nfcFehler}</p>}
      </div>
    </>
  );
}

function ProbetrainingWidget({ rechte, benutzer, onPersonOeffnen, onOffenChange, initialOffen }) {
  const [filter, setFilter] = useState("aktiv"); // aktiv | beendet | mitglied_geworden | alle
  // Standard bleibt aufgeklappt; nur ein ausdruecklich zugeklappter Zustand (false) wird gemerkt.
  const [listeOffen, setListeOffen] = useState(initialOffen !== false);
  const toggleListe = () => { const neu = !listeOffen; setListeOffen(neu); onOffenChange?.(neu); };
  const [liste, setListe] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [ladeFehler, setLadeFehler] = useState("");
  const [neuOffen, setNeuOffen] = useState(false);
  const [neuForm, setNeuForm] = useState(PROBETRAINING_LEER);
  const [neuErfassungsart, setNeuErfassungsart] = useState(null);
  const [neuFehler, setNeuFehler] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailForm, setDetailForm] = useState(null);
  const [detailFehler, setDetailFehler] = useState("");
  const [rfidNutzer, setRfidNutzer] = useState(null);
  const [rfidWert, setRfidWert] = useState("");
  const [rfidMedientyp, setRfidMedientyp] = useState("");
  const [rfidErfassungsart, setRfidErfassungsart] = useState(null);
  const [rfidFehler, setRfidFehler] = useState("");
  const [rfidZutritt, setRfidZutritt] = useState(true);
  const [beendenNutzer, setBeendenNutzer] = useState(null);
  const [rfidFreigeben, setRfidFreigeben] = useState(true);
  const [umwandeln, setUmwandeln] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const darfMitgliedAnlegen = rechte?.rolle === "admin" || !!rechte?.darf_mitglieder_anlegen;

  const laden = () => {
    fetch(`${API}/probetraining/nutzer?status=${filter}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => { setListe(Array.isArray(d) ? d : []); setLadeFehler(""); })
      .catch(() => setLadeFehler("Probetrainings konnten nicht geladen werden."))
      .finally(() => setLaedt(false));
  };
  useEffect(() => {
    setLaedt(true); laden();
    const t = setInterval(laden, 60000); // neue Besuche zeitnah sichtbar
    return () => clearInterval(t);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const ladeDetail = (id) => {
    fetch(`${API}/probetraining/nutzer/${id}`).then(r => r.json()).then(d => {
      setDetail(d);
      setDetailForm({ vorname: d.vorname, nachname: d.nachname, telefon: d.telefon || "", email: d.email || "", notiz: d.notiz || "" });
    }).catch(() => {});
  };
  const oeffneDetail = (n) => { setDetailId(n.id); setDetail(null); setDetailForm(null); setDetailFehler(""); ladeDetail(n.id); };
  const schliesseDetail = () => { setDetailId(null); setDetail(null); setDetailForm(null); };

  const rfidNormalisieren = (wert, erfassungsart) => {
    let karte = (wert || "").trim();
    if (karte && erfassungsart !== "handy_nfc") {
      const umgerechnet = pcLeserZuKartennummer(karte);
      if (umgerechnet != null) karte = String(umgerechnet);
    }
    return karte;
  };

  const handleAnlegen = async () => {
    if (laeuft) return;
    setNeuFehler("");
    if (!neuForm.vorname.trim() || !neuForm.nachname.trim()) { setNeuFehler("Bitte Vor- und Nachname angeben."); return; }
    setLaeuft(true);
    try {
      const karte = rfidNormalisieren(neuForm.rfid, neuErfassungsart);
      const res = await fetch(`${API}/probetraining/nutzer`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vorname: neuForm.vorname, nachname: neuForm.nachname, telefon: neuForm.telefon || null, email: neuForm.email || null, notiz: neuForm.notiz || null,
          rfid_kartennummer: karte || null, erfassungsart: karte ? neuErfassungsart : null, medientyp: karte ? (neuForm.medientyp || null) : null,
          zutritt_erlaubt: neuForm.zutritt !== false,
        }),
      });
      if (!res.ok) { setNeuFehler(await probetrainingFehlertext(res)); return; }
      try { await logAktivitaet("Probetraining angelegt", karte ? "mit RFID" : "ohne RFID", null, `${neuForm.nachname}, ${neuForm.vorname}`, benutzer); } catch {}
      setNeuOffen(false); setNeuForm(PROBETRAINING_LEER); setNeuErfassungsart(null);
      setFilter("aktiv"); laden();
    } finally { setLaeuft(false); }
  };

  const handleDetailSpeichern = async () => {
    if (laeuft || !detailForm) return;
    setDetailFehler("");
    setLaeuft(true);
    try {
      const res = await fetch(`${API}/probetraining/nutzer/${detailId}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(detailForm) });
      if (!res.ok) { setDetailFehler(await probetrainingFehlertext(res)); return; }
      ladeDetail(detailId); laden();
    } finally { setLaeuft(false); }
  };

  const oeffneRfid = (n) => { setRfidNutzer(n); setRfidZutritt(true); setRfidWert(""); setRfidMedientyp(n.rfid_medientyp || ""); setRfidErfassungsart(null); setRfidFehler(""); };
  const handleRfidSpeichern = async () => {
    if (laeuft || !rfidNutzer) return;
    const karte = rfidNormalisieren(rfidWert, rfidErfassungsart);
    if (!rfidMedientyp) { setRfidFehler("Bitte zuerst Karte oder Band auswählen."); return; }
    if (!karte) { setRfidFehler("Bitte Karte/Band scannen oder Nummer eingeben."); return; }
    setLaeuft(true);
    try {
      const res = await fetch(`${API}/probetraining/nutzer/${rfidNutzer.id}/rfid`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rfid_kartennummer: karte, erfassungsart: rfidErfassungsart, medientyp: rfidMedientyp || null, zutritt_erlaubt: rfidZutritt }),
      });
      if (!res.ok) { setRfidFehler(await probetrainingFehlertext(res)); return; }
      const id = rfidNutzer.id;
      setRfidNutzer(null); laden(); if (detailId === id) ladeDetail(id);
    } finally { setLaeuft(false); }
  };
  const handleRfidAktion = async (n, aktion) => {
    const texte = { "rfid-sperren": "RFID sperren? Der Türzugang wird sofort entzogen.", entfernen: "RFID-Zuordnung wirklich entfernen? Der Türzugang wird entzogen." };
    if (texte[aktion] && !window.confirm(texte[aktion])) return;
    const res = aktion === "entfernen"
      ? await fetch(`${API}/probetraining/nutzer/${n.id}/rfid`, { method: "DELETE" })
      : await fetch(`${API}/probetraining/nutzer/${n.id}/${aktion}`, { method: "POST" });
    if (!res.ok) { alert(await probetrainingFehlertext(res)); return; }
    laden(); if (detailId === n.id) ladeDetail(n.id);
  };

  const handleBeenden = async () => {
    if (laeuft || !beendenNutzer) return;
    setLaeuft(true);
    try {
      const res = await fetch(`${API}/probetraining/nutzer/${beendenNutzer.id}/beenden`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rfid_freigeben: rfidFreigeben }),
      });
      if (!res.ok) { alert(await probetrainingFehlertext(res)); return; }
      try { await logAktivitaet("Probetraining beendet", null, null, `${beendenNutzer.nachname}, ${beendenNutzer.vorname}`, benutzer); } catch {}
      setBeendenNutzer(null); schliesseDetail(); laden();
    } finally { setLaeuft(false); }
  };

  const zeitDE = (iso) => iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "–";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <select style={{ ...s.input, width: "auto" }} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="aktiv">Aktive Probetrainings</option>
          <option value="beendet">Beendet (Historie)</option>
          <option value="mitglied_geworden">Als Mitglied übernommen</option>
          <option value="alle">Alle</option>
        </select>
        <button onClick={() => { setNeuForm(PROBETRAINING_LEER); setNeuErfassungsart(null); setNeuFehler(""); setNeuOffen(true); }} title="Neues Probetraining anlegen" aria-label="Neues Probetraining anlegen" style={{ ...s.btn, width: "auto", padding: "8px 16px", fontSize: THEME.fontSize.xl, lineHeight: 1 }}>+</button>
        <span onClick={toggleListe} role="button" aria-expanded={listeOffen} aria-label={listeOffen ? "Liste zuklappen" : "Liste aufklappen"} title={listeOffen ? "Liste zuklappen" : "Liste aufklappen"} style={{ marginLeft: "auto", cursor: "pointer", padding: "4px 8px", color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{listeOffen ? "▲" : "▼"}</span>
      </div>

      {ladeFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base }}>{ladeFehler}</p>}
      {listeOffen && laedt && liste.length === 0 && !ladeFehler && <p style={{ color: THEME.colors.textMuted }}>Lädt…</p>}
      {listeOffen && !laedt && liste.length === 0 && !ladeFehler && filter !== "aktiv" && (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base }}>Keine Einträge.</p>
      )}

      <div style={{ display: listeOffen ? "flex" : "none", flexDirection: "column", gap: 8 }}>
        {liste.map(n => {
          const rfidText = probetrainingRfidText(n);
          return (
            <div key={n.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div style={{ cursor: "pointer", minWidth: 0 }} onClick={() => oeffneDetail(n)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ color: THEME.colors.text, fontWeight: 600, fontSize: THEME.fontSize.lg }}>{n.vorname} {n.nachname}</span>
                    {n.status !== "aktiv" && <span style={{ color: PROBETRAINING_STATUS_FARBE[n.status] || THEME.colors.textMuted, fontSize: THEME.fontSize.xs, fontWeight: 700, textTransform: "uppercase" }}>{PROBETRAINING_STATUS_LABEL[n.status] || n.status}</span>}
                  </div>
                  <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginTop: 4 }}>
                    {n.besuche} Besuch{n.besuche === 1 ? "" : "e"}
                    {n.letzter_besuch ? ` · Letzter Besuch: ${datumDE(n.letzter_besuch)}` : ""}
                  </div>
                  <div style={{ fontSize: THEME.fontSize.sm, marginTop: 2, color: rfidText ? (n.rfid_status === "gesperrt" ? THEME.colors.danger : THEME.colors.success) : (n.status === "aktiv" ? THEME.colors.warning : THEME.colors.textMuted) }}>
                    {rfidText || (n.status === "aktiv" ? "Kein RFID" : n.rfid_zuletzt ? `RFID frei (zuletzt ${n.rfid_zuletzt})` : "Keine RFID")}
                    {n.telefon ? <span style={{ color: THEME.colors.textMuted }}> · {n.telefon}</span> : null}
                  </div>
                  {n.status === "mitglied_geworden" && (
                    <div style={{ fontSize: THEME.fontSize.sm, marginTop: 4, color: THEME.colors.textSecondary }}>
                      Mitglied geworden am {datumDE((n.mitglied_geworden_am || "").slice(0, 10))} →{" "}
                      {n.mitglied_id && onPersonOeffnen
                        ? <strong onClick={() => onPersonOeffnen("mitglied", n.mitglied_id)} style={{ color: THEME.colors.primary, cursor: "pointer", textDecoration: "underline" }}>Mitglied #{n.mitglied_id}{n.mitglied_name ? ` (${n.mitglied_name})` : ""}</strong>
                        : <span>Mitglied #{n.mitglied_id}</span>}
                    </div>
                  )}
                  {n.status === "beendet" && n.beendet_am && (
                    <div style={{ fontSize: THEME.fontSize.sm, marginTop: 4, color: THEME.colors.textMuted }}>Beendet am {datumDE(n.beendet_am.slice(0, 10))}</div>
                  )}
                </div>
                {n.status === "aktiv" && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button onClick={() => oeffneDetail(n)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 10px" }}>Öffnen</button>
                    <button onClick={() => oeffneRfid(n)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 10px" }}>{n.hat_rfid ? "RFID ändern" : "RFID zuweisen"}</button>
                    {darfMitgliedAnlegen && <button onClick={() => setUmwandeln(n)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 10px", background: THEME.colors.success }}>Als Mitglied übernehmen</button>}
                    <button onClick={() => { setRfidFreigeben(n.hat_rfid); setBeendenNutzer(n); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 10px", color: THEME.colors.danger }}>Beenden</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {neuOffen && createPortal(
        <div style={s.modalBg} onClick={() => setNeuOffen(false)}>
          <div style={{ ...s.modal, width: "min(520px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>Neues Probetraining</h2>
            {neuFehler && <p style={{ color: THEME.colors.danger }}>{neuFehler}</p>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={s.inputGroup}><label style={s.label}>Vorname *</label><input style={s.input} autoFocus value={neuForm.vorname} onChange={e => setNeuForm({ ...neuForm, vorname: e.target.value })} /></div>
              <div style={s.inputGroup}><label style={s.label}>Nachname *</label><input style={s.input} value={neuForm.nachname} onChange={e => setNeuForm({ ...neuForm, nachname: e.target.value })} /></div>
              <div style={s.inputGroup}><label style={s.label}>Telefon</label><input style={s.input} value={neuForm.telefon} onChange={e => setNeuForm({ ...neuForm, telefon: e.target.value })} /></div>
              <div style={s.inputGroup}><label style={s.label}>E-Mail</label><input style={s.input} type="email" value={neuForm.email} onChange={e => setNeuForm({ ...neuForm, email: e.target.value })} /></div>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Notiz</label><textarea style={{ ...s.input, minHeight: 56 }} value={neuForm.notiz} onChange={e => setNeuForm({ ...neuForm, notiz: e.target.value })} /></div>
            <ProbetrainingRfidFeld wert={neuForm.rfid} setWert={v => setNeuForm(f => ({ ...f, rfid: v }))} medientyp={neuForm.medientyp}
              setMedientyp={v => setNeuForm(f => ({ ...f, medientyp: v }))} setErfassungsart={setNeuErfassungsart} onEnter={handleAnlegen}
              zutritt={neuForm.zutritt} setZutritt={v => setNeuForm(f => ({ ...f, zutritt: v }))} />
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 10px" }}>Die Karte/das Band kann auch später zugewiesen werden. Ohne Haken wird sie nur zugeordnet, am Schloss aber nicht freigeschaltet.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setNeuOffen(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
              <button onClick={handleAnlegen} disabled={laeuft} style={{ ...s.btn, width: "auto", opacity: laeuft ? 0.6 : 1 }}>{laeuft ? "Speichert…" : "Anlegen"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {detailId && createPortal(
        <div style={s.modalBg} onClick={schliesseDetail}>
          <div style={{ ...s.modal, width: "min(720px, 95vw)", maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            {!detail || !detailForm ? <p style={{ color: THEME.colors.textMuted }}>Lädt…</p> : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <h2 style={{ color: THEME.colors.text, margin: 0 }}>{detail.vorname} {detail.nachname}</h2>
                  <span style={{ color: PROBETRAINING_STATUS_FARBE[detail.status], fontWeight: 700, textTransform: "uppercase", fontSize: THEME.fontSize.sm }}>{PROBETRAINING_STATUS_LABEL[detail.status] || detail.status}</span>
                </div>
                {detailFehler && <p style={{ color: THEME.colors.danger }}>{detailFehler}</p>}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
                  <div style={s.inputGroup}><label style={s.label}>Vorname</label><input style={s.input} value={detailForm.vorname} onChange={e => setDetailForm({ ...detailForm, vorname: e.target.value })} /></div>
                  <div style={s.inputGroup}><label style={s.label}>Nachname</label><input style={s.input} value={detailForm.nachname} onChange={e => setDetailForm({ ...detailForm, nachname: e.target.value })} /></div>
                  <div style={s.inputGroup}><label style={s.label}>Telefon</label><input style={s.input} value={detailForm.telefon} onChange={e => setDetailForm({ ...detailForm, telefon: e.target.value })} /></div>
                  <div style={s.inputGroup}><label style={s.label}>E-Mail</label><input style={s.input} value={detailForm.email} onChange={e => setDetailForm({ ...detailForm, email: e.target.value })} /></div>
                </div>
                <div style={s.inputGroup}><label style={s.label}>Notiz</label><textarea style={{ ...s.input, minHeight: 56 }} value={detailForm.notiz} onChange={e => setDetailForm({ ...detailForm, notiz: e.target.value })} /></div>
                <button onClick={handleDetailSpeichern} disabled={laeuft} style={{ ...s.btn, width: "auto" }}>Stammdaten speichern</button>

                <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${THEME.colors.border}` }}>
                  <h3 style={{ color: THEME.colors.text, margin: "0 0 8px", fontSize: THEME.fontSize.lg }}>RFID &amp; Zutritt</h3>
                  <p style={{ color: THEME.colors.textSecondary, margin: "2px 0" }}>Nummer: <span style={{ color: THEME.colors.text }}>{detail.rfid_kartennummer || "–"}</span> · Status: <span style={{ color: RFID_STATUS_FARBE[detail.rfid_status] || THEME.colors.text, fontWeight: 600 }}>{RFID_STATUS_LABEL[detail.rfid_status] || "Keine RFID"}</span></p>
                  {detail.status === "aktiv" && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0" }}>
                      <button onClick={() => oeffneRfid(detail)} style={{ ...s.btnGray, width: "auto" }}>{detail.hat_rfid ? "RFID ändern" : "RFID zuweisen"}</button>
                      {detail.hat_rfid && detail.rfid_status === "aktiv" && <button onClick={() => handleRfidAktion(detail, "rfid-sperren")} style={{ ...s.btnGray, width: "auto" }}>RFID sperren</button>}
                      {detail.hat_rfid && detail.rfid_status === "gesperrt" && <button onClick={() => handleRfidAktion(detail, "rfid-aktivieren")} style={{ ...s.btnGray, width: "auto" }}>RFID aktivieren</button>}
                      {detail.hat_rfid && <button onClick={() => handleRfidAktion(detail, "entfernen")} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>RFID entfernen</button>}
                    </div>
                  )}
                  <TTLockZutrittZeile personTyp="probetraining" personId={detail.id} />
                </div>

                <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${THEME.colors.border}` }}>
                  <h3 style={{ color: THEME.colors.text, margin: "0 0 8px", fontSize: THEME.fontSize.lg }}>Besuche ({detail.besuche})</h3>
                  {detail.besuchsverlauf.length === 0 ? <p style={{ color: THEME.colors.textMuted }}>Noch kein Besuch erfasst.</p> : (
                    <div style={{ maxHeight: 180, overflowY: "auto" }}>
                      {detail.besuchsverlauf.map((b, i) => (
                        <div key={i} style={{ padding: "5px 0", borderTop: i ? `1px solid ${THEME.colors.border}` : "none", color: THEME.colors.textSecondary }}>{zeitDE(b.zeitpunkt)}</div>
                      ))}
                    </div>
                  )}
                  {detail.status === "mitglied_geworden" && <p style={{ color: THEME.colors.textSecondary, marginTop: 10 }}>Mitglied geworden am {datumDE((detail.mitglied_geworden_am || "").slice(0, 10))} → Mitglied #{detail.mitglied_id}{detail.mitglied_name ? ` (${detail.mitglied_name})` : ""}</p>}
                  {detail.status === "beendet" && detail.beendet_am && <p style={{ color: THEME.colors.textMuted, marginTop: 10 }}>Beendet am {datumDE(detail.beendet_am.slice(0, 10))}</p>}
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 22 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {detail.status === "aktiv" && darfMitgliedAnlegen && <button onClick={() => { schliesseDetail(); setUmwandeln(detail); }} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>Als Mitglied übernehmen</button>}
                    {detail.status === "aktiv" && <button onClick={() => { setRfidFreigeben(detail.hat_rfid); setBeendenNutzer(detail); }} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Probetraining beenden</button>}
                  </div>
                  <button onClick={schliesseDetail} style={{ ...s.btnGray, width: "auto" }}>Schließen</button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {rfidNutzer && createPortal(
        <div style={s.modalBg} onClick={() => setRfidNutzer(null)}>
          <div style={{ ...s.modal, width: "min(440px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>RFID für {rfidNutzer.vorname} {rfidNutzer.nachname}</h2>
            {rfidNutzer.hat_rfid && <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.base }}>Aktuell zugewiesen: {rfidNutzer.rfid_kartennummer} – wird durch die neue Nummer ersetzt.</p>}
            {rfidFehler && <p style={{ color: THEME.colors.danger }}>{rfidFehler}</p>}
            <ProbetrainingRfidFeld autoFocus wert={rfidWert} setWert={setRfidWert} medientyp={rfidMedientyp} setMedientyp={setRfidMedientyp} setErfassungsart={setRfidErfassungsart} onEnter={handleRfidSpeichern} zutritt={rfidZutritt} setZutritt={setRfidZutritt} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setRfidNutzer(null)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
              <button onClick={handleRfidSpeichern} disabled={laeuft} style={{ ...s.btn, width: "auto", opacity: laeuft ? 0.6 : 1 }}>{laeuft ? "Speichert…" : (rfidZutritt ? "Zuweisen & Zutritt aktivieren" : "Zuweisen (ohne Türzutritt)")}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {beendenNutzer && createPortal(
        <div style={s.modalBg} onClick={() => setBeendenNutzer(null)}>
          <div style={{ ...s.modal, width: "min(460px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>Probetraining beenden?</h2>
            <p style={{ color: THEME.colors.textSecondary, lineHeight: 1.5 }}>
              <strong>{beendenNutzer.vorname} {beendenNutzer.nachname}</strong> verliert den Studiozutritt und verschwindet vom Dashboard.
              Der Eintrag und alle Besuche bleiben in der Historie erhalten.
            </p>
            {beendenNutzer.hat_rfid && (
              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", color: THEME.colors.textSecondary, margin: "10px 0" }}>
                <input type="checkbox" checked={rfidFreigeben} onChange={e => setRfidFreigeben(e.target.checked)} style={{ marginTop: 3 }} />
                <span>Karte/Band wurde zurückgegeben – RFID {beendenNutzer.rfid_kartennummer} freigeben (kann neu vergeben werden)</span>
              </label>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button onClick={() => setBeendenNutzer(null)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
              <button onClick={handleBeenden} disabled={laeuft} style={{ ...s.btn, width: "auto", background: THEME.colors.danger, opacity: laeuft ? 0.6 : 1 }}>{laeuft ? "Beendet…" : "Ja, Probetraining beenden"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {umwandeln && createPortal(
        <PersonFormular
          typ="mitglied" typLocked benutzer={benutzer}
          initial={{ vorname: umwandeln.vorname, nachname: umwandeln.nachname, telefon: umwandeln.telefon || "", email: umwandeln.email || "" }}
          konvertierung={{ richtung: "zu_mitglied", quelleTyp: "probetraining", quelleId: umwandeln.id, quelleName: `${umwandeln.vorname} ${umwandeln.nachname}` }}
          onSpeichern={() => { setUmwandeln(null); laden(); }}
          onAbbrechen={() => setUmwandeln(null)}
        />,
        document.body
      )}
    </div>
  );
}

const DASHBOARD_MODULE_DEFS = {
  stats: { titel: "Statistik-Kacheln", admin: true, layout: { x: 0, y: 0, w: 12, h: 5 } },
  kalender: { titel: "Kalender", admin: false, layout: { x: 0, y: 5, w: 6, h: 8 } },
  studio: { titel: "Studio-Steuerung", admin: false, layout: { x: 6, y: 5, w: 6, h: 8 } },
  onlinePortal: { titel: "Online Portal", admin: false, layout: { x: 0, y: 13, w: 6, h: 8 } },
  brett: { titel: "Schwarzes Brett", admin: false, layout: { x: 6, y: 13, w: 6, h: 6 } },
  geburtstage: { titel: "Geburtstage", admin: false, layout: { x: 0, y: 21, w: 6, h: 8 } },
  todos: { titel: "To-dos", admin: false, layout: { x: 6, y: 21, w: 6, h: 8 } },
  aktivitaet: { titel: "Aktivitätsprotokoll", admin: true, layout: { x: 0, y: 29, w: 12, h: 8 } },
  wetter: { titel: "Wetter", admin: false, layout: { x: 0, y: 37, w: 4, h: 6 } },
  checkin: { titel: "Check In", admin: false, layout: { x: 4, y: 37, w: 4, h: 6 } },
  rfidSuche: { titel: "🔍 Karte/Band gefunden", admin: false, layout: { x: 0, y: 43, w: 8, h: 10 } },
  ttlockStatus: { titel: "🔐 TTLock-Zutritt", admin: true, layout: { x: 8, y: 43, w: 4, h: 10 } },
  ttlockUnzugeordnet: { titel: "📥 TTLock: Unzugeordnete Karten", admin: true, layout: { x: 0, y: 53, w: 12, h: 9 } },
  probetraining: { titel: "🏃 Probetrainings", admin: false, layout: { x: 0, y: 5, w: 12, h: 10 } },
  rfidPruefliste: { titel: "🪪 RFID-Prüfliste", admin: true, layout: { x: 0, y: 0, w: 12, h: 4 } },
};

function Dashboard({ benutzer, rolle, benutzerId, seiteWechseln, onPersonOeffnen, rechte }) {
  const heute = new Date();
  const [monat, setMonat] = useState(heute.getMonth() + 1);
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [stats, setStats] = useState(null);
  const [statsVormonat, setStatsVormonat] = useState(null);
  const [aktivitaeten, setAktivitaeten] = useState([]);
  const [aktivWochen, setAktivWochen] = useState(1);
  const [todos, setTodos] = useState([]);
  const [todoFormOffen, setTodoFormOffen] = useState(false);
  const [todoTitel, setTodoTitel] = useState("");
  const [todoBeschreibung, setTodoBeschreibung] = useState("");
  const [todoWiederholung, setTodoWiederholung] = useState("einmalig");
  const [todoZugewiesen, setTodoZugewiesen] = useState("alle");
  const [geburtstage, setGeburtstage] = useState([]);
  const [gebOffen, setGebOffen] = useState(() => leseAufgeklappt("geburtstage", false));
  const [brettLimit, setBrettLimit] = useState(3);
  const [todoLimit, setTodoLimit] = useState(5);
  const [statsOffen, setStatsOffen] = useState(() => leseAufgeklappt("stats", true));
  const [brettOffen, setBrettOffen] = useState(() => leseAufgeklappt("brett", false));
  const [todoOffen, setTodoOffen] = useState(() => leseAufgeklappt("todos", false));
  const [aktivOffen, setAktivOffen] = useState(() => leseAufgeklappt("aktivitaet", false));
  const [autoMeldung, setAutoMeldung] = useState("");
  const [news, setNews] = useState([]);
  const [wetter, setWetter] = useState(null);
  const MONATE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

  const moduleVerfuegbar = Object.keys(DASHBOARD_MODULE_DEFS).filter(id => !DASHBOARD_MODULE_DEFS[id].admin || rolle === "admin");

  // Neues Widget "probetraining": erscheint auch bei bereits gespeicherter Dashboard-Konfiguration
  // von selbst, es sei denn, diese Person hat es an diesem Geraet bewusst ausgeblendet.
  // Gleiches gilt fuer "rfidPruefliste" (war vorher eine feste Karte oben auf dem Dashboard, jetzt ein Widget).
  const mitNeuenModulen = (liste) => {
    let ergebnis = liste;
    [["probetraining", "dashboardProbetrainingAusgeblendet"], ["rfidPruefliste", "dashboardRfidPruefAusgeblendet"]].forEach(([id, flag]) => {
      try { if (localStorage.getItem(flag)) return; } catch {}
      if (!ergebnis.includes(id) && moduleVerfuegbar.includes(id)) ergebnis = [id, ...ergebnis];
    });
    return ergebnis;
  };
  const [sichtbar, setSichtbar] = useState(() => {
    try {
      const gespeichert = JSON.parse(localStorage.getItem("dashboardSichtbar"));
      if (Array.isArray(gespeichert)) return mitNeuenModulen(gespeichert.filter(id => moduleVerfuegbar.includes(id)));
    } catch {}
    return moduleVerfuegbar;
  });
  const [layout, setLayout] = useState(() => {
    try {
      const gespeichert = JSON.parse(localStorage.getItem("dashboardLayout"));
      if (Array.isArray(gespeichert)) {
        const bekannt = gespeichert.filter(l => DASHBOARD_MODULE_DEFS[l.i]);
        const fehlend = moduleVerfuegbar.filter(id => !bekannt.some(l => l.i === id)).map(id => ({ i: id, ...DASHBOARD_MODULE_DEFS[id].layout }));
        return [...bekannt, ...fehlend];
      }
    } catch {}
    return moduleVerfuegbar.map(id => ({ i: id, ...DASHBOARD_MODULE_DEFS[id].layout }));
  });
  const [modulMenuOffen, setModulMenuOffen] = useState(false);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [aufgeklappt, setAufgeklappt] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dashboardAufgeklappt")) || {}; } catch { return {}; }
  });
  const [autoHoehePx, setAutoHoehePx] = useState({});
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const pruefen = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", pruefen);
    return () => window.removeEventListener("resize", pruefen);
  }, []);
  const ROW_HOEHE_PX = 30, GRID_MARGIN_PX = 20;

  const handleLayoutChange = (neuesLayout) => {
    if (mobil) return; // Mobile-Einspalten-Ansicht nicht ins gespeicherte Desktop-Layout übernehmen
    setLayout(alteLayout => {
      const merged = alteLayout.map(alt => {
        const aktualisiert = neuesLayout.find(n => n.i === alt.i);
        if (!aktualisiert) return alt;
        // Während ein Modul automatisch aufgeklappt ist, dessen Höhe nicht dauerhaft übernehmen
        if (aufgeklappt[alt.i]) { const { h, ...rest } = aktualisiert; return { ...alt, ...rest }; }
        return { ...alt, ...aktualisiert };
      });
      localStorage.setItem("dashboardLayout", JSON.stringify(merged));
      return merged;
    });
  };
  const modulAufklappen = (id, offen) => {
    setAufgeklappt(alt => {
      const neu = { ...alt, [id]: offen };
      localStorage.setItem("dashboardAufgeklappt", JSON.stringify(neu));
      return neu;
    });
    if (!offen) setAutoHoehePx(alt => { const neu = { ...alt }; delete neu[id]; return neu; });
  };
  const modulHoeheGemessen = (id, px) => {
    setAutoHoehePx(alt => (alt[id] === px ? alt : { ...alt, [id]: px }));
  };

  const serverGeladenRef = useRef(false);
  useEffect(() => {
    if (!benutzerId) { serverGeladenRef.current = true; return; }
    fetch(`${API}/dashboard/einstellungen/${benutzerId}`).then(r => r.json()).then(daten => {
      if (daten && daten.config) {
        try {
          const geladen = JSON.parse(daten.config);
          if (Array.isArray(geladen.layout)) {
            const bekannt = geladen.layout.filter(l => DASHBOARD_MODULE_DEFS[l.i]);
            const fehlend = moduleVerfuegbar.filter(id => !bekannt.some(l => l.i === id)).map(id => ({ i: id, ...DASHBOARD_MODULE_DEFS[id].layout }));
            setLayout([...bekannt, ...fehlend]);
          }
          if (Array.isArray(geladen.sichtbar)) setSichtbar(mitNeuenModulen(geladen.sichtbar.filter(id => moduleVerfuegbar.includes(id))));
          if (geladen.aufgeklappt && typeof geladen.aufgeklappt === "object") {
            setAufgeklappt(geladen.aufgeklappt);
            if ("stats" in geladen.aufgeklappt) setStatsOffen(!!geladen.aufgeklappt.stats);
            if ("brett" in geladen.aufgeklappt) setBrettOffen(!!geladen.aufgeklappt.brett);
            if ("todos" in geladen.aufgeklappt) setTodoOffen(!!geladen.aufgeklappt.todos);
            if ("geburtstage" in geladen.aufgeklappt) setGebOffen(!!geladen.aufgeklappt.geburtstage);
            if ("aktivitaet" in geladen.aufgeklappt) setAktivOffen(!!geladen.aufgeklappt.aktivitaet);
          }
        } catch {}
      }
    }).catch(() => {}).finally(() => { serverGeladenRef.current = true; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benutzerId]);

  useEffect(() => {
    if (!benutzerId || !serverGeladenRef.current) return;
    const timer = setTimeout(() => {
      const config = JSON.stringify({ layout, sichtbar, aufgeklappt });
      fetch(`${API}/dashboard/einstellungen/${benutzerId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config }),
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(timer);
  }, [layout, sichtbar, aufgeklappt, benutzerId]);

  const angezeigtesLayout = layout
    .map(l => {
      const px = autoHoehePx[l.i];
      const benoetigteReihen = px ? Math.ceil((px + 40) / (ROW_HOEHE_PX + GRID_MARGIN_PX)) : l.h;
      // Eingeklappt: Box schrumpft auf den tatsaechlich gemessenen (kleinen) Inhalt, keine Mindesthoehe.
      if (!aufgeklappt[l.i]) return { ...l, h: benoetigteReihen };
      // Ausgeklappt auf Mobile nicht auf die (breitere Desktop-Spalten-)Standardhöhe hochfloren, sonst bleibt bei kurzem Inhalt viel Leerraum
      return { ...l, h: mobil ? benoetigteReihen : Math.max(l.h, benoetigteReihen) };
    })
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map(l => mobil ? { ...l, x: 0, w: 1 } : l);
  const modulAusblenden = (id) => {
    if (id === "probetraining") { try { localStorage.setItem("dashboardProbetrainingAusgeblendet", "1"); } catch {} }
    if (id === "rfidPruefliste") { try { localStorage.setItem("dashboardRfidPruefAusgeblendet", "1"); } catch {} }
    setSichtbar(alte => {
      const neu = alte.filter(x => x !== id);
      localStorage.setItem("dashboardSichtbar", JSON.stringify(neu));
      return neu;
    });
  };
  const modulEinblenden = (id) => {
    if (id === "probetraining") { try { localStorage.removeItem("dashboardProbetrainingAusgeblendet"); } catch {} }
    if (id === "rfidPruefliste") { try { localStorage.removeItem("dashboardRfidPruefAusgeblendet"); } catch {} }
    setSichtbar(alte => {
      const neu = [...alte, id];
      localStorage.setItem("dashboardSichtbar", JSON.stringify(neu));
      return neu;
    });
  };

  const navigiere = (r) => {
    if (r === -1) { if (monat === 1) { setMonat(12); setJahr(j => j-1); } else setMonat(m => m-1); }
    else { if (monat === 12) { setMonat(1); setJahr(j => j+1); } else setMonat(m => m+1); }
  };

  const ladenAlles = () => {
    fetch(`${API}/dashboard/stats?jahr=${jahr}&monat=${monat}`).then(r => r.json()).then(setStats).catch(() => {});
    // Vormonat für Vergleich
    const vm = monat === 1 ? 12 : monat - 1;
    const vmJahr = monat === 1 ? jahr - 1 : jahr;
    fetch(`${API}/dashboard/stats?jahr=${vmJahr}&monat=${vm}`).then(r => r.json()).then(setStatsVormonat).catch(() => {});
    fetch(`${API}/aktivitaeten/?limit=50`).then(r => r.json()).then(setAktivitaeten).catch(() => {});
    fetch(`${API}/todos/`).then(r => r.json()).then(setTodos).catch(() => {});
    fetch(`${API}/news/`).then(r => r.json()).then(setNews).catch(() => {});
    // Geburtstage
    fetch(`${API}/mitglieder/`).then(r => r.json()).then(data => {
      const jetzt = new Date();
      const geburtstageMonate = data.filter(m => {
        if (!m.geburtsdatum) return false;
        const geb = new Date(m.geburtsdatum);
        return geb.getMonth() === jetzt.getMonth();
      }).sort((a,b) => {
        const da = new Date(a.geburtsdatum).getDate();
        const db = new Date(b.geburtsdatum).getDate();
        return da - db;
      }).slice(0, 10);
      setGeburtstage(geburtstageMonate);
    }).catch(() => {});
  };

  useEffect(() => {
    const letzterCheck = localStorage.getItem("letzterEhemaligCheck");
    const heuteStr = heute.toISOString().split("T")[0];
    if (letzterCheck !== heuteStr) {
      fetch(`${API}/statistiken/auto-ehemalig`, { method: "POST" }).then(r => r.json()).then(data => {
        if (data.anzahl > 0) setAutoMeldung(`✓ ${data.anzahl} Mitglied${data.anzahl > 1 ? "er wurden" : " wurde"} auf "ehemalig" gesetzt.`);
        localStorage.setItem("letzterEhemaligCheck", heuteStr);
      }).catch(() => {});
    }
  }, []);

  useEffect(() => { ladenAlles(); }, [monat, jahr]);

  useEffect(() => {
    fetch(`${API}/wetter/`).then(r => r.json()).then(setWetter).catch(() => {});
  }, []);

  const handleTodoHinzufuegen = async () => {
    if (!todoTitel) return;
    const res = await fetch(`${API}/todos/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ titel: todoTitel, beschreibung: todoBeschreibung, wiederholung: todoWiederholung, zugewiesen_an: todoZugewiesen }) });
    if (!res.ok) { alert("To-do konnte nicht gespeichert werden."); return; }
    setTodoTitel(""); setTodoBeschreibung(""); setTodoFormOffen(false);
    fetch(`${API}/todos/`).then(r => r.json()).then(setTodos);
  };

  const handleTodoErledigt = async (id, erledigt) => {
    const res = await fetch(`${API}/todos/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ erledigt: !erledigt }) });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); return; }
    fetch(`${API}/todos/`).then(r => r.json()).then(setTodos);
  };

  const handleTodoLoeschen = async (id) => {
    const res = await fetch(`${API}/todos/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    fetch(`${API}/todos/`).then(r => r.json()).then(setTodos);
  };

  const offeneTodos = todos.filter(t => !t.erledigt && (rolle === "admin" || t.zugewiesen_an === "alle" || t.zugewiesen_an === benutzer));
  const erledigteTodos = todos.filter(t => t.erledigt && (rolle === "admin" || t.zugewiesen_an === "alle" || t.zugewiesen_an === benutzer));
  const istAktuell = monat === heute.getMonth() + 1 && jahr === heute.getFullYear();

  const aktivIcon = (aktion) => {
    if (!aktion) return "📄";
    const a = aktion.toLowerCase();
    if (a.includes("pause")) return "⏸";
    if (a.includes("kündigung") || a.includes("gekündigt")) return "❌";
    if (a.includes("mitglied") || a.includes("angelegt")) return "👤";
    if (a.includes("dokument")) return "📄";
    if (a.includes("sepa") || a.includes("zahlung") || a.includes("lastschrift")) return "💳";
    if (a.includes("reaktiviert") || a.includes("aktiv")) return "✅";
    if (a.includes("partner")) return "🤝";
    if (a.includes("todo")) return "☑";
    return "📋";
  };

  const umsatzDiff = stats && statsVormonat && statsVormonat.umsatz_diesen > 0
    ? (((stats.umsatz_diesen - statsVormonat.umsatz_diesen) / statsVormonat.umsatz_diesen) * 100).toFixed(1)
    : null;

  // Aktivitäten nach Wochen filtern
  const jetzt = new Date();
  const aktivitaetenGefiltert = aktivitaeten.filter(a => {
    if (!a.erstellt_am) return false;
    const parts = a.erstellt_am.split(" ");
    const [tag, mon, j] = parts[0].split(".");
    const [h, m] = (parts[1] || "00:00").split(":");
    const dann = new Date(j, mon-1, tag, h, m);
    const diffTage = (jetzt - dann) / (1000 * 60 * 60 * 24);
    return diffTage <= aktivWochen * 7;
  });

  const modulInhalt = {
    stats: (
      <div>
        <div onClick={() => { const neu = !statsOffen; setStatsOffen(neu); modulAufklappen("stats", neu); }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: statsOffen ? 12 : 0 }}>
          <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl, display: "flex", alignItems: "center", gap: 8 }}><BarChart3 size={18} color={THEME.colors.primary} /> Statistik-Kacheln</h3>
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{statsOffen ? "▲" : "▼"}</span>
        </div>
        {statsOffen && (stats ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            {[
              { label: "Aktive Mitglieder", diesen: stats.aktiv_diesen, naechsten: stats.aktiv_naechsten, farbe: THEME.colors.success, Icon: Users },
              { label: "Pausierte Mitglieder", diesen: stats.pausiert_diesen, naechsten: stats.pausiert_naechsten, farbe: THEME.colors.warning, Icon: PauseCircle },
              { label: "Neuzugänge", diesen: stats.neuzugaenge_diesen, naechsten: stats.neuzugaenge_naechsten, farbe: THEME.colors.accentPurple, Icon: UserPlus },
              { label: "Kündigungen", diesen: stats.kuendigungen_diesen, naechsten: stats.kuendigungen_naechsten, farbe: THEME.colors.danger, Icon: UserX },
            ].map(k => (
              <div key={k.label} style={{ ...s.karte, borderLeftColor: k.farbe }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${k.farbe}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <k.Icon size={18} color={k.farbe} />
                  </div>
                  <p style={{ ...s.karteLabel, fontSize: THEME.fontSize.base, margin: 0 }}>{k.label}</p>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginTop: 8 }}>
                  <div><p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, margin: 0 }}>Dieser Monat</p><p style={{ color: k.farbe, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: 0 }}>{k.diesen}</p></div>
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>→</span>
                  <div><p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, margin: 0 }}>Nächster Monat</p><p style={{ color: k.farbe, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: 0 }}>{k.naechsten}</p></div>
                </div>
              </div>
            ))}
            <div style={{ ...s.karte, borderLeftColor: THEME.colors.primary }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#2b8cc422", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Euro size={18} color={THEME.colors.primary} />
                </div>
                <p style={{ ...s.karteLabel, fontSize: THEME.fontSize.base, margin: 0 }}>Umsatz</p>
              </div>
              <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "8px 0 0" }}>{(stats.umsatz_diesen || 0).toFixed(2)} €</p>
              {umsatzDiff !== null && (
                <p style={{ color: parseFloat(umsatzDiff) >= 0 ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: "4px 0 0", fontWeight: 600 }}>
                  {parseFloat(umsatzDiff) >= 0 ? "▲" : "▼"} {Math.abs(umsatzDiff)} % zum Vormonat
                </p>
              )}
            </div>
          </div>
        ) : <p style={{ color: THEME.colors.textMuted }}>Lade Statistik...</p>)}
      </div>
    ),

    kalender: <KalenderWidget initialOffen={aufgeklappt.kalender} onOffenChange={(offen) => modulAufklappen("kalender", offen)} />,
    studio: <ShellyWidget initialOffen={aufgeklappt.studio} onOffenChange={(offen) => modulAufklappen("studio", offen)} />,
    onlinePortal: <OnlinePortalWidget initialOffen={aufgeklappt.onlinePortal} onOffenChange={(offen) => modulAufklappen("onlinePortal", offen)} navigiere={seiteWechseln} rechte={rechte} />,

    brett: (() => {
      const aktuelleNews = news.filter(n => !n.abgelaufen);
      return (
        <div>
          <div onClick={() => { const neu = !brettOffen; setBrettOffen(neu); modulAufklappen("brett", neu); }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: brettOffen ? 12 : 0 }}>
            <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl, display: "flex", alignItems: "center", gap: 8 }}><Megaphone size={18} color={THEME.colors.warning} /> Schwarzes Brett ({aktuelleNews.length})</h3>
            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{brettOffen ? "▲" : "▼"}</span>
          </div>
          {brettOffen && (
            <div>
              {aktuelleNews.length > 0 ? aktuelleNews.slice(0, brettLimit).map(n => (
                <div key={n.id} style={{ padding: "10px 0", borderTop: "1px solid #2b3d52" }}>
                  <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.md, fontWeight: 600, margin: "0 0 4px" }}>{n.titel}</p>
                  <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "0 0 4px", whiteSpace: "pre-wrap" }}>{n.inhalt}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>{n.erstellt_von} · {n.erstellt_am}</p>
                </div>
              )) : <p style={{ color: THEME.colors.textMuted }}>Keine aktuellen Mitteilungen.</p>}
              {aktuelleNews.length > brettLimit && (
                <button onClick={() => setBrettLimit(l => l + 3)} style={{ ...s.btnGray, width: "auto", padding: "6px 14px", fontSize: THEME.fontSize.sm, marginTop: 8 }}>
                  + {Math.min(3, aktuelleNews.length - brettLimit)} mehr anzeigen
                </button>
              )}
            </div>
          )}
        </div>
      );
    })(),

    geburtstage: (
      <div>
        <div onClick={() => { const neu = !gebOffen; setGebOffen(neu); modulAufklappen("geburtstage", neu); }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: gebOffen ? 12 : 0 }}>
          <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl, display: "flex", alignItems: "center", gap: 8 }}><Cake size={18} color={THEME.colors.accentPink} /> Geburtstage diesen Monat ({geburtstage.length})</h3>
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{gebOffen ? "▲" : "▼"}</span>
        </div>
        {gebOffen && (
          <div>
            {geburtstage.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine Geburtstage diesen Monat.</p>}
            {geburtstage.map(m => {
              const geb = new Date(m.geburtsdatum);
              const alter = berechneAlter(m.geburtsdatum);
              const heute2 = new Date();
              const istHeute = geb.getDate() === heute2.getDate() && geb.getMonth() === heute2.getMonth();
              return (
                <div key={m.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
                  <span style={{ color: istHeute ? THEME.colors.warning : THEME.colors.text, fontSize: THEME.fontSize.md, fontWeight: istHeute ? 700 : 400 }}>
                    {istHeute ? "🎉 " : ""}{m.vorname} {m.nachname}
                  </span>
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{geb.getDate()}.{geb.getMonth()+1}. ({alter} J.)</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    ),

    todos: (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: todoOffen ? 12 : 0 }}>
          <div onClick={() => { const neu = !todoOffen; setTodoOffen(neu); modulAufklappen("todos", neu); }} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }}>
            <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl, display: "flex", alignItems: "center", gap: 8 }}><CheckSquare size={18} color={THEME.colors.success} /> To-dos ({offeneTodos.length})</h3>
            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{todoOffen ? "▲" : "▼"}</span>
          </div>
          <button onClick={() => { setTodoFormOffen(!todoFormOffen); if (!todoOffen) { setTodoOffen(true); modulAufklappen("todos", true); } }} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>+ Neu</button>
        </div>
        {todoOffen && (
        <>
        {todoFormOffen && (
          <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={s.inputGroup}><input style={s.input} placeholder="Titel *" value={todoTitel} onChange={e => setTodoTitel(e.target.value)} /></div>
            <div style={s.inputGroup}><textarea style={{ ...s.input, height: 60 }} placeholder="Beschreibung (optional)" value={todoBeschreibung} onChange={e => setTodoBeschreibung(e.target.value)} /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <select style={s.input} value={todoWiederholung} onChange={e => setTodoWiederholung(e.target.value)}>
                <option value="einmalig">Einmalig</option>
                <option value="taeglich">Täglich</option>
                <option value="woechentlich">Wöchentlich</option>
                <option value="monatlich">Monatlich</option>
              </select>
              <select style={s.input} value={todoZugewiesen} onChange={e => setTodoZugewiesen(e.target.value)}>
                <option value="alle">Alle</option>
                <option value="admin">Nur Admin</option>
                {rolle !== "admin" && benutzer && <option value={benutzer}>Nur ich ({benutzer})</option>}
              </select>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setTodoFormOffen(false)} style={{ ...s.btnGray, flex: 1, padding: "8px" }}>Abbrechen</button>
              <button onClick={handleTodoHinzufuegen} style={{ ...s.btn, flex: 1, padding: "8px" }}>Hinzufügen</button>
            </div>
          </div>
        )}
        {offeneTodos.length === 0 && !todoFormOffen && <p style={{ color: THEME.colors.textMuted }}>Keine offenen Aufgaben 🎉</p>}
        {offeneTodos.slice(0, todoLimit).map(t => (
          <div key={t.id} style={{ padding: "8px 0", borderTop: "1px solid #2b3d52" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600, margin: "0 0 2px" }}>
                  {t.titel}
                  {t.wiederholung !== "einmalig" && <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, marginLeft: 6 }}>({t.wiederholung})</span>}
                </p>
                {t.beschreibung && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>{t.beschreibung}</p>}
              </div>
              <div style={{ display: "flex", gap: 4, marginLeft: 8 }}>
                <button onClick={() => handleTodoErledigt(t.id, t.erledigt)} style={{ ...s.btn, width: "auto", background: THEME.colors.success, padding: "4px 8px", fontSize: THEME.fontSize.sm }}>✓</button>
                <button onClick={() => handleTodoLoeschen(t.id)} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg, padding: "4px 8px", fontSize: THEME.fontSize.sm }}>✗</button>
              </div>
            </div>
          </div>
        ))}
        {offeneTodos.length > todoLimit && (
          <button onClick={() => setTodoLimit(l => l + 5)} style={{ ...s.btnGray, width: "auto", padding: "6px 14px", fontSize: THEME.fontSize.sm, marginTop: 8 }}>
            + {Math.min(5, offeneTodos.length - todoLimit)} mehr anzeigen
          </button>
        )}
        {erledigteTodos.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #2b3d52" }}>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 8px" }}>Erledigt ({erledigteTodos.length})</p>
            {erledigteTodos.slice(0, 3).map(t => (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
                <span style={{ color: THEME.colors.textDisabled, fontSize: THEME.fontSize.sm, textDecoration: "line-through" }}>{t.titel}</span>
                <button onClick={() => handleTodoLoeschen(t.id)} style={{ ...s.btn, width: "auto", background: THEME.colors.surfaceMuted, padding: "2px 6px", fontSize: THEME.fontSize.xxs }}>✗</button>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    ),

    aktivitaet: (
      <div>
        <div onClick={() => { const neu = !aktivOffen; setAktivOffen(neu); modulAufklappen("aktivitaet", neu); }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: aktivOffen ? 12 : 0 }}>
          <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>Aktivitätsprotokoll</h3>
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{aktivOffen ? "▲" : "▼"}</span>
        </div>
        {aktivOffen && (
        <>
        {aktivitaetenGefiltert.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine Aktivitäten in diesem Zeitraum.</p>}
        {aktivitaetenGefiltert.map(a => (
          mobil ? (
            <div key={a.id} style={{ padding: "10px 0", borderTop: "1px solid #2b3d52" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ fontSize: THEME.fontSize.lg }}>{aktivIcon(a.aktion)}</span>
                <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{a.erstellt_am}</span>
                <span style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xs, fontWeight: 600 }}>{a.benutzer}</span>
              </div>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, wordBreak: "break-word" }}>
                {a.aktion}
                {a.mitglied_name && (a.mitglied_id && onPersonOeffnen
                  ? <span> – <span onClick={() => onPersonOeffnen("mitglied", a.mitglied_id)} title="Mitglied öffnen" style={{ color: THEME.colors.primary, cursor: "pointer", textDecoration: "underline" }}>{a.mitglied_name}</span></span>
                  : <span style={{ color: THEME.colors.textMuted }}> – {a.mitglied_name}</span>)}
                {a.details && <span style={{ color: THEME.colors.textMuted }}> ({a.details})</span>}
              </span>
            </div>
          ) : (
            <div key={a.id} style={{ display: "grid", gridTemplateColumns: "28px 150px 100px 1fr", gap: 10, padding: "10px 0", borderTop: "1px solid #2b3d52", alignItems: "center" }}>
              <span style={{ fontSize: THEME.fontSize.xl }}>{aktivIcon(a.aktion)}</span>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{a.erstellt_am}</span>
              <span style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.sm, fontWeight: 600 }}>{a.benutzer}</span>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base }}>
                {a.aktion}
                {a.mitglied_name && (a.mitglied_id && onPersonOeffnen
                  ? <span> – <span onClick={() => onPersonOeffnen("mitglied", a.mitglied_id)} title="Mitglied öffnen" style={{ color: THEME.colors.primary, cursor: "pointer", textDecoration: "underline" }}>{a.mitglied_name}</span></span>
                  : <span style={{ color: THEME.colors.textMuted }}> – {a.mitglied_name}</span>)}
                {a.details && <span style={{ color: THEME.colors.textMuted }}> ({a.details})</span>}
              </span>
            </div>
          )
        ))}
        <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Zeigt letzte {aktivWochen} Woche{aktivWochen > 1 ? "n" : ""}</span>
          <button onClick={() => setAktivWochen(w => w + 4)} style={{ ...s.btnGray, width: "auto", padding: "6px 14px", fontSize: THEME.fontSize.sm }}>+ 1 Monat mehr anzeigen</button>
        </div>
        </>
        )}
      </div>
    ),

    wetter: (
      <div>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Wetter</h3>
        {!wetter && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Lädt…</p>}
        {wetter && wetter.fehler && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{wetter.fehler}</p>}
        {wetter && !wetter.fehler && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ fontSize: 44, lineHeight: 1 }}>{wetter.icon}</span>
              <div>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxxl2, fontWeight: 700, margin: 0 }}>{Math.round(wetter.temperatur)}°C</p>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>{wetter.beschreibung}</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 20, marginTop: 14 }}>
              <div>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, margin: 0 }}>Niederschlag</p>
                <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 600, margin: 0 }}>{wetter.niederschlag_mm} mm</p>
              </div>
              {wetter.regenwahrscheinlichkeit !== null && (
                <div>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, margin: 0 }}>Regenwahrsch.</p>
                  <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 600, margin: 0 }}>{wetter.regenwahrscheinlichkeit}%</p>
                </div>
              )}
            </div>
            {wetter.pollen && (
              <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.xs, marginTop: 12 }}>
                🌾 Pollenflug erhöht: {wetter.pollen.staerkste_belastung}
              </p>
            )}
          </div>
        )}
      </div>
    ),

    checkin: <CheckInWidget navigiere={seiteWechseln} />,
    rfidSuche: <RfidSucheWidget benutzerId={benutzerId} onPersonOeffnen={onPersonOeffnen} benutzer={benutzer} />,
    ttlockStatus: <TTLockStatusWidget navigiere={seiteWechseln} rechte={rechte} />,
    ttlockUnzugeordnet: <TTLockUnzugeordneteKartenWidget benutzerId={benutzerId} benutzer={benutzer} />,
    probetraining: <ProbetrainingWidget rechte={rechte} benutzer={benutzer} onPersonOeffnen={onPersonOeffnen} initialOffen={aufgeklappt.probetraining} onOffenChange={(offen) => modulAufklappen("probetraining", offen)} />,
    rfidPruefliste: <RfidPruefHinweis seiteWechseln={seiteWechseln} onPersonOeffnen={onPersonOeffnen} initialOffen={aufgeklappt.rfidPruefliste} onOffenChange={(offen) => modulAufklappen("rfidPruefliste", offen)} />,
  };

  const ausgeblendet = moduleVerfuegbar.filter(id => !sichtbar.includes(id));

  return (
    <div>
      {autoMeldung && (
        <div style={{ background: "#22c55e22", border: "1px solid #22c55e", borderRadius: 8, padding: "10px 16px", marginBottom: 16, color: THEME.colors.success, fontSize: THEME.fontSize.md }}>
          {autoMeldung}<button onClick={() => setAutoMeldung("")} style={{ float: "right", background: "none", border: "none", color: THEME.colors.success, cursor: "pointer", fontSize: THEME.fontSize.xl }}>×</button>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h1 style={{ ...s.heading, margin: 0 }}>Willkommen, {benutzer}</h1>
          {wetter && !wetter.fehler && (
            <span style={{ display: "flex", alignItems: "center", gap: 6, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.lg }}>
              {Math.round(wetter.temperatur)}°C <span style={{ fontSize: THEME.fontSize.xl }}>{wetter.icon}</span>
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {(rolle === "admin" || rechte?.module?.kalender) && (
            <button
              onClick={() => { kalenderNeuerTerminBeimOeffnen = true; seiteWechseln("Kalender"); }}
              style={{ ...s.btnGray, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm, background: THEME.colors.primary }}
            >+ Termin</button>
          )}
          {rolle === "admin" && <TuerOeffnenKnopf rechte={rechte} />}
          <button
            onClick={async () => {
              try {
                localStorage.clear();
                sessionStorage.clear();
                if (window.caches) { const keys = await caches.keys(); await Promise.all(keys.map(k => caches.delete(k))); }
              } catch (e) { console.error("Cache leeren fehlgeschlagen:", e); }
              window.location.href = window.location.pathname + "?cache_bust=" + Date.now();
            }}
            title="Nur für die Entwicklungsphase – leert Browser-Cache/lokalen Speicher und lädt neu"
            style={{ ...s.btnGray, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm, background: THEME.colors.accentViolet }}
          >🗑 Cache leeren</button>
          {bearbeiten && (
            <div style={{ position: "relative" }}>
              <button onClick={() => setModulMenuOffen(!modulMenuOffen)} style={{ ...s.btnGray, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.sm }}>+ Modul {ausgeblendet.length > 0 && `(${ausgeblendet.length})`}</button>
              {modulMenuOffen && (
                <div style={{ position: "absolute", top: "110%", right: 0, background: THEME.colors.surface, border: "1px solid #2b3d52", borderRadius: 8, padding: 8, zIndex: 20, minWidth: 200 }}>
                  {ausgeblendet.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 4, padding: 4 }}>Alle Module sichtbar.</p>}
                  {ausgeblendet.map(id => (
                    <div key={id} onClick={() => { modulEinblenden(id); setModulMenuOffen(false); }} style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, padding: "8px 10px", borderRadius: 6, cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      + {DASHBOARD_MODULE_DEFS[id].titel}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 12, background: THEME.colors.surface, padding: "8px 16px", borderRadius: 10 }}>
            <button onClick={() => navigiere(-1)} style={{ background: "none", border: "none", color: THEME.colors.primary, fontSize: THEME.fontSize.xl2, cursor: "pointer" }}>◀</button>
            <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 600, minWidth: 160, textAlign: "center" }}>
              {MONATE[monat-1]} {jahr}
              {istAktuell && <span style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xs, marginLeft: 8 }}>● Aktuell</span>}
            </span>
            <button onClick={() => navigiere(1)} style={{ background: "none", border: "none", color: THEME.colors.primary, fontSize: THEME.fontSize.xl2, cursor: "pointer" }}>▶</button>
            {!istAktuell && <button onClick={() => { setMonat(heute.getMonth()+1); setJahr(heute.getFullYear()); }} style={{ ...s.btnGray, padding: "4px 10px", fontSize: THEME.fontSize.sm }}>Heute</button>}
          </div>
        </div>
      </div>

      <MilonSyncWarnung rolle={rolle} />

      {mobil ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {angezeigtesLayout.filter(l => sichtbar.includes(l.i)).map(l => (
            <WidgetRahmen key={l.i} titel={DASHBOARD_MODULE_DEFS[l.i].titel} onSchliessen={() => modulAusblenden(l.i)} bearbeiten={bearbeiten}
              autoHoehe={true} onHoeheGemessen={() => {}}>
              {modulInhalt[l.i]}
            </WidgetRahmen>
          ))}
        </div>
      ) : (
        <ResponsiveGridLayout
          className="layout"
          layout={angezeigtesLayout.filter(l => sichtbar.includes(l.i))}
          cols={12}
          rowHeight={30}
          margin={[20, 20]}
          draggableHandle=".dash-drag-handle"
          isDraggable={bearbeiten}
          isResizable={bearbeiten}
          onLayoutChange={handleLayoutChange}
        >
          {sichtbar.map(id => (
            <div key={id}>
              <WidgetRahmen titel={DASHBOARD_MODULE_DEFS[id].titel} onSchliessen={() => modulAusblenden(id)} bearbeiten={bearbeiten}
                autoHoehe={true} onHoeheGemessen={(px) => modulHoeheGemessen(id, px)}>
                {modulInhalt[id]}
              </WidgetRahmen>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 24 }}>
        {bearbeiten && (
          <button onClick={() => {
            if (!window.confirm("Dashboard-Layout wirklich auf Standard zurücksetzen?")) return;
            localStorage.removeItem("dashboardLayout");
            localStorage.removeItem("dashboardSichtbar");
            localStorage.removeItem("dashboardAufgeklappt");
            setLayout(moduleVerfuegbar.map(id => ({ i: id, ...DASHBOARD_MODULE_DEFS[id].layout })));
            setSichtbar(moduleVerfuegbar);
            setAufgeklappt({});
            setAutoHoehePx({});
            setStatsOffen(true);
            setGebOffen(false);
            setBrettOffen(false);
            setTodoOffen(false);
            setAktivOffen(false);
          }} style={{ ...s.btnGray, width: "auto", padding: "10px 24px" }}>↺ Zurücksetzen</button>
        )}
        {bearbeiten
          ? <button onClick={() => { setBearbeiten(false); setModulMenuOffen(false); }} style={{ ...s.btn, width: "auto", padding: "10px 24px", background: THEME.colors.success }}>💾 Dashboard speichern</button>
          : <button onClick={() => setBearbeiten(true)} style={{ ...s.btnGray, width: "auto", padding: "10px 24px" }}>🔧 Dashboard bearbeiten</button>}
      </div>
    </div>
  );
}


// Markdown-Schreibweise des E-Mail-Editors -> lesbarer Nur-Text (für den Outlook-mailto-Weg, der kein HTML kann)
function emailMarkdownZuText(t) {
  return (t || "").replace(/\r\n/g, "\n").split("\n").map(z => {
    const knopf = z.trim().match(/^\[\[([^|\]]+)\|(\S+?)\]\]$/);
    if (knopf) return `${knopf[1].trim()}: ${knopf[2]}`;
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(z)) return "------------------------------";
    const ueb = z.match(/^\s*(#{1,3})\s+(.+)$/);
    if (ueb) return ueb[1].length === 1 ? ueb[2].toUpperCase() : ueb[2];
    return z
      .replace(/\[([^\[\]]+)\]\(([^()\s]+)\)/g, (x, a, u) => (/^(tel:|mailto:)/i.test(u) ? a : `${a} (${u})`))
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/(^|[^*\w])\*(?![\s*])(.+?)(?<![\s*])\*(?![*\w])/g, "$1$2")
      .replace(/(^|[^\w_])_(?![\s_])(.+?)(?<![\s_])_(?![\w_])/g, "$1$2")
      .replace(/^\s*[*•]\s+/, "- ");
  }).join("\n");
}

// ============ E-MAIL SEITE ============
function MassenEmailTab() {
  const [betreff, setBetreff] = useState("");
  const [text, setText] = useState("");
  const [vorschautext, setVorschautext] = useState("");
  const [gruppe, setGruppe] = useState("aktiv");
  const [uebersicht, setUebersicht] = useState(null);
  const [vorlagen, setVorlagen] = useState([]);
  const textareaRef = useRef(null);

  const laden = () => {
    fetch(`${API}/massenemail/empfaenger?status=${gruppe}`).then(r => r.json()).then(setUebersicht).catch(() => setUebersicht(null));
  };
  useEffect(laden, [gruppe]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetch(`${API}/massenemail/vorlagen`).then(r => r.json()).then(d => setVorlagen(Array.isArray(d) ? d : [])).catch(() => setVorlagen([]));
  }, []);

  const anzahlMitEmails = uebersicht?.mailbar ?? 0;
  const empfaengerListe = uebersicht?.empfaenger ?? [];

  // Outlook-Weg (mailto): kann kein HTML, daher Markdown-Zeichen vorher in sauberen Text umwandeln
  const handleSenden = () => {
    if (!betreff || !text) { alert("Bitte Betreff und Text eingeben!"); return; }
    const emails = empfaengerListe.map(m => m.email).join(";");
    if (!emails) { alert("Keine E-Mail-Adressen gefunden!"); return; }
    const mailto = `mailto:?bcc=${encodeURIComponent(emails)}&subject=${encodeURIComponent(betreff)}&body=${encodeURIComponent(emailMarkdownZuText(text))}`;
    window.open(mailto, "_blank");
  };

  // ---- Editor-Werkzeugleiste ----
  const ersetzeAuswahl = (fn) => {
    const feld = textareaRef.current;
    const start = feld ? feld.selectionStart : text.length;
    const ende = feld ? feld.selectionEnd : text.length;
    const auswahl = text.slice(start, ende);
    const { neu, cursorStart, cursorEnde } = fn(auswahl);
    setText(text.slice(0, start) + neu + text.slice(ende));
    requestAnimationFrame(() => {
      if (feld) { feld.focus(); feld.selectionStart = start + cursorStart; feld.selectionEnd = start + cursorEnde; }
    });
  };
  const umschliessen = (vor, nach, beispiel) => ersetzeAuswahl(a => {
    const inhalt = a || beispiel;
    return { neu: vor + inhalt + nach, cursorStart: vor.length, cursorEnde: vor.length + inhalt.length };
  });
  const zeilenAnfang = (praefix, beispiel) => ersetzeAuswahl(a => {
    const inhalt = a || beispiel;
    const neu = inhalt.split("\n").map(z => praefix + z).join("\n");
    return { neu: "\n" + neu + "\n", cursorStart: 1 + praefix.length, cursorEnde: 1 + neu.length };
  });
  const platzhalterEinfuegen = (platzhalter) => ersetzeAuswahl(() => ({ neu: platzhalter, cursorStart: platzhalter.length, cursorEnde: platzhalter.length }));
  const linkEinfuegen = () => {
    const url = window.prompt("Link-Adresse (z.B. https://www.comefit.de/termin-buchen oder tel:075716871959):", "https://");
    if (!url || url === "https://") return;
    ersetzeAuswahl(a => { const neu = `[${a || "Hier klicken"}](${url.trim()})`; return { neu, cursorStart: 1, cursorEnde: neu.indexOf("]") }; });
  };
  const knopfEinfuegen = () => {
    const beschriftung = window.prompt("Beschriftung des Buttons (z.B. Termin buchen):", "Jetzt buchen");
    if (!beschriftung) return;
    const url = window.prompt("Wohin soll der Button führen? (https://…)", "https://");
    if (!url || url === "https://") return;
    ersetzeAuswahl(() => { const neu = `\n\n[[${beschriftung.trim()}|${url.trim()}]]\n\n`; return { neu, cursorStart: neu.length, cursorEnde: neu.length }; });
  };
  const vorlageLaden = (name) => {
    const v = vorlagen.find(x => x.name === name);
    if (!v) return;
    if ((betreff || text) && !window.confirm("Aktuellen Betreff und Text durch die Vorlage ersetzen?")) return;
    setBetreff(v.betreff); setText(v.text); setVorschautext(v.vorschautext || "");
  };

  // ---- Live-Vorschau (so sieht die Mail im Postfach aus) ----
  const [live, setLive] = useState({ html: "", nur_text: "", beispiel_name: "" });
  const [nurTextAnsicht, setNurTextAnsicht] = useState(false);
  const [handyAnsicht, setHandyAnsicht] = useState(false);
  useEffect(() => {
    if (!betreff.trim() && !text.trim()) { setLive({ html: "", nur_text: "", beispiel_name: "" }); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/massenemail/html-vorschau`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: gruppe, betreff, text, vorschautext }) });
        if (res.ok) setLive(await res.json());
      } catch (e) { /* Vorschau ist nur Komfort */ }
    }, 700);
    return () => clearTimeout(timer);
  }, [betreff, text, vorschautext, gruppe]);

  // ---- Personalisierter Echtversand (SMTP, HTML, ein Vorname pro Empfänger) ----
  const [vorschau, setVorschau] = useState(null);
  const [vorschauLaedt, setVorschauLaedt] = useState(false);
  const [vorschauFehler, setVorschauFehler] = useState("");
  const handleVorschau = async () => {
    if (!betreff || !text) { alert("Bitte Betreff und Text eingeben!"); return; }
    setVorschauLaedt(true); setVorschauFehler(""); setVorschau(null);
    try {
      const res = await fetch(`${API}/massenemail/vorschau`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: gruppe, betreff, text, vorschautext }) });
      const d = await res.json();
      if (!res.ok) { setVorschauFehler(d.detail || "Vorschau fehlgeschlagen."); return; }
      setVorschau(d);
    } finally { setVorschauLaedt(false); }
  };
  useEffect(() => { setVorschau(null); }, [betreff, text, vorschautext, gruppe]);

  const [testEmail, setTestEmail] = useState("");
  const [testLaedt, setTestLaedt] = useState(false);
  const [testMeldung, setTestMeldung] = useState("");
  const handleTest = async () => {
    if (!testEmail.trim()) { alert("Bitte deine eigene E-Mail-Adresse für die Testmail eingeben."); return; }
    if (!betreff || !text) { alert("Bitte Betreff und Text eingeben!"); return; }
    setTestLaedt(true); setTestMeldung("");
    try {
      const res = await fetch(`${API}/massenemail/test`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ziel_email: testEmail.trim(), betreff, text, vorschautext }) });
      const d = await res.json();
      if (!res.ok) { setTestMeldung("❌ " + (d.detail || "Testmail fehlgeschlagen.")); return; }
      setTestMeldung(`✓ Testmail an ${testEmail.trim()} gesendet (mit „${d.an_beispielname || "?"}" als Beispielname). Schau sie dir in Outlook UND auf dem Handy an.`);
    } finally { setTestLaedt(false); }
  };

  const [versendetLaedt, setVersendetLaedt] = useState(false);
  const [versendetErgebnis, setVersendetErgebnis] = useState(null);
  const handleEchtVersenden = async () => {
    if (!vorschau) { alert("Bitte zuerst „Empfänger prüfen” nutzen, damit klar ist, was verschickt wird."); return; }
    if (!window.confirm(`Wirklich JETZT ${vorschau.anzahl_empfaenger} einzelne, echte E-Mails versenden? Das kann nicht rückgängig gemacht werden.`)) return;
    setVersendetLaedt(true); setVersendetErgebnis(null);
    try {
      const res = await fetch(`${API}/massenemail/versenden`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: gruppe, betreff, text, vorschautext, bestaetigung: true }) });
      const d = await res.json();
      if (!res.ok) { alert(d.detail || "Versand fehlgeschlagen."); return; }
      setVersendetErgebnis(d);
    } finally { setVersendetLaedt(false); }
  };

  const werkzeug = { ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "5px 11px", minWidth: 34 };

  return (
    <div style={{ ...s.karte }}>
      <div style={s.inputGroup}>
        <label style={s.label}>Empfänger</label>
        <select style={s.input} value={gruppe} onChange={e => setGruppe(e.target.value)}>
          <option value="aktiv">Alle aktiven Mitglieder</option>
          <option value="pausiert">Alle pausierten Mitglieder</option>
          <option value="gekündigt">Alle gekündigten Mitglieder</option>
          <option value="ehemalig">Alle ehemaligen Mitglieder</option>
          <option value="">Alle Mitglieder</option>
        </select>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 6 }}>
          {uebersicht?.gesamt ?? "…"} Mitglieder gefunden – davon {anzahlMitEmails} mit E-Mail-Adresse
          {uebersicht?.ausgeschlossen_bereits_aktiv > 0 && (
            <> ({uebersicht.ausgeschlossen_bereits_aktiv} weitere bereits ausgeschlossen, weil sie inzwischen unter demselben Namen aktiv bei Wellpass/Hansefit/Sonstige sind)</>
          )}
          {uebersicht?.ausgeschlossen_abgemeldet > 0 && (
            <> ({uebersicht.ausgeschlossen_abgemeldet} haben sich von Werbe-E-Mails abgemeldet und werden ausgelassen)</>
          )}
        </p>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Vorlage:</span>
        {vorlagen.length === 0 && <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>keine vorhanden</span>}
        {vorlagen.map(v => (
          <button key={v.name} type="button" onClick={() => vorlageLaden(v.name)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>📄 {v.name}</button>
        ))}
      </div>

      <div style={s.inputGroup}>
        <label style={s.label}>Betreff *</label>
        <input style={s.input} value={betreff} onChange={e => setBetreff(e.target.value)} placeholder="z.B. Wichtige Information von ComeFit" />
      </div>
      <div style={s.inputGroup}>
        <label style={s.label}>Vorschautext (optional – die graue Zeile hinter dem Betreff im Postfach)</label>
        <input style={s.input} value={vorschautext} onChange={e => setVorschautext(e.target.value)} maxLength={140} placeholder="z.B. 0 € Monatsbeitrag bis 31.12.2026" />
      </div>
      <div style={s.inputGroup}>
        <label style={s.label}>Text *</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          <button type="button" title="Fett" onClick={() => umschliessen("**", "**", "fett")} style={{ ...werkzeug, fontWeight: 900 }}>B</button>
          <button type="button" title="Kursiv" onClick={() => umschliessen("*", "*", "kursiv")} style={{ ...werkzeug, fontStyle: "italic" }}>I</button>
          <button type="button" title="Große Überschrift" onClick={() => zeilenAnfang("# ", "Überschrift")} style={werkzeug}>H1</button>
          <button type="button" title="Überschrift" onClick={() => zeilenAnfang("## ", "Überschrift")} style={werkzeug}>H2</button>
          <button type="button" title="Aufzählung" onClick={() => zeilenAnfang("- ", "Punkt")} style={werkzeug}>• Liste</button>
          <button type="button" title="Link einfügen" onClick={linkEinfuegen} style={werkzeug}>🔗 Link</button>
          <button type="button" title="Orangefarbenen Button einfügen" onClick={knopfEinfuegen} style={{ ...werkzeug, background: "#DF8B30", color: "#1E1712", fontWeight: 700 }}>🔘 Button</button>
          <button type="button" title="Trennlinie" onClick={() => ersetzeAuswahl(() => ({ neu: "\n\n---\n\n", cursorStart: 7, cursorEnde: 7 }))} style={werkzeug}>― Linie</button>
        </div>
        <textarea ref={textareaRef} style={{ ...s.input, height: 320, fontFamily: "ui-monospace, Consolas, monospace", fontSize: 14 }} value={text} onChange={e => setText(e.target.value)} placeholder="Deine Nachricht an die Mitglieder…" />
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Platzhalter einfügen (beim personalisierten Versand wirksam):</span>
          {["{Vorname}", "{Nachname}", "{Anrede}"].map(p => (
            <button key={p} type="button" onClick={() => platzhalterEinfuegen(p)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "4px 10px" }}>{p}</button>
          ))}
        </div>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "8px 0 0", lineHeight: 1.6 }}>
          Schreibweise: <code>**fett**</code> · <code>*kursiv*</code> · <code># Überschrift</code> / <code>## Unterüberschrift</code> · <code>- Aufzählung</code> · <code>[Linktext](https://…)</code> · <code>[[Button-Text|https://…]]</code> (eigene Zeile) · <code>---</code> Trennlinie · Leerzeile = neuer Absatz
        </p>
      </div>

      {/* Live-Vorschau */}
      <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <strong style={{ color: THEME.colors.text }}>👁 Vorschau{live.beispiel_name ? ` (Beispiel: ${live.beispiel_name})` : ""}</strong>
          <button type="button" onClick={() => setNurTextAnsicht(false)} style={{ ...werkzeug, opacity: nurTextAnsicht ? 0.6 : 1 }}>E-Mail</button>
          <button type="button" onClick={() => setNurTextAnsicht(true)} style={{ ...werkzeug, opacity: nurTextAnsicht ? 1 : 0.6 }}>Nur-Text-Fassung</button>
          {!nurTextAnsicht && (
            <label style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={handyAnsicht} onChange={e => setHandyAnsicht(e.target.checked)} /> Handy-Breite
            </label>
          )}
        </div>
        {!live.html ? (
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>Sobald du Betreff und Text eingibst, erscheint hier die Vorschau.</p>
        ) : nurTextAnsicht ? (
          <pre style={{ whiteSpace: "pre-wrap", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: 0, fontFamily: "inherit" }}>{live.nur_text}</pre>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", background: "#d9d9d9", padding: 8, borderRadius: 6 }}>
            <iframe title="E-Mail-Vorschau" sandbox="" srcDoc={live.html}
              style={{ width: handyAnsicht ? 375 : "100%", maxWidth: "100%", height: 720, border: 0, background: "#f1f1f1" }} />
          </div>
        )}
        {live.betreff && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "10px 0 0" }}>Betreff: <strong style={{ color: THEME.colors.text }}>{live.betreff}</strong></p>}
      </div>

      <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 16, borderLeft: "4px solid #2b8cc4" }}>
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: 0 }}>
          ℹ Outlook öffnet sich mit allen Empfängern im BCC-Feld – EINE Mail an alle als <strong>reiner Text</strong> (Fett, Buttons und Design gibt es dort nicht), Platzhalter werden hier NICHT ersetzt.<br />
          Du kannst die Mail noch anpassen bevor du sie sendest. Für die schöne HTML-Mail nutze den Einzelversand unten.
        </p>
      </div>
      <button onClick={handleSenden} style={{ ...s.btn, width: "auto", marginBottom: 28 }}>
        📧 Outlook öffnen ({anzahlMitEmails} Empfänger, ohne Personalisierung)
      </button>

      <div style={{ borderTop: `1px solid ${THEME.colors.border}`, paddingTop: 20 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 8px", fontSize: THEME.fontSize.lg }}>Oder: einzeln versenden – als schöne HTML-Mail, mit echtem Vornamen</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 14px" }}>
          Verschickt {anzahlMitEmails} einzelne E-Mails über den ComeFit-Mailserver im ComeFit-Design (mit Nur-Text-Fassung für alte Mailprogramme). {"{Vorname}"} usw. werden je Empfänger ersetzt, jede Mail enthält einen Abmeldelink – wer sich abmeldet, wird künftig automatisch ausgelassen. Nicht rückgängig zu machen – vorher unbedingt Vorschau und Testmail prüfen.
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <button onClick={handleVorschau} disabled={vorschauLaedt} style={{ ...s.btnGray, width: "auto" }}>{vorschauLaedt ? "Lädt…" : "✔ Empfänger prüfen"}</button>
        </div>
        {vorschauFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base }}>{vorschauFehler}</p>}
        {vorschau && (
          <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: 0 }}>
              <strong>{vorschau.anzahl_empfaenger}</strong> Empfänger. Beispiel: <strong>{vorschau.beispiel_name}</strong> – Betreff „{vorschau.beispiel_betreff}“.
            </p>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <input style={{ ...s.input, width: "auto", flex: "1 1 260px" }} type="email" placeholder="deine-email@comefit.de (für die Testmail)" value={testEmail} onChange={e => setTestEmail(e.target.value)} />
          <button onClick={handleTest} disabled={testLaedt} style={{ ...s.btnGray, width: "auto" }}>{testLaedt ? "Sendet…" : "✉ Testmail an mich"}</button>
        </div>
        {testMeldung && <p style={{ color: testMeldung.startsWith("✓") ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.base }}>{testMeldung}</p>}

        <button onClick={handleEchtVersenden} disabled={versendetLaedt || !vorschau} style={{ ...s.btn, width: "auto", background: THEME.colors.danger, opacity: (versendetLaedt || !vorschau) ? 0.6 : 1 }}>
          {versendetLaedt ? "Wird versendet…" : `🚀 Jetzt an ${vorschau?.anzahl_empfaenger ?? anzahlMitEmails} Empfänger einzeln versenden`}
        </button>
        {versendetErgebnis && (
          <div style={{ marginTop: 12 }}>
            <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.base }}>✓ {versendetErgebnis.gesendet} E-Mails versendet (im Aktivitätsprotokoll vermerkt).</p>
            {versendetErgebnis.fehlgeschlagen?.length > 0 && (
              <div>
                <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base }}>{versendetErgebnis.fehlgeschlagen.length} fehlgeschlagen:</p>
                {versendetErgebnis.fehlgeschlagen.map(f => <p key={f.id} style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "2px 0" }}>{f.name}: {f.fehler}</p>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PosteingangTab() {
  const LIMIT = 50;
  const [nachrichten, setNachrichten] = useState([]);
  const [gesamt, setGesamt] = useState(0);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [ausgewaehlt, setAusgewaehlt] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLaedt, setDetailLaedt] = useState(false);
  const [antwortOffen, setAntwortOffen] = useState(false);
  const [antwortText, setAntwortText] = useState("");
  const [antwortSendet, setAntwortSendet] = useState(false);

  const laden = (offset) => {
    setLaedt(true);
    setFehler(null);
    fetch(`${API}/email-posteingang/nachrichten?limit=${LIMIT}&offset=${offset}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setNachrichten(prev => offset === 0 ? d.nachrichten : [...prev, ...d.nachrichten]);
        setGesamt(d.gesamt);
      })
      .catch(() => setFehler("Posteingang konnte nicht geladen werden. Ist IMAP eingerichtet?"))
      .finally(() => setLaedt(false));
  };

  useEffect(() => { laden(0); }, []);

  const oeffnen = (uid) => {
    setAusgewaehlt(uid);
    setDetail(null);
    setAntwortOffen(false);
    setAntwortText("");
    setDetailLaedt(true);
    fetch(`${API}/email-posteingang/nachrichten/${uid}`)
      .then(r => r.json())
      .then(d => {
        setDetail(d);
        setNachrichten(prev => prev.map(n => n.uid === uid ? { ...n, gelesen: true } : n));
      })
      .finally(() => setDetailLaedt(false));
  };

  const antwortSenden = async () => {
    if (!antwortText.trim()) return;
    setAntwortSendet(true);
    try {
      const res = await fetch(`${API}/email-posteingang/nachrichten/${ausgewaehlt}/antworten`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: antwortText }),
      });
      if (!res.ok) throw new Error();
      alert("Antwort gesendet.");
      setAntwortOffen(false);
      setAntwortText("");
    } catch {
      alert("Antwort konnte nicht gesendet werden.");
    } finally {
      setAntwortSendet(false);
    }
  };

  return (
    <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div style={{ ...s.karte, flex: "1 1 380px", minWidth: 320, maxHeight: "70vh", overflowY: "auto" }}>
        {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{fehler}</p>}
        {!fehler && nachrichten.length === 0 && !laedt && <p style={{ color: THEME.colors.textMuted }}>Posteingang ist leer.</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {nachrichten.map(n => (
            <div key={n.uid} onClick={() => oeffnen(n.uid)}
              style={{
                cursor: "pointer", padding: "10px 12px", borderRadius: 8,
                background: ausgewaehlt === n.uid ? THEME.colors.surfaceMuted : "transparent",
                borderLeft: `3px solid ${n.gelesen ? "transparent" : THEME.colors.success}`,
              }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ color: THEME.colors.text, fontWeight: n.gelesen ? 400 : 700, fontSize: THEME.fontSize.sm, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.von}</span>
                <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, flexShrink: 0 }}>{n.datum ? n.datum.slice(0, 16) : ""}</span>
              </div>
              <div style={{ color: n.gelesen ? THEME.colors.textSecondary : THEME.colors.text, fontWeight: n.gelesen ? 400 : 600, fontSize: THEME.fontSize.sm, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.betreff}</div>
            </div>
          ))}
        </div>
        {nachrichten.length < gesamt && (
          <button onClick={() => laden(nachrichten.length)} disabled={laedt} style={{ ...s.btnGray, width: "100%", marginTop: 12, fontSize: THEME.fontSize.sm }}>
            {laedt ? "Lädt…" : `Mehr laden (${nachrichten.length} von ${gesamt})`}
          </button>
        )}
      </div>

      <div style={{ ...s.karte, flex: "2 1 480px", minWidth: 320, maxHeight: "70vh", overflowY: "auto" }}>
        {!ausgewaehlt && <p style={{ color: THEME.colors.textMuted }}>Wähle links eine Nachricht aus.</p>}
        {ausgewaehlt && detailLaedt && <p style={{ color: THEME.colors.textMuted }}>Lädt…</p>}
        {detail && !detailLaedt && (
          <div>
            <h3 style={{ color: THEME.colors.text, margin: "0 0 4px", fontSize: THEME.fontSize.xl }}>{detail.betreff}</h3>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 16px" }}>
              Von: {detail.von}<br />
              An: {detail.an}<br />
              {detail.datum}
            </p>
            <div style={{ whiteSpace: "pre-wrap", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, lineHeight: 1.6, marginBottom: 16 }}>{detail.text}</div>
            {detail.anhaenge.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ color: THEME.colors.text, fontWeight: 600, fontSize: THEME.fontSize.sm, marginBottom: 6 }}>Anhänge</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {detail.anhaenge.map(a => (
                    <a key={a.index} href={`${API}/email-posteingang/nachrichten/${detail.uid}/anhang/${a.index}`} target="_blank" rel="noreferrer"
                      style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.sm }}>
                      📎 {a.dateiname} ({Math.round(a.groesse / 1024)} KB)
                    </a>
                  ))}
                </div>
              </div>
            )}

            {!antwortOffen ? (
              <button onClick={() => setAntwortOffen(true)} style={{ ...s.btn, width: "auto" }}>↩ Antworten</button>
            ) : (
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14 }}>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 8 }}>
                  An: {detail.von}<br />Betreff: {detail.betreff.toLowerCase().startsWith("re:") ? detail.betreff : `Re: ${detail.betreff}`}
                </p>
                <textarea style={{ ...s.input, height: 160 }} value={antwortText} onChange={e => setAntwortText(e.target.value)} placeholder="Deine Antwort…" />
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button onClick={antwortSenden} disabled={antwortSendet} style={{ ...s.btn, width: "auto" }}>{antwortSendet ? "Sendet…" : "Senden"}</button>
                  <button onClick={() => setAntwortOffen(false)} style={s.btnGray}>Abbrechen</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EMailSeite() {
  const [subTab, setSubTab] = useState("massen");
  return (
    <div style={{ maxWidth: 1100 }}>
      <h1 style={s.heading}>E-Mails</h1>
      <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
        {[["massen", "Massen-E-Mail"], ["posteingang", "Posteingang"]].map(([key, label]) => (
          <button key={key} onClick={() => setSubTab(key)}
            style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: subTab === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: subTab === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md }}>{label}</button>
        ))}
      </div>
      {subTab === "massen" && <MassenEmailTab />}
      {subTab === "posteingang" && <PosteingangTab />}
    </div>
  );
}





function MitgliedKurseTab({ mitgliedId }) {
  const [kurse, setKurse] = useState([]);
  useEffect(() => {
    fetch(`${API}/kurse/mitglied/${mitgliedId}`).then(r => r.json()).then(d => setKurse(Array.isArray(d) ? d : [])).catch(() => {});
  }, [mitgliedId]);

  const handleEntfernen = async (teilnehmerId) => {
    if (!window.confirm("Aus Kurs entfernen?")) return;
    await fetch(`${API}/kurse/teilnehmer/${teilnehmerId}`, { method: "DELETE" });
    fetch(`${API}/kurse/mitglied/${mitgliedId}`).then(r => r.json()).then(d => setKurse(Array.isArray(d) ? d : []));
  };

  return (
    <div>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 16px" }}>🏃 Kurse</h3>
      {kurse.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Nimmt an keinen Kursen teil.</p>}
      {kurse.map(k => (
        <div key={k.kurs_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "1px solid #2b3d52" }}>
          <div>
            <p style={{ color: THEME.colors.text, fontWeight: 600, margin: "0 0 4px" }}>{k.kurs_name}</p>
            {k.trainer && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 2px" }}>👤 {k.trainer}</p>}
            {k.wochentag && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>📅 {k.wochentag}{k.uhrzeit ? ` · ${k.uhrzeit} Uhr` : ""}</p>}
          </div>
          <button onClick={() => handleEntfernen(k.teilnehmer_id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px", background: THEME.colors.dangerBg }}>Aus Kurs entfernen</button>
        </div>
      ))}
    </div>
  );
}

// ============ KURS-QR-CODE-DIALOG (öffentliche Registrierungsseite zum Ausdrucken/Aufhängen) ============
function KursQrDialog({ kurs, onClose }) {
  const [kopiert, setKopiert] = useState(false);
  const link = kurs.oeffentlicher_link;

  const handleLinkKopieren = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch {
      window.prompt("Link kopieren:", link);
    }
  };

  return (
    <div style={s.modalBg} onClick={onClose}>
      <div style={{ ...s.modal, width: "min(420px, 95vw)", textAlign: "center" }} onClick={e => e.stopPropagation()}>
        <h2 style={s.modalTitel}>{kurs.name} – QR-Code</h2>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 16px" }}>QR-Code für:<br />{link}</p>
        <img src={`${API}/kurse/${kurs.id}/qr.png`} alt={`QR-Code ${kurs.name}`} style={{ width: 260, height: 260, background: "#fff", borderRadius: 8, padding: 10 }} />
        <div style={{ marginTop: 16, textAlign: "left" }}>
          <label style={s.label}>Öffentlicher Link</label>
          <input style={{ ...s.input, fontSize: THEME.fontSize.sm }} value={link} readOnly onFocus={e => e.target.select()} />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20, flexWrap: "wrap" }}>
          <a href={`${API}/kurse/${kurs.id}/qr.png`} download={`comefit-kurs-${kurs.slug}-qr.png`} style={{ ...s.btnGray, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>QR-Code herunterladen</a>
          <button onClick={handleLinkKopieren} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>{kopiert ? "✓ Kopiert!" : "Link kopieren"}</button>
        </div>
        <button onClick={onClose} style={{ ...s.btnGray, marginTop: 20 }}>Schließen</button>
      </div>
    </div>
  );
}

// ============ KURSVERWALTUNG ============
function KursVerwaltung({ navigiere }) {
  const [kurse, setKurse] = useState([]);
  const [gewaehlterKurs, setGewaehlterKurs] = useState(null);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [kursFormOffen, setKursFormOffen] = useState(false);
  const [bearbeitenKurs, setBearbeitenKurs] = useState(null);
  const [kursForm, setKursForm] = useState({ name: "", trainer: "", beschreibung: "", wochentag: "", uhrzeit: "", raum: "", max_teilnehmer: "" });
  const [mitgliedSuche, setMitgliedSuche] = useState("");
  const [mitgliedErgebnisse, setMitgliedErgebnisse] = useState([]);
  // "Externer Teilnehmer" -> "Teilnehmer manuell hinzufügen": Kursteilnehmer werden nicht mehr
  // sichtbar in Mitglied/Extern unterschieden (Nutzer-Wunsch 2026-08-19, QR-Selbstregistrierung) -
  // Vorname/Nachname getrennt statt einem Freitext-"Name"-Feld, passend zur öffentlichen Seite.
  const [manuellForm, setManuellForm] = useState({ vorname: "", nachname: "", email: "", telefon: "" });
  const [manuellOffen, setManuellOffen] = useState(false);
  const [bearbeitenTeilnehmerId, setBearbeitenTeilnehmerId] = useState(null);
  const [bearbeitenTeilnehmerForm, setBearbeitenTeilnehmerForm] = useState({ vorname: "", nachname: "", email: "", telefon: "" });
  const [qrKurs, setQrKurs] = useState(null);
  const [emailAnAlleOffen, setEmailAnAlleOffen] = useState(false);
  const [emailEmpfaenger, setEmailEmpfaenger] = useState([]);
  const [emailBetreff, setEmailBetreff] = useState("");
  const [emailText, setEmailText] = useState("");

  const WOCHENTAGE = ["Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag","Sonntag"];

  const ladenKurse = () => fetch(`${API}/kurse/`).then(r => r.json()).then(setKurse).catch(() => {});
  const ladenTeilnehmer = (id) => fetch(`${API}/kurse/${id}/teilnehmer`).then(r => r.json()).then(setTeilnehmer).catch(() => {});

  useEffect(() => { ladenKurse(); }, []);
  useEffect(() => { if (gewaehlterKurs) ladenTeilnehmer(gewaehlterKurs.id); }, [gewaehlterKurs]);

  const handleKursSpeichern = async () => {
    if (!kursForm.name) { alert("Bitte Kursname eingeben!"); return; }
    const url = bearbeitenKurs ? `${API}/kurse/${bearbeitenKurs.id}` : `${API}/kurse/`;
    const method = bearbeitenKurs ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(kursForm) });
    if (!res.ok) { alert("Kurs konnte nicht gespeichert werden."); return; }
    setKursFormOffen(false); setBearbeitenKurs(null);
    setKursForm({ name: "", trainer: "", beschreibung: "", wochentag: "", uhrzeit: "", raum: "", max_teilnehmer: "" });
    ladenKurse();
  };

  const handleKursBearbeiten = (k) => {
    setBearbeitenKurs(k);
    setKursForm({ name: k.name, trainer: k.trainer || "", beschreibung: k.beschreibung || "", wochentag: k.wochentag || "", uhrzeit: k.uhrzeit || "", raum: k.raum || "", max_teilnehmer: k.max_teilnehmer || "" });
    setKursFormOffen(true);
  };

  const handleKursLoeschen = async (id) => {
    if (!window.confirm("Kurs wirklich löschen?")) return;
    const res = await fetch(`${API}/kurse/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    if (gewaehlterKurs?.id === id) setGewaehlterKurs(null);
    ladenKurse();
  };

  const handleMitgliedSuche = async (q) => {
    setMitgliedSuche(q);
    if (q.length < 2) { setMitgliedErgebnisse([]); return; }
    const res = await fetch(`${API}/mitglieder/?suche=${encodeURIComponent(q)}&status=aktiv`);
    const data = await res.json();
    setMitgliedErgebnisse(data.slice(0, 8));
  };

  const handleMitgliedHinzufuegen = async (m) => {
    try {
      const res = await fetch(`${API}/kurse/teilnehmer`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kurs_id: gewaehlterKurs.id, mitglied_id: m.id, name: `${m.vorname} ${m.nachname}`, email: m.email || "", telefon: m.telefon || "", extern: false })
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Fehler beim Hinzufügen"); return; }
      setMitgliedSuche(""); setMitgliedErgebnisse([]);
      ladenTeilnehmer(gewaehlterKurs.id); ladenKurse();
    } catch { alert("Fehler beim Hinzufügen"); }
  };

  const handleManuellHinzufuegen = async () => {
    if (!manuellForm.vorname.trim() || !manuellForm.nachname.trim()) { alert("Bitte Vorname und Nachname eingeben!"); return; }
    const res = await fetch(`${API}/kurse/teilnehmer`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        kurs_id: gewaehlterKurs.id,
        name: `${manuellForm.vorname.trim()} ${manuellForm.nachname.trim()}`,
        vorname: manuellForm.vorname.trim(), nachname: manuellForm.nachname.trim(),
        email: manuellForm.email, telefon: manuellForm.telefon, extern: true,
      })
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Teilnehmer konnte nicht hinzugefügt werden."); return; }
    setManuellForm({ vorname: "", nachname: "", email: "", telefon: "" }); setManuellOffen(false);
    ladenTeilnehmer(gewaehlterKurs.id); ladenKurse();
  };

  const handleTeilnehmerEntfernen = async (id) => {
    if (!window.confirm("Teilnehmer aus Kurs entfernen?")) return;
    const res = await fetch(`${API}/kurse/teilnehmer/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Entfernen fehlgeschlagen."); return; }
    ladenTeilnehmer(gewaehlterKurs.id); ladenKurse();
  };

  const handleTeilnehmerBearbeitenStart = (t) => {
    setBearbeitenTeilnehmerId(t.id);
    setBearbeitenTeilnehmerForm({ vorname: t.vorname || "", nachname: t.nachname || "", email: t.email || "", telefon: t.telefon || "" });
  };

  const handleTeilnehmerBearbeitenSpeichern = async () => {
    if (!bearbeitenTeilnehmerForm.vorname.trim() || !bearbeitenTeilnehmerForm.nachname.trim()) { alert("Bitte Vorname und Nachname eingeben!"); return; }
    const res = await fetch(`${API}/kurse/teilnehmer/${bearbeitenTeilnehmerId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(bearbeitenTeilnehmerForm)
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Teilnehmer konnte nicht gespeichert werden."); return; }
    setBearbeitenTeilnehmerId(null);
    ladenTeilnehmer(gewaehlterKurs.id);
  };

  const handleEmailAnAlle = () => {
    const empf = teilnehmer.filter(t => t.email).map(t => ({ id: t.id, name: t.name, email: t.email, aktiv: true }));
    setEmailEmpfaenger(empf);
    setEmailBetreff(`Kurs: ${gewaehlterKurs.name}`);
    setEmailText("");
    setEmailAnAlleOffen(true);
  };

  const KURS_VORLAGEN = [
    { id: "", name: "– Vorlage wählen –", betreff: "", text: "" },
    { id: "ausfall", name: "Kurs fällt aus", betreff: "Kurs fällt heute leider aus", text: `Liebe Kursteilnehmer,

leider muss der heutige Kurs "${gewaehlterKurs?.name}" kurzfristig ausfallen.

Wir bitten um Entschuldigung für die Unannehmlichkeiten und melden uns sobald ein Ersatztermin feststeht.

Mit freundlichen Grüßen
ComeFit Team` },
    { id: "zeitaenderung", name: "Kurszeit geändert", betreff: `Änderung der Kurszeit – ${gewaehlterKurs?.name}`, text: `Liebe Kursteilnehmer,

wir möchten Sie darüber informieren, dass sich die Kurszeit für "${gewaehlterKurs?.name}" geändert hat.

Neue Kurszeit: [BITTE EINTRAGEN]

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
ComeFit Team` },
    { id: "erinnerung", name: "Erinnerung nächster Termin", betreff: `Erinnerung – ${gewaehlterKurs?.name} findet statt`, text: `Liebe Kursteilnehmer,

hiermit möchten wir Sie an den nächsten Kurstermin erinnern:

Kurs: ${gewaehlterKurs?.name}
Trainer: ${gewaehlterKurs?.trainer || "–"}
Wochentag: ${gewaehlterKurs?.wochentag || "–"}
Uhrzeit: ${gewaehlterKurs?.uhrzeit || "–"} Uhr

Wir freuen uns auf Sie!

Mit freundlichen Grüßen
ComeFit Team` },
    { id: "ferien", name: "Ferienregelung", betreff: `Ferienregelung – ${gewaehlterKurs?.name}`, text: `Liebe Kursteilnehmer,

wir möchten Sie über die Ferienregelung für den Kurs "${gewaehlterKurs?.name}" informieren:

In der Zeit vom [VON] bis [BIS] findet kein Kurs statt.

Ab [DATUM] geht es wie gewohnt weiter.

Wir wünschen Ihnen eine schöne Ferienzeit!

Mit freundlichen Grüßen
ComeFit Team` },
    { id: "neustart", name: "Kurs startet ab...", betreff: `${gewaehlterKurs?.name} startet neu`, text: `Liebe Kursteilnehmer,

wir freuen uns Ihnen mitteilen zu können, dass der Kurs "${gewaehlterKurs?.name}" ab [DATUM] wieder startet.

Kursdetails:
Wochentag: ${gewaehlterKurs?.wochentag || "–"}
Uhrzeit: ${gewaehlterKurs?.uhrzeit || "–"} Uhr
Trainer: ${gewaehlterKurs?.trainer || "–"}

Wir freuen uns auf Sie!

Mit freundlichen Grüßen
ComeFit Team` },
    { id: "info", name: "Allgemeine Information", betreff: `Information – ${gewaehlterKurs?.name}`, text: `Liebe Kursteilnehmer,

[IHRE NACHRICHT HIER]

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
ComeFit Team` },
  ];

  const handleVorlageWaehlen = (vorlageId) => {
    const v = KURS_VORLAGEN.find(v => v.id === vorlageId);
    if (v && v.id) {
      setEmailBetreff(v.betreff);
      setEmailText(v.text);
    }
  };

  const handleEmailSenden = () => {
    const aktiveEmpf = emailEmpfaenger.filter(e => e.aktiv);
    if (aktiveEmpf.length === 0) { alert("Keine Empfänger ausgewählt!"); return; }
    if (!emailBetreff || !emailText) { alert("Bitte Betreff und Text eingeben!"); return; }
    const bcc = aktiveEmpf.map(e => e.email).join(";");
    const mailto = `mailto:?bcc=${encodeURIComponent(bcc)}&subject=${encodeURIComponent(emailBetreff)}&body=${encodeURIComponent(emailText)}`;
    window.open(mailto, "_blank");
    setEmailAnAlleOffen(false);
  };

  return (
    <div>
      {/* E-Mail Dialog */}
      {emailAnAlleOffen && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(600px, 95vw)" }}>
            <h2 style={s.modalTitel}>📧 E-Mail an alle Teilnehmer</h2>
            <p style={{ color: THEME.colors.textMuted, marginBottom: 12 }}>Empfänger abwählen die keine E-Mail erhalten sollen:</p>
            <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 16 }}>
              {emailEmpfaenger.map((e, i) => (
                <label key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", cursor: "pointer", color: e.aktiv ? THEME.colors.text : THEME.colors.textMuted }}>
                  <input type="checkbox" checked={e.aktiv} onChange={() => setEmailEmpfaenger(emp => emp.map((x,j) => j===i ? {...x, aktiv: !x.aktiv} : x))} />
                  {e.name} – {e.email}
                </label>
              ))}
              {emailEmpfaenger.length === 0 && <p style={{ color: THEME.colors.danger }}>Keine Teilnehmer mit E-Mail-Adresse!</p>}
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Vorlage</label>
              <select style={s.input} onChange={e => handleVorlageWaehlen(e.target.value)}>
                {KURS_VORLAGEN.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Betreff</label><input style={s.input} value={emailBetreff} onChange={e => setEmailBetreff(e.target.value)} /></div>
            <div style={s.inputGroup}><label style={s.label}>Text</label><textarea style={{ ...s.input, height: 150 }} value={emailText} onChange={e => setEmailText(e.target.value)} placeholder="Ihre Nachricht..." /></div>
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 10, marginBottom: 12, borderLeft: "3px solid #2b8cc4" }}>
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>ℹ Outlook öffnet sich mit allen Empfängern im BCC-Feld.</p>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setEmailAnAlleOffen(false)} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleEmailSenden} style={{ ...s.btn, width: "auto" }}>
                📧 Outlook öffnen ({emailEmpfaenger.filter(e=>e.aktiv).length} Empfänger)
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={s.heading}>Kursverwaltung</h1>
        <button onClick={() => { setBearbeitenKurs(null); setKursForm({ name: "", trainer: "", beschreibung: "", wochentag: "", uhrzeit: "", raum: "", max_teilnehmer: "" }); setKursFormOffen(true); }} style={{ ...s.btn, width: "auto" }}>+ Neuer Kurs</button>
      </div>

      {/* Kurs Formular */}
      {kursFormOffen && (
        <div style={{ ...s.karte, marginBottom: 20 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px" }}>{bearbeitenKurs ? "Kurs bearbeiten" : "Neuer Kurs"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={s.inputGroup}><label style={s.label}>Kursname *</label><input style={s.input} value={kursForm.name} onChange={e => setKursForm(f => ({...f, name: e.target.value}))} /></div>
            <div style={s.inputGroup}><label style={s.label}>Trainer</label><input style={s.input} value={kursForm.trainer} onChange={e => setKursForm(f => ({...f, trainer: e.target.value}))} /></div>
            <div style={s.inputGroup}><label style={s.label}>Wochentag</label>
              <select style={s.input} value={kursForm.wochentag} onChange={e => setKursForm(f => ({...f, wochentag: e.target.value}))}>
                <option value="">– wählen –</option>
                {WOCHENTAGE.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Uhrzeit</label><input style={s.input} type="time" value={kursForm.uhrzeit} onChange={e => setKursForm(f => ({...f, uhrzeit: e.target.value}))} /></div>
            <div style={s.inputGroup}><label style={s.label}>Raum</label><input style={s.input} value={kursForm.raum} onChange={e => setKursForm(f => ({...f, raum: e.target.value}))} /></div>
            <div style={s.inputGroup}><label style={s.label}>Max. Teilnehmer</label><input style={s.input} type="number" value={kursForm.max_teilnehmer} onChange={e => setKursForm(f => ({...f, max_teilnehmer: e.target.value}))} /></div>
          </div>
          <div style={s.inputGroup}><label style={s.label}>Beschreibung</label><textarea style={{ ...s.input, height: 60 }} value={kursForm.beschreibung} onChange={e => setKursForm(f => ({...f, beschreibung: e.target.value}))} /></div>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <button onClick={() => setKursFormOffen(false)} style={s.btnGray}>Abbrechen</button>
            <button onClick={handleKursSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: gewaehlterKurs ? "repeat(auto-fit, minmax(300px, 1fr))" : "1fr", gap: 16 }}>
        {/* Kursliste */}
        <div>
          {kurse.length === 0 && !kursFormOffen && <div style={{ ...s.karte, textAlign: "center", padding: 40 }}><p style={{ color: THEME.colors.textMuted }}>Noch keine Kurse. Erstelle deinen ersten Kurs!</p></div>}
          {kurse.map(k => (
            <div key={k.id} onClick={() => setGewaehlterKurs(k)}
              style={{ ...s.karte, marginBottom: 12, cursor: "pointer", borderLeftColor: gewaehlterKurs?.id === k.id ? THEME.colors.primary : THEME.colors.surface, borderLeftWidth: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, fontWeight: 700, margin: "0 0 4px" }}>{k.name}</p>
                  {k.trainer && <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>👤 {k.trainer}</p>}
                  {k.wochentag && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>📅 {k.wochentag}{k.uhrzeit ? ` · ${k.uhrzeit} Uhr` : ""}</p>}
                  {k.raum && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>📍 {k.raum}</p>}
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: "0 0 8px" }}>{k.anzahl_teilnehmer}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, margin: "0 0 8px" }}>Teilnehmer{k.max_teilnehmer ? ` / ${k.max_teilnehmer}` : ""}</p>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={e => { e.stopPropagation(); setQrKurs(k); }} title="QR-Code für öffentliche Registrierung" style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>▦</button>
                    <button onClick={e => { e.stopPropagation(); handleKursBearbeiten(k); }} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>✏</button>
                    <button onClick={e => { e.stopPropagation(); handleKursLoeschen(k.id); }} style={{ ...s.btn, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs, background: THEME.colors.dangerBg }}>✗</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Teilnehmer */}
        {gewaehlterKurs && (
          <div style={s.karte}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <h3 style={{ color: THEME.colors.text, margin: 0 }}>{gewaehlterKurs.name} – Teilnehmer</h3>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setQrKurs(gewaehlterKurs)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>▦ QR-Code</button>
                <button onClick={handleEmailAnAlle} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>📧 E-Mail an alle</button>
                <button onClick={() => setGewaehlterKurs(null)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>✕</button>
              </div>
            </div>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 16px" }}>Teilnehmer insgesamt: {teilnehmer.length}</p>

            {/* Teilnehmer hinzufügen: vorhandene Person suchen ... */}
            <div style={{ marginBottom: 16 }}>
              <label style={s.label}>Teilnehmer hinzufügen</label>
              <input style={s.input} placeholder="Vorhandene Person suchen…" value={mitgliedSuche} onChange={e => handleMitgliedSuche(e.target.value)} />
              {mitgliedErgebnisse.length > 0 && (
                <div style={{ background: THEME.colors.surface, borderRadius: 8, marginTop: 4 }}>
                  {mitgliedErgebnisse.map(m => (
                    <div key={m.id} onClick={() => handleMitgliedHinzufuegen(m)}
                      style={{ padding: "10px 16px", cursor: "pointer", borderBottom: "1px solid #2b3d52", color: THEME.colors.text, display: "flex", justifyContent: "space-between" }}
                      onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span>{m.vorname} {m.nachname}</span>
                      <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{m.email || "keine E-Mail"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ... oder manuell erfassen */}
            <div style={{ marginBottom: 16 }}>
              <button onClick={() => setManuellOffen(!manuellOffen)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>+ Teilnehmer manuell hinzufügen</button>
              {manuellOffen && (
                <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginTop: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={s.inputGroup}><label style={s.label}>Vorname *</label><input style={s.input} value={manuellForm.vorname} onChange={e => setManuellForm(f => ({...f, vorname: e.target.value}))} /></div>
                    <div style={s.inputGroup}><label style={s.label}>Nachname *</label><input style={s.input} value={manuellForm.nachname} onChange={e => setManuellForm(f => ({...f, nachname: e.target.value}))} /></div>
                    <div style={s.inputGroup}><label style={s.label}>E-Mail-Adresse</label><input style={s.input} value={manuellForm.email} onChange={e => setManuellForm(f => ({...f, email: e.target.value}))} /></div>
                    <div style={s.inputGroup}><label style={s.label}>Handynummer (optional)</label><input style={s.input} value={manuellForm.telefon} onChange={e => setManuellForm(f => ({...f, telefon: e.target.value}))} /></div>
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                    <button onClick={() => setManuellOffen(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                    <button onClick={handleManuellHinzufuegen} style={{ ...s.btn, width: "auto" }}>Teilnehmer hinzufügen</button>
                  </div>
                </div>
              )}
            </div>

            {/* Teilnehmerliste - bewusst ohne Mitglied/Extern-Unterscheidung (Nutzer-Wunsch
                2026-08-19): fuer den Kurs ist jede Person einfach "Kursteilnehmer". */}
            {teilnehmer.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Teilnehmer.</p>}
            {teilnehmer.map(t => (
              bearbeitenTeilnehmerId === t.id ? (
                <div key={t.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginTop: 8, marginBottom: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={s.inputGroup}><label style={s.label}>Vorname *</label><input style={s.input} value={bearbeitenTeilnehmerForm.vorname} onChange={e => setBearbeitenTeilnehmerForm(f => ({...f, vorname: e.target.value}))} /></div>
                    <div style={s.inputGroup}><label style={s.label}>Nachname *</label><input style={s.input} value={bearbeitenTeilnehmerForm.nachname} onChange={e => setBearbeitenTeilnehmerForm(f => ({...f, nachname: e.target.value}))} /></div>
                    <div style={s.inputGroup}><label style={s.label}>E-Mail-Adresse</label><input style={s.input} value={bearbeitenTeilnehmerForm.email} onChange={e => setBearbeitenTeilnehmerForm(f => ({...f, email: e.target.value}))} /></div>
                    <div style={s.inputGroup}><label style={s.label}>Handynummer</label><input style={s.input} value={bearbeitenTeilnehmerForm.telefon} onChange={e => setBearbeitenTeilnehmerForm(f => ({...f, telefon: e.target.value}))} /></div>
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                    <button onClick={() => setBearbeitenTeilnehmerId(null)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                    <button onClick={handleTeilnehmerBearbeitenSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                  </div>
                </div>
              ) : (
                <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "1px solid #2b3d52" }}>
                  <div>
                    <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md, fontWeight: 600, margin: 0 }}>{t.vorname && t.nachname ? `${t.vorname} ${t.nachname}` : t.name}</p>
                    {t.email && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "2px 0 0" }}>✉ {t.email}</p>}
                    {t.telefon && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>📞 {t.telefon}</p>}
                    {t.registriert_am && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "2px 0 0" }}>Registriert am {t.registriert_am}</p>}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => handleTeilnehmerBearbeitenStart(t)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 8px" }}>✏</button>
                    <button onClick={() => handleTeilnehmerEntfernen(t.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 8px", background: THEME.colors.dangerBg }}>✗</button>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {qrKurs && <KursQrDialog kurs={qrKurs} onClose={() => setQrKurs(null)} />}
    </div>
  );
}

// ============ RECHNUNGEN SEITE ============
function RechnungenSeite() {
  const [rechnungen, setRechnungen] = useState([]);
  const [vorlagen, setVorlagen] = useState([]);
  const [artikel, setArtikel] = useState([]);
  const [kurse, setKurse] = useState([]);
  const [bearbeitenId, setBearbeitenId] = useState(null);
  const [formOffen, setFormOffen] = useState(false);
  const [artikelVerwaltung, setArtikelVerwaltung] = useState(false);
  const [offenePostenOffen, setOffenePostenOffen] = useState(false);
  const [offenePosten, setOffenePosten] = useState([]);
  const [naechsteNummer, setNaechsteNummer] = useState("");
  const [vorlageAlsNameDialog, setVorlageAlsNameDialog] = useState(false);
  const [vorlageAlsName, setVorlageAlsName] = useState("");
  const [bruttoModus, setBruttoModus] = useState(true);
  const [neuerArtikel, setNeuerArtikel] = useState({ name: "", beschreibung: "", einheit: "", brutto: "", mwst_prozent: 19 });
  const [bearbeitenArtikelId, setBearbeitenArtikelId] = useState(null);
  const [artikelBearbeitenForm, setArtikelBearbeitenForm] = useState(null);
  const [form, setForm] = useState({
    datum: new Date().toISOString().split("T")[0],
    empfaenger_name: "", empfaenger_adresse: "", email: "",
    vorlage: "freitext", notiz: "", zahlungsart: "ueberweisung",
    mwst_prozent: 19,
    positionen: [{ beschreibung: "", menge: 1, einheit: "", einzelpreis_netto: 0, einzelpreis_brutto: 0, gesamt: 0, kurs_id: null }],
    mitglied_id: null,
  });
  const [mitgliedSuche, setMitgliedSuche] = useState("");
  const [mitgliedErgebnisse, setMitgliedErgebnisse] = useState([]);
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  // Rechnung per E-Mail versenden (Nutzer-Wunsch 2026-08-07): kleine Vorlage mit
  // Platzhaltern, editierbar vor dem Versand, PDF wird serverseitig automatisch angehaengt.
  const [emailDialogRechnung, setEmailDialogRechnung] = useState(null);
  const [emailDialogAn, setEmailDialogAn] = useState("");
  const [emailDialogBetreff, setEmailDialogBetreff] = useState("");
  const [emailDialogText, setEmailDialogText] = useState("");
  const [emailSendetGerade, setEmailSendetGerade] = useState(false);

  useEffect(() => {
    const handler = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const laden = () => fetch(`${API}/rechnungen/`).then(r => r.json()).then(setRechnungen).catch(() => {});
  const ladeNummer = () => fetch(`${API}/rechnungen/naechste-nummer`).then(r => r.json()).then(d => setNaechsteNummer(d.nummer)).catch(() => {});
  const ladeVorlagen = () => fetch(`${API}/rechnungen/vorlagen`).then(r => r.json()).then(d => setVorlagen(Array.isArray(d) ? d : [])).catch(() => setVorlagen([]));
  const ladeArtikel = () => fetch(`${API}/rechnungen/artikel`).then(r => r.json()).then(d => setArtikel(Array.isArray(d) ? d : [])).catch(() => setArtikel([]));
  const ladeKurse = () => fetch(`${API}/kurse/`).then(r => r.json()).then(d => setKurse(Array.isArray(d) ? d : [])).catch(() => setKurse([]));

  useEffect(() => { laden(); ladeNummer(); ladeVorlagen(); ladeArtikel(); ladeKurse(); }, []);

  useEffect(() => {
    if (offenePostenOffen) {
      fetch(`${API}/rechnungen/offene-posten`).then(r => r.json()).then(d => setOffenePosten(Array.isArray(d) ? d : [])).catch(() => setOffenePosten([]));
    }
  }, [offenePostenOffen]);

  const berechneBrutto = (netto, mwst) => Math.round(netto * (1 + mwst/100) * 100) / 100;
  const berechneNetto = (brutto, mwst) => Math.round(brutto / (1 + mwst/100) * 100) / 100;

  const handlePositionAendern = (i, feld, wert) => {
    const pos = [...form.positionen];
    pos[i] = { ...pos[i], [feld]: wert };
    const mwst = parseFloat(form.mwst_prozent) || 19;
    if (feld === "einzelpreis_brutto") {
      pos[i].einzelpreis_netto = berechneNetto(parseFloat(wert) || 0, mwst);
    } else if (feld === "einzelpreis_netto") {
      pos[i].einzelpreis_brutto = berechneBrutto(parseFloat(wert) || 0, mwst);
    }
    const menge = parseFloat(pos[i].menge) || 1;
    // Im Brutto-Modus "Gesamt Netto" aus Brutto-GESAMT (Einzelpreis x Menge) herleiten und erst
    // EINMAL runden - nicht aus dem bereits auf 2 Nachkommastellen gerundeten Brutto->Netto-
    // Einzelpreis multipliziert mit Menge. Sonst weicht "Brutto x Menge" am Ende um ein paar Cent
    // vom angezeigten Rechnungsbetrag ab (Nutzer-Fund 2026-10-01: 35 EUR x 15 ergab 524.97 EUR
    // statt der erwarteten glatten 525.00 EUR, weil 35/1.19=29.41 schon gerundet war, bevor x15
    // gerechnet wurde). Im Netto-Modus ist der eingegebene Netto-Einzelpreis bereits die exakte,
    // vom Nutzer gemeinte Zahl - dort bleibt die bisherige direkte Multiplikation richtig.
    pos[i].gesamt = bruttoModus
      ? berechneNetto((parseFloat(pos[i].einzelpreis_brutto) || 0) * menge, mwst)
      : Math.round((parseFloat(pos[i].einzelpreis_netto) || 0) * menge * 100) / 100;
    setForm(f => ({ ...f, positionen: pos }));
  };

  const handleArtikelEinfuegen = (a) => {
    const pos = {
      beschreibung: a.name, menge: 1, einheit: a.einheit || "",
      einzelpreis_netto: a.netto, einzelpreis_brutto: a.brutto, gesamt: a.netto, kurs_id: null
    };
    setForm(f => ({
      ...f,
      positionen: [...f.positionen.filter(p => p.beschreibung !== ""), pos]
    }));
  };

  const handleVorlageWaehlen = (vorlageId) => {
    const v = vorlagen.find(v => v.id === vorlageId);
    if (v && vorlageId !== "freitext") {
      const mwst = v.mwst_prozent || 19;
      const pos = (v.positionen || []).map(p => ({
        ...p,
        einzelpreis_netto: p.einzelpreis || p.einzelpreis_netto || 0,
        einzelpreis_brutto: berechneBrutto(p.einzelpreis || p.einzelpreis_netto || 0, mwst),
        gesamt: p.gesamt || 0
      }));
      setForm(f => ({ ...f, vorlage: vorlageId, notiz: v.notiz || "", positionen: pos.length > 0 ? pos : [{beschreibung:"",menge:1,einheit:"",einzelpreis_netto:0,einzelpreis_brutto:0,gesamt:0}], mwst_prozent: mwst, zahlungsart: v.zahlungsart }));
    } else {
      setForm(f => ({ ...f, vorlage: vorlageId }));
    }
  };

  const handleMitgliedSuche = async (q) => {
    setMitgliedSuche(q);
    if (q.length < 2) { setMitgliedErgebnisse([]); return; }
    const res = await fetch(`${API}/rechnungen/kunden-suche?q=${encodeURIComponent(q)}`);
    const data = await res.json();
    setMitgliedErgebnisse(data.slice(0, 8));
  };

  const KUNDEN_TYP_LABEL = { mitglied: "Mitglied", wellpass: "Wellpass", hansefit: "Hansefit", sonstige: "Sonstige" };

  const handleMitgliedWaehlen = (m) => {
    const adresse = m.strasse || m.plz || m.ort ? `${m.strasse}\n${m.plz} ${m.ort}` : "";
    setForm(f => ({ ...f, empfaenger_name: `${m.vorname} ${m.nachname}`, empfaenger_adresse: adresse, mitglied_id: m.typ === "mitglied" ? m.id : null }));
    setMitgliedSuche(""); setMitgliedErgebnisse([]);
  };

  const handleVorlageAlsSpeichern = async () => {
    if (!vorlageAlsName) { alert("Bitte Namen eingeben!"); return; }
    const res = await fetch(`${API}/rechnungen/vorlagen`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: vorlageAlsName, notiz: form.notiz, positionen: form.positionen.map(p => ({...p, einzelpreis: p.einzelpreis_netto})), mwst_prozent: form.mwst_prozent, zahlungsart: form.zahlungsart })
    });
    if (!res.ok) { alert("Vorlage konnte nicht gespeichert werden."); return; }
    setVorlageAlsNameDialog(false); setVorlageAlsName(""); ladeVorlagen();
    alert(`Vorlage "${vorlageAlsName}" gespeichert!`);
  };

  const handleNeuerArtikelSpeichern = async () => {
    if (!neuerArtikel.name || !neuerArtikel.brutto) { alert("Bitte Name und Preis eingeben!"); return; }
    const brutto = parseFloat(neuerArtikel.brutto);
    const netto = berechneNetto(brutto, parseFloat(neuerArtikel.mwst_prozent));
    const res = await fetch(`${API}/rechnungen/artikel`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: neuerArtikel.name, beschreibung: neuerArtikel.beschreibung, einheit: neuerArtikel.einheit, netto, brutto, mwst_prozent: parseFloat(neuerArtikel.mwst_prozent) })
    });
    if (!res.ok) { alert("Artikel konnte nicht gespeichert werden."); return; }
    setNeuerArtikel({ name: "", beschreibung: "", einheit: "", brutto: "", mwst_prozent: 19 });
    ladeArtikel();
  };

  const handleArtikelBearbeitenStart = (a) => {
    setBearbeitenArtikelId(a.id);
    setArtikelBearbeitenForm({ name: a.name, beschreibung: a.beschreibung || "", einheit: a.einheit || "", brutto: String(a.brutto), mwst_prozent: a.mwst_prozent });
  };

  const handleArtikelBearbeitenAbbrechen = () => {
    setBearbeitenArtikelId(null);
    setArtikelBearbeitenForm(null);
  };

  const handleArtikelBearbeitenSpeichern = async () => {
    if (!artikelBearbeitenForm.name || !artikelBearbeitenForm.brutto) { alert("Bitte Name und Preis eingeben!"); return; }
    const brutto = parseFloat(artikelBearbeitenForm.brutto);
    const netto = berechneNetto(brutto, parseFloat(artikelBearbeitenForm.mwst_prozent));
    const res = await fetch(`${API}/rechnungen/artikel/${bearbeitenArtikelId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: artikelBearbeitenForm.name, beschreibung: artikelBearbeitenForm.beschreibung,
        einheit: artikelBearbeitenForm.einheit, netto, brutto,
        mwst_prozent: parseFloat(artikelBearbeitenForm.mwst_prozent),
      })
    });
    if (!res.ok) { alert("Artikel konnte nicht gespeichert werden."); return; }
    setBearbeitenArtikelId(null);
    setArtikelBearbeitenForm(null);
    ladeArtikel();
  };

  const handleArtikelLoeschen = async (id) => {
    if (!window.confirm("Artikel wirklich löschen?")) return;
    const res = await fetch(`${API}/rechnungen/artikel/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladeArtikel();
  };

  const netto = form.positionen.reduce((s, p) => s + parseFloat(p.gesamt || 0), 0);
  const mwst = Math.round(netto * form.mwst_prozent / 100 * 100) / 100;
  const gesamt = Math.round((netto + mwst) * 100) / 100;

  const formZuruecksetzen = () => {
    setForm({ datum: new Date().toISOString().split("T")[0], empfaenger_name: "", empfaenger_adresse: "", email: "", vorlage: "freitext", notiz: "", zahlungsart: "ueberweisung", mwst_prozent: 19, positionen: [{beschreibung:"",menge:1,einheit:"",einzelpreis_netto:0,einzelpreis_brutto:0,gesamt:0,kurs_id:null}], mitglied_id: null });
    setBearbeitenId(null);
  };

  const handleSpeichern = async () => {
    if (!form.empfaenger_name || form.positionen.some(p => !p.beschreibung)) { alert("Bitte Empfänger und alle Positionen ausfüllen!"); return; }
    const payload = { ...form, positionen: form.positionen.map(p => ({ beschreibung: p.beschreibung, menge: p.menge, einheit: p.einheit, einzelpreis: p.einzelpreis_netto, gesamt: p.gesamt, kurs_id: p.kurs_id || null })) };
    const url = bearbeitenId ? `${API}/rechnungen/${bearbeitenId}` : `${API}/rechnungen/`;
    const res = await fetch(url, { method: bearbeitenId ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (res.ok) {
      setFormOffen(false); laden(); ladeNummer();
      formZuruecksetzen();
    } else {
      const data = await res.json().catch(() => ({}));
      alert(data.detail || "Speichern fehlgeschlagen.");
    }
  };

  const handleLoeschen = async (id, nummer) => {
    if (!window.confirm(`Rechnung ${nummer} wirklich löschen?`)) return;
    const res = await fetch(`${API}/rechnungen/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    laden(); ladeNummer();
  };

  const handleBearbeiten = async (id) => {
    const res = await fetch(`${API}/rechnungen/${id}`);
    const r = await res.json();
    const mwst = r.mwst_prozent || 19;
    setForm({
      datum: r.datum, empfaenger_name: r.empfaenger_name, empfaenger_adresse: r.empfaenger_adresse || "", email: r.email || "",
      vorlage: r.vorlage || "freitext", notiz: r.notiz || "",
      zahlungsart: r.zahlungsart, mwst_prozent: mwst,
      positionen: r.positionen.map(p => ({ beschreibung: p.beschreibung, menge: p.menge, einheit: p.einheit || "", einzelpreis_netto: p.einzelpreis, einzelpreis_brutto: berechneBrutto(p.einzelpreis, mwst), gesamt: p.gesamt, kurs_id: p.kurs_id || null })),
      mitglied_id: r.mitglied_id,
    });
    setBearbeitenId(id);
    setFormOffen(true); window.scrollTo(0, 0);
  };

  const handleBezahltUmschalten = async (r) => {
    const neu = !r.bezahlt;
    setRechnungen(alt => alt.map(x => x.id === r.id ? { ...x, bezahlt: neu } : x));
    const res = await fetch(`${API}/rechnungen/${r.id}/bezahlt`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bezahlt: neu }),
    });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); }
    laden();
  };

  const handleDuplizieren = async (id) => {
    const res = await fetch(`${API}/rechnungen/${id}`);
    const r = await res.json();
    const mwst = r.mwst_prozent || 19;
    setForm({
      datum: new Date().toISOString().split("T")[0],
      empfaenger_name: r.empfaenger_name, empfaenger_adresse: r.empfaenger_adresse || "", email: r.email || "",
      vorlage: r.vorlage || "freitext", notiz: r.notiz || "",
      zahlungsart: r.zahlungsart, mwst_prozent: mwst,
      positionen: r.positionen.map(p => ({ beschreibung: p.beschreibung, menge: p.menge, einheit: p.einheit || "", einzelpreis_netto: p.einzelpreis, einzelpreis_brutto: berechneBrutto(p.einzelpreis, mwst), gesamt: p.gesamt, kurs_id: p.kurs_id || null })),
      mitglied_id: r.mitglied_id,
    });
    setBearbeitenId(null);
    setFormOffen(true); window.scrollTo(0, 0);
  };

  const handleEmailDialogOeffnen = async (id) => {
    const res = await fetch(`${API}/rechnungen/${id}`);
    const r = await res.json();
    setEmailDialogRechnung(r);
    setEmailDialogAn(r.email || "");
    setEmailDialogBetreff(`Ihre Rechnung ${r.nummer} von ComeFit`);
    setEmailDialogText(
      `Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie die Rechnung ${r.nummer} über ${parseFloat(r.gesamt).toFixed(2)} €.\n\nMit freundlichen Grüßen\nIhr ComeFit-Team`
    );
  };

  const handleEmailDialogSchliessen = () => {
    setEmailDialogRechnung(null);
    setEmailDialogAn(""); setEmailDialogBetreff(""); setEmailDialogText("");
  };

  const handleEmailSenden = async () => {
    if (!emailDialogAn.trim()) { alert("Bitte eine E-Mail-Adresse eingeben!"); return; }
    setEmailSendetGerade(true);
    const res = await fetch(`${API}/rechnungen/${emailDialogRechnung.id}/email-senden`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empfaenger_email: emailDialogAn.trim(), betreff: emailDialogBetreff, text: emailDialogText }),
    });
    setEmailSendetGerade(false);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(data.detail || "Versand fehlgeschlagen."); return; }
    alert(`✓ ${data.message}`);
    handleEmailDialogSchliessen();
    laden();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <h1 style={s.heading}>Rechnungen</h1>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => setArtikelVerwaltung(!artikelVerwaltung)} style={{ ...s.btnGray, width: "auto" }}>📦 Artikel</button>
          <button onClick={() => setOffenePostenOffen(!offenePostenOffen)} style={{ ...s.btnGray, width: "auto" }}>💰 Offene Posten</button>
          <button onClick={() => { const j = new Date().getFullYear(); window.open(`${API}/rechnungen/jahresabschluss/${j}/pdf`, "_blank"); }} style={{ ...s.btnGray, width: "auto" }}>📄 Jahresabschluss {new Date().getFullYear()}</button>
          <button onClick={() => { if (!formOffen) formZuruecksetzen(); setFormOffen(!formOffen); }} style={{ ...s.btn, width: "auto" }}>+ Neue Rechnung</button>
        </div>
      </div>

      {/* Offene Posten */}
      {offenePostenOffen && (
        <div style={{ ...s.karte, marginBottom: 20, borderLeftColor: THEME.colors.warning }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ color: THEME.colors.text, margin: 0 }}>💰 Offene Posten – Ausstehende SEPA Zahlungen</h3>
            <button onClick={() => setOffenePostenOffen(false)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>✕ Schließen</button>
          </div>
          {offenePosten.length === 0 && <p style={{ color: THEME.colors.success }}>✓ Keine offenen Posten – alle Zahlungen eingezogen!</p>}
          {offenePosten.length > 0 && (
            <>
              <div style={{ background: "#f59e0b22", border: "1px solid #f59e0b", borderRadius: 8, padding: "10px 16px", marginBottom: 16 }}>
                <p style={{ color: THEME.colors.warning, margin: 0, fontSize: THEME.fontSize.base }}>
                  {offenePosten.length} ausstehende Zahlungen · Gesamt: <strong>{offenePosten.reduce((s,p) => s + p.betrag, 0).toFixed(2)} €</strong>
                </p>
              </div>
              <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: THEME.colors.background }}>
                    {["Mitglied", "Tarif", "Abbuchung", "Betrag", "IBAN"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {offenePosten.map(p => (
                    <tr key={p.zahlung_id} style={{ borderTop: "1px solid #2b3d52" }}>
                      <td style={{ padding: "10px 14px", color: THEME.colors.text, fontWeight: 600 }}>{p.name}</td>
                      <td style={{ padding: "10px 14px", color: THEME.colors.primary }}>{p.tarif}</td>
                      <td style={{ padding: "10px 14px", color: THEME.colors.textSecondary }}>{datumDE(p.abbuchungsdatum)}</td>
                      <td style={{ padding: "10px 14px", color: THEME.colors.warning, fontWeight: 700 }}>{p.betrag.toFixed(2)} €</td>
                      <td style={{ padding: "10px 14px", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{p.iban}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Artikel Verwaltung */}
      {artikelVerwaltung && (
        <div style={{ ...s.karte, marginBottom: 20 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 16px" }}>📦 Artikelverwaltung</h3>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 40px", gap: 8, marginBottom: 8 }}>
            <input style={s.input} placeholder="Artikelname *" value={neuerArtikel.name} onChange={e => setNeuerArtikel(n => ({...n, name: e.target.value}))} />
            <input style={s.input} placeholder="Einheit" value={neuerArtikel.einheit} onChange={e => setNeuerArtikel(n => ({...n, einheit: e.target.value}))} />
            <input style={s.input} type="number" step="0.01" placeholder="Brutto-Preis €" value={neuerArtikel.brutto} onChange={e => setNeuerArtikel(n => ({...n, brutto: e.target.value}))} />
            <select style={s.input} value={neuerArtikel.mwst_prozent} onChange={e => setNeuerArtikel(n => ({...n, mwst_prozent: e.target.value}))}>
              <option value={19}>19% MwSt</option>
              <option value={7}>7% MwSt</option>
              <option value={0}>0% MwSt</option>
            </select>
            <button onClick={handleNeuerArtikelSpeichern} style={{ ...s.btn, padding: "8px" }}>+</button>
          </div>
          <div style={{ marginBottom: 12 }}>
            <input style={{ ...s.input, width: "100%" }} placeholder="Beschreibung (erscheint auf der Rechnung, optional)"
              value={neuerArtikel.beschreibung} onChange={e => setNeuerArtikel(n => ({...n, beschreibung: e.target.value}))} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
            {artikel.map(a => (
              bearbeitenArtikelId === a.id ? (
                <div key={a.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: 12, gridColumn: "1 / -1", display: "grid", gap: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 8 }}>
                    <input style={s.input} placeholder="Artikelname *" value={artikelBearbeitenForm.name} onChange={e => setArtikelBearbeitenForm(f => ({...f, name: e.target.value}))} />
                    <input style={s.input} placeholder="Einheit" value={artikelBearbeitenForm.einheit} onChange={e => setArtikelBearbeitenForm(f => ({...f, einheit: e.target.value}))} />
                    <input style={s.input} type="number" step="0.01" placeholder="Brutto-Preis €" value={artikelBearbeitenForm.brutto} onChange={e => setArtikelBearbeitenForm(f => ({...f, brutto: e.target.value}))} />
                    <select style={s.input} value={artikelBearbeitenForm.mwst_prozent} onChange={e => setArtikelBearbeitenForm(f => ({...f, mwst_prozent: e.target.value}))}>
                      <option value={19}>19% MwSt</option>
                      <option value={7}>7% MwSt</option>
                      <option value={0}>0% MwSt</option>
                    </select>
                  </div>
                  <input style={{ ...s.input, width: "100%" }} placeholder="Beschreibung (erscheint auf der Rechnung, optional)"
                    value={artikelBearbeitenForm.beschreibung} onChange={e => setArtikelBearbeitenForm(f => ({...f, beschreibung: e.target.value}))} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={handleArtikelBearbeitenSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                    <button onClick={handleArtikelBearbeitenAbbrechen} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div key={a.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600, margin: "0 0 2px" }}>{a.name}</p>
                    <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, margin: "0 0 2px" }}>{a.brutto.toFixed(2)} € Brutto</p>
                    <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, margin: 0 }}>{a.netto.toFixed(2)} € Netto · {a.mwst_prozent}% MwSt</p>
                    {a.beschreibung && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, margin: "2px 0 0", fontStyle: "italic" }}>{a.beschreibung}</p>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => handleArtikelBearbeitenStart(a)} style={{ ...s.btnGray, width: "auto", padding: "4px 8px", fontSize: THEME.fontSize.xs }}>✎</button>
                    <button onClick={() => handleArtikelLoeschen(a.id)} style={{ ...s.btn, width: "auto", padding: "4px 8px", background: THEME.colors.dangerBg, fontSize: THEME.fontSize.xs }}>✗</button>
                  </div>
                </div>
              )
            ))}
            {artikel.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Artikel angelegt.</p>}
          </div>
        </div>
      )}

      {formOffen && (
        <div style={{ ...s.karte, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>
              {bearbeitenId ? "Rechnung bearbeiten" : "Neue Rechnung"} – <span style={{ color: THEME.colors.primary }}>{bearbeitenId ? rechnungen.find(r => r.id === bearbeitenId)?.nummer : naechsteNummer}</span>
            </h3>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Preise eingeben als:</span>
              <button onClick={() => setBruttoModus(true)} style={{ ...s.btnGray, width: "auto", padding: "5px 12px", fontSize: THEME.fontSize.sm, background: bruttoModus ? THEME.colors.primary : THEME.colors.surfaceMuted }}>Brutto</button>
              <button onClick={() => setBruttoModus(false)} style={{ ...s.btnGray, width: "auto", padding: "5px 12px", fontSize: THEME.fontSize.sm, background: !bruttoModus ? THEME.colors.primary : THEME.colors.surfaceMuted }}>Netto</button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div style={s.inputGroup}><label style={s.label}>Datum</label><input type="date" style={s.input} value={form.datum} onChange={e => setForm(f => ({...f, datum: e.target.value}))} /></div>
            <div style={s.inputGroup}>
              <label style={s.label}>Vorlage</label>
              <select style={s.input} value={form.vorlage} onChange={e => handleVorlageWaehlen(e.target.value)}>
                {vorlagen.map(v => <option key={v.id} value={v.id}>{v.name}{!v.standard ? " ⭐" : ""}</option>)}
              </select>
            </div>
          </div>

          <div style={s.inputGroup}>
            <label style={s.label}>Mitglied suchen (optional)</label>
            <input style={s.input} placeholder="Name eingeben... (Mitglied, Wellpass, Hansefit, Sonstige)" value={mitgliedSuche} onChange={e => handleMitgliedSuche(e.target.value)} />
            {mitgliedErgebnisse.length > 0 && (
              <div style={{ background: THEME.colors.surface, borderRadius: 8, marginTop: 4 }}>
                {mitgliedErgebnisse.map(m => (
                  <div key={`${m.typ}-${m.id}`} onClick={() => handleMitgliedWaehlen(m)}
                    style={{ padding: "10px 16px", cursor: "pointer", borderBottom: "1px solid #2b3d52", color: THEME.colors.text }}
                    onMouseEnter={e => e.currentTarget.style.background = THEME.colors.surfaceMuted}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {m.vorname} {m.nachname} {(m.strasse || m.plz || m.ort) ? `– ${m.strasse}, ${m.plz} ${m.ort}` : ""}
                    {m.typ !== "mitglied" && <span style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xs, marginLeft: 8 }}>({KUNDEN_TYP_LABEL[m.typ]})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={s.inputGroup}><label style={s.label}>Empfänger *</label><input style={s.input} value={form.empfaenger_name} onChange={e => setForm(f => ({...f, empfaenger_name: e.target.value}))} placeholder="Name / Firma" /></div>
            <div style={s.inputGroup}><label style={s.label}>Adresse</label><textarea style={{ ...s.input, height: 60 }} value={form.empfaenger_adresse} onChange={e => setForm(f => ({...f, empfaenger_adresse: e.target.value}))} placeholder={"Straße\nPLZ Ort"} /></div>
          </div>

          <div style={s.inputGroup}><label style={s.label}>E-Mail (optional, für Versand per Mail)</label><input type="email" style={s.input} value={form.email || ""} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="empfaenger@beispiel.de" /></div>

          <div style={s.inputGroup}><label style={s.label}>Beschreibungstext</label><textarea style={{ ...s.input, height: 80 }} value={form.notiz} onChange={e => setForm(f => ({...f, notiz: e.target.value}))} placeholder="Text der in der Rechnung erscheint..." /></div>

          {/* Artikel schnell einfügen */}
          {artikel.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...s.label, marginBottom: 8, display: "block" }}>Artikel einfügen</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {artikel.map(a => (
                  <button key={a.id} onClick={() => handleArtikelEinfuegen(a)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "6px 12px" }}>
                    + {a.name} ({a.brutto.toFixed(2)} €)
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Positionen */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ ...s.label, marginBottom: 8, display: "block" }}>Positionen</label>
            <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: 700 }}>
            <div style={{ display: "grid", gridTemplateColumns: "3fr 80px 80px 120px 120px 140px 40px", gap: 8, marginBottom: 4 }}>
              {["Beschreibung", "Menge", "Einheit", bruttoModus ? "Brutto €" : "Netto €", "Gesamt Netto", "Kurs", ""].map(h => (
                <span key={h} style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, paddingLeft: 4 }}>{h}</span>
              ))}
            </div>
            {form.positionen.map((p, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 80px 80px 120px 120px 140px 40px", gap: 8, marginBottom: 8 }}>
                <input style={s.input} placeholder="Beschreibung *" value={p.beschreibung} onChange={e => handlePositionAendern(i, "beschreibung", e.target.value)} />
                <input style={s.input} type="number" value={p.menge} onChange={e => handlePositionAendern(i, "menge", e.target.value)} />
                <input style={s.input} placeholder="Stk" value={p.einheit} onChange={e => handlePositionAendern(i, "einheit", e.target.value)} />
                <input style={s.input} type="number" step="0.01"
                  value={bruttoModus ? p.einzelpreis_brutto : p.einzelpreis_netto}
                  onChange={e => handlePositionAendern(i, bruttoModus ? "einzelpreis_brutto" : "einzelpreis_netto", e.target.value)} />
                <input style={{ ...s.input, color: THEME.colors.success }} readOnly value={`${parseFloat(p.gesamt || 0).toFixed(2)} €`} />
                <select style={s.input} value={p.kurs_id || ""} onChange={e => handlePositionAendern(i, "kurs_id", e.target.value ? parseInt(e.target.value, 10) : null)}>
                  <option value="">– kein Kurs –</option>
                  {kurse.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
                {form.positionen.length > 1 && (
                  <button onClick={() => setForm(f => ({...f, positionen: f.positionen.filter((_,j) => j !== i)}))} style={{ ...s.btn, background: THEME.colors.dangerBg, padding: "8px" }}>✗</button>
                )}
              </div>
            ))}
            </div>
            </div>
            <button onClick={() => setForm(f => ({...f, positionen: [...f.positionen, {beschreibung:"",menge:1,einheit:"",einzelpreis_netto:0,einzelpreis_brutto:0,gesamt:0,kurs_id:null}]}))} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", marginTop: 8 }}>+ Position hinzufügen</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div style={s.inputGroup}><label style={s.label}>MwSt %</label>
              <select style={s.input} value={form.mwst_prozent} onChange={e => setForm(f => ({...f, mwst_prozent: parseFloat(e.target.value)}))}>
                <option value={19}>19%</option><option value={7}>7%</option><option value={0}>0%</option>
              </select>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Zahlung</label>
              <select style={s.input} value={form.zahlungsart} onChange={e => setForm(f => ({...f, zahlungsart: e.target.value}))}>
                <option value="ueberweisung">Bitte überweisen</option>
                <option value="bar">Bereits bar bezahlt</option>
              </select>
            </div>
          </div>

          <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 16, textAlign: "right" }}>
            <p style={{ color: THEME.colors.textSecondary, margin: "0 0 4px" }}>Netto: {netto.toFixed(2)} €</p>
            <p style={{ color: THEME.colors.textSecondary, margin: "0 0 4px" }}>MwSt {form.mwst_prozent}%: {mwst.toFixed(2)} €</p>
            <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xl2, fontWeight: 700, margin: 0 }}>Gesamt: {gesamt.toFixed(2)} €</p>
          </div>

          {vorlageAlsNameDialog && (
            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <label style={s.label}>Name der Vorlage *</label>
              <input style={{ ...s.input, marginBottom: 8 }} value={vorlageAlsName} onChange={e => setVorlageAlsName(e.target.value)} placeholder="z.B. Präventionskurs Extra Fit" autoFocus />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setVorlageAlsNameDialog(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                <button onClick={handleVorlageAlsSpeichern} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>⭐ Speichern</button>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <button onClick={() => setVorlageAlsNameDialog(true)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>⭐ Als Vorlage speichern</button>
            <button onClick={() => { setFormOffen(false); formZuruecksetzen(); }} style={s.btnGray}>Abbrechen</button>
            <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto" }}>{bearbeitenId ? "Änderungen speichern" : "Rechnung erstellen"}</button>
          </div>
        </div>
      )}

      <div style={s.karte}>
        {rechnungen.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Rechnungen.</p>}
        {rechnungen.length > 0 && mobil && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {rechnungen.map(r => (
              <div key={r.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: "12px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div>
                    <div style={{ color: THEME.colors.primary, fontWeight: 700 }}>{r.nummer}</div>
                    <div style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, marginTop: 2 }}>{r.empfaenger_name}</div>
                    <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 2 }}>{datumDE(r.datum)} · {r.zahlungsart === "bar" ? "✓ Bar" : "→ Überweisung"}</div>
                  </div>
                  <div style={{ color: THEME.colors.success, fontWeight: 600, whiteSpace: "nowrap" }}>{parseFloat(r.gesamt).toFixed(2)} €</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                  <button onClick={() => handleBezahltUmschalten(r)} title={r.bezahlt ? `Bezahlt am ${datumDE(r.bezahlt_am)}` : "Als bezahlt markieren"}
                    style={{ border: "none", cursor: "pointer", borderRadius: 6, padding: "5px 10px", fontSize: THEME.fontSize.xs, fontWeight: 600,
                      background: r.bezahlt ? "#16351f" : "#3d2a12", color: r.bezahlt ? THEME.colors.success : THEME.colors.warning }}>
                    {r.bezahlt ? "✓ Bezahlt" : "○ Offen"}
                  </button>
                  <a href={`${API}/rechnungen/${r.id}/pdf`} target="_blank" rel="noreferrer"
                    style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px", textDecoration: "none", textAlign: "center" }}>📄 PDF</a>
                  <a href={`${API}/rechnungen/${r.id}/docx`} target="_blank" rel="noreferrer"
                    style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px", textDecoration: "none", textAlign: "center" }}>📝 Word</a>
                  <button onClick={() => handleEmailDialogOeffnen(r.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>✉ Mail</button>
                  {!r.bezahlt && <button onClick={() => handleBearbeiten(r.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>✎ Bearbeiten</button>}
                  <button onClick={() => handleDuplizieren(r.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>⊕ Kopie</button>
                  <button onClick={() => handleLoeschen(r.id, r.nummer)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px", background: THEME.colors.dangerBg }}>✗</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {rechnungen.length > 0 && !mobil && (
          <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: THEME.colors.background }}>
                {["Nr.", "Datum", "Empfänger", "Betrag", "Zahlung", "Bezahlt", ""].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rechnungen.map(r => (
                <tr key={r.id} style={{ borderTop: "1px solid #2b3d52", fontSize: THEME.fontSize.md }}
                  onMouseEnter={e => e.currentTarget.style.background = "#1f2f42"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "12px 16px", color: THEME.colors.primary, fontWeight: 700 }}>{r.nummer}</td>
                  <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{datumDE(r.datum)}</td>
                  <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{r.empfaenger_name}</td>
                  <td style={{ padding: "12px 16px", color: THEME.colors.success, fontWeight: 600 }}>{parseFloat(r.gesamt).toFixed(2)} €</td>
                  <td style={{ padding: "12px 16px", color: r.zahlungsart === "bar" ? THEME.colors.success : THEME.colors.warning, fontSize: THEME.fontSize.base }}>
                    {r.zahlungsart === "bar" ? "✓ Bar" : "→ Überweisung"}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <button onClick={() => handleBezahltUmschalten(r)} title={r.bezahlt ? `Bezahlt am ${datumDE(r.bezahlt_am)}` : "Als bezahlt markieren"}
                      style={{ border: "none", cursor: "pointer", borderRadius: 6, padding: "6px 12px", fontSize: THEME.fontSize.sm, fontWeight: 600,
                        background: r.bezahlt ? "#16351f" : "#3d2a12", color: r.bezahlt ? THEME.colors.success : THEME.colors.warning }}>
                      {r.bezahlt ? "✓ Bezahlt" : "○ Offen"}
                    </button>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <a href={`${API}/rechnungen/${r.id}/pdf`} target="_blank" rel="noreferrer"
                        style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", textDecoration: "none", textAlign: "center" }}>📄 PDF</a>
                      <a href={`${API}/rechnungen/${r.id}/docx`} target="_blank" rel="noreferrer"
                        style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", textDecoration: "none", textAlign: "center" }}>📝 Word</a>
                      <button onClick={() => handleEmailDialogOeffnen(r.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>✉ Mail</button>
                      {!r.bezahlt && <button onClick={() => handleBearbeiten(r.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>✎ Bearbeiten</button>}
                      <button onClick={() => handleDuplizieren(r.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>⊕ Kopie</button>
                      <button onClick={() => handleLoeschen(r.id, r.nummer)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", background: THEME.colors.dangerBg }}>✗</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {emailDialogRechnung && (
        <div style={s.modalBg} onClick={handleEmailDialogSchliessen}>
          <div style={{ ...s.modal, width: "min(600px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitel}>Rechnung {emailDialogRechnung.nummer} per E-Mail senden</h2>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 16px" }}>
              Die Rechnung wird als PDF automatisch angehängt.
            </p>
            <div style={s.inputGroup}><label style={s.label}>An</label><input type="email" style={s.input} value={emailDialogAn} onChange={e => setEmailDialogAn(e.target.value)} placeholder="empfaenger@beispiel.de" /></div>
            <div style={s.inputGroup}><label style={s.label}>Betreff</label><input style={s.input} value={emailDialogBetreff} onChange={e => setEmailDialogBetreff(e.target.value)} /></div>
            <div style={s.inputGroup}><label style={s.label}>Text</label><textarea style={{ ...s.input, height: 160 }} value={emailDialogText} onChange={e => setEmailDialogText(e.target.value)} /></div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={handleEmailDialogSchliessen} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleEmailSenden} disabled={emailSendetGerade} style={{ ...s.btn, width: "auto" }}>{emailSendetGerade ? "Sendet…" : "✉ Senden"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ STUDIO SEITE ============
function StudioSeite({ benutzer, rechte, navigiere, onMitgliedOeffnen, vorfallOeffnen, vorfallOeffnenNonce, eingebettet = false }) {
  const [news, setNews] = useState([]);
  const [newsFormOffen, setNewsFormOffen] = useState(false);
  const [newsTitel, setNewsTitel] = useState("");
  const [newsInhalt, setNewsInhalt] = useState("");
  const [newsAblauf, setNewsAblauf] = useState("");
  const [bearbeitenNews, setBearbeitenNews] = useState(null);
  const [reinigungStatus, setReinigungStatus] = useState([]);
  const [verlaufOffen, setVerlaufOffen] = useState(false);
  const [verlauf, setVerlauf] = useState([]);
  const [aktiveTab, setAktiveTab] = useState("news");
  // Direktsprung von einem Personendatensatz auf einen einzelnen Vorfallbericht (siehe
  // VorfallberichteEmbed/onVorfallOeffnen-Kette in HauptApp) - springt zuerst auf den Reiter,
  // die eigentliche Detailanzeige uebernimmt VorfallberichteSeite selbst ueber dieselbe Nonce.
  useEffect(() => { if (vorfallOeffnen) setAktiveTab("vorfaelle"); }, [vorfallOeffnenNonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const [bereicheFormOffen, setBereicheFormOffen] = useState(false);
  const [bearbeitenBereich, setBearbeitenBereich] = useState(null);
  const [bereichName, setBereichName] = useState("");
  const [bereichIntervallTyp, setBereichIntervallTyp] = useState("taeglich");
  const [bereichIntervallStunden, setBereichIntervallStunden] = useState("");
  const [bereichFristUhrzeit, setBereichFristUhrzeit] = useState("");
  const [bereichFehler, setBereichFehler] = useState("");

  const ladenNews = () => fetch(`${API}/news/`).then(r => r.json()).then(setNews).catch(() => {});
  const ladenStatus = () => fetch(`${API}/news/reinigung/status`).then(r => r.json()).then(setReinigungStatus).catch(() => {});

  useEffect(() => { ladenNews(); ladenStatus(); }, []);

  const handleNewsErstellen = async () => {
    if (!newsTitel || !newsInhalt) { alert("Bitte Titel und Inhalt eingeben!"); return; }
    const url = bearbeitenNews ? `${API}/news/${bearbeitenNews.id}` : `${API}/news/`;
    const method = bearbeitenNews ? "PUT" : "POST";
    await fetch(url, { method, headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ titel: newsTitel, inhalt: newsInhalt, erstellt_von: benutzer, ablauf_datum: newsAblauf || null }) });
    setNewsTitel(""); setNewsInhalt(""); setNewsAblauf(""); setNewsFormOffen(false); setBearbeitenNews(null);
    ladenNews();
  };

  const handleNewsLoeschen = async (id) => {
    if (!window.confirm("News löschen?")) return;
    const res = await fetch(`${API}/news/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladenNews();
  };

  const handleReinigung = async (bereichId) => {
    const res = await fetch(`${API}/news/reinigung/`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bereich_id: bereichId, gereinigt_von: benutzer }) });
    if (!res.ok) { alert("Konnte nicht als gereinigt markiert werden."); return; }
    ladenStatus();
  };

  const handleVerlaufLaden = async () => {
    const res = await fetch(`${API}/news/reinigung/verlauf`);
    setVerlauf(await res.json());
    setVerlaufOffen(true);
  };

  const zeitSeit = (datumStr) => {
    if (!datumStr) return "Noch nie";
    const [datumTeil, zeitTeil] = datumStr.split(" ");
    const [tag, monat, jahr] = datumTeil.split(".");
    const [h, m] = zeitTeil.split(":");
    const dann = new Date(jahr, monat-1, tag, h, m);
    const diff = Math.floor((new Date() - dann) / 60000);
    if (diff < 60) return `vor ${diff} Min.`;
    if (diff < 1440) return `vor ${Math.floor(diff/60)} Std.`;
    return `vor ${Math.floor(diff/1440)} Tag(en)`;
  };

  // Intervall-Dauer in Minuten je Typ - Grundlage fuer die Faelligkeitsberechnung statt der
  // frueheren festen 2h/8h-Schwellen, die fuer alle Bereiche identisch waren.
  const INTERVALL_MINUTEN = { taeglich: 1440, woechentlich: 10080, monatlich: 43200 };
  const intervallDauerMinuten = (b) => b.intervall_typ === "stunden" ? (b.intervall_stunden || 0) * 60 : (INTERVALL_MINUTEN[b.intervall_typ] || 1440);
  const INTERVALL_LABEL = { taeglich: "Täglich", woechentlich: "Wöchentlich", monatlich: "Monatlich" };
  const intervallLabel = (b) => {
    const basis = b.intervall_typ === "stunden" ? `Alle ${b.intervall_stunden} Stunden` : (INTERVALL_LABEL[b.intervall_typ] || b.intervall_typ);
    return b.frist_uhrzeit ? `${basis} · Frist ${b.frist_uhrzeit} Uhr` : basis;
  };

  // Faelligkeitszeitpunkt = letzte Reinigung + Intervall-Dauer; falls eine Frist-Uhrzeit gesetzt
  // ist, wird die Uhrzeit des Faelligkeitszeitpunkts darauf gesetzt (auf den naechsten Tag
  // verschoben, falls die Frist-Uhrzeit sonst das Intervall verkuerzen wuerde) - entspricht
  // "muss bis dann erledigt sein" statt einer minutengenauen Ablauffrist.
  const faelligkeitBerechnen = (b) => {
    const [datumTeil, zeitTeil] = b.zuletzt.gereinigt_am.split(" ");
    const [tag, monat, jahr] = datumTeil.split(".");
    const [h, m] = zeitTeil.split(":");
    const letzte = new Date(jahr, monat - 1, tag, h, m);
    const dauerMin = intervallDauerMinuten(b);
    let faellig = new Date(letzte.getTime() + dauerMin * 60000);
    if (b.frist_uhrzeit) {
      const [fh, fm] = b.frist_uhrzeit.split(":").map(Number);
      const mitFrist = new Date(faellig);
      mitFrist.setHours(fh, fm, 0, 0);
      if (mitFrist.getTime() < faellig.getTime()) mitFrist.setDate(mitFrist.getDate() + 1);
      faellig = mitFrist;
    }
    return { faellig, dauerMin };
  };

  const ampelFarbe = (b) => {
    if (!b.zuletzt) return THEME.colors.danger;
    const { faellig, dauerMin } = faelligkeitBerechnen(b);
    const restMin = (faellig.getTime() - new Date().getTime()) / 60000;
    if (restMin <= 0) return THEME.colors.danger;
    if (restMin <= dauerMin * 0.2) return THEME.colors.warning;
    return THEME.colors.success;
  };

  const istUeberfaellig = (b) => {
    if (!b.zuletzt) return true;
    const { faellig } = faelligkeitBerechnen(b);
    return faellig.getTime() - new Date().getTime() <= 0;
  };

  const oeffneBereichNeu = () => {
    setBearbeitenBereich(null); setBereichName(""); setBereichIntervallTyp("taeglich");
    setBereichIntervallStunden(""); setBereichFristUhrzeit(""); setBereichFehler("");
    setBereicheFormOffen(true);
  };

  const oeffneBereichBearbeiten = (b) => {
    setBearbeitenBereich(b); setBereichName(b.name); setBereichIntervallTyp(b.intervall_typ);
    setBereichIntervallStunden(b.intervall_stunden ? String(b.intervall_stunden) : "");
    setBereichFristUhrzeit(b.frist_uhrzeit || ""); setBereichFehler("");
    setBereicheFormOffen(true);
  };

  const handleBereichSpeichern = async () => {
    if (!bereichName.trim()) { setBereichFehler("Bitte einen Namen eingeben."); return; }
    if (bereichIntervallTyp === "stunden" && !bereichIntervallStunden) { setBereichFehler("Bitte die Stundenzahl angeben."); return; }
    const body = {
      name: bereichName.trim(), intervall_typ: bereichIntervallTyp,
      intervall_stunden: bereichIntervallTyp === "stunden" ? parseInt(bereichIntervallStunden, 10) : null,
      frist_uhrzeit: bereichFristUhrzeit || null,
    };
    const url = bearbeitenBereich ? `${API}/news/reinigung/bereiche/${bearbeitenBereich.id}` : `${API}/news/reinigung/bereiche`;
    const method = bearbeitenBereich ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) { setBereichFehler(typeof data.detail === "string" ? data.detail : "Fehler beim Speichern."); return; }
    setBereicheFormOffen(false); setBearbeitenBereich(null);
    ladenStatus();
  };

  const handleBereichLoeschen = async (b) => {
    if (!window.confirm(`Bereich "${b.name}" wirklich löschen?`)) return;
    const res = await fetch(`${API}/news/reinigung/bereiche/${b.id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladenStatus();
  };

  return (
    <div>
      {!eingebettet && <h1 style={s.heading}>Studio</h1>}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
        {[
          ["news", "📢 Schwarzes Brett", null], ["reinigung", "🧹 Reinigungsprotokoll", null], ["shelly", "⚡ Studio-Steuerung", null],
          ["kurse", "🎓 Kurse", "kurse"], ["emails", "✉ E-Mails", "emails"], ["aktionen", "🏷 Aktionen", "aktionen"],
          ["vorfaelle", "🚑 Vorfallberichte", "vorfaelle_ansehen"],
          ["aenderungswuensche", "✏️ Änderungswünsche", "__admin__"],
        ].filter(([, , modul]) => !modul || rechte?.rolle === "admin" || (modul !== "__admin__" && rechte?.module?.[modul])).map(([key, label]) => (
          <button key={key} onClick={() => setAktiveTab(key)}
            style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: aktiveTab === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: aktiveTab === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md, flexShrink: 0 }}>{label}</button>
        ))}
      </div>

      {/* SHELLY STEUERUNG */}
      {aktiveTab === "shelly" && <ShellySeite />}

      {/* KURSE */}
      {aktiveTab === "kurse" && <KursVerwaltung navigiere={navigiere} />}

      {/* E-MAILS */}
      {aktiveTab === "emails" && <EMailSeite />}

      {/* AKTIONEN */}
      {aktiveTab === "aktionen" && <AktionenSeite onMitgliedOeffnen={onMitgliedOeffnen} />}

      {/* VORFALL-/UNFALLBERICHTE */}
      {aktiveTab === "vorfaelle" && <VorfallberichteSeite rechte={rechte} onMitgliedOeffnen={onMitgliedOeffnen} vorfallOeffnen={vorfallOeffnen} vorfallOeffnenNonce={vorfallOeffnenNonce} />}

      {/* ÄNDERUNGSWÜNSCHE (Mitglieder-App) */}
      {aktiveTab === "aenderungswuensche" && <AenderungswuenscheSeite />}

      {/* SCHWARZES BRETT */}
      {aktiveTab === "news" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl2, margin: 0 }}>Schwarzes Brett</h2>
            <button onClick={() => { setBearbeitenNews(null); setNewsTitel(""); setNewsInhalt(""); setNewsAblauf(""); setNewsFormOffen(!newsFormOffen); }} style={{ ...s.btn, width: "auto" }}>+ Neue Nachricht</button>
          </div>

          {newsFormOffen && (
            <div style={{ ...s.karte, marginBottom: 16, borderLeftColor: THEME.colors.warning }}>
              <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>{bearbeitenNews ? "Nachricht bearbeiten" : "Neue Nachricht"}</h3>
              <div style={s.inputGroup}><label style={s.label}>Titel *</label><input style={s.input} value={newsTitel} onChange={e => setNewsTitel(e.target.value)} placeholder="z.B. Geräteinspektion Montag" /></div>
              <div style={s.inputGroup}><label style={s.label}>Nachricht *</label><textarea style={{ ...s.input, height: 100 }} value={newsInhalt} onChange={e => setNewsInhalt(e.target.value)} placeholder="Details..." /></div>
              <div style={s.inputGroup}><label style={s.label}>Ablaufdatum (optional)</label><input type="date" style={s.input} value={newsAblauf} onChange={e => setNewsAblauf(e.target.value)} /></div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setNewsFormOffen(false); setBearbeitenNews(null); }} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                <button onClick={handleNewsErstellen} style={{ ...s.btn, width: "auto" }}>{bearbeitenNews ? "Speichern" : "Veröffentlichen"}</button>
              </div>
            </div>
          )}

          {news.length === 0 && !newsFormOffen && (
            <div style={{ ...s.karte, textAlign: "center", padding: 40 }}>
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.lg }}>Noch keine Nachrichten.</p>
            </div>
          )}

          {news.map(n => (
            <div key={n.id} style={{ ...s.karte, marginBottom: 12, borderLeftColor: n.abgelaufen ? THEME.colors.textDisabled : THEME.colors.warning, opacity: n.abgelaufen ? 0.6 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.lg2 }}>{n.titel}</h3>
                    {n.abgelaufen && <span style={{ color: THEME.colors.textDisabled, fontSize: THEME.fontSize.xs, background: THEME.colors.surfaceMuted, padding: "2px 8px", borderRadius: 4 }}>Abgelaufen</span>}
                  </div>
                  <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, margin: "0 0 8px", whiteSpace: "pre-wrap" }}>{n.inhalt}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>
                    {n.erstellt_von} · {n.erstellt_am}
                    {n.ablauf_datum && ` · Läuft ab: ${datumDE(n.ablauf_datum)}`}
                  </p>
                </div>
                <div style={{ display: "flex", gap: 6, marginLeft: 12, flexShrink: 0 }}>
                  <button onClick={() => { setBearbeitenNews(n); setNewsTitel(n.titel); setNewsInhalt(n.inhalt); setNewsAblauf(n.ablauf_datum || ""); setNewsFormOffen(true); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>✏ Bearbeiten</button>
                  <button onClick={() => handleNewsLoeschen(n.id)} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg, fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>Löschen</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* REINIGUNGSPROTOKOLL */}
      {aktiveTab === "reinigung" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
            <h2 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl2, margin: 0 }}>Reinigungsprotokoll</h2>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={oeffneBereichNeu} style={{ ...s.btn, width: "auto" }}>+ Neuer Bereich</button>
              <button onClick={handleVerlaufLaden} style={{ ...s.btnGray, width: "auto" }}>📋 Verlauf anzeigen</button>
            </div>
          </div>

          {bereicheFormOffen && (
            <div style={{ ...s.karte, marginBottom: 16, borderLeftColor: THEME.colors.warning }}>
              <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>{bearbeitenBereich ? "Bereich bearbeiten" : "Neuer Bereich"}</h3>
              {bereichFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{bereichFehler}</p>}
              <div style={s.inputGroup}><label style={s.label}>Name *</label><input style={s.input} value={bereichName} onChange={e => setBereichName(e.target.value)} placeholder="z.B. Umkleiden Herren" /></div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ ...s.inputGroup, flex: 1, minWidth: 160 }}>
                  <label style={s.label}>Intervall</label>
                  <select style={s.input} value={bereichIntervallTyp} onChange={e => setBereichIntervallTyp(e.target.value)}>
                    <option value="taeglich">Täglich</option>
                    <option value="woechentlich">Wöchentlich</option>
                    <option value="monatlich">Monatlich</option>
                    <option value="stunden">Alle X Stunden</option>
                  </select>
                </div>
                {bereichIntervallTyp === "stunden" && (
                  <div style={{ ...s.inputGroup, flex: 1, minWidth: 120 }}>
                    <label style={s.label}>Stunden *</label>
                    <input style={s.input} type="number" min="1" value={bereichIntervallStunden} onChange={e => setBereichIntervallStunden(e.target.value)} placeholder="z.B. 6" />
                  </div>
                )}
                <div style={{ ...s.inputGroup, flex: 1, minWidth: 120 }}>
                  <label style={s.label}>Frist-Uhrzeit (optional)</label>
                  <input style={s.input} type="time" value={bereichFristUhrzeit} onChange={e => setBereichFristUhrzeit(e.target.value)} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setBereicheFormOffen(false); setBearbeitenBereich(null); }} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                <button onClick={handleBereichSpeichern} style={{ ...s.btn, width: "auto" }}>{bearbeitenBereich ? "Speichern" : "Anlegen"}</button>
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
            {reinigungStatus.map(b => (
              <div key={b.id} style={{ ...s.karte, borderLeftColor: ampelFarbe(b) }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.lg }}>{b.name}</h3>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", background: ampelFarbe(b), flexShrink: 0, marginTop: 4 }} />
                </div>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "0 0 6px" }}>{intervallLabel(b)}</p>
                {b.zuletzt ? (
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 10px" }}>
                    {zeitSeit(b.zuletzt.gereinigt_am)} · {b.zuletzt.gereinigt_von}<br />
                    <span style={{ fontSize: THEME.fontSize.xs }}>{b.zuletzt.gereinigt_am}</span>
                    {istUeberfaellig(b) && <><br /><span style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.xs, fontWeight: 700 }}>Überfällig</span></>}
                  </p>
                ) : (
                  <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: "0 0 10px" }}>Noch nie gereinigt</p>
                )}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => handleReinigung(b.id)} style={{ ...s.btn, width: "auto", padding: "8px 14px", fontSize: THEME.fontSize.base }}>
                    ✓ Jetzt gereinigt
                  </button>
                  <button onClick={() => oeffneBereichBearbeiten(b)} style={{ ...s.btnGray, width: "auto", padding: "8px 12px", fontSize: THEME.fontSize.sm }}>✏</button>
                  <button onClick={() => handleBereichLoeschen(b)} style={{ ...s.btnGray, width: "auto", padding: "8px 12px", fontSize: THEME.fontSize.sm, color: THEME.colors.danger }}>🗑</button>
                </div>
              </div>
            ))}
          </div>

          {verlaufOffen && (
            <div style={{ ...s.karte, marginTop: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl }}>Verlauf (letzte 50 Einträge)</h3>
                <button onClick={() => setVerlaufOffen(false)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>Schließen</button>
              </div>
              {verlauf.map((v, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "10px 0", borderTop: "1px solid #2b3d52" }}>
                  <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600 }}>{v.bereich}</span>
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base }}>{v.gereinigt_von}</span>
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base }}>{v.gereinigt_am}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ============ VORFALL-/UNFALLBERICHTE ============
// Neuer Reiter unter "Studio" (Nutzer-Wunsch 2026-08-22) - interne Dokumentation von Unfaellen,
// Verletzungen, Streitigkeiten, Sachschaeden etc. Backend: routers_vorfaelle.py. Rechte: sechs
// granulare Modul-Keys (vorfaelle_ansehen/erstellen/bearbeiten/abschliessen/loeschen/pdf_export),
// gleiches Muster wie TTLock. Personensuche (betroffene Person + Zeugen) nutzt den bestehenden,
// typuebergreifenden RfidZielAuswahlDialog wieder statt einer eigenen Suche.
const VORFALL_ORT_OPTIONEN = ["Trainingsfläche", "Milon-/Zirkelbereich", "Kursraum", "Sauna", "Dusche", "Umkleide", "Empfang", "Außenbereich", "Sonstiger Ort"];
const VORFALL_ART_OPTIONEN = ["Unfall", "Verletzung", "Medizinischer Zwischenfall", "Sturz", "Kreislaufproblem", "Streit/Auseinandersetzung", "Sachschaden", "Beschwerde", "Sicherheitsvorfall", "Sonstiger Vorfall"];
const VORFALL_MASSNAHMEN_OPTIONEN = ["Erste Hilfe geleistet", "Rettungsdienst verständigt", "Polizei verständigt", "Angehörige verständigt", "Person nach Hause geschickt", "Training beendet", "Bereich/Gerät gesperrt", "Keine Maßnahme erforderlich", "Sonstige Maßnahme"];
const VORFALL_STATUS_LABEL = { offen: "Offen", nachbearbeitung_erforderlich: "Nachbearbeitung erforderlich", abgeschlossen: "Abgeschlossen" };
const VORFALL_STATUS_FARBE = (st) => st === "abgeschlossen" ? THEME.colors.success : st === "nachbearbeitung_erforderlich" ? THEME.colors.warning : THEME.colors.danger;
const VORFALL_PERSON_TYP_LABEL = { mitglied: "Mitglied", wellpass: "Wellpass-Nutzer", hansefit: "Hansefit-Nutzer", sonstige: "Sonstige" };

// Kleiner Wrapper um den bestehenden UnterschriftPad: Unterschrift ist ueberall optional (siehe
// Anforderung Punkt 9), darum hier als "erst Button, dann Pad, dann Bestaetigung" statt fest
// eingeblendetem Zeichenfeld.
function VorfallUnterschriftFeld({ label, wert, onWertChange, hatteBereits }) {
  const [offen, setOffen] = useState(false);
  if (offen) {
    return (
      <div>
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginBottom: 6 }}>{label}</p>
        <UnterschriftPad onSave={(png) => { onWertChange(png); setOffen(false); }} onCancel={() => setOffen(false)} />
      </div>
    );
  }
  const vorhanden = !!wert || hatteBereits;
  return (
    <div>
      <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginBottom: 6 }}>{label}</p>
      {vorhanden && <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, margin: "0 0 6px" }}>✓ Unterschrieben</p>}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => setOffen(true)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>{vorhanden ? "Ändern" : "✍ Unterschreiben"}</button>
        {wert && <button onClick={() => onWertChange(null)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>Entfernen</button>}
      </div>
    </div>
  );
}

// Kompakte Liste fuer den Personendatensatz (Anforderung Punkt 14) - nur Datum/Art/Status/Verweis.
// onOeffnen ist optional: aktuell nur aus MitgliedDetail heraus mit echtem Sprung ins Detail
// verdrahtet (siehe onVorfallOeffnen-Kette in HauptApp), bei Wellpass/Hansefit/Sonstige zeigt die
// Liste sich (ohne Klick-Sprung), bis diese Seiten ebenfalls eine rechte-Prop samt Sprungziel
// bekommen - kann bei Bedarf leicht nachgezogen werden.
function VorfallberichteEmbed({ typ, personId, rechte, onOeffnen }) {
  const [liste, setListe] = useState([]);
  const darf = rechte?.rolle === "admin" || !!rechte?.module?.vorfaelle_ansehen;
  useEffect(() => {
    if (!darf || !personId) return;
    fetch(`${API}/vorfaelle/person/${typ}/${personId}`).then(r => r.ok ? r.json() : []).then(setListe).catch(() => {});
  }, [darf, typ, personId]);
  if (!darf) return null;
  return (
    <div style={{ ...s.karte, marginBottom: 16 }}>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>Vorfallberichte</h3>
      {liste.length === 0 ? (
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, margin: 0 }}>Keine dokumentierten Vorfälle.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {liste.map(v => (
            <div key={v.id} onClick={() => onOeffnen && onOeffnen(v.id)}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: THEME.colors.background, borderRadius: 8, padding: "10px 12px", cursor: onOeffnen ? "pointer" : "default" }}>
              <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm }}>{datumDE(v.datum)} · {v.art.join(", ")}</span>
              <span style={{ color: VORFALL_STATUS_FARBE(v.status), fontWeight: 700, fontSize: THEME.fontSize.xs }}>{VORFALL_STATUS_LABEL[v.status] || v.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VorfallDetailAnsicht({ vorfallId, rechte, onZurueck, onBearbeiten, onGeloescht, onMitgliedOeffnen }) {
  const [v, setV] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const pruefen = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", pruefen);
    return () => window.removeEventListener("resize", pruefen);
  }, []);

  const darf = (schluessel) => rechte?.rolle === "admin" || !!rechte?.module?.[schluessel];

  const laden = () => {
    setLaedt(true);
    fetch(`${API}/vorfaelle/${vorfallId}`).then(r => r.json()).then(d => { setV(d); setLaedt(false); }).catch(() => setLaedt(false));
  };
  useEffect(() => { laden(); }, [vorfallId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusAendern = async (neu) => {
    const res = await fetch(`${API}/vorfaelle/${vorfallId}/status`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: neu }) });
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Status ändern fehlgeschlagen."); return; }
    laden();
  };

  const handleLoeschen = async () => {
    if (!window.confirm(`Vorfall ${v.vorfallnummer} wirklich unwiderruflich löschen?`)) return;
    const res = await fetch(`${API}/vorfaelle/${vorfallId}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    onGeloescht();
  };

  const handlePdf = () => window.open(`${API}/vorfaelle/${vorfallId}/pdf`, "_blank");

  if (laedt || !v) return <p style={{ color: THEME.colors.textMuted }}>Lädt…</p>;

  const zeile = (label, wert) => wert ? (
    <div style={{ marginBottom: 10 }}>
      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "0 0 2px" }}>{label}</p>
      <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md, margin: 0, whiteSpace: "pre-wrap" }}>{wert}</p>
    </div>
  ) : null;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div>
          <button onClick={onZurueck} style={{ ...s.btnGray, width: "auto", marginBottom: 8 }}>← Zurück</button>
          <h2 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl2, margin: 0 }}>{v.vorfallnummer}</h2>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {darf("vorfaelle_pdf_export") && <button onClick={handlePdf} style={{ ...s.btnGray, width: "auto" }}>📄 PDF erstellen</button>}
          {darf("vorfaelle_bearbeiten") && <button onClick={onBearbeiten} style={{ ...s.btnGray, width: "auto" }}>✏ Bearbeiten</button>}
          {darf("vorfaelle_loeschen") && <button onClick={handleLoeschen} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg }}>Löschen</button>}
        </div>
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <span style={{ color: VORFALL_STATUS_FARBE(v.status), fontWeight: 700, fontSize: THEME.fontSize.lg }}>{VORFALL_STATUS_LABEL[v.status] || v.status}</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {darf("vorfaelle_bearbeiten") && v.status !== "abgeschlossen" && (
              <button onClick={() => handleStatusAendern(v.status === "offen" ? "nachbearbeitung_erforderlich" : "offen")} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>
                {v.status === "offen" ? "Als Nachbearbeitung erforderlich markieren" : "Als Offen markieren"}
              </button>
            )}
            {darf("vorfaelle_abschliessen") && v.status !== "abgeschlossen" && <button onClick={() => handleStatusAendern("abgeschlossen")} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>✓ Abschließen</button>}
            {darf("vorfaelle_abschliessen") && v.status === "abgeschlossen" && <button onClick={() => handleStatusAendern("offen")} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>Wieder öffnen</button>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
          {zeile("Datum/Uhrzeit", `${datumDE(v.datum)} · ${v.uhrzeit} Uhr`)}
          {zeile("Ort", v.ort)}
          {zeile("Art des Vorfalls", v.art.join(", "))}
        </div>
        {v.betroffene_person && (
          <div>
            {zeile("Betroffene Person", v.betroffene_person)}
            {v.betroffene_person_typ === "mitglied" && onMitgliedOeffnen && (
              <button onClick={() => onMitgliedOeffnen(v.betroffene_person_id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>Zum Mitglied →</button>
            )}
          </div>
        )}
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Beschreibung</h3>
        {zeile("Was ist passiert?", v.beschreibung_was)}
        {zeile("Wie kam es dazu?", v.beschreibung_wie)}
        {zeile("Verletzungen/Beschwerden", v.beschreibung_verletzungen)}
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Maßnahmen</h3>
        {zeile("Maßnahmen", v.massnahmen.join(", "))}
        {zeile("Weitere Informationen", v.massnahmen_text)}
        {zeile("Rettungsdienst verständigt", v.rettungsdienst_zeit)}
        {zeile("Rettungsdienst eingetroffen", v.rettungsdienst_eintreffen_zeit)}
      </div>

      {v.zeugen.length > 0 && (
        <div style={{ ...s.karte, marginBottom: 16 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Zeugen</h3>
          {v.zeugen.map(z => (
            <div key={z.id} style={{ background: THEME.colors.background, borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <p style={{ color: THEME.colors.text, margin: "0 0 4px", fontWeight: 600 }}>{z.vorname} {z.nachname}{z.telefon ? ` · ${z.telefon}` : ""}</p>
              {z.aussage && <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: 0, whiteSpace: "pre-wrap" }}>{z.aussage}</p>}
              {z.hat_unterschrift && <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xs, margin: "4px 0 0" }}>✓ Unterschrieben ({z.unterschrift_zeitpunkt})</p>}
            </div>
          ))}
        </div>
      )}

      {v.anwesende_mitarbeiter.length > 0 && (
        <div style={{ ...s.karte, marginBottom: 16 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Anwesende Mitarbeiter</h3>
          <p style={{ color: THEME.colors.textSecondary, margin: 0 }}>{v.anwesende_mitarbeiter.map(m => m.name).join(", ")}</p>
        </div>
      )}

      {(v.hat_unterschrift_betroffene || v.hat_unterschrift_mitarbeiter) && (
        <div style={{ ...s.karte, marginBottom: 16 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Unterschriften</h3>
          {v.hat_unterschrift_betroffene && <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>✓ Betroffene Person ({v.unterschrift_betroffene_zeitpunkt})</p>}
          {v.hat_unterschrift_mitarbeiter && <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, margin: 0 }}>✓ Mitarbeiter ({v.unterschrift_mitarbeiter_zeitpunkt})</p>}
        </div>
      )}

      {v.dateien.length > 0 && (
        <div style={{ ...s.karte, marginBottom: 16 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Bilder und Dokumente</h3>
          {v.dateien.map(d => (
            <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: THEME.colors.background, borderRadius: 8, padding: "8px 12px", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
              <a href={`${API}/vorfaelle/dateien/${d.id}/download`} target="_blank" rel="noreferrer" style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.sm }}>{d.dateiname}</a>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{d.hochgeladen_von} · {d.hochgeladen_am}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Nachbearbeitung</h3>
        {zeile("Erforderlich", v.nachbearbeitung_erforderlich ? "Ja" : "Nein")}
        {zeile("Wiedervorlage", v.wiedervorlage_datum ? datumDE(v.wiedervorlage_datum) : null)}
        {zeile("Zuständig", v.nachbearbeitung_zustaendig_name)}
        {zeile("Notiz", v.nachbearbeitung_notiz)}
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Erfassung</h3>
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: 0 }}>Aufgenommen von {v.aufgenommen_von} am {v.erstellt_am}{v.aktualisiert_am ? ` · zuletzt geändert am ${v.aktualisiert_am}` : ""}</p>
      </div>

      {v.verlauf.length > 0 && (
        <div style={{ ...s.karte, marginBottom: 16 }}>
          <h3 style={{ color: THEME.colors.text, margin: "0 0 12px", fontSize: THEME.fontSize.xl }}>Änderungsverlauf</h3>
          {v.verlauf.map((a, i) => (
            <div key={i} style={{ borderBottom: `1px solid ${THEME.colors.border}`, padding: "8px 0" }}>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm, margin: 0 }}><b>{a.feld}</b> von {a.benutzer_name} · {a.zeitpunkt}</p>
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "2px 0 0" }}>„{a.alter_wert}" → „{a.neuer_wert}"</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VorfallFormular({ vorfallId, onGespeichert, onAbbrechen }) {
  const bearbeiten = !!vorfallId;
  const [laedt, setLaedt] = useState(bearbeiten);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState("");
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const pruefen = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", pruefen);
    return () => window.removeEventListener("resize", pruefen);
  }, []);

  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [uhrzeit, setUhrzeit] = useState(new Date().toTimeString().slice(0, 5));
  const [ort, setOrt] = useState("");
  const [art, setArt] = useState([]);

  const [personModus, setPersonModus] = useState("keine"); // keine | vorhanden | extern
  const [betroffenePerson, setBetroffenePerson] = useState(null);
  const [personSucheOffen, setPersonSucheOffen] = useState(false);
  const [externVorname, setExternVorname] = useState("");
  const [externNachname, setExternNachname] = useState("");
  const [externTelefon, setExternTelefon] = useState("");
  const [externEmail, setExternEmail] = useState("");

  const [beschreibungWas, setBeschreibungWas] = useState("");
  const [beschreibungWie, setBeschreibungWie] = useState("");
  const [beschreibungVerletzungen, setBeschreibungVerletzungen] = useState("");

  const [massnahmen, setMassnahmen] = useState([]);
  const [massnahmenText, setMassnahmenText] = useState("");
  const [rettungsdienstZeit, setRettungsdienstZeit] = useState("");
  const [rettungsdienstEintreffenZeit, setRettungsdienstEintreffenZeit] = useState("");

  const [zeugen, setZeugen] = useState([]);

  const [mitarbeiterListe, setMitarbeiterListe] = useState([]);
  const [anwesendeMitarbeiter, setAnwesendeMitarbeiter] = useState([]);

  const [dateien, setDateien] = useState([]);
  const [neueDateien, setNeueDateien] = useState([]);

  const [unterschriftBetroffene, setUnterschriftBetroffene] = useState(null);
  const [unterschriftMitarbeiter, setUnterschriftMitarbeiter] = useState(null);

  const [nachbearbeitungErforderlich, setNachbearbeitungErforderlich] = useState(false);
  const [wiedervorlageDatum, setWiedervorlageDatum] = useState("");
  const [nachbearbeitungZustaendigId, setNachbearbeitungZustaendigId] = useState("");
  const [nachbearbeitungNotiz, setNachbearbeitungNotiz] = useState("");

  useEffect(() => {
    fetch(`${API}/benutzer/`).then(r => r.json()).then(d => setMitarbeiterListe(Array.isArray(d) ? d.filter(b => b.aktiv) : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!bearbeiten) return;
    fetch(`${API}/vorfaelle/${vorfallId}`).then(r => r.json()).then(v => {
      setDatum(v.datum || ""); setUhrzeit(v.uhrzeit || ""); setOrt(v.ort || ""); setArt(v.art || []);
      if (v.betroffene_person_typ === "extern") {
        setPersonModus("extern"); setExternVorname(v.extern_vorname || ""); setExternNachname(v.extern_nachname || "");
        setExternTelefon(v.extern_telefon || ""); setExternEmail(v.extern_email || "");
      } else if (v.betroffene_person_typ) {
        setPersonModus("vorhanden"); setBetroffenePerson({ typ: v.betroffene_person_typ, id: v.betroffene_person_id, name: v.betroffene_person });
      }
      setBeschreibungWas(v.beschreibung_was || ""); setBeschreibungWie(v.beschreibung_wie || ""); setBeschreibungVerletzungen(v.beschreibung_verletzungen || "");
      setMassnahmen(v.massnahmen || []); setMassnahmenText(v.massnahmen_text || "");
      setRettungsdienstZeit(v.rettungsdienst_zeit || ""); setRettungsdienstEintreffenZeit(v.rettungsdienst_eintreffen_zeit || "");
      setZeugen((v.zeugen || []).map(z => ({ vorname: z.vorname, nachname: z.nachname, person_typ: z.person_typ, person_id: z.person_id, telefon: z.telefon || "", aussage: z.aussage || "", unterschrift_png_base64: null, hatteBereitsUnterschrift: z.hat_unterschrift })));
      setAnwesendeMitarbeiter((v.anwesende_mitarbeiter || []).map(mm => mm.id));
      setDateien(v.dateien || []);
      setNachbearbeitungErforderlich(!!v.nachbearbeitung_erforderlich);
      setWiedervorlageDatum(v.wiedervorlage_datum || ""); setNachbearbeitungZustaendigId(v.nachbearbeitung_zustaendig_id || "");
      setNachbearbeitungNotiz(v.nachbearbeitung_notiz || "");
      setLaedt(false);
    }).catch(() => setLaedt(false));
  }, [vorfallId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleArt = (a) => setArt(l => l.includes(a) ? l.filter(x => x !== a) : [...l, a]);
  const toggleMassnahme = (m) => setMassnahmen(l => l.includes(m) ? l.filter(x => x !== m) : [...l, m]);
  const toggleMitarbeiter = (id) => setAnwesendeMitarbeiter(l => l.includes(id) ? l.filter(x => x !== id) : [...l, id]);

  const zeugeHinzufuegen = () => setZeugen(l => [...l, { vorname: "", nachname: "", person_typ: null, person_id: null, telefon: "", aussage: "", unterschrift_png_base64: null }]);
  const zeugeAendern = (i, feld, wert) => setZeugen(l => l.map((z, idx) => idx === i ? { ...z, [feld]: wert } : z));
  const zeugeEntfernen = (i) => setZeugen(l => l.filter((_, idx) => idx !== i));

  const handleDateiHochladen = async (datei) => {
    const fd = new FormData();
    fd.append("datei", datei);
    const res = await fetch(`${API}/vorfaelle/${vorfallId}/dateien`, { method: "POST", body: fd });
    if (!res.ok) { alert("Datei-Upload fehlgeschlagen."); return; }
    const neu = await res.json();
    setDateien(d => [...d, neu]);
  };

  const handleDateiLoeschen = async (dateiId) => {
    if (!window.confirm("Datei löschen?")) return;
    const res = await fetch(`${API}/vorfaelle/dateien/${dateiId}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    setDateien(d => d.filter(x => x.id !== dateiId));
  };

  const handleSpeichern = async () => {
    if (!datum || !uhrzeit || !ort.trim()) { setFehler("Bitte Datum, Uhrzeit und Ort angeben."); return; }
    if (art.length === 0) { setFehler("Bitte mindestens eine Art des Vorfalls auswählen."); return; }
    if (!beschreibungWas.trim()) { setFehler('Bitte "Was ist passiert?" ausfüllen.'); return; }
    setFehler(""); setSpeichert(true);

    const body = {
      datum, uhrzeit, ort: ort.trim(), art,
      betroffene_person_typ: personModus === "vorhanden" ? (betroffenePerson?.typ || null) : (personModus === "extern" ? "extern" : null),
      betroffene_person_id: personModus === "vorhanden" ? (betroffenePerson?.id || null) : null,
      extern_vorname: personModus === "extern" ? (externVorname || null) : null,
      extern_nachname: personModus === "extern" ? (externNachname || null) : null,
      extern_telefon: personModus === "extern" ? (externTelefon || null) : null,
      extern_email: personModus === "extern" ? (externEmail || null) : null,
      beschreibung_was: beschreibungWas, beschreibung_wie: beschreibungWie || null, beschreibung_verletzungen: beschreibungVerletzungen || null,
      massnahmen, massnahmen_text: massnahmenText || null,
      rettungsdienst_zeit: rettungsdienstZeit || null, rettungsdienst_eintreffen_zeit: rettungsdienstEintreffenZeit || null,
      anwesende_mitarbeiter: anwesendeMitarbeiter,
      nachbearbeitung_erforderlich: nachbearbeitungErforderlich,
      wiedervorlage_datum: nachbearbeitungErforderlich ? (wiedervorlageDatum || null) : null,
      nachbearbeitung_zustaendig_id: nachbearbeitungErforderlich && nachbearbeitungZustaendigId ? parseInt(nachbearbeitungZustaendigId, 10) : null,
      nachbearbeitung_notiz: nachbearbeitungErforderlich ? (nachbearbeitungNotiz || null) : null,
      unterschrift_betroffene_png_base64: unterschriftBetroffene,
      unterschrift_mitarbeiter_png_base64: unterschriftMitarbeiter,
      zeugen: zeugen.filter(z => z.vorname.trim() || z.nachname.trim()).map(z => ({
        vorname: z.vorname.trim(), nachname: z.nachname.trim(), person_typ: z.person_typ, person_id: z.person_id,
        telefon: z.telefon || null, aussage: z.aussage || null, unterschrift_png_base64: z.unterschrift_png_base64,
      })),
    };

    const url = bearbeiten ? `${API}/vorfaelle/${vorfallId}` : `${API}/vorfaelle/`;
    const res = await fetch(url, { method: bearbeiten ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setFehler(typeof data.detail === "string" ? data.detail : "Speichern fehlgeschlagen."); setSpeichert(false); return; }

    const zielId = bearbeiten ? vorfallId : data.id;
    if (!bearbeiten && neueDateien.length > 0) {
      for (const datei of neueDateien) {
        const fd = new FormData();
        fd.append("datei", datei);
        await fetch(`${API}/vorfaelle/${zielId}/dateien`, { method: "POST", body: fd });
      }
    }
    setSpeichert(false);
    onGespeichert(zielId);
  };

  if (laedt) return <p style={{ color: THEME.colors.textMuted }}>Lädt…</p>;

  return (
    <div>
      <h2 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl2, margin: "0 0 16px" }}>{bearbeiten ? "Vorfall bearbeiten" : "Neuer Vorfall"}</h2>
      {fehler && <div style={{ ...s.karte, borderLeftColor: THEME.colors.danger, marginBottom: 16 }}><p style={{ color: THEME.colors.danger, margin: 0 }}>{fehler}</p></div>}

      {personSucheOffen && (
        <RfidZielAuswahlDialog titel="Betroffene Person suchen" typLabel={VORFALL_PERSON_TYP_LABEL}
          onSchliessen={() => setPersonSucheOffen(false)}
          onAusgewaehlt={(p) => { setBetroffenePerson({ typ: p.typ, id: p.id, name: p.name }); setPersonSucheOffen(false); }} />
      )}

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>1. Allgemeine Angaben</h3>
        <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
          <div style={s.inputGroup}><label style={s.label}>Datum *</label><input type="date" style={s.input} value={datum} onChange={e => setDatum(e.target.value)} /></div>
          <div style={s.inputGroup}><label style={s.label}>Uhrzeit *</label><input type="time" style={s.input} value={uhrzeit} onChange={e => setUhrzeit(e.target.value)} /></div>
          <div style={s.inputGroup}><label style={s.label}>Ort im Studio *</label>
            <select style={s.input} value={ort} onChange={e => setOrt(e.target.value)}>
              <option value="">Bitte wählen…</option>
              {VORFALL_ORT_OPTIONEN.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>2. Art des Vorfalls *</h3>
        <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr 1fr", gap: 8 }}>
          {VORFALL_ART_OPTIONEN.map(a => (
            <label key={a} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer" }}>
              <input type="checkbox" checked={art.includes(a)} onChange={() => toggleArt(a)} />{a}
            </label>
          ))}
        </div>
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>3. Betroffene Person</h3>
        <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
          {[["keine", "Keine Angabe"], ["vorhanden", "Vorhandene Person"], ["extern", "Externe Person / Besucher"]].map(([k, l]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, color: THEME.colors.textSecondary, cursor: "pointer" }}>
              <input type="radio" name="personModus" checked={personModus === k} onChange={() => { setPersonModus(k); setBetroffenePerson(null); }} />{l}
            </label>
          ))}
        </div>
        {personModus === "vorhanden" && (
          betroffenePerson ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: THEME.colors.text }}>{betroffenePerson.name} <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>({VORFALL_PERSON_TYP_LABEL[betroffenePerson.typ] || betroffenePerson.typ})</span></span>
              <button onClick={() => setPersonSucheOffen(true)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>Ändern</button>
            </div>
          ) : (
            <button onClick={() => setPersonSucheOffen(true)} style={{ ...s.btn, width: "auto" }}>🔍 Person suchen</button>
          )
        )}
        {personModus === "extern" && (
          <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr", gap: 12 }}>
            <div style={s.inputGroup}><label style={s.label}>Vorname</label><input style={s.input} value={externVorname} onChange={e => setExternVorname(e.target.value)} /></div>
            <div style={s.inputGroup}><label style={s.label}>Nachname</label><input style={s.input} value={externNachname} onChange={e => setExternNachname(e.target.value)} /></div>
            <div style={s.inputGroup}><label style={s.label}>Telefonnummer (optional)</label><input style={s.input} value={externTelefon} onChange={e => setExternTelefon(e.target.value)} /></div>
            <div style={s.inputGroup}><label style={s.label}>E-Mail (optional)</label><input style={s.input} value={externEmail} onChange={e => setExternEmail(e.target.value)} /></div>
          </div>
        )}
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>4. Beschreibung</h3>
        <div style={s.inputGroup}><label style={s.label}>Was ist passiert? *</label><textarea style={{ ...s.input, height: 100 }} value={beschreibungWas} onChange={e => setBeschreibungWas(e.target.value)} /></div>
        <div style={s.inputGroup}><label style={s.label}>Wie kam es zu dem Vorfall?</label><textarea style={{ ...s.input, height: 80 }} value={beschreibungWie} onChange={e => setBeschreibungWie(e.target.value)} /></div>
        <div style={s.inputGroup}><label style={s.label}>Welche Verletzungen/Beschwerden wurden angegeben oder festgestellt? (optional)</label><textarea style={{ ...s.input, height: 80 }} value={beschreibungVerletzungen} onChange={e => setBeschreibungVerletzungen(e.target.value)} /></div>
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>5. Maßnahmen</h3>
        <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
          {VORFALL_MASSNAHMEN_OPTIONEN.map(m => (
            <label key={m} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer" }}>
              <input type="checkbox" checked={massnahmen.includes(m)} onChange={() => toggleMassnahme(m)} />{m}
            </label>
          ))}
        </div>
        <div style={s.inputGroup}><label style={s.label}>Durchgeführte Maßnahmen / weitere Informationen</label><textarea style={{ ...s.input, height: 80 }} value={massnahmenText} onChange={e => setMassnahmenText(e.target.value)} /></div>
        <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div style={s.inputGroup}><label style={s.label}>Zeitpunkt Rettungsdienst verständigt (optional)</label><input type="time" style={s.input} value={rettungsdienstZeit} onChange={e => setRettungsdienstZeit(e.target.value)} /></div>
          <div style={s.inputGroup}><label style={s.label}>Zeitpunkt Eintreffen Rettungsdienst (optional)</label><input type="time" style={s.input} value={rettungsdienstEintreffenZeit} onChange={e => setRettungsdienstEintreffenZeit(e.target.value)} /></div>
        </div>
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>6. Zeugen</h3>
        {zeugen.map((z, i) => (
          <div key={i} style={{ background: THEME.colors.background, borderRadius: 8, padding: 12, marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Zeuge {i + 1}{z.person_typ ? ` · verknüpft (${VORFALL_PERSON_TYP_LABEL[z.person_typ] || z.person_typ})` : ""}</span>
              <button onClick={() => zeugeEntfernen(i)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "4px 10px", background: THEME.colors.dangerBg }}>✗ Entfernen</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr", gap: 10, marginBottom: 8 }}>
              <input style={s.input} placeholder="Vorname" value={z.vorname} onChange={e => zeugeAendern(i, "vorname", e.target.value)} />
              <input style={s.input} placeholder="Nachname" value={z.nachname} onChange={e => zeugeAendern(i, "nachname", e.target.value)} />
              <input style={s.input} placeholder="Telefonnummer (optional)" value={z.telefon} onChange={e => zeugeAendern(i, "telefon", e.target.value)} />
            </div>
            <textarea style={{ ...s.input, height: 60, marginBottom: 8 }} placeholder="Kurze Aussage / Bemerkung (optional)" value={z.aussage} onChange={e => zeugeAendern(i, "aussage", e.target.value)} />
            <VorfallUnterschriftFeld label="Unterschrift Zeuge" wert={z.unterschrift_png_base64} hatteBereits={z.hatteBereitsUnterschrift} onWertChange={(w) => zeugeAendern(i, "unterschrift_png_base64", w)} />
          </div>
        ))}
        <button onClick={zeugeHinzufuegen} style={{ ...s.btnGray, width: "auto" }}>+ Zeuge hinzufügen</button>
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>7. Anwesende Mitarbeiter</h3>
        <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr 1fr", gap: 8 }}>
          {mitarbeiterListe.map(m => (
            <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer" }}>
              <input type="checkbox" checked={anwesendeMitarbeiter.includes(m.id)} onChange={() => toggleMitarbeiter(m.id)} />{m.name}
            </label>
          ))}
        </div>
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>8. Bilder und Dokumente (optional)</h3>
        {bearbeiten ? (
          <>
            {dateien.map(d => (
              <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: THEME.colors.background, borderRadius: 8, padding: "8px 12px", marginBottom: 8 }}>
                <a href={`${API}/vorfaelle/dateien/${d.id}/download`} target="_blank" rel="noreferrer" style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.sm }}>{d.dateiname}</a>
                <button onClick={() => handleDateiLoeschen(d.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "4px 10px", background: THEME.colors.dangerBg }}>✗</button>
              </div>
            ))}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={e => { if (e.target.files[0]) handleDateiHochladen(e.target.files[0]); e.target.value = ""; }} />
          </>
        ) : (
          <>
            {neueDateien.map((f, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: THEME.colors.background, borderRadius: 8, padding: "8px 12px", marginBottom: 8 }}>
                <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm }}>{f.name}</span>
                <button onClick={() => setNeueDateien(l => l.filter((_, idx) => idx !== i))} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "4px 10px", background: THEME.colors.dangerBg }}>✗</button>
              </div>
            ))}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={e => { if (e.target.files[0]) setNeueDateien(l => [...l, e.target.files[0]]); e.target.value = ""; }} />
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 6 }}>Wird nach dem Speichern hochgeladen.</p>
          </>
        )}
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>9. Unterschriften (optional)</h3>
        <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr", gap: 16 }}>
          <VorfallUnterschriftFeld label="Betroffene Person" wert={unterschriftBetroffene} onWertChange={setUnterschriftBetroffene} />
          <VorfallUnterschriftFeld label="Mitarbeiter" wert={unterschriftMitarbeiter} onWertChange={setUnterschriftMitarbeiter} />
        </div>
      </div>

      <div style={{ ...s.karte, marginBottom: 16 }}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>10. Nachbearbeitung</h3>
        <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, cursor: "pointer", marginBottom: 12 }}>
          <input type="checkbox" checked={nachbearbeitungErforderlich} onChange={e => setNachbearbeitungErforderlich(e.target.checked)} /> Nachbearbeitung erforderlich
        </label>
        {nachbearbeitungErforderlich && (
          <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr", gap: 12 }}>
            <div style={s.inputGroup}><label style={s.label}>Wiedervorlage-Datum</label><input type="date" style={s.input} value={wiedervorlageDatum} onChange={e => setWiedervorlageDatum(e.target.value)} /></div>
            <div style={s.inputGroup}><label style={s.label}>Zuständiger Mitarbeiter</label>
              <select style={s.input} value={nachbearbeitungZustaendigId} onChange={e => setNachbearbeitungZustaendigId(e.target.value)}>
                <option value="">Bitte wählen…</option>
                {mitarbeiterListe.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div style={{ ...s.inputGroup, gridColumn: "1 / -1" }}><label style={s.label}>Notiz</label><textarea style={{ ...s.input, height: 70 }} placeholder="z.B. Mitglied zurückrufen, Versicherung informieren, Gerät überprüfen…" value={nachbearbeitungNotiz} onChange={e => setNachbearbeitungNotiz(e.target.value)} /></div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onAbbrechen} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
        <button onClick={handleSpeichern} disabled={speichert} style={{ ...s.btn, width: "auto" }}>{speichert ? "Speichert…" : (bearbeiten ? "Speichern" : "Vorfall anlegen")}</button>
      </div>
    </div>
  );
}

// Mitglieder-App (my.comefit.de), Anforderung Punkt 10: vom Mitglied vorgeschlagene Profil-
// Aenderungen ueberschreiben nie direkt den Datensatz, sondern landen hier zur Pruefung
// (Backend: routers_member.py, /api/member/admin/aenderungswuensche - admin-only).
function AenderungswuenscheSeite() {
  const [liste, setListe] = useState([]);
  const [laedt, setLaedt] = useState(true);

  const laden = () => {
    setLaedt(true);
    fetch(`${API}/api/member/admin/aenderungswuensche`).then(r => r.ok ? r.json() : []).then(setListe).catch(() => setListe([])).finally(() => setLaedt(false));
  };
  useEffect(() => { laden(); }, []);

  const handleFreigeben = async (id) => {
    const res = await fetch(`${API}/api/member/admin/aenderungswuensche/${id}/freigeben`, { method: "PUT" });
    if (!res.ok) { alert("Konnte nicht übernommen werden."); return; }
    laden();
  };
  const handleAblehnen = async (id) => {
    if (!window.confirm("Änderungswunsch ablehnen?")) return;
    const res = await fetch(`${API}/api/member/admin/aenderungswuensche/${id}/ablehnen`, { method: "PUT" });
    if (!res.ok) { alert("Ablehnen fehlgeschlagen."); return; }
    laden();
  };

  const offen = liste.filter(e => e.status === "offen");
  const erledigt = liste.filter(e => e.status !== "offen");

  return (
    <div>
      <h2 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl2, margin: "0 0 16px" }}>Änderungswünsche aus der Mitglieder-App</h2>
      {laedt && <p style={{ color: THEME.colors.textMuted }}>Lädt…</p>}
      {!laedt && offen.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine offenen Änderungswünsche.</p>}
      {offen.map(e => (
        <div key={e.id} style={{ ...s.karte, marginBottom: 12, borderLeftColor: THEME.colors.warning }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
            <div>
              <p style={{ color: THEME.colors.text, fontWeight: 600, margin: "0 0 6px" }}>{e.mitglied_name} <span style={{ color: THEME.colors.textMuted, fontWeight: 400, fontSize: THEME.fontSize.xs }}>· {e.zeitpunkt}</span></p>
              {Object.entries(e.felder).map(([feld, wert]) => (
                <p key={feld} style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 2px" }}><b>{PROFIL_FELD_LABEL_DE[feld] || feld}:</b> {wert}</p>
              ))}
              {e.notiz && <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "4px 0 0", whiteSpace: "pre-wrap" }}>„{e.notiz}"</p>}
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button onClick={() => handleFreigeben(e.id)} style={{ ...s.btn, width: "auto", background: THEME.colors.success }}>✓ Übernehmen</button>
              <button onClick={() => handleAblehnen(e.id)} style={{ ...s.btnGray, width: "auto" }}>Ablehnen</button>
            </div>
          </div>
        </div>
      ))}
      {erledigt.length > 0 && (
        <details style={{ marginTop: 20 }}>
          <summary style={{ color: THEME.colors.textMuted, cursor: "pointer", fontSize: THEME.fontSize.sm }}>Bereits bearbeitet ({erledigt.length})</summary>
          {erledigt.map(e => (
            <div key={e.id} style={{ ...s.karte, marginTop: 10, opacity: 0.7 }}>
              <p style={{ color: THEME.colors.text, margin: "0 0 4px" }}>{e.mitglied_name} <span style={{ color: e.status === "freigegeben" ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.xs }}>{e.status === "freigegeben" ? "Übernommen" : "Abgelehnt"}</span></p>
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: 0 }}>von {e.bearbeitet_von} am {e.bearbeitet_am}</p>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

const PROFIL_FELD_LABEL_DE = { telefon: "Telefonnummer", email: "E-Mail", strasse: "Straße", plz: "PLZ", ort: "Ort", iban: "IBAN", kontoinhaber: "Kontoinhaber" };

function VorfallberichteSeite({ rechte, onMitgliedOeffnen, vorfallOeffnen, vorfallOeffnenNonce }) {
  const [liste, setListe] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [suche, setSuche] = useState("");
  const [von, setVon] = useState("");
  const [bis, setBis] = useState("");
  const [artFilter, setArtFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ansicht, setAnsicht] = useState("liste"); // liste | formular | detail
  const [aktiverVorfall, setAktiverVorfall] = useState(null);
  const [mobil, setMobil] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const pruefen = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", pruefen);
    return () => window.removeEventListener("resize", pruefen);
  }, []);

  const darf = (schluessel) => rechte?.rolle === "admin" || !!rechte?.module?.[schluessel];

  const laden = () => {
    setLaedt(true);
    const params = new URLSearchParams();
    if (suche.trim()) params.set("suche", suche.trim());
    if (von) params.set("von", von);
    if (bis) params.set("bis", bis);
    if (artFilter) params.set("art", artFilter);
    if (statusFilter) params.set("status", statusFilter);
    fetch(`${API}/vorfaelle/?${params.toString()}`)
      .then(r => r.ok ? r.json() : [])
      .then(setListe)
      .catch(() => setListe([]))
      .finally(() => setLaedt(false));
  };

  useEffect(() => { if (ansicht === "liste") laden(); }, [ansicht, suche, von, bis, artFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Direktsprung aus einem Personendatensatz heraus (siehe VorfallberichteEmbed/onVorfallOeffnen-Kette).
  useEffect(() => {
    if (vorfallOeffnen) { setAktiverVorfall(vorfallOeffnen); setAnsicht("detail"); }
  }, [vorfallOeffnenNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!darf("vorfaelle_ansehen")) {
    return <div style={{ ...s.karte, textAlign: "center", padding: 40 }}><p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.lg }}>Keine Berechtigung für Vorfallberichte.</p></div>;
  }

  if (ansicht === "formular") {
    return <VorfallFormular vorfallId={aktiverVorfall} onGespeichert={(id) => { setAktiverVorfall(id); setAnsicht("detail"); }} onAbbrechen={() => setAnsicht(aktiverVorfall ? "detail" : "liste")} />;
  }
  if (ansicht === "detail") {
    return <VorfallDetailAnsicht vorfallId={aktiverVorfall} rechte={rechte} onMitgliedOeffnen={onMitgliedOeffnen}
      onZurueck={() => { setAnsicht("liste"); setAktiverVorfall(null); }}
      onBearbeiten={() => setAnsicht("formular")}
      onGeloescht={() => { setAnsicht("liste"); setAktiverVorfall(null); }} />;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl2, margin: 0 }}>Vorfall-/Unfallberichte</h2>
        {darf("vorfaelle_erstellen") && <button onClick={() => { setAktiverVorfall(null); setAnsicht("formular"); }} style={{ ...s.btn, width: "auto" }}>+ Neuer Vorfall</button>}
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <input style={{ ...s.input, flex: 1, minWidth: 180 }} placeholder="Suche nach Name…" value={suche} onChange={e => setSuche(e.target.value)} />
        <input type="date" style={{ ...s.input, width: 150 }} value={von} onChange={e => setVon(e.target.value)} title="Zeitraum von" />
        <input type="date" style={{ ...s.input, width: 150 }} value={bis} onChange={e => setBis(e.target.value)} title="Zeitraum bis" />
        <select style={{ ...s.input, width: 200 }} value={artFilter} onChange={e => setArtFilter(e.target.value)}>
          <option value="">Art: alle</option>
          {VORFALL_ART_OPTIONEN.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select style={{ ...s.input, width: 220 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Status: alle</option>
          {Object.entries(VORFALL_STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>

      {laedt && <p style={{ color: THEME.colors.textMuted }}>Lädt…</p>}
      {!laedt && (
        <div style={{ overflowX: "auto" }}>
          <div style={{ ...s.tabelle, minWidth: mobil ? "auto" : 950 }}>
            <div style={mobil
              ? { ...s.tabelleHeader, gridTemplateColumns: "1fr 1fr 1fr" }
              : { ...s.tabelleHeader, gridTemplateColumns: "0.8fr 0.6fr 1.3fr 1.2fr 0.9fr 1fr 1.2fr" }}>
              {(mobil
                ? [["datum", "Datum"], ["art", "Art"], ["status", "Status"]]
                : [["datum", "Datum"], ["uhrzeit", "Uhrzeit"], ["art", "Art"], ["betroffene_person", "Betroffene Person"], ["ort", "Ort"], ["aufgenommen_von", "Aufgenommen von"], ["status", "Status"]]
              ).map(([key, label]) => <span key={key}>{label}</span>)}
            </div>
            {liste.length === 0 && <p style={{ color: THEME.colors.textMuted, padding: 20, fontSize: THEME.fontSize.lg }}>Keine Vorfälle gefunden.</p>}
            {liste.map(v => (
              <div key={v.id} onClick={() => { setAktiverVorfall(v.id); setAnsicht("detail"); }}
                style={mobil
                  ? { ...s.tabelleZeile, gridTemplateColumns: "1fr 1fr 1fr", cursor: "pointer" }
                  : { ...s.tabelleZeile, gridTemplateColumns: "0.8fr 0.6fr 1.3fr 1.2fr 0.9fr 1fr 1.2fr", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = "#1f2f42"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{datumDE(v.datum)}</span>
                {!mobil && <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>{v.uhrzeit}</span>}
                <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>{v.art.join(", ")}</span>
                {!mobil && <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md }}>{v.betroffene_person || "–"}</span>}
                {!mobil && <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>{v.ort}</span>}
                {!mobil && <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>{v.aufgenommen_von || "–"}</span>}
                <span style={{ color: VORFALL_STATUS_FARBE(v.status), fontWeight: 700, fontSize: THEME.fontSize.md }}>{VORFALL_STATUS_LABEL[v.status] || v.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ KALENDER SEITE ============
// Der "+ Termin"-Knopf im Dashboard-Kopf wechselt zum Kalender und soll dort gleich "Neuer Termin" oeffnen
let kalenderNeuerTerminBeimOeffnen = false;
const KALENDER_LABELS = { "info@comefit.de": "Termine", "e.lauria93.el@gmail.com": "Schichtplan" };
const kalenderLabel = (id) => KALENDER_LABELS[id] || id || "";

function KalenderSeite({ mitgliedOeffnen }) {
  const [verbunden, setVerbunden] = useState(null);
  const [termine, setTermine] = useState([]);
  const [urlaubKal, setUrlaubKal] = useState({}); // "YYYY-MM-DD" -> Kuerzel mit genehmigtem Urlaub/Betriebsferien (Anzeige "AK U")
  const [kalender, setKalender] = useState([]);
  const [gewaehlteKalenderIds, setGewaehlteKalenderIds] = useState(Object.keys(KALENDER_LABELS));
  const [ansicht, setAnsicht] = useState("woche");
  const [aktuellesDatum, setAktuellesDatum] = useState(new Date());
  const [terminDialog, setTerminDialog] = useState(false);
  const [bearbeitenTermin, setBearbeitenTermin] = useState(null);
  const [form, setForm] = useState({ titel: "", start: "", ende: "", beschreibung: "", ort: "", ganztaegig: false, kalender_id: "info@comefit.de" });
  const [laedt, setLaedt] = useState(false);
  const [calendlyZeiten, setCalendlyZeiten] = useState([]);
  const [calendlyDauer, setCalendlyDauer] = useState(30);
  const [calendlyOffset, setCalendlyOffset] = useState(0);
  const [calendlySchichten, setCalendlySchichten] = useState({});
  const datumKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [ziehStart, setZiehStart] = useState(null);
  const [ziehAktuell, setZiehAktuell] = useState(null);
  const [mobil, setMobil] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const pruefen = () => setMobil(window.innerWidth < 1024);
    window.addEventListener("resize", pruefen);
    return () => window.removeEventListener("resize", pruefen);
  }, []);

  // Stundenraster fuellt am Desktop die Seite bis unten (Nutzer-Wunsch 2026-10-06): verfuegbare Hoehe
  // = Fensterhoehe minus Abstand des Rasters zum Seitenanfang, daraus ergibt sich die Stundenhoehe
  // (siehe STUNDE_HOEHE unten) - auf kleinen Bildschirmen bleibt es bei der Mindesthoehe und scrollt.
  const rasterRef = useRef(null);
  const [verfuegbareHoehe, setVerfuegbareHoehe] = useState(640);
  useEffect(() => {
    const messen = () => {
      if (!rasterRef.current) return;
      const oben = rasterRef.current.getBoundingClientRect().top + window.scrollY;
      setVerfuegbareHoehe(Math.max(420, window.innerHeight - oben - 24));
    };
    messen();
    const t = setTimeout(messen, 150);
    window.addEventListener("resize", messen);
    return () => { clearTimeout(t); window.removeEventListener("resize", messen); };
  }, [laedt, ansicht, termine, verbunden]);

  useEffect(() => {
    fetch(`${API}/kalender/status`).then(r => r.json()).then(d => setVerbunden(d.verbunden)).catch(() => setVerbunden(false));
  }, []);

  useEffect(() => { if (terminDialog) setCalendlyOffset(0); }, [terminDialog]);

  useEffect(() => {
    if (!kalenderNeuerTerminBeimOeffnen) return;
    kalenderNeuerTerminBeimOeffnen = false;
    setBearbeitenTermin(null);
    setForm({ titel: "", start: "", ende: "", beschreibung: "", ort: "", ganztaegig: false, kalender_id: "info@comefit.de" });
    setTerminDialog(true);
  }, []);

  useEffect(() => {
    if (terminDialog && !bearbeitenTermin) {
      fetch(`${API}/calendly/event-types`).then(r => r.json()).then(typen => { if (typen[0]) setCalendlyDauer(typen[0].dauer_minuten); }).catch(() => {});
      const von = new Date(Date.now() + calendlyOffset * 7 * 86400000);
      const bis = new Date(von.getTime() + 7 * 86400000);
      fetch(`${API}/calendly/verfuegbare-zeiten?von=${von.toISOString().slice(0,19)}&bis=${bis.toISOString().slice(0,19)}`)
        .then(r => r.json()).then(d => setCalendlyZeiten(Array.isArray(d) ? d : [])).catch(() => setCalendlyZeiten([]));
      // Schicht-Kuerzel (AK, JM, ...) der angezeigten Tage, damit man beim Vergeben eines Termins sieht, wer arbeitet
      const schichtKalenderId = Object.keys(KALENDER_LABELS).find(id => KALENDER_LABELS[id] === "Schichtplan");
      const tagVon = new Date(von); tagVon.setHours(0, 0, 0, 0);
      const tagBis = new Date(tagVon.getTime() + 8 * 86400000);
      let abgebrochen = false;
      fetch(`${API}/kalender/termine?von=${tagVon.toISOString().slice(0,19)}&bis=${tagBis.toISOString().slice(0,19)}&kalender_id=${schichtKalenderId}`)
        .then(r => r.json()).then(d => {
          if (abgebrochen) return;
          const proTag = {};
          (Array.isArray(d) ? d : []).forEach(t => {
            if (!/^[A-ZÄÖÜ]{2,3}$/.test((t.titel || "").trim())) return; // nur Kuerzel, keine Hinweise wie "1-2 Termine max."
            const tag = datumKey(new Date(t.start));
            (proTag[tag] = proTag[tag] || []).push(t.titel.trim());
          });
          Object.keys(proTag).forEach(k => { proTag[k] = [...new Set(proTag[k])].sort(); });
          setCalendlySchichten(proTag);
        }).catch(() => { if (!abgebrochen) setCalendlySchichten({}); });
      return () => { abgebrochen = true; };
    }
  }, [terminDialog, bearbeitenTermin, calendlyOffset]);

  const calendlySlotWaehlen = (startIso) => {
    const start = new Date(startIso);
    const ende = new Date(start.getTime() + calendlyDauer * 60000);
    const fmt = (d) => { const p = n => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
    setForm(f => ({ ...f, start: fmt(start), ende: fmt(ende) }));
  };

  useEffect(() => {
    if (verbunden) {
      fetch(`${API}/kalender/kalender-liste`).then(r => r.json()).then(setKalender).catch(() => {});
      ladenTermine();
    }
  }, [verbunden, aktuellesDatum, ansicht, gewaehlteKalenderIds]);

  const ladenTermine = async () => {
    setLaedt(true);
    let von, bis;
    const d = new Date(aktuellesDatum);
    if (ansicht === "tag") {
      von = new Date(d); von.setHours(0,0,0,0);
      bis = new Date(d); bis.setHours(23,59,59,0);
    } else if (ansicht === "woche") {
      const tag = d.getDay();
      von = new Date(d); von.setDate(d.getDate() - tag + (tag === 0 ? -6 : 1)); von.setHours(0,0,0,0);
      bis = new Date(von); bis.setDate(von.getDate() + 6); bis.setHours(23,59,59,0);
    } else {
      von = new Date(d.getFullYear(), d.getMonth(), 1);
      bis = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
    }
    try {
      const ergebnisse = await Promise.all(gewaehlteKalenderIds.map(id =>
        fetch(`${API}/kalender/termine?von=${von.toISOString().slice(0,19)}&bis=${bis.toISOString().slice(0,19)}&kalender_id=${id}`).then(r => r.json())
      ));
      setTermine(ergebnisse.flat());
    } catch {}
    try {
      const abw = await fetch(`${API}/urlaub/abwesenheiten?von=${datumKey(von)}&bis=${datumKey(bis)}`).then(r => r.json());
      const proTag = {};
      (Array.isArray(abw) ? abw : []).filter(a => a.kuerzel && (a.art === "urlaub" || a.art === "betriebsferien")).forEach(a => { (proTag[a.datum] = proTag[a.datum] || []).push(a.kuerzel); });
      setUrlaubKal(proTag);
    } catch { setUrlaubKal({}); }
    setLaedt(false);
  };

  const toggleKalender = (id) => {
    setGewaehlteKalenderIds(alte => alte.includes(id) ? alte.filter(x => x !== id) : [...alte, id]);
  };

  const handleAuth = async () => {
    const res = await fetch(`${API}/kalender/auth`);
    const data = await res.json();
    window.open(data.auth_url, "_blank");
    setTimeout(() => {
      fetch(`${API}/kalender/status`).then(r => r.json()).then(d => setVerbunden(d.verbunden));
    }, 10000);
  };

  const handleSpeichern = async () => {
    if (!form.titel || !form.start) { alert("Bitte Titel und Startzeit eingeben!"); return; }
    const payload = { ...form, kalender_id: bearbeitenTermin ? bearbeitenTermin.kalender_id : form.kalender_id };
    const res = bearbeitenTermin
      ? await fetch(`${API}/kalender/termine/${bearbeitenTermin.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
      : await fetch(`${API}/kalender/termine`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Termin konnte nicht gespeichert werden."); return; }
    setTerminDialog(false); setBearbeitenTermin(null); setForm({ titel: "", start: "", ende: "", beschreibung: "", ort: "", ganztaegig: false, kalender_id: "info@comefit.de" });
    ladenTermine();
  };

  const handleBearbeiten = (termin) => {
    setBearbeitenTermin(termin);
    setForm({ titel: termin.titel, start: termin.start?.slice(0,16) || "", ende: termin.ende?.slice(0,16) || "", beschreibung: termin.beschreibung || "", ort: termin.ort || "", ganztaegig: termin.ganztaegig, kalender_id: termin.kalender_id });
    setTerminDialog(true);
  };

  const navigiere = (richtung) => {
    const d = new Date(aktuellesDatum);
    if (ansicht === "tag") d.setDate(d.getDate() + richtung);
    else if (ansicht === "woche") d.setDate(d.getDate() + richtung * 7);
    else d.setMonth(d.getMonth() + richtung);
    setAktuellesDatum(d);
  };

  const WOCHENTAGE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const MONATE_LANG = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

  const titelAnzeige = () => {
    const d = aktuellesDatum;
    if (ansicht === "tag") return `${d.getDate()}. ${MONATE_LANG[d.getMonth()]} ${d.getFullYear()}`;
    if (ansicht === "monat") return `${MONATE_LANG[d.getMonth()]} ${d.getFullYear()}`;
    return `KW – ${MONATE_LANG[d.getMonth()]} ${d.getFullYear()}`;
  };

  const FARBEN = [THEME.colors.primary, THEME.colors.accentViolet, THEME.colors.accentGreenDark, "#ea580c", THEME.colors.accentPink, THEME.colors.accentCyan, "#65a30d", "#dc2626"];
  const terminFarbe = (t) => {
    if (t.kalender_id === SCHICHT_KALENDER_ID && (t.titel || "").trim() === "AK") return "#7e57c2"; // AK-Schichten immer in derselben Farbe (Lila), Nutzerwunsch 29.10.2026
    const key = String(t.id || t.titel || "");
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) % 997;
    return FARBEN[hash % FARBEN.length];
  };
  const parseStart = (t) => new Date(t.start);
  const parseEnde = (t) => new Date(t.ende || t.start);
  const heutTag = new Date();

  const tage = ansicht === "monat" ? [] : ansicht === "tag" ? [new Date(aktuellesDatum)] : (() => {
    const d = new Date(aktuellesDatum);
    const tagIdx = d.getDay();
    const montag = new Date(d); montag.setDate(d.getDate() - tagIdx + (tagIdx === 0 ? -6 : 1)); montag.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => { const t = new Date(montag); t.setDate(montag.getDate() + i); return t; });
  })();

  const terminePerTag = (tag) => termine.filter(t => !t.ganztaegig && parseStart(t).toDateString() === tag.toDateString());
  const ganztagPerTag = (tag) => termine.filter(t => t.ganztaegig && parseStart(t).toDateString() === tag.toDateString());

  const layoutTag = (evts) => {
    const sortiert = [...evts].sort((a, b) => parseStart(a) - parseStart(b));
    // Spaltenanzahl pro Block ueberlappender Termine (nicht pro Tag), damit einzelne Termine volle Breite behalten
    const ergebnis = [];
    let block = [], spalten = [], blockEnde = null;
    const blockAbschliessen = () => {
      const gesamt = spalten.length || 1;
      block.forEach(ev => ergebnis.push({ ...ev, _gesamtSpalten: gesamt }));
      block = []; spalten = []; blockEnde = null;
    };
    sortiert.forEach(ev => {
      const start = parseStart(ev), ende = parseEnde(ev);
      if (blockEnde !== null && start >= blockEnde) blockAbschliessen();
      let idx = spalten.findIndex(endeZeit => endeZeit <= start);
      if (idx === -1) { idx = spalten.length; spalten.push(ende); } else spalten[idx] = ende;
      block.push({ ...ev, _spalte: idx });
      if (blockEnde === null || ende > blockEnde) blockEnde = ende;
    });
    blockAbschliessen();
    return ergebnis;
  };

  // Schichten (nur Kuerzel, z.B. "AK") liegen als schmaler Streifen links im Tag, Termine bekommen die restliche Breite
  const SCHICHT_KALENDER_ID = Object.keys(KALENDER_LABELS).find(id => KALENDER_LABELS[id] === "Schichtplan");
  const SCHICHT_BREITE = mobil ? 20 : 28;

  const stundeStart = 9, stundeEnde = 22;
  const STUNDE_HOEHE = mobil ? 52 : Math.max(52, Math.floor(verfuegbareHoehe / (stundeEnde - stundeStart)));
  const SPERR_START = 12, SPERR_ENDE = 16;

  const fmtDatetimeLocal = (d) => { const p = n => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
  const stdZuDatum = (tag, std) => { const d = new Date(tag); const h = Math.floor(std); const m = Math.round((std - h) * 60); d.setHours(h, m, 0, 0); return d; };
  const stdVonMausEvent = (e, rect) => {
    const y = e.clientY - rect.top;
    let std = stundeStart + y / STUNDE_HOEHE;
    std = Math.round(std * 4) / 4;
    return Math.min(Math.max(std, stundeStart), stundeEnde);
  };

  const starteZiehen = (e, tag) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const std = stdVonMausEvent(e, rect);
    setZiehStart({ tag, rect, startStd: std });
    setZiehAktuell(std);
  };

  // Auf dem Handy funktioniert Ziehen-zum-Erstellen nicht zuverlaessig (Touch-Scroll
  // kollidiert mit dem Drag) - stattdessen legt ein einfaches Antippen direkt einen
  // 1-Stunden-Termin an der angetippten Uhrzeit an.
  const tippeNeuerTermin = (e, tag) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const std = stdVonMausEvent(e, rect);
    setForm({ titel: "", start: fmtDatetimeLocal(stdZuDatum(tag, std)), ende: fmtDatetimeLocal(stdZuDatum(tag, std + 1)), beschreibung: "", ort: "", ganztaegig: false, kalender_id: "info@comefit.de" });
    setBearbeitenTermin(null);
    setTerminDialog(true);
  };

  useEffect(() => {
    if (!ziehStart) return;
    const onMove = (e) => setZiehAktuell(stdVonMausEvent(e, ziehStart.rect));
    const onUp = (e) => {
      const endStd = stdVonMausEvent(e, ziehStart.rect);
      let start = ziehStart.startStd, ende = endStd;
      if (ende < start) { const t = start; start = ende; ende = t; }
      if (ende - start < 0.25) ende = start + 1;
      setForm({ titel: "", start: fmtDatetimeLocal(stdZuDatum(ziehStart.tag, start)), ende: fmtDatetimeLocal(stdZuDatum(ziehStart.tag, ende)), beschreibung: "", ort: "", ganztaegig: false, kalender_id: "info@comefit.de" });
      setBearbeitenTermin(null);
      setTerminDialog(true);
      setZiehStart(null); setZiehAktuell(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [ziehStart]);

  if (verbunden === null) return <div style={{ color: THEME.colors.textMuted, padding: 40, textAlign: "center" }}>Verbindung prüfen...</div>;

  if (!verbunden) return (
    <div style={{ maxWidth: 500, margin: "60px auto", textAlign: "center" }}>
      <h1 style={s.heading}>Google Kalender</h1>
      <div style={{ ...s.karte, padding: 40 }}>
        <p style={{ fontSize: THEME.fontSize.hero, margin: "0 0 16px" }}>📅</p>
        <h2 style={{ color: THEME.colors.text, marginBottom: 12 }}>Kalender verbinden</h2>
        <p style={{ color: THEME.colors.textMuted, marginBottom: 24 }}>Verbinde deinen Google Kalender um Termine direkt in ComeFit zu sehen und zu verwalten.</p>
        <button onClick={handleAuth} style={{ ...s.btn, width: "auto", padding: "14px 28px", fontSize: THEME.fontSize.lg }}>
          Mit Google verbinden
        </button>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 16 }}>Ein neues Fenster öffnet sich für die Google Anmeldung.</p>
      </div>
    </div>
  );

  return (
    <div>
      {terminDialog && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: mobil ? "95vw" : "min(1100px, 95vw)", padding: mobil ? "1.25rem" : "2.5rem", maxHeight: "92vh", overflowY: "auto" }}>
            <h2 style={{ ...s.modalTitel, fontSize: THEME.fontSize.xxl2, marginBottom: 24 }}>{bearbeitenTermin ? "Termin bearbeiten" : "Neuer Termin"}</h2>
            {bearbeitenTermin
              ? <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>Kalender: {kalenderLabel(bearbeitenTermin.kalender_id)}</p>
              : (
                <div style={s.inputGroup}>
                  <label style={s.label}>Kalender</label>
                  <select style={s.input} value={form.kalender_id} onChange={e => setForm(f => ({...f, kalender_id: e.target.value}))}>
                    {Object.keys(KALENDER_LABELS).map(id => <option key={id} value={id}>{kalenderLabel(id)}</option>)}
                  </select>
                </div>
              )}
            <div style={s.inputGroup}><label style={s.label}>Titel *</label><input style={s.input} value={form.titel} onChange={e => setForm(f => ({...f, titel: e.target.value}))} /></div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, marginBottom: 16, cursor: "pointer" }}>
              <input type="checkbox" checked={form.ganztaegig} onChange={e => setForm(f => ({...f, ganztaegig: e.target.checked}))} />
              Ganztägig
            </label>
            {!bearbeitenTermin && !form.ganztaegig && (
              <div style={{ marginBottom: 16, padding: 12, background: THEME.colors.background, borderRadius: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>📅 Freie Zeiten laut Calendly (anklicken zum Übernehmen):</p>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button type="button" disabled={calendlyOffset <= 0} onClick={() => setCalendlyOffset(o => o - 1)} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", fontSize: THEME.fontSize.xs, opacity: calendlyOffset <= 0 ? 0.4 : 1 }}>◀</button>
                    <button type="button" onClick={() => setCalendlyOffset(o => o + 1)} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", fontSize: THEME.fontSize.xs }}>▶</button>
                  </div>
                </div>
                {calendlyZeiten.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>Keine freien Zeiten in dieser Woche.</p>}
                {Object.entries(calendlyZeiten.reduce((acc, z) => {
                  const tag = new Date(z.start).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
                  (acc[tag] = acc[tag] || []).push(z);
                  return acc;
                }, {})).map(([tag, zeiten]) => (
                  <div key={tag} style={{ marginBottom: 8 }}>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.xs, margin: "0 0 4px", fontWeight: 600 }}>
                      {tag}
                      {(calendlySchichten[datumKey(new Date(zeiten[0].start))] || []).length > 0 && (
                        <span style={{ color: THEME.colors.textMuted, fontWeight: 700 }}> · {calendlySchichten[datumKey(new Date(zeiten[0].start))].join(", ")}</span>
                      )}
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {zeiten.map(z => (
                        <button key={z.start} onClick={() => calendlySlotWaehlen(z.start)} style={{ background: THEME.colors.surfaceMuted, border: "none", borderRadius: 6, padding: "4px 10px", color: THEME.colors.text, fontSize: THEME.fontSize.xs, cursor: "pointer" }}>
                          {new Date(z.start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: mobil ? "1fr" : "1fr 1fr", gap: mobil ? 12 : 24, marginBottom: 8 }}>
              <div style={s.inputGroup}><label style={s.label}>Start *</label><input style={s.input} type={form.ganztaegig ? "date" : "datetime-local"} value={form.start} onChange={e => setForm(f => ({...f, start: e.target.value}))} /></div>
              <div style={s.inputGroup}><label style={s.label}>Ende</label><input style={s.input} type={form.ganztaegig ? "date" : "datetime-local"} value={form.ende} onChange={e => setForm(f => ({...f, ende: e.target.value}))} /></div>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Beschreibung</label><textarea style={{ ...s.input, ...(bearbeitenTermin ? { height: mobil ? "35vh" : "42vh", minHeight: 220 } : { height: 90 }), resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }} value={form.beschreibung} onChange={e => setForm(f => ({...f, beschreibung: e.target.value}))} /></div>
            <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {bearbeitenTermin && (
                  <>
                    <a href={bearbeitenTermin.link} target="_blank" rel="noreferrer" style={{ ...s.btnGray, width: "auto", padding: "10px 14px", textDecoration: "none" }}>🔗 Google</a>
                    <button onClick={async () => {
                      if (!window.confirm("Termin wirklich löschen?")) return;
                      const res = await fetch(`${API}/kalender/termine/${bearbeitenTermin.id}?kalender_id=${bearbeitenTermin.kalender_id}`, { method: "DELETE" });
                      if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || "Termin konnte nicht gelöscht werden."); return; }
                      setTerminDialog(false); setBearbeitenTermin(null); ladenTermine();
                    }} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg }}>Löschen</button>
                  </>
                )}
              </div>
              <div style={{ display: "flex", gap: 12, width: mobil ? "100%" : "auto" }}>
                <button onClick={() => { setTerminDialog(false); setBearbeitenTermin(null); }} style={{ ...s.btnGray, ...(mobil ? { flex: 1 } : {}) }}>Abbrechen</button>
                <button onClick={handleSpeichern} style={{ ...s.btn, width: mobil ? "auto" : "auto", flex: mobil ? 1 : undefined }}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <h1 style={s.heading}>Kalender</h1>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {kalender.filter(k => !/feiertage|holidays/i.test(k.name)).map(k => (
            <label key={k.id} style={{ display: "flex", alignItems: "center", gap: 4, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, cursor: "pointer" }}>
              <input type="checkbox" checked={gewaehlteKalenderIds.includes(k.id)} onChange={() => toggleKalender(k.id)} />
              {kalenderLabel(k.id)}
            </label>
          ))}
          {["tag","woche","monat"].map(a => (
            <button key={a} onClick={() => setAnsicht(a)} style={{ ...s.btnGray, width: "auto", padding: "8px 14px", background: ansicht === a ? THEME.colors.primary : THEME.colors.surfaceMuted, fontSize: THEME.fontSize.base }}>
              {a === "tag" ? "Tag" : a === "woche" ? "Woche" : "Monat"}
            </button>
          ))}
          <button onClick={() => { setBearbeitenTermin(null); setForm({ titel: "", start: "", ende: "", beschreibung: "", ort: "", ganztaegig: false, kalender_id: "info@comefit.de" }); setTerminDialog(true); }} style={{ ...s.btn, width: "auto" }}>+ Termin</button>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={() => navigiere(-1)} style={{ ...s.btnGray, width: "auto", padding: "8px 14px" }}>◀</button>
        <h2 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl, minWidth: 140, textAlign: "center" }}>{titelAnzeige()}</h2>
        <button onClick={() => navigiere(1)} style={{ ...s.btnGray, width: "auto", padding: "8px 14px" }}>▶</button>
        <button onClick={() => setAktuellesDatum(new Date())} style={{ ...s.btnGray, width: "auto", padding: "6px 12px", fontSize: THEME.fontSize.sm }}>Heute</button>
      </div>

      {laedt && <p style={{ color: THEME.colors.textMuted }}>Lade Termine...</p>}

      {!laedt && (ansicht === "tag" || ansicht === "woche") && (
        <div style={{ ...s.karte, padding: 0, overflowX: mobil ? "auto" : "hidden", overflowY: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: `56px repeat(${tage.length}, ${mobil ? "minmax(104px, 1fr)" : "1fr"})`, minWidth: mobil ? 56 + tage.length * 104 : "auto" }}>
            <div />
            {tage.map(tag => {
              const heute = tag.toDateString() === heutTag.toDateString();
              return (
                <div key={tag.toISOString()} style={{ textAlign: "center", padding: "10px 4px", borderLeft: "2px solid #4a6285" }}>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, margin: "0 0 4px", textTransform: "uppercase" }}>{WOCHENTAGE[tag.getDay() === 0 ? 6 : tag.getDay() - 1]}</p>
                  <p style={{ color: heute ? THEME.colors.text : THEME.colors.textSecondary, fontSize: THEME.fontSize.lg, fontWeight: 700, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "50%", background: heute ? THEME.colors.primary : "transparent" }}>{tag.getDate()}</p>
                </div>
              );
            })}
          </div>

          {tage.some(tag => ganztagPerTag(tag).length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: `56px repeat(${tage.length}, ${mobil ? "minmax(104px, 1fr)" : "1fr"})`, minWidth: mobil ? 56 + tage.length * 104 : "auto", borderTop: "1px solid #2b3d52" }}>
              <div style={{ padding: 4, fontSize: THEME.fontSize.tiny, color: THEME.colors.textMuted }}>Ganztägig</div>
              {tage.map(tag => (
                <div key={tag.toISOString()} style={{ borderLeft: "2px solid #4a6285", padding: 4 }}>
                  {ganztagPerTag(tag).map(t => (
                    <div key={t.id} onClick={() => handleBearbeiten(t)} style={{ background: terminFarbe(t), color: THEME.colors.text, fontSize: mobil ? THEME.fontSize.xs : THEME.fontSize.xxs, borderRadius: 4, padding: mobil ? "4px 8px" : "2px 6px", marginBottom: 2, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.titel}</div>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div ref={rasterRef} style={{ position: "relative", display: "grid", gridTemplateColumns: `56px repeat(${tage.length}, ${mobil ? "minmax(104px, 1fr)" : "1fr"})`, minWidth: mobil ? 56 + tage.length * 104 : "auto", borderTop: "1px solid #2b3d52", maxHeight: mobil ? 640 : verfuegbareHoehe, overflowY: "auto" }}>
            <div>
              {Array.from({ length: stundeEnde - stundeStart }, (_, i) => stundeStart + i).map(std => (
                <div key={std} style={{ height: STUNDE_HOEHE, textAlign: "right", paddingRight: 6, color: THEME.colors.textMuted, fontSize: THEME.fontSize.xxs, boxSizing: "border-box", paddingTop: 2 }}>{String(std).padStart(2, "0")}:00</div>
              ))}
            </div>
            {tage.map(tag => {
              const tagesEvents = terminePerTag(tag);
              const istSchicht = (t) => t.kalender_id === SCHICHT_KALENDER_ID && /^[A-ZÄÖÜ]{2,3}$/.test((t.titel || "").trim());
              const schichten = layoutTag(tagesEvents.filter(istSchicht));
              const evts = layoutTag(tagesEvents.filter(t => !istSchicht(t)));
              // Urlaub: vorhandene Schicht-Streifen der Person werden zu schwarzem "AK U"; hat die Person an dem Tag keinen Streifen, kommt ein ganztaegiger "AK U"-Streifen dazu
              const urlaubKuerzel = urlaubKal[datumKey(tag)] || [];
              const urlaubOhneSchicht = urlaubKuerzel.filter(k => !schichten.some(t => t.titel.trim() === k));
              const schichtSpalten = schichten.length ? Math.max(...schichten.map(t => t._spalte + 1)) : 0;
              const schichtStreifen = (schichtSpalten + urlaubOhneSchicht.length) * SCHICHT_BREITE;
              const ziehtHier = ziehStart && ziehAktuell !== null && ziehStart.tag.toDateString() === tag.toDateString();
              const wochenende = tag.getDay() === 0 || tag.getDay() === 6; // Sa/So: keine Termine, nur Schichten
              return (
                <div key={tag.toISOString()}
                  onMouseDown={mobil ? undefined : (e) => starteZiehen(e, tag)}
                  onClick={mobil ? (e) => tippeNeuerTermin(e, tag) : undefined}
                  style={{ position: "relative", borderLeft: "2px solid #4a6285", overflow: "hidden", cursor: "pointer" }}>
                  {Array.from({ length: stundeEnde - stundeStart }, (_, i) => {
                    const std = stundeStart + i;
                    const gesperrt = wochenende || (std >= SPERR_START && std < SPERR_ENDE);
                    return <div key={i} style={{ height: STUNDE_HOEHE, boxSizing: "border-box", borderBottom: "1px solid #1a2535", background: gesperrt ? "repeating-linear-gradient(45deg, #3b4252 0px, #3b4252 7px, #2e3442 7px, #2e3442 14px)" : "transparent" }} />;
                  })}
                  {schichten.map(t => {
                    const start = parseStart(t), ende = parseEnde(t);
                    const startStd = start.getHours() + start.getMinutes() / 60;
                    const endStd = Math.max(ende.getHours() + ende.getMinutes() / 60, startStd + 0.5);
                    const imUrlaub = urlaubKuerzel.includes(t.titel.trim());
                    return (
                      <div key={t.id} title={imUrlaub ? `${t.titel.trim()} U – Urlaub (${t.uhrzeit})` : `${t.uhrzeit} ${t.titel}`} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); handleBearbeiten(t); }} style={{
                        position: "absolute", top: (startStd - stundeStart) * STUNDE_HOEHE, height: Math.max((endStd - startStd) * STUNDE_HOEHE - 2, imUrlaub ? 34 : 18), left: t._spalte * SCHICHT_BREITE + 1, width: SCHICHT_BREITE - 2,
                        boxSizing: "border-box", background: imUrlaub ? "#000" : terminFarbe(t), border: imUrlaub ? "1px solid #5b6b80" : undefined, borderRadius: 4, padding: "2px 3px", overflow: "hidden", cursor: "pointer"
                      }}>
                        <p style={{ color: imUrlaub ? "#fff" : THEME.colors.text, fontSize: mobil ? THEME.fontSize.xxs : THEME.fontSize.xs, fontWeight: 700, margin: 0, whiteSpace: imUrlaub ? "normal" : "nowrap", lineHeight: imUrlaub ? 1.15 : undefined, overflow: "hidden", textOverflow: "clip", textAlign: "center" }}>{imUrlaub ? <>{t.titel.trim()}<br />U</> : t.titel}</p>
                      </div>
                    );
                  })}
                  {urlaubOhneSchicht.map((k, i) => (
                    <div key={`u-${k}`} title={`${k} U – Urlaub`} onMouseDown={(e) => e.stopPropagation()} style={{
                      position: "absolute", top: 0, height: (stundeEnde - stundeStart) * STUNDE_HOEHE - 2, left: (schichtSpalten + i) * SCHICHT_BREITE + 1, width: SCHICHT_BREITE - 2,
                      boxSizing: "border-box", background: "#000", border: "1px solid #5b6b80", borderRadius: 4, padding: "2px 3px", overflow: "hidden"
                    }} data-testid="kalender-urlaub-streifen">
                      <p style={{ color: "#fff", fontSize: mobil ? THEME.fontSize.xxs : THEME.fontSize.xs, fontWeight: 700, margin: 0, lineHeight: 1.15, textAlign: "center" }}>{k}<br />U</p>
                    </div>
                  ))}
                  {evts.map(t => {
                    const start = parseStart(t), ende = parseEnde(t);
                    const startStd = start.getHours() + start.getMinutes() / 60;
                    const endStd = Math.max(ende.getHours() + ende.getMinutes() / 60, startStd + 0.5);
                    const top = (startStd - stundeStart) * STUNDE_HOEHE;
                    const hoehe = (endStd - startStd) * STUNDE_HOEHE;
                    return (
                      <div key={t.id} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); handleBearbeiten(t); }} style={{
                        position: "absolute", top, height: Math.max(hoehe - 2, mobil ? 28 : 18), left: `calc(${schichtStreifen}px + (100% - ${schichtStreifen}px) * ${t._spalte / t._gesamtSpalten} + 1px)`, width: `calc((100% - ${schichtStreifen}px) / ${t._gesamtSpalten} - 3px)`,
                        boxSizing: "border-box", background: terminFarbe(t), borderRadius: 4, padding: mobil ? "4px 8px" : "2px 6px", overflow: "hidden", cursor: "pointer"
                      }}>
                        <p style={{ color: THEME.colors.text, fontSize: mobil ? THEME.fontSize.xs : THEME.fontSize.xxs, fontWeight: 700, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.uhrzeit} {t.titel}</p>
                      </div>
                    );
                  })}
                  {ziehtHier && (() => {
                    let start = ziehStart.startStd, ende = ziehAktuell;
                    if (ende < start) { const tmp = start; start = ende; ende = tmp; }
                    if (ende - start < 0.25) ende = start + 1;
                    const top = (start - stundeStart) * STUNDE_HOEHE;
                    const hoehe = (ende - start) * STUNDE_HOEHE;
                    return (
                      <div style={{ position: "absolute", top, height: hoehe, left: 1, width: "calc(100% - 3px)", boxSizing: "border-box", background: "#2b8cc499", border: "1px dashed #2b8cc4", borderRadius: 4, pointerEvents: "none" }} />
                    );
                  })()}
                </div>
              );
            })}
            <div style={{
              position: "absolute", left: 56, right: 0, top: (SPERR_START - stundeStart) * STUNDE_HOEHE, height: (SPERR_ENDE - SPERR_START) * STUNDE_HOEHE,
              display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none"
            }}>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, fontWeight: 600, background: "#0f1923cc", padding: "3px 10px", borderRadius: 4 }}>🚫 {SPERR_START}–{SPERR_ENDE} Uhr: keine Termine (nur Ausnahmen)</span>
            </div>
          </div>
        </div>
      )}

      {!laedt && ansicht === "monat" && (() => {
        const jahr = aktuellesDatum.getFullYear(), monat = aktuellesDatum.getMonth();
        const ersterTag = new Date(jahr, monat, 1);
        const startOffset = ersterTag.getDay() === 0 ? 6 : ersterTag.getDay() - 1;
        const tageImMonat = new Date(jahr, monat + 1, 0).getDate();
        const zellen = [];
        for (let i = 0; i < startOffset; i++) zellen.push(null);
        for (let d = 1; d <= tageImMonat; d++) zellen.push(new Date(jahr, monat, d));
        while (zellen.length % 7 !== 0) zellen.push(null);

        return (
          <div style={{ ...s.karte, padding: 0, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid #2b3d52" }}>
              {WOCHENTAGE.map(w => <div key={w} style={{ textAlign: "center", padding: 8, color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs }}>{w}</div>)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
              {zellen.map((tag, i) => {
                if (!tag) return <div key={i} style={{ minHeight: 96, borderRight: "1px solid #2b3d52", borderBottom: "1px solid #2b3d52" }} />;
                const heute = tag.toDateString() === heutTag.toDateString();
                const alleTermine = [...ganztagPerTag(tag), ...terminePerTag(tag)];
                const sichtbar = alleTermine.slice(0, 3);
                return (
                  <div key={i} onClick={() => { setAktuellesDatum(tag); setAnsicht("tag"); }} style={{ minHeight: mobil ? 76 : 96, borderRight: "1px solid #2b3d52", borderBottom: "1px solid #2b3d52", padding: 4, cursor: "pointer", background: (tag.getDay() === 0 || tag.getDay() === 6) ? "repeating-linear-gradient(45deg, #3b4252 0px, #3b4252 7px, #2e3442 7px, #2e3442 14px)" : undefined }}>
                    <p style={{ margin: "0 0 4px", fontSize: THEME.fontSize.xs, color: heute ? THEME.colors.text : THEME.colors.textMuted, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: "50%", background: heute ? THEME.colors.primary : "transparent" }}>{tag.getDate()}</p>
                    {sichtbar.map(t => (
                      <div key={t.id} onClick={(e) => { e.stopPropagation(); handleBearbeiten(t); }} style={{ background: terminFarbe(t), color: THEME.colors.text, fontSize: mobil ? THEME.fontSize.xxs : THEME.fontSize.tiny, borderRadius: 3, padding: mobil ? "3px 5px" : "1px 4px", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.titel}</div>
                    ))}
                    {alleTermine.length > 3 && <p style={{ margin: 0, fontSize: THEME.fontSize.tiny, color: THEME.colors.textMuted }}>+{alleTermine.length - 3} mehr</p>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Gemeinsamer Kopfbereich mit ComeFit-Logo fuer die oeffentlichen, ohne Login
// erreichbaren Seiten (Online-Vertrag, Online-Kuendigung, Umfrage).
function OeffentlicheSeiteLogo() {
  return (
    <div style={{ textAlign: "center", marginBottom: 24 }}>
      <img src={`${API}/einstellungen/logo`} alt="ComeFit" style={{ width: 220 }} />
    </div>
  );
}

// ============ ÖFFENTLICHE KURS-REGISTRIERUNG (QR-Code im Kursraum, /kurs/:slug) ============
// Bewusst OHNE Turnstile-Spam-Schutz (anders als Mitgliedsantrag/Kündigung/Feedback) - die
// URL wird ausschliesslich ueber einen im Kursraum ausgehaengten QR-Code erreicht, kein
// oeffentlich beworbener Link, und die Daten sind harmlos (nur Kontaktdaten fuer Kursorganisation,
// keine Vertrags-/Zahlungsdaten wie beim Mitgliedsantrag).
function KursRegistrierungSeite({ slug }) {
  const [kurs, setKurs] = useState(undefined); // undefined = laedt, null = nicht gefunden
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [email, setEmail] = useState("");
  const [handynummer, setHandynummer] = useState("");
  const [zustimmung, setZustimmung] = useState(false);
  const [fehler, setFehler] = useState("");
  const [sendetGerade, setSendetGerade] = useState(false);
  const [erfolgsmeldung, setErfolgsmeldung] = useState(null);

  useEffect(() => {
    fetch(`${API}/kurse/oeffentlich/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setKurs)
      .catch(() => setKurs(null));
  }, [slug]);

  const handleRegistrieren = async (e) => {
    e.preventDefault();
    if (!vorname.trim() || !nachname.trim() || !email.trim()) { setFehler("Bitte Vorname, Nachname und E-Mail-Adresse angeben."); return; }
    if (!zustimmung) { setFehler("Bitte der Verwendung deiner Kontaktdaten zustimmen."); return; }
    setFehler("");
    setSendetGerade(true);
    try {
      const res = await fetch(`${API}/kurse/oeffentlich/${encodeURIComponent(slug)}/registrieren`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vorname: vorname.trim(), nachname: nachname.trim(), email: email.trim(), telefon: handynummer.trim(), datenschutz_zustimmung: zustimmung }),
      });
      const data = await res.json();
      if (!res.ok) { setFehler(data.detail || "Registrierung fehlgeschlagen. Bitte versuche es erneut."); setSendetGerade(false); return; }
      setErfolgsmeldung(data.message);
    } catch {
      setFehler("Registrierung fehlgeschlagen. Bitte prüfe deine Internetverbindung.");
    }
    setSendetGerade(false);
  };

  const seitenStyle = { minHeight: "100vh", background: THEME.colors.background, display: "flex", justifyContent: "center", padding: "40px 16px", fontFamily: "inherit" };
  const kartenStyle = { background: THEME.colors.surface, borderRadius: 16, padding: "2rem", width: "min(480px, 100%)", height: "fit-content" };

  if (kurs === undefined) {
    return <div style={{ ...seitenStyle, alignItems: "center" }}><p style={{ color: THEME.colors.textMuted }}>Lädt…</p></div>;
  }

  if (kurs === null) {
    return (
      <div style={seitenStyle}>
        <div style={{ ...kartenStyle, textAlign: "center" }}>
          <OeffentlicheSeiteLogo />
          <h1 style={{ ...s.heading, textAlign: "center" }}>Dieser Kurs wurde leider nicht gefunden.</h1>
        </div>
      </div>
    );
  }

  if (erfolgsmeldung) {
    return (
      <div style={seitenStyle}>
        <div style={{ ...kartenStyle, textAlign: "center" }}>
          <OeffentlicheSeiteLogo />
          <h1 style={{ ...s.heading, textAlign: "center" }}>{erfolgsmeldung}</h1>
        </div>
      </div>
    );
  }

  return (
    <div style={seitenStyle}>
      <div style={kartenStyle}>
        <OeffentlicheSeiteLogo />
        <h1 style={{ ...s.heading, textAlign: "center", marginBottom: 4 }}>{kurs.name}</h1>
        <p style={{ ...s.subtitle, textAlign: "center", marginBottom: 20 }}>Kontaktdaten hinterlegen</p>
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, lineHeight: 1.6, marginBottom: 24 }}>
          Bitte hinterlege kurz deine Kontaktdaten. So können wir dich bei wichtigen Informationen, Änderungen oder einem kurzfristigen Kursausfall direkt erreichen.
        </p>

        <form onSubmit={handleRegistrieren}>
          <div style={s.inputGroup}>
            <label style={s.label}>Vorname *</label>
            <input style={s.input} value={vorname} onChange={e => setVorname(e.target.value)} required autoFocus />
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>Nachname *</label>
            <input style={s.input} value={nachname} onChange={e => setNachname(e.target.value)} required />
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>E-Mail-Adresse *</label>
            <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>Handynummer (optional)</label>
            <input style={s.input} type="tel" value={handynummer} onChange={e => setHandynummer(e.target.value)} />
          </div>

          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, margin: "18px 0", cursor: "pointer" }}>
            <input type="checkbox" checked={zustimmung} onChange={e => setZustimmung(e.target.checked)} style={{ marginTop: 3 }} />
            <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>
              Ich bin damit einverstanden, dass meine angegebenen Kontaktdaten zur Organisation des Kurses sowie zur Information über Änderungen oder Kursausfälle verwendet werden.
            </span>
          </label>

          {fehler && <p style={{ color: "#ff6b6b", fontSize: THEME.fontSize.md, marginBottom: 16 }}>{fehler}</p>}

          <button style={s.btn} type="submit" disabled={sendetGerade}>{sendetGerade ? "Wird gesendet…" : "Registrieren"}</button>
        </form>
      </div>
    </div>
  );
}

// ============ ÖFFENTLICHER ONLINE-MITGLIEDSANTRAG ============
// ============ WLAN-CHECK-IN: Registrierungsseite (wlan.comefit.de) ============
// Eigenstaendige, fuer Smartphones optimierte Seite - Mitglied identifiziert sich einmalig per
// Name (+ ggf. E-Mail/Geburtsdatum bei Namensgleichheit), willigt ein, das aktuelle Geraet wird
// registriert. Reines Frontend fuer routers_wifi.py, keine eigene Backend-Logik hier.
function WlanRegistrierungSeite() {
  const [schritt, setSchritt] = useState("name"); // name | zusatzangabe | einwilligung | fertig | fehler
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [zusatzTyp, setZusatzTyp] = useState("email"); // "email" | "geburtsdatum"
  const [email, setEmail] = useState("");
  const [geburtsdatum, setGeburtsdatum] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [einwilligung, setEinwilligung] = useState(false);
  const [token, setToken] = useState(null);
  const [gefundenerVorname, setGefundenerVorname] = useState("");
  const [ladt, setLadt] = useState(false);
  const [fehlermeldung, setFehlermeldung] = useState("");
  const [datenschutzOffen, setDatenschutzOffen] = useState(false);
  const [datenschutzText, setDatenschutzText] = useState("");

  useEffect(() => {
    fetch(`${API}/einstellungen/oeffentlich`).then(r => r.json()).then(d => setDatenschutzText(d.datenschutz_text || "")).catch(() => {});
  }, []);

  const identifizieren = async (zusatz = {}) => {
    if (!vorname.trim() || !nachname.trim()) { setFehlermeldung("Bitte Vor- und Nachnamen eingeben."); return; }
    setLadt(true);
    setFehlermeldung("");
    try {
      const res = await fetch(`${API}/api/wifi/identifizieren`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vorname: vorname.trim(), nachname: nachname.trim(), ...zusatz }),
      });
      const daten = await res.json();
      if (res.status === 429) { setFehlermeldung(daten.detail || "Zu viele Versuche. Bitte später erneut versuchen."); setLadt(false); return; }
      if (daten.status === "gefunden") {
        setToken(daten.token);
        setGefundenerVorname(daten.vorname || vorname.trim());
        setSchritt("einwilligung");
      } else if (daten.status === "mehrdeutig") {
        setSchritt("zusatzangabe");
      } else if (daten.status === "nicht_eindeutig") {
        setFehlermeldung("Die Angaben stimmen nicht mit unseren Daten überein. Bitte prüfe deine Eingabe oder sprich uns an der Theke an.");
      } else {
        setFehlermeldung("Wir konnten dich mit diesen Angaben nicht finden. Bitte prüfe die Schreibweise oder sprich uns an der Theke an.");
      }
    } catch {
      setFehlermeldung("Verbindung fehlgeschlagen. Bitte erneut versuchen.");
    }
    setLadt(false);
  };

  const registrieren = async () => {
    if (!einwilligung) { setFehlermeldung("Bitte der automatischen Erkennung zustimmen, um fortzufahren."); return; }
    setLadt(true);
    setFehlermeldung("");
    try {
      const res = await fetch(`${API}/api/wifi/registrieren`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, einwilligung: true, device_name: deviceName.trim() || null }),
      });
      const daten = await res.json();
      if (!res.ok) { setFehlermeldung(daten.detail || "Registrierung fehlgeschlagen. Bitte erneut versuchen."); setLadt(false); return; }
      setSchritt("fertig");
    } catch {
      setFehlermeldung("Verbindung fehlgeschlagen. Bitte erneut versuchen.");
    }
    setLadt(false);
  };

  const wlanStyle = {
    seite: { minHeight: "100vh", background: THEME.colors.background, display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 20px", boxSizing: "border-box" },
    karte: { width: "100%", maxWidth: 420, background: THEME.colors.surface, borderRadius: 16, padding: "28px 24px", boxSizing: "border-box" },
    titel: { color: THEME.colors.text, fontSize: THEME.fontSize.xxl, fontWeight: 800, margin: "0 0 8px", textAlign: "center" },
    text: { color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, lineHeight: 1.5, textAlign: "center", margin: "0 0 24px" },
    fehler: { background: "#ef444422", border: `1px solid ${THEME.colors.danger}`, borderRadius: 8, padding: "10px 14px", color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginBottom: 16 },
  };

  return (
    <div style={wlanStyle.seite}>
      <img src="/logo.png" alt="ComeFit" style={{ height: 48, objectFit: "contain", marginBottom: 24 }} />
      <div style={wlanStyle.karte}>
        {schritt === "name" && (
          <>
            <h1 style={wlanStyle.titel}>Willkommen im ComeFit WLAN</h1>
            <p style={wlanStyle.text}>Melde dein Gerät einmalig an. Danach kann dein Studiobesuch automatisch erkannt werden, sobald sich dein Smartphone mit unserem WLAN verbindet.</p>
            {fehlermeldung && <div style={wlanStyle.fehler}>{fehlermeldung}</div>}
            <div style={s.inputGroup}><label style={s.label}>Vorname</label><input style={s.input} value={vorname} onChange={e => setVorname(e.target.value)} autoFocus /></div>
            <div style={s.inputGroup}><label style={s.label}>Nachname</label><input style={s.input} value={nachname} onChange={e => setNachname(e.target.value)} /></div>
            <button style={{ ...s.btn, marginTop: 8, opacity: ladt ? 0.6 : 1 }} disabled={ladt} onClick={() => identifizieren()}>{ladt ? "Suche…" : "Weiter"}</button>
            {/* Wichtig (Nutzer-Vorgabe): WLAN-Nutzung ist nie an diese Registrierung gekoppelt -
                wer nicht will/kein Mitglied ist, hat trotzdem ganz normal Internet. Diese Seite
                erscheint automatisch beim Verbinden (FRITZ!Box-Weiterleitung nach der
                Vorschaltseite), deshalb hier ausdruecklich klarstellen statt implizit lassen. */}
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, textAlign: "center", marginTop: 20 }}>
              Du bist bereits mit dem Internet verbunden. Diese Anmeldung ist freiwillig und nur nötig, wenn du den automatischen Check-in nutzen möchtest.
            </p>
          </>
        )}

        {schritt === "zusatzangabe" && (
          <>
            <h1 style={wlanStyle.titel}>Kurze Nachfrage</h1>
            <p style={wlanStyle.text}>Es gibt bei uns mehrere Mitglieder mit diesem Namen. Bitte gib zusätzlich deine bei ComeFit hinterlegte E-Mail-Adresse oder dein Geburtsdatum an.</p>
            {fehlermeldung && <div style={wlanStyle.fehler}>{fehlermeldung}</div>}
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
              <button onClick={() => setZusatzTyp("email")} style={{ ...s.btnGray, width: "auto", flex: 1, marginBottom: 0, background: zusatzTyp === "email" ? THEME.colors.primary : "transparent" }}>E-Mail</button>
              <button onClick={() => setZusatzTyp("geburtsdatum")} style={{ ...s.btnGray, width: "auto", flex: 1, marginBottom: 0, background: zusatzTyp === "geburtsdatum" ? THEME.colors.primary : "transparent" }}>Geburtsdatum</button>
            </div>
            {zusatzTyp === "email" ? (
              <div style={s.inputGroup}><label style={s.label}>E-Mail-Adresse</label><input type="email" style={s.input} value={email} onChange={e => setEmail(e.target.value)} autoFocus /></div>
            ) : (
              <div style={s.inputGroup}><label style={s.label}>Geburtsdatum</label><input type="date" style={s.input} value={geburtsdatum} onChange={e => setGeburtsdatum(e.target.value)} /></div>
            )}
            <button style={{ ...s.btn, marginTop: 8, opacity: ladt ? 0.6 : 1 }} disabled={ladt}
              onClick={() => identifizieren(zusatzTyp === "email" ? { email } : { geburtsdatum })}>{ladt ? "Prüfe…" : "Bestätigen"}</button>
            <button style={s.btnGray} onClick={() => { setSchritt("name"); setFehlermeldung(""); }}>Zurück</button>
          </>
        )}

        {schritt === "einwilligung" && (
          <>
            <h1 style={wlanStyle.titel}>Hi {gefundenerVorname}! 👋</h1>
            <p style={wlanStyle.text}>Nur noch ein Schritt, dann ist dein Gerät eingerichtet.</p>
            {fehlermeldung && <div style={wlanStyle.fehler}>{fehlermeldung}</div>}
            <div style={s.inputGroup}><label style={s.label}>Gerätename (optional)</label><input style={s.input} placeholder="z.B. iPhone" value={deviceName} onChange={e => setDeviceName(e.target.value)} /></div>
            <div style={{ background: THEME.colors.background, borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <p style={{ color: THEME.colors.text, fontWeight: 700, fontSize: THEME.fontSize.base, margin: "0 0 10px" }}>Automatischer WLAN-Check-in</p>
              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                <input type="checkbox" checked={einwilligung} onChange={e => setEinwilligung(e.target.checked)} style={{ marginTop: 3 }} />
                <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, lineHeight: 1.5 }}>
                  Ich willige ein, dass ComeFit mein registriertes Gerät erkennt, wenn es mit dem ComeFit-WLAN verbunden ist. Die Information wird ausschließlich dazu verwendet, meinen Studiobesuch automatisch als Check-in zu erfassen.
                </span>
              </label>
              <p style={{ margin: "10px 0 0" }}>
                <span onClick={() => setDatenschutzOffen(true)} style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.sm, textDecoration: "underline", cursor: "pointer" }}>Datenschutzhinweise</span>
              </p>
            </div>
            <button style={{ ...s.btn, opacity: (ladt || !einwilligung) ? 0.6 : 1 }} disabled={ladt || !einwilligung} onClick={registrieren}>{ladt ? "Speichert…" : "Gerät registrieren"}</button>
          </>
        )}

        {schritt === "fertig" && (
          <>
            <h1 style={wlanStyle.titel}>✅ Fertig!</h1>
            <p style={wlanStyle.text}>Dein Gerät ist jetzt registriert. Ab deinem nächsten Studiobesuch wird dein Check-in automatisch erfasst, sobald du im ComeFit-WLAN bist.</p>
          </>
        )}
      </div>

      {datenschutzOffen && (
        <div style={s.modalBg} onClick={() => setDatenschutzOffen(false)}>
          <div style={{ ...s.modal, maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitel}>Datenschutzerklärung</h2>
            <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, whiteSpace: "pre-wrap" }}>{datenschutzText || "Wird geladen…"}</div>
            <button style={{ ...s.btnGray, marginTop: 16 }} onClick={() => setDatenschutzOffen(false)}>Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MitgliedWerdenSeite() {
  const [schritt, setSchritt] = useState("tarif");
  const [tarifeUndAktionen, setTarifeUndAktionen] = useState({ tarife: TARIF_PREISE, aktionen: [] });
  const [agbText, setAgbText] = useState("");
  const [datenschutzText, setDatenschutzText] = useState("");
  const [rechtstextOffen, setRechtstextOffen] = useState(null); // "agb" | "datenschutz" | null
  const [form, setForm] = useState({
    tarif: "", aktion_id: null, anrede: "Frau", vorname: "", nachname: "", geburtsdatum: "",
    telefon: "", email: "", strasse: "", plz: "", ort: "", iban: "", kontoinhaber: "", partner_name: "",
    vertragsbeginn: new Date().toISOString().slice(0, 10),
  });
  const set = (feld, wert) => setForm(f => ({ ...f, [feld]: wert }));
  const istPartnerTarif = (tarif) => tarif.includes("Partner");

  const berechneAlter = (geburtsdatum) => {
    if (!geburtsdatum) return null;
    const geb = new Date(geburtsdatum);
    const heute = new Date();
    let alter = heute.getFullYear() - geb.getFullYear();
    const nochNichtGehabt = heute.getMonth() < geb.getMonth() || (heute.getMonth() === geb.getMonth() && heute.getDate() < geb.getDate());
    if (nochNichtGehabt) alter -= 1;
    return alter;
  };

  const istGueltigeDeutscheIban = (iban) => {
    const bereinigt = (iban || "").replace(/\s/g, "").toUpperCase();
    if (!/^DE\d{20}$/.test(bereinigt)) return false;
    const umgestellt = bereinigt.slice(4) + bereinigt.slice(0, 4);
    const numerisch = umgestellt.split("").map(z => (/[0-9]/.test(z) ? z : (z.charCodeAt(0) - 55).toString())).join("");
    let rest = 0;
    for (let i = 0; i < numerisch.length; i++) rest = (rest * 10 + parseInt(numerisch[i], 10)) % 97;
    return rest === 1;
  };
  const [agbAkzeptiert, setAgbAkzeptiert] = useState(false);
  const [datenschutzAkzeptiert, setDatenschutzAkzeptiert] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const signaturePad = useRef(null);
  const turnstileDiv = useRef(null);
  const [einreichenLaedt, setEinreichenLaedt] = useState(false);
  const [fehler, setFehler] = useState("");
  const [ergebnis, setErgebnis] = useState(null);

  useEffect(() => {
    fetch(`${API}/vertraege/tarife-und-aktionen`).then(r => r.json()).then(d => {
      setTarifeUndAktionen(d);
      // Vorauswahl per Link, z.B. von der Kampagnenseite: /mitglied-werden?aktion=3&tarif=Premium
      try {
        const q = new URLSearchParams(window.location.search);
        const tarifParam = q.get("tarif");
        const tarifName = tarifParam && Object.keys(d.tarife || {}).find(t => t.toLowerCase() === tarifParam.toLowerCase());
        const aktionParam = parseInt(q.get("aktion"));
        const aktion = aktionParam && (d.aktionen || []).find(a => a.id === aktionParam);
        if (tarifName) {
          setForm(f => ({
            ...f, tarif: tarifName,
            aktion_id: aktion && (!aktion.betroffene_tarife || aktion.betroffene_tarife.includes(tarifName)) ? aktion.id : null,
          }));
        }
      } catch (e) { /* Vorauswahl ist nur Komfort */ }
    }).catch(() => {});
    fetch(`${API}/einstellungen/oeffentlich`).then(r => r.json()).then(d => { setAgbText(d.agb_text || ""); setDatenschutzText(d.datenschutz_text || ""); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (schritt !== "zustimmung") return;
    const scriptId = "turnstile-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      document.body.appendChild(script);
    }
    const intervall = setInterval(() => {
      if (window.turnstile && turnstileDiv.current && !turnstileDiv.current.hasChildNodes()) {
        window.turnstile.render(turnstileDiv.current, { sitekey: tarifeUndAktionen.turnstile_site_key || TURNSTILE_SITE_KEY_FALLBACK, callback: setTurnstileToken });
        clearInterval(intervall);
      }
    }, 300);
    return () => clearInterval(intervall);
  }, [schritt]);

  const aktionenFuerTarif = (tarif) => (tarifeUndAktionen.aktionen || []).filter(a => !a.betroffene_tarife || a.betroffene_tarife.includes(tarif));

  const handleEinreichen = async () => {
    if (!agbAkzeptiert || !datenschutzAkzeptiert) { setFehler("Bitte AGB und Datenschutz akzeptieren."); return; }
    if (!signaturePad.current || signaturePad.current.isEmpty()) { setFehler("Bitte im Unterschriftsfeld unterschreiben."); return; }
    if (!turnstileToken) { setFehler("Bitte kurz warten, der Spam-Schutz lädt noch."); return; }
    setFehler("");
    setEinreichenLaedt(true);
    try {
      const res = await fetch(`${API}/vertraege/einreichen`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          unterschrift_png_base64: signaturePad.current.toDataURL("image/png"),
          turnstile_token: turnstileToken,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setFehler(data.detail || "Antrag konnte nicht übermittelt werden."); setEinreichenLaedt(false); return; }
      setErgebnis(data);
      setSchritt("fertig");
    } catch (e) {
      console.error("Einreichen-Fehler:", e);
      setFehler("Antrag konnte nicht übermittelt werden. Bitte prüfe deine Internetverbindung.");
    }
    setEinreichenLaedt(false);
  };

  const seitenStyle = { minHeight: "100vh", background: THEME.colors.background, display: "flex", justifyContent: "center", padding: "40px 16px", fontFamily: "inherit" };
  const kartenStyle = { background: THEME.colors.surface, borderRadius: 16, padding: "2rem", width: "min(640px, 100%)", height: "fit-content" };

  if (schritt === "fertig") {
    return (
      <div style={seitenStyle}>
        <div style={kartenStyle}>
          <OeffentlicheSeiteLogo />
          <h1 style={{ ...s.heading, textAlign: "center" }}>Willkommen bei ComeFit!</h1>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.lg, lineHeight: 1.6 }}>
            Vielen Dank. Dein Mitgliedsantrag wurde erfolgreich an uns übermittelt.<br /><br />
            {ergebnis?.email_versendet
              ? "Alle Vertragsunterlagen wurden zusätzlich an deine E-Mail-Adresse gesendet."
              : "Deine Vertragsunterlagen liegen bei uns bereit – bei Fragen melde dich gerne unter info@comefit.de."}
          </p>
          <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 16, marginTop: 16 }}>
            <p style={{ color: THEME.colors.text, fontWeight: 600, marginBottom: 8 }}>Nächster Schritt</p>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, lineHeight: 1.6 }}>
              Für deinen ersten Trainingstag komm bitte während unserer Betreuungszeiten vorbei. Dort richten wir dein
              persönliches RFID-Zugangsband bzw. deine Zutrittskarte ein und führen dich kurz durch unser Studio.
              Alternativ kannst du direkt online einen Termin vereinbaren.
            </p>
          </div>
          <a href="https://comefit.de/termin-buchen" style={{ ...s.btn, display: "block", textAlign: "center", textDecoration: "none", marginTop: 20 }}>
            Jetzt Termin buchen
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={seitenStyle}>
      <div style={kartenStyle}>
        <OeffentlicheSeiteLogo />
        <h1 style={s.heading}>Jetzt Mitglied werden</h1>
        <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
          {["tarif", "person", "sepa", "zustimmung"].map(s2 => (
            <div key={s2} style={{ flex: 1, height: 4, borderRadius: 2, background: ["tarif", "person", "sepa", "zustimmung"].indexOf(schritt) >= ["tarif", "person", "sepa", "zustimmung"].indexOf(s2) ? THEME.colors.primary : THEME.colors.surfaceMuted }} />
          ))}
        </div>

        {schritt === "tarif" && (
          <div>
            <label style={s.label}>Tarif wählen</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
              {Object.entries(tarifeUndAktionen.tarife).map(([name, preis]) => (
                <div key={name} onClick={() => set("tarif", name)}
                  style={{ background: form.tarif === name ? "#2b8cc422" : THEME.colors.background, border: `2px solid ${form.tarif === name ? THEME.colors.primary : "transparent"}`,
                    borderRadius: 8, padding: "14px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{name}</span>
                  <span style={{ color: THEME.colors.success, fontWeight: 600 }}>{preis.toFixed(2)} € / Monat</span>
                </div>
              ))}
            </div>
            {form.tarif && (
              <>
                <label style={s.label}>Vertragsart</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                  <div onClick={() => set("aktion_id", null)}
                    style={{ background: !form.aktion_id ? "#2b8cc422" : THEME.colors.background, border: `2px solid ${!form.aktion_id ? THEME.colors.primary : "transparent"}`,
                      borderRadius: 8, padding: "14px 16px", cursor: "pointer" }}>
                    <span style={{ color: THEME.colors.text, fontWeight: 600 }}>Standardvertrag</span>
                    <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "4px 0 0" }}>Monatlich kündbar, normale Konditionen</p>
                  </div>
                  {aktionenFuerTarif(form.tarif).map(a => (
                    <div key={a.id} onClick={() => set("aktion_id", a.id)}
                      style={{ background: form.aktion_id === a.id ? "#2b8cc422" : THEME.colors.background, border: `2px solid ${form.aktion_id === a.id ? THEME.colors.primary : "transparent"}`,
                        borderRadius: 8, padding: "14px 16px", cursor: "pointer" }}>
                      <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{a.name}</span>
                      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "4px 0 0" }}>
                        {a.gratis_bis_datum
                          ? `0 € Monatsbeitrag bis ${datumDE(a.gratis_bis_datum)} – danach ${a.laufzeit_monate} Monate Laufzeit`
                          : `${a.laufzeit_monate} Monate Laufzeit${a.gratis_monate ? ` + ${a.gratis_monate} Monat(e) gratis` : ""}`}
                      </p>
                    </div>
                  ))}
                </div>
                {istPartnerTarif(form.tarif) && (
                  <div style={s.inputGroup}>
                    <label style={s.label}>Name des Partners / Familienmitglieds *</label>
                    <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 8px" }}>
                      Dieser Tarif ist nur in Verbindung mit einer bereits bestehenden Mitgliedschaft eines Ehe-/Lebenspartners
                      oder Familienmitglieds möglich.
                    </p>
                    <input style={s.input} value={form.partner_name} onChange={e => set("partner_name", e.target.value)} placeholder="Vor- und Nachname" required />
                  </div>
                )}
              </>
            )}
            {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base, marginBottom: 12 }}>{fehler}</p>}
            <button
              disabled={!form.tarif || (istPartnerTarif(form.tarif) && !form.partner_name.trim())}
              onClick={() => { setFehler(""); setSchritt("person"); }}
              style={{ ...s.btn, opacity: (!form.tarif || (istPartnerTarif(form.tarif) && !form.partner_name.trim())) ? 0.5 : 1 }}>
              Weiter
            </button>
          </div>
        )}

        {schritt === "person" && (
          <div>
            <div style={s.inputGroup}><label style={s.label}>Anrede *</label>
              <select style={s.input} value={form.anrede} onChange={e => set("anrede", e.target.value)}><option>Frau</option><option>Herr</option></select>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Vorname *</label><input style={s.input} value={form.vorname} onChange={e => set("vorname", e.target.value)} required /></div>
            <div style={s.inputGroup}><label style={s.label}>Nachname *</label><input style={s.input} value={form.nachname} onChange={e => set("nachname", e.target.value)} required /></div>
            <div style={s.inputGroup}><label style={s.label}>Geburtsdatum *</label><input type="date" style={s.input} value={form.geburtsdatum} onChange={e => set("geburtsdatum", e.target.value)} required /></div>
            <div style={s.inputGroup}><label style={s.label}>Straße &amp; Nr. *</label><input style={s.input} value={form.strasse} onChange={e => set("strasse", e.target.value)} required /></div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ ...s.inputGroup, flex: 1 }}><label style={s.label}>PLZ *</label><input style={s.input} value={form.plz} onChange={e => set("plz", e.target.value)} required /></div>
              <div style={{ ...s.inputGroup, flex: 2 }}><label style={s.label}>Ort *</label><input style={s.input} value={form.ort} onChange={e => set("ort", e.target.value)} required /></div>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Telefon *</label><input style={s.input} value={form.telefon} onChange={e => set("telefon", e.target.value)} required /></div>
            <div style={s.inputGroup}><label style={s.label}>E-Mail *</label><input type="email" style={s.input} value={form.email} onChange={e => set("email", e.target.value)} required /></div>
            {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base, marginBottom: 12 }}>{fehler}</p>}
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => { setFehler(""); setSchritt("tarif"); }} style={s.btnGray}>Zurück</button>
              <button onClick={() => {
                const pflichtfelder = ["anrede", "vorname", "nachname", "geburtsdatum", "strasse", "plz", "ort", "telefon", "email"];
                if (pflichtfelder.some(feld => !form[feld] || !form[feld].trim())) { setFehler("Bitte alle Felder ausfüllen."); return; }
                const alter = berechneAlter(form.geburtsdatum);
                if (alter === null || alter < 18) {
                  setFehler("Für Mitglieder unter 18 Jahren ist der Online-Abschluss leider nicht möglich, da ein Erziehungsberechtigter persönlich unterschreiben muss. Bitte komm gemeinsam mit deinem Erziehungsberechtigten bei uns im Studio vorbei.");
                  return;
                }
                setFehler("");
                setSchritt("sepa");
              }} style={s.btn}>Weiter</button>
            </div>
          </div>
        )}

        {schritt === "sepa" && (
          <div>
            <div style={s.inputGroup}>
              <label style={s.label}>Vertragsbeginn ab *</label>
              <input style={s.input} type="date" min={new Date().toISOString().slice(0, 10)} value={form.vertragsbeginn} onChange={e => set("vertragsbeginn", e.target.value)} required />
            </div>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: 16 }}>SEPA-Lastschriftmandat – Gläubiger-ID: DE45ZZZ00002726314</p>
            <div style={s.inputGroup}><label style={s.label}>Kontoinhaber *</label><input style={s.input} value={form.kontoinhaber} onChange={e => set("kontoinhaber", e.target.value)} required /></div>
            <div style={s.inputGroup}><label style={s.label}>IBAN *</label><input style={s.input} value={form.iban} onChange={e => set("iban", e.target.value.toUpperCase())} placeholder="DE..." required /></div>
            {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base, marginBottom: 12 }}>{fehler}</p>}
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => { setFehler(""); setSchritt("person"); }} style={s.btnGray}>Zurück</button>
              <button onClick={() => {
                if (!form.kontoinhaber.trim() || !form.iban.trim() || !form.vertragsbeginn) { setFehler("Bitte alle Felder ausfüllen."); return; }
                if (form.vertragsbeginn < new Date().toISOString().slice(0, 10)) { setFehler("Der Vertragsbeginn darf nicht in der Vergangenheit liegen."); return; }
                if (!istGueltigeDeutscheIban(form.iban)) { setFehler("Bitte eine gültige deutsche IBAN eingeben (beginnt mit DE)."); return; }
                setFehler("");
                setSchritt("zustimmung");
              }} style={s.btn}>Weiter</button>
            </div>
          </div>
        )}

        {schritt === "zustimmung" && (
          <div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={agbAkzeptiert} onChange={e => setAgbAkzeptiert(e.target.checked)} style={{ marginTop: 3 }} />
              <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>Ich akzeptiere die <span onClick={(e) => { e.preventDefault(); setRechtstextOffen("agb"); }} style={{ color: THEME.colors.primary, textDecoration: "underline" }}>AGB</span></span>
            </label>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20, cursor: "pointer" }}>
              <input type="checkbox" checked={datenschutzAkzeptiert} onChange={e => setDatenschutzAkzeptiert(e.target.checked)} style={{ marginTop: 3 }} />
              <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>Ich akzeptiere die <span onClick={(e) => { e.preventDefault(); setRechtstextOffen("datenschutz"); }} style={{ color: THEME.colors.primary, textDecoration: "underline" }}>Datenschutzerklärung</span></span>
            </label>

            {rechtstextOffen && (
              <div style={s.modalBg} onClick={() => setRechtstextOffen(null)}>
                <div style={{ ...s.modal, width: "min(640px, 95vw)" }} onClick={e => e.stopPropagation()}>
                  <h2 style={s.modalTitel}>{rechtstextOffen === "agb" ? "AGB" : "Datenschutzerklärung"}</h2>
                  <div style={{ maxHeight: "60vh", overflowY: "auto", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                    {rechtstextOffen === "agb" ? agbText : datenschutzText}
                  </div>
                  <button onClick={() => setRechtstextOffen(null)} style={{ ...s.btnGray, marginTop: 16 }}>Schließen</button>
                </div>
              </div>
            )}

            <label style={s.label}>Digitale Unterschrift</label>
            <div style={{ background: THEME.colors.text, borderRadius: 8, marginBottom: 8 }}>
              <SignatureCanvas ref={signaturePad} penColor="black" canvasProps={{ width: 560, height: 160, style: { width: "100%", height: 160, borderRadius: 8 } }} />
            </div>
            <button onClick={() => signaturePad.current?.clear()} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", marginBottom: 20 }}>Unterschrift löschen</button>

            <div ref={turnstileDiv} style={{ marginBottom: 20 }}></div>

            {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base, marginBottom: 16 }}>{fehler}</p>}

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setSchritt("sepa")} style={s.btnGray}>Zurück</button>
              <button onClick={handleEinreichen} disabled={einreichenLaedt} style={{ ...s.btn, background: THEME.colors.success, opacity: einreichenLaedt ? 0.6 : 1 }}>
                {einreichenLaedt ? "Wird übermittelt…" : "Vertrag verbindlich abschließen"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ ÖFFENTLICH: ONLINE-KÜNDIGUNG ============
function VertragKuendigenSeite() {
  const [schritt, setSchritt] = useState("person");
  const [konfiguration, setKonfiguration] = useState({});
  const [form, setForm] = useState({
    anrede: "Frau", vorname: "", nachname: "", geburtsdatum: "", email: "", telefon: "", adresse: "",
    kuendigungsart: "ordentlich", gewuenschtes_kuendigungsdatum: "", grund: "", notiz: "",
  });
  const set = (feld, wert) => setForm(f => ({ ...f, [feld]: wert }));
  const [nachweisDatei, setNachweisDatei] = useState(null);
  const [bestaetigung1, setBestaetigung1] = useState(false);
  const [bestaetigung2, setBestaetigung2] = useState(false);
  const [bestaetigung3, setBestaetigung3] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const signaturePad = useRef(null);
  const turnstileDiv = useRef(null);
  const [einreichenLaedt, setEinreichenLaedt] = useState(false);
  const [fehler, setFehler] = useState("");
  const [ergebnis, setErgebnis] = useState(null);

  useEffect(() => {
    fetch(`${API}/kuendigungen/konfiguration`).then(r => r.json()).then(setKonfiguration).catch(() => {});
  }, []);

  useEffect(() => {
    if (schritt !== "zustimmung") return;
    const scriptId = "turnstile-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      document.body.appendChild(script);
    }
    const intervall = setInterval(() => {
      if (window.turnstile && turnstileDiv.current && !turnstileDiv.current.hasChildNodes()) {
        window.turnstile.render(turnstileDiv.current, { sitekey: konfiguration.turnstile_site_key || TURNSTILE_SITE_KEY_FALLBACK, callback: setTurnstileToken });
        clearInterval(intervall);
      }
    }, 300);
    return () => clearInterval(intervall);
  }, [schritt]);

  const berechneFruehestesDatum = () => {
    const heute = new Date();
    let naechsterMonatIndex = heute.getMonth() + 1;
    let jahr = heute.getFullYear();
    if (naechsterMonatIndex > 11) { naechsterMonatIndex = 0; jahr += 1; }
    return new Date(jahr, naechsterMonatIndex + 1, 0);
  };

  const monatsEndenOptionen = () => {
    const optionen = [];
    let d = berechneFruehestesDatum();
    for (let i = 0; i < 24; i++) {
      optionen.push(new Date(d));
      d = new Date(d.getFullYear(), d.getMonth() + 2, 0);
    }
    return optionen;
  };

  const zuIsoDatum = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const handleEinreichen = async () => {
    if (!bestaetigung1 || !bestaetigung2 || !bestaetigung3) { setFehler("Bitte alle drei Bestätigungen ankreuzen."); return; }
    if (!signaturePad.current || signaturePad.current.isEmpty()) { setFehler("Bitte im Unterschriftsfeld unterschreiben."); return; }
    if (!turnstileToken) { setFehler("Bitte kurz warten, der Spam-Schutz lädt noch."); return; }
    setFehler("");
    setEinreichenLaedt(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, v || ""));
      formData.append("unterschrift_png_base64", signaturePad.current.toDataURL("image/png"));
      formData.append("turnstile_token", turnstileToken);
      if (nachweisDatei) formData.append("nachweis", nachweisDatei);

      const res = await fetch(`${API}/kuendigungen/einreichen`, { method: "POST", body: formData });
      const data = await res.json();
      if (!res.ok) { setFehler(data.detail || "Kündigung konnte nicht übermittelt werden."); setEinreichenLaedt(false); return; }
      setErgebnis(data);
      setSchritt("fertig");
    } catch (e) {
      console.error("Einreichen-Fehler:", e);
      setFehler("Kündigung konnte nicht übermittelt werden. Bitte prüfe deine Internetverbindung.");
    }
    setEinreichenLaedt(false);
  };

  const seitenStyle = { minHeight: "100vh", background: THEME.colors.background, display: "flex", justifyContent: "center", padding: "40px 16px", fontFamily: "inherit" };
  const kartenStyle = { background: THEME.colors.surface, borderRadius: 16, padding: "2rem", width: "min(640px, 100%)", height: "fit-content" };

  if (schritt === "fertig") {
    return (
      <div style={seitenStyle}>
        <div style={kartenStyle}>
          <OeffentlicheSeiteLogo />
          <h1 style={{ ...s.heading, textAlign: "center" }}>Kündigung eingegangen</h1>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.lg, lineHeight: 1.6 }}>
            Vielen Dank für deine Nachricht.<br /><br />
            Deine Kündigung ist bei uns eingegangen und wird nun geprüft. Bitte beachte, dass dies noch keine endgültige
            Kündigungsbestätigung ist – nach Prüfung deiner Vertragsdaten erhältst du von uns eine separate schriftliche
            Bestätigung mit dem genauen Austrittsdatum.<br /><br />
            {ergebnis?.email_versendet
              ? "Eine Eingangsbestätigung wurde zusätzlich an deine E-Mail-Adresse gesendet."
              : "Bei Fragen melde dich gerne unter info@comefit.de."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={seitenStyle}>
      <div style={kartenStyle}>
        <OeffentlicheSeiteLogo />
        <h1 style={s.heading}>Vertrag kündigen</h1>
        <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
          {["person", "kuendigung", "zustimmung"].map(s2 => (
            <div key={s2} style={{ flex: 1, height: 4, borderRadius: 2, background: ["person", "kuendigung", "zustimmung"].indexOf(schritt) >= ["person", "kuendigung", "zustimmung"].indexOf(s2) ? THEME.colors.primary : THEME.colors.surfaceMuted }} />
          ))}
        </div>

        {schritt === "person" && (
          <div>
            <div style={s.inputGroup}><label style={s.label}>Anrede</label>
              <select style={s.input} value={form.anrede} onChange={e => set("anrede", e.target.value)}><option>Frau</option><option>Herr</option></select>
            </div>
            <div style={s.inputGroup}><label style={s.label}>Vorname *</label><input style={s.input} value={form.vorname} onChange={e => set("vorname", e.target.value)} required /></div>
            <div style={s.inputGroup}><label style={s.label}>Nachname *</label><input style={s.input} value={form.nachname} onChange={e => set("nachname", e.target.value)} required /></div>
            <div style={s.inputGroup}><label style={s.label}>Geburtsdatum *</label><input type="date" style={s.input} value={form.geburtsdatum} onChange={e => set("geburtsdatum", e.target.value)} required /></div>
            <div style={s.inputGroup}><label style={s.label}>E-Mail *</label><input type="email" style={s.input} value={form.email} onChange={e => set("email", e.target.value)} required /></div>
            <div style={s.inputGroup}><label style={s.label}>Telefonnummer (optional)</label><input style={s.input} value={form.telefon} onChange={e => set("telefon", e.target.value)} /></div>
            <div style={s.inputGroup}><label style={s.label}>Adresse (optional)</label><input style={s.input} value={form.adresse} onChange={e => set("adresse", e.target.value)} /></div>
            {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base, marginBottom: 12 }}>{fehler}</p>}
            <button onClick={() => {
              if (!form.vorname.trim() || !form.nachname.trim() || !form.geburtsdatum || !form.email.trim()) { setFehler("Bitte Vorname, Nachname, Geburtsdatum und E-Mail ausfüllen."); return; }
              setFehler("");
              setSchritt("kuendigung");
            }} style={s.btn}>Weiter</button>
          </div>
        )}

        {schritt === "kuendigung" && (
          <div>
            <div style={s.inputGroup}>
              <label style={s.label}>Kündigungsart *</label>
              <select style={s.input} value={form.kuendigungsart} onChange={e => { set("kuendigungsart", e.target.value); set("gewuenschtes_kuendigungsdatum", ""); }}>
                <option value="ordentlich">Ordentliche Kündigung</option>
                <option value="ausserordentlich">Außerordentliche Kündigung</option>
              </select>
            </div>
            {form.kuendigungsart === "ordentlich" ? (
              <div style={s.inputGroup}>
                <label style={s.label}>Gewünschtes Kündigungsdatum *</label>
                <select style={s.input} value={form.gewuenschtes_kuendigungsdatum} onChange={e => set("gewuenschtes_kuendigungsdatum", e.target.value)}>
                  <option value="">Bitte wählen…</option>
                  {monatsEndenOptionen().map(d => (
                    <option key={zuIsoDatum(d)} value={zuIsoDatum(d)}>{datumDE(zuIsoDatum(d))}</option>
                  ))}
                </select>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "6px 0 0" }}>Bei ordentlicher Kündigung ist nur ein Monatsende ab dem frühestmöglichen Termin wählbar.</p>
              </div>
            ) : (
              <div style={s.inputGroup}>
                <label style={s.label}>Gewünschtes Kündigungsdatum *</label>
                <input type="date" style={s.input} value={form.gewuenschtes_kuendigungsdatum} onChange={e => set("gewuenschtes_kuendigungsdatum", e.target.value)} required />
              </div>
            )}
            <div style={s.inputGroup}>
              <label style={s.label}>{form.kuendigungsart === "ausserordentlich" ? "Grund der außerordentlichen Kündigung *" : "Kündigungsgrund / Nachricht *"}</label>
              <textarea style={{ ...s.input, height: 90 }} value={form.grund} onChange={e => set("grund", e.target.value)} required />
            </div>
            {form.kuendigungsart === "ausserordentlich" && (
              <div style={s.inputGroup}>
                <label style={s.label}>Notiz / Erklärung *</label>
                <textarea style={{ ...s.input, height: 90 }} value={form.notiz} onChange={e => set("notiz", e.target.value)} required />
              </div>
            )}
            <div style={s.inputGroup}>
              <label style={s.label}>Datei-Upload für Nachweise (optional, PDF/JPG/PNG, max. 10 MB)</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setNachweisDatei(e.target.files[0] || null)} />
            </div>
            {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base, marginBottom: 12 }}>{fehler}</p>}
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => { setFehler(""); setSchritt("person"); }} style={s.btnGray}>Zurück</button>
              <button onClick={() => {
                if (!form.gewuenschtes_kuendigungsdatum || !form.grund.trim()) { setFehler("Bitte alle Pflichtfelder ausfüllen."); return; }
                if (form.kuendigungsart === "ausserordentlich" && !form.notiz.trim()) { setFehler("Bitte eine Notiz/Erklärung angeben."); return; }
                setFehler("");
                setSchritt("zustimmung");
              }} style={s.btn}>Weiter</button>
            </div>
          </div>
        )}

        {schritt === "zustimmung" && (
          <div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={bestaetigung1} onChange={e => setBestaetigung1(e.target.checked)} style={{ marginTop: 3 }} />
              <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>Ich bestätige, dass ich meinen Vertrag kündigen möchte.</span>
            </label>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={bestaetigung2} onChange={e => setBestaetigung2(e.target.checked)} style={{ marginTop: 3 }} />
              <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>Ich bestätige, dass meine Angaben zur Bearbeitung gespeichert werden dürfen.</span>
            </label>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20, cursor: "pointer" }}>
              <input type="checkbox" checked={bestaetigung3} onChange={e => setBestaetigung3(e.target.checked)} style={{ marginTop: 3 }} />
              <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>Ich habe verstanden, dass dies zunächst nur eine Eingangsbestätigung ist und die Kündigung noch geprüft wird.</span>
            </label>

            <label style={s.label}>Digitale Unterschrift</label>
            <div style={{ background: THEME.colors.text, borderRadius: 8, marginBottom: 8 }}>
              <SignatureCanvas ref={signaturePad} penColor="black" canvasProps={{ width: 560, height: 160, style: { width: "100%", height: 160, borderRadius: 8 } }} />
            </div>
            <button onClick={() => signaturePad.current?.clear()} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", marginBottom: 20 }}>Unterschrift löschen</button>

            <div ref={turnstileDiv} style={{ marginBottom: 20 }}></div>

            {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base, marginBottom: 16 }}>{fehler}</p>}

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setSchritt("kuendigung")} style={s.btnGray}>Zurück</button>
              <button onClick={handleEinreichen} disabled={einreichenLaedt} style={{ ...s.btn, background: THEME.colors.danger, opacity: einreichenLaedt ? 0.6 : 1 }}>
                {einreichenLaedt ? "Wird übermittelt…" : "Kündigung absenden"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ ÖFFENTLICH: SONDERAKTION / TAG DER OFFENEN TÜR ============
function SonderaktionSeite() {
  const [aktionen, setAktionen] = useState([]);
  const [teilenText, setTeilenText] = useState("Event teilen");

  useEffect(() => {
    fetch(`${API}/vertraege/tarife-und-aktionen`).then(r => r.json()).then(d => setAktionen(d.aktionen || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/besucherzaehler/erfassen`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ seite: "sonderaktion" }) }).catch(() => {});
  }, []);

  // Solange noch keine Aktion in Studio -> Aktionen angelegt ist, zeigt die Box trotzdem das
  // bekannte Jubilaeums-Angebot als Platzhalter - sobald echte Aktionen existieren, ersetzen
  // sie diesen Platzhalter automatisch (kein Code-Aenderung noetig).
  const angebote = aktionen.length > 0
    ? aktionen.map(a => ({
        titel: `${a.laufzeit_monate} Monate`,
        text: a.gratis_monate ? `${a.gratis_monate} Monat${a.gratis_monate > 1 ? "e" : ""} geschenkt` : (a.anmeldegebuehr_entfaellt ? "Keine Anmeldegebühr" : a.name),
      }))
    : [
        { titel: "6 Monate", text: "1 Monat geschenkt" },
        { titel: "12 Monate", text: "2 Monate geschenkt" },
      ];

  return (
    <div className="cf-page">
      <a
        className="cf-whatsapp-fab"
        href="https://wa.me/4975716871959?text=Hallo%20ComeFit%20%F0%9F%91%8B%2C%20ich%20habe%20eine%20Frage%20zur%20Jubil%C3%A4ums-Aktion."
        target="_blank"
        rel="noreferrer"
        aria-label="Per WhatsApp schreiben"
      >
        <svg viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91C21.95 6.45 17.5 2 12.04 2zm5.83 14.09c-.25.7-1.45 1.34-2 1.42-.51.08-1.15.11-1.86-.12-.43-.13-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07 0-1.46.77-2.18 1.04-2.48.27-.3.6-.37.8-.37.2 0 .4 0 .57.01.18.01.43-.07.67.51.25.6.85 2.07.92 2.22.07.15.12.33.02.53-.1.2-.15.33-.3.5-.15.18-.31.4-.44.53-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.02 1.12 1 2.06 1.31 2.36 1.46.3.15.48.13.65-.08.18-.2.75-.87.95-1.17.2-.3.4-.25.68-.15.28.1 1.78.84 2.08 1 .3.15.5.23.57.35.08.13.08.73-.17 1.43z" />
        </svg>
      </a>
      <div className="cf-container">
        <OeffentlicheSeiteLogo />
        <img className="cf-hero-image" src="/sonderaktion_hero.png" alt="Das ComeFit-Team" />

        <section className="cf-hero">
          <span className="cf-eyebrow">Wir feiern Jubiläum</span>
          <h1>2 Jahre ComeFit</h1>
          <p className="cf-hero-subtitle">Tag der offenen Tür</p>
          <p className="cf-event-date">Sonntag, 20. September · 10–18 Uhr</p>
          <p className="cf-location">📍 Hauptstraße 50, 72488 Sigmaringen-Laiz</p>
          <p className="cf-intro">
            Lerne unser Studio, unser Team und unsere Trainingsangebote persönlich kennen. Du kannst dich unverbindlich umschauen, Fragen stellen und den ganzen Tag kostenlos probetrainieren.
          </p>
          <a className="cf-button cf-button-primary" href="#anmeldung">Kostenlos vorbeikommen →</a>
        </section>

        <section className="cf-section">
          <div className="cf-offer">
            <p className="cf-offer-title">🎁 Unsere Jubiläums-Aktion</p>
            <div className="cf-offer-grid">
              {angebote.map((a, i) => (
                <div className="cf-offer-item" key={i}>
                  <strong>{a.titel}</strong>
                  <span>{a.text}</span>
                </div>
              ))}
            </div>
            <p className="cf-offer-note">
              Alternativ ist auch unser monatlich kündbarer Flexvertrag möglich. Keine Anmeldegebühr und kein Starterpaket.
            </p>
          </div>
        </section>

        <section className="cf-section">
          <h2>Ablauf des Tages</h2>
          <p className="cf-text">
            Ab 10 Uhr erwarten dich ein Sektempfang und kleine Snacks. Im Anschluss zeigen wir dir bei einem persönlichen Rundgang unser Studio, unsere Geräte und unser Trainingskonzept.
          </p>
          <div className="cf-benefits" style={{ marginTop: 16 }}>
            {["Sektempfang & Snacks", "Persönlicher Rundgang durchs Studio", "Verlosung um 17:00 Uhr"].map(text => (
              <div className="cf-benefit" key={text}>
                <span className="cf-check">✓</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
          <p className="cf-text" style={{ marginTop: 16 }}>
            Der Tag soll vor allem eins sein: locker. Nimm dir Zeit, fühl dich wohl und genieß den Tag bei uns.
          </p>
        </section>

        <section className="cf-section">
          <div className="cf-offer">
            <p className="cf-offer-title">🎉 Unsere Verlosung</p>
            <p className="cf-offer-note" style={{ marginBottom: 14 }}>
              Unsere 6-Monats-Aktion gilt schon jetzt und noch bis einschließlich 20. September. Egal, ob du schon vorher unterschreibst oder direkt am Tag der offenen Tür dazustößt: Bist du am 20. September bei uns vor Ort, nimmst du automatisch an unserer Verlosung teil.
            </p>
            <p className="cf-offer-note" style={{ marginBottom: 10, fontWeight: 700 }}>Zu gewinnen gibt es:</p>
            <div className="cf-offer-grid" style={{ gridTemplateColumns: "1fr", gap: 10 }}>
              <div className="cf-offer-item">
                <strong>1. Platz</strong>
                <span>12 Monate kostenlose Mitgliedschaft</span>
              </div>
              <div className="cf-offer-item">
                <strong>2. Platz</strong>
                <span>6 Monate kostenlose Mitgliedschaft</span>
              </div>
              <div className="cf-offer-item">
                <strong>3. Platz</strong>
                <span>3 Monate kostenlose Mitgliedschaft</span>
              </div>
            </div>
            <p className="cf-offer-note" style={{ fontWeight: 700 }}>Die Verlosung findet um 17:00 Uhr statt – sei dabei!</p>
          </div>
        </section>

        <section className="cf-section">
          <h2>Unser Studio im Überblick</h2>
          <p className="cf-section-intro">Entdecke unser vielseitiges Angebot für Fitness, Gesundheit, Beweglichkeit und Erholung.</p>
          <div className="cf-feature-grid">
            {[
              ["🏃", "Ausdauer", "Laufband, Stepper, Cardiogeräte und Ausdauerzirkel.", "/sonderaktion_ausdauer.jpg"],
              ["💪", "Kraft", "Geführte Geräte, Milon-Zirkel und freie Übungen.", "/sonderaktion_kraft.jpg"],
              ["🤸", "Beweglichkeit", "Stretching, Rückenfit und Beweglichkeitszirkel.", "/sonderaktion_beweglichkeit.jpg"],
              ["🧖", "Erholung", "Sauna, Massagegeräte, Duschen und Getränke.", "/sonderaktion_erholung.jpg"],
            ].map(([icon, titel, text, bild]) => (
              <article className="cf-feature-card" key={titel}>
                <img src={bild} alt={titel} loading="lazy" />
                <div className="cf-feature-content">
                  <p className="cf-feature-title">{icon} {titel}</p>
                  <p>{text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="cf-section">
          <h2>Was uns besonders macht</h2>
          <p className="cf-highlight">Familiär. Persönlich. Ohne Massenabfertigung.</p>
          <p className="cf-text">
            ComeFit ist kein anonymes Kettenstudio. Bei uns bist du keine Nummer. Wir begleiten dich persönlich und helfen dir dabei, deine Ziele sicher und langfristig zu erreichen.
          </p>
          <div className="cf-benefits">
            {[
              "Individuelle Betreuung und persönliche Trainingsplanung",
              "Moderne Geräte sowie Milon- und Beweglichkeitszirkel",
              "Kurse wie Rückenkurs, Zumba & STRONG Nation",
              "Kostenlose Duschen, Parkplätze und WLAN",
            ].map(text => (
              <div className="cf-benefit" key={text}>
                <span className="cf-check">✓</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="cf-final-cta" id="anmeldung">
          <h2>Mach den ersten Schritt</h2>
          <p>Komm am Tag der offenen Tür unverbindlich vorbei oder vereinbare direkt dein persönliches Probetraining.</p>
          <a className="cf-button cf-button-primary" href="/mitglied-werden">Jetzt Mitglied werden →</a>
          <a className="cf-button cf-button-secondary" href="https://comefit.de/termin-buchen">Probetraining vereinbaren</a>
        </section>

        <button
          type="button"
          className="cf-button cf-button-secondary"
          style={{ marginTop: 30 }}
          onClick={async () => {
            const teilenDaten = {
              title: "2 Jahre ComeFit – Tag der offenen Tür",
              text: "Feier mit uns 2 Jahre ComeFit! Tag der offenen Tür am 20. September.",
              url: window.location.href,
            };
            if (navigator.share) {
              try { await navigator.share(teilenDaten); } catch (e) { /* Nutzer hat den Teilen-Dialog abgebrochen */ }
            } else {
              try {
                await navigator.clipboard.writeText(teilenDaten.url);
                setTeilenText("Link kopiert!");
                setTimeout(() => setTeilenText("Event teilen"), 2500);
              } catch (e) { /* Zwischenablage nicht verfuegbar */ }
            }
          }}
        >
          📤 {teilenText}
        </button>

        <p className="cf-footer-note">
          Eine normale, monatlich kündbare Mitgliedschaft ohne feste Laufzeit ist bei ComeFit natürlich jederzeit möglich.
        </p>
      </div>
    </div>
  );
}

// ============ ÖFFENTLICH: ANONYME FEEDBACK-UMFRAGE ============
function UmfrageSeite() {
  const [konfiguration, setKonfiguration] = useState({});
  const [kategorie, setKategorie] = useState("Kritik");
  const [nachricht, setNachricht] = useState("");
  const [antwortGewuenscht, setAntwortGewuenscht] = useState(false);
  const [kontakt, setKontakt] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileDiv = useRef(null);
  const [einreichenLaedt, setEinreichenLaedt] = useState(false);
  const [fehler, setFehler] = useState("");
  const [fertig, setFertig] = useState(false);

  useEffect(() => {
    fetch(`${API}/feedback/konfiguration`).then(r => r.json()).then(setKonfiguration).catch(() => {});
  }, []);

  useEffect(() => {
    const scriptId = "turnstile-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      document.body.appendChild(script);
    }
    const intervall = setInterval(() => {
      if (window.turnstile && turnstileDiv.current && !turnstileDiv.current.hasChildNodes()) {
        window.turnstile.render(turnstileDiv.current, { sitekey: konfiguration.turnstile_site_key || TURNSTILE_SITE_KEY_FALLBACK, callback: setTurnstileToken });
        clearInterval(intervall);
      }
    }, 300);
    return () => clearInterval(intervall);
  }, [konfiguration]);

  const handleEinreichen = async () => {
    if (!nachricht.trim()) { setFehler("Bitte eine Nachricht eingeben."); return; }
    if (antwortGewuenscht && !kontakt.trim()) { setFehler("Bitte E-Mail-Adresse oder Telefonnummer angeben."); return; }
    if (!turnstileToken) { setFehler("Bitte kurz warten, der Spam-Schutz lädt noch."); return; }
    setFehler("");
    setEinreichenLaedt(true);
    try {
      const res = await fetch(`${API}/feedback/einreichen`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kategorie, nachricht, antwort_gewuenscht: antwortGewuenscht, kontakt: antwortGewuenscht ? kontakt : null, turnstile_token: turnstileToken }),
      });
      const data = await res.json();
      if (!res.ok) { setFehler(data.detail || "Feedback konnte nicht übermittelt werden."); setEinreichenLaedt(false); return; }
      setFertig(true);
    } catch (e) {
      console.error("Einreichen-Fehler:", e);
      setFehler("Feedback konnte nicht übermittelt werden. Bitte prüfe deine Internetverbindung.");
    }
    setEinreichenLaedt(false);
  };

  const seitenStyle = { minHeight: "100vh", background: THEME.colors.background, display: "flex", justifyContent: "center", padding: "40px 16px", fontFamily: "inherit" };
  const kartenStyle = { background: THEME.colors.surface, borderRadius: 16, padding: "2rem", width: "min(640px, 100%)", height: "fit-content" };

  if (fertig) {
    return (
      <div style={seitenStyle}>
        <div style={kartenStyle}>
          <OeffentlicheSeiteLogo />
          <h1 style={{ ...s.heading, textAlign: "center" }}>Vielen Dank für dein Feedback! ❤️</h1>
        </div>
      </div>
    );
  }

  return (
    <div style={seitenStyle}>
      <div style={kartenStyle}>
        <OeffentlicheSeiteLogo />
        <h1 style={s.heading}>Hilf uns, besser zu werden!</h1>
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, lineHeight: 1.6, marginBottom: 24 }}>
          Deine Meinung ist uns wichtig! Diese Umfrage ist komplett anonym und hilft uns dabei, unser Fitnessstudio stetig zu verbessern.
        </p>

        <div style={s.inputGroup}>
          <label style={s.label}>Kategorie</label>
          <select style={s.input} value={kategorie} onChange={e => setKategorie(e.target.value)}>
            <option>Kritik</option>
            <option>Verbesserungsvorschlag</option>
            <option>Lob</option>
            <option>Sonstiges</option>
          </select>
        </div>

        <div style={s.inputGroup}>
          <label style={s.label}>Nachricht *</label>
          <textarea style={{ ...s.input, height: 160 }} value={nachricht} onChange={e => setNachricht(e.target.value)} required />
        </div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={antwortGewuenscht} onChange={e => setAntwortGewuenscht(e.target.checked)} style={{ marginTop: 3 }} />
          <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>Ich möchte eine Antwort erhalten.</span>
        </label>

        {antwortGewuenscht && (
          <div style={s.inputGroup}>
            <label style={s.label}>E-Mail-Adresse oder Telefonnummer *</label>
            <input style={s.input} value={kontakt} onChange={e => setKontakt(e.target.value)} required />
          </div>
        )}

        <div ref={turnstileDiv} style={{ margin: "16px 0" }}></div>

        {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base, marginBottom: 16 }}>{fehler}</p>}

        <button onClick={handleEinreichen} disabled={einreichenLaedt} style={{ ...s.btn, opacity: einreichenLaedt ? 0.6 : 1 }}>
          {einreichenLaedt ? "Wird übermittelt…" : "Feedback anonym senden"}
        </button>
      </div>
    </div>
  );
}

// ============ GEMEINSAM: QR-CODE-MODAL ============
function QrCodeModal({ bildUrl, dateiname, onClose }) {
  return (
    <div style={s.modalBg} onClick={onClose}>
      <div style={{ ...s.modal, width: "min(400px, 95vw)", textAlign: "center" }} onClick={e => e.stopPropagation()}>
        <h2 style={s.modalTitel}>QR-Code</h2>
        <img src={bildUrl} alt="QR-Code" style={{ width: 260, height: 260, background: THEME.colors.text, borderRadius: 8, padding: 10 }} />
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
          <a href={bildUrl} download={dateiname} style={{ ...s.btnGray, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>PNG herunterladen</a>
        </div>
        <button onClick={onClose} style={{ ...s.btnGray, marginTop: 20 }}>Schließen</button>
      </div>
    </div>
  );
}

// ============ ADMIN: ONLINE-VERTRÄGE ============
const VERTRAG_STATUS_LABEL = { neu: "🟠 Neu eingegangen", freigegeben: "🟢 Freigegeben", abgelehnt: "🔴 Abgelehnt" };

function OnlineVertraegeSeite({ onMitgliedOeffnen }) {
  const [antraege, setAntraege] = useState([]);
  const [ansehenAntrag, setAnsehenAntrag] = useState(null);
  const [ablehnenAntrag, setAblehnenAntrag] = useState(null);
  const [ablehnenGrund, setAblehnenGrund] = useState("");
  const [qrOffen, setQrOffen] = useState(false);

  const laden = () => fetch(`${API}/vertraege/`).then(r => r.json()).then(setAntraege).catch(() => {});
  useEffect(() => { laden(); }, []);

  const handleFreigeben = async (antrag) => {
    if (!window.confirm(`${antrag.vorname} ${antrag.nachname} als Mitglied freigeben?`)) return;
    const res = await fetch(`${API}/vertraege/${antrag.id}/freigeben`, { method: "PATCH" });
    const data = await res.json();
    if (res.ok) { alert(`✓ Freigegeben – Mitglied #${data.mitglied_id} angelegt.`); laden(); if (onMitgliedOeffnen) onMitgliedOeffnen(data.mitglied_id); }
    else alert(data.detail || "Fehler beim Freigeben");
  };

  const handleAblehnen = async () => {
    const res = await fetch(`${API}/vertraege/${ablehnenAntrag.id}/ablehnen`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ grund: ablehnenGrund || null }),
    });
    if (!res.ok) { alert("Ablehnen fehlgeschlagen."); return; }
    setAblehnenAntrag(null); setAblehnenGrund("");
    laden();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h1 style={s.heading}>Online-Verträge</h1>
        <button onClick={() => setQrOffen(true)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm }}>QR</button>
      </div>
      {qrOffen && <QrCodeModal bildUrl={`${API}/vertraege/qr-code.png`} dateiname="comefit-mitglied-werden-qr.png" onClose={() => setQrOffen(false)} />}
      {antraege.length === 0 ? (
        <p style={{ color: THEME.colors.textMuted }}>Noch keine Online-Anträge eingegangen.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {antraege.map(a => (
            <div key={a.id} style={{ ...s.karte, borderLeftColor: a.status === "neu" ? THEME.colors.warning : a.status === "freigegeben" ? THEME.colors.success : THEME.colors.danger }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <p style={{ color: THEME.colors.text, fontWeight: 600, fontSize: THEME.fontSize.lg, margin: 0 }}>{a.vorname} {a.nachname}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "4px 0 0" }}>{a.tarif} · Eingegangen am {a.eingereicht_am}</p>
                </div>
                <span style={{ fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>{VERTRAG_STATUS_LABEL[a.status] || a.status}</span>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                <button onClick={() => setAnsehenAntrag(a)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>Ansehen</button>
                {a.status === "neu" && (
                  <>
                    <button onClick={() => handleFreigeben(a)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", background: THEME.colors.success }}>Freigeben</button>
                    <button onClick={() => setAblehnenAntrag(a)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", background: THEME.colors.dangerBg }}>Ablehnen</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {ansehenAntrag && (
        <div style={s.modalBg} onClick={() => setAnsehenAntrag(null)}>
          <div style={{ ...s.modal, width: "min(720px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ ...s.modalTitel, fontSize: THEME.fontSize.xxl2 }}>{ansehenAntrag.vorname} {ansehenAntrag.nachname}</h2>
            <p style={{ color: "#c8d3de", fontSize: THEME.fontSize.lg, lineHeight: 2 }}>
              <b>Anrede:</b> {ansehenAntrag.anrede}<br />
              <b>Geburtsdatum:</b> {datumDE(ansehenAntrag.geburtsdatum)}<br />
              <b>Adresse:</b> {ansehenAntrag.strasse}, {ansehenAntrag.plz} {ansehenAntrag.ort}<br />
              <b>Telefon:</b> {ansehenAntrag.telefon}<br />
              <b>E-Mail:</b> {ansehenAntrag.email}<br />
              <b>Tarif:</b> {ansehenAntrag.tarif} ({parseFloat(ansehenAntrag.preis).toFixed(2)} €/Monat)<br />
              {ansehenAntrag.firma && (
                <>
                  <b>Firmenfitness – Firma:</b> {ansehenAntrag.firma}<br />
                  <span style={{ color: "#fbbf24", fontSize: THEME.fontSize.base }}>⚠ Nachweis über das Arbeitsverhältnis beim ersten Besuch prüfen</span><br />
                </>
              )}
              {ansehenAntrag.partner_name && (
                <>
                  <b>Partner/Familienmitglied:</b> {ansehenAntrag.partner_name}<br />
                  <span style={{ color: ansehenAntrag.partner_kandidaten?.length ? "#4ade80" : "#f87171", fontSize: THEME.fontSize.base }}>
                    {ansehenAntrag.partner_kandidaten?.length
                      ? `✓ Mögliches Mitglied gefunden: ${ansehenAntrag.partner_kandidaten.map(k => `${k.name} (${k.tarif})`).join(", ")}`
                      : "⚠ Kein passendes Mitglied gefunden"}
                  </span><br />
                </>
              )}
              <b>Vertragslaufzeit:</b> {ansehenAntrag.laufzeit_monate == 1 && !ansehenAntrag.gratis_monate
                ? "1 Monat (monatlich kündbar)"
                : `${ansehenAntrag.laufzeit_monate} Monate${ansehenAntrag.gratis_monate ? ` + ${ansehenAntrag.gratis_monate} Monat(e) gratis` : ""}`}<br />
              <b>Vertragsbeginn:</b> {datumDE(ansehenAntrag.vertragsbeginn)}<br />
              <b>Ende Erstlaufzeit:</b> {datumDE(ansehenAntrag.erstlaufzeit_ende)}<br />
              <b>IBAN:</b> {ansehenAntrag.iban}<br />
              <b>Kontoinhaber:</b> {ansehenAntrag.kontoinhaber}<br />
              {ansehenAntrag.ablehnungsgrund && <><b>Ablehnungsgrund:</b> {ansehenAntrag.ablehnungsgrund}<br /></>}
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
              <a href={`${API}/vertraege/${ansehenAntrag.id}/pdf/vertrag`} target="_blank" rel="noreferrer" style={{ ...s.btn, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>📄 Vertrag</a>
              <a href={`${API}/vertraege/${ansehenAntrag.id}/pdf/agb`} target="_blank" rel="noreferrer" style={{ ...s.btnGray, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>📄 AGB</a>
              <a href={`${API}/vertraege/${ansehenAntrag.id}/pdf/datenschutz`} target="_blank" rel="noreferrer" style={{ ...s.btnGray, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>📄 Datenschutz</a>
            </div>
            <button onClick={() => setAnsehenAntrag(null)} style={{ ...s.btnGray, marginTop: 16 }}>Schließen</button>
          </div>
        </div>
      )}

      {ablehnenAntrag && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(440px, 95vw)" }}>
            <h2 style={s.modalTitel}>Antrag ablehnen – {ablehnenAntrag.vorname} {ablehnenAntrag.nachname}</h2>
            <div style={s.inputGroup}>
              <label style={s.label}>Grund (optional)</label>
              <textarea style={{ ...s.input, height: 100 }} value={ablehnenGrund} onChange={e => setAblehnenGrund(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setAblehnenAntrag(null); setAblehnenGrund(""); }} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleAblehnen} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg }}>Ablehnen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ ADMIN: KÜNDIGUNGEN ============
const KUENDIGUNG_STATUS_FARBE = { neu: THEME.colors.warning, in_pruefung: "#3b82f6", rueckfrage: "#a855f7", bestaetigt: THEME.colors.success, abgelehnt: THEME.colors.danger, erledigt: THEME.colors.textDisabled };
const MATCHING_STATUS_LABEL = { gefunden: "✓ Mitglied gefunden", moegliche_treffer: "⚠ Mögliche Treffer", kein_treffer: "✗ Kein Mitglied gefunden" };
const MATCHING_STATUS_FARBE = { gefunden: THEME.colors.success, moegliche_treffer: THEME.colors.warning, kein_treffer: THEME.colors.danger };

function KuendigungenSeite() {
  const [kuendigungen, setKuendigungen] = useState([]);
  const [ansehenId, setAnsehenId] = useState(null);
  const [ablehnenId, setAblehnenId] = useState(null);
  const [ablehnenGrund, setAblehnenGrund] = useState("");
  const [bestaetigenDatum, setBestaetigenDatum] = useState("");
  const [bestaetigungsVorlagen, setBestaetigungsVorlagen] = useState([]);
  const [bestaetigenVorlageId, setBestaetigenVorlageId] = useState(null);
  const [rueckfrageOffen, setRueckfrageOffen] = useState(false);
  const [rueckfrageText, setRueckfrageText] = useState("");
  const [notizText, setNotizText] = useState("");
  const [manuelleMitgliedId, setManuelleMitgliedId] = useState("");
  const [qrOffen, setQrOffen] = useState(false);

  const laden = () => fetch(`${API}/kuendigungen/`).then(r => r.json()).then(setKuendigungen).catch(() => {});
  useEffect(() => {
    laden();
    fetch(`${API}/email-vorlagen/?kategorie=kuendigung_bestaetigung`).then(r => r.json()).then(d => {
      const liste = Array.isArray(d) ? d : [];
      setBestaetigungsVorlagen(liste);
      if (liste[0]) setBestaetigenVorlageId(liste[0].id);
    }).catch(() => {});
  }, []);

  const ansehen = kuendigungen.find(k => k.id === ansehenId) || null;

  useEffect(() => {
    if (ansehen) setBestaetigenDatum(ansehen.berechnetes_fruehestes_datum || ansehen.gewuenschtes_kuendigungsdatum || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ansehenId]);

  const schliessenUndLaden = () => { setAnsehenId(null); setRueckfrageOffen(false); laden(); };

  const handleZuordnen = async (mitgliedId) => {
    const res = await fetch(`${API}/kuendigungen/${ansehenId}/zuordnen`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mitglied_id: mitgliedId }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.detail || "Zuordnen fehlgeschlagen"); return; }
    laden();
  };

  const handleStatus = async (status) => {
    const res = await fetch(`${API}/kuendigungen/${ansehenId}/status`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }),
    });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); return; }
    laden();
  };

  const handleNotiz = async () => {
    if (!notizText.trim()) return;
    const res = await fetch(`${API}/kuendigungen/${ansehenId}/notiz`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: notizText }),
    });
    if (!res.ok) { alert("Notiz konnte nicht gespeichert werden."); return; }
    setNotizText("");
    laden();
  };

  const handleRueckfrage = async () => {
    if (!rueckfrageText.trim()) return;
    const res = await fetch(`${API}/kuendigungen/${ansehenId}/rueckfrage`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: rueckfrageText }),
    });
    if (!res.ok) { alert("Rückfrage konnte nicht gespeichert werden."); return; }
    setRueckfrageOffen(false); setRueckfrageText("");
    laden();
  };

  const handleBestaetigen = async () => {
    if (!bestaetigenDatum) { alert("Bitte ein Vertragsende auswählen."); return; }
    const res = await fetch(`${API}/kuendigungen/${ansehenId}/bestaetigen`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ vertragsende: bestaetigenDatum, vorlage_id: bestaetigenVorlageId }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.detail || "Bestätigen fehlgeschlagen"); return; }
    alert(data.email_versendet ? "✓ Kündigung bestätigt, E-Mail versendet." : "✓ Kündigung bestätigt (E-Mail-Versand nicht konfiguriert).");
    laden();
  };

  const handleAblehnen = async () => {
    const res = await fetch(`${API}/kuendigungen/${ablehnenId}/ablehnen`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ grund: ablehnenGrund || null }),
    });
    if (!res.ok) { alert("Ablehnen fehlgeschlagen."); return; }
    setAblehnenId(null); setAblehnenGrund("");
    laden();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h1 style={s.heading}>Kündigungen</h1>
        <button onClick={() => setQrOffen(true)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm }}>QR</button>
      </div>
      {qrOffen && <QrCodeModal bildUrl={`${API}/kuendigungen/qr-code.png`} dateiname="comefit-vertrag-kuendigen-qr.png" onClose={() => setQrOffen(false)} />}
      {kuendigungen.length === 0 ? (
        <p style={{ color: THEME.colors.textMuted }}>Noch keine Online-Kündigungen eingegangen.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {kuendigungen.map(k => (
            <div key={k.id} style={{ ...s.karte, borderLeftColor: KUENDIGUNG_STATUS_FARBE[k.bearbeitungsstatus] || THEME.colors.textDisabled }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <p style={{ color: THEME.colors.text, fontWeight: 600, fontSize: THEME.fontSize.lg, margin: 0 }}>{k.vorname} {k.nachname}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "4px 0 0" }}>
                    {k.kuendigungsart === "ordentlich" ? "Ordentliche Kündigung" : "Außerordentliche Kündigung"} · Zum {datumDE(k.gewuenschtes_kuendigungsdatum)} · Eingegangen am {k.eingereicht_am}
                  </p>
                  <p style={{ fontSize: THEME.fontSize.sm, margin: "4px 0 0", color: MATCHING_STATUS_FARBE[k.matching_status] }}>
                    {MATCHING_STATUS_LABEL[k.matching_status]}{k.zugeordnetes_mitglied && ` – zugeordnet: ${k.zugeordnetes_mitglied.name}`}
                  </p>
                </div>
                <span style={{ fontSize: THEME.fontSize.sm, color: KUENDIGUNG_STATUS_FARBE[k.bearbeitungsstatus] || THEME.colors.textSecondary, fontWeight: 600 }}>{k.bearbeitungsstatus_label}</span>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                <button onClick={() => setAnsehenId(k.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>Ansehen</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {ansehen && (
        <div style={s.modalBg} onClick={schliessenUndLaden}>
          <div style={{ ...s.modal, width: "min(640px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitel}>{ansehen.vorname} {ansehen.nachname}</h2>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, lineHeight: 1.8 }}>
              <b>Anrede:</b> {ansehen.anrede || "–"}<br />
              <b>Geburtsdatum:</b> {datumDE(ansehen.geburtsdatum)}<br />
              <b>E-Mail:</b> {ansehen.email}<br />
              <b>Telefon:</b> {ansehen.telefon || "–"}<br />
              <b>Adresse:</b> {ansehen.adresse || "–"}<br />
              <b>Kündigungsart:</b> {ansehen.kuendigungsart === "ordentlich" ? "Ordentliche Kündigung" : "Außerordentliche Kündigung"}<br />
              <b>Gewünschtes Kündigungsdatum:</b> {datumDE(ansehen.gewuenschtes_kuendigungsdatum)}<br />
              {ansehen.berechnetes_fruehestes_datum && <><b>Berechnetes frühestes Datum:</b> {datumDE(ansehen.berechnetes_fruehestes_datum)}<br /></>}
              <b>Grund:</b> {ansehen.grund}<br />
              {ansehen.notiz && <><b>Notiz/Erklärung:</b> {ansehen.notiz}<br /></>}
              {ansehen.ablehnungsgrund && <><b>Ablehnungsgrund:</b> {ansehen.ablehnungsgrund}<br /></>}
              {ansehen.bestaetigtes_vertragsende && <><b>Bestätigtes Vertragsende:</b> {datumDE(ansehen.bestaetigtes_vertragsende)}<br /></>}
            </p>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              <a href={`${API}/kuendigungen/${ansehen.id}/pdf/eingang`} target="_blank" rel="noreferrer" style={{ ...s.btn, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>📄 Kündigungseingang</a>
              {ansehen.bearbeitungsstatus === "bestaetigt" && (
                <a href={`${API}/kuendigungen/${ansehen.id}/pdf/bestaetigung`} target="_blank" rel="noreferrer" style={{ ...s.btnGray, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>📄 Kündigungsbestätigung</a>
              )}
              {ansehen.nachweis_vorhanden && (
                <a href={`${API}/kuendigungen/${ansehen.id}/nachweis`} target="_blank" rel="noreferrer" style={{ ...s.btnGray, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>📎 Nachweis</a>
              )}
            </div>

            <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <p style={{ color: THEME.colors.text, fontWeight: 600, marginBottom: 8, fontSize: THEME.fontSize.base }}>Mitglied-Zuordnung</p>
              {ansehen.zugeordnetes_mitglied ? (
                <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.base, margin: 0 }}>✓ Zugeordnet: {ansehen.zugeordnetes_mitglied.name} ({ansehen.zugeordnetes_mitglied.tarif})</p>
              ) : (
                <>
                  {ansehen.matching_kandidaten.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {ansehen.matching_kandidaten.map(kand => (
                        <div key={kand.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                          <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{kand.name} ({kand.tarif}) – #{kand.id}</span>
                          <button onClick={() => handleZuordnen(kand.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Zuordnen</button>
                        </div>
                      ))}
                    </div>
                  ) : <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>Kein passendes Mitglied automatisch gefunden.</p>}
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <input style={{ ...s.input, flex: 1 }} placeholder="Mitglieds-ID manuell eingeben" value={manuelleMitgliedId} onChange={e => setManuelleMitgliedId(e.target.value)} />
                    <button onClick={() => { if (manuelleMitgliedId) handleZuordnen(parseInt(manuelleMitgliedId, 10)); setManuelleMitgliedId(""); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>Zuordnen</button>
                  </div>
                </>
              )}
            </div>

            {(ansehen.bearbeitungsstatus === "neu" || ansehen.bearbeitungsstatus === "in_pruefung" || ansehen.bearbeitungsstatus === "rueckfrage") && (
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <p style={{ color: THEME.colors.text, fontWeight: 600, marginBottom: 8, fontSize: THEME.fontSize.base }}>Kündigung bestätigen</p>
                <div style={s.inputGroup}><label style={s.label}>Vertragsende</label><input type="date" style={s.input} value={bestaetigenDatum} onChange={e => setBestaetigenDatum(e.target.value)} /></div>
                <div style={s.inputGroup}>
                  <label style={s.label}>E-Mail-Vorlage</label>
                  <select style={s.input} value={bestaetigenVorlageId || ""} onChange={e => setBestaetigenVorlageId(parseInt(e.target.value, 10))}>
                    {bestaetigungsVorlagen.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <button onClick={handleBestaetigen} disabled={!ansehen.mitglied_id} style={{ ...s.btn, width: "auto", background: THEME.colors.success, opacity: ansehen.mitglied_id ? 1 : 0.5 }} title={!ansehen.mitglied_id ? "Bitte zuerst ein Mitglied zuordnen" : ""}>Bestätigen &amp; senden</button>
              </div>
            )}

            {ansehen.verlauf.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ color: THEME.colors.text, fontWeight: 600, marginBottom: 8, fontSize: THEME.fontSize.base }}>Verlauf</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {ansehen.verlauf.map((e, i) => (
                    <p key={i} style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>
                      <span style={{ color: "#5a6b7d" }}>{e.erstellt_am}</span> – <b style={{ color: THEME.colors.textSecondary }}>{e.erstellt_von}</b>: {e.text || e.typ}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <textarea style={{ ...s.input, flex: 1, height: 60 }} placeholder="Interne Notiz hinzufügen…" value={notizText} onChange={e => setNotizText(e.target.value)} />
              <button onClick={handleNotiz} style={{ ...s.btnGray, width: "auto" }}>Hinzufügen</button>
            </div>

            {(ansehen.bearbeitungsstatus === "neu" || ansehen.bearbeitungsstatus === "in_pruefung" || ansehen.bearbeitungsstatus === "rueckfrage") && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                {ansehen.bearbeitungsstatus === "neu" && <button onClick={() => handleStatus("in_pruefung")} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>In Prüfung setzen</button>}
                <button onClick={() => setRueckfrageOffen(true)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>Rückfrage stellen</button>
                <button onClick={() => setAblehnenId(ansehen.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, background: THEME.colors.dangerBg }}>Ablehnen</button>
              </div>
            )}
            {ansehen.bearbeitungsstatus === "bestaetigt" && (
              <button onClick={() => handleStatus("erledigt")} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, marginBottom: 16 }}>Als erledigt markieren</button>
            )}

            {rueckfrageOffen && (
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <p style={{ color: THEME.colors.text, fontWeight: 600, marginBottom: 8, fontSize: THEME.fontSize.base }}>Rückfrage vermerken</p>
                <textarea style={{ ...s.input, height: 70, marginBottom: 8 }} value={rueckfrageText} onChange={e => setRueckfrageText(e.target.value)} placeholder="Worum geht es bei der Rückfrage?" />
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginBottom: 8 }}>Die eigentliche Rückfrage-Mail verschickst du danach selbst per E-Mail-Programm.</p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setRueckfrageOffen(false)} style={s.btnGray}>Abbrechen</button>
                  <button onClick={handleRueckfrage} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                </div>
              </div>
            )}

            <button onClick={schliessenUndLaden} style={s.btnGray}>Schließen</button>
          </div>
        </div>
      )}

      {ablehnenId && (
        <div style={s.modalBg}>
          <div style={{ ...s.modal, width: "min(440px, 95vw)" }}>
            <h2 style={s.modalTitel}>Kündigung ablehnen</h2>
            <div style={s.inputGroup}>
              <label style={s.label}>Grund (optional)</label>
              <textarea style={{ ...s.input, height: 100 }} value={ablehnenGrund} onChange={e => setAblehnenGrund(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => { setAblehnenId(null); setAblehnenGrund(""); }} style={s.btnGray}>Abbrechen</button>
              <button onClick={handleAblehnen} style={{ ...s.btn, width: "auto", background: THEME.colors.dangerBg }}>Ablehnen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ ADMIN: FEEDBACK ============
const FEEDBACK_STATUS_FARBE = { neu: THEME.colors.warning, in_bearbeitung: "#3b82f6", erledigt: THEME.colors.success };

function FeedbackSeite() {
  const [eintraege, setEintraege] = useState([]);
  const [sortierungAufsteigend, setSortierungAufsteigend] = useState(false);
  const [kategorieFilter, setKategorieFilter] = useState("");
  const [qrOffen, setQrOffen] = useState(false);

  const laden = () => fetch(`${API}/feedback/`).then(r => r.json()).then(setEintraege).catch(() => {});
  useEffect(() => { laden(); }, []);

  const handleStatus = async (id, status) => {
    const res = await fetch(`${API}/feedback/${id}/status`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }) });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); return; }
    laden();
  };

  const handleLoeschen = async (id) => {
    if (!window.confirm("Dieses Feedback wirklich löschen?")) return;
    const res = await fetch(`${API}/feedback/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    laden();
  };

  const sichtbareEintraege = eintraege
    .filter(e => !kategorieFilter || e.kategorie === kategorieFilter)
    .sort((a, b) => sortierungAufsteigend ? a.id - b.id : b.id - a.id);

  return (
    <div>
      <h1 style={s.heading}>Feedback</h1>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20, alignItems: "center" }}>
        <select style={{ ...s.input, width: "auto" }} value={kategorieFilter} onChange={e => setKategorieFilter(e.target.value)}>
          <option value="">Alle Kategorien</option>
          <option>Kritik</option>
          <option>Verbesserungsvorschlag</option>
          <option>Lob</option>
          <option>Sonstiges</option>
        </select>
        <button onClick={() => setSortierungAufsteigend(v => !v)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm }}>
          Sortierung: {sortierungAufsteigend ? "Älteste zuerst" : "Neueste zuerst"}
        </button>
        <button onClick={() => setQrOffen(true)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, marginLeft: "auto" }}>QR-Code anzeigen</button>
      </div>

      {sichtbareEintraege.length === 0 ? (
        <p style={{ color: THEME.colors.textMuted }}>Noch kein Feedback eingegangen.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sichtbareEintraege.map(e => (
            <div key={e.id} style={{ ...s.karte, borderLeftColor: FEEDBACK_STATUS_FARBE[e.status] || THEME.colors.textDisabled }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <p style={{ color: THEME.colors.text, fontWeight: 600, fontSize: THEME.fontSize.md, margin: 0 }}>{e.kategorie}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "2px 0 0" }}>{e.eingereicht_am}</p>
                </div>
                <span style={{ fontSize: THEME.fontSize.sm, color: FEEDBACK_STATUS_FARBE[e.status] || THEME.colors.textSecondary, fontWeight: 600 }}>{e.status_label}</span>
              </div>
              <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "10px 0", whiteSpace: "pre-wrap" }}>{e.nachricht}</p>
              {e.antwort_gewuenscht && (
                <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.sm, margin: "0 0 10px" }}>
                  💬 Antwort gewünscht – Kontakt: {e.kontakt}
                  {e.kontakt && e.kontakt.includes("@") && (
                    <> · <a
                      href={`mailto:${e.kontakt}?subject=${encodeURIComponent("Re: Dein Feedback bei ComeFit")}&body=${encodeURIComponent(`Hallo,\n\nvielen Dank für dein Feedback:\n"${e.nachricht}"\n\n`)}`}
                      style={{ color: THEME.colors.success, textDecoration: "underline" }}
                    >Antworten</a></>
                  )}
                </p>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {e.status !== "in_bearbeitung" && <button onClick={() => handleStatus(e.id, "in_bearbeitung")} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>In Bearbeitung</button>}
                {e.status !== "erledigt" && <button onClick={() => handleStatus(e.id, "erledigt")} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px", background: THEME.colors.success }}>Erledigt</button>}
                <button onClick={() => handleLoeschen(e.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px", background: THEME.colors.dangerBg }}>Löschen</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {qrOffen && (
        <div style={s.modalBg} onClick={() => setQrOffen(false)}>
          <div style={{ ...s.modal, width: "min(440px, 95vw)", textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitel}>Deine Meinung zählt!</h2>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: 16 }}>Scanne den QR-Code und gib uns anonym Feedback.</p>
            <img src={`${API}/feedback/qr-code.png`} alt="QR-Code zur Umfrage" style={{ width: 260, height: 260, background: THEME.colors.text, borderRadius: 8, padding: 10 }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20, flexWrap: "wrap" }}>
              <a href={`${API}/feedback/qr-code.png`} download="comefit-feedback-qr.png" style={{ ...s.btnGray, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>PNG herunterladen</a>
              <a href={`${API}/feedback/qr-poster.pdf`} target="_blank" rel="noreferrer" style={{ ...s.btn, width: "auto", textDecoration: "none", fontSize: THEME.fontSize.sm, padding: "8px 14px" }}>Als PDF drucken</a>
            </div>
            <button onClick={() => setQrOffen(false)} style={{ ...s.btnGray, marginTop: 20 }}>Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ EINSTELLUNGEN: PUSH-BENACHRICHTIGUNGEN ============
// Frueher ein kleiner Dropdown-Button im Online Portal (schwer zu finden, siehe Nutzer-Feedback) -
// jetzt eine eigene Karte im Einstellungen-Tab "Benachrichtigungen".
function PushEinstellungenKarte() {
  const [unterstuetzt, setUnterstuetzt] = useState(false);
  const [pruefLaedt, setPruefLaedt] = useState(true);
  const [aktiv, setAktiv] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [praeferenzen, setPraeferenzen] = useState({ feedback: true, kuendigung: true, vertrag: true, shelly: true });

  useEffect(() => {
    pushWirdUnterstuetzt().then(async (ok) => {
      setUnterstuetzt(ok);
      if (!ok) { setPruefLaedt(false); return; }
      const istAktiv = !!(await holeAktivesAbo());
      setAktiv(istAktiv);
      if (istAktiv) {
        const gespeichert = await pushEinstellungenLaden(API);
        if (gespeichert) setPraeferenzen(gespeichert);
      }
      setPruefLaedt(false);
    });
  }, []);

  const togglePraeferenz = (key) => setPraeferenzen(p => ({ ...p, [key]: !p[key] }));

  const handleAktivieren = async () => {
    setLaedt(true);
    try {
      await pushAktivieren(API, praeferenzen);
      setAktiv(true);
    } catch (e) {
      window.alert("Push-Benachrichtigungen konnten nicht aktiviert werden: " + e.message);
    } finally {
      setLaedt(false);
    }
  };

  const handleSpeichern = async () => {
    setLaedt(true);
    try {
      await pushEinstellungenSpeichern(API, praeferenzen);
    } finally {
      setLaedt(false);
    }
  };

  const handleDeaktivieren = async () => {
    setLaedt(true);
    try {
      await pushDeaktivieren(API);
      setAktiv(false);
    } finally {
      setLaedt(false);
    }
  };

  return (
    <div style={s.karte}>
      <h3 style={{ color: THEME.colors.text, margin: "0 0 16px", fontSize: THEME.fontSize.xl }}>🔔 Push-Benachrichtigungen</h3>
      {pruefLaedt ? (
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Wird geprüft…</p>
      ) : !unterstuetzt ? (
        <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Dieser Browser unterstützt leider keine Push-Benachrichtigungen.</p>
      ) : (
        <>
          <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md, marginBottom: 16 }}>
            Erhalte Benachrichtigungen direkt auf diesem Gerät, sobald etwas Neues reinkommt. Gilt nur für dieses Gerät/diesen Browser.
          </p>
          <div style={{ marginBottom: 16 }}>
            {[["feedback", "Feedback"], ["kuendigung", "Kündigungen"], ["vertrag", "Online-Verträgen"], ["shelly", "Shelly-Status (Studio, Sauna, ...)"]].map(([key, label]) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: THEME.colors.text, fontSize: THEME.fontSize.base, cursor: "pointer" }}>
                <input type="checkbox" checked={praeferenzen[key]} onChange={() => togglePraeferenz(key)} />
                {label}
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {aktiv ? (
              <>
                <button onClick={handleSpeichern} disabled={laedt} style={{ ...s.btn, width: "auto", padding: "10px 20px" }}>Speichern</button>
                <button onClick={handleDeaktivieren} disabled={laedt} style={{ ...s.btnGray, width: "auto", padding: "10px 20px", color: THEME.colors.danger }}>Deaktivieren</button>
              </>
            ) : (
              <button onClick={handleAktivieren} disabled={laedt} style={{ ...s.btn, width: "auto", padding: "10px 20px" }}>Aktivieren</button>
            )}
          </div>
          {aktiv && <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, marginTop: 12 }}>✓ Auf diesem Gerät aktiv.</p>}
        </>
      )}
    </div>
  );
}

// ============ ONLINE PORTAL (buendelt Online-Vertraege / Kuendigungen / Feedback) ============

// ============ CHECK IN (buendelt Wellpass + Hansefit Check-in-Verwaltung) ============
function CheckInSeite({ onMitgliedOeffnen, rechte }) {
  const [aktiveTab, setAktiveTab] = useState("wellpass");
  return (
    <div>
      <h1 style={s.heading}>Check In</h1>
      <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
        {[["wellpass", "Wellpass", "wellpass"], ["hansefit", "Hansefit", "hansefit"]]
          .filter(([, , modul]) => rechte?.rolle === "admin" || rechte?.module?.[modul])
          .map(([key, label]) => (
            <button key={key} onClick={() => setAktiveTab(key)}
              style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: aktiveTab === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: aktiveTab === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md, flexShrink: 0 }}>{label}</button>
          ))}
      </div>
      {aktiveTab === "wellpass" && <WellpassSeite onMitgliedOeffnen={onMitgliedOeffnen} rechte={rechte} />}
      {aktiveTab === "hansefit" && <HansefitSeite rechte={rechte} />}
    </div>
  );
}

// ============ WELLPASS ============
function WellpassSeite({ onMitgliedOeffnen, rechte }) {
  const darfExterneCheckins = rechte?.rolle === "admin" || !!rechte?.module?.checkins_extern;
  const darfZuKlaeren = rechte?.rolle === "admin" || !!rechte?.module?.checkins_zu_klaeren;
  // Auszahlungen zeigen tatsaechliche Umsatzzahlen - bewusst nur fuer Admins, keine eigene
  // Modul-Rechte-Checkbox dafuer (analog zu Finanzen an anderer Stelle in der App).
  const darfAuszahlungen = rechte?.rolle === "admin";
  const [subTab, setSubTab] = useState("uebersicht");
  const [stats, setStats] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [zuKlaeren, setZuKlaeren] = useState([]);
  const [auszahlungen, setAuszahlungen] = useState([]);
  const [mitglieder, setMitglieder] = useState([]);
  const [syncLaedt, setSyncLaedt] = useState(false);
  const [syncMeldung, setSyncMeldung] = useState("");
  const [zuordnenId, setZuordnenId] = useState(null);
  const [zuordnenSuche, setZuordnenSuche] = useState("");
  const [wellpassNutzerListe, setWellpassNutzerListe] = useState([]);
  const [neuNutzerId, setNeuNutzerId] = useState(null);
  const [nutzerZuordnenId, setNutzerZuordnenId] = useState(null);
  const [nutzerZuordnenSuche, setNutzerZuordnenSuche] = useState("");

  const laden = () => {
    fetch(`${API}/wellpass/statistiken`).then(r => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/wellpass/checkins?limit=100`).then(r => r.json()).then(d => setCheckins(d.checkins || [])).catch(() => {});
    fetch(`${API}/wellpass/zu-klaeren`).then(r => r.json()).then(d => setZuKlaeren(Array.isArray(d) ? d : [])).catch(() => {});
    if (darfAuszahlungen) {
      fetch(`${API}/wellpass/auszahlungen`).then(r => r.ok ? r.json() : []).then(d => setAuszahlungen(Array.isArray(d) ? d : [])).catch(() => {});
    }
  };
  useEffect(() => { laden(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (zuordnenId !== null && mitglieder.length === 0) {
      fetch(`${API}/mitglieder/`).then(r => r.json()).then(d => setMitglieder(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [zuordnenId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (nutzerZuordnenId !== null && wellpassNutzerListe.length === 0) {
      fetch(`${API}/wellpass/nutzer`).then(r => r.json()).then(d => setWellpassNutzerListe(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [nutzerZuordnenId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = async () => {
    setSyncLaedt(true);
    setSyncMeldung("");
    try {
      const res = await fetch(`${API}/wellpass/sync`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) { setSyncMeldung(data.detail || "Synchronisierung fehlgeschlagen."); return; }
      const auszahlungenZusatz = data.auszahlungen && !data.auszahlungen.uebersprungen
        ? `, ${data.auszahlungen.neu} neue/${data.auszahlungen.aktualisiert} aktualisierte Auszahlungsmonate`
        : "";
      setSyncMeldung(`✓ ${data.neu} neue Check-ins übernommen${auszahlungenZusatz}.`);
      laden();
    } catch {
      setSyncMeldung("Synchronisierung fehlgeschlagen.");
    } finally {
      setSyncLaedt(false);
    }
  };

  const handleZuordnen = async (mitgliedId) => {
    const res = await fetch(`${API}/wellpass/checkins/${zuordnenId}/zuordnen`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mitglied_id: mitgliedId }),
    });
    if (!res.ok) { alert("Zuordnen fehlgeschlagen."); return; }
    setZuordnenId(null); setZuordnenSuche("");
    laden();
  };

  const handleIgnorieren = async (id) => {
    const res = await fetch(`${API}/wellpass/checkins/${id}/ignorieren`, { method: "POST" });
    if (!res.ok) { alert("Ignorieren fehlgeschlagen."); return; }
    laden();
  };

  const handleNeuerNutzerGespeichert = async (typ, data) => {
    if (data.vorschau && data.vorschau.anzahl > 0) {
      const res = await fetch(`${API}/wellpass/nutzer/${data.id}/rueckwirkend-zuordnen`, { method: "POST" });
      if (!res.ok) { alert("Nutzer wurde angelegt, aber die rückwirkende Zuordnung der Check-ins ist fehlgeschlagen."); }
    }
    setNeuNutzerId(null);
    setWellpassNutzerListe([]);
    laden();
  };

  const handleNutzerZuordnen = async (wellpassNutzerId) => {
    const res = await fetch(`${API}/wellpass/checkins/${nutzerZuordnenId}/nutzer-zuordnen`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ wellpass_nutzer_id: wellpassNutzerId }),
    });
    if (!res.ok) { alert("Zuordnen fehlgeschlagen."); return; }
    setNutzerZuordnenId(null); setNutzerZuordnenSuche("");
    laden();
  };

  const gefilterteMitglieder = mitglieder.filter(m =>
    !zuordnenSuche || `${m.vorname} ${m.nachname}`.toLowerCase().includes(zuordnenSuche.toLowerCase())
  ).slice(0, 30);

  const gefilterteNutzer = wellpassNutzerListe.filter(n =>
    !nutzerZuordnenSuche || `${n.vorname} ${n.nachname}`.toLowerCase().includes(nutzerZuordnenSuche.toLowerCase())
  ).slice(0, 30);

  // Ein Name/eine externe Nutzer-ID soll in der "Zu klären"-Liste nur einmal auftauchen -
  // Zuordnen/Ignorieren wirkt serverseitig ohnehin schon auf alle offenen Check-ins derselben
  // externen ID (siehe routers_wellpass.py), hier wird das nur zusammengefasst dargestellt.
  // zuKlaeren ist bereits nach Zeitpunkt absteigend sortiert, die erste gesehene Zeile pro
  // Gruppe ist also automatisch die neueste.
  const zuKlaerenGruppiert = Object.values(zuKlaeren.reduce((gruppen, c) => {
    const schluessel = c.externe_user_id || c.name_roh;
    if (!gruppen[schluessel]) gruppen[schluessel] = { ...c, anzahl: 0 };
    gruppen[schluessel].anzahl += 1;
    return gruppen;
  }, {}));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        <h1 style={{ ...s.heading, margin: 0 }}>Wellpass</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {syncMeldung && <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{syncMeldung}</span>}
          <button onClick={handleSync} disabled={syncLaedt} style={{ ...s.btn, width: "auto" }}>
            {syncLaedt ? "Synchronisiert…" : "🔄 Jetzt synchronisieren"}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
        {[["uebersicht", "Übersicht", true], ["checkins", "Check-ins", darfExterneCheckins], ["zuklaeren", `Zu klären${zuKlaeren.length > 0 ? ` (${zuKlaeren.length})` : ""}`, darfZuKlaeren], ["auszahlungen", "Auszahlungen", darfAuszahlungen]]
          .filter(([, , erlaubt]) => erlaubt)
          .map(([key, label]) => (
          <button key={key} onClick={() => setSubTab(key)}
            style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: subTab === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: subTab === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md }}>{label}</button>
        ))}
      </div>

      {subTab === "auszahlungen" && darfAuszahlungen && (
        <div>
          {auszahlungen.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Auszahlungsdaten importiert — auf „Jetzt synchronisieren" klicken.</p>}
          {auszahlungen.length > 0 && (() => {
            const aktuell = auszahlungen[0];
            const monatLabel = (m) => {
              const [j, mo] = m.split("-");
              return `${["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"][parseInt(mo,10)-1]} ${j}`;
            };
            return (<>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 24 }}>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.primary }}>
                  <p style={{ ...s.karteLabel, margin: 0 }}>{monatLabel(aktuell.monat)}</p>
                  <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "6px 0 0" }}>{aktuell.brutto_betrag?.toFixed(2)} €</p>
                  {aktuell.veraenderung_prozent != null && (
                    <p style={{ margin: "4px 0 0", fontSize: THEME.fontSize.sm, color: aktuell.veraenderung_prozent >= 0 ? THEME.colors.success : THEME.colors.danger }}>
                      {aktuell.veraenderung_prozent >= 0 ? "+" : ""}{aktuell.veraenderung_prozent}% im Vergleich zum Vormonat
                    </p>
                  )}
                </div>
                <div style={{ ...s.karte, borderLeftColor: THEME.colors.success }}>
                  <p style={{ ...s.karteLabel, margin: 0 }}>Mitgliederereignisse</p>
                  <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "6px 0 0" }}>{aktuell.mitgliederereignisse}</p>
                </div>
              </div>
              <div style={s.karte}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: THEME.colors.background }}>
                        {["Monat", "Bruttoauszahlungsbetrag", "Entwicklung zum Vormonat", "Mitgliederereignisse"].map(h => (
                          <th key={h} style={{ padding: "12px 16px", textAlign: "left", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {auszahlungen.map(a => (
                        <tr key={a.id} style={{ borderTop: "1px solid #2b3d52" }}>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text, fontWeight: 600 }}>{monatLabel(a.monat)}</td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{a.brutto_betrag?.toFixed(2)} €</td>
                          <td style={{ padding: "12px 16px", color: a.veraenderung_prozent == null ? THEME.colors.textMuted : (a.veraenderung_prozent >= 0 ? THEME.colors.success : THEME.colors.danger) }}>
                            {a.veraenderung_prozent == null ? "–" : `${a.veraenderung_prozent >= 0 ? "+" : ""}${a.veraenderung_prozent}%`}
                          </td>
                          <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary }}>{a.mitgliederereignisse}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>);
          })()}
        </div>
      )}

      {subTab === "uebersicht" && stats && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20 }}>
            {[
              { label: "Heute", wert: stats.checkins_heute },
              { label: "Diese Woche", wert: stats.checkins_woche },
              { label: "Dieser Monat", wert: stats.checkins_monat },
              { label: "Vormonat", wert: stats.checkins_vormonat },
            ].map(k => (
              <div key={k.label} style={{ ...s.karte, borderLeftColor: THEME.colors.primary }}>
                <p style={{ ...s.karteLabel, margin: 0 }}>{k.label}</p>
                <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "6px 0 0" }}>{k.wert}</p>
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
            <div style={s.karte}>
              <p style={{ color: THEME.colors.text, fontWeight: 600, marginBottom: 10 }}>Top-Wochentage</p>
              {stats.top_wochentage.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Noch keine Daten.</p>}
              {stats.top_wochentage.map(w => (
                <div key={w.wochentag} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                  <span>{w.wochentag}</span><span style={{ fontWeight: 600 }}>{w.anzahl}</span>
                </div>
              ))}
            </div>
            <div style={s.karte}>
              <p style={{ color: THEME.colors.text, fontWeight: 600, marginBottom: 10 }}>Stoßzeiten</p>
              {stats.stosszeiten.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Noch keine Daten.</p>}
              {stats.stosszeiten.map(s2 => (
                <div key={s2.stunde} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                  <span>{s2.stunde}:00 – {s2.stunde + 1}:00 Uhr</span><span style={{ fontWeight: 600 }}>{s2.anzahl}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {subTab === "checkins" && darfExterneCheckins && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {checkins.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Check-ins importiert.</p>}
          {checkins.map(c => (
            <div key={c.id} style={{ ...s.karte, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{c.mitglied_name || c.name_roh}</span>
                <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginLeft: 10 }}>{c.datum} · {c.zeit}</span>
              </div>
              {c.mitglied_id && (
                <button onClick={() => onMitgliedOeffnen && onMitgliedOeffnen(c.mitglied_id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Mitglied ansehen</button>
              )}
            </div>
          ))}
        </div>
      )}

      {subTab === "zuklaeren" && darfZuKlaeren && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {zuKlaerenGruppiert.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Nichts zu klären.</p>}
          {zuKlaerenGruppiert.map(c => (
            <div key={c.id} style={{ ...s.karte, padding: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{c.name_roh}</span>
                  {c.anzahl > 1 && <span style={{ marginLeft: 8, background: THEME.colors.surfaceMuted, color: THEME.colors.textMuted, borderRadius: 10, padding: "1px 8px", fontSize: THEME.fontSize.xs, fontWeight: 700 }}>×{c.anzahl}</span>}
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginLeft: 10 }}>{c.datum} · {c.zeit}</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => setNeuNutzerId(c.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Neuer Wellpass-Nutzer</button>
                  <button onClick={() => { setNutzerZuordnenId(c.id); setNutzerZuordnenSuche(""); }} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Nutzer zuordnen</button>
                  <button onClick={() => { setZuordnenId(c.id); setZuordnenSuche(""); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Mitglied zuordnen</button>
                  <button onClick={() => handleIgnorieren(c.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Ignorieren</button>
                </div>
              </div>
              {zuordnenId === c.id && (
                <div style={{ marginTop: 10, background: THEME.colors.background, borderRadius: 8, padding: 12 }}>
                  <input style={s.input} placeholder="Mitglied suchen…" value={zuordnenSuche} onChange={e => setZuordnenSuche(e.target.value)} autoFocus />
                  <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8 }}>
                    {gefilterteMitglieder.map(m => (
                      <div key={m.id} onClick={() => handleZuordnen(m.id)}
                        style={{ padding: "6px 10px", borderRadius: 6, cursor: "pointer", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                        {m.vorname} {m.nachname}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setZuordnenId(null)} style={{ ...s.btnGray, marginTop: 8, width: "auto", fontSize: THEME.fontSize.xs }}>Abbrechen</button>
                </div>
              )}
              {nutzerZuordnenId === c.id && (
                <div style={{ marginTop: 10, background: THEME.colors.background, borderRadius: 8, padding: 12 }}>
                  <input style={s.input} placeholder="Wellpass-Nutzer suchen…" value={nutzerZuordnenSuche} onChange={e => setNutzerZuordnenSuche(e.target.value)} autoFocus />
                  <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8 }}>
                    {gefilterteNutzer.map(n => (
                      <div key={n.id} onClick={() => handleNutzerZuordnen(n.id)}
                        style={{ padding: "6px 10px", borderRadius: 6, cursor: "pointer", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                        {n.vorname} {n.nachname}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setNutzerZuordnenId(null)} style={{ ...s.btnGray, marginTop: 8, width: "auto", fontSize: THEME.fontSize.xs }}>Abbrechen</button>
                </div>
              )}
              {neuNutzerId === c.id && (
                <PersonFormular
                  typ="wellpass" typLocked
                  initial={(() => {
                    const teile = c.name_roh.trim().split(/\s+/);
                    return { vorname: teile[0] || "", nachname: teile.slice(1).join(" ") || "" };
                  })()}
                  onSpeichern={handleNeuerNutzerGespeichert}
                  onAbbrechen={() => setNeuNutzerId(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ HANSEFIT ============
function HansefitSeite({ rechte }) {
  const darfExterneCheckins = rechte?.rolle === "admin" || !!rechte?.module?.checkins_extern;
  const darfZuKlaeren = rechte?.rolle === "admin" || !!rechte?.module?.checkins_zu_klaeren;
  // Auszahlungen zeigen tatsaechliche Umsatzzahlen - bewusst nur fuer Admins (analog zu Wellpass).
  const darfAuszahlungen = rechte?.rolle === "admin";
  const [subTab, setSubTab] = useState("uebersicht");
  const [stats, setStats] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [zuKlaeren, setZuKlaeren] = useState([]);
  const [auszahlungen, setAuszahlungen] = useState([]);
  const [hansefitNutzerListe, setHansefitNutzerListe] = useState([]);
  const [syncLaedt, setSyncLaedt] = useState(false);
  const [syncMeldung, setSyncMeldung] = useState("");
  const [neuNutzerId, setNeuNutzerId] = useState(null);
  const [nutzerZuordnenId, setNutzerZuordnenId] = useState(null);
  const [nutzerZuordnenSuche, setNutzerZuordnenSuche] = useState("");

  const laden = () => {
    fetch(`${API}/hansefit/statistiken`).then(r => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/hansefit/checkins?limit=100`).then(r => r.json()).then(d => setCheckins(d.checkins || [])).catch(() => {});
    fetch(`${API}/hansefit/zu-klaeren`).then(r => r.json()).then(d => setZuKlaeren(Array.isArray(d) ? d : [])).catch(() => {});
    if (darfAuszahlungen) {
      fetch(`${API}/hansefit/auszahlungen`).then(r => r.ok ? r.json() : []).then(d => setAuszahlungen(Array.isArray(d) ? d : [])).catch(() => {});
    }
  };
  useEffect(() => { laden(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (nutzerZuordnenId !== null && hansefitNutzerListe.length === 0) {
      fetch(`${API}/hansefit/nutzer`).then(r => r.json()).then(d => setHansefitNutzerListe(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [nutzerZuordnenId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = async () => {
    setSyncLaedt(true);
    setSyncMeldung("");
    try {
      const res = await fetch(`${API}/hansefit/sync`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) { setSyncMeldung(data.detail || "Synchronisierung fehlgeschlagen."); return; }
      const auszahlungenZusatz = data.auszahlungen && !data.auszahlungen.uebersprungen
        ? `, ${data.auszahlungen.neu} neue Auszahlungen`
        : "";
      setSyncMeldung(`✓ ${data.neu} neue Check-ins übernommen${auszahlungenZusatz}.`);
      laden();
    } catch {
      setSyncMeldung("Synchronisierung fehlgeschlagen.");
    } finally {
      setSyncLaedt(false);
    }
  };

  const handleIgnorieren = async (id) => {
    const res = await fetch(`${API}/hansefit/checkins/${id}/ignorieren`, { method: "POST" });
    if (!res.ok) { alert("Ignorieren fehlgeschlagen."); return; }
    laden();
  };

  const handleNeuerNutzerGespeichert = async (typ, data) => {
    if (data.vorschau && data.vorschau.anzahl > 0) {
      const res = await fetch(`${API}/hansefit/nutzer/${data.id}/rueckwirkend-zuordnen`, { method: "POST" });
      if (!res.ok) { alert("Nutzer wurde angelegt, aber die rückwirkende Zuordnung der Check-ins ist fehlgeschlagen."); }
    }
    setNeuNutzerId(null);
    setHansefitNutzerListe([]);
    laden();
  };

  const handleNutzerZuordnen = async (hansefitNutzerId) => {
    const res = await fetch(`${API}/hansefit/checkins/${nutzerZuordnenId}/nutzer-zuordnen`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ hansefit_nutzer_id: hansefitNutzerId }),
    });
    if (!res.ok) { alert("Zuordnen fehlgeschlagen."); return; }
    setNutzerZuordnenId(null); setNutzerZuordnenSuche("");
    laden();
  };

  const gefilterteHansefitNutzer = hansefitNutzerListe.filter(n =>
    !nutzerZuordnenSuche || `${n.vorname} ${n.nachname}`.toLowerCase().includes(nutzerZuordnenSuche.toLowerCase())
  ).slice(0, 30);

  // Gleiche Zusammenfassung wie bei Wellpass: eine externe Nutzer-ID soll in der
  // "Zu klären"-Liste nur einmal auftauchen, Aktionen wirken serverseitig ohnehin schon auf
  // alle offenen Check-ins derselben ID (siehe routers_hansefit.py).
  const zuKlaerenGruppiert = Object.values(zuKlaeren.reduce((gruppen, c) => {
    const schluessel = c.externe_user_id;
    if (!gruppen[schluessel]) gruppen[schluessel] = { ...c, anzahl: 0 };
    gruppen[schluessel].anzahl += 1;
    return gruppen;
  }, {}));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        <h1 style={{ ...s.heading, margin: 0 }}>Hansefit</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {syncMeldung && <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{syncMeldung}</span>}
          <button onClick={handleSync} disabled={syncLaedt} style={{ ...s.btn, width: "auto" }}>
            {syncLaedt ? "Synchronisiert…" : "🔄 Jetzt synchronisieren"}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
        {[["uebersicht", "Übersicht", true], ["checkins", "Check-ins", darfExterneCheckins], ["zuklaeren", `Zu klären${zuKlaeren.length > 0 ? ` (${zuKlaeren.length})` : ""}`, darfZuKlaeren], ["auszahlungen", "Auszahlungen", darfAuszahlungen]]
          .filter(([, , erlaubt]) => erlaubt)
          .map(([key, label]) => (
          <button key={key} onClick={() => setSubTab(key)}
            style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: subTab === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: subTab === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md }}>{label}</button>
        ))}
      </div>

      {subTab === "auszahlungen" && darfAuszahlungen && (
        <div>
          {auszahlungen.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Auszahlungsdaten importiert — auf „Jetzt synchronisieren" klicken.</p>}
          {auszahlungen.length > 0 && (() => {
            const checkinsListe = auszahlungen.filter(a => a.beschreibung === "Check-Ins");
            const gutschriftenListe = auszahlungen.filter(a => a.beschreibung !== "Check-Ins");
            const aktuell = checkinsListe[0];
            const monatLabel = (m) => {
              if (!m) return "–";
              const [j, mo] = m.split("-");
              return `${["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"][parseInt(mo,10)-1]} ${j}`;
            };
            const tabelle = (titel, zeilen, mitCheckinSpalte) => (
              <div style={{ marginBottom: 28 }}>
                <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, margin: "0 0 10px" }}>{titel}</h3>
                {zeilen.length === 0 ? (
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Keine Einträge.</p>
                ) : (
                  <div style={s.karte}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: THEME.colors.background }}>
                            {["Monat", "Gutschrift Nr.", ...(mitCheckinSpalte ? ["Check-Ins"] : ["Art"]), "Netto", "MwSt.", "Brutto"].map(h => (
                              <th key={h} style={{ padding: "12px 16px", textAlign: "left", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {zeilen.map(a => (
                            <tr key={a.id} style={{ borderTop: "1px solid #2b3d52" }}>
                              <td style={{ padding: "12px 16px", color: THEME.colors.text, fontWeight: 600 }}>{monatLabel(a.monat)}</td>
                              <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary }}>{a.gutschrift_nr}</td>
                              <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary }}>{mitCheckinSpalte ? (a.anzahl_checkins ?? "–") : a.beschreibung}</td>
                              <td style={{ padding: "12px 16px", color: THEME.colors.text }}>{a.betrag_netto?.toFixed(2)} €</td>
                              <td style={{ padding: "12px 16px", color: THEME.colors.textSecondary }}>{a.mwst_betrag?.toFixed(2)} €</td>
                              <td style={{ padding: "12px 16px", color: THEME.colors.text, fontWeight: 600 }}>{a.betrag_brutto?.toFixed(2)} €</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
            return (<>
              {aktuell && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 24 }}>
                  <div style={{ ...s.karte, borderLeftColor: THEME.colors.primary }}>
                    <p style={{ ...s.karteLabel, margin: 0 }}>{monatLabel(aktuell.monat)} (aktuellste Check-In-Gutschrift)</p>
                    <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "6px 0 0" }}>{aktuell.betrag_brutto?.toFixed(2)} €</p>
                  </div>
                  <div style={{ ...s.karte, borderLeftColor: THEME.colors.success }}>
                    <p style={{ ...s.karteLabel, margin: 0 }}>Check-Ins in dieser Gutschrift</p>
                    <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "6px 0 0" }}>{aktuell.anzahl_checkins ?? "–"}</p>
                  </div>
                </div>
              )}
              {tabelle("Check-Ins", checkinsListe, true)}
              {tabelle("Gutschriften", gutschriftenListe, false)}
            </>);
          })()}
        </div>
      )}

      {subTab === "uebersicht" && stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
          {[
            { label: "Heute", wert: stats.checkins_heute },
            { label: "Diese Woche", wert: stats.checkins_woche },
            { label: "Dieser Monat", wert: stats.checkins_monat },
            { label: "Dieses Jahr", wert: stats.checkins_jahr },
            { label: "Gesamt", wert: stats.checkins_gesamt },
          ].map(k => (
            <div key={k.label} style={{ ...s.karte, borderLeftColor: THEME.colors.primary }}>
              <p style={{ ...s.karteLabel, margin: 0 }}>{k.label}</p>
              <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xxxl, fontWeight: 700, margin: "6px 0 0" }}>{k.wert}</p>
            </div>
          ))}
        </div>
      )}

      {subTab === "checkins" && darfExterneCheckins && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {checkins.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Check-ins importiert.</p>}
          {checkins.map(c => (
            <div key={c.id} style={{ ...s.karte, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{c.hansefit_nutzer_name || c.name_roh || c.externe_user_id}</span>
                <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginLeft: 10 }}>{c.datum} · {c.zeit}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {subTab === "zuklaeren" && darfZuKlaeren && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {zuKlaerenGruppiert.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Nichts zu klären.</p>}
          {zuKlaerenGruppiert.map(c => (
            <div key={c.id} style={{ ...s.karte, padding: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{c.name_roh || c.externe_user_id}</span>
                  {c.name_roh && <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginLeft: 8 }}>{c.externe_user_id}</span>}
                  {c.anzahl > 1 && <span style={{ marginLeft: 8, background: THEME.colors.surfaceMuted, color: THEME.colors.textMuted, borderRadius: 10, padding: "1px 8px", fontSize: THEME.fontSize.xs, fontWeight: 700 }}>×{c.anzahl}</span>}
                  <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginLeft: 10 }}>{c.datum} · {c.zeit}</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => setNeuNutzerId(c.id)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Neuer Hansefit-Nutzer</button>
                  <button onClick={() => { setNutzerZuordnenId(c.id); setNutzerZuordnenSuche(""); }} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Nutzer zuordnen</button>
                  <button onClick={() => handleIgnorieren(c.id)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.xs, padding: "5px 10px" }}>Ignorieren</button>
                </div>
              </div>
              {nutzerZuordnenId === c.id && (
                <div style={{ marginTop: 10, background: THEME.colors.background, borderRadius: 8, padding: 12 }}>
                  <input style={s.input} placeholder="Hansefit-Nutzer suchen…" value={nutzerZuordnenSuche} onChange={e => setNutzerZuordnenSuche(e.target.value)} autoFocus />
                  <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8 }}>
                    {gefilterteHansefitNutzer.map(n => (
                      <div key={n.id} onClick={() => handleNutzerZuordnen(n.id)}
                        style={{ padding: "6px 10px", borderRadius: 6, cursor: "pointer", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                        {n.vorname} {n.nachname}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setNutzerZuordnenId(null)} style={{ ...s.btnGray, marginTop: 8, width: "auto", fontSize: THEME.fontSize.xs }}>Abbrechen</button>
                </div>
              )}
              {neuNutzerId === c.id && (
                <PersonFormular
                  typ="hansefit" typLocked
                  initial={(() => {
                    const teile = (c.name_roh || "").trim().split(/\s+/).filter(Boolean);
                    return { externe_id: c.externe_user_id, vorname: teile[0] || "", nachname: teile.slice(1).join(" ") };
                  })()}
                  onSpeichern={handleNeuerNutzerGespeichert}
                  onAbbrechen={() => setNeuNutzerId(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ WELLPASS-NUTZER (lokaler Test, getrennt von der regulaeren Mitgliederverwaltung) ============
function WellpassNutzerSeite({ nutzerOeffnen }) {
  const leerForm = { vorname: "", nachname: "", anrede: "", geburtsdatum: "", telefon: "", email: "", strasse: "", plz: "", ort: "", externe_wellpass_id: "", rfid_kartennummer: "", gueltig_von: "", gueltig_bis: "", notiz: "" };
  const [liste, setListe] = useState([]);
  const [suche, setSuche] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rfidFilter, setRfidFilter] = useState("");
  const [checkinsFilter, setCheckinsFilter] = useState("");
  const [formOffen, setFormOffen] = useState(false);
  const [bearbeitenId, setBearbeitenId] = useState(null);
  const [form, setForm] = useState(leerForm);
  const [fehler, setFehler] = useState("");
  const [vorschau, setVorschau] = useState(null);
  const [profilId, setProfilId] = useState(null);
  const [profil, setProfil] = useState(null);
  const [checkinHistorieOffen, setCheckinHistorieOffen] = useState(false);
  const [rfidDialogId, setRfidDialogId] = useState(null);
  const [rfidWert, setRfidWert] = useState("");
  const [rfidMedientyp, setRfidMedientyp] = useState("");
  const [umwandelnNutzer, setUmwandelnNutzer] = useState(null);

  const laden = () => {
    const params = new URLSearchParams();
    if (suche) params.set("suche", suche);
    if (statusFilter) params.set("status", statusFilter);
    if (rfidFilter) params.set("rfid", rfidFilter);
    if (checkinsFilter) params.set("checkins", checkinsFilter);
    fetch(`${API}/wellpass/nutzer?${params.toString()}`).then(r => r.json()).then(d => setListe(Array.isArray(d) ? d : [])).catch(() => {});
  };
  useEffect(() => { laden(); }, [suche, statusFilter, rfidFilter, checkinsFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const oeffneBearbeiten = (n) => {
    setForm({
      vorname: n.vorname, nachname: n.nachname,
      anrede: n.anrede || "", geburtsdatum: n.geburtsdatum || "",
      telefon: n.telefon || "", email: n.email || "",
      strasse: n.strasse || "", plz: n.plz || "", ort: n.ort || "",
      externe_wellpass_id: n.externe_wellpass_id || "", rfid_kartennummer: n.rfid_kartennummer || "",
      gueltig_von: n.gueltig_von || "", gueltig_bis: n.gueltig_bis || "", notiz: n.notiz || "",
    });
    setBearbeitenId(n.id); setFehler(""); setFormOffen(true);
  };

  const handleSpeichern = async () => {
    setFehler("");
    const rfidUmgerechnet = form.rfid_kartennummer ? (pcLeserZuKartennummer(form.rfid_kartennummer) ?? form.rfid_kartennummer) : null;
    const body = {
      vorname: form.vorname, nachname: form.nachname,
      anrede: form.anrede || null, geburtsdatum: form.geburtsdatum || null,
      telefon: form.telefon || null, email: form.email || null,
      strasse: form.strasse || null, plz: form.plz || null, ort: form.ort || null,
      externe_wellpass_id: form.externe_wellpass_id || null,
      rfid_kartennummer: rfidUmgerechnet != null ? String(rfidUmgerechnet) : null,
      gueltig_von: form.gueltig_von || null, gueltig_bis: form.gueltig_bis || null,
      notiz: form.notiz || null,
    };
    const url = bearbeitenId ? `${API}/wellpass/nutzer/${bearbeitenId}` : `${API}/wellpass/nutzer`;
    const method = bearbeitenId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) { setFehler(data.detail || "Fehler beim Speichern."); return; }
    setFormOffen(false);
    laden();
    if (!bearbeitenId && data.vorschau && data.vorschau.anzahl > 0) {
      setVorschau({ nutzerId: data.id, ...data.vorschau });
    }
  };

  const handleRueckwirkendZuordnen = async () => {
    if (!vorschau) return;
    const res = await fetch(`${API}/wellpass/nutzer/${vorschau.nutzerId}/rueckwirkend-zuordnen`, { method: "POST" });
    if (!res.ok) { alert("Rückwirkende Zuordnung fehlgeschlagen."); return; }
    setVorschau(null);
    laden();
  };

  const handleStatusAendern = async (id, aktion) => {
    const res = await fetch(`${API}/wellpass/nutzer/${id}/${aktion}`, { method: "POST" });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); return; }
    laden();
    if (profilId === id) oeffneProfil(id);
  };

  const handleLoeschen = async (n) => {
    if (!window.confirm(`${n.vorname} ${n.nachname} wirklich unwiderruflich löschen?`)) return;
    const res = await fetch(`${API}/wellpass/nutzer/${n.id}`, { method: "DELETE" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(typeof data.detail === "string" ? data.detail : "Löschen fehlgeschlagen."); return; }
    setProfilId(null);
    laden();
  };

  const oeffneProfil = (id) => {
    setProfilId(id); setProfil(null); setCheckinHistorieOffen(false);
    fetch(`${API}/wellpass/nutzer/${id}`).then(r => r.json()).then(setProfil).catch(() => {});
  };
  useEffect(() => { if (nutzerOeffnen) oeffneProfil(nutzerOeffnen); }, [nutzerOeffnen]); // eslint-disable-line react-hooks/exhaustive-deps

  const oeffneRfidDialog = (n) => { setRfidDialogId(n.id); setRfidWert(n.rfid_kartennummer || ""); setRfidMedientyp(n.rfid_medientyp || ""); };
  const handleRfidSpeichern = async () => {
    let karte = rfidWert.trim();
    const umgerechnet = pcLeserZuKartennummer(karte);
    if (umgerechnet != null) { karte = String(umgerechnet); setRfidWert(karte); }
    const res = await fetch(`${API}/wellpass/nutzer/${rfidDialogId}/rfid`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rfid_kartennummer: karte, medientyp: rfidMedientyp || null }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.detail || "Fehler."); return; }
    setRfidDialogId(null);
    laden();
  };
  const handleRfidEntfernen = async (id) => {
    const res = await fetch(`${API}/wellpass/nutzer/${id}/rfid`, { method: "DELETE" });
    if (!res.ok) { alert("RFID konnte nicht entfernt werden."); return; }
    laden();
  };

  const statusFarbe = (status) => status === "aktiv" ? THEME.colors.success : status === "gesperrt" ? THEME.colors.danger : THEME.colors.textMuted;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        <h1 style={{ ...s.heading, margin: 0 }}>Wellpass-Nutzer</h1>
      </div>

      {vorschau && (
        <div style={{ ...s.karte, borderLeftColor: THEME.colors.warning, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <span style={{ color: THEME.colors.text }}>
            {vorschau.anzahl} bestehende Check-ins gefunden ({vorschau.erster_checkin} – {vorschau.letzter_checkin}). Jetzt rückwirkend zuordnen?
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleRueckwirkendZuordnen} style={{ ...s.btn, width: "auto" }}>Zuordnen</button>
            <button onClick={() => setVorschau(null)} style={{ ...s.btnGray, width: "auto" }}>Nicht jetzt</button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input style={{ ...s.input, maxWidth: 260 }} placeholder="Suche (Name, externe ID, RFID)…" value={suche} onChange={e => setSuche(e.target.value)} />
        <select style={{ ...s.input, width: "auto" }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Alle Status</option>
          <option value="aktiv">Aktiv</option>
          <option value="gesperrt">Gesperrt</option>
          <option value="archiviert">Archiviert</option>
        </select>
        <select style={{ ...s.input, width: "auto" }} value={rfidFilter} onChange={e => setRfidFilter(e.target.value)}>
          <option value="">RFID: alle</option>
          <option value="mit">Mit RFID</option>
          <option value="ohne">Ohne RFID</option>
        </select>
        <select style={{ ...s.input, width: "auto" }} value={checkinsFilter} onChange={e => setCheckinsFilter(e.target.value)}>
          <option value="">Check-ins: alle</option>
          <option value="mit">Mit Check-ins</option>
          <option value="ohne">Ohne Check-ins</option>
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {liste.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine Wellpass-Nutzer gefunden.</p>}
        {liste.map(n => (
          <div key={n.id} onClick={() => oeffneProfil(n.id)} style={{ ...s.karte, padding: 14, cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <div>
                <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{n.vorname} {n.nachname}</span>
                <span style={{ marginLeft: 10, color: statusFarbe(n.zutrittsstatus), fontSize: THEME.fontSize.xs, fontWeight: 600, textTransform: "uppercase" }}>{n.zutrittsstatus}</span>
                <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 4 }}>
                  {n.externe_wellpass_id && <span>ID: {n.externe_wellpass_id} · </span>}
                  {n.rfid_kartennummer ? <span>RFID: {n.rfid_kartennummer}</span> : <span>Keine RFID</span>}
                  {n.letzter_checkin && <span> · Letzter Check-in: {n.letzter_checkin}</span>}
                  <span> · Monat: {n.checkins_monat} · Jahr: {n.checkins_jahr}</span>
                </div>
              </div>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.lg }}>›</span>
            </div>
          </div>
        ))}
      </div>

      {formOffen && (
        <div style={s.modalBg} onClick={() => setFormOffen(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>Wellpass-Nutzer bearbeiten</h2>
            {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={s.inputGroup}>
                <label style={s.label}>Anrede</label>
                <select style={s.input} value={form.anrede} onChange={e => setForm({ ...form, anrede: e.target.value })}><option value="">–</option><option>Herr</option><option>Frau</option></select>
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Vorname</label>
                <input style={s.input} value={form.vorname} onChange={e => setForm({ ...form, vorname: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Nachname</label>
                <input style={s.input} value={form.nachname} onChange={e => setForm({ ...form, nachname: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Geburtsdatum</label>
                <input type="date" style={s.input} value={form.geburtsdatum} onChange={e => setForm({ ...form, geburtsdatum: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Telefon</label>
                <input style={s.input} value={form.telefon} onChange={e => setForm({ ...form, telefon: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>E-Mail</label>
                <input type="email" style={s.input} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Straße</label>
                <input style={s.input} value={form.strasse} onChange={e => setForm({ ...form, strasse: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>PLZ</label>
                <input style={s.input} value={form.plz} onChange={e => setForm({ ...form, plz: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Ort</label>
                <input style={s.input} value={form.ort} onChange={e => setForm({ ...form, ort: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>RFID-Kartennummer</label>
                <input style={s.input} value={form.rfid_kartennummer} onChange={e => setForm({ ...form, rfid_kartennummer: e.target.value })} />
              </div>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Notiz</label>
              <textarea style={{ ...s.input, minHeight: 70 }} value={form.notiz} onChange={e => setForm({ ...form, notiz: e.target.value })} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
              <button onClick={() => setFormOffen(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
              <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {rfidDialogId && (
        <div style={s.modalBg} onClick={() => setRfidDialogId(null)}>
          <div style={{ ...s.modal, width: "min(420px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>RFID-Band zuweisen</h2>
            <div style={s.inputGroup}>
              <label style={s.label}>Kartennummer</label>
              <input style={s.input} value={rfidWert} onChange={e => setRfidWert(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleRfidSpeichern(); }} autoFocus />
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Medientyp (für TTLock-Zutritt)</label>
              <select style={s.input} value={rfidMedientyp} onChange={e => setRfidMedientyp(e.target.value)}>
                <option value="">Automatisch (Band)</option>
                <option value="karte">Karte</option>
                <option value="band">Band</option>
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 10 }}>
              <button onClick={() => { handleRfidEntfernen(rfidDialogId); setRfidDialogId(null); }} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>RFID entfernen</button>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setRfidDialogId(null)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                <button onClick={handleRfidSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {umwandelnNutzer && (
        <PersonFormular
          typ="mitglied" typLocked
          initial={{
            anrede: umwandelnNutzer.anrede, vorname: umwandelnNutzer.vorname, nachname: umwandelnNutzer.nachname,
            geburtsdatum: umwandelnNutzer.geburtsdatum, telefon: umwandelnNutzer.telefon,
            email: umwandelnNutzer.email, strasse: umwandelnNutzer.strasse, plz: umwandelnNutzer.plz, ort: umwandelnNutzer.ort,
          }}
          konvertierung={{ richtung: "zu_mitglied", quelleTyp: "wellpass", quelleId: umwandelnNutzer.id, quelleName: `${umwandelnNutzer.vorname} ${umwandelnNutzer.nachname}` }}
          onSpeichern={() => { setUmwandelnNutzer(null); laden(); }}
          onAbbrechen={() => setUmwandelnNutzer(null)}
        />
      )}

      {profilId && (
        <div style={s.modalBg} onClick={() => setProfilId(null)}>
          <div style={{ ...s.modal, width: "min(960px, 95vw)", padding: 40 }} onClick={e => e.stopPropagation()}>
            {!profil && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.lg }}>Lädt…</p>}
            {profil && (
              <>
                <h2 style={{ color: THEME.colors.text, marginTop: 0, fontSize: THEME.fontSize.xxl }}>{profil.vorname} {profil.nachname}</h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                  <div>
                    <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl }}>Stammdaten</h3>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Externe ID: {profil.externe_wellpass_id || "–"}</p>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Status: {profil.zutrittsstatus}</p>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Notiz: {profil.notiz || "–"}</p>
                  </div>
                  <div>
                    <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl }}>Zutritt</h3>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>RFID: {profil.rfid_kartennummer || "Keine RFID"}</p>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Gültig: {profil.gueltig_von || "–"} bis {profil.gueltig_bis || "–"}</p>
                  </div>
                </div>
                <TTLockZutrittZeile personTyp="wellpass" personId={profil.id} />
                <MitgliederAppZugangBereich herkunft="wellpass" quelleId={profil.id} />
                <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl, marginTop: 20 }}>Wellpass-Statistik</h3>
                <div style={{ display: "flex", gap: 20, marginBottom: 16, flexWrap: "wrap" }}>
                  {[["Heute", profil.statistik.checkins_heute], ["Woche", profil.statistik.checkins_woche], ["Monat", profil.statistik.checkins_monat], ["Jahr", profil.statistik.checkins_jahr], ["Gesamt", profil.statistik.checkins_gesamt]].map(([label, wert]) => (
                    <div key={label}>
                      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>{label}</p>
                      <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xl2, fontWeight: 700, margin: 0 }}>{wert}</p>
                    </div>
                  ))}
                </div>
                <h3 onClick={() => setCheckinHistorieOffen(o => !o)} style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, userSelect: "none" }}>
                  Check-in-Historie <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base }}>{checkinHistorieOffen ? "▲ einklappen" : "▼ anzeigen"}</span>
                </h3>
                {checkinHistorieOffen && (
                  <div style={{ maxHeight: 240, overflowY: "auto" }}>
                    {profil.statistik.verlauf.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Noch keine Check-ins.</p>}
                    {profil.statistik.verlauf.map((v, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid #2b3d52", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                        <span>{v.datum}</span><span>{v.zeit}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 28, paddingTop: 28, borderTop: `1px solid ${THEME.colors.border}` }}>
                  <button onClick={() => { setProfilId(null); oeffneBearbeiten(profil); }} style={{ ...s.btnGray, width: "auto" }}>Bearbeiten</button>
                  <button onClick={() => { setProfilId(null); oeffneRfidDialog(profil); }} style={{ ...s.btnGray, width: "auto" }}>RFID</button>
                  {profil.zutrittsstatus !== "gesperrt" && <button onClick={() => handleStatusAendern(profil.id, "sperren")} style={{ ...s.btnGray, width: "auto" }}>Sperren</button>}
                  {profil.zutrittsstatus !== "aktiv" && <button onClick={() => handleStatusAendern(profil.id, "aktivieren")} style={{ ...s.btnGray, width: "auto" }}>Freigeben</button>}
                  {profil.zutrittsstatus !== "archiviert" && <button onClick={() => handleStatusAendern(profil.id, "archivieren")} style={{ ...s.btnGray, width: "auto" }}>Archivieren</button>}
                  {!profil.mitglied_id && <button onClick={() => { setProfilId(null); setUmwandelnNutzer(profil); }} style={{ ...s.btn, width: "auto", background: THEME.colors.warning }}>🔄 In Mitglied umwandeln</button>}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                  <button onClick={() => handleLoeschen(profil)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Löschen</button>
                  <button onClick={() => setProfilId(null)} style={{ ...s.btnGray, width: "auto" }}>Schließen</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function HansefitNutzerSeite({ nutzerOeffnen }) {
  const leerForm = { vorname: "", nachname: "", anrede: "", geburtsdatum: "", telefon: "", email: "", strasse: "", plz: "", ort: "", externe_hansefit_id: "", rfid_kartennummer: "", gueltig_von: "", gueltig_bis: "", notiz: "" };
  const [liste, setListe] = useState([]);
  const [suche, setSuche] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rfidFilter, setRfidFilter] = useState("");
  const [checkinsFilter, setCheckinsFilter] = useState("");
  const [formOffen, setFormOffen] = useState(false);
  const [bearbeitenId, setBearbeitenId] = useState(null);
  const [form, setForm] = useState(leerForm);
  const [fehler, setFehler] = useState("");
  const [vorschau, setVorschau] = useState(null);
  const [profilId, setProfilId] = useState(null);
  const [profil, setProfil] = useState(null);
  const [checkinHistorieOffen, setCheckinHistorieOffen] = useState(false);
  const [rfidDialogId, setRfidDialogId] = useState(null);
  const [rfidWert, setRfidWert] = useState("");
  const [rfidMedientyp, setRfidMedientyp] = useState("");
  const [umwandelnNutzer, setUmwandelnNutzer] = useState(null);

  const laden = () => {
    const params = new URLSearchParams();
    if (suche) params.set("suche", suche);
    if (statusFilter) params.set("status", statusFilter);
    if (rfidFilter) params.set("rfid", rfidFilter);
    if (checkinsFilter) params.set("checkins", checkinsFilter);
    fetch(`${API}/hansefit/nutzer?${params.toString()}`).then(r => r.json()).then(d => setListe(Array.isArray(d) ? d : [])).catch(() => {});
  };
  useEffect(() => { laden(); }, [suche, statusFilter, rfidFilter, checkinsFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const oeffneBearbeiten = (n) => {
    setForm({
      vorname: n.vorname, nachname: n.nachname,
      anrede: n.anrede || "", geburtsdatum: n.geburtsdatum || "",
      telefon: n.telefon || "", email: n.email || "",
      strasse: n.strasse || "", plz: n.plz || "", ort: n.ort || "",
      externe_hansefit_id: n.externe_hansefit_id || "", rfid_kartennummer: n.rfid_kartennummer || "",
      gueltig_von: n.gueltig_von || "", gueltig_bis: n.gueltig_bis || "", notiz: n.notiz || "",
    });
    setBearbeitenId(n.id); setFehler(""); setFormOffen(true);
  };

  const handleSpeichern = async () => {
    setFehler("");
    const rfidUmgerechnet = form.rfid_kartennummer ? (pcLeserZuKartennummer(form.rfid_kartennummer) ?? form.rfid_kartennummer) : null;
    const body = {
      vorname: form.vorname, nachname: form.nachname,
      anrede: form.anrede || null, geburtsdatum: form.geburtsdatum || null,
      telefon: form.telefon || null, email: form.email || null,
      strasse: form.strasse || null, plz: form.plz || null, ort: form.ort || null,
      externe_hansefit_id: form.externe_hansefit_id || null,
      rfid_kartennummer: rfidUmgerechnet != null ? String(rfidUmgerechnet) : null,
      gueltig_von: form.gueltig_von || null, gueltig_bis: form.gueltig_bis || null,
      notiz: form.notiz || null,
    };
    const url = bearbeitenId ? `${API}/hansefit/nutzer/${bearbeitenId}` : `${API}/hansefit/nutzer`;
    const method = bearbeitenId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) { setFehler(data.detail || "Fehler beim Speichern."); return; }
    setFormOffen(false);
    laden();
    if (!bearbeitenId && data.vorschau && data.vorschau.anzahl > 0) {
      setVorschau({ nutzerId: data.id, ...data.vorschau });
    }
  };

  const handleRueckwirkendZuordnen = async () => {
    if (!vorschau) return;
    const res = await fetch(`${API}/hansefit/nutzer/${vorschau.nutzerId}/rueckwirkend-zuordnen`, { method: "POST" });
    if (!res.ok) { alert("Rückwirkende Zuordnung fehlgeschlagen."); return; }
    setVorschau(null);
    laden();
  };

  const handleStatusAendern = async (id, aktion) => {
    const res = await fetch(`${API}/hansefit/nutzer/${id}/${aktion}`, { method: "POST" });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); return; }
    laden();
    if (profilId === id) oeffneProfil(id);
  };

  const handleLoeschen = async (n) => {
    if (!window.confirm(`${n.vorname} ${n.nachname} wirklich unwiderruflich löschen?`)) return;
    const res = await fetch(`${API}/hansefit/nutzer/${n.id}`, { method: "DELETE" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(typeof data.detail === "string" ? data.detail : "Löschen fehlgeschlagen."); return; }
    setProfilId(null);
    laden();
  };

  const oeffneProfil = (id) => {
    setProfilId(id); setProfil(null); setCheckinHistorieOffen(false);
    fetch(`${API}/hansefit/nutzer/${id}`).then(r => r.json()).then(setProfil).catch(() => {});
  };
  useEffect(() => { if (nutzerOeffnen) oeffneProfil(nutzerOeffnen); }, [nutzerOeffnen]); // eslint-disable-line react-hooks/exhaustive-deps

  const oeffneRfidDialog = (n) => { setRfidDialogId(n.id); setRfidWert(n.rfid_kartennummer || ""); setRfidMedientyp(n.rfid_medientyp || ""); };
  const handleRfidSpeichern = async () => {
    let karte = rfidWert.trim();
    const umgerechnet = pcLeserZuKartennummer(karte);
    if (umgerechnet != null) { karte = String(umgerechnet); setRfidWert(karte); }
    const res = await fetch(`${API}/hansefit/nutzer/${rfidDialogId}/rfid`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rfid_kartennummer: karte, medientyp: rfidMedientyp || null }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.detail || "Fehler."); return; }
    setRfidDialogId(null);
    laden();
  };
  const handleRfidEntfernen = async (id) => {
    const res = await fetch(`${API}/hansefit/nutzer/${id}/rfid`, { method: "DELETE" });
    if (!res.ok) { alert("RFID konnte nicht entfernt werden."); return; }
    laden();
  };

  const statusFarbe = (status) => status === "aktiv" ? THEME.colors.success : status === "gesperrt" ? THEME.colors.danger : THEME.colors.textMuted;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        <h1 style={{ ...s.heading, margin: 0 }}>Hansefit-Nutzer</h1>
      </div>

      {vorschau && (
        <div style={{ ...s.karte, borderLeftColor: THEME.colors.warning, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <span style={{ color: THEME.colors.text }}>
            {vorschau.anzahl} bestehende Check-ins gefunden ({vorschau.erster_checkin} – {vorschau.letzter_checkin}). Jetzt rückwirkend zuordnen?
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleRueckwirkendZuordnen} style={{ ...s.btn, width: "auto" }}>Zuordnen</button>
            <button onClick={() => setVorschau(null)} style={{ ...s.btnGray, width: "auto" }}>Nicht jetzt</button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input style={{ ...s.input, maxWidth: 260 }} placeholder="Suche (Name, externe ID, RFID)…" value={suche} onChange={e => setSuche(e.target.value)} />
        <select style={{ ...s.input, width: "auto" }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Alle Status</option>
          <option value="aktiv">Aktiv</option>
          <option value="gesperrt">Gesperrt</option>
          <option value="archiviert">Archiviert</option>
        </select>
        <select style={{ ...s.input, width: "auto" }} value={rfidFilter} onChange={e => setRfidFilter(e.target.value)}>
          <option value="">RFID: alle</option>
          <option value="mit">Mit RFID</option>
          <option value="ohne">Ohne RFID</option>
        </select>
        <select style={{ ...s.input, width: "auto" }} value={checkinsFilter} onChange={e => setCheckinsFilter(e.target.value)}>
          <option value="">Check-ins: alle</option>
          <option value="mit">Mit Check-ins</option>
          <option value="ohne">Ohne Check-ins</option>
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {liste.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine Hansefit-Nutzer gefunden.</p>}
        {liste.map(n => (
          <div key={n.id} onClick={() => oeffneProfil(n.id)} style={{ ...s.karte, padding: 14, cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <div>
                <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{n.vorname} {n.nachname}</span>
                <span style={{ marginLeft: 10, color: statusFarbe(n.zutrittsstatus), fontSize: THEME.fontSize.xs, fontWeight: 600, textTransform: "uppercase" }}>{n.zutrittsstatus}</span>
                <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 4 }}>
                  <span>ID: {n.externe_hansefit_id} · </span>
                  {n.rfid_kartennummer ? <span>RFID: {n.rfid_kartennummer}</span> : <span>Keine RFID</span>}
                  {n.letzter_checkin && <span> · Letzter Check-in: {n.letzter_checkin}</span>}
                  <span> · Monat: {n.checkins_monat} · Jahr: {n.checkins_jahr}</span>
                </div>
              </div>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.lg }}>›</span>
            </div>
          </div>
        ))}
      </div>

      {formOffen && (
        <div style={s.modalBg} onClick={() => setFormOffen(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>Hansefit-Nutzer bearbeiten</h2>
            {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={s.inputGroup}>
                <label style={s.label}>Anrede</label>
                <select style={s.input} value={form.anrede} onChange={e => setForm({ ...form, anrede: e.target.value })}><option value="">–</option><option>Herr</option><option>Frau</option></select>
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Vorname</label>
                <input style={s.input} value={form.vorname} onChange={e => setForm({ ...form, vorname: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Nachname</label>
                <input style={s.input} value={form.nachname} onChange={e => setForm({ ...form, nachname: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Geburtsdatum</label>
                <input type="date" style={s.input} value={form.geburtsdatum} onChange={e => setForm({ ...form, geburtsdatum: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Telefon</label>
                <input style={s.input} value={form.telefon} onChange={e => setForm({ ...form, telefon: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>E-Mail</label>
                <input type="email" style={s.input} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Straße</label>
                <input style={s.input} value={form.strasse} onChange={e => setForm({ ...form, strasse: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>PLZ</label>
                <input style={s.input} value={form.plz} onChange={e => setForm({ ...form, plz: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Ort</label>
                <input style={s.input} value={form.ort} onChange={e => setForm({ ...form, ort: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Externe Hansefit-ID</label>
                <input style={s.input} value={form.externe_hansefit_id} onChange={e => setForm({ ...form, externe_hansefit_id: e.target.value })} />
                {!form.externe_hansefit_id && <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.xs, marginTop: 4 }}>⚠ Ohne externe ID kein automatisches Check-in-Matching.</p>}
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>RFID-Kartennummer</label>
                <input style={s.input} value={form.rfid_kartennummer} onChange={e => setForm({ ...form, rfid_kartennummer: e.target.value })} />
              </div>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Notiz</label>
              <textarea style={{ ...s.input, minHeight: 70 }} value={form.notiz} onChange={e => setForm({ ...form, notiz: e.target.value })} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
              <button onClick={() => setFormOffen(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
              <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {rfidDialogId && (
        <div style={s.modalBg} onClick={() => setRfidDialogId(null)}>
          <div style={{ ...s.modal, width: "min(420px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>RFID-Band zuweisen</h2>
            <div style={s.inputGroup}>
              <label style={s.label}>Kartennummer</label>
              <input style={s.input} value={rfidWert} onChange={e => setRfidWert(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleRfidSpeichern(); }} autoFocus />
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Medientyp (für TTLock-Zutritt)</label>
              <select style={s.input} value={rfidMedientyp} onChange={e => setRfidMedientyp(e.target.value)}>
                <option value="">Automatisch (Band)</option>
                <option value="karte">Karte</option>
                <option value="band">Band</option>
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 10 }}>
              <button onClick={() => { handleRfidEntfernen(rfidDialogId); setRfidDialogId(null); }} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>RFID entfernen</button>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setRfidDialogId(null)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                <button onClick={handleRfidSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {umwandelnNutzer && (
        <PersonFormular
          typ="mitglied" typLocked
          initial={{
            anrede: umwandelnNutzer.anrede, vorname: umwandelnNutzer.vorname, nachname: umwandelnNutzer.nachname,
            geburtsdatum: umwandelnNutzer.geburtsdatum, telefon: umwandelnNutzer.telefon,
            email: umwandelnNutzer.email, strasse: umwandelnNutzer.strasse, plz: umwandelnNutzer.plz, ort: umwandelnNutzer.ort,
          }}
          konvertierung={{ richtung: "zu_mitglied", quelleTyp: "hansefit", quelleId: umwandelnNutzer.id, quelleName: `${umwandelnNutzer.vorname} ${umwandelnNutzer.nachname}` }}
          onSpeichern={() => { setUmwandelnNutzer(null); laden(); }}
          onAbbrechen={() => setUmwandelnNutzer(null)}
        />
      )}

      {profilId && (
        <div style={s.modalBg} onClick={() => setProfilId(null)}>
          <div style={{ ...s.modal, width: "min(960px, 95vw)", padding: 40 }} onClick={e => e.stopPropagation()}>
            {!profil && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.lg }}>Lädt…</p>}
            {profil && (
              <>
                <h2 style={{ color: THEME.colors.text, marginTop: 0, fontSize: THEME.fontSize.xxl }}>{profil.vorname} {profil.nachname}</h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                  <div>
                    <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl }}>Stammdaten</h3>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Externe ID: {profil.externe_hansefit_id}</p>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Status: {profil.zutrittsstatus}</p>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Notiz: {profil.notiz || "–"}</p>
                  </div>
                  <div>
                    <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl }}>Zutritt</h3>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>RFID: {profil.rfid_kartennummer || "Keine RFID"}</p>
                    <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Gültig: {profil.gueltig_von || "–"} bis {profil.gueltig_bis || "–"}</p>
                  </div>
                </div>
                <TTLockZutrittZeile personTyp="hansefit" personId={profil.id} />
                <MitgliederAppZugangBereich herkunft="hansefit" quelleId={profil.id} />
                <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl, marginTop: 20 }}>Hansefit-Statistik</h3>
                <div style={{ display: "flex", gap: 20, marginBottom: 16, flexWrap: "wrap" }}>
                  {[["Heute", profil.statistik.checkins_heute], ["Woche", profil.statistik.checkins_woche], ["Monat", profil.statistik.checkins_monat], ["Jahr", profil.statistik.checkins_jahr], ["Gesamt", profil.statistik.checkins_gesamt]].map(([label, wert]) => (
                    <div key={label}>
                      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>{label}</p>
                      <p style={{ color: THEME.colors.primary, fontSize: THEME.fontSize.xl2, fontWeight: 700, margin: 0 }}>{wert}</p>
                    </div>
                  ))}
                </div>
                <h3 onClick={() => setCheckinHistorieOffen(o => !o)} style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, userSelect: "none" }}>
                  Check-in-Historie <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base }}>{checkinHistorieOffen ? "▲ einklappen" : "▼ anzeigen"}</span>
                </h3>
                {checkinHistorieOffen && (
                  <div style={{ maxHeight: 240, overflowY: "auto" }}>
                    {profil.statistik.verlauf.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Noch keine Check-ins.</p>}
                    {profil.statistik.verlauf.map((v, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid #2b3d52", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                        <span>{v.datum}</span><span>{v.zeit}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 28, paddingTop: 28, borderTop: `1px solid ${THEME.colors.border}` }}>
                  <button onClick={() => { setProfilId(null); oeffneBearbeiten(profil); }} style={{ ...s.btnGray, width: "auto" }}>Bearbeiten</button>
                  <button onClick={() => { setProfilId(null); oeffneRfidDialog(profil); }} style={{ ...s.btnGray, width: "auto" }}>RFID</button>
                  {profil.zutrittsstatus !== "gesperrt" && <button onClick={() => handleStatusAendern(profil.id, "sperren")} style={{ ...s.btnGray, width: "auto" }}>Sperren</button>}
                  {profil.zutrittsstatus !== "aktiv" && <button onClick={() => handleStatusAendern(profil.id, "aktivieren")} style={{ ...s.btnGray, width: "auto" }}>Freigeben</button>}
                  {profil.zutrittsstatus !== "archiviert" && <button onClick={() => handleStatusAendern(profil.id, "archivieren")} style={{ ...s.btnGray, width: "auto" }}>Archivieren</button>}
                  {!profil.mitglied_id && <button onClick={() => { setProfilId(null); setUmwandelnNutzer(profil); }} style={{ ...s.btn, width: "auto", background: THEME.colors.warning }}>🔄 In Mitglied umwandeln</button>}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                  <button onClick={() => handleLoeschen(profil)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Löschen</button>
                  <button onClick={() => setProfilId(null)} style={{ ...s.btnGray, width: "auto" }}>Schließen</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SonstigeNutzerSeite({ nutzerOeffnen }) {
  // Mirrort WellpassNutzerSeite/HansefitNutzerSeite, aber bewusst ohne Check-in-Konzept (keine
  // Vorschau/Statistik/Historie) und mit nur Vorname/Nachname/RFID/Zutrittsstatus/Gueltig-bis/
  // Notiz als Feldern - SonstigeNutzer hat keine Stammdaten-Spalten wie Wellpass/Hansefit.
  const leerForm = { vorname: "", nachname: "", email: "", telefon: "", rfid_kartennummer: "", gueltig_bis: "", notiz: "" };
  const [liste, setListe] = useState([]);
  const [suche, setSuche] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rfidFilter, setRfidFilter] = useState("");
  const [formOffen, setFormOffen] = useState(false);
  const [bearbeitenId, setBearbeitenId] = useState(null);
  const [form, setForm] = useState(leerForm);
  const [fehler, setFehler] = useState("");
  const [profilId, setProfilId] = useState(null);
  const [profil, setProfil] = useState(null);
  const [rfidDialogId, setRfidDialogId] = useState(null);
  const [rfidWert, setRfidWert] = useState("");
  const [rfidMedientyp, setRfidMedientyp] = useState("");
  const [umwandelnNutzer, setUmwandelnNutzer] = useState(null);

  const laden = () => {
    const params = new URLSearchParams();
    if (suche) params.set("suche", suche);
    if (statusFilter) params.set("status", statusFilter);
    if (rfidFilter) params.set("rfid", rfidFilter);
    fetch(`${API}/sonstige/nutzer?${params.toString()}`).then(r => r.json()).then(d => setListe(Array.isArray(d) ? d : [])).catch(() => {});
  };
  useEffect(() => { laden(); }, [suche, statusFilter, rfidFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const oeffneBearbeiten = (n) => {
    setForm({
      vorname: n.vorname, nachname: n.nachname, email: n.email || "", telefon: n.telefon || "",
      rfid_kartennummer: n.rfid_kartennummer || "", gueltig_bis: n.gueltig_bis || "", notiz: n.notiz || "",
    });
    setBearbeitenId(n.id); setFehler(""); setFormOffen(true);
  };

  const handleSpeichern = async () => {
    setFehler("");
    const rfidUmgerechnet = form.rfid_kartennummer ? (pcLeserZuKartennummer(form.rfid_kartennummer) ?? form.rfid_kartennummer) : null;
    const body = {
      vorname: form.vorname, nachname: form.nachname, email: form.email || null, telefon: form.telefon || null,
      rfid_kartennummer: rfidUmgerechnet != null ? String(rfidUmgerechnet) : null,
      gueltig_bis: form.gueltig_bis || null, notiz: form.notiz || null,
    };
    const url = bearbeitenId ? `${API}/sonstige/nutzer/${bearbeitenId}` : `${API}/sonstige/nutzer`;
    const method = bearbeitenId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) { setFehler(data.detail || "Fehler beim Speichern."); return; }
    setFormOffen(false);
    laden();
  };

  const handleStatusAendern = async (id, aktion) => {
    const res = await fetch(`${API}/sonstige/nutzer/${id}/${aktion}`, { method: "POST" });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); return; }
    laden();
    if (profilId === id) oeffneProfil(id);
  };

  const handleUmwandeln = async (nutzer, ziel) => {
    const res = await fetch(`${API}/sonstige/nutzer/${nutzer.id}/umwandeln-${ziel}`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(typeof data.detail === "string" ? data.detail : "Umwandeln fehlgeschlagen."); return; }
    laden();
    if (profilId === nutzer.id) oeffneProfil(nutzer.id);
  };

  const handleLoeschen = async (n) => {
    if (!window.confirm(`${n.vorname} ${n.nachname} wirklich unwiderruflich löschen?`)) return;
    const res = await fetch(`${API}/sonstige/nutzer/${n.id}`, { method: "DELETE" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(typeof data.detail === "string" ? data.detail : "Löschen fehlgeschlagen."); return; }
    setProfilId(null);
    laden();
  };

  const oeffneProfil = (id) => {
    setProfilId(id); setProfil(null);
    fetch(`${API}/sonstige/nutzer/${id}`).then(r => r.json()).then(setProfil).catch(() => {});
  };
  useEffect(() => { if (nutzerOeffnen) oeffneProfil(nutzerOeffnen); }, [nutzerOeffnen]); // eslint-disable-line react-hooks/exhaustive-deps

  const oeffneRfidDialog = (n) => { setRfidDialogId(n.id); setRfidWert(n.rfid_kartennummer || ""); setRfidMedientyp(n.rfid_medientyp || ""); };
  const handleRfidSpeichern = async () => {
    let karte = rfidWert.trim();
    const umgerechnet = pcLeserZuKartennummer(karte);
    if (umgerechnet != null) { karte = String(umgerechnet); setRfidWert(karte); }
    const res = await fetch(`${API}/sonstige/nutzer/${rfidDialogId}/rfid`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rfid_kartennummer: karte, medientyp: rfidMedientyp || null }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.detail || "Fehler."); return; }
    setRfidDialogId(null);
    laden();
  };
  const handleRfidEntfernen = async (id) => {
    const res = await fetch(`${API}/sonstige/nutzer/${id}/rfid`, { method: "DELETE" });
    if (!res.ok) { alert("RFID konnte nicht entfernt werden."); return; }
    laden();
  };

  const statusFarbe = (status) => status === "aktiv" ? THEME.colors.success : status === "gesperrt" ? THEME.colors.danger : THEME.colors.textMuted;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        <h1 style={{ ...s.heading, margin: 0 }}>Sonstige</h1>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input style={{ ...s.input, maxWidth: 260 }} placeholder="Suche (Name, RFID)…" value={suche} onChange={e => setSuche(e.target.value)} />
        <select style={{ ...s.input, width: "auto" }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Alle Status</option>
          <option value="aktiv">Aktiv</option>
          <option value="gesperrt">Gesperrt</option>
          <option value="archiviert">Archiviert</option>
        </select>
        <select style={{ ...s.input, width: "auto" }} value={rfidFilter} onChange={e => setRfidFilter(e.target.value)}>
          <option value="">RFID: alle</option>
          <option value="mit">Mit RFID</option>
          <option value="ohne">Ohne RFID</option>
        </select>
        <button onClick={() => { setForm(leerForm); setBearbeitenId(null); setFehler(""); setFormOffen(true); }} style={{ ...s.btn, width: "auto" }}>+ Neu</button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {liste.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine Sonstige-Nutzer gefunden.</p>}
        {liste.map(n => (
          <div key={n.id} onClick={() => oeffneProfil(n.id)} style={{ ...s.karte, padding: 14, cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <div>
                <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{n.vorname} {n.nachname}</span>
                <span style={{ marginLeft: 10, color: statusFarbe(n.zutrittsstatus), fontSize: THEME.fontSize.xs, fontWeight: 600, textTransform: "uppercase" }}>{n.zutrittsstatus}</span>
                <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 4 }}>
                  {n.rfid_kartennummer ? <span>RFID: {n.rfid_kartennummer}</span> : <span>Keine RFID</span>}
                  {n.gueltig_bis && <span> · Gültig bis: {n.gueltig_bis}</span>}
                </div>
              </div>
              <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.lg }}>›</span>
            </div>
          </div>
        ))}
      </div>

      {formOffen && (
        <div style={s.modalBg} onClick={() => setFormOffen(false)}>
          <div style={{ ...s.modal, width: "min(480px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>{bearbeitenId ? "Sonstige-Nutzer bearbeiten" : "Sonstige-Nutzer anlegen"}</h2>
            {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={s.inputGroup}>
                <label style={s.label}>Vorname</label>
                <input style={s.input} value={form.vorname} onChange={e => setForm({ ...form, vorname: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Nachname</label>
                <input style={s.input} value={form.nachname} onChange={e => setForm({ ...form, nachname: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>E-Mail</label>
                <input type="email" style={s.input} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Telefon / Mobil</label>
                <input style={s.input} value={form.telefon} onChange={e => setForm({ ...form, telefon: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Gültig bis</label>
                <input type="date" style={s.input} value={form.gueltig_bis} onChange={e => setForm({ ...form, gueltig_bis: e.target.value })} />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>RFID-Kartennummer</label>
                <input style={s.input} value={form.rfid_kartennummer} onChange={e => setForm({ ...form, rfid_kartennummer: e.target.value })} />
              </div>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Notiz</label>
              <textarea style={{ ...s.input, minHeight: 70 }} value={form.notiz} onChange={e => setForm({ ...form, notiz: e.target.value })} placeholder="z.B. Grund/Kategorie: Kurzzeitmitgliedschaft, Präventionskurs…" />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
              <button onClick={() => setFormOffen(false)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
              <button onClick={handleSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {rfidDialogId && (
        <div style={s.modalBg} onClick={() => setRfidDialogId(null)}>
          <div style={{ ...s.modal, width: "min(420px, 95vw)" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>RFID-Band zuweisen</h2>
            <div style={s.inputGroup}>
              <label style={s.label}>Kartennummer</label>
              <input style={s.input} value={rfidWert} onChange={e => setRfidWert(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleRfidSpeichern(); }} autoFocus />
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Medientyp (für TTLock-Zutritt)</label>
              <select style={s.input} value={rfidMedientyp} onChange={e => setRfidMedientyp(e.target.value)}>
                <option value="">Automatisch (Band)</option>
                <option value="karte">Karte</option>
                <option value="band">Band</option>
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 10 }}>
              <button onClick={() => { handleRfidEntfernen(rfidDialogId); setRfidDialogId(null); }} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>RFID entfernen</button>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setRfidDialogId(null)} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                <button onClick={handleRfidSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {umwandelnNutzer && (
        <PersonFormular
          typ="mitglied" typLocked
          initial={{ vorname: umwandelnNutzer.vorname, nachname: umwandelnNutzer.nachname, email: umwandelnNutzer.email || "", telefon: umwandelnNutzer.telefon || "" }}
          konvertierung={{ richtung: "zu_mitglied", quelleTyp: "sonstige", quelleId: umwandelnNutzer.id, quelleName: `${umwandelnNutzer.vorname} ${umwandelnNutzer.nachname}` }}
          onSpeichern={() => { setUmwandelnNutzer(null); laden(); }}
          onAbbrechen={() => setUmwandelnNutzer(null)}
        />
      )}

      {profilId && (
        <div style={s.modalBg} onClick={() => setProfilId(null)}>
          <div style={{ ...s.modal, width: "min(960px, 95vw)", padding: 40 }} onClick={e => e.stopPropagation()}>
            {!profil && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.lg }}>Lädt…</p>}
            {profil && (
              <>
                <h2 style={{ color: THEME.colors.text, marginTop: 0, fontSize: THEME.fontSize.xxl }}>{profil.vorname} {profil.nachname}</h2>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Status: {profil.zutrittsstatus}</p>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>E-Mail: {profil.email || "–"}{profil.telefon ? ` · Telefon: ${profil.telefon}` : ""}</p>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>RFID: {profil.rfid_kartennummer || "Keine RFID"}</p>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Gültig bis: {profil.gueltig_bis || "–"}</p>
                <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.md }}>Notiz: {profil.notiz || "–"}</p>
                <TTLockZutrittZeile personTyp="sonstige" personId={profil.id} />
                <MitgliederAppZugangBereich herkunft="sonstige" quelleId={profil.id} />
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 28, paddingTop: 28, borderTop: `1px solid ${THEME.colors.border}` }}>
                  <button onClick={() => { setProfilId(null); oeffneBearbeiten(profil); }} style={{ ...s.btnGray, width: "auto" }}>Bearbeiten</button>
                  <button onClick={() => { setProfilId(null); oeffneRfidDialog(profil); }} style={{ ...s.btnGray, width: "auto" }}>RFID</button>
                  {profil.zutrittsstatus !== "gesperrt" && <button onClick={() => handleStatusAendern(profil.id, "sperren")} style={{ ...s.btnGray, width: "auto" }}>Sperren</button>}
                  {profil.zutrittsstatus !== "aktiv" && <button onClick={() => handleStatusAendern(profil.id, "aktivieren")} style={{ ...s.btnGray, width: "auto" }}>Freigeben</button>}
                  {profil.zutrittsstatus !== "archiviert" && <button onClick={() => handleStatusAendern(profil.id, "archivieren")} style={{ ...s.btnGray, width: "auto" }}>Archivieren</button>}
                  {!profil.mitglied_id && <button onClick={() => { setProfilId(null); setUmwandelnNutzer(profil); }} style={{ ...s.btn, width: "auto", background: THEME.colors.warning }}>🔄 In Mitglied umwandeln</button>}
                  {!profil.mitglied_id && <button onClick={() => handleUmwandeln(profil, "wellpass")} style={{ ...s.btnGray, width: "auto" }}>🔄 In Wellpass umwandeln</button>}
                  {!profil.mitglied_id && <button onClick={() => handleUmwandeln(profil, "hansefit")} style={{ ...s.btnGray, width: "auto" }}>🔄 In Hansefit umwandeln</button>}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                  <button onClick={() => handleLoeschen(profil)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Löschen</button>
                  <button onClick={() => setProfilId(null)} style={{ ...s.btnGray, width: "auto" }}>Schließen</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function OnlinePortalSeite({ onMitgliedOeffnen, rechte, neueVertraege, neueKuendigungen, neuesFeedback }) {
  const alleTabs = [
    { key: "vertraege", label: "Online-Verträge", modul: "online_vertraege", neu: neueVertraege },
    { key: "kuendigungen", label: "Kündigungen", modul: "kuendigungen", neu: neueKuendigungen },
    { key: "feedback", label: "Feedback", modul: "feedback", neu: neuesFeedback },
    { key: "aktion", label: "Aktion" }, // bewusst ungegatet (kein modul-Key) - reine Besucherzahlen, kein sensibler Inhalt
  ];
  const tabs = alleTabs.filter(t => !t.modul || rechte?.rolle === "admin" || rechte?.module?.[t.modul]);
  const [aktiveTab, setAktiveTab] = useState(tabs[0]?.key || "vertraege");

  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setAktiveTab(t.key)}
            style={{ ...s.btnGray, padding: "10px 20px", borderRadius: 8, background: aktiveTab === t.key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: aktiveTab === t.key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md, flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
            {t.label}
            {t.neu > 0 && <span style={{ width: 8, height: 8, borderRadius: "50%", background: THEME.colors.success, display: "inline-block" }} />}
          </button>
        ))}
      </div>
      {aktiveTab === "vertraege" && <OnlineVertraegeSeite onMitgliedOeffnen={onMitgliedOeffnen} />}
      {aktiveTab === "kuendigungen" && <KuendigungenSeite />}
      {aktiveTab === "feedback" && <FeedbackSeite />}
      {aktiveTab === "aktion" && <><KampagneBesucherBox /><SonderaktionBesucherSeite /></>}
    </div>
  );
}


// ============ ONLINE PORTAL: KAMPAGNE aktion.comefit.de (Trichter) ============
function KampagneBesucherBox() {
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const laden = () => fetch(`${API}/besucherzaehler/kampagne/uebersicht`).then(r => r.json()).then(d => { setDaten(d); setLaedt(false); }).catch(() => setLaedt(false));
  useEffect(() => { laden(); const iv = setInterval(laden, 60000); return () => clearInterval(iv); }, []);
  const st = daten?.stationen || [];
  const start30 = st[0]?.monat_30_tage || 0;
  const quote = (n) => (start30 > 0 ? `${Math.round((n / start30) * 100)} %` : "–");
  const zelle = { padding: "8px 10px", textAlign: "right", color: THEME.colors.text };
  const kopf = { ...zelle, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600 };
  const quellen = Object.entries(daten?.quellen || {}).sort((a, b) => (b[1]["aktion-start"] || 0) - (a[1]["aktion-start"] || 0));
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ ...s.heading, fontSize: THEME.fontSize.xl2, marginBottom: 6 }}>📈 Kampagne "Trainiere bis 2027 gratis"</h2>
      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 16px" }}>
        Seitenaufrufe auf <code>aktion.comefit.de</code> · ohne Cookies und ohne Personenbezug · ein Neuladen zählt mit
      </p>
      {laedt ? <p style={{ color: THEME.colors.textMuted }}>Lädt…</p> : (
        <div style={s.karte}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={{ ...kopf, textAlign: "left" }}>Station</th><th style={kopf}>Heute</th><th style={kopf}>7 Tage</th><th style={kopf}>30 Tage</th><th style={kopf}>Gesamt</th><th style={kopf}>Anteil (30 T.)</th>
              </tr></thead>
              <tbody>
                {st.map(z => (
                  <tr key={z.schluessel} style={{ borderTop: `1px solid ${THEME.colors.border}` }}>
                    <td style={{ ...zelle, textAlign: "left", fontWeight: 600 }}>{z.name}</td>
                    <td style={zelle}>{z.heute}</td><td style={zelle}>{z.woche_7_tage}</td><td style={zelle}>{z.monat_30_tage}</td><td style={zelle}>{z.gesamt}</td>
                    <td style={{ ...zelle, color: THEME.colors.textMuted }}>{quote(z.monat_30_tage)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {quellen.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 6px", fontWeight: 600 }}>Herkunft der Besucher (letzte 30 Tage)</p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={{ ...kopf, textAlign: "left" }}>Quelle</th>{st.map(z => <th key={z.schluessel} style={kopf}>{z.name}</th>)}</tr></thead>
                  <tbody>
                    {quellen.map(([q, w]) => (
                      <tr key={q} style={{ borderTop: `1px solid ${THEME.colors.border}` }}>
                        <td style={{ ...zelle, textAlign: "left" }}>{q}</td>
                        {st.map(z => <td key={z.schluessel} style={zelle}>{w[z.schluessel] || 0}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {start30 === 0 && <p style={{ color: THEME.colors.textMuted, margin: "14px 0 0" }}>Noch keine Aufrufe erfasst.</p>}
        </div>
      )}
    </div>
  );
}

// ============ ONLINE PORTAL: BESUCHERZAHLEN SONDERAKTION ============
function SonderaktionBesucherSeite() {
  const [stat, setStat] = useState(null);
  const [laedt, setLaedt] = useState(true);

  const laden = () => {
    fetch(`${API}/besucherzaehler/sonderaktion/statistik`).then(r => r.json()).then(d => { setStat(d); setLaedt(false); }).catch(() => setLaedt(false));
  };
  useEffect(() => { laden(); const intervall = setInterval(laden, 60000); return () => clearInterval(intervall); }, []);

  const kacheln = stat ? [
    { label: "Heute", wert: stat.heute },
    { label: "Letzte 7 Tage", wert: stat.woche_7_tage },
    { label: "Letzte 30 Tage", wert: stat.monat_30_tage },
    { label: "Gesamt", wert: stat.gesamt },
  ] : [];

  return (
    <div>
      <h2 style={{ ...s.heading, fontSize: THEME.fontSize.xl2, marginBottom: 6 }}>📈 Besucherzahlen "Sonderaktion"</h2>
      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 20px" }}>
        Seitenaufrufe der öffentlichen Landingpage <code>/sonderaktion</code>
        {stat?.seit && ` · erfasst seit ${new Date(stat.seit).toLocaleDateString("de-DE")}`}
      </p>
      {laedt ? (
        <p style={{ color: THEME.colors.textMuted }}>Lädt…</p>
      ) : !stat || stat.gesamt === 0 ? (
        <p style={{ color: THEME.colors.textMuted }}>Noch keine Aufrufe erfasst.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
          {kacheln.map(k => (
            <div key={k.label} style={{ ...s.karte, borderLeftColor: THEME.colors.accentViolet, textAlign: "center" }}>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxxl2, fontWeight: 700, margin: "0 0 4px" }}>{k.wert}</p>
              <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>{k.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ ADMIN: AKTIONEN ============
function AktionenSeite({ onMitgliedOeffnen }) {
  const LEER = { name: "", aktiv: true, gueltig_von: "", gueltig_bis: "", laufzeit_monate: 6, gratis_monate: 1, betroffene_tarife: "", startpaket_preis: "", anmeldegebuehr_entfaellt: false, gratis_bis_datum: "" };
  const [aktionen, setAktionen] = useState([]);
  const [formOffen, setFormOffen] = useState(false);
  const [form, setForm] = useState(LEER);
  const [bearbeitenId, setBearbeitenId] = useState(null);
  const [teilnehmer, setTeilnehmer] = useState({}); // aktion.id -> {anzahl, mitglieder}
  const [teilnehmerOffen, setTeilnehmerOffen] = useState({}); // aktion.id -> bool
  const set = (feld, wert) => setForm(f => ({ ...f, [feld]: wert }));

  const laden = () => fetch(`${API}/aktionen/`).then(r => r.json()).then(d => {
    setAktionen(d);
    d.forEach(a => {
      fetch(`${API}/aktionen/${a.id}/teilnehmer`).then(r => r.json())
        .then(t => setTeilnehmer(prev => ({ ...prev, [a.id]: t }))).catch(() => {});
    });
  }).catch(() => {});
  useEffect(() => { laden(); }, []);

  const handleSpeichern = async () => {
    const payload = {
      ...form,
      gueltig_von: form.gueltig_von || null, gueltig_bis: form.gueltig_bis || null,
      laufzeit_monate: parseInt(form.laufzeit_monate) || 1, gratis_monate: parseInt(form.gratis_monate) || 0,
      startpaket_preis: form.startpaket_preis === "" ? null : parseFloat(form.startpaket_preis),
      betroffene_tarife: form.betroffene_tarife || null,
      gratis_bis_datum: form.gratis_bis_datum || null,
    };
    const url = bearbeitenId ? `${API}/aktionen/${bearbeitenId}` : `${API}/aktionen/`;
    await fetch(url, { method: bearbeitenId ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    setFormOffen(false); setForm(LEER); setBearbeitenId(null);
    laden();
  };

  const handleBearbeiten = (a) => {
    setForm({ ...a, gueltig_von: a.gueltig_von || "", gueltig_bis: a.gueltig_bis || "", betroffene_tarife: a.betroffene_tarife || "", startpaket_preis: a.startpaket_preis ?? "", gratis_bis_datum: a.gratis_bis_datum || "" });
    setBearbeitenId(a.id);
    setFormOffen(true);
  };

  const handleLoeschen = async (a) => {
    if (!window.confirm(`Aktion "${a.name}" deaktivieren?`)) return;
    const res = await fetch(`${API}/aktionen/${a.id}`, { method: "DELETE" });
    if (!res.ok) { alert("Deaktivieren fehlgeschlagen."); return; }
    laden();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ ...s.heading, margin: 0 }}>Aktionen</h1>
        <button onClick={() => { setForm(LEER); setBearbeitenId(null); setFormOffen(true); }} style={{ ...s.btn, width: "auto" }}>+ Neue Aktion</button>
      </div>

      {formOffen && (
        <div style={{ ...s.karte, marginBottom: 20 }}>
          <div style={s.inputGroup}><label style={s.label}>Aktionsname</label><input style={s.input} value={form.name} onChange={e => set("name", e.target.value)} placeholder="z.B. 6+1 Monat gratis" /></div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ ...s.inputGroup, flex: 1 }}><label style={s.label}>Gültig von</label><input type="date" style={s.input} value={form.gueltig_von} onChange={e => set("gueltig_von", e.target.value)} /></div>
            <div style={{ ...s.inputGroup, flex: 1 }}><label style={s.label}>Gültig bis</label><input type="date" style={s.input} value={form.gueltig_bis} onChange={e => set("gueltig_bis", e.target.value)} /></div>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ ...s.inputGroup, flex: 1 }}><label style={s.label}>Vertragslaufzeit (Monate)</label><input type="number" style={s.input} value={form.laufzeit_monate} onChange={e => set("laufzeit_monate", e.target.value)} /></div>
            <div style={{ ...s.inputGroup, flex: 1 }}><label style={s.label}>Gratis-Monate</label><input type="number" style={s.input} value={form.gratis_monate} onChange={e => set("gratis_monate", e.target.value)} /></div>
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>Fester Gratis-Zeitraum bis (optional)</label>
            <input type="date" style={s.input} value={form.gratis_bis_datum || ""} onChange={e => set("gratis_bis_datum", e.target.value)} />
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "4px 0 0" }}>
              Beitrag 0 € ab Anmeldung bis einschließlich diesem Tag, die Vertragslaufzeit beginnt erst am Tag danach. Wird beim Freigeben automatisch als Gratis-Zeitraum beim Mitglied eingetragen. „Gratis-Monate“ wird dann ignoriert.
            </p>
          </div>
          <div style={s.inputGroup}>
            <label style={s.label}>Betroffene Tarife (leer = alle Tarife)</label>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {TARIFE.map(t => {
                const ausgewaehlt = form.betroffene_tarife ? form.betroffene_tarife.split(",").map(x => x.trim()).includes(t) : false;
                return (
                  <label key={t} style={{ display: "flex", alignItems: "center", gap: 6, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, cursor: "pointer" }}>
                    <input type="checkbox" checked={ausgewaehlt} onChange={e => {
                      const liste = form.betroffene_tarife ? form.betroffene_tarife.split(",").map(x => x.trim()).filter(Boolean) : [];
                      const neu = e.target.checked ? [...liste, t] : liste.filter(x => x !== t);
                      set("betroffene_tarife", neu.join(", "));
                    }} />
                    {t}
                  </label>
                );
              })}
            </div>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ ...s.inputGroup, flex: 1 }}><label style={s.label}>Startpaketpreis (optional, überschreibt Standard)</label><input type="number" style={s.input} value={form.startpaket_preis} onChange={e => set("startpaket_preis", e.target.value)} /></div>
            <div style={{ ...s.inputGroup, flex: 1, display: "flex", alignItems: "flex-end" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, cursor: "pointer", marginBottom: 14 }}>
                <input type="checkbox" checked={form.anmeldegebuehr_entfaellt} onChange={e => set("anmeldegebuehr_entfaellt", e.target.checked)} />
                Anmeldegebühr entfällt
              </label>
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, cursor: "pointer", marginBottom: 16 }}>
            <input type="checkbox" checked={form.aktiv} onChange={e => set("aktiv", e.target.checked)} />
            Aktiv (im öffentlichen Formular sichtbar)
          </label>
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={() => { setFormOffen(false); setForm(LEER); setBearbeitenId(null); }} style={s.btnGray}>Abbrechen</button>
            <button onClick={handleSpeichern} style={s.btn}>Speichern</button>
          </div>
        </div>
      )}

      {aktionen.length === 0 ? (
        <p style={{ color: THEME.colors.textMuted }}>Noch keine Aktionen angelegt.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {aktionen.map(a => (
            <div key={a.id} style={{ ...s.karte, borderLeftColor: a.aktiv ? THEME.colors.success : THEME.colors.textDisabled, opacity: a.aktiv ? 1 : 0.6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <p style={{ color: THEME.colors.text, fontWeight: 600, fontSize: THEME.fontSize.lg, margin: 0 }}>{a.name} {!a.aktiv && <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>(inaktiv)</span>}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "4px 0 0" }}>
                    {a.laufzeit_monate} Monate Laufzeit{a.gratis_bis_datum ? ` ab ${datumDE(datumZuLokalerISO(new Date(a.gratis_bis_datum.slice(0, 4), parseInt(a.gratis_bis_datum.slice(5, 7)) - 1, parseInt(a.gratis_bis_datum.slice(8, 10)) + 1)))}, beitragsfrei bis ${datumDE(a.gratis_bis_datum)}` : (a.gratis_monate ? ` + ${a.gratis_monate} gratis` : "")} · {a.betroffene_tarife || "alle Tarife"}
                    {(a.gueltig_von || a.gueltig_bis) && ` · gültig ${a.gueltig_von ? datumDE(a.gueltig_von) : "…"} – ${a.gueltig_bis ? datumDE(a.gueltig_bis) : "…"}`}
                  </p>
                  <button onClick={() => setTeilnehmerOffen(o => ({ ...o, [a.id]: !o[a.id] }))}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 8, color: THEME.colors.primary, fontSize: THEME.fontSize.sm, display: "flex", alignItems: "center", gap: 4 }}>
                    👥 {teilnehmer[a.id] ? `${teilnehmer[a.id].anzahl} Teilnehmer` : "…"} {teilnehmerOffen[a.id] ? "▲" : "▼"}
                  </button>
                  {teilnehmerOffen[a.id] && (
                    teilnehmer[a.id]?.mitglieder?.length > 0 ? (
                      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                        {teilnehmer[a.id].mitglieder.map(m => (
                          <button key={m.id} onClick={() => onMitgliedOeffnen?.(m.id)}
                            style={{ background: "none", border: "none", cursor: onMitgliedOeffnen ? "pointer" : "default", padding: "2px 0", textAlign: "left", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
                            {m.vorname} {m.nachname} · {m.tarif} · seit {datumDE(m.vertragsbeginn)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "8px 0 0" }}>Noch keine Teilnehmer.</p>
                    )
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => handleBearbeiten(a)} style={{ ...s.btnGray, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px" }}>Bearbeiten</button>
                  <button onClick={() => handleLoeschen(a)} style={{ ...s.btn, width: "auto", fontSize: THEME.fontSize.sm, padding: "6px 12px", background: THEME.colors.dangerBg }}>Deaktivieren</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ APP ============
function HauptApp() {
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem("comefitSession")); } catch { return null; }
  });
  // Der localStorage-Cache dient nur der sofortigen UI-Anzeige - massgeblich fuer den
  // tatsaechlichen Zugriff ist ausschliesslich das httpOnly-Sitzungs-Cookie. Beim Start einmal
  // gegen den Server validieren/auffrischen (GET /benutzer/me), statt dem Cache dauerhaft blind
  // zu vertrauen; waehrenddessen ein kurzer Ladezustand statt eines Login-Flashs.
  const [sitzungWirdGeprueft, setSitzungWirdGeprueft] = useState(() => !!localStorage.getItem("comefitSession"));
  // Aus sessionStorage wiederherstellen, damit ein Reload (z.B. Pull-to-Refresh in der
  // installierten PWA) auf derselben Seite bleibt statt immer aufs Dashboard zu springen -
  // sessionStorage statt localStorage, damit ein frischer App-Neustart (nicht nur ein Reload
  // im selben Tab) weiterhin regulär auf dem Dashboard startet.
  const [seite, setSeite] = useState(() => {
    try { return sessionStorage.getItem("comefitSeite") || "Dashboard"; } catch { return "Dashboard"; }
  });
  const [menuOffen, setMenuOffen] = useState(false);
  const [mitgliedOeffnen, setMitgliedOeffnen] = useState(() => {
    try {
      const gespeichert = sessionStorage.getItem("comefitMitgliedOeffnen");
      return gespeichert ? parseInt(gespeichert, 10) : null;
    } catch { return null; }
  });
  const [personSubTabOeffnen, setPersonSubTabOeffnen] = useState(null);
  // Erzwingt einen erneuten "Mitglied oeffnen"-Effekt in MitgliederSeite auch dann, wenn dieselbe
  // ID wie zuvor erneut geoeffnet wird (z.B. Person geschlossen, Sub-Tab gewechselt, dieselbe Person
  // erneut ueber die uebergreifende Suche gewaehlt) - sonst erkennt React keine Aenderung von
  // mitgliedOeffnen und der Tab-Umschalt-Effekt feuert nicht erneut (gefunden 2026-07-24).
  const [mitgliedOeffnenNonce, setMitgliedOeffnenNonce] = useState(0);
  // Direktsprung von einem Personendatensatz auf einen einzelnen Vorfallbericht unter
  // Studio -> Vorfallberichte (siehe VorfallberichteEmbed) - gleiches Nonce-Muster wie
  // mitgliedOeffnenNonce oben, aus demselben, dort dokumentierten Grund.
  const [vorfallOeffnen, setVorfallOeffnen] = useState(null);
  const [vorfallOeffnenNonce, setVorfallOeffnenNonce] = useState(0);
  const [neueVertraege, setNeueVertraege] = useState(0);
  const [neueKuendigungen, setNeueKuendigungen] = useState(0);
  const [neuesFeedback, setNeuesFeedback] = useState(0);
  const [sidebarErweitert, setSidebarErweitert] = useState(false);
  // null = automatisch (Hover-Verhalten wie bisher), "offen"/"zu" = vom Nutzer dauerhaft fixiert
  // (per Browser gespeichert, da es eine reine Anzeige-Vorliebe ist, keine Studio-weite Einstellung)
  const [sidebarPin, setSidebarPin] = useState(() => localStorage.getItem("comefitSidebarPin") || null);
  const sidebarTimeoutRef = useRef(null);
  const sidebarSichtbar = sidebarPin ? sidebarPin === "offen" : sidebarErweitert;

  useEffect(() => { window.scrollTo(0, 0); }, [seite]);
  useEffect(() => { try { sessionStorage.setItem("comefitSeite", seite); } catch {} }, [seite]);
  useEffect(() => {
    try {
      if (mitgliedOeffnen) sessionStorage.setItem("comefitMitgliedOeffnen", String(mitgliedOeffnen));
      else sessionStorage.removeItem("comefitMitgliedOeffnen");
    } catch {}
  }, [mitgliedOeffnen]);

  useEffect(() => {
    const ladenAnzahlNeu = () => {
      fetch(`${API}/vertraege/anzahl-neu`).then(r => r.json()).then(d => setNeueVertraege(d.anzahl || 0)).catch(() => {});
      fetch(`${API}/kuendigungen/anzahl-neu`).then(r => r.json()).then(d => setNeueKuendigungen(d.anzahl || 0)).catch(() => {});
      fetch(`${API}/feedback/anzahl-neu`).then(r => r.json()).then(d => setNeuesFeedback(d.anzahl || 0)).catch(() => {});
    };
    ladenAnzahlNeu();
    const intervall = setInterval(ladenAnzahlNeu, 30000);
    return () => clearInterval(intervall);
  }, [seite]);

  const handleLogin = (data) => { localStorage.setItem("comefitSession", JSON.stringify(data)); setSession(data); };
  const sitzungLokalBeenden = () => { localStorage.removeItem("comefitSession"); setSession(null); };
  const handleLogout = () => {
    fetch(`${API}/benutzer/logout`, { method: "POST" }).catch(() => {}).finally(sitzungLokalBeenden);
  };
  const handleLogoutAlleGeraete = () => {
    if (!window.confirm("Auf allen Geräten abmelden, auf denen du gerade eingeloggt bist?")) return;
    fetch(`${API}/benutzer/logout-alle-geraete`, { method: "POST" }).catch(() => {}).finally(sitzungLokalBeenden);
  };

  useEffect(() => {
    // Zentrale Reaktion auf eine 401-Antwort von IRGENDEINEM der zahlreichen fetch-Aufrufe in
    // dieser Datei (siehe globaler fetch-Patch oben) - Sitzung lokal beenden, Login-Bildschirm.
    setzeSitzungAbgelaufenCallback(sitzungLokalBeenden);
    if (!localStorage.getItem("comefitSession")) return;
    fetch(`${API}/benutzer/me`)
      .then(r => { if (!r.ok) throw new Error("ungueltig"); return r.json(); })
      .then(daten => { localStorage.setItem("comefitSession", JSON.stringify(daten)); setSession(daten); })
      .catch(sitzungLokalBeenden)
      .finally(() => setSitzungWirdGeprueft(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleMitgliedOeffnen = (id) => { setMitgliedOeffnen(id); setMitgliedOeffnenNonce(n => n + 1); setSeite("Mitglieder"); };
  const handleVorfallOeffnen = (id) => { setVorfallOeffnen(id); setVorfallOeffnenNonce(n => n + 1); try { sessionStorage.setItem(START_TAB_SCHLUESSEL, "studiobereich"); } catch {} setSeite("Einstellungen"); };
  // Allgemeinere Variante fuer die RFID-Kartensuche (Dashboard-Widget + Zutritt-Seite), die
  // alle 5 Personentypen kennt - bei Wellpass/Hansefit/Sonstige wird zusaetzlich der passende
  // Sub-Tab unter "Mitglieder" angesprungen (siehe personSubTabOeffnen in MitgliederSeite).
  // Mitarbeiter haben bewusst keine eigene Profilansicht in diesem Sinn, bleiben unverlinkt.
  const handlePersonOeffnen = (typ, id) => {
    if (typ === "mitglied") { handleMitgliedOeffnen(id); return; }
    setMitgliedOeffnen(null);
    setPersonSubTabOeffnen({ typ, id: Number(id) });
    setSeite("Mitglieder");
  };
  const handleSidebarEnter = () => {
    if (sidebarPin) return;
    if (sidebarTimeoutRef.current) clearTimeout(sidebarTimeoutRef.current);
    setSidebarErweitert(true);
  };
  const handleSidebarLeave = () => {
    if (sidebarPin) return;
    sidebarTimeoutRef.current = setTimeout(() => setSidebarErweitert(false), 180);
  };
  const handleSidebarPinToggle = () => {
    if (sidebarPin) {
      localStorage.removeItem("comefitSidebarPin");
      setSidebarPin(null);
      setSidebarErweitert(false);
    } else {
      const neuerPin = sidebarSichtbar ? "offen" : "zu";
      localStorage.setItem("comefitSidebarPin", neuerPin);
      setSidebarPin(neuerPin);
    }
  };

  if (sitzungWirdGeprueft) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: THEME.colors.background, color: THEME.colors.textMuted }}>Lädt…</div>;
  }
  if (!session) return <Login onLogin={handleLogin} />;

  const rechte = session.rechte || {};
  const isAdmin = session.rolle === "admin";

  const alleNavItems = ["Dashboard", "Mitglieder", "Kalender", "Statistiken", "Rechnungen", "Finanzen", "Mitarbeiter", "Online Portal", "Check In", "TV", "Einstellungen"];
  // SEPA ist kein eigener Nav-Punkt mehr, sondern der erste Bereich in "Finanzen" (Modulrecht "sepa" bleibt eigenstaendig)
  const darfSepa = isAdmin || !!rechte.module?.sepa;
  const darfFinanzen = isAdmin || !!rechte.module?.finanzen_anzeigen;
  const navItems = alleNavItems.filter(item => {
    if (item === "Dashboard" || isAdmin) return true;
    if (item === "Online Portal") return ONLINE_PORTAL_MODULE_KEYS.some(k => !!rechte.module?.[k]);
    if (item === "Einstellungen") return !!rechte.module?.einstellungen || STUDIO_MODULE_KEYS.some(k => !!rechte.module?.[k]);
    if (item === "Mitglieder") return MITGLIEDER_MODULE_KEYS.some(k => !!rechte.module?.[k]);
    if (item === "Check In") return CHECKIN_MODULE_KEYS.some(k => !!rechte.module?.[k]);
    if (item === "Finanzen") return darfFinanzen || darfSepa;
    const modulKey = NAV_MODUL_KEY[item];
    return modulKey ? !!rechte.module?.[modulKey] : true;
  });

  // Aus sessionStorage wiederhergestellte Seite koennte inzwischen nicht mehr erlaubt sein
  // (z.B. Rechte wurden entzogen) - dann lieber aufs Dashboard zurueckfallen statt eine Seite
  // zu rendern, die im Nav gar nicht mehr auftaucht.
  if (seite !== "Dashboard" && !navItems.includes(seite)) {
    setSeite("Dashboard");
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: THEME.colors.background }}>
      <div className="sidebar" onMouseEnter={handleSidebarEnter} onMouseLeave={handleSidebarLeave} style={{
        ...s.sidebar, overflow: "hidden", flexShrink: 0,
        width: sidebarSichtbar ? THEME.sizes.sidebarWidth : THEME.sizes.sidebarCollapsedWidth,
        transition: "width 200ms ease",
      }}>
        {/* Logo oben links in der Seitenleiste auf Nutzerwunsch 2026-10-06 entfernt. */}
        <nav>
          {navItems.map(item => {
            const NavIcon = NAV_ICON[item];
            return (
            <div key={item} onClick={() => { setSeite(item); setMitgliedOeffnen(null); setPersonSubTabOeffnen(null); }} style={{ ...s.navItem, ...(seite === item ? s.navItemAktiv : {}), display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 38, flexShrink: 0 }}>{NavIcon && <NavIcon size={34} />}</span>
              <span style={{ marginLeft: 10, opacity: sidebarSichtbar ? 1 : 0, pointerEvents: sidebarSichtbar ? "auto" : "none", transition: "opacity 150ms ease" }}>{item}</span>
              {item === "Online Portal" && (neueVertraege + neueKuendigungen + neuesFeedback) > 0 && <span style={{ width: 9, height: 9, borderRadius: "50%", background: THEME.colors.success, display: "inline-block", flexShrink: 0, marginLeft: sidebarSichtbar ? "auto" : 4 }} />}
            </div>
            );
          })}
        </nav>
        <div style={{ marginTop: 16, whiteSpace: "nowrap" }}>
          <div onClick={handleSidebarPinToggle} title={sidebarPin ? "Navigation lösen" : "Navigation anheften"}
            style={{ ...s.navItem, color: sidebarPin ? THEME.colors.primary : THEME.colors.textMuted, display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 38, flexShrink: 0 }}>
              {sidebarPin ? <PinOff size={20} /> : <Pin size={20} />}
            </span>
            <span style={{ marginLeft: 10, fontSize: THEME.fontSize.sm, opacity: sidebarSichtbar ? 1 : 0, pointerEvents: sidebarSichtbar ? "auto" : "none", transition: "opacity 150ms ease" }}>
              {sidebarPin ? "Navigation lösen" : "Navigation anheften"}
            </span>
          </div>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, padding: "4px 14px", marginTop: 8, opacity: sidebarSichtbar ? 1 : 0, transition: "opacity 150ms ease" }}>Angemeldet als:<br /><strong style={{ color: THEME.colors.text }}>{session.name}</strong></p>
          <div onClick={handleLogout} style={{ ...s.navItem, color: THEME.colors.danger, display: "flex", alignItems: "center" }}>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 38, flexShrink: 0 }}><LogOut size={34} /></span>
            <span style={{ marginLeft: 10, opacity: sidebarSichtbar ? 1 : 0, pointerEvents: sidebarSichtbar ? "auto" : "none", transition: "opacity 150ms ease" }}>Abmelden</span>
          </div>
          <div onClick={handleLogoutAlleGeraete} style={{ ...s.navItem, color: THEME.colors.textMuted, display: "flex", alignItems: "center" }}>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 38, flexShrink: 0 }}><LogOut size={17} /></span>
            <span style={{ marginLeft: 10, fontSize: THEME.fontSize.xs, opacity: sidebarSichtbar ? 1 : 0, pointerEvents: sidebarSichtbar ? "auto" : "none", transition: "opacity 150ms ease" }}>Von allen Geräten abmelden</span>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div className="mobileheader" style={{ background: THEME.colors.surface, padding: "10px 16px", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => setMenuOffen(!menuOffen)} style={{ background: "none", border: "none", color: THEME.colors.text, fontSize: THEME.fontSize.xxxl, cursor: "pointer" }}>☰</button>
          <img src={`${API}/einstellungen/logo`} alt="ComeFit" onClick={() => { setSeite("Dashboard"); setMitgliedOeffnen(null); setPersonSubTabOeffnen(null); setMenuOffen(false); }} style={{ height: 32, cursor: "pointer" }} />
        </div>
        {menuOffen && (
          <div className="mobilemenu" style={{ background: THEME.colors.surface }}>
            {navItems.map(item => {
              const NavIcon = NAV_ICON[item];
              return (
              <div key={item} onClick={() => { setSeite(item); setMenuOffen(false); setMitgliedOeffnen(null); setPersonSubTabOeffnen(null); }} style={{ ...s.navItem, ...(seite === item ? s.navItemAktiv : {}), borderRadius: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>{NavIcon && <NavIcon size={17} />} {item}</span>
                {item === "Online Portal" && (neueVertraege + neueKuendigungen + neuesFeedback) > 0 && <span style={{ width: 9, height: 9, borderRadius: "50%", background: THEME.colors.success, display: "inline-block", flexShrink: 0 }} />}
              </div>
              );
            })}
            <div onClick={handleLogout} style={{ ...s.navItem, color: THEME.colors.danger, borderRadius: 0, display: "flex", alignItems: "center", gap: 10 }}><LogOut size={17} /> Abmelden</div>
            <div onClick={handleLogoutAlleGeraete} style={{ ...s.navItem, color: THEME.colors.textMuted, borderRadius: 0, display: "flex", alignItems: "center", gap: 10, fontSize: THEME.fontSize.xs }}><LogOut size={15} /> Von allen Geräten abmelden</div>
          </div>
        )}
        <div className="inhaltsbereich" style={{ flex: 1, padding: "2.5rem" }}>
          {seite === "Dashboard" && <Dashboard benutzer={session.name} rolle={session.rolle} benutzerId={session.id || session.benutzer_id} seiteWechseln={setSeite} onPersonOeffnen={handlePersonOeffnen} rechte={{ ...rechte, rolle: session.rolle }} />}
          {seite === "Mitglieder" && <MitgliederSeite mitgliedOeffnen={mitgliedOeffnen} mitgliedOeffnenNonce={mitgliedOeffnenNonce} personSubTabOeffnen={personSubTabOeffnen} onPersonOeffnen={handlePersonOeffnen} onVorfallOeffnen={handleVorfallOeffnen} benutzer={session.name} rechte={{ ...rechte, rolle: session.rolle }} />}
                    {seite === "Statistiken" && <StatistikenSeite />}
          {seite === "Einstellungen" && <EinstellungenSeite rechte={{ ...rechte, rolle: session.rolle }} onPersonOeffnen={handlePersonOeffnen} studioSeite={<StudioSeite eingebettet benutzer={session.name} rechte={{ ...rechte, rolle: session.rolle }} navigiere={setSeite} onMitgliedOeffnen={handleMitgliedOeffnen} vorfallOeffnen={vorfallOeffnen} vorfallOeffnenNonce={vorfallOeffnenNonce} />} />}
          {seite === "Kalender" && <KalenderSeite mitgliedOeffnen={handleMitgliedOeffnen} />}
          {seite === "Rechnungen" && <RechnungenSeite />}
          {seite === "Finanzen" && <FinanzModul benutzerId={session.id || session.benutzer_id} verwandteSeiten={navItems.filter(n => n === "Rechnungen")} onSeite={setSeite} darfFinanzen={darfFinanzen} darfSepa={darfSepa} sepaSeite={<SepaSeite onMitgliedOeffnen={handleMitgliedOeffnen} />} />}
          {seite === "Mitarbeiter" && <MitarbeiterSeite session={{...session, id: session.id || session.benutzer_id || 1}} rechte={{ ...rechte, rolle: session.rolle }} />}
          {seite === "Online Portal" && <OnlinePortalSeite onMitgliedOeffnen={handleMitgliedOeffnen} rechte={{ ...rechte, rolle: session.rolle }} neueVertraege={neueVertraege} neueKuendigungen={neueKuendigungen} neuesFeedback={neuesFeedback} />}
          {seite === "Check In" && <CheckInSeite onMitgliedOeffnen={handleMitgliedOeffnen} rechte={{ ...rechte, rolle: session.rolle }} />}
          {seite === "TV" && <TvVerwaltungSeite />}
          {!["Dashboard", "Mitglieder", "Statistiken", "Einstellungen", "Kalender", "Rechnungen", "Finanzen", "Mitarbeiter", "Online Portal", "Check In", "TV"].includes(seite) && (
            <div style={{ color: THEME.colors.textMuted, marginTop: 40, textAlign: "center" }}>
              <h2 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxl }}>{seite}</h2>
              <p style={{ fontSize: THEME.fontSize.lg }}>Wird bald gebaut...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", background: THEME.colors.background, display: "flex", alignItems: "center", justifyContent: "center", padding: THEME.spacing.lg },
  loginCard: { background: THEME.colors.surface, borderRadius: THEME.radius.xxl, padding: "3rem", width: "100%", maxWidth: THEME.sizes.loginCardMaxWidth },
  logoText: { fontSize: THEME.fontSize.xxxl2, fontWeight: 800, color: THEME.colors.primary, letterSpacing: 2 },
  logoAccent: { color: THEME.colors.accent, fontSize: THEME.fontSize.xl },
  subtitle: { textAlign: "center", color: THEME.colors.textMuted, marginBottom: 32, marginTop: 4, fontSize: THEME.fontSize.lg },
  inputGroup: { marginBottom: THEME.spacing.xl },
  label: { display: "block", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, marginBottom: THEME.spacing.sm },
  input: { width: "100%", padding: "14px 16px", borderRadius: THEME.radius.md, border: `1px solid ${THEME.colors.border}`, background: THEME.colors.background, color: THEME.colors.text, fontSize: THEME.fontSize.lg, boxSizing: "border-box" },
  btn: { padding: "14px 24px", borderRadius: THEME.radius.md, background: THEME.colors.primary, color: kontrastTextfarbe(THEME.colors.primary), fontSize: THEME.fontSize.lg, fontWeight: 600, border: "none", cursor: "pointer", width: "100%" },
  btnGray: { padding: "14px 24px", borderRadius: THEME.radius.md, background: THEME.colors.surfaceMuted, color: kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.lg, border: "none", cursor: "pointer" },
  sidebar: { width: THEME.sizes.sidebarWidth, background: THEME.colors.surface, padding: "2rem 1.5rem", display: "flex", flexDirection: "column", height: "100vh", position: "sticky", top: 0, flexShrink: 0, overflowY: "auto" },
  navItem: { padding: "14px 18px", color: THEME.colors.textMuted, borderRadius: THEME.radius.md, cursor: "pointer", marginBottom: 4, fontSize: THEME.fontSize.lg, fontWeight: 500 },
  navItemAktiv: { background: THEME.colors.surfaceMuted, color: THEME.colors.primary, fontWeight: 700 },
  heading: { color: THEME.colors.text, marginBottom: 30, fontSize: THEME.fontSize.heading, fontWeight: 700, letterSpacing: "-0.02em" },
  karte: { background: THEME.colors.surface, borderRadius: THEME.radius.xl, padding: "1.75rem", borderLeft: `4px solid ${THEME.colors.primary}` },
  karteLabel: { color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, margin: 0 },
  tabelle: { background: THEME.colors.surface, borderRadius: THEME.radius.xl, overflow: "hidden" },
  tabelleHeader: { display: "grid", gridTemplateColumns: "1.5fr 1.5fr 1.5fr 1fr 1.2fr 1fr 1fr", padding: "18px 24px", background: THEME.colors.background, color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, fontWeight: 600, textTransform: "uppercase" },
  tabelleZeile: { display: "grid", gridTemplateColumns: "1.5fr 1.5fr 1.5fr 1fr 1.2fr 1fr 1fr", padding: "20px 24px", borderTop: `1px solid ${THEME.colors.border}`, alignItems: "center" },
  modalBg: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modal: { background: THEME.colors.surface, borderRadius: THEME.radius.xxxl, padding: "2.75rem", width: THEME.sizes.modalMaxWidth, maxHeight: "90vh", overflowY: "auto" },
  modalTitel: { color: THEME.colors.text, marginBottom: 28, fontSize: THEME.fontSize.xxxl, fontWeight: 700, letterSpacing: "-0.02em" },
};

function PasswortZuruecksetzenSeite() {
  const token = new URLSearchParams(window.location.search).get("token");
  const [neuesPasswort, setNeuesPasswort] = useState("");
  const [wiederholung, setWiederholung] = useState("");
  const [fehler, setFehler] = useState("");
  const [erfolg, setErfolg] = useState(false);
  const [laedt, setLaedt] = useState(false);

  const handleAbsenden = async (e) => {
    e.preventDefault();
    if (neuesPasswort.length < 6) { setFehler("Das Passwort muss mindestens 6 Zeichen lang sein."); return; }
    if (neuesPasswort !== wiederholung) { setFehler("Die beiden Passwörter stimmen nicht überein."); return; }
    setFehler("");
    setLaedt(true);
    try {
      const res = await fetch(`${API}/benutzer/passwort-zuruecksetzen`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, neues_passwort: neuesPasswort }),
      });
      const data = await res.json();
      if (!res.ok) { setFehler(data.detail || "Zurücksetzen fehlgeschlagen."); setLaedt(false); return; }
      setErfolg(true);
    } catch {
      setFehler("Verbindung zum Server fehlgeschlagen.");
    }
    setLaedt(false);
  };

  return (
    <div style={s.page}>
      <div style={s.loginCard}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <img src={`${API}/einstellungen/logo`} alt="ComeFit" style={{ width: 220 }} />
        </div>
        <p style={s.subtitle}>Neues Passwort vergeben</p>
        {!token ? (
          <p style={{ color: "#ff6b6b", fontSize: THEME.fontSize.md }}>Dieser Link ist ungültig. Bitte fordere über "Passwort vergessen?" im Login einen neuen Link an.</p>
        ) : erfolg ? (
          <>
            <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.md, lineHeight: 1.6 }}>✓ Dein Passwort wurde erfolgreich geändert. Du kannst dich jetzt anmelden.</p>
            <a href="/" style={{ ...s.btn, display: "block", textAlign: "center", textDecoration: "none", marginTop: 16 }}>Zur Anmeldung</a>
          </>
        ) : (
          <form onSubmit={handleAbsenden}>
            <div style={s.inputGroup}><label style={s.label}>Neues Passwort</label><input style={s.input} type="password" value={neuesPasswort} onChange={e => setNeuesPasswort(e.target.value)} placeholder="••••••••" autoComplete="new-password" /></div>
            <div style={s.inputGroup}><label style={s.label}>Passwort wiederholen</label><input style={s.input} type="password" value={wiederholung} onChange={e => setWiederholung(e.target.value)} placeholder="••••••••" autoComplete="new-password" /></div>
            {fehler && <p style={{ color: "#ff6b6b", fontSize: THEME.fontSize.md }}>{fehler}</p>}
            <button style={s.btn} type="submit" disabled={laedt}>{laedt ? "Wird gespeichert…" : "Passwort speichern"}</button>
          </form>
        )}
      </div>
    </div>
  );
}

// Trainingsplan-Ersteller: einzige Oberfläche unter /training (frontend/src/training/); /training2 leitet dorthin um.

// ============ TV-Infoboard (/tv) ============
const TV_WOCHENTAGE = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

function tvBerlinTeile() {
  const jetzt = new Date();
  const teile = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin", weekday: "long", day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(jetzt);
  const p = {};
  teile.forEach(t => { p[t.type] = t.value; });
  return p;
}

function tvMinuten(hhmm) {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  return h * 60 + m;
}

function tvIstBetreutJetzt(betreuungszeiten, wochentag, minutenJetzt) {
  const bereiche = (betreuungszeiten && betreuungszeiten[wochentag]) || [];
  return bereiche.some(([von, bis]) => minutenJetzt >= tvMinuten(von) && minutenJetzt < tvMinuten(bis));
}

function tvZeitenListe(zeitplan, wochentag) {
  // Liefert jede Zeitspanne als eigenes Array-Element statt eines einzelnen, komma-getrennten
  // Strings - so kann jede Spanne im JSX als eigener Block mit white-space:nowrap gerendert werden
  // (siehe .tv-today-zeit in App.css) und der Zeilenumbruch bei mehreren Zeitfenstern faellt immer
  // zwischen zwei Spannen, nie mitten in einer ("16:00 –" / "20:00 Uhr" getrennt).
  const bereiche = (zeitplan && zeitplan[wochentag]) || [];
  if (bereiche.length === 0) return ["geschlossen"];
  return bereiche.map(([von, bis]) => `${von} – ${bis} Uhr`);
}

function TvGoogleIcon() {
  // lucide-react hat kein Marken-Logo - offizielles vierfarbiges Google-"G" als Inline-SVG, damit
  // die Schnellzugriff-Karte auf einen Blick als "das ist der Google-Bewertungslink" erkennbar ist.
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.52 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.59-5.17 3.59-8.82z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z" />
    </svg>
  );
}

function TvSeite() {
  // Vorschau-Modus (?vorschau=1): wird von der Live-Vorschau im Admin-Tab TV per iframe geladen
  // (siehe TvVorschau in TvVerwaltungSeite). Zeigt den Entwurf ueber /tv/vorschau (auch wenn
  // "Aktiv" noch nicht gesetzt ist) statt des oeffentlichen Live-Stands, und verzichtet auf den
  // 30-Minuten-Hard-Reload, der fuer den echten Kiosk-Browser gedacht ist.
  const vorschauModus = new URLSearchParams(window.location.search).get("vorschau") === "1";
  const [daten, setDaten] = useState(null);
  const [uhrzeit, setUhrzeit] = useState("");
  const [wochentagLabel, setWochentagLabel] = useState("");
  const [betreutJetzt, setBetreutJetzt] = useState(true);
  const [aktionIndex, setAktionIndex] = useState(0);

  const laden = () => {
    fetch(`${API}/tv/${vorschauModus ? "vorschau" : "oeffentlich"}`).then(r => r.json()).then(setDaten).catch(() => {});
  };

  useEffect(() => { laden(); }, []);

  useEffect(() => {
    // Eigenes Intervall statt fest verdrahteter Zahl, damit die in Einstellungen -> Allgemein
    // gepflegte Aktualisierungszeit (tv_aktualisierung_sekunden) direkt greift, sobald der erste
    // Datensatz geladen ist. Vorschau pollt bewusst fest alle 4s, unabhaengig von der Einstellung,
    // damit Aenderungen im Admin-Panel zuegig sichtbar werden.
    const sekunden = vorschauModus ? 4 : Math.max(10, Number(daten?.aktualisierung_sekunden) || 60);
    const intervall = setInterval(laden, sekunden * 1000);
    return () => clearInterval(intervall);
  }, [daten?.aktualisierung_sekunden, vorschauModus]);

  useEffect(() => {
    // Sicherheitsnetz fuer einen dauerhaft laufenden TV-Browser (Langzeit-Speicherprobleme etc.) -
    // im Vorschau-iframe des Admin-Panels unnoetig und wuerde dort nur den iframe neu laden.
    if (vorschauModus) return;
    const reloadTimer = setInterval(() => window.location.reload(), 30 * 60 * 1000);
    return () => clearInterval(reloadTimer);
  }, [vorschauModus]);

  useEffect(() => {
    const tick = () => {
      const p = tvBerlinTeile();
      setUhrzeit(`${p.hour}:${p.minute}`);
      setWochentagLabel(`${p.weekday}, ${p.day}.${p.month}.`);
      if (daten) {
        setBetreutJetzt(tvIstBetreutJetzt(daten.betreuungszeiten, p.weekday, tvMinuten(`${p.hour}:${p.minute}`)));
      }
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => clearInterval(t);
  }, [daten]);

  // Rotation der Hauptaktionen auf der grossen Hauptflaeche: ruhig & langsam (alle 25s), bleibt
  // stehen wenn nur eine (oder keine) Aktion aktiv ist. Reine Anzeigelogik hier im Client, die
  // eigentliche Auswahl/Filterung (aktiv, Zeitfenster, als_hauptaktion) macht bereits das Backend.
  const aktionenListe = daten?.aktionen || [];
  useEffect(() => {
    if (aktionenListe.length <= 1) { setAktionIndex(0); return; }
    const intervall = setInterval(() => setAktionIndex(i => (i + 1) % aktionenListe.length), 25000);
    return () => clearInterval(intervall);
  }, [aktionenListe.length]);

  // "Schon gewusst?"-Rotation: eigener, kuerzerer Takt als die grosse Aktionen-Rotation (12s statt
  // 25s), weil das hier nur ein kurzer Einzeiler ist, kein grosses Motiv - bleibt stehen bei <=1
  // Eintrag, exakt dasselbe Muster wie bei den Aktionen oben.
  const schonGewusstListe = daten?.schon_gewusst || [];
  const [schonGewusstIndex, setSchonGewusstIndex] = useState(0);
  useEffect(() => {
    if (schonGewusstListe.length <= 1) { setSchonGewusstIndex(0); return; }
    const intervall = setInterval(() => setSchonGewusstIndex(i => (i + 1) % schonGewusstListe.length), 12000);
    return () => clearInterval(intervall);
  }, [schonGewusstListe.length]);

  // .tv-screen ist fest 1920x1080 (siehe App.css) - hier auf die tatsaechlich gemessene Fensterflaeche
  // herunterskaliert, exakt wie die Live-Vorschau im Admin-Tab TV (TvVorschau). Bewusst per JS
  // (window.innerWidth/innerHeight -> einfache Zahl) statt per CSS scale(100vw/1920): Letzteres ist
  // ungueltiges CSS (scale() verlangt eine reine Zahl, eine durch eine Zahl geteilte vw-Laenge bleibt
  // aber eine Laenge) - der Browser wuerde die Regel lautlos verwerfen und gar nicht skalieren.
  // Im Hochformat (Handy zur Kontrolle) bleibt die Skalierung bewusst bei 1 - dort greift stattdessen
  // die gestapelte, scrollbare CSS-Regel @media (orientation: portrait).
  const [skala, setSkala] = useState(1);
  useEffect(() => {
    const berechnen = () => {
      if (window.matchMedia && window.matchMedia("(orientation: portrait)").matches) {
        setSkala(1);
        return;
      }
      const sx = window.innerWidth / 1920;
      const sy = window.innerHeight / 1080;
      setSkala(Number.isFinite(sx) && Number.isFinite(sy) && sx > 0 && sy > 0 ? Math.min(sx, sy) : 1);
    };
    berechnen();
    window.addEventListener("resize", berechnen);
    window.addEventListener("orientationchange", berechnen);
    return () => {
      window.removeEventListener("resize", berechnen);
      window.removeEventListener("orientationchange", berechnen);
    };
  }, []);

  if (!daten) return <div className="tv-page" />;

  // Individuelle Hintergrund-/Textfarbe (Admin -> TV -> Einstellungen -> Design) als CSS-Custom-
  // Properties gesetzt - ueberschreiben die Default-Werte aus .tv-page in App.css. Fallback greift
  // nur, falls die Felder mal leer ankommen sollten.
  const farbVariablen = { "--tv-bg": daten.farbe_hintergrund || "#080808", "--tv-text": daten.farbe_text || "#ffffff" };

  if (!vorschauModus && daten.seite_aktiv === false) {
    // Admin hat die TV-Seite in Einstellungen -> Allgemein bewusst deaktiviert (z.B. Umbau/Pause).
    return (
      <div className="tv-page" style={farbVariablen}>
        <div className="tv-pausiert">
          <img src={`${API}/einstellungen/logo`} alt="ComeFit" />
          <p>Wird gleich fortgesetzt</p>
        </div>
      </div>
    );
  }

  const heute = daten.heutiger_wochentag;
  const aktiveAktion = aktionenListe.length > 0 ? aktionenListe[aktionIndex % aktionenListe.length] : null;

  return (
    <div className="tv-page" style={farbVariablen}>
      <main className="tv-screen" style={{ transform: `scale(${skala})` }}>
        <header className="tv-header">
          <div className="tv-brand">
            <img src={`${API}/einstellungen/logo`} alt="ComeFit" className="tv-logo-img" />
          </div>
          <div className="tv-clock"><small>{wochentagLabel}</small><span>{uhrzeit}</span></div>
        </header>

        <section className="tv-left">
          <article className="tv-card tv-hero">
            {aktiveAktion ? (
              <>
                <div className="tv-hero-bg" style={aktiveAktion.bild_url ? {
                  backgroundImage: `url(${API}${aktiveAktion.bild_url})`,
                  backgroundSize: aktiveAktion.bild_modus === "contain" ? "contain" : "cover",
                  backgroundPosition: `${aktiveAktion.bild_position || "center"} ${aktiveAktion.bild_position_vertikal || "center"}`,
                } : undefined} />
                {/* key wechselt pro Aktion -> React haengt die Karte neu ein, die CSS-Fade-Animation
                    (siehe .tv-hero-content in App.css) spielt dadurch bei jedem Wechsel erneut ab. */}
                <div className="tv-hero-content" key={`aktion-${aktiveAktion.id}`}>
                  <div className="tv-eyebrow">Aktion</div>
                  <h1>{aktiveAktion.titel}</h1>
                  {aktiveAktion.text && <p className="tv-text-body">{aktiveAktion.text}</p>}
                  {aktiveAktion.qr_url && <img className="tv-hero-qr" src={`${API}${aktiveAktion.qr_url}`} alt="QR-Code zur Aktion" />}
                </div>
              </>
            ) : daten.hauptmeldung ? (
              <>
                <div className="tv-hero-bg" style={daten.hauptmeldung.bild_url ? {
                  backgroundImage: `url(${API}${daten.hauptmeldung.bild_url})`,
                  // "contain" fuer hochformatige/textlastige Grafiken (z.B. Insta-Story-Format) -
                  // sonst schneidet "cover" bei falschem Seitenverhaeltnis Teile des Bildes/Textes ab
                  // oder wirkt verzerrt. Einstellbar im Admin-Tab TV -> Hauptmeldung.
                  backgroundSize: daten.hauptmeldung.bild_modus === "contain" ? "contain" : "cover",
                  backgroundPosition: `${daten.hauptmeldung.bild_position || "center"} ${daten.hauptmeldung.bild_position_vertikal || "center"}`,
                } : undefined} />
                <div className="tv-hero-content">
                  <div className="tv-eyebrow">Aktuell bei ComeFit</div>
                  <h1>{daten.hauptmeldung.titel}</h1>
                  {daten.hauptmeldung.text && <p className="tv-text-body">{daten.hauptmeldung.text}</p>}
                </div>
              </>
            ) : (
              <>
                <div className="tv-hero-bg" />
                <div className="tv-hero-content">
                  <div className="tv-eyebrow">Willkommen bei</div>
                  <h1>COME<span>FIT</span></h1>
                  <p className="tv-text-body">Schön, dass du da bist!</p>
                </div>
              </>
            )}
          </article>

          {daten.news.length > 0 && (
            <article className="tv-card tv-news">
              <h2 className="tv-section-title"><span className="tv-icon">●</span> News</h2>
              <div className="tv-news-list">
                {daten.news.map((titel, i) => <div className="tv-news-item" key={i}>{titel}</div>)}
              </div>
            </article>
          )}

          {schonGewusstListe.length > 0 && (
            <article className="tv-card tv-schon-gewusst">
              <h2 className="tv-section-title"><span className="tv-icon">💡</span> Schon gewusst?</h2>
              {/* key wechselt pro Eintrag -> spielt dieselbe dezente Fade-Animation wie beim
                  Aktionswechsel im Hero erneut ab (siehe .tv-hero-content in App.css). */}
              <p className="tv-schon-gewusst-text" key={schonGewusstIndex}>
                {schonGewusstListe[schonGewusstIndex % schonGewusstListe.length]}
              </p>
            </article>
          )}
        </section>

        <section className="tv-right">
          <article className="tv-card tv-today">
            <h2 className="tv-section-title"><span className="tv-icon">◷</span> Heute bei ComeFit</h2>
            {daten.kurse_heute.map((k, i) => (
              <div className="tv-today-row" key={i}>
                <div className="tv-today-label"><span className="tv-dot">●</span>{k.name}</div>
                <div className="tv-today-value">
                  <span className="tv-today-zeit">{k.von && k.bis ? `${k.von} – ${k.bis} Uhr` : (k.von ? `${k.von} Uhr` : "")}</span>
                </div>
              </div>
            ))}
            <div className="tv-today-row">
              <div className="tv-today-label">
                <span className="tv-dot">●</span>Sauna
                {(daten.sauna_an === true || daten.sauna_an === false) && (
                  <span className={`tv-sauna-status ${daten.sauna_an ? "an" : "aus"}`}>{daten.sauna_an ? "An" : "Aus"}</span>
                )}
              </div>
              <div className="tv-today-value">
                {tvZeitenListe(daten.saunazeiten, heute).map((zeile, i) => <span className="tv-today-zeit" key={i}>{zeile}</span>)}
              </div>
            </div>
            <div className="tv-today-row">
              <div className="tv-today-label"><span className="tv-dot">●</span>Betreuung</div>
              <div className="tv-today-value">
                {tvZeitenListe(daten.betreuungszeiten, heute).map((zeile, i) => <span className="tv-today-zeit" key={i}>{zeile}</span>)}
              </div>
            </div>
            <div className="tv-today-row">
              <div className="tv-today-label"><span className="tv-dot">●</span>Training</div>
              <div className="tv-today-value">
                {tvZeitenListe(daten.trainingszeiten, heute).map((zeile, i) => <span className="tv-today-zeit" key={i}>{zeile}</span>)}
              </div>
            </div>
          </article>

          {daten.kursangebot.length > 0 && (
            <article className="tv-card tv-kurse-werbung">
              <h2 className="tv-kurse-titel"><span className="tv-icon">🏋</span> Kurse</h2>
              <div className="tv-kurse-liste">
                {daten.kursangebot.map((k, i) => {
                  const zeit = k.von && k.bis ? `${k.von}–${k.bis} Uhr` : (k.von ? `${k.von} Uhr` : "");
                  return (
                    <div className="tv-kurse-item" key={i}>
                      <b>{k.name}</b>
                      {(k.wochentag || zeit || k.zusatztext) && (
                        <span className="tv-kurse-meta">
                          {k.wochentag && <span className="tv-kurse-tag">{k.wochentag}</span>}
                          {k.wochentag && zeit && <span className="tv-kurse-trenner"> · </span>}
                          {zeit && <span className="tv-kurse-zeit">{zeit}</span>}
                          {k.zusatztext && <span className="tv-kurse-zusatz"> {(k.wochentag || zeit) ? "— " : ""}{k.zusatztext}</span>}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </article>
          )}

          <article className="tv-card tv-qr-card">
            <div className="tv-qr-grid">
              <div className="tv-qr">
                <div className="tv-qr-icon"><TvGoogleIcon /></div>
                <img src={`${API}/tv/qr/google.png`} alt="QR-Code für Google-Bewertung" />
              </div>
              <div className="tv-qr">
                <div className="tv-qr-icon"><Wifi strokeWidth={2.2} /></div>
                <img src={`${API}/tv/qr/wifi.png`} alt="QR-Code für WLAN-Zugang" />
              </div>
              <div className="tv-qr">
                <div className="tv-qr-icon"><MessageCircle strokeWidth={2.2} /></div>
                <img src={`${API}/tv/qr/feedback.png`} alt="QR-Code für Feedback-Umfrage" />
              </div>
            </div>
          </article>
        </section>

        {/* Immer sichtbar (nicht mehr nur bei Unbetreut) - Text/Optik passt sich an: ruhiger
            Kontakt-Hinweis waehrend Betreuungszeiten, deutliche Warnfarbe + Puls-Animation nur
            wenn wirklich gerade unbetreut ist. */}
        <section className={`tv-staffing-banner${betreutJetzt ? "" : " tv-staffing-banner--unbetreut"}`}>
          <div className="tv-staffing-status">
            <span className="tv-staffing-dot" />
            <span>{betreutJetzt ? "Bei Fragen erreichst du uns:" : "Studio aktuell unbetreut – bei Fragen oder Problemen erreichst du uns:"}</span>
          </div>
          <div className="tv-staffing-contact">
            Emanuele: {daten.telefon_emanuele || "–"} &nbsp;•&nbsp; Katharina: {daten.telefon_katharina || "–"}
          </div>
        </section>
      </main>
    </div>
  );
}

function TvZeitplanTabelle({ titel, plan, onChange }) {
  const bereichAendern = (tag, idx, feld, wert) => {
    const kopie = { ...plan };
    const liste = [...(kopie[tag] || [])];
    liste[idx] = feld === "von" ? [wert, liste[idx][1]] : [liste[idx][0], wert];
    kopie[tag] = liste;
    onChange(kopie);
  };
  const bereichHinzufuegen = (tag) => {
    const kopie = { ...plan };
    kopie[tag] = [...(kopie[tag] || []), ["09:00", "12:00"]];
    onChange(kopie);
  };
  const bereichEntfernen = (tag, idx) => {
    const kopie = { ...plan };
    kopie[tag] = (kopie[tag] || []).filter((_, i) => i !== idx);
    onChange(kopie);
  };
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 10 }}>{titel}</h3>
      {TV_WOCHENTAGE.map(tag => (
        <div key={tag} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
          <span style={{ width: 100, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{tag}</span>
          {(plan[tag] || []).map((bereich, idx) => (
            <span key={idx} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input type="time" value={bereich[0]} onChange={e => bereichAendern(tag, idx, "von", e.target.value)} style={{ ...s.input, width: 110, padding: "6px 8px" }} />
              <span style={{ color: THEME.colors.textMuted }}>–</span>
              <input type="time" value={bereich[1]} onChange={e => bereichAendern(tag, idx, "bis", e.target.value)} style={{ ...s.input, width: 110, padding: "6px 8px" }} />
              <button onClick={() => bereichEntfernen(tag, idx)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px" }}>×</button>
            </span>
          ))}
          <button onClick={() => bereichHinzufuegen(tag)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.sm }}>+ Zeitraum</button>
        </div>
      ))}
    </div>
  );
}

function TvKurseTabelle({ plan, onChange }) {
  // Eigener, einfacher TV-Kursplan (siehe tv_kurse_json in routers_tv.py) - bewusst NICHT die
  // echte Kurse-/Buchungsverwaltung, damit die TV-Anzeige nie mit Teilnehmerlisten/Buchungen
  // kollidiert. Jeder Tag hat eine Liste von {name, von, bis, aktiv}.
  const eintragAendern = (tag, idx, feld, wert) => {
    const kopie = { ...plan };
    const liste = [...(kopie[tag] || [])];
    liste[idx] = { ...liste[idx], [feld]: wert };
    kopie[tag] = liste;
    onChange(kopie);
  };
  const eintragHinzufuegen = (tag) => {
    const kopie = { ...plan };
    kopie[tag] = [...(kopie[tag] || []), { name: "", von: "09:00", bis: "10:00", aktiv: true }];
    onChange(kopie);
  };
  const eintragEntfernen = (tag, idx) => {
    const kopie = { ...plan };
    kopie[tag] = (kopie[tag] || []).filter((_, i) => i !== idx);
    onChange(kopie);
  };
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 10 }}>Kurse</h3>
      {TV_WOCHENTAGE.map(tag => (
        <div key={tag} style={{ marginBottom: 10 }}>
          <span style={{ display: "block", width: 100, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginBottom: 4 }}>{tag}</span>
          {(plan[tag] || []).map((eintrag, idx) => (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
              <input placeholder="Kursname" value={eintrag.name || ""} onChange={e => eintragAendern(tag, idx, "name", e.target.value)} style={{ ...s.input, width: 160, padding: "6px 8px" }} />
              <input type="time" value={eintrag.von || ""} onChange={e => eintragAendern(tag, idx, "von", e.target.value)} style={{ ...s.input, width: 110, padding: "6px 8px" }} />
              <span style={{ color: THEME.colors.textMuted }}>–</span>
              <input type="time" value={eintrag.bis || ""} onChange={e => eintragAendern(tag, idx, "bis", e.target.value)} style={{ ...s.input, width: 110, padding: "6px 8px" }} />
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                <input type="checkbox" checked={eintrag.aktiv !== false} onChange={e => eintragAendern(tag, idx, "aktiv", e.target.checked)} /> Aktiv
              </label>
              <button onClick={() => eintragEntfernen(tag, idx)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px" }}>×</button>
            </div>
          ))}
          <button onClick={() => eintragHinzufuegen(tag)} style={{ ...s.btnGray, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.sm }}>+ Kurs</button>
        </div>
      ))}
    </div>
  );
}

function TvVorschau({ vorschauVersion }) {
  // Skaliert eine echte 1920x1080-Instanz der /tv-Seite (per iframe, gleicher Origin -> Sitzungs-
  // Cookie wird automatisch mitgeschickt) auf die verfuegbare Breite herunter. So sieht man exakt
  // das spaetere TV-Bild direkt beim Bearbeiten, ohne extra Tab/Fernseher pruefen zu muessen.
  const boxRef = useRef(null);
  const [breite, setBreite] = useState(960);

  useEffect(() => {
    const messen = () => { if (boxRef.current) setBreite(boxRef.current.offsetWidth); };
    messen();
    window.addEventListener("resize", messen);
    return () => window.removeEventListener("resize", messen);
  }, []);

  const skala = breite / 1920;

  return (
    <div style={{ ...s.karte, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, margin: 0 }}>📺 Live-Vorschau</h3>
        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Zeigt den aktuellen Entwurf – auch wenn „Aktiv" noch nicht gesetzt ist</span>
      </div>
      <div ref={boxRef} style={{ width: "100%", aspectRatio: "16 / 9", borderRadius: 14, overflow: "hidden", border: `1px solid ${THEME.colors.border}`, background: "#000" }}>
        {breite > 0 && (
          <iframe
            key={vorschauVersion}
            title="TV-Infoboard Vorschau"
            src="/tv?vorschau=1"
            style={{ width: 1920, height: 1080, border: 0, transform: `scale(${skala})`, transformOrigin: "top left" }}
          />
        )}
      </div>
    </div>
  );
}

function TvVerwaltungSeite() {
  const [aktiveTab, setAktiveTab] = useState("hauptmeldung");
  const [hauptmeldung, setHauptmeldung] = useState({ titel: "", text: "", start_datum: "", end_datum: "", aktiv: false, bild_vorhanden: false, bild_modus: "cover", bild_position: "center", bild_position_vertikal: "center" });
  const [hmGespeichert, setHmGespeichert] = useState(false);
  // Zaehler, der als iframe-"key" fuer TvVorschau dient - bei jeder erfolgreichen Speicherung
  // hochgezaehlt, damit die Vorschau den iframe neu laedt und sofort den aktuellen Stand zeigt
  // (ein einfacher location.reload() im iframe wuerde React dort nicht zuverlaessig neu mounten).
  const [vorschauVersion, setVorschauVersion] = useState(0);
  const aktualisiereVorschau = () => setVorschauVersion(v => v + 1);
  const [bildLaedt, setBildLaedt] = useState(false);
  const [bildFehler, setBildFehler] = useState("");
  const [bildVersion, setBildVersion] = useState(() => Date.now());
  const [vorlagenListe, setVorlagenListe] = useState([]);
  const [ausgewaehlteVorlageId, setAusgewaehlteVorlageId] = useState("");
  const [bearbeitenVorlageId, setBearbeitenVorlageId] = useState(null);
  const [vorlageBildLaedt, setVorlageBildLaedt] = useState(false);
  const [vorlageBildVersion, setVorlageBildVersion] = useState(() => Date.now());
  const [newsListe, setNewsListe] = useState([]);
  const [neueNews, setNeueNews] = useState({ titel: "", aktiv: true, start_datum: "", end_datum: "", in_app_sichtbar: false });
  const [bearbeitenNewsId, setBearbeitenNewsId] = useState(null);
  const [schonGewusstListe, setSchonGewusstListe] = useState([]);
  const [neuerSchonGewusstText, setNeuerSchonGewusstText] = useState("");
  const [bearbeitenSchonGewusstId, setBearbeitenSchonGewusstId] = useState(null);
  const [kurseListe, setKurseListe] = useState([]);
  const [neuerKursName, setNeuerKursName] = useState("");
  const [bearbeitenKursId, setBearbeitenKursId] = useState(null);
  const [aktionenListe, setAktionenListe] = useState([]);
  const [neueAktionTitel, setNeueAktionTitel] = useState("");
  const [bearbeitenAktionId, setBearbeitenAktionId] = useState(null);
  const [aktionBildLaedt, setAktionBildLaedt] = useState(false);
  const [aktionBildVersion, setAktionBildVersion] = useState(() => Date.now());
  const [einstellungenForm, setEinstellungenForm] = useState({});
  const [einstellungenGespeichert, setEinstellungenGespeichert] = useState(false);

  const ladeHauptmeldung = () => fetch(`${API}/tv/hauptmeldung`).then(r => r.json()).then(setHauptmeldung);
  const ladeVorlagen = () => fetch(`${API}/tv/hauptmeldung/vorlagen`).then(r => r.json()).then(setVorlagenListe);
  const ladeNews = () => fetch(`${API}/tv/news`).then(r => r.json()).then(setNewsListe);
  const ladeSchonGewusst = () => fetch(`${API}/tv/schon-gewusst`).then(r => r.json()).then(setSchonGewusstListe);
  const ladeKurse = () => fetch(`${API}/tv/kurse`).then(r => r.json()).then(setKurseListe);
  const ladeAktionen = () => fetch(`${API}/tv/aktionen`).then(r => r.json()).then(setAktionenListe);
  const ladeEinstellungen = () => fetch(`${API}/einstellungen/`).then(r => r.json()).then(setEinstellungenForm);

  useEffect(() => { ladeHauptmeldung(); ladeVorlagen(); ladeNews(); ladeSchonGewusst(); ladeKurse(); ladeAktionen(); ladeEinstellungen(); }, []);

  const handleHauptmeldungSpeichern = async () => {
    const res = await fetch(`${API}/tv/hauptmeldung`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        titel: hauptmeldung.titel, text: hauptmeldung.text,
        start_datum: hauptmeldung.start_datum || null, end_datum: hauptmeldung.end_datum || null,
        aktiv: hauptmeldung.aktiv, bild_modus: hauptmeldung.bild_modus || "cover",
        bild_position: hauptmeldung.bild_position || "center",
        bild_position_vertikal: hauptmeldung.bild_position_vertikal || "center",
      }),
    });
    if (!res.ok) { alert("Hauptmeldung konnte nicht gespeichert werden."); return; }
    setHmGespeichert(true);
    setTimeout(() => setHmGespeichert(false), 3000);
    aktualisiereVorschau();
  };

  const handleBildHochladen = async (datei) => {
    if (!datei) return;
    setBildLaedt(true);
    setBildFehler("");
    try {
      const formData = new FormData();
      formData.append("datei", datei);
      const res = await fetch(`${API}/tv/hauptmeldung/bild`, { method: "POST", body: formData });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Bild konnte nicht hochgeladen werden.");
      }
      setBildVersion(Date.now());
      ladeHauptmeldung();
      aktualisiereVorschau();
    } catch (e) {
      setBildFehler(e.message || "Bild konnte nicht hochgeladen werden.");
    }
    setBildLaedt(false);
  };

  const handleBildEntfernen = async () => {
    if (!window.confirm("Bild wirklich entfernen?")) return;
    setBildLaedt(true);
    const res = await fetch(`${API}/tv/hauptmeldung/bild`, { method: "DELETE" });
    setBildLaedt(false);
    if (!res.ok) { alert("Bild konnte nicht entfernt werden."); return; }
    ladeHauptmeldung();
    aktualisiereVorschau();
  };

  // ---- Hauptmeldung-Vorlagen ----
  const handleVorlageAnwenden = async () => {
    if (!ausgewaehlteVorlageId) return;
    const res = await fetch(`${API}/tv/hauptmeldung/vorlagen/${ausgewaehlteVorlageId}/anwenden`, { method: "POST" });
    if (!res.ok) { alert("Vorlage konnte nicht angewendet werden."); return; }
    setBildVersion(Date.now());
    await ladeHauptmeldung();
    aktualisiereVorschau();
  };

  const handleAlsVorlageSpeichern = async () => {
    const name = window.prompt("Name für die neue Vorlage:", hauptmeldung.titel || "");
    if (!name || !name.trim()) return;
    const res = await fetch(`${API}/tv/hauptmeldung/vorlagen/aus-hauptmeldung`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: name.trim() }),
    });
    if (!res.ok) { alert("Vorlage konnte nicht gespeichert werden."); return; }
    ladeVorlagen();
  };

  const handleVorlageErstellen = async () => {
    const name = window.prompt("Name der neuen Vorlage:");
    if (!name || !name.trim()) return;
    const res = await fetch(`${API}/tv/hauptmeldung/vorlagen`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: name.trim() }),
    });
    if (!res.ok) { alert("Vorlage konnte nicht angelegt werden."); return; }
    const { id } = await res.json();
    await ladeVorlagen();
    setBearbeitenVorlageId(id);
  };

  const handleVorlageSpeichern = async (v) => {
    const res = await fetch(`${API}/tv/hauptmeldung/vorlagen/${v.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: v.name, titel: v.titel || "", text: v.text || "",
        bild_modus: v.bild_modus || "cover", bild_position: v.bild_position || "center",
        bild_position_vertikal: v.bild_position_vertikal || "center",
        standard_dauer_tage: v.standard_dauer_tage ? Number(v.standard_dauer_tage) : null,
      }),
    });
    if (!res.ok) { alert("Vorlage konnte nicht gespeichert werden."); return; }
    setBearbeitenVorlageId(null);
    ladeVorlagen();
  };

  const handleVorlageLoeschen = async (id) => {
    if (!window.confirm("Vorlage wirklich löschen?")) return;
    const res = await fetch(`${API}/tv/hauptmeldung/vorlagen/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    if (String(ausgewaehlteVorlageId) === String(id)) setAusgewaehlteVorlageId("");
    ladeVorlagen();
  };

  const handleVorlageDuplizieren = async (id) => {
    const res = await fetch(`${API}/tv/hauptmeldung/vorlagen/${id}/duplizieren`, { method: "POST" });
    if (!res.ok) { alert("Duplizieren fehlgeschlagen."); return; }
    ladeVorlagen();
  };

  const handleVorlageBildHochladen = async (vorlageId, datei) => {
    if (!datei) return;
    setVorlageBildLaedt(true);
    try {
      const formData = new FormData();
      formData.append("datei", datei);
      const res = await fetch(`${API}/tv/hauptmeldung/vorlagen/${vorlageId}/bild`, { method: "POST", body: formData });
      if (res.ok) {
        setVorlageBildVersion(Date.now());
        ladeVorlagen();
      } else {
        alert("Bild konnte nicht hochgeladen werden.");
      }
    } catch { alert("Bild konnte nicht hochgeladen werden."); }
    setVorlageBildLaedt(false);
  };

  const handleVorlageBildEntfernen = async (vorlageId) => {
    if (!window.confirm("Bild wirklich entfernen?")) return;
    const res = await fetch(`${API}/tv/hauptmeldung/vorlagen/${vorlageId}/bild`, { method: "DELETE" });
    if (!res.ok) { alert("Bild konnte nicht entfernt werden."); return; }
    ladeVorlagen();
  };

  const handleNewsErstellen = async () => {
    if (!neueNews.titel.trim()) return;
    const res = await fetch(`${API}/tv/news`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...neueNews, start_datum: neueNews.start_datum || null, end_datum: neueNews.end_datum || null }),
    });
    if (!res.ok) { alert("News konnte nicht gespeichert werden."); return; }
    setNeueNews({ titel: "", aktiv: true, start_datum: "", end_datum: "", in_app_sichtbar: false });
    ladeNews();
    aktualisiereVorschau();
  };

  const handleNewsSpeichern = async (n) => {
    const res = await fetch(`${API}/tv/news/${n.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ titel: n.titel, aktiv: n.aktiv, start_datum: n.start_datum || null, end_datum: n.end_datum || null, in_app_sichtbar: !!n.in_app_sichtbar }),
    });
    if (!res.ok) { alert("News konnte nicht gespeichert werden."); return; }
    setBearbeitenNewsId(null);
    ladeNews();
    aktualisiereVorschau();
  };

  const handleNewsToggleAktiv = async (n, aktiv) => {
    const res = await fetch(`${API}/tv/news/${n.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ titel: n.titel, aktiv, start_datum: n.start_datum || null, end_datum: n.end_datum || null, in_app_sichtbar: !!n.in_app_sichtbar }),
    });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); return; }
    ladeNews();
    aktualisiereVorschau();
  };

  const handleNewsToggleAppSichtbar = async (n, inAppSichtbar) => {
    const res = await fetch(`${API}/tv/news/${n.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ titel: n.titel, aktiv: n.aktiv, start_datum: n.start_datum || null, end_datum: n.end_datum || null, in_app_sichtbar: inAppSichtbar }),
    });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); return; }
    ladeNews();
  };

  const handleNewsLoeschen = async (id) => {
    if (!window.confirm("News wirklich löschen?")) return;
    const res = await fetch(`${API}/tv/news/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladeNews();
    aktualisiereVorschau();
  };

  const handleNewsVerschieben = async (index, richtung) => {
    const neueListe = [...newsListe];
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= neueListe.length) return;
    [neueListe[index], neueListe[ziel]] = [neueListe[ziel], neueListe[index]];
    setNewsListe(neueListe);
    const res = await fetch(`${API}/tv/news/reihenfolge`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: neueListe.map(n => n.id) }),
    });
    if (!res.ok) { alert("Reihenfolge konnte nicht gespeichert werden."); return; }
    aktualisiereVorschau();
  };

  // ---- Schon gewusst ----
  const handleSchonGewusstErstellen = async () => {
    if (!neuerSchonGewusstText.trim()) return;
    const res = await fetch(`${API}/tv/schon-gewusst`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: neuerSchonGewusstText, aktiv: true }),
    });
    if (!res.ok) { alert("Eintrag konnte nicht gespeichert werden."); return; }
    setNeuerSchonGewusstText("");
    ladeSchonGewusst();
    aktualisiereVorschau();
  };

  const handleSchonGewusstSpeichern = async (s) => {
    const res = await fetch(`${API}/tv/schon-gewusst/${s.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: s.text, aktiv: s.aktiv }),
    });
    if (!res.ok) { alert("Eintrag konnte nicht gespeichert werden."); return; }
    setBearbeitenSchonGewusstId(null);
    ladeSchonGewusst();
    aktualisiereVorschau();
  };

  const handleSchonGewusstToggleAktiv = async (s, aktiv) => {
    const res = await fetch(`${API}/tv/schon-gewusst/${s.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: s.text, aktiv }),
    });
    if (!res.ok) { alert("Status konnte nicht geändert werden."); return; }
    ladeSchonGewusst();
    aktualisiereVorschau();
  };

  const handleSchonGewusstLoeschen = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    const res = await fetch(`${API}/tv/schon-gewusst/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladeSchonGewusst();
    aktualisiereVorschau();
  };

  const handleSchonGewusstVerschieben = async (index, richtung) => {
    const neueListe = [...schonGewusstListe];
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= neueListe.length) return;
    [neueListe[index], neueListe[ziel]] = [neueListe[ziel], neueListe[index]];
    setSchonGewusstListe(neueListe);
    const res = await fetch(`${API}/tv/schon-gewusst/reihenfolge`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: neueListe.map(s => s.id) }),
    });
    if (!res.ok) { alert("Reihenfolge konnte nicht gespeichert werden."); return; }
    aktualisiereVorschau();
  };

  // ---- Kurse (eigenstaendige Werbekarte, unabhaengig von "Heute") ----
  const handleKursErstellen = async () => {
    if (!neuerKursName.trim()) return;
    const res = await fetch(`${API}/tv/kurse`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: neuerKursName, aktiv: true }),
    });
    if (!res.ok) { alert("Kurs konnte nicht angelegt werden."); return; }
    const { id } = await res.json();
    setNeuerKursName("");
    await ladeKurse();
    setBearbeitenKursId(id);
    aktualisiereVorschau();
  };

  const handleKursSpeichern = async (k) => {
    const res = await fetch(`${API}/tv/kurse/${k.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: k.name, wochentag: k.wochentag || "", von: k.von || "", bis: k.bis || "",
        zusatztext: k.zusatztext || "", aktiv: k.aktiv,
      }),
    });
    if (!res.ok) { alert("Kurs konnte nicht gespeichert werden."); return; }
    setBearbeitenKursId(null);
    ladeKurse();
    aktualisiereVorschau();
  };

  const handleKursToggleAktiv = async (k, aktiv) => {
    await handleKursSpeichern({ ...k, aktiv });
  };

  const handleKursLoeschen = async (id) => {
    if (!window.confirm("Kurs wirklich löschen?")) return;
    const res = await fetch(`${API}/tv/kurse/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladeKurse();
    aktualisiereVorschau();
  };

  const handleKursVerschieben = async (index, richtung) => {
    const neueListe = [...kurseListe];
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= neueListe.length) return;
    [neueListe[index], neueListe[ziel]] = [neueListe[ziel], neueListe[index]];
    setKurseListe(neueListe);
    const res = await fetch(`${API}/tv/kurse/reihenfolge`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: neueListe.map(k => k.id) }),
    });
    if (!res.ok) { alert("Reihenfolge konnte nicht gespeichert werden."); return; }
    aktualisiereVorschau();
  };

  // ---- Aktionen ----
  const handleAktionErstellen = async () => {
    if (!neueAktionTitel.trim()) return;
    const res = await fetch(`${API}/tv/aktionen`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ titel: neueAktionTitel, aktiv: true }),
    });
    if (!res.ok) { alert("Aktion konnte nicht angelegt werden."); return; }
    const { id } = await res.json();
    setNeueAktionTitel("");
    await ladeAktionen();
    setBearbeitenAktionId(id);
    aktualisiereVorschau();
  };

  const handleAktionSpeichern = async (a) => {
    const res = await fetch(`${API}/tv/aktionen/${a.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        titel: a.titel, text: a.text || "", aktiv: a.aktiv,
        start_datum: a.start_datum || null, end_datum: a.end_datum || null,
        link: a.link || "", zeige_qr: !!a.zeige_qr, als_hauptaktion: !!a.als_hauptaktion,
        bild_modus: a.bild_modus || "cover", bild_position: a.bild_position || "center",
        bild_position_vertikal: a.bild_position_vertikal || "center",
      }),
    });
    if (!res.ok) { alert("Aktion konnte nicht gespeichert werden."); return; }
    setBearbeitenAktionId(null);
    ladeAktionen();
    aktualisiereVorschau();
  };

  const handleAktionToggleAktiv = async (a, aktiv) => {
    await handleAktionSpeichern({ ...a, aktiv });
  };

  const handleAktionLoeschen = async (id) => {
    if (!window.confirm("Aktion wirklich löschen?")) return;
    const res = await fetch(`${API}/tv/aktionen/${id}`, { method: "DELETE" });
    if (!res.ok) { alert("Löschen fehlgeschlagen."); return; }
    ladeAktionen();
    aktualisiereVorschau();
  };

  const handleAktionVerschieben = async (index, richtung) => {
    const neueListe = [...aktionenListe];
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= neueListe.length) return;
    [neueListe[index], neueListe[ziel]] = [neueListe[ziel], neueListe[index]];
    setAktionenListe(neueListe);
    const res = await fetch(`${API}/tv/aktionen/reihenfolge`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: neueListe.map(a => a.id) }),
    });
    if (!res.ok) { alert("Reihenfolge konnte nicht gespeichert werden."); return; }
    aktualisiereVorschau();
  };

  const handleAktionBildHochladen = async (aktionId, datei) => {
    if (!datei) return;
    setAktionBildLaedt(true);
    try {
      const formData = new FormData();
      formData.append("datei", datei);
      const res = await fetch(`${API}/tv/aktionen/${aktionId}/bild`, { method: "POST", body: formData });
      if (res.ok) {
        setAktionBildVersion(Date.now());
        ladeAktionen();
        aktualisiereVorschau();
      } else {
        alert("Bild konnte nicht hochgeladen werden.");
      }
    } catch { alert("Bild konnte nicht hochgeladen werden."); }
    setAktionBildLaedt(false);
  };

  const handleAktionBildEntfernen = async (aktionId) => {
    if (!window.confirm("Bild wirklich entfernen?")) return;
    const res = await fetch(`${API}/tv/aktionen/${aktionId}/bild`, { method: "DELETE" });
    if (!res.ok) { alert("Bild konnte nicht entfernt werden."); return; }
    ladeAktionen();
    aktualisiereVorschau();
  };

  const handleEinstellungenSpeichern = async () => {
    const res = await fetch(`${API}/einstellungen/batch`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(einstellungenForm),
    });
    if (!res.ok) { alert("Einstellungen konnten nicht gespeichert werden."); return; }
    setEinstellungenGespeichert(true);
    setTimeout(() => setEinstellungenGespeichert(false), 3000);
    aktualisiereVorschau();
  };

  const zeitplanAendern = (schluessel, neuerPlan) => {
    setEinstellungenForm(f => ({ ...f, [schluessel]: JSON.stringify(neuerPlan) }));
  };
  const zeitplanParsen = (schluessel) => {
    try { return JSON.parse(einstellungenForm[schluessel] || "{}"); } catch { return {}; }
  };

  const tabs = [["hauptmeldung", "📢 Hauptmeldung"], ["news", "📰 News"], ["schongewusst", "💡 Schon gewusst?"], ["kurse", "🏋 Kurse"], ["aktionen", "🏷️ Aktionen"], ["heute", "📅 Heute"], ["einstellungen", "⚙️ Einstellungen"]];

  return (
    <div>
      <h1 style={s.heading}>TV-Infoboard</h1>
      <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setAktiveTab(key)}
            style={{ ...s.btnGray, width: "auto", padding: "10px 20px", borderRadius: 8, background: aktiveTab === key ? THEME.colors.primary : THEME.colors.surfaceMuted, color: aktiveTab === key ? kontrastTextfarbe(THEME.colors.primary) : kontrastTextfarbe(THEME.colors.surfaceMuted), fontSize: THEME.fontSize.md }}>{label}</button>
        ))}
      </div>

      <TvVorschau vorschauVersion={vorschauVersion} />

      {aktiveTab === "hauptmeldung" && (
        <div style={s.karte}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select style={{ ...s.input, width: "auto", minWidth: 220 }} value={ausgewaehlteVorlageId} onChange={e => setAusgewaehlteVorlageId(e.target.value)}>
              <option value="">Vorlage auswählen…</option>
              {vorlagenListe.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <button onClick={handleVorlageAnwenden} disabled={!ausgewaehlteVorlageId} style={{ ...s.btnGray, width: "auto", opacity: ausgewaehlteVorlageId ? 1 : 0.5 }}>Übernehmen</button>
            <button onClick={handleAlsVorlageSpeichern} style={{ ...s.btnGray, width: "auto" }}>Als Vorlage speichern</button>
          </div>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 20 }}>
            Eine Vorlage überträgt Titel, Text und Bild-Einstellungen sofort in die Felder unten – „Aktiv" wird dabei nie automatisch gesetzt, du kannst vor dem Speichern noch alles anpassen. Vorlagen selbst verwalten (bearbeiten/duplizieren/löschen) kannst du weiter unten.
          </p>
          <div style={s.inputGroup}><label style={s.label}>Titel</label><input style={s.input} value={hauptmeldung.titel || ""} onChange={e => setHauptmeldung({ ...hauptmeldung, titel: e.target.value })} /></div>
          <div style={s.inputGroup}><label style={s.label}>Text</label><textarea style={{ ...s.input, minHeight: 100 }} value={hauptmeldung.text || ""} onChange={e => setHauptmeldung({ ...hauptmeldung, text: e.target.value })} /></div>
          <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}><label style={s.label}>Start (optional)</label><input type="date" style={s.input} value={hauptmeldung.start_datum || ""} onChange={e => setHauptmeldung({ ...hauptmeldung, start_datum: e.target.value })} /></div>
            <div style={{ flex: 1 }}><label style={s.label}>Ende (optional)</label><input type="date" style={s.input} value={hauptmeldung.end_datum || ""} onChange={e => setHauptmeldung({ ...hauptmeldung, end_datum: e.target.value })} /></div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, color: THEME.colors.textSecondary }}>
            <input type="checkbox" checked={!!hauptmeldung.aktiv} onChange={e => setHauptmeldung({ ...hauptmeldung, aktiv: e.target.checked })} /> Aktiv (wird auf dem TV angezeigt)
          </label>
          <div style={{ marginBottom: 20 }}>
            <label style={s.label}>Bild (optional)</label>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "-2px 0 8px" }}>
              Empfohlen: <b>1600 × 900 px, quer</b> (passt für beide Anzeigemodi gut). Kleinere/größere Bilder gehen auch, werden dann entsprechend zugeschnitten oder eingepasst.
            </p>
            {hauptmeldung.bild_vorhanden && <img key={bildVersion} src={`${API}/tv/hauptmeldung/bild?v=${bildVersion}`} alt="Hauptmeldung" style={{ maxWidth: 300, maxHeight: 160, objectFit: "contain", display: "block", marginBottom: 8, borderRadius: 8 }} />}
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={e => handleBildHochladen(e.target.files[0])} disabled={bildLaedt} />
              {hauptmeldung.bild_vorhanden && (
                <button onClick={handleBildEntfernen} disabled={bildLaedt} style={{ ...s.btnGray, width: "auto", padding: "6px 14px", color: THEME.colors.danger }}>Bild entfernen</button>
              )}
            </div>
            {bildFehler && <p style={{ color: "#ff6b6b", fontSize: THEME.fontSize.sm }}>{bildFehler}</p>}
            {hauptmeldung.bild_vorhanden && (
              <>
                <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                    <input type="radio" name="bild_modus" checked={(hauptmeldung.bild_modus || "cover") === "cover"} onChange={() => setHauptmeldung({ ...hauptmeldung, bild_modus: "cover" })} />
                    Ausfüllen (schneidet Ränder ggf. an – für breite Fotos)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                    <input type="radio" name="bild_modus" checked={hauptmeldung.bild_modus === "contain"} onChange={() => setHauptmeldung({ ...hauptmeldung, bild_modus: "contain" })} />
                    Vollständig anzeigen (für Plakate/Hochformat-Grafiken mit Text am Rand)
                  </label>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
                  <label style={{ ...s.label, marginBottom: 0 }}>Bildausrichtung waagerecht</label>
                  {[["left", "Links"], ["center", "Zentriert"], ["right", "Rechts"]].map(([wert, label]) => (
                    <label key={wert} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                      <input type="radio" name="bild_position" checked={(hauptmeldung.bild_position || "center") === wert} onChange={() => setHauptmeldung({ ...hauptmeldung, bild_position: wert })} />
                      {label}
                    </label>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                  <label style={{ ...s.label, marginBottom: 0 }}>Bildausrichtung senkrecht</label>
                  {[["top", "Oben"], ["center", "Mitte"], ["bottom", "Unten"]].map(([wert, label]) => (
                    <label key={wert} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                      <input type="radio" name="bild_position_vertikal" checked={(hauptmeldung.bild_position_vertikal || "center") === wert} onChange={() => setHauptmeldung({ ...hauptmeldung, bild_position_vertikal: wert })} />
                      {label}
                    </label>
                  ))}
                </div>
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "4px 0 0" }}>
                  Falls das Hauptmotiv (z. B. eine Person) nicht mittig im Bild ist, hier passend ausrichten.
                </p>
              </>
            )}
          </div>
          <button onClick={handleHauptmeldungSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
          {hmGespeichert && <span style={{ color: THEME.colors.success, marginLeft: 12 }}>✓ Gespeichert</span>}
        </div>
      )}

      {aktiveTab === "hauptmeldung" && (
        <div style={s.karte}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, margin: 0 }}>Vorlagen verwalten</h3>
            <button onClick={handleVorlageErstellen} style={{ ...s.btn, width: "auto" }}>+ Neue Vorlage</button>
          </div>
          {vorlagenListe.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Vorlagen angelegt.</p>}
          {vorlagenListe.map(v => (
            <div key={v.id} style={{ borderTop: `1px solid ${THEME.colors.border}`, padding: "12px 0" }}>
              {bearbeitenVorlageId === v.id ? (
                <div>
                  <div style={s.inputGroup}><label style={s.label}>Vorlagenname</label><input style={s.input} value={v.name} onChange={e => setVorlagenListe(vorlagenListe.map(x => x.id === v.id ? { ...x, name: e.target.value } : x))} /></div>
                  <div style={s.inputGroup}><label style={s.label}>Titel</label><input style={s.input} value={v.titel || ""} onChange={e => setVorlagenListe(vorlagenListe.map(x => x.id === v.id ? { ...x, titel: e.target.value } : x))} /></div>
                  <div style={s.inputGroup}><label style={s.label}>Text</label><textarea style={{ ...s.input, minHeight: 80 }} value={v.text || ""} onChange={e => setVorlagenListe(vorlagenListe.map(x => x.id === v.id ? { ...x, text: e.target.value } : x))} /></div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={s.label}>Standard-Dauer (optional, in Tagen ab Anwenden)</label>
                    <input type="number" min={1} style={{ ...s.input, width: 120 }} value={v.standard_dauer_tage || ""} placeholder="z. B. 1" onChange={e => setVorlagenListe(vorlagenListe.map(x => x.id === v.id ? { ...x, standard_dauer_tage: e.target.value } : x))} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={s.label}>Bild (optional)</label>
                    {v.bild_vorhanden && <img key={vorlageBildVersion} src={`${API}/tv/hauptmeldung/vorlagen/${v.id}/bild?v=${vorlageBildVersion}`} alt={v.name} style={{ maxWidth: 260, maxHeight: 140, objectFit: "contain", display: "block", marginBottom: 8, borderRadius: 8 }} />}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={e => handleVorlageBildHochladen(v.id, e.target.files[0])} disabled={vorlageBildLaedt} />
                      {v.bild_vorhanden && <button onClick={() => handleVorlageBildEntfernen(v.id)} style={{ ...s.btnGray, width: "auto", padding: "6px 14px", color: THEME.colors.danger }}>Bild entfernen</button>}
                    </div>
                    {v.bild_vorhanden && (
                      <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                          <input type="radio" name={`vorlage_bild_modus_${v.id}`} checked={(v.bild_modus || "cover") === "cover"} onChange={() => setVorlagenListe(vorlagenListe.map(x => x.id === v.id ? { ...x, bild_modus: "cover" } : x))} /> Ausfüllen
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                          <input type="radio" name={`vorlage_bild_modus_${v.id}`} checked={v.bild_modus === "contain"} onChange={() => setVorlagenListe(vorlagenListe.map(x => x.id === v.id ? { ...x, bild_modus: "contain" } : x))} /> Vollständig anzeigen
                        </label>
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleVorlageSpeichern(v)} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                    <button onClick={() => { setBearbeitenVorlageId(null); ladeVorlagen(); }} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ flex: 1, minWidth: 160, color: THEME.colors.text }}>
                    {v.name}
                    {v.titel && <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}> — {v.titel}</span>}
                  </span>
                  <button onClick={() => setBearbeitenVorlageId(v.id)} style={{ ...s.btnGray, width: "auto" }}>Bearbeiten</button>
                  <button onClick={() => handleVorlageDuplizieren(v.id)} style={{ ...s.btnGray, width: "auto" }}>Duplizieren</button>
                  <button onClick={() => handleVorlageLoeschen(v.id)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Löschen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {aktiveTab === "news" && (
        <div style={s.karte}>
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
            <input style={{ ...s.input, flex: 1, minWidth: 200 }} placeholder="Neue News..." value={neueNews.titel} onChange={e => setNeueNews({ ...neueNews, titel: e.target.value })} onKeyDown={e => { if (e.key === "Enter") handleNewsErstellen(); }} />
            <button onClick={handleNewsErstellen} style={{ ...s.btn, width: "auto" }}>+ Hinzufügen</button>
          </div>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 12 }}>
            Auf dem TV werden aus Platzgründen maximal 6 aktive News gleichzeitig angezeigt (in der Reihenfolge unten, ▲/▼ zum Sortieren). Kurz &amp; prägnant formulieren – der Bildschirm wird auf große Distanz gelesen.
          </p>
          {newsListe.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine News angelegt.</p>}
          {newsListe.map((n, i) => (
            <div key={n.id} style={{ borderTop: `1px solid ${THEME.colors.border}`, padding: "12px 0" }}>
              {bearbeitenNewsId === n.id ? (
                <div>
                  <input style={{ ...s.input, marginBottom: 8 }} value={n.titel} onChange={e => setNewsListe(newsListe.map(x => x.id === n.id ? { ...x, titel: e.target.value } : x))} />
                  <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                    <div><label style={s.label}>Start</label><input type="date" style={s.input} value={n.start_datum || ""} onChange={e => setNewsListe(newsListe.map(x => x.id === n.id ? { ...x, start_datum: e.target.value } : x))} /></div>
                    <div><label style={s.label}>Ende</label><input type="date" style={s.input} value={n.end_datum || ""} onChange={e => setNewsListe(newsListe.map(x => x.id === n.id ? { ...x, end_datum: e.target.value } : x))} /></div>
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary, marginBottom: 8 }}>
                    <input type="checkbox" checked={!!n.in_app_sichtbar} onChange={e => setNewsListe(newsListe.map(x => x.id === n.id ? { ...x, in_app_sichtbar: e.target.checked } : x))} /> In Mitglieder-App anzeigen
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleNewsSpeichern(n)} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                    <button onClick={() => { setBearbeitenNewsId(null); ladeNews(); }} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <button onClick={() => handleNewsVerschieben(i, -1)} disabled={i === 0} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", opacity: i === 0 ? 0.4 : 1 }}>▲</button>
                    <button onClick={() => handleNewsVerschieben(i, 1)} disabled={i === newsListe.length - 1} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", opacity: i === newsListe.length - 1 ? 0.4 : 1 }}>▼</button>
                  </div>
                  <span style={{ flex: 1, color: n.aktiv ? THEME.colors.text : THEME.colors.textMuted }}>{n.titel}{!n.aktiv && " (inaktiv)"}</span>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                    <input type="checkbox" checked={n.aktiv} onChange={e => handleNewsToggleAktiv(n, e.target.checked)} /> Auf TV
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                    <input type="checkbox" checked={!!n.in_app_sichtbar} onChange={e => handleNewsToggleAppSichtbar(n, e.target.checked)} /> In App
                  </label>
                  <button onClick={() => setBearbeitenNewsId(n.id)} style={{ ...s.btnGray, width: "auto" }}>Bearbeiten</button>
                  <button onClick={() => handleNewsLoeschen(n.id)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Löschen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {aktiveTab === "schongewusst" && (
        <div style={s.karte}>
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
            <input style={{ ...s.input, flex: 1, minWidth: 200 }} placeholder="Neuer Hinweis, z. B. „Partner sparen je 5 €“..." value={neuerSchonGewusstText} onChange={e => setNeuerSchonGewusstText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleSchonGewusstErstellen(); }} />
            <button onClick={handleSchonGewusstErstellen} style={{ ...s.btn, width: "auto" }}>+ Hinzufügen</button>
          </div>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 12 }}>
            Erscheint unten links neben/statt News. Bei mehreren aktiven Einträgen wechselt die Anzeige langsam durch (Reihenfolge unten, ▲/▼ zum Sortieren). Kurz &amp; prägnant formulieren.
          </p>
          {schonGewusstListe.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Hinweise angelegt.</p>}
          {schonGewusstListe.map((s2, i) => (
            <div key={s2.id} style={{ borderTop: `1px solid ${THEME.colors.border}`, padding: "12px 0" }}>
              {bearbeitenSchonGewusstId === s2.id ? (
                <div>
                  <input style={{ ...s.input, marginBottom: 8 }} value={s2.text} onChange={e => setSchonGewusstListe(schonGewusstListe.map(x => x.id === s2.id ? { ...x, text: e.target.value } : x))} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleSchonGewusstSpeichern(s2)} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                    <button onClick={() => { setBearbeitenSchonGewusstId(null); ladeSchonGewusst(); }} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <button onClick={() => handleSchonGewusstVerschieben(i, -1)} disabled={i === 0} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", opacity: i === 0 ? 0.4 : 1 }}>▲</button>
                    <button onClick={() => handleSchonGewusstVerschieben(i, 1)} disabled={i === schonGewusstListe.length - 1} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", opacity: i === schonGewusstListe.length - 1 ? 0.4 : 1 }}>▼</button>
                  </div>
                  <span style={{ flex: 1, color: s2.aktiv ? THEME.colors.text : THEME.colors.textMuted }}>{s2.text}{!s2.aktiv && " (inaktiv)"}</span>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                    <input type="checkbox" checked={s2.aktiv} onChange={e => handleSchonGewusstToggleAktiv(s2, e.target.checked)} /> Aktiv
                  </label>
                  <button onClick={() => setBearbeitenSchonGewusstId(s2.id)} style={{ ...s.btnGray, width: "auto" }}>Bearbeiten</button>
                  <button onClick={() => handleSchonGewusstLoeschen(s2.id)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Löschen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {aktiveTab === "kurse" && (
        <div style={s.karte}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <input style={{ ...s.input, flex: 1, minWidth: 200 }} placeholder="Neuer Kurs, z. B. „Rückenfit“..." value={neuerKursName} onChange={e => setNeuerKursName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleKursErstellen(); }} />
            <button onClick={handleKursErstellen} style={{ ...s.btn, width: "auto" }}>+ Hinzufügen</button>
          </div>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 12 }}>
            Eigene Kurs-Werbekarte rechts zwischen „Heute bei ComeFit" und den QR-Codes — unabhängig vom Wochentag, du entscheidest selbst welche Kurse gerade beworben werden. Maximal 4 aktive Kurse werden gleichzeitig angezeigt (Reihenfolge unten, ▲/▼ zum Sortieren). Das ist getrennt von den Kurszeiten im Tab „Heute" (die nur für den aktuellen Wochentag unter „Heute bei ComeFit" erscheinen).
          </p>
          {kurseListe.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Kurse angelegt.</p>}
          {kurseListe.map((k, i) => (
            <div key={k.id} style={{ borderTop: `1px solid ${THEME.colors.border}`, padding: "12px 0" }}>
              {bearbeitenKursId === k.id ? (
                <div>
                  <div style={s.inputGroup}><label style={s.label}>Kursname</label><input style={s.input} value={k.name} onChange={e => setKurseListe(kurseListe.map(x => x.id === k.id ? { ...x, name: e.target.value } : x))} /></div>
                  <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
                    <div>
                      <label style={s.label}>Wochentag</label>
                      <select style={s.input} value={k.wochentag || ""} onChange={e => setKurseListe(kurseListe.map(x => x.id === k.id ? { ...x, wochentag: e.target.value } : x))}>
                        <option value="">–</option>
                        {TV_WOCHENTAGE.map(tag => <option key={tag} value={tag}>{tag}</option>)}
                      </select>
                    </div>
                    <div><label style={s.label}>Startzeit</label><input type="time" style={s.input} value={k.von || ""} onChange={e => setKurseListe(kurseListe.map(x => x.id === k.id ? { ...x, von: e.target.value } : x))} /></div>
                    <div><label style={s.label}>Endzeit</label><input type="time" style={s.input} value={k.bis || ""} onChange={e => setKurseListe(kurseListe.map(x => x.id === k.id ? { ...x, bis: e.target.value } : x))} /></div>
                  </div>
                  <div style={s.inputGroup}><label style={s.label}>Zusatztext (optional)</label><input style={s.input} placeholder="z. B. „Anfänger willkommen“" value={k.zusatztext || ""} onChange={e => setKurseListe(kurseListe.map(x => x.id === k.id ? { ...x, zusatztext: e.target.value } : x))} /></div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleKursSpeichern(k)} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                    <button onClick={() => { setBearbeitenKursId(null); ladeKurse(); }} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <button onClick={() => handleKursVerschieben(i, -1)} disabled={i === 0} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", opacity: i === 0 ? 0.4 : 1 }}>▲</button>
                    <button onClick={() => handleKursVerschieben(i, 1)} disabled={i === kurseListe.length - 1} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", opacity: i === kurseListe.length - 1 ? 0.4 : 1 }}>▼</button>
                  </div>
                  <span style={{ flex: 1, minWidth: 160, color: k.aktiv ? THEME.colors.text : THEME.colors.textMuted }}>
                    {k.name}{!k.aktiv && " (inaktiv)"}
                    {(k.wochentag || k.von) && <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}> — {[k.wochentag, k.von && k.bis ? `${k.von}–${k.bis} Uhr` : k.von].filter(Boolean).join(" · ")}</span>}
                  </span>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                    <input type="checkbox" checked={k.aktiv} onChange={e => handleKursToggleAktiv(k, e.target.checked)} /> Aktiv
                  </label>
                  <button onClick={() => setBearbeitenKursId(k.id)} style={{ ...s.btnGray, width: "auto" }}>Bearbeiten</button>
                  <button onClick={() => handleKursLoeschen(k.id)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Löschen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {aktiveTab === "aktionen" && (
        <div style={s.karte}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <input style={{ ...s.input, flex: 1, minWidth: 200 }} placeholder="Neue Aktion (z.B. „10 Tage kostenlos testen“)..." value={neueAktionTitel} onChange={e => setNeueAktionTitel(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleAktionErstellen(); }} />
            <button onClick={handleAktionErstellen} style={{ ...s.btn, width: "auto" }}>+ Hinzufügen</button>
          </div>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 12 }}>
            Nur Aktionen mit „Als Hauptaktion anzeigen“ erscheinen auf der großen Hauptfläche des TVs. Sind mehrere gleichzeitig aktiv, wechseln sie dort alle 25 Sekunden ruhig durch (Reihenfolge per ▲/▼). Ist keine Aktion als Hauptaktion aktiv, zeigt die Fläche stattdessen die Hauptmeldung.
          </p>
          {aktionenListe.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Noch keine Aktionen angelegt.</p>}
          {aktionenListe.map((a, i) => (
            <div key={a.id} style={{ borderTop: `1px solid ${THEME.colors.border}`, padding: "12px 0" }}>
              {bearbeitenAktionId === a.id ? (
                <div>
                  <div style={s.inputGroup}><label style={s.label}>Titel</label><input style={s.input} value={a.titel} onChange={e => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, titel: e.target.value } : x))} /></div>
                  <div style={s.inputGroup}><label style={s.label}>Text</label><textarea style={{ ...s.input, minHeight: 70 }} value={a.text || ""} onChange={e => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, text: e.target.value } : x))} /></div>
                  <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
                    <div><label style={s.label}>Start</label><input type="date" style={s.input} value={a.start_datum || ""} onChange={e => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, start_datum: e.target.value } : x))} /></div>
                    <div><label style={s.label}>Ende</label><input type="date" style={s.input} value={a.end_datum || ""} onChange={e => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, end_datum: e.target.value } : x))} /></div>
                    <div style={{ flex: 1, minWidth: 200 }}><label style={s.label}>Link (optional)</label><input style={s.input} placeholder="https://..." value={a.link || ""} onChange={e => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, link: e.target.value } : x))} /></div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={s.label}>Bild (optional)</label>
                    <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "-2px 0 6px" }}>
                      Empfohlen: <b>1600 × 900 px, quer</b>.
                    </p>
                    {a.bild_vorhanden && <img key={aktionBildVersion} src={`${API}/tv/aktionen/${a.id}/bild?v=${aktionBildVersion}`} alt="Aktion" style={{ maxWidth: 260, maxHeight: 140, objectFit: "contain", display: "block", marginBottom: 8, borderRadius: 8 }} />}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={e => handleAktionBildHochladen(a.id, e.target.files[0])} disabled={aktionBildLaedt} />
                      {a.bild_vorhanden && <button onClick={() => handleAktionBildEntfernen(a.id)} style={{ ...s.btnGray, width: "auto", padding: "6px 14px", color: THEME.colors.danger }}>Bild entfernen</button>}
                    </div>
                    {a.bild_vorhanden && (
                      <>
                        <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                            <input type="radio" name={`aktion_bild_modus_${a.id}`} checked={(a.bild_modus || "cover") === "cover"} onChange={() => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, bild_modus: "cover" } : x))} /> Ausfüllen
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                            <input type="radio" name={`aktion_bild_modus_${a.id}`} checked={a.bild_modus === "contain"} onChange={() => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, bild_modus: "contain" } : x))} /> Vollständig anzeigen
                          </label>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                          <label style={{ ...s.label, marginBottom: 0, fontSize: THEME.fontSize.sm }}>Ausrichtung waagerecht</label>
                          {[["left", "Links"], ["center", "Zentriert"], ["right", "Rechts"]].map(([wert, label]) => (
                            <label key={wert} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                              <input type="radio" name={`aktion_bild_position_${a.id}`} checked={(a.bild_position || "center") === wert} onChange={() => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, bild_position: wert } : x))} />
                              {label}
                            </label>
                          ))}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                          <label style={{ ...s.label, marginBottom: 0, fontSize: THEME.fontSize.sm }}>Ausrichtung senkrecht</label>
                          {[["top", "Oben"], ["center", "Mitte"], ["bottom", "Unten"]].map(([wert, label]) => (
                            <label key={wert} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                              <input type="radio" name={`aktion_bild_position_vertikal_${a.id}`} checked={(a.bild_position_vertikal || "center") === wert} onChange={() => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, bild_position_vertikal: wert } : x))} />
                              {label}
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, color: THEME.colors.textSecondary }}>
                      <input type="checkbox" checked={!!a.zeige_qr} onChange={e => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, zeige_qr: e.target.checked } : x))} /> Eigenen QR-Code zum Link anzeigen
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, color: THEME.colors.text, fontWeight: 700 }}>
                      <input type="checkbox" checked={!!a.als_hauptaktion} onChange={e => setAktionenListe(aktionenListe.map(x => x.id === a.id ? { ...x, als_hauptaktion: e.target.checked } : x))} /> Als Hauptaktion anzeigen
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleAktionSpeichern(a)} style={{ ...s.btn, width: "auto" }}>Speichern</button>
                    <button onClick={() => { setBearbeitenAktionId(null); ladeAktionen(); }} style={{ ...s.btnGray, width: "auto" }}>Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <button onClick={() => handleAktionVerschieben(i, -1)} disabled={i === 0} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", opacity: i === 0 ? 0.4 : 1 }}>▲</button>
                    <button onClick={() => handleAktionVerschieben(i, 1)} disabled={i === aktionenListe.length - 1} style={{ ...s.btnGray, width: "auto", padding: "2px 8px", opacity: i === aktionenListe.length - 1 ? 0.4 : 1 }}>▼</button>
                  </div>
                  <span style={{ flex: 1, minWidth: 120, color: a.aktiv ? THEME.colors.text : THEME.colors.textMuted }}>
                    {a.titel}{!a.aktiv && " (inaktiv)"}{a.als_hauptaktion && <span style={{ marginLeft: 8, color: THEME.colors.primary, fontSize: THEME.fontSize.sm, fontWeight: 700 }}>★ Hauptaktion</span>}
                  </span>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
                    <input type="checkbox" checked={a.aktiv} onChange={e => handleAktionToggleAktiv(a, e.target.checked)} /> Aktiv
                  </label>
                  <button onClick={() => setBearbeitenAktionId(a.id)} style={{ ...s.btnGray, width: "auto" }}>Bearbeiten</button>
                  <button onClick={() => handleAktionLoeschen(a.id)} style={{ ...s.btnGray, width: "auto", color: THEME.colors.danger }}>Löschen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {aktiveTab === "heute" && (
        <div style={s.karte}>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 20 }}>
            Diese Zeiten erscheinen auf dem TV unter „Heute bei ComeFit“ – automatisch passend zum aktuellen Wochentag (Zeitzone Europe/Berlin). Die Kurse hier sind eine eigene, einfache TV-Anzeigeliste, unabhängig von der Kurse-/Buchungsverwaltung. <b>Hinweis:</b> Das ist etwas anderes als der Tab „Kurse“ oben – dort pflegst du eine eigenständige Kurs-Werbekarte, die unabhängig vom Wochentag immer angezeigt wird.
          </p>
          <TvKurseTabelle plan={zeitplanParsen("tv_kurse_json")} onChange={p => zeitplanAendern("tv_kurse_json", p)} />
          <TvZeitplanTabelle titel="Sauna" plan={zeitplanParsen("tv_saunazeiten_json")} onChange={p => zeitplanAendern("tv_saunazeiten_json", p)} />
          <TvZeitplanTabelle titel="Betreuung" plan={zeitplanParsen("tv_betreuungszeiten_json")} onChange={p => zeitplanAendern("tv_betreuungszeiten_json", p)} />
          <TvZeitplanTabelle titel="Training / Öffnungszeiten" plan={zeitplanParsen("tv_trainingszeiten_json")} onChange={p => zeitplanAendern("tv_trainingszeiten_json", p)} />
          <button onClick={handleEinstellungenSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
          {einstellungenGespeichert && <span style={{ color: THEME.colors.success, marginLeft: 12 }}>✓ Gespeichert</span>}
        </div>
      )}

      {aktiveTab === "einstellungen" && (
        <div style={s.karte}>
          <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 12 }}>WLAN</h3>
          <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 160 }}><label style={s.label}>SSID</label><input style={s.input} value={einstellungenForm.tv_wlan_ssid || ""} onChange={e => setEinstellungenForm({ ...einstellungenForm, tv_wlan_ssid: e.target.value })} /></div>
            <div style={{ flex: 1, minWidth: 160 }}><label style={s.label}>Passwort</label><input style={s.input} value={einstellungenForm.tv_wlan_passwort || ""} onChange={e => setEinstellungenForm({ ...einstellungenForm, tv_wlan_passwort: e.target.value })} /></div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={s.label}>Verschlüsselung</label>
              <select style={s.input} value={einstellungenForm.tv_wlan_verschluesselung || "WPA"} onChange={e => setEinstellungenForm({ ...einstellungenForm, tv_wlan_verschluesselung: e.target.value })}>
                <option value="WPA">WPA/WPA2</option><option value="WEP">WEP</option><option value="nopass">Kein Passwort</option>
              </select>
            </div>
          </div>
          <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 12 }}>Kontakt (Unbetreut-Hinweis)</h3>
          <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 160 }}><label style={s.label}>Telefon Emanuele</label><input style={s.input} value={einstellungenForm.tv_telefon_emanuele || ""} onChange={e => setEinstellungenForm({ ...einstellungenForm, tv_telefon_emanuele: e.target.value })} /></div>
            <div style={{ flex: 1, minWidth: 160 }}><label style={s.label}>Telefon Katharina</label><input style={s.input} value={einstellungenForm.tv_telefon_katharina || ""} onChange={e => setEinstellungenForm({ ...einstellungenForm, tv_telefon_katharina: e.target.value })} /></div>
          </div>
          <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 12 }}>QR-Codes</h3>
          <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200 }}><label style={s.label}>Google-Bewertungslink</label><input style={s.input} value={einstellungenForm.tv_google_bewertung_url || ""} onChange={e => setEinstellungenForm({ ...einstellungenForm, tv_google_bewertung_url: e.target.value })} /></div>
            <div style={{ flex: 1, minWidth: 200 }}><label style={s.label}>Feedback-Link</label><input style={s.input} value={einstellungenForm.tv_feedback_url || ""} onChange={e => setEinstellungenForm({ ...einstellungenForm, tv_feedback_url: e.target.value })} /></div>
          </div>
          <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 12 }}>Allgemein</h3>
          <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, color: THEME.colors.textSecondary }}>
              <input type="checkbox" checked={(einstellungenForm.tv_seite_aktiv || "true") !== "false"} onChange={e => setEinstellungenForm({ ...einstellungenForm, tv_seite_aktiv: e.target.checked ? "true" : "false" })} /> TV-Seite aktiv (öffentlich unter /tv erreichbar)
            </label>
            <div>
              <label style={s.label}>Aktualisierung alle (Sekunden)</label>
              <input type="number" min={10} style={{ ...s.input, width: 100 }} value={einstellungenForm.tv_aktualisierung_sekunden || "60"} onChange={e => setEinstellungenForm({ ...einstellungenForm, tv_aktualisierung_sekunden: e.target.value })} />
            </div>
          </div>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 20 }}>
            Das Logo oben links auf dem TV ist automatisch dasselbe wie euer Studio-Logo (Einstellungen → Design).
          </p>
          <h3 style={{ color: THEME.colors.text, fontSize: THEME.fontSize.lg, marginBottom: 4 }}>Design (nur TV-Seite)</h3>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginBottom: 16 }}>
            Gilt nur für /tv, unabhängig vom App-Design unter Einstellungen → Design. Die Orange-Markenfarbe bleibt fest.
          </p>
          {[
            ["tv_farbe_hintergrund", "Hintergrundfarbe", "#080808"],
            ["tv_farbe_text", "Textfarbe", "#ffffff"],
          ].map(([key, label, standard]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
              <input
                type="color"
                value={einstellungenForm[key] || standard}
                onChange={e => setEinstellungenForm({ ...einstellungenForm, [key]: e.target.value })}
                style={{ width: 44, height: 44, padding: 0, border: `1px solid ${THEME.colors.border}`, borderRadius: 8, background: "none", cursor: "pointer", flexShrink: 0 }}
              />
              <label style={{ ...s.label, minWidth: 140 }}>{label}</label>
              <input
                style={{ ...s.input, width: 120, flexShrink: 0 }}
                value={einstellungenForm[key] || ""}
                onChange={e => setEinstellungenForm({ ...einstellungenForm, [key]: e.target.value })}
              />
            </div>
          ))}
          <button onClick={handleEinstellungenSpeichern} style={{ ...s.btn, width: "auto" }}>Speichern</button>
          {einstellungenGespeichert && <span style={{ color: THEME.colors.success, marginLeft: 12 }}>✓ Gespeichert</span>}
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <a href="/tv" target="_blank" rel="noopener noreferrer" style={{ color: THEME.colors.primary }}>→ TV-Seite in neuem Tab öffnen</a>
      </div>
    </div>
  );
}

// ============ EIGENES MELDUNGS-FENSTER STATT DER NATIVEN BROWSER-alert()-BOX ============
// Nutzer-Wunsch 2026-08-20: die native alert()-Box sieht erkennbar nach Browser statt nach der
// eigentlichen App aus (eigene Titelzeile "app.comefit.de sagt:", System-Schriftart, oben/beliebig
// positioniert je Betriebssystem). Da im ganzen Projekt an >150 Stellen einfach `alert(...)`
// aufgerufen wird, ist ein Ersatz JEDES einzelnen Aufrufs unrealistisch aufwendig - stattdessen
// wird hier `window.alert` global durch eine eigene, im ComeFit-Design gehaltene, zentrierte
// Modal-Box ersetzt (gleiche modalBg/modal-Bausteine wie jeder andere Dialog in der App). Jeder
// bestehende `alert("...")`-Aufruf im Code funktioniert dadurch unveraendert weiter, sieht ab
// sofort aber wie ein normaler Teil der Software aus. Bewusst NUR alert() betroffen, nicht
// window.confirm()/window.prompt() - die brauchen einen synchronen Rueckgabewert (Ja/Nein-
// Entscheidung, die viele Handler direkt per `if (!window.confirm(...)) return;` auswerten), das
// liesse sich ohne den kompletten Aufbau jedes einzelnen Aufrufs nicht gefahrlos nachbilden.
const _nativerAlert = typeof window !== "undefined" ? window.alert.bind(window) : () => {};

function AlertUeberlagerung() {
  const [meldungen, setMeldungen] = useState([]);

  useEffect(() => {
    window.alert = (text) => {
      // Weiterhin auch in der Browser-Konsole/im Terminal sichtbar (z.B. bei automatisierten
      // Tests oder falls der Effekt aus irgendeinem Grund noch nicht aktiv ist).
      console.log("[Meldung]", text);
      setMeldungen(m => [...m, { id: `${Date.now()}-${Math.random()}`, text: String(text) }]);
    };
    return () => { window.alert = _nativerAlert; };
  }, []);

  if (meldungen.length === 0) return null;
  const aktuell = meldungen[0];
  const istErfolg = aktuell.text.trim().startsWith("✓");
  const schliessen = () => setMeldungen(m => m.filter(x => x.id !== aktuell.id));

  return (
    <div style={s.modalBg} onClick={schliessen}>
      <div style={{ ...s.modal, width: "min(420px, 92vw)", padding: "2rem", textAlign: "center" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>{istErfolg ? "✅" : "⚠️"}</div>
        <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.md, lineHeight: 1.6, whiteSpace: "pre-wrap", margin: "0 0 24px" }}>{aktuell.text}</p>
        <button onClick={schliessen} style={{ ...s.btn, width: "auto", minWidth: 120, margin: "0 auto" }} autoFocus>OK</button>
        {meldungen.length > 1 && (
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, marginTop: 14 }}>+{meldungen.length - 1} weitere Meldung{meldungen.length - 1 > 1 ? "en" : ""}</p>
        )}
      </div>
    </div>
  );
}

function App() {
  let seite;
  if (window.location.hostname === "wlan.comefit.de") {
    // Eigene Subdomain statt Pfad auf app.comefit.de - unabhaengig vom Pfad IMMER die
    // Registrierungsseite zeigen (einfache Anlaufstelle, egal wie genau verlinkt/eingegeben).
    seite = <WlanRegistrierungSeite />;
  } else if (window.location.pathname === "/tuer") {
    seite = <TuerOeffnenSeite />;
  } else if (window.location.pathname === "/belege-upload") {
    seite = <BelegeUploadApp />;
  } else if (window.location.pathname === "/mitglied-werden") {
    seite = <MitgliedWerdenSeite />;
  } else if (window.location.pathname === "/passwort-zuruecksetzen") {
    seite = <PasswortZuruecksetzenSeite />;
  } else if (window.location.pathname === "/vertrag-kuendigen") {
    seite = <VertragKuendigenSeite />;
  } else if (window.location.pathname === "/umfrage") {
    seite = <UmfrageSeite />;
  } else if (window.location.pathname === "/sonderaktion" || window.location.pathname === "/tag-der-offenen-tuer") {
    seite = <SonderaktionSeite />;
  } else if (window.location.pathname === "/training") {
    seite = <Suspense fallback={<div style={{ padding: 24, color: "#CDAA85", background: "#140E0A", minHeight: "100vh" }}>Lädt …</div>}><TrainingApp /></Suspense>;
  } else if (window.location.pathname === "/training2") {
    window.location.replace("/training" + window.location.search + window.location.hash);      // alter Link -> einzige Trainingsplan-App
    seite = null;
  } else if (window.location.pathname === "/tv") {
    seite = <TvSeite />;
  } else if (window.location.pathname === "/tv2") {
    seite = <TV2Page />;
  } else if (window.location.pathname.startsWith("/kurs/")) {
    seite = <KursRegistrierungSeite slug={window.location.pathname.slice("/kurs/".length).replace(/\/$/, "")} />;
  } else {
    seite = <HauptApp />;
  }
  // AlertUeberlagerung app-weit auf oberster Ebene, unabhaengig davon welche der obigen Seiten
  // gerade aktiv ist - jede von ihnen ruft an mindestens einer Stelle alert() auf.
  return (<>{seite}<AlertUeberlagerung /></>);
}

export default App;